/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * document_storage_service
 * OpenAPI spec version: 0.1.0
 */
import * as zod from 'zod';

/**
 * @summary Handler for `GET /agents`.
 */
export const listAgentsResponseItem = zod
  .object({
    auto_accept_permissions: zod
      .boolean()
      .nullish()
      .describe(
        "Whether the agent's sessions approve ACP permission requests without\nasking. `None` means always prompt. Bypass also requires the harness's opt-in."
      ),
    bot: zod
      .object({
        avatar_url: zod.string().nullish().describe('Optional avatar URL.'),
        created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
        created_by: zod
          .string()
          .nullish()
          .describe('User that created this bot.'),
        deleted_at: zod.iso
          .datetime({})
          .nullish()
          .describe('Soft-delete timestamp.'),
        description: zod.string().nullish().describe('Optional description.'),
        handle: zod.string().describe('Stable handle.'),
        has_agent: zod
          .boolean()
          .describe(
            'Whether mentioning this bot opens a sandboxed coding-agent session.'
          ),
        id: zod.string(),
        kind: zod.enum(['owned', 'system']).describe('Bot kind.'),
        name: zod.string().describe('Display name.'),
        owner: zod
          .union([
            zod.null(),
            zod
              .union([
                zod
                  .object({
                    type: zod.enum(['user']),
                    user_id: zod.string().describe('Owner user id.'),
                  })
                  .describe('User-owned bot.'),
                zod
                  .object({
                    team_id: zod.uuid().describe('Owner team id.'),
                    type: zod.enum(['team']),
                  })
                  .describe('Team-owned bot.'),
              ])
              .describe('Bot owner.'),
          ])
          .optional(),
        updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
      })
      .describe(
        'Bot row.\n\nClients deserialize this, so both derives are used.'
      ),
    channel_ids: zod
      .array(zod.uuid())
      .describe('Selected channel ids. Empty for a global agent.'),
    channel_scope: zod
      .enum(['all', 'selected'])
      .describe(
        'Whether an agent is available everywhere or only in selected channels.'
      ),
    default_model: zod
      .string()
      .describe('Model selected specifically for this agent.'),
    harness: zod.string().describe('Harness used to run the agent.'),
    harness_id: zod.union([zod.null(), zod.string()]).optional(),
    instructions: zod
      .string()
      .describe(
        'Instructions supplied to the agent at the start of a conversation.'
      ),
    is_coding: zod
      .boolean()
      .describe(
        "Whether the agent works in a repository, which decides how it answers\na channel mention: a coding agent posts a magic chip into its live\nsession, a chat agent replies in the thread. Chosen in the agent's\nsettings; the persona's word, not the runtime's."
      ),
    mcp: zod
      .union([
        zod
          .object({
            scope: zod.enum(['owner_connections']),
          })
          .describe(
            'Whatever apps the person running the session has connected.'
          ),
        zod
          .object({
            scope: zod.enum(['selected']),
            servers: zod
              .array(
                zod
                  .object({
                    app_slug: zod
                      .string()
                      .describe('Pipedream app slug, e.g. `linear`.'),
                    server_name: zod
                      .string()
                      .describe('Display name, e.g. `Linear`.'),
                  })
                  .describe(
                    'One Pipedream app an agent lists under [`AgentMcpServers::Selected`].\n\nOnly the catalog identity is stored. Whether a given person has connected\nthe app is theirs, resolved at call time by the egress proxy, never here.'
                  )
              )
              .describe(
                "The apps, in the order the agent's author picked them."
              ),
          })
          .describe('Exactly these apps, connected or not.'),
      ])
      .describe(
        "Which Pipedream MCP servers an agent's sessions are handed.\n\nOne value for the whole choice, so a selection can never travel without\nits scope or a scope without its selection. Serialized with a `scope` tag,\nwhich the generated TypeScript sees as a discriminated union."
      ),
  })
  .describe('A persisted user- or team-owned AI agent.');
export const listAgentsResponse = zod.array(listAgentsResponseItem);

/**
 * @summary Handler for `POST /agents`.
 */
export const createAgentBody = zod
  .object({
    auto_accept_permissions: zod
      .boolean()
      .nullish()
      .describe(
        "Whether the agent's sessions approve ACP permission requests without\nasking. Omit to always prompt."
      ),
    avatar_url: zod
      .string()
      .nullish()
      .describe('Optional avatar URL or data URL.'),
    channel_ids: zod
      .array(zod.uuid())
      .optional()
      .describe(
        'Selected channels. Must be non-empty only for `selected` scope.'
      ),
    channel_scope: zod
      .enum(['all', 'selected'])
      .describe(
        'Whether an agent is available everywhere or only in selected channels.'
      ),
    default_model: zod
      .string()
      .describe('Model selected specifically for this agent.'),
    description: zod.string().nullish().describe('Optional description.'),
    handle: zod.string().describe('Stable `@` handle.'),
    harness: zod.string().describe('Harness used to run the agent.'),
    harness_id: zod.union([zod.null(), zod.string()]).optional(),
    instructions: zod
      .string()
      .describe(
        'Instructions supplied to the agent at the start of a conversation.'
      ),
    is_coding: zod
      .boolean()
      .describe(
        'Whether the agent is a coding agent: a mention is answered with a magic\nchip into its live session (`true`) or a reply in the thread (`false`).'
      ),
    mcp: zod
      .union([
        zod
          .object({
            scope: zod.enum(['owner_connections']),
          })
          .describe(
            'Whatever apps the person running the session has connected.'
          ),
        zod
          .object({
            scope: zod.enum(['selected']),
            servers: zod
              .array(
                zod
                  .object({
                    app_slug: zod
                      .string()
                      .describe('Pipedream app slug, e.g. `linear`.'),
                    server_name: zod
                      .string()
                      .describe('Display name, e.g. `Linear`.'),
                  })
                  .describe(
                    'One Pipedream app an agent lists under [`AgentMcpServers::Selected`].\n\nOnly the catalog identity is stored. Whether a given person has connected\nthe app is theirs, resolved at call time by the egress proxy, never here.'
                  )
              )
              .describe(
                "The apps, in the order the agent's author picked them."
              ),
          })
          .describe('Exactly these apps, connected or not.'),
      ])
      .optional()
      .describe(
        "Which Pipedream MCP servers an agent's sessions are handed.\n\nOne value for the whole choice, so a selection can never travel without\nits scope or a scope without its selection. Serialized with a `scope` tag,\nwhich the generated TypeScript sees as a discriminated union."
      ),
    name: zod.string().describe('Display name.'),
    team_id: zod
      .uuid()
      .nullish()
      .describe('Team owner. Omit for a private, user-owned agent.'),
  })
  .describe('Request to create a persisted AI agent.');

/**
 * @summary Handler for `PUT /agents/{agent_id}`.
 */
export const updateAgentParams = zod.object({
  agent_id: zod.string().describe('Agent bot ID'),
});

export const updateAgentBody = zod
  .object({
    auto_accept_permissions: zod
      .boolean()
      .nullish()
      .describe(
        "Whether the agent's sessions approve ACP permission requests without\nasking. Omit to always prompt."
      ),
    avatar_url: zod
      .string()
      .nullish()
      .describe('Optional avatar URL or data URL.'),
    channel_ids: zod
      .array(zod.uuid())
      .optional()
      .describe(
        'Selected channels. Must be non-empty only for `selected` scope.'
      ),
    channel_scope: zod
      .enum(['all', 'selected'])
      .describe(
        'Whether an agent is available everywhere or only in selected channels.'
      ),
    default_model: zod
      .string()
      .describe('Model selected specifically for this agent.'),
    description: zod.string().nullish().describe('Optional description.'),
    handle: zod.string().describe('Stable `@` handle.'),
    harness: zod.string().describe('Harness used to run the agent.'),
    harness_id: zod.union([zod.null(), zod.string()]).optional(),
    instructions: zod
      .string()
      .describe(
        'Instructions supplied to the agent at the start of a conversation.'
      ),
    is_coding: zod
      .boolean()
      .describe(
        'Whether the agent is a coding agent: a mention is answered with a magic\nchip into its live session (`true`) or a reply in the thread (`false`).'
      ),
    mcp: zod
      .union([
        zod
          .object({
            scope: zod.enum(['owner_connections']),
          })
          .describe(
            'Whatever apps the person running the session has connected.'
          ),
        zod
          .object({
            scope: zod.enum(['selected']),
            servers: zod
              .array(
                zod
                  .object({
                    app_slug: zod
                      .string()
                      .describe('Pipedream app slug, e.g. `linear`.'),
                    server_name: zod
                      .string()
                      .describe('Display name, e.g. `Linear`.'),
                  })
                  .describe(
                    'One Pipedream app an agent lists under [`AgentMcpServers::Selected`].\n\nOnly the catalog identity is stored. Whether a given person has connected\nthe app is theirs, resolved at call time by the egress proxy, never here.'
                  )
              )
              .describe(
                "The apps, in the order the agent's author picked them."
              ),
          })
          .describe('Exactly these apps, connected or not.'),
      ])
      .optional()
      .describe(
        "Which Pipedream MCP servers an agent's sessions are handed.\n\nOne value for the whole choice, so a selection can never travel without\nits scope or a scope without its selection. Serialized with a `scope` tag,\nwhich the generated TypeScript sees as a discriminated union."
      ),
    name: zod.string().describe('Display name.'),
    team_id: zod
      .uuid()
      .nullish()
      .describe('Team owner. Omit to make the agent private to the caller.'),
  })
  .describe(
    'Request to replace the editable configuration of a persisted AI agent.'
  );

export const updateAgentResponse = zod
  .object({
    auto_accept_permissions: zod
      .boolean()
      .nullish()
      .describe(
        "Whether the agent's sessions approve ACP permission requests without\nasking. `None` means always prompt. Bypass also requires the harness's opt-in."
      ),
    bot: zod
      .object({
        avatar_url: zod.string().nullish().describe('Optional avatar URL.'),
        created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
        created_by: zod
          .string()
          .nullish()
          .describe('User that created this bot.'),
        deleted_at: zod.iso
          .datetime({})
          .nullish()
          .describe('Soft-delete timestamp.'),
        description: zod.string().nullish().describe('Optional description.'),
        handle: zod.string().describe('Stable handle.'),
        has_agent: zod
          .boolean()
          .describe(
            'Whether mentioning this bot opens a sandboxed coding-agent session.'
          ),
        id: zod.string(),
        kind: zod.enum(['owned', 'system']).describe('Bot kind.'),
        name: zod.string().describe('Display name.'),
        owner: zod
          .union([
            zod.null(),
            zod
              .union([
                zod
                  .object({
                    type: zod.enum(['user']),
                    user_id: zod.string().describe('Owner user id.'),
                  })
                  .describe('User-owned bot.'),
                zod
                  .object({
                    team_id: zod.uuid().describe('Owner team id.'),
                    type: zod.enum(['team']),
                  })
                  .describe('Team-owned bot.'),
              ])
              .describe('Bot owner.'),
          ])
          .optional(),
        updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
      })
      .describe(
        'Bot row.\n\nClients deserialize this, so both derives are used.'
      ),
    channel_ids: zod
      .array(zod.uuid())
      .describe('Selected channel ids. Empty for a global agent.'),
    channel_scope: zod
      .enum(['all', 'selected'])
      .describe(
        'Whether an agent is available everywhere or only in selected channels.'
      ),
    default_model: zod
      .string()
      .describe('Model selected specifically for this agent.'),
    harness: zod.string().describe('Harness used to run the agent.'),
    harness_id: zod.union([zod.null(), zod.string()]).optional(),
    instructions: zod
      .string()
      .describe(
        'Instructions supplied to the agent at the start of a conversation.'
      ),
    is_coding: zod
      .boolean()
      .describe(
        "Whether the agent works in a repository, which decides how it answers\na channel mention: a coding agent posts a magic chip into its live\nsession, a chat agent replies in the thread. Chosen in the agent's\nsettings; the persona's word, not the runtime's."
      ),
    mcp: zod
      .union([
        zod
          .object({
            scope: zod.enum(['owner_connections']),
          })
          .describe(
            'Whatever apps the person running the session has connected.'
          ),
        zod
          .object({
            scope: zod.enum(['selected']),
            servers: zod
              .array(
                zod
                  .object({
                    app_slug: zod
                      .string()
                      .describe('Pipedream app slug, e.g. `linear`.'),
                    server_name: zod
                      .string()
                      .describe('Display name, e.g. `Linear`.'),
                  })
                  .describe(
                    'One Pipedream app an agent lists under [`AgentMcpServers::Selected`].\n\nOnly the catalog identity is stored. Whether a given person has connected\nthe app is theirs, resolved at call time by the egress proxy, never here.'
                  )
              )
              .describe(
                "The apps, in the order the agent's author picked them."
              ),
          })
          .describe('Exactly these apps, connected or not.'),
      ])
      .describe(
        "Which Pipedream MCP servers an agent's sessions are handed.\n\nOne value for the whole choice, so a selection can never travel without\nits scope or a scope without its selection. Serialized with a `scope` tag,\nwhich the generated TypeScript sees as a discriminated union."
      ),
  })
  .describe('A persisted user- or team-owned AI agent.');

/**
 * @summary Deletes a highlight and any attached discussion under the annotation policy.
 */
export const deleteAnchorBody = zod
  .object({
    anchorType: zod.enum(['highlight']),
    uuid: zod.uuid(),
  })
  .and(
    zod.object({
      fileType: zod.enum(['pdf']),
    })
  );

export const deleteAnchorResponse = zod
  .union([
    zod.object({
      anchorType: zod.enum(['free-comment']),
      uuid: zod.uuid(),
    }),
    zod.object({
      anchorType: zod.enum(['highlight']),
      uuid: zod.uuid(),
    }),
  ])
  .and(
    zod.object({
      fileType: zod.enum(['pdf']),
    })
  )
  .and(
    zod.object({
      documentId: zod.string(),
      rootId: zod
        .uuid()
        .nullish()
        .describe(
          'Root of the discussion that was deleted with the anchor, if it had one.'
        ),
    })
  );

/**
 * @summary Edits a single anchor for a document
 */
export const editAnchorBody = zod
  .object({
    allowableEdits: zod.unknown().optional(),
    heightPct: zod.number().nullish(),
    originalIndex: zod.number().nullish(),
    originalPage: zod.number().nullish(),
    page: zod.number().nullish(),
    rotation: zod.number().nullish(),
    shouldLockOnSave: zod.boolean().nullish(),
    uuid: zod.uuid(),
    wasDeleted: zod.boolean().nullish(),
    wasEdited: zod.boolean().nullish(),
    widthPct: zod.number().nullish(),
    xPct: zod.number().nullish(),
    yPct: zod.number().nullish(),
  })
  .and(
    zod.object({
      anchorType: zod.enum(['free-comment']),
    })
  )
  .and(
    zod.object({
      fileType: zod.enum(['pdf']),
    })
  );

export const editAnchorResponse = zod
  .union([
    zod
      .object({
        allowableEdits: zod.unknown().optional(),
        documentId: zod.string(),
        heightPct: zod.number(),
        originalIndex: zod.number(),
        originalPage: zod.number(),
        owner: zod.string(),
        page: zod.number(),
        rootId: zod.uuid().nullish(),
        rotation: zod.number(),
        shouldLockOnSave: zod.boolean(),
        uuid: zod.uuid(),
        wasDeleted: zod.boolean(),
        wasEdited: zod.boolean(),
        widthPct: zod.number(),
        xPct: zod.number(),
        yPct: zod.number(),
      })
      .and(
        zod.object({
          anchorType: zod.enum(['placeable']),
        })
      ),
    zod
      .object({
        alpha: zod.number(),
        blue: zod.number(),
        createdAt: zod.iso.datetime({}).nullish(),
        deletedAt: zod.iso.datetime({}).nullish(),
        documentId: zod.string(),
        green: zod.number(),
        highlightRects: zod.array(
          zod.object({
            height: zod.number(),
            id: zod.number(),
            left: zod.number(),
            top: zod.number(),
            width: zod.number(),
          })
        ),
        highlightType: zod.union([
          zod.literal(1),
          zod.literal(2),
          zod.literal(3),
        ]),
        owner: zod.string(),
        page: zod.number(),
        pageViewportHeight: zod.number(),
        pageViewportWidth: zod.number(),
        red: zod.number(),
        rootId: zod.uuid().nullish(),
        text: zod.string(),
        updatedAt: zod.iso.datetime({}).nullish(),
        uuid: zod.uuid(),
      })
      .and(
        zod.object({
          anchorType: zod.enum(['highlight']),
        })
      ),
  ])
  .and(
    zod.object({
      documentId: zod.string(),
    })
  );

/**
 * @summary Gets a set of comment anchors for a document
 */
export const getDocumentAnchorsParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentAnchorsResponse = zod.object({
  data: zod.array(
    zod.union([
      zod
        .object({
          allowableEdits: zod.unknown().optional(),
          documentId: zod.string(),
          heightPct: zod.number(),
          originalIndex: zod.number(),
          originalPage: zod.number(),
          owner: zod.string(),
          page: zod.number(),
          rootId: zod.uuid().nullish(),
          rotation: zod.number(),
          shouldLockOnSave: zod.boolean(),
          uuid: zod.uuid(),
          wasDeleted: zod.boolean(),
          wasEdited: zod.boolean(),
          widthPct: zod.number(),
          xPct: zod.number(),
          yPct: zod.number(),
        })
        .and(
          zod.object({
            anchorType: zod.enum(['placeable']),
          })
        ),
      zod
        .object({
          alpha: zod.number(),
          blue: zod.number(),
          createdAt: zod.iso.datetime({}).nullish(),
          deletedAt: zod.iso.datetime({}).nullish(),
          documentId: zod.string(),
          green: zod.number(),
          highlightRects: zod.array(
            zod.object({
              height: zod.number(),
              id: zod.number(),
              left: zod.number(),
              top: zod.number(),
              width: zod.number(),
            })
          ),
          highlightType: zod.union([
            zod.literal(1),
            zod.literal(2),
            zod.literal(3),
          ]),
          owner: zod.string(),
          page: zod.number(),
          pageViewportHeight: zod.number(),
          pageViewportWidth: zod.number(),
          red: zod.number(),
          rootId: zod.uuid().nullish(),
          text: zod.string(),
          updatedAt: zod.iso.datetime({}).nullish(),
          uuid: zod.uuid(),
        })
        .and(
          zod.object({
            anchorType: zod.enum(['highlight']),
          })
        ),
    ])
  ),
});

/**
 * @summary Creates an unthreaded anchor for a document
Attach a discussion through the shared message API.
 */
export const createAnchorParams = zod.object({
  document_id: zod.string().describe('The document id'),
});

export const createAnchorBody = zod
  .object({
    alpha: zod.number(),
    blue: zod.number(),
    green: zod.number(),
    highlightRects: zod.array(
      zod.object({
        height: zod.number(),
        left: zod.number(),
        top: zod.number(),
        width: zod.number(),
      })
    ),
    highlightType: zod.union([zod.literal(1), zod.literal(2), zod.literal(3)]),
    page: zod.number(),
    pageViewportHeight: zod.number(),
    pageViewportWidth: zod.number(),
    red: zod.number(),
    text: zod.string(),
    uuid: zod.uuid().nullish(),
  })
  .and(
    zod.object({
      anchorType: zod.enum(['highlight']),
    })
  )
  .and(
    zod.object({
      fileType: zod.enum(['pdf']),
    })
  );

export const createAnchorResponse = zod
  .union([
    zod
      .object({
        allowableEdits: zod.unknown().optional(),
        documentId: zod.string(),
        heightPct: zod.number(),
        originalIndex: zod.number(),
        originalPage: zod.number(),
        owner: zod.string(),
        page: zod.number(),
        rootId: zod.uuid().nullish(),
        rotation: zod.number(),
        shouldLockOnSave: zod.boolean(),
        uuid: zod.uuid(),
        wasDeleted: zod.boolean(),
        wasEdited: zod.boolean(),
        widthPct: zod.number(),
        xPct: zod.number(),
        yPct: zod.number(),
      })
      .and(
        zod.object({
          anchorType: zod.enum(['placeable']),
        })
      ),
    zod
      .object({
        alpha: zod.number(),
        blue: zod.number(),
        createdAt: zod.iso.datetime({}).nullish(),
        deletedAt: zod.iso.datetime({}).nullish(),
        documentId: zod.string(),
        green: zod.number(),
        highlightRects: zod.array(
          zod.object({
            height: zod.number(),
            id: zod.number(),
            left: zod.number(),
            top: zod.number(),
            width: zod.number(),
          })
        ),
        highlightType: zod.union([
          zod.literal(1),
          zod.literal(2),
          zod.literal(3),
        ]),
        owner: zod.string(),
        page: zod.number(),
        pageViewportHeight: zod.number(),
        pageViewportWidth: zod.number(),
        red: zod.number(),
        rootId: zod.uuid().nullish(),
        text: zod.string(),
        updatedAt: zod.iso.datetime({}).nullish(),
        uuid: zod.uuid(),
      })
      .and(
        zod.object({
          anchorType: zod.enum(['highlight']),
        })
      ),
  ])
  .and(
    zod.object({
      documentId: zod.string(),
    })
  );

/**
 * @summary Handler for `GET /bots/me`.
 */
export const getSelfBotResponse = zod
  .object({
    avatar_url: zod.string().nullish().describe('Optional avatar URL.'),
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by: zod.string().nullish().describe('User that created this bot.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Soft-delete timestamp.'),
    description: zod.string().nullish().describe('Optional description.'),
    handle: zod.string().describe('Stable handle.'),
    has_agent: zod
      .boolean()
      .describe(
        'Whether mentioning this bot opens a sandboxed coding-agent session.'
      ),
    id: zod.string(),
    kind: zod.enum(['owned', 'system']).describe('Bot kind.'),
    name: zod.string().describe('Display name.'),
    owner: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                type: zod.enum(['user']),
                user_id: zod.string().describe('Owner user id.'),
              })
              .describe('User-owned bot.'),
            zod
              .object({
                team_id: zod.uuid().describe('Owner team id.'),
                type: zod.enum(['team']),
              })
              .describe('Team-owned bot.'),
          ])
          .describe('Bot owner.'),
      ])
      .optional(),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
  })
  .describe('Bot row.\n\nClients deserialize this, so both derives are used.');

/**
 * Returns display fields and the sponsor for each requested bot. Not a
manageability check: any authenticated user or internal caller may look up
ids they already have.
 * @summary Handler for `GET /bots/profiles`.
 */
export const getBotOwnerProfilesQueryParams = zod.object({
  ids: zod
    .array(zod.string())
    .optional()
    .describe('Bot ids. Repeat the key: `?ids=<uuid>&ids=<uuid>`.'),
});

export const getBotOwnerProfilesResponseItem = zod
  .object({
    avatar_url: zod
      .string()
      .nullish()
      .describe('Avatar URL. Registry system bots have none.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Soft-delete time. Absent for an active bot and for a registry system bot.'
      ),
    id: zod.string(),
    name: zod.string().describe('Display name.'),
    owner: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                type: zod.enum(['user']),
                user_id: zod.string().describe('Owner user id.'),
              })
              .describe('User-owned bot.'),
            zod
              .object({
                team_id: zod.uuid().describe('Owner team id.'),
                type: zod.enum(['team']),
              })
              .describe('Team-owned bot.'),
          ])
          .describe('Bot owner.'),
      ])
      .optional(),
  })
  .describe(
    'Bot identity for rendering, including the sponsor and soft-delete time.\n\n`owner` is none only for a registry system bot. A persisted row always has\na sponsor.'
  );
export const getBotOwnerProfilesResponse = zod.array(
  getBotOwnerProfilesResponseItem
);

/**
 * @summary Handler for `GET /bots/{bot_id}/channels`.
 */
export const listBotChannelsParams = zod.object({
  bot_id: zod.string().describe('Bot ID'),
});

export const listBotChannelsResponseItem = zod
  .object({
    channel_id: zod.uuid().describe('Channel id.'),
    channel_type: zod
      .enum(['public', 'private', 'direct_message', 'team'])
      .describe('Channel type for a channel containing a bot.'),
    joined_at: zod.iso
      .datetime({})
      .describe('Timestamp when the bot joined the channel.'),
    name: zod.string().nullish().describe('Channel display name.'),
  })
  .describe('Channel containing a bot.');
export const listBotChannelsResponse = zod.array(listBotChannelsResponseItem);

/**
 * @summary Handler for `DELETE /bots/{bot_id}/channels/{channel_id}`.
 */
export const removeBotFromChannelByBotParams = zod.object({
  bot_id: zod.string().describe('Bot ID'),
  channel_id: zod.uuid().describe('Channel ID'),
});

/**
 * @summary Return calendar occurrences visible to the authenticated requester.
 */
export const listOccurrencesQueryLimitMax = 2000;

export const listOccurrencesQueryParams = zod.object({
  start: zod.iso.datetime({}).describe('Inclusive UTC viewport start.'),
  end: zod.iso.datetime({}).describe('Exclusive UTC viewport end.'),
  startDate: zod.iso
    .date()
    .optional()
    .describe('Inclusive local date boundary for all-day events.'),
  endDate: zod.iso
    .date()
    .optional()
    .describe('Exclusive local date boundary for all-day events.'),
  limit: zod
    .number()
    .min(1)
    .max(listOccurrencesQueryLimitMax)
    .optional()
    .describe('Maximum number of occurrences, from 1 through 2,000.'),
  cursor: zod
    .string()
    .optional()
    .describe('Opaque continuation cursor returned by the previous page.'),
});

export const listOccurrencesResponseItemsItemEventRemindersOverridesItemMinutesMin = 0;

export const listOccurrencesResponseItemsItemEventSequenceMin = 0;

export const listOccurrencesResponseItemsItemEventSourcesItemRemindersOverridesItemMinutesMin = 0;

export const listOccurrencesResponse = zod
  .object({
    hasMore: zod.boolean(),
    items: zod.array(
      zod
        .object({
          event: zod
            .object({
              attendees: zod
                .array(
                  zod
                    .object({
                      comment: zod
                        .string()
                        .nullish()
                        .describe('Optional attendee comment.'),
                      displayName: zod
                        .string()
                        .nullish()
                        .describe('Provider display name.'),
                      email: zod.string().describe('Normalized email address.'),
                      isOptional: zod
                        .boolean()
                        .describe('Whether attendance is optional.'),
                      isOrganizer: zod
                        .boolean()
                        .describe('Whether this attendee is the organizer.'),
                      isSelf: zod
                        .boolean()
                        .describe(
                          "Whether this attendee is one of the viewing requester's inboxes."
                        ),
                      responseStatus: zod
                        .enum([
                          'needs_action',
                          'accepted',
                          'declined',
                          'tentative',
                        ])
                        .describe('RSVP state for an attendee.'),
                    })
                    .describe('An attendee on a calendar event.')
                )
                .describe('Attendees, keyed by email during persistence.'),
              calendarId: zod
                .uuid()
                .nullish()
                .describe(
                  'Calendar the canonical source belongs to, when known. Absent only in\nprojections stored before calendars were attributed.'
                ),
              conferenceProvider: zod
                .union([
                  zod.null(),
                  zod
                    .enum(['google_meet', 'other'])
                    .describe(
                      "The conferencing system backing an event's join URL.\n\nMacro generates only Google Meet conferences, so this distinguishes one it\ncreated from a third party's — Zoom and friends arriving as `addOn`\nconference data, or a legacy classic Hangout. Clients use it to label the\nconference and to tell whether the Meet toggle reflects a Macro-managed\nconference.\n\nIt does not gate mutation. An explicit request replaces or detaches any\nconference, third-party included, exactly as deleting the event would;\nwhat protects a conference is that omitting the field leaves it untouched,\nso an unrelated edit never disturbs it."
                    ),
                ])
                .optional(),
              conferenceUrl: zod
                .string()
                .nullish()
                .describe('Direct join URL when known.'),
              createdAt: zod.iso.datetime({}).describe('Entity creation time.'),
              creatorEmail: zod
                .string()
                .nullish()
                .describe(
                  'Provider-reported creator email. Distinct from the organizer when\nsomeone writes onto a calendar they do not own. Omitted from stored\nprojections when unknown so events ingested before this field still\ncompare equal.'
                ),
              creatorName: zod
                .string()
                .nullish()
                .describe('Provider-reported creator display name.'),
              description: zod
                .string()
                .nullish()
                .describe('Optional event body.'),
              eventType: zod
                .enum([
                  'default',
                  'out_of_office',
                  'focus_time',
                  'working_location',
                  'birthday',
                  'from_gmail',
                ])
                .optional()
                .describe(
                  "Google's event type: ordinary meetings versus the status-style entries\n(working location, out of office, focus time, birthdays) Google renders\nand notifies differently. Immutable at the provider after creation."
                ),
              icalUid: zod
                .string()
                .describe(
                  'RFC 5545 UID used to reconcile provider and email sources.'
                ),
              id: zod.uuid().describe('Macro entity identifier.'),
              isReadOnly: zod
                .boolean()
                .describe(
                  "Whether the canonical source's calendar prohibits editing it."
                ),
              location: zod
                .string()
                .nullish()
                .describe('Optional physical or virtual location label.'),
              organizerEmail: zod
                .string()
                .nullish()
                .describe('Organizer email.'),
              organizerName: zod
                .string()
                .nullish()
                .describe('Organizer display name.'),
              ownerId: zod
                .string()
                .describe('Macro user who owns this event entity.'),
              recurrenceLines: zod
                .array(zod.string())
                .describe(
                  'Raw RFC 5545 recurrence properties (`RRULE`, `RDATE`, `EXDATE`).'
                ),
              reminders: zod
                .object({
                  overrides: zod
                    .array(
                      zod
                        .object({
                          method: zod
                            .string()
                            .describe(
                              'Provider method, stored verbatim; only `popup` fires Macro\nnotifications.'
                            ),
                          minutes: zod
                            .number()
                            .min(
                              listOccurrencesResponseItemsItemEventRemindersOverridesItemMinutesMin
                            )
                            .describe('Minutes before the event start.'),
                        })
                        .describe(
                          "One reminder: how it alerts and how many minutes before the event start\n(before midnight in the calendar's zone for all-day events) it fires."
                        )
                    )
                    .optional()
                    .describe(
                      'Explicit reminders replacing the defaults when `use_default` is off.'
                    ),
                  useDefault: zod
                    .boolean()
                    .describe(
                      "Whether the calendar's default reminders apply."
                    ),
                })
                .optional()
                .describe(
                  "Per-user reminder configuration for an event, mirroring Google's model:\neither the calendar's default reminders apply, or the explicit overrides\nreplace them entirely."
                ),
              sequence: zod
                .number()
                .min(listOccurrencesResponseItemsItemEventSequenceMin)
                .describe('Provider\/iCalendar sequence number.'),
              sources: zod
                .array(
                  zod
                    .object({
                      calendarId: zod
                        .uuid()
                        .describe('Calendar this copy lives on.'),
                      creatorEmail: zod
                        .string()
                        .nullish()
                        .describe('Provider-reported creator email.'),
                      creatorName: zod
                        .string()
                        .nullish()
                        .describe('Provider-reported creator display name.'),
                      description: zod
                        .string()
                        .nullish()
                        .describe('Optional event body.'),
                      eventType: zod
                        .enum([
                          'default',
                          'out_of_office',
                          'focus_time',
                          'working_location',
                          'birthday',
                          'from_gmail',
                        ])
                        .describe(
                          "Google's event type: ordinary meetings versus the status-style entries\n(working location, out of office, focus time, birthdays) Google renders\nand notifies differently. Immutable at the provider after creation."
                        ),
                      isReadOnly: zod
                        .boolean()
                        .describe(
                          "Whether the calendar's access role prohibits editing this copy."
                        ),
                      location: zod
                        .string()
                        .nullish()
                        .describe(
                          'Optional physical or virtual location label.'
                        ),
                      reminders: zod
                        .object({
                          overrides: zod
                            .array(
                              zod
                                .object({
                                  method: zod
                                    .string()
                                    .describe(
                                      'Provider method, stored verbatim; only `popup` fires Macro\nnotifications.'
                                    ),
                                  minutes: zod
                                    .number()
                                    .min(
                                      listOccurrencesResponseItemsItemEventSourcesItemRemindersOverridesItemMinutesMin
                                    )
                                    .describe(
                                      'Minutes before the event start.'
                                    ),
                                })
                                .describe(
                                  "One reminder: how it alerts and how many minutes before the event start\n(before midnight in the calendar's zone for all-day events) it fires."
                                )
                            )
                            .optional()
                            .describe(
                              'Explicit reminders replacing the defaults when `use_default` is off.'
                            ),
                          useDefault: zod
                            .boolean()
                            .describe(
                              "Whether the calendar's default reminders apply."
                            ),
                        })
                        .describe(
                          "Per-user reminder configuration for an event, mirroring Google's model:\neither the calendar's default reminders apply, or the explicit overrides\nreplace them entirely."
                        ),
                      title: zod.string().describe('Display title.'),
                      transparency: zod
                        .enum(['opaque', 'transparent'])
                        .describe('Whether an event blocks availability.'),
                      visibility: zod
                        .enum(['default', 'public', 'private', 'confidential'])
                        .describe('Visibility of event details.'),
                    })
                    .describe(
                      "The content one provider copy of an event carries.\n\nGoogle keeps these fields per calendar copy: a shared calendar's copy of a\nmember's event can have its own title, type, reminders, and access role.\nThe entity holds its canonical source's values. Every other copy's values\nare read from here so a client can show the copy that belongs to the\ncalendar being viewed."
                    )
                )
                .optional()
                .describe(
                  "Content of every active copy of this event, canonical first: the\nprimary calendar's copy, then the freshest. A client picks the copy\nwhose calendar it is showing and falls back to the first. Populated\nonly on the read path, so stored projections omit it."
                ),
              status: zod
                .enum(['confirmed', 'tentative', 'cancelled'])
                .describe('Canonical event status.'),
              time: zod
                .union([
                  zod
                    .object({
                      endsAt: zod.iso
                        .datetime({})
                        .describe(
                          'Exclusive end instant; equal to the start for an imported point event.'
                        ),
                      kind: zod.enum(['timed']),
                      startsAt: zod.iso
                        .datetime({})
                        .describe('Inclusive start instant.'),
                      timeZone: zod
                        .string()
                        .nullish()
                        .describe(
                          'Original IANA time-zone identifier, when supplied.'
                        ),
                    })
                    .describe('An event with absolute instants.'),
                  zod
                    .object({
                      endDate: zod.iso
                        .date()
                        .describe('Exclusive local end date.'),
                      kind: zod.enum(['allDay']),
                      startDate: zod.iso
                        .date()
                        .describe('Inclusive local start date.'),
                    })
                    .describe(
                      "An all-day event using RFC 5545's exclusive end date."
                    ),
                ])
                .describe(
                  'The mutually exclusive time shape of a calendar event.\n\nFields are renamed per variant rather than with `rename_all_fields`\nbecause utoipa only honors variant-level serde renames when it\nderives the OpenAPI schema.'
                ),
              title: zod.string().describe('Display title.'),
              transparency: zod
                .enum(['opaque', 'transparent'])
                .describe('Whether an event blocks availability.'),
              updatedAt: zod.iso.datetime({}).describe('Entity update time.'),
              visibility: zod
                .enum(['default', 'public', 'private', 'confidential'])
                .describe('Visibility of event details.'),
            })
            .describe(
              "A stable, first-class Macro calendar event entity.\n\nContent fields hold the canonical source's values: the account's primary\ncalendar copy when one is synced, else the freshest remaining copy."
            ),
          occurrence: zod
            .object({
              eventId: zod.uuid().describe('Owning event entity.'),
              isCancelled: zod
                .boolean()
                .describe('Whether the instance was cancelled.'),
              occurrenceKey: zod
                .string()
                .describe('Stable key within the event.'),
              recurrenceId: zod
                .string()
                .nullish()
                .describe('Provider recurrence identifier, when applicable.'),
              time: zod
                .union([
                  zod
                    .object({
                      endsAt: zod.iso
                        .datetime({})
                        .describe(
                          'Exclusive end instant; equal to the start for an imported point event.'
                        ),
                      kind: zod.enum(['timed']),
                      startsAt: zod.iso
                        .datetime({})
                        .describe('Inclusive start instant.'),
                      timeZone: zod
                        .string()
                        .nullish()
                        .describe(
                          'Original IANA time-zone identifier, when supplied.'
                        ),
                    })
                    .describe('An event with absolute instants.'),
                  zod
                    .object({
                      endDate: zod.iso
                        .date()
                        .describe('Exclusive local end date.'),
                      kind: zod.enum(['allDay']),
                      startDate: zod.iso
                        .date()
                        .describe('Inclusive local start date.'),
                    })
                    .describe(
                      "An all-day event using RFC 5545's exclusive end date."
                    ),
                ])
                .describe(
                  'The mutually exclusive time shape of a calendar event.\n\nFields are renamed per variant rather than with `rename_all_fields`\nbecause utoipa only honors variant-level serde renames when it\nderives the OpenAPI schema.'
                ),
            })
            .describe(
              'A materialized recurrence instance optimized for range queries.'
            ),
        })
        .describe(
          'One materialized occurrence paired with its stable calendar event entity.'
        )
    ),
    nextCursor: zod.string().nullish(),
    syncStatus: zod
      .enum(['syncing', 'ready'])
      .describe(
        'Aggregate ingestion state across every calendar account visible to a\nrequester, letting clients render progressively while sources build.'
      ),
  })
  .describe('Paginated calendar occurrence viewport response.');

/**
 * @summary Resolve mentioned calendar events to the requester's own projections.
 */
export const mentionPreviewsBody = zod
  .object({
    items: zod
      .array(
        zod
          .object({
            eventId: zod.uuid().describe('Mentioned calendar event id.'),
            occurrenceKey: zod
              .string()
              .nullish()
              .describe(
                'Occurrence the mention points at, when it targets one instance.'
              ),
          })
          .describe('One mentioned event to resolve for the requester.')
      )
      .describe('Mentioned events to resolve, at most 100.'),
  })
  .describe('Batch calendar mention preview request.');

export const mentionPreviewsResponseItemsItemEventAttendeeCountMin = 0;

export const mentionPreviewsResponse = zod
  .object({
    items: zod.array(
      zod
        .object({
          event: zod
            .union([
              zod.null(),
              zod
                .object({
                  attendeeCount: zod
                    .number()
                    .min(mentionPreviewsResponseItemsItemEventAttendeeCountMin)
                    .describe('Number of attendees on the previewed copy.'),
                  description: zod
                    .string()
                    .nullish()
                    .describe(
                      'Provider description, plain text or HTML, truncated for the preview.\nClients must sanitize it before rendering.'
                    ),
                  isRecurring: zod
                    .boolean()
                    .describe('Whether the event repeats.'),
                  location: zod
                    .string()
                    .nullish()
                    .describe('Location label, when set.'),
                  occurrenceKey: zod
                    .string()
                    .nullish()
                    .describe(
                      'Key of the previewed instance, absent when no occurrence is\nmaterialized.'
                    ),
                  organizerEmail: zod
                    .string()
                    .nullish()
                    .describe('Organizer email.'),
                  organizerName: zod
                    .string()
                    .nullish()
                    .describe('Organizer display name.'),
                  time: zod
                    .union([
                      zod
                        .object({
                          endsAt: zod.iso
                            .datetime({})
                            .describe(
                              'Exclusive end instant; equal to the start for an imported point event.'
                            ),
                          kind: zod.enum(['timed']),
                          startsAt: zod.iso
                            .datetime({})
                            .describe('Inclusive start instant.'),
                          timeZone: zod
                            .string()
                            .nullish()
                            .describe(
                              'Original IANA time-zone identifier, when supplied.'
                            ),
                        })
                        .describe('An event with absolute instants.'),
                      zod
                        .object({
                          endDate: zod.iso
                            .date()
                            .describe('Exclusive local end date.'),
                          kind: zod.enum(['allDay']),
                          startDate: zod.iso
                            .date()
                            .describe('Inclusive local start date.'),
                        })
                        .describe(
                          "An all-day event using RFC 5545's exclusive end date."
                        ),
                    ])
                    .describe(
                      'The mutually exclusive time shape of a calendar event.\n\nFields are renamed per variant rather than with `rename_all_fields`\nbecause utoipa only honors variant-level serde renames when it\nderives the OpenAPI schema.'
                    ),
                  title: zod.string().describe('Display title.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Entity update time of the previewed copy.'),
                  viewerEventId: zod
                    .uuid()
                    .nullish()
                    .describe(
                      "The requester's own event entity for the mentioned meeting. Differs\nfrom the mentioned id when the mention came from another attendee.\nAbsent when the meeting is on none of the requester's calendars and\nthey see it only because it was shared with one of their channels:\nthat preview is read-only and there is no event of theirs to open."
                    ),
                })
                .describe(
                  "Meeting-level fields shown in a calendar event mention preview, taken from\nthe requester's own projection of the meeting, or — when the requester has\nnone — from the mentioned projection a channel they belong to was given."
                ),
            ])
            .optional(),
          eventId: zod
            .uuid()
            .describe('The mentioned event id, echoed from the request.'),
          type: zod
            .enum(['access', 'no_access', 'does_not_exist'])
            .describe('Requester-relative visibility of one mentioned event.'),
        })
        .describe('Resolution of one mentioned event, in request order.')
    ),
  })
  .describe('Batch calendar mention preview response.');

/**
 * @summary Query authorized projections in a bounded viewport.
 */
export const listTeamCalendarQueryLimitMin = 0;

export const listTeamCalendarQueryParams = zod.object({
  start: zod.iso.datetime({}).describe('Inclusive instant.'),
  end: zod.iso.datetime({}).describe('Exclusive instant.'),
  cursor: zod
    .string()
    .nullish()
    .describe('Continuation returned by the preceding page.'),
  limit: zod
    .number()
    .min(listTeamCalendarQueryLimitMin)
    .nullish()
    .describe(
      'Maximum source occurrences, at most 2,000. Zero returns the roster only.'
    ),
});

export const listTeamCalendarResponse = zod
  .object({
    items: zod
      .array(
        zod
          .union([
            zod
              .object({
                kind: zod.enum(['busy']),
              })
              .describe('No provider or event metadata.'),
            zod
              .object({
                details: zod
                  .object({
                    attendees: zod
                      .array(
                        zod
                          .object({
                            comment: zod
                              .string()
                              .nullish()
                              .describe('Optional attendee comment.'),
                            displayName: zod
                              .string()
                              .nullish()
                              .describe('Provider display name.'),
                            email: zod
                              .string()
                              .describe('Normalized email address.'),
                            isOptional: zod
                              .boolean()
                              .describe('Whether attendance is optional.'),
                            isOrganizer: zod
                              .boolean()
                              .describe(
                                'Whether this attendee is the organizer.'
                              ),
                            isSelf: zod
                              .boolean()
                              .describe(
                                "Whether this attendee is one of the viewing requester's inboxes."
                              ),
                            responseStatus: zod
                              .enum([
                                'needs_action',
                                'accepted',
                                'declined',
                                'tentative',
                              ])
                              .describe('RSVP state for an attendee.'),
                          })
                          .describe('An attendee on a calendar event.')
                      )
                      .describe(
                        'Attendees; self flags are relative to the requesting viewer.'
                      ),
                    calendarName: zod
                      .string()
                      .describe(
                        'Source calendar name, shown only with details.'
                      ),
                    conferenceUrl: zod
                      .string()
                      .nullish()
                      .describe('Join URL, if supplied by this source.'),
                    description: zod
                      .string()
                      .nullish()
                      .describe('Body, if supplied by this source.'),
                    location: zod
                      .string()
                      .nullish()
                      .describe('Location, if supplied by this source.'),
                    organizerEmail: zod
                      .string()
                      .nullish()
                      .describe('Organizer address from this source.'),
                    organizerName: zod
                      .string()
                      .nullish()
                      .describe('Organizer name from this source.'),
                    title: zod.string().describe('Display title.'),
                  })
                  .describe('Safe details from a single authorized source.'),
                kind: zod.enum(['details']),
              })
              .describe('Explicitly authorized source content.'),
          ])
          .describe(
            'Disjoint busy\/detail shapes prevent new event fields leaking by default.'
          )
          .and(
            zod.object({
              contributesToAvailability: zod
                .boolean()
                .describe(
                  "Whether this occurrence blocks the sharer's personal availability.\nA subscribed calendar's block can be shared without occupying the sharer."
                ),
              id: zod
                .string()
                .describe(
                  'Stable opaque projection identity, never an event entity id.'
                ),
              ownerId: zod
                .string()
                .describe('The person sharing access to the source.'),
              time: zod
                .union([
                  zod
                    .object({
                      endsAt: zod.iso
                        .datetime({})
                        .describe(
                          'Exclusive end instant; equal to the start for an imported point event.'
                        ),
                      kind: zod.enum(['timed']),
                      startsAt: zod.iso
                        .datetime({})
                        .describe('Inclusive start instant.'),
                      timeZone: zod
                        .string()
                        .nullish()
                        .describe(
                          'Original IANA time-zone identifier, when supplied.'
                        ),
                    })
                    .describe('An event with absolute instants.'),
                  zod
                    .object({
                      endDate: zod.iso
                        .date()
                        .describe('Exclusive local end date.'),
                      kind: zod.enum(['allDay']),
                      startDate: zod.iso
                        .date()
                        .describe('Inclusive local start date.'),
                    })
                    .describe(
                      "An all-day event using RFC 5545's exclusive end date."
                    ),
                ])
                .describe(
                  'The mutually exclusive time shape of a calendar event.\n\nFields are renamed per variant rather than with `rename_all_fields`\nbecause utoipa only honors variant-level serde renames when it\nderives the OpenAPI schema.'
                ),
            })
          )
          .describe('A read-only team calendar occurrence.')
      )
      .describe('Authorized read-only projections.'),
    members: zod
      .array(
        zod
          .object({
            coverage: zod
              .enum(['ready', 'unavailable', 'hidden'])
              .describe(
                "Whether a member's calendar projection can establish availability."
              ),
            sharing: zod
              .enum(['all', 'busy_only', 'none'])
              .describe('What a user exposes through their team membership.'),
            userId: zod.string().describe('Macro user identifier.'),
          })
          .describe("A current member of the requester's team.")
      )
      .describe('Current membership and sharing policies.'),
    nextCursor: zod
      .string()
      .nullish()
      .describe(
        'Opaque continuation token; null after the last source occurrence.'
      ),
  })
  .describe('A bounded page of team calendar projections.');

/**
 * @summary Return teammates' out-of-office occurrences in the requested viewport.
 */
export const listTeamOutOfOfficeQueryLimitMax = 2000;

export const listTeamOutOfOfficeQueryParams = zod.object({
  start: zod.iso.datetime({}).describe('Inclusive UTC viewport start.'),
  end: zod.iso.datetime({}).describe('Exclusive UTC viewport end.'),
  startDate: zod.iso
    .date()
    .optional()
    .describe('Inclusive local date boundary for all-day events.'),
  endDate: zod.iso
    .date()
    .optional()
    .describe('Exclusive local date boundary for all-day events.'),
  limit: zod
    .number()
    .min(1)
    .max(listTeamOutOfOfficeQueryLimitMax)
    .optional()
    .describe('Maximum number of occurrences, from 1 through 2,000.'),
});

export const listTeamOutOfOfficeResponse = zod
  .object({
    hasMore: zod.boolean(),
    items: zod.array(
      zod
        .object({
          eventId: zod.uuid().describe("The teammate's calendar event id."),
          occurrenceKey: zod
            .string()
            .describe('Stable occurrence key within the event.'),
          ownerId: zod
            .string()
            .describe('Macro user id of the teammate who is out.'),
          time: zod
            .union([
              zod
                .object({
                  endsAt: zod.iso
                    .datetime({})
                    .describe(
                      'Exclusive end instant; equal to the start for an imported point event.'
                    ),
                  kind: zod.enum(['timed']),
                  startsAt: zod.iso
                    .datetime({})
                    .describe('Inclusive start instant.'),
                  timeZone: zod
                    .string()
                    .nullish()
                    .describe(
                      'Original IANA time-zone identifier, when supplied.'
                    ),
                })
                .describe('An event with absolute instants.'),
              zod
                .object({
                  endDate: zod.iso.date().describe('Exclusive local end date.'),
                  kind: zod.enum(['allDay']),
                  startDate: zod.iso
                    .date()
                    .describe('Inclusive local start date.'),
                })
                .describe(
                  "An all-day event using RFC 5545's exclusive end date."
                ),
            ])
            .describe(
              'The mutually exclusive time shape of a calendar event.\n\nFields are renamed per variant rather than with `rename_all_fields`\nbecause utoipa only honors variant-level serde renames when it\nderives the OpenAPI schema.'
            ),
          title: zod
            .string()
            .nullish()
            .describe(
              'Event title, absent unless the teammate shares event details.'
            ),
        })
        .describe("One teammate's out-of-office occurrence.")
    ),
  })
  .describe('Team out-of-office viewport response.');

/**
 * Lists all active calls in channels the caller is an active member of,
newest first. Calls with no active participants (orphaned by dropped RTC
webhooks) are excluded.
 * @summary Handler for `GET /call/active`.
 */
export const getActiveCallsResponse = zod
  .object({
    calls: zod
      .array(
        zod
          .object({
            callId: zod.uuid().describe('The call identifier.'),
            channelId: zod.uuid().describe('The channel this call belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the call was created.'),
            createdBy: zod.string().describe('User who created the call.'),
            participantCount: zod
              .number()
              .describe(
                'Number of active participants. Always >= 1 — calls with no active\nparticipants (e.g. orphaned by a dropped RTC webhook) are excluded.'
              ),
          })
          .describe(
            'A currently active call, as returned by the batch active-calls listing.'
          )
      )
      .describe('The active calls, newest first.'),
  })
  .describe(
    'Response listing all active calls in channels the caller is a member of.'
  );

/**
 * @summary Handle `GET /call/join/{token}` through the call domain service.
 */
export const meetingLookupParams = zod.object({
  token: zod.string(),
});

export const meetingLookupResponse = zod
  .object({
    callId: zod
      .uuid()
      .nullish()
      .describe('Currently active call session, if any.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe(
        'Associated channel, for links to existing channel calls only.'
      ),
    id: zod.uuid().describe('Persistent meeting identifier.'),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled end, or none for an instant meeting.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled start, or none for an instant meeting.'),
    shareToken: zod
      .string()
      .describe(
        "A bearer capability that grants access only to a meeting's RTC room."
      ),
    title: zod.string().describe('Human-readable meeting title.'),
  })
  .describe(
    'Persistent meeting metadata. No channel contents or archived media are exposed.'
  );

/**
 * @summary Handle `POST /call/join/{token}` through the call domain service.
 */
export const meetingGuestJoinParams = zod.object({
  token: zod.string(),
});

export const meetingGuestJoinBody = zod
  .object({
    displayName: zod
      .string()
      .describe("Guest's name, displayed to everyone in the room."),
  })
  .describe(
    'Public guest join inputs. The server generates the participant identity.'
  );

export const meetingGuestJoinResponse = zod
  .object({
    callId: zod.uuid().describe('The call identifier.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe('The channel this call is associated with.'),
    participantId: zod.string().describe('RTC participant identity.'),
    roomName: zod.string().describe('The RTC room name.'),
    serverUrl: zod
      .string()
      .describe('The RTC server URL for the frontend SDK to connect to.'),
    shareToken: zod
      .string()
      .nullish()
      .describe('Meeting link capability, when joined using a link.'),
    token: zod.string().describe('The RTC token for connecting to the room.'),
  })
  .describe('Response returned when creating or joining a call.');

/**
 * @summary Handle `POST /call/join/{token}/leave` through the call domain service.
 */
export const meetingLeaveParams = zod.object({
  token: zod.string(),
});

export const meetingLeaveResponse = zod
  .object({
    callEnded: zod
      .boolean()
      .describe('Whether the entire call was ended (room deleted).'),
  })
  .describe('Response for the leave\/end call operation.');

/**
 * @summary Read a standalone meeting preview without admitting a guest to the room.
 */
export const meetingGuestParticipantsParams = zod.object({
  token: zod.string(),
});

export const meetingGuestParticipantsResponse = zod
  .object({
    participants: zod
      .array(
        zod
          .object({
            avatarUrl: zod
              .string()
              .nullish()
              .describe('Profile image, when available for a Macro member.'),
            displayName: zod
              .string()
              .describe('Name displayed in the waiting room.'),
          })
          .describe(
            'Minimal waiting-room display data, without account identities or call content.'
          )
      )
      .describe('Human attendees only; transcription agents are excluded.'),
  })
  .describe("People currently connected to a meeting's room.");

/**
 * @summary Handle `GET /call/meetings` through the call domain service.
 */
export const meetingListResponse = zod
  .object({
    meetings: zod
      .array(
        zod
          .object({
            callId: zod
              .uuid()
              .nullish()
              .describe('Currently active call session, if any.'),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Associated channel, for links to existing channel calls only.'
              ),
            id: zod.uuid().describe('Persistent meeting identifier.'),
            scheduledEnd: zod.iso
              .datetime({})
              .nullish()
              .describe('Scheduled end, or none for an instant meeting.'),
            scheduledStart: zod.iso
              .datetime({})
              .nullish()
              .describe('Scheduled start, or none for an instant meeting.'),
            shareToken: zod
              .string()
              .describe(
                "A bearer capability that grants access only to a meeting's RTC room."
              ),
            title: zod.string().describe('Human-readable meeting title.'),
          })
          .describe(
            'Persistent meeting metadata. No channel contents or archived media are exposed.'
          )
      )
      .describe('Persistent meeting invitations, most recently created first.'),
  })
  .describe(
    'Uncancelled standalone meetings visible in the requested meeting list.'
  );

/**
 * @summary Handle `POST /call/meetings` through the call domain service.
 */
export const meetingCreateBody = zod
  .object({
    preparationId: zod
      .uuid()
      .nullish()
      .describe(
        'Optional unused room reserved by this actor on the setup screen.'
      ),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Optional scheduled end.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Optional scheduled start.'),
    title: zod.string().nullish().describe('Optional display title.'),
  })
  .describe('Inputs for creating a meeting without starting its RTC room.');

export const meetingCreateResponse = zod
  .object({
    callId: zod
      .uuid()
      .nullish()
      .describe('Currently active call session, if any.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe(
        'Associated channel, for links to existing channel calls only.'
      ),
    id: zod.uuid().describe('Persistent meeting identifier.'),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled end, or none for an instant meeting.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled start, or none for an instant meeting.'),
    shareToken: zod
      .string()
      .describe(
        "A bearer capability that grants access only to a meeting's RTC room."
      ),
    title: zod.string().describe('Human-readable meeting title.'),
  })
  .describe(
    'Persistent meeting metadata. No channel contents or archived media are exposed.'
  );

/**
 * @summary List the authenticated actor's active quick calls.
 */
export const meetingListActiveResponse = zod
  .object({
    meetings: zod
      .array(
        zod
          .object({
            callId: zod
              .uuid()
              .nullish()
              .describe('Currently active call session, if any.'),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Associated channel, for links to existing channel calls only.'
              ),
            id: zod.uuid().describe('Persistent meeting identifier.'),
            scheduledEnd: zod.iso
              .datetime({})
              .nullish()
              .describe('Scheduled end, or none for an instant meeting.'),
            scheduledStart: zod.iso
              .datetime({})
              .nullish()
              .describe('Scheduled start, or none for an instant meeting.'),
            shareToken: zod
              .string()
              .describe(
                "A bearer capability that grants access only to a meeting's RTC room."
              ),
            title: zod.string().describe('Human-readable meeting title.'),
          })
          .describe(
            'Persistent meeting metadata. No channel contents or archived media are exposed.'
          )
          .and(
            zod.object({
              createdBy: zod
                .string()
                .describe(
                  'Creator identity for displaying the caller in the authenticated active list.'
                ),
            })
          )
          .describe(
            'Active quick-call metadata available to its authenticated owner or attendees.'
          )
      )
      .describe(
        'Persistent meeting invitations for currently active sessions.'
      ),
  })
  .describe(
    "The authenticated actor's active quick calls, including their creators."
  );

/**
 * @summary Read the authenticated caller's invitation capability without exposing the creator.
 */
export const meetingInvitePermissionsParams = zod.object({
  token: zod.string(),
});

export const meetingInvitePermissionsResponse = zod
  .object({
    canInvite: zod
      .boolean()
      .describe('True for the owner of an uncancelled standalone meeting.'),
  })
  .describe(
    'Whether the authenticated caller can invite teammates to this meeting.'
  );

/**
 * @summary Queue an owner-authorized guest invitation email.
 */
export const meetingInviteParams = zod.object({
  token: zod.string(),
});

export const meetingInviteBody = zod
  .object({
    email: zod
      .string()
      .describe('Recipient email; no Macro account is required.'),
  })
  .describe('A single email recipient for a call invitation.');

/**
 * @summary Ring registered teammates selected by the standalone meeting owner.
 */
export const meetingInviteUsersParams = zod.object({
  token: zod.string(),
});

export const meetingInviteUsersBody = zod
  .object({
    userIds: zod
      .array(zod.string())
      .describe(
        'Human user principals; bot principals and historical bare bot UUIDs are invalid.'
      ),
  })
  .describe('Registered teammates selected for an incoming call invitation.');

/**
 * @summary Handle `POST /call/meetings/join/{token}` through the call domain service.
 */
export const meetingJoinParams = zod.object({
  token: zod.string(),
});

export const meetingJoinResponse = zod
  .object({
    callId: zod.uuid().describe('The call identifier.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe('The channel this call is associated with.'),
    participantId: zod.string().describe('RTC participant identity.'),
    roomName: zod.string().describe('The RTC room name.'),
    serverUrl: zod
      .string()
      .describe('The RTC server URL for the frontend SDK to connect to.'),
    shareToken: zod
      .string()
      .nullish()
      .describe('Meeting link capability, when joined using a link.'),
    token: zod.string().describe('The RTC token for connecting to the room.'),
  })
  .describe('Response returned when creating or joining a call.');

/**
 * @summary Read the participant preview for an authenticated invitation holder.
 */
export const meetingParticipantsParams = zod.object({
  token: zod.string(),
});

export const meetingParticipantsResponse = zod
  .object({
    participants: zod
      .array(
        zod
          .object({
            avatarUrl: zod
              .string()
              .nullish()
              .describe('Profile image, when available for a Macro member.'),
            displayName: zod
              .string()
              .describe('Name displayed in the waiting room.'),
          })
          .describe(
            'Minimal waiting-room display data, without account identities or call content.'
          )
      )
      .describe('Human attendees only; transcription agents are excluded.'),
  })
  .describe("People currently connected to a meeting's room.");

/**
 * @summary Reserve a room while the authenticated caller configures a new call.
 */
export const meetingPrepareResponse = zod
  .object({
    expiresAt: zod.iso
      .datetime({})
      .describe(
        'After this deadline Start falls back to ordinary room creation.'
      ),
    id: zod
      .uuid()
      .describe("Also the reserved RTC room's UUID and prospective call ID."),
  })
  .describe('An empty room reservation, not a started call or an invitation.');

/**
 * @summary Cancel an unused room; the domain owns ownership and activation checks.
 */
export const meetingCancelPreparationParams = zod.object({
  preparation_id: zod.uuid(),
});

/**
 * @summary Handle `DELETE /call/meetings/{meeting_id}` through the call domain service.
 */
export const meetingCancelParams = zod.object({
  meeting_id: zod.uuid(),
});

/**
 * @summary Handle owner-authorized meeting title and schedule edits.
 */
export const meetingUpdateParams = zod.object({
  meeting_id: zod.uuid(),
});

export const meetingUpdateBody = zod
  .object({
    clearSchedule: zod
      .boolean()
      .optional()
      .describe(
        'Remove timed scheduling, for example when the calendar event becomes all-day.'
      ),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Replacement scheduled end; requires a matching start.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Replacement scheduled start; requires a matching end.'),
    title: zod.string().nullish().describe('Replacement title, when supplied.'),
  })
  .describe(
    "Changes to a meeting's title or scheduled time; omitted values stay unchanged."
  );

export const meetingUpdateResponse = zod
  .object({
    callId: zod
      .uuid()
      .nullish()
      .describe('Currently active call session, if any.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe(
        'Associated channel, for links to existing channel calls only.'
      ),
    id: zod.uuid().describe('Persistent meeting identifier.'),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled end, or none for an instant meeting.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled start, or none for an instant meeting.'),
    shareToken: zod
      .string()
      .describe(
        "A bearer capability that grants access only to a meeting's RTC room."
      ),
    title: zod.string().describe('Human-readable meeting title.'),
  })
  .describe(
    'Persistent meeting metadata. No channel contents or archived media are exposed.'
  );

/**
 * Batch-fetches lightweight previews for a list of call ids. Mirrors the
`POST /documents/preview` endpoint: no per-id access checks, duplicate
ids are deduplicated server-side, and missing ids come back as
`CallRecordPreview::DoesNotExist` rather than producing an error.
 * @summary Handler for `POST /call/record/preview`.
 */
export const getBatchCallRecordPreviewBody = zod
  .object({
    callIds: zod
      .array(zod.uuid())
      .describe(
        'The call ids to preview. Duplicate ids are deduplicated server-side.\nCapped at [`MAX_BATCH_CALL_IDS`] entries.'
      ),
  })
  .describe(
    'Request body for `POST \/call\/record\/preview`.\n\nThe `call_ids` list is bounded at [`MAX_BATCH_CALL_IDS`]; the handler\nrejects anything larger with a 400 before touching the database.'
  );

export const getBatchCallRecordPreviewResponse = zod
  .object({
    previews: zod
      .array(
        zod
          .union([
            zod
              .object({
                callId: zod.uuid().describe('The call identifier.'),
                channelId: zod
                  .uuid()
                  .nullish()
                  .describe('The channel this call belongs to.'),
                channelName: zod
                  .string()
                  .nullish()
                  .describe('Resolved display name for the channel.'),
                customName: zod
                  .string()
                  .nullish()
                  .describe(
                    'User-supplied or AI-generated display name for the call. Only set on\narchived `call_records`; active calls always return `None`.'
                  ),
                endedAt: zod.iso
                  .datetime({})
                  .nullish()
                  .describe('When the call ended (None if still active).'),
                startedAt: zod.iso
                  .datetime({})
                  .describe(
                    'When the call started (created_at for active, started_at for archived).'
                  ),
              })
              .describe('Preview payload returned for each found call id.')
              .and(
                zod.object({
                  type: zod.enum(['exists']),
                })
              )
              .describe(
                'The call exists (in either the active or archived table).'
              ),
            zod
              .object({
                callId: zod.uuid().describe('The call identifier.'),
              })
              .describe(
                'Wrapper carrying just a call id. Used by the [`CallRecordPreview::DoesNotExist`]\nvariant.'
              )
              .and(
                zod.object({
                  type: zod.enum(['does_not_exist']),
                })
              )
              .describe(
                'No call with this id exists in either the active or archived tables.'
              ),
          ])
          .describe(
            'Lightweight preview of a call record, returned by the batch preview endpoint.\n\nEach requested id resolves to one of two outcomes: the call exists\n(`Exists`) or it does not (`DoesNotExist`). The endpoint does not perform\naccess checks, so there is no separate \"not authorized\" variant.'
          )
      )
      .describe('One entry per deduplicated input id.'),
  })
  .describe('Response body for `POST \/call\/record\/preview`.');

/**
 * Returns the full [`CallRecord`] (metadata + participants + transcript)
for a call identified by its own id. Covers both active and archived calls.
Access is validated via channel membership (MemberParticipantRole).
 * @summary Handler for `GET /call/record/{call_id}`.
 */
export const getCallRecordParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

export const getCallRecordResponse = zod
  .object({
    callId: zod.uuid().describe('The call identifier.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe('The channel this call belongs to.'),
    channelName: zod
      .string()
      .nullish()
      .describe('Resolved display name for the channel.'),
    createdBy: zod.string().describe('User who created the call.'),
    customName: zod
      .string()
      .nullish()
      .describe(
        'User-supplied or AI-generated display name for the call. Only set on\narchived `call_records`; active calls always return `None`.'
      ),
    durationMs: zod
      .number()
      .nullish()
      .describe('Call duration in milliseconds (None if still active).'),
    egressId: zod.string().nullish().describe('Recording egress ID, if any.'),
    endedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When the call ended (None if still active).'),
    guests: zod
      .array(
        zod
          .object({
            displayName: zod.string().describe('Guest-provided display name.'),
            id: zod
              .uuid()
              .describe(
                "A non-account guest of a single call session.\n\nThe id doubles as the guest's RTC participant identity, so identities are\nopaque UUIDs and never share a namespace (or a column) with Macro user\nids. Only the server mints them; Macro users keep `macro|…` identities,\nso an RTC identity classifies as exactly one of the two."
              ),
            joinedAt: zod.iso
              .datetime({})
              .describe('When the guest joined the call.'),
            leftAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the guest left (None if still in an active call).'
              ),
          })
          .describe('A non-account guest as returned in a [`CallRecord`].')
      )
      .describe(
        'Non-account guests (both active and historic). Guests only ever exist\non standalone meeting calls, never on channel calls.'
      ),
    isActive: zod
      .boolean()
      .describe('Whether the call is currently active (from `calls` table).'),
    participants: zod
      .array(
        zod
          .object({
            joinedAt: zod.iso
              .datetime({})
              .describe('When the user joined the call.'),
            leftAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the user left (None if still in an active call).'
              ),
            userId: zod.string().describe('The Macro user id.'),
          })
          .describe(
            'A participant as returned in a [`CallRecord`] (historic — includes `left_at`).'
          )
      )
      .describe('Macro-account participants (both active and historic).'),
    recordingPreviewUrl: zod
      .string()
      .nullish()
      .describe(
        'Presigned URL for the call recording preview image, if available.'
      ),
    recordingStartedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'When the egress recording actually began. `None` until the\n`egress_started` webhook arrives (typically a few seconds after\n`started_at`). Frontend should anchor transcript-to-audio sync to\nthis value when present, falling back to `started_at` otherwise.'
      ),
    recordingUrl: zod
      .string()
      .nullish()
      .describe('Presigned URL for the call recording, if available.'),
    roomName: zod.string().describe('The RTC room name.'),
    shareWithTeam: zod
      .boolean()
      .describe(
        "Whether the call is shared with the creator's team. While the call is\nlive this is the pending toggle applied at archive; afterwards it\nmirrors `team_share_access_level`."
      ),
    startedAt: zod.iso
      .datetime({})
      .describe(
        'When the call started (created_at for active, started_at for archived).'
      ),
    status: zod
      .union([
        zod.null(),
        zod
          .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
          .describe(
            'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
          ),
      ])
      .optional(),
    summary: zod
      .string()
      .nullish()
      .describe(
        'AI-generated summary of the call. Only set on archived `call_records`\nonce summarization has run; active calls always return `None`.'
      ),
    teamShareAccessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    transcript: zod
      .array(
        zod
          .object({
            content: zod.string().describe('The transcribed text content.'),
            diarizedSpeakerId: zod
              .string()
              .nullish()
              .describe(
                "Stable per-speaker identifier produced by the STT provider's diarization\npass. Unique across tracks in the call. `None` when the provider didn't\nreturn a speaker label."
              ),
            endedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the speaker stopped (if known).'),
            segmentId: zod
              .string()
              .nullish()
              .describe('LiveKit segment ID (nullable for archived records).'),
            sequenceNum: zod.number().describe('Ordering within the call.'),
            speakerId: zod.string().describe("The speaker's user ID."),
            startedAt: zod.iso
              .datetime({})
              .describe('When the speaker started this segment.'),
            transcriptId: zod
              .uuid()
              .describe('Stable DB-row id for the segment.'),
          })
          .describe('A transcript segment as returned in a [`CallRecord`].')
      )
      .describe('Transcript segments ordered by `sequence_num`.'),
    userAccessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    viewerHasDeclined: zod
      .boolean()
      .optional()
      .describe(
        'Whether the authenticated viewer has declined this call on any device.\nSet on the single-record read so clients that missed `call_declined`\ncan still stop ringing via reconciliation; `false` in list contexts.'
      ),
  })
  .describe(
    'Full record of a call, unifying rows from `calls` (active) and\n`call_records` (archived) into a single response shape.'
  );

/**
 * Deletes a call record (and its participants/transcripts via cascade).
Access is validated via channel membership (MemberParticipantRole).
 * @summary Handler for `DELETE /call/record/{call_id}`.
 */
export const deleteCallRecordParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

/**
 * Edits a call record: link/channel share permissions, display name, and
team sharing. Edit access (channel membership) is required for the request.
`sharePermission.teamShareAccessLevel` only accepts `view` or `null`; while
the call is live it sets the pending share-with-team toggle, and once the
call is archived it is additionally authorized against the call's creator.
 * @summary Handler for `PATCH /call/record/{call_id}`.
 */
export const editCallRecordParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

export const editCallRecordBody = zod
  .object({
    customName: zod
      .string()
      .nullish()
      .describe(
        'Updated user-supplied display name for the call. `None` is a no-op;\n`Some(\"\")` clears `call_records.custom_name`; any other `Some(s)`\noverwrites it with `s`. Only the archived `call_records` row carries\nthis column — patching while the call is still active is a no-op for\nthis field.'
      ),
    sharePermission: zod
      .union([
        zod.null(),
        zod.object({
          channelSharePermissions: zod
            .array(
              zod.object({
                accessLevel: zod
                  .union([
                    zod.null(),
                    zod
                      .enum(['view', 'comment', 'edit', 'owner'])
                      .describe(
                        'Ordered from least to most access top -> bottom'
                      ),
                  ])
                  .optional(),
                channelId: zod.string().describe('The channel id'),
                operation: zod.enum(['add', 'remove', 'replace']),
              })
            )
            .nullish()
            .describe(
              'Any channel share permissions to be created\/updated\/removed'
            ),
          linkShare: zod
            .union([
              zod.null(),
              zod
                .enum(['PUBLIC', 'TEAM'])
                .describe(
                  'Defines who can access an item through its share link.'
                ),
            ])
            .optional(),
          linkShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
          teamShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
        }),
      ])
      .optional(),
    shareWithTeam: zod
      .boolean()
      .nullish()
      .describe(
        'Deprecated alias for `sharePermission.teamShareAccessLevel`:\n`Some(true)` behaves like `\"view\"`, `Some(false)` like `null`, and\n`None` is a no-op. Supplying both with disagreeing values is rejected.\nThe team is resolved from the call\'s `created_by`, not the acting user.'
      ),
  })
  .describe('Edit call request, as supplied by inbound callers.');

/**
 * Declines the identified active call for the caller without joining it.
Bound to `call_id` so a stale incoming-call UI cannot decline a newer call
that replaced it in the same channel. The caller's other devices are told
to stop ringing (`call_declined`); the call continues for everyone else.
 * @summary Handler for `POST /call/record/{call_id}/decline`.
 */
export const declineCallParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

/**
 * @summary Handle `POST /call/record/{call_id}/link` through the call domain service.
 */
export const meetingShareParams = zod.object({
  call_id: zod.uuid(),
});

export const meetingShareResponse = zod
  .object({
    callId: zod
      .uuid()
      .nullish()
      .describe('Currently active call session, if any.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe(
        'Associated channel, for links to existing channel calls only.'
      ),
    id: zod.uuid().describe('Persistent meeting identifier.'),
    scheduledEnd: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled end, or none for an instant meeting.'),
    scheduledStart: zod.iso
      .datetime({})
      .nullish()
      .describe('Scheduled start, or none for an instant meeting.'),
    shareToken: zod
      .string()
      .describe(
        "A bearer capability that grants access only to a meeting's RTC room."
      ),
    title: zod.string().describe('Human-readable meeting title.'),
  })
  .describe(
    'Persistent meeting metadata. No channel contents or archived media are exposed.'
  );

/**
 * Flips the live call's share-with-team toggle and returns the new value as
the JSON body. The toggle is applied as canonical team sharing (View for
the creator's team) when the call is archived; archived calls answer 409
and are edited through `PATCH /call/record/{call_id}` instead.
 * @summary Handler for `POST /call/record/{call_id}/share-with-team/toggle`.
 */
export const toggleShareWithTeamParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

export const toggleShareWithTeamResponse = zod.boolean();

/**
 * Applies per-diarized-speaker `custom_speaker` overrides to the call's
archived transcript rows. Auth uses the same `EditAccessLevel` extractor
as `edit_call_record_handler`.
 * @summary Handler for `PATCH /call/record/{call_id}/transcript`.
 */
export const editCallTranscriptParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

export const editCallTranscriptBody = zod
  .object({
    assignments: zod
      .array(
        zod
          .object({
            customSpeaker: zod
              .string()
              .describe(
                'Macro user id to attribute matching rows to, or `None` to clear.'
              ),
            diarizedSpeakerId: zod
              .string()
              .describe(
                'The diarization label whose rows this assignment targets.'
              ),
          })
          .describe(
            'One per-diarized-speaker override, used in [`EditCallTranscriptRequest`].\n\n`custom_speaker = None` clears any existing override for this\n`diarized_speaker_id`; `Some(macro_user_id)` sets it. The string is\nexpected to parse as a `MacroUserId` (e.g. `macro|alice@example.com`);\nthe service layer rejects malformed values with `400 Bad Request`.'
          )
      )
      .describe('The set of per-diarized-speaker overrides to apply.'),
  })
  .describe(
    'Body of `PATCH \/call\/record\/{call_id}\/transcript`.\n\nEach entry in `assignments` sets (or clears, when `custom_speaker` is\n`None`) the `custom_speaker` override for every transcript row in the\ncall whose `diarized_speaker_id` matches. Diarized speakers not listed\nare left untouched. Empty `assignments` is a 204 no-op.'
  );

/**
 * Reports whether the authenticated user should keep ringing for the call.
Polled by native clients while the CallKit incoming-call UI is showing, so
a ring can be cancelled when the user answers on another device
(`answered`) or the call ends before anyone answers (`ended`).

Outside the user-auth layer: the bearer credential is the recipient's
LiveKit JWT from the VoIP push payload, verified with the LiveKit secret.
 * @summary Handler for `GET /call/ring-status/{call_id}`.
 */
export const getRingStatusParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

export const getRingStatusResponse = zod
  .object({
    status: zod
      .enum(['ringing', 'answered', 'declined', 'ended'])
      .describe(
        'Per-user status of an incoming-call ring, as reported by the\nring-status endpoint while a native client is ringing.'
      ),
  })
  .describe('Response body for `GET \/call\/ring-status\/{call_id}`.');

/**
 * Declines a ringing call from a native client, e.g. the iPhone lock-screen
decline button, so the user's other devices stop ringing. Authorized like
[`ring_status_handler`]: the bearer credential is the recipient's LiveKit
JWT from the VoIP push payload, which identifies both the room and the
declining user.
 * @summary Handler for `POST /call/ring-status/{call_id}/decline`.
 */
export const declineRingParams = zod.object({
  call_id: zod.uuid().describe('Call ID'),
});

/**
 * Gets or creates a call for the channel. If a call already exists, joins it;
otherwise creates a new one. Always returns a join token.
 * @summary Handler for `GET /call/{channel_id}`.
 */
export const getOrCreateCallParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const getOrCreateCallResponse = zod
  .object({
    callId: zod.uuid().describe('The call identifier.'),
    channelId: zod
      .uuid()
      .nullish()
      .describe('The channel this call is associated with.'),
    participantId: zod.string().describe('RTC participant identity.'),
    roomName: zod.string().describe('The RTC room name.'),
    serverUrl: zod
      .string()
      .describe('The RTC server URL for the frontend SDK to connect to.'),
    shareToken: zod
      .string()
      .nullish()
      .describe('Meeting link capability, when joined using a link.'),
    token: zod.string().describe('The RTC token for connecting to the room.'),
  })
  .describe('Response returned when creating or joining a call.');

/**
 * @summary Handler for `DELETE /call/{channel_id}`.
 */
export const leaveOrEndCallParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const leaveOrEndCallResponse = zod
  .object({
    callEnded: zod
      .boolean()
      .describe('Whether the entire call was ended (room deleted).'),
  })
  .describe('Response for the leave\/end call operation.');

/**
 * Returns 200 with call info if an active call exists, or 204 No Content if not.
 * @summary Handler for `GET /call/{channel_id}/active`.
 */
export const checkActiveCallParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const checkActiveCallResponse = zod
  .object({
    callId: zod.uuid().describe('The call identifier.'),
    channelId: zod.uuid().describe('The channel this call belongs to.'),
    createdAt: zod.iso.datetime({}).describe('When the call was created.'),
    createdBy: zod.string().describe('User who created the call.'),
  })
  .describe('Response indicating whether an active call exists for a channel.');

/**
 * Receives transcript segments from the transcription agent.
Authenticated via the `x-macro-internal-call` shared secret.
Duplicate segments (same `segment_id`) are ignored.
 * @summary Handler for `POST /call/{channel_id}/transcript`.
 */
export const ingestTranscriptParams = zod.object({
  room_name: zod
    .uuid()
    .describe(
      'RTC room name; the transcription agent passes its LiveKit room verbatim'
    ),
});

export const ingestTranscriptBody = zod
  .object({
    content: zod.string().describe('The transcribed text content.'),
    diarizedSpeakerId: zod
      .string()
      .nullish()
      .describe(
        "Stable per-speaker identifier produced by the STT provider's diarization\npass. Namespaced upstream by audio track so values are unique across all\ntracks in a call. `None` when the provider didn't return a speaker label."
      ),
    embedding: zod
      .array(zod.number())
      .nullish()
      .describe(
        'Speaker voice embedding computed by the transcription agent\n(e.g. a Resemblyzer 256-dim vector). When present, the server\nupserts a `voice` row and stores the resulting id on the transcript\nsegment so the call-finished pipeline can match it to enrolled users.'
      ),
    endedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When the speaker stopped talking for this segment.'),
    isFinal: zod
      .boolean()
      .describe('Whether this is a final transcription (not interim).'),
    segmentId: zod
      .string()
      .describe(
        'LiveKit segment ID (used for deduplication across multiple submitters).'
      ),
    speakerId: zod
      .string()
      .describe("The speaker's user ID (from `lk.transcribed_track_id`)."),
    startedAt: zod.iso
      .datetime({})
      .describe('When the speaker started talking for this segment.'),
    streamStartedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        "Wall-clock when the transcriber's STT stream first received audio\nfor this participant. The server takes the earliest non-null value\nacross all participants and uses it to overwrite the\n`egress_started`-derived `recording_started_at`, which is too early\n(it stamps egress bootstrap, not first audio frame)."
      ),
  })
  .describe('A transcript segment from LiveKit Inference STT.');

/**
 * @summary List the caller's shared or private labels.
 */
export const listChannelLabelsResponse = zod
  .object({
    labels: zod
      .array(
        zod
          .object({
            channelCount: zod
              .number()
              .describe(
                'All assignments for a manual label; visible matches for a smart tag.'
              ),
            channelIds: zod
              .array(zod.uuid())
              .describe(
                'Channels in this label that the requesting user participates in.'
              ),
            createdAt: zod.iso
              .datetime({})
              .describe('When the label was created.'),
            id: zod.uuid().describe('Stable label id.'),
            name: zod
              .string()
              .describe(
                'Display name, unique within the scope (case-insensitive).'
              ),
            rule: zod
              .union([
                zod.null(),
                zod
                  .object({
                    attribute: zod.enum(['name']),
                    contains: zod
                      .string()
                      .describe('The substring to find anywhere in the name.'),
                  })
                  .describe(
                    'Case-insensitive, literal substring matching on the channel name.'
                  )
                  .describe(
                    'An attribute rule that automatically groups matching channels.'
                  ),
              ])
              .optional(),
            sortOrder: zod
              .number()
              .describe(
                'Manual ordering value within the scope; lower sorts first.'
              ),
            teamId: zod
              .uuid()
              .nullish()
              .describe('Owning team, or `None` for account-private labels.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the label was last renamed or reordered.'),
          })
          .describe(
            'A shared or account-private label grouping chat channels in the sidebar.\n\n`channel_ids` is viewer-relative: it lists only the labelled channels the\nrequesting user participates in. `channel_count` counts every channel in\nthe label so clients can warn accurately before a delete.'
          )
      )
      .describe(
        'Every label of the scope, whether or not the caller sees channels in it.'
      ),
    teamId: zod
      .uuid()
      .nullish()
      .describe('Team scope, or `None` for private labels.'),
  })
  .describe("The authorized scope's labels in manual order.");

/**
 * @summary Create a label for the caller's authorized scope.
 */
export const createChannelLabelBody = zod
  .object({
    channelIds: zod
      .array(zod.uuid())
      .optional()
      .describe('Channels to move into the new label.'),
    name: zod
      .string()
      .describe('Display name; unique within the scope, case-insensitively.'),
    rule: zod
      .union([
        zod.null(),
        zod
          .object({
            attribute: zod.enum(['name']),
            contains: zod
              .string()
              .describe('The substring to find anywhere in the name.'),
          })
          .describe(
            'Case-insensitive, literal substring matching on the channel name.'
          )
          .describe(
            'An attribute rule that automatically groups matching channels.'
          ),
      ])
      .optional(),
  })
  .describe('Request body for creating a label.');

export const createChannelLabelResponse = zod
  .object({
    channelCount: zod
      .number()
      .describe(
        'All assignments for a manual label; visible matches for a smart tag.'
      ),
    channelIds: zod
      .array(zod.uuid())
      .describe(
        'Channels in this label that the requesting user participates in.'
      ),
    createdAt: zod.iso.datetime({}).describe('When the label was created.'),
    id: zod.uuid().describe('Stable label id.'),
    name: zod
      .string()
      .describe('Display name, unique within the scope (case-insensitive).'),
    rule: zod
      .union([
        zod.null(),
        zod
          .object({
            attribute: zod.enum(['name']),
            contains: zod
              .string()
              .describe('The substring to find anywhere in the name.'),
          })
          .describe(
            'Case-insensitive, literal substring matching on the channel name.'
          )
          .describe(
            'An attribute rule that automatically groups matching channels.'
          ),
      ])
      .optional(),
    sortOrder: zod
      .number()
      .describe('Manual ordering value within the scope; lower sorts first.'),
    teamId: zod
      .uuid()
      .nullish()
      .describe('Owning team, or `None` for account-private labels.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the label was last renamed or reordered.'),
  })
  .describe(
    'A shared or account-private label grouping chat channels in the sidebar.\n\n`channel_ids` is viewer-relative: it lists only the labelled channels the\nrequesting user participates in. `channel_count` counts every channel in\nthe label so clients can warn accurately before a delete.'
  );

/**
 * @summary Move a channel into a label of the caller's authorized scope, or out of any label.
 */
export const setChannelLabelParams = zod.object({
  channel_id: zod.uuid().describe('The channel id.'),
});

export const setChannelLabelBody = zod
  .object({
    labelId: zod
      .uuid()
      .nullish()
      .describe(
        'The label to put the channel in, or `null` to remove it from its label.'
      ),
  })
  .describe('Request body for moving a channel between labels.');

/**
 * @summary Preview visible channels matched by a smart tag, without creating it.
 */
export const previewSmartTagBody = zod
  .object({
    attribute: zod.enum(['name']),
    contains: zod
      .string()
      .describe('The substring to find anywhere in the name.'),
  })
  .describe('Case-insensitive, literal substring matching on the channel name.')
  .describe('An attribute rule that automatically groups matching channels.');

export const previewSmartTagResponse = zod
  .object({
    channels: zod
      .array(
        zod
          .object({
            id: zod.uuid().describe('Channel id.'),
            name: zod.string().describe('Channel display name.'),
          })
          .describe(
            'A channel visible to the caller that matches a smart tag rule.'
          )
      )
      .describe('First matches, in alphabetical order.'),
    totalCount: zod
      .number()
      .describe(
        'Number of matching channels the caller participates in, including overflow.'
      ),
  })
  .describe(
    'A bounded preview and the total number of visible channels matching a rule.'
  );

/**
 * @summary Delete a label of the caller's authorized scope. Its channels return to the plain list.
 */
export const deleteChannelLabelParams = zod.object({
  label_id: zod.uuid().describe('The label id.'),
});

/**
 * @summary Rename a label of the caller's authorized scope.
 */
export const renameChannelLabelParams = zod.object({
  label_id: zod.uuid().describe('The label id.'),
});

export const renameChannelLabelBody = zod
  .object({
    name: zod.string().describe('New display name.'),
    rule: zod
      .union([
        zod.null(),
        zod
          .object({
            attribute: zod.enum(['name']),
            contains: zod
              .string()
              .describe('The substring to find anywhere in the name.'),
          })
          .describe(
            'Case-insensitive, literal substring matching on the channel name.'
          )
          .describe(
            'An attribute rule that automatically groups matching channels.'
          ),
      ])
      .optional(),
  })
  .describe('Request body for renaming a label.');

export const renameChannelLabelResponse = zod
  .object({
    channelCount: zod
      .number()
      .describe(
        'All assignments for a manual label; visible matches for a smart tag.'
      ),
    channelIds: zod
      .array(zod.uuid())
      .describe(
        'Channels in this label that the requesting user participates in.'
      ),
    createdAt: zod.iso.datetime({}).describe('When the label was created.'),
    id: zod.uuid().describe('Stable label id.'),
    name: zod
      .string()
      .describe('Display name, unique within the scope (case-insensitive).'),
    rule: zod
      .union([
        zod.null(),
        zod
          .object({
            attribute: zod.enum(['name']),
            contains: zod
              .string()
              .describe('The substring to find anywhere in the name.'),
          })
          .describe(
            'Case-insensitive, literal substring matching on the channel name.'
          )
          .describe(
            'An attribute rule that automatically groups matching channels.'
          ),
      ])
      .optional(),
    sortOrder: zod
      .number()
      .describe('Manual ordering value within the scope; lower sorts first.'),
    teamId: zod
      .uuid()
      .nullish()
      .describe('Owning team, or `None` for account-private labels.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the label was last renamed or reordered.'),
  })
  .describe(
    'A shared or account-private label grouping chat channels in the sidebar.\n\n`channel_ids` is viewer-relative: it lists only the labelled channels the\nrequesting user participates in. `channel_count` counts every channel in\nthe label so clients can warn accurately before a delete.'
  );

/**
 * @summary Handler for `POST /channels`.
 */
export const createChannelBodyAutoJoinTeamDefault = false;

export const createChannelBody = zod
  .object({
    auto_join_team: zod
      .boolean()
      .optional()
      .describe(
        'Whether team members automatically join this channel. Defaults to false.'
      ),
    channel_type: zod
      .enum(['public', 'private', 'direct_message', 'team'])
      .describe('Type of channel.'),
    name: zod.string().nullish().describe('Optional channel name.'),
    participants: zod
      .array(zod.string())
      .describe('Participants to add, excluding the owner.'),
    team_id: zod.uuid().nullish().describe('Team id for team channels.'),
  })
  .describe('Request to create a channel.');

export const createChannelResponse = zod
  .object({
    id: zod.string().describe('Created channel id.'),
  })
  .describe('Response returned after creating a channel.');

/**
 * @summary Handler for `GET /channels/activity`.
 */
export const getActivityResponseItem = zod
  .object({
    channel_id: zod.uuid().describe('Channel id.'),
    created_at: zod.iso
      .datetime({})
      .describe('When the activity row was created.'),
    id: zod.uuid().describe('Activity id.'),
    interacted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('The last time the user interacted with the channel.'),
    updated_at: zod.iso
      .datetime({})
      .describe('When the activity row was last updated.'),
    user_id: zod.string().describe('User id.'),
    viewed_at: zod.iso
      .datetime({})
      .nullish()
      .describe('The last time the user viewed the channel.'),
  })
  .describe("A user's activity (view\/interaction) within a channel.");
export const getActivityResponse = zod.array(getActivityResponseItem);

/**
 * @summary Handler for `POST /channels/activity`.
 */
export const postActivityBody = zod
  .object({
    activity_type: zod
      .enum(['view', 'interact'])
      .describe('The kind of activity a user performs in a channel.'),
    channel_id: zod.string().describe('Channel id to record activity for.'),
  })
  .describe('Request body for `POST \/channels\/activity`.');

export const postActivityResponse = zod
  .object({
    channel_id: zod.uuid().describe('Channel id.'),
    created_at: zod.iso
      .datetime({})
      .describe('When the activity row was created.'),
    id: zod.uuid().describe('Activity id.'),
    interacted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('The last time the user interacted with the channel.'),
    updated_at: zod.iso
      .datetime({})
      .describe('When the activity row was last updated.'),
    user_id: zod.string().describe('User id.'),
    viewed_at: zod.iso
      .datetime({})
      .nullish()
      .describe('The last time the user viewed the channel.'),
  })
  .describe("A user's activity (view\/interaction) within a channel.");

/**
 * @summary Handler for `GET /channels/attachments/{entity_type}/{entity_id}/references`.
 */
export const getAttachmentReferencesParams = zod.object({
  entity_type: zod.string().describe('Type of the attachment entity'),
  entity_id: zod.string().describe('Id of the attachment entity'),
});

export const getAttachmentReferencesResponse = zod
  .object({
    references: zod
      .array(
        zod
          .union([
            zod
              .object({
                attachment_created_at: zod.iso
                  .datetime({})
                  .describe('When the attachment row was created.'),
                channel_id: zod
                  .uuid()
                  .describe('Channel that contains the message.'),
                channel_name: zod
                  .string()
                  .nullish()
                  .describe('Optional channel name (DMs do not have a name).'),
                message_content: zod
                  .string()
                  .describe(
                    'Full message content (might be used for preview\/snippet).'
                  ),
                message_created_at: zod.iso
                  .datetime({})
                  .describe('When the message itself was created.'),
                message_id: zod
                  .uuid()
                  .describe('Message that contains the attachment reference.'),
                sender_id: zod.string().describe('Sender of the message.'),
                thread_id: zod
                  .uuid()
                  .nullish()
                  .describe(
                    'If the message belongs to a thread this is the parent id.'
                  ),
              })
              .describe(
                'A reference to an attachment entity from a channel message.'
              )
              .and(
                zod.object({
                  reference_type: zod.enum(['channel']),
                })
              )
              .describe('Referenced from a channel message.'),
            zod
              .object({
                created_at: zod.iso
                  .datetime({})
                  .describe('When this reference was created.'),
                entity_id: zod
                  .string()
                  .describe('ID of the referenced entity.'),
                entity_type: zod
                  .string()
                  .describe('Type of the referenced entity.'),
                source_entity_id: zod
                  .string()
                  .describe('ID of the source entity.'),
                source_entity_type: zod
                  .string()
                  .describe(
                    'Type of the source entity (e.g., \"document\", \"chat\", etc.).'
                  ),
                user_id: zod
                  .string()
                  .nullish()
                  .describe(
                    'User who created this reference (optional for non-user sources).'
                  ),
              })
              .describe(
                'A reference to an attachment entity from a non-message source.'
              )
              .and(
                zod.object({
                  reference_type: zod.enum(['generic']),
                })
              )
              .describe('Referenced from any non-message source entity.'),
          ])
          .describe('An attachment reference, tagged by source kind.')
      )
      .describe('References to the requested entity, newest-first.'),
  })
  .describe('Response from the attachment-references endpoint.');

/**
 * @summary Handler for `POST /channels/get_or_create_dm`.
 */
export const getOrCreateDmBody = zod
  .object({
    recipient_id: zod
      .string()
      .describe(
        'Recipient user id (`macro|...`) or agent persona id (`bot|...`).'
      ),
  })
  .describe('Request to get or create a direct message channel.');

export const getOrCreateDmResponse = zod
  .object({
    action: zod
      .enum(['get', 'create'])
      .describe('Result of a get-or-create channel operation.'),
    channel_id: zod.string().describe('Channel id.'),
  })
  .describe('Response for get-or-create channel operations.');

/**
 * @summary Handler for `POST /channels/get_or_create_private`.
 */
export const getOrCreatePrivateBody = zod
  .object({
    recipients: zod.array(zod.string()).describe('Recipient user ids.'),
  })
  .describe('Request to get or create a private channel.');

export const getOrCreatePrivateResponse = zod
  .object({
    action: zod
      .enum(['get', 'create'])
      .describe('Result of a get-or-create channel operation.'),
    channel_id: zod.string().describe('Channel id.'),
  })
  .describe('Response for get-or-create channel operations.');

/**
 * @summary Handler for `POST /channels/join/{join_code}`.
 */
export const joinChannelByCodeParams = zod.object({
  join_code: zod.uuid().describe('Channel join code'),
});

/**
 * @summary Handler for `POST /channels/mentions`.
 */
export const createEntityMentionBody = zod
  .object({
    entity_id: zod.string().describe('Id of the mentioned entity.'),
    entity_type: zod.string().describe('Type of the mentioned entity.'),
    source_entity_id: zod
      .string()
      .describe('Id of the entity that owns the mention.'),
    source_entity_type: zod
      .string()
      .describe('Type of the entity that owns the mention.'),
  })
  .describe('Request body for `POST \/channels\/mentions`.');

/**
 * @summary Handler for `DELETE /channels/mentions/{mention_id}`.
 */
export const deleteEntityMentionParams = zod.object({
  mention_id: zod.uuid().describe('Entity mention id'),
});

export const deleteEntityMentionResponse = zod
  .object({
    deleted: zod.boolean().describe('Whether the mention was deleted.'),
  })
  .describe('Response body for `DELETE \/channels\/mentions\/{mention_id}`.');

/**
 * @summary Handler for `POST /channels/preview`.
 */
export const getBatchChannelPreviewBody = zod
  .object({
    channel_ids: zod.array(zod.string()).describe('Channel ids to look up.'),
  })
  .describe('Request for a batched channel preview lookup.');

export const getBatchChannelPreviewResponse = zod
  .object({
    previews: zod
      .array(
        zod
          .union([
            zod
              .object({
                channel_id: zod.string().describe('Channel id.'),
                channel_name: zod
                  .string()
                  .describe('Resolved channel display name.'),
                channel_type: zod
                  .enum(['public', 'private', 'direct_message', 'team'])
                  .describe('Type of channel.'),
                profile_picture_id: zod
                  .uuid()
                  .nullish()
                  .describe(
                    "Static image file used as the channel's profile picture, when accessible."
                  ),
              })
              .describe('Preview payload returned for accessible channels.')
              .and(
                zod.object({
                  type: zod.enum(['access']),
                })
              )
              .describe('Viewer can access the channel.'),
            zod
              .object({
                channel_id: zod.string().describe('Channel id.'),
              })
              .describe(
                'Preview payload returned for channels with only id information.'
              )
              .and(
                zod.object({
                  type: zod.enum(['no_access']),
                })
              )
              .describe('Viewer cannot access the channel.'),
            zod
              .object({
                channel_id: zod.string().describe('Channel id.'),
              })
              .describe(
                'Preview payload returned for channels with only id information.'
              )
              .and(
                zod.object({
                  type: zod.enum(['does_not_exist']),
                })
              )
              .describe('Channel does not exist.'),
          ])
          .describe('Preview entry for a single channel id.')
      )
      .describe('Resolved channel previews, one per requested channel id.'),
  })
  .describe('Response for a batched channel preview lookup.');

/**
 * @summary Handler for `GET /channels/{channel_id}`.
 */
export const getChannelParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const getChannelResponse = zod
  .object({
    channel_id: zod.uuid().describe('Channel id.'),
    channel_name: zod
      .string()
      .describe("Resolved display name from the viewer's perspective."),
    channel_type: zod
      .enum(['public', 'private', 'direct_message', 'team'])
      .describe('Type of channel.'),
    participants: zod
      .array(
        zod
          .object({
            channel_id: zod.uuid().describe('Channel id.'),
            joined_at: zod.iso.datetime({}).describe('When the user joined.'),
            role: zod
              .enum(['owner', 'admin', 'member'])
              .describe('Participant role in a channel.'),
            user_id: zod.string().describe('User id.'),
          })
          .describe('A channel participant.')
      )
      .describe('Active participants.'),
  })
  .describe(
    'Channel detail: metadata and active participants.\n\nMessages are read through `\/messages\/channel\/{channel_id}`.'
  );

/**
 * @summary Handler for `DELETE /channels/{channel_id}`.
 */
export const deleteChannelParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

/**
 * @summary Handler for `PATCH /channels/{channel_id}`.
 */
export const patchChannelParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const patchChannelBody = zod
  .object({
    auto_join_team: zod
      .boolean()
      .nullish()
      .describe('Whether team members should automatically join the channel.'),
    channel_name: zod.string().nullish().describe('New channel name.'),
    convert_to_team_channel: zod
      .boolean()
      .nullish()
      .describe(
        'Sets whether the channel is a team channel.\n\n`true` converts a non-team channel to a team channel, while `false`\nconverts a team channel to a private channel.'
      ),
  })
  .describe('Request to patch a channel.');

/**
 * @summary Handler for `GET /channels/{channel_id}/attachments`.
 */
export const getChannelAttachmentsParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const getChannelAttachmentsQueryLimitMin = 0;

export const getChannelAttachmentsQueryParams = zod.object({
  limit: zod
    .number()
    .min(getChannelAttachmentsQueryLimitMin)
    .optional()
    .describe('Page size (1-500, default 50)'),
  cursor: zod.string().optional().describe('Base64 encoded cursor value'),
  attachment_type: zod
    .string()
    .optional()
    .describe(
      "Filter by type: 'static' for images\/videos, 'dss' for documents"
    ),
});

export const getChannelAttachmentsResponse = zod
  .object({
    items: zod
      .array(
        zod
          .object({
            channel_id: zod.uuid().describe('Channel id.'),
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was created.'),
            entity_id: zod.string().describe('Entity id.'),
            entity_type: zod.string().describe('Type of entity.'),
            height: zod.number().nullish().describe('Height (for images).'),
            id: zod.uuid().describe('Attachment id.'),
            message_id: zod
              .uuid()
              .describe('Message id this attachment belongs to.'),
            sender_id: zod
              .string()
              .describe(
                'The user who sent the message containing this attachment.'
              ),
            width: zod.number().nullish().describe('Width (for images).'),
          })
          .describe('A channel-level attachment.')
      )
      .describe('Attachments on this page.'),
    next_cursor: zod
      .string()
      .nullish()
      .describe('Cursor for the next page, null if no more pages.'),
  })
  .describe('Paginated response of channel attachments.');

/**
 * @summary Handler for `POST /channels/{channel_id}/bots/scoped`.
 */
export const createChannelScopedBotParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const createChannelScopedBotBody = zod
  .object({
    avatar_url: zod.string().nullish().describe('Optional avatar URL.'),
    description: zod.string().nullish().describe('Optional description.'),
    handle: zod.string().describe('Stable handle.'),
    has_agent: zod
      .boolean()
      .nullish()
      .describe(
        'Whether mentioning this bot opens a sandboxed coding-agent session. Defaults to false.'
      ),
    name: zod.string().describe('Display name.'),
    team_id: zod
      .uuid()
      .nullish()
      .describe(
        'Team owner. The caller must be a team administrator or owner. Omit for a user-owned bot.'
      ),
    token_expires_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Optional token expiration timestamp.'),
    token_label: zod.string().nullish().describe('Optional token label.'),
  })
  .describe('Request to create a bot scoped to a channel.');

/**
 * @summary Handler for `POST /channels/{channel_id}/join`.
 */
export const joinChannelParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

/**
 * @summary Handler for `GET /channels/{channel_id}/join-link`.
 */
export const getChannelJoinLinkParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const getChannelJoinLinkResponse = zod
  .object({
    join_code: zod.uuid().describe('Reusable code for joining the channel.'),
  })
  .describe("Response containing a channel's reusable join code.");

/**
 * @summary Handler for `POST /channels/{channel_id}/leave`.
 */
export const leaveChannelParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

/**
 * @summary Handler for `GET /channels/{channel_id}/participants`.
 */
export const getChannelParticipantsParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const getChannelParticipantsResponseItem = zod
  .object({
    channel_id: zod.uuid().describe('Channel id.'),
    joined_at: zod.iso.datetime({}).describe('When the user joined.'),
    role: zod
      .enum(['owner', 'admin', 'member'])
      .describe('Participant role in a channel.'),
    user_id: zod.string().describe('User id.'),
  })
  .describe('A channel participant.');
export const getChannelParticipantsResponse = zod.array(
  getChannelParticipantsResponseItem
);

/**
 * @summary Handler for `POST /channels/{channel_id}/participants`.
 */
export const addParticipantsParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const addParticipantsBody = zod
  .object({
    participants: zod.array(zod.string()).describe('User ids to add.'),
  })
  .describe('Request to add participants.');

/**
 * @summary Handler for `DELETE /channels/{channel_id}/participants`.
 */
export const removeParticipantsParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const removeParticipantsBody = zod
  .object({
    participants: zod.array(zod.string()).describe('User ids to remove.'),
  })
  .describe('Request to remove participants.');

/**
 * @summary Set a channel or group chat profile picture. Requires channel participation.
 */
export const setChannelPictureParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const setChannelPictureBody = zod
  .object({
    profile_picture_id: zod
      .uuid()
      .nullish()
      .describe(
        'Static image file id; null restores the default channel icon.'
      ),
  })
  .describe(
    "Replace a channel's picture, or remove it by sending a null file id."
  );

/**
 * @summary Handler for `POST /channels/{channel_id}/webhook`.
 */
export const postChannelBotWebhookParams = zod.object({
  channel_id: zod.uuid().describe('Channel ID'),
});

export const postChannelBotWebhookHeader = zod.object({
  'x-macro-bot-token': zod
    .string()
    .nullish()
    .describe('Preferred bot authentication token'),
  'x-macro-channel-bot-token': zod
    .string()
    .nullish()
    .describe('Legacy channel-scoped bot authentication token'),
});

export const postChannelBotWebhookBody = zod
  .object({
    content: zod.string().describe('Message body.'),
  })
  .describe('Request to post a channel webhook message.');

export const postChannelBotWebhookResponse = zod
  .object({
    message_id: zod.string().describe('Created message id.'),
  })
  .describe('Response returned after posting a channel webhook message.');

/**
 * @summary Fetch a collab surface.
 */
export const getCollabSurfaceParams = zod.object({
  id: zod.uuid().describe('The surface id.'),
});

export const getCollabSurfaceResponse = zod
  .object({
    id: zod
      .uuid()
      .describe('The surface id — also the sync-service session key.'),
    parentEntityId: zod.string().describe('Id of the parent entity.'),
    parentEntityType: zod
      .enum([
        'user',
        'chat',
        'channel',
        'channel_message',
        'document',
        'project',
        'email_thread',
        'calendar_event',
        'team',
        'call',
        'foreign_entity',
        'static_file',
        'crm_company',
        'crm_contact',
        'crm_pipeline',
        'reminder',
        'skill',
        'agent_session',
        'scheduled_action',
        'initiative',
        'database',
        'database_row',
        'form',
      ])
      .describe('The type of an entity in Macro')
      .describe('Type of the parent entity.'),
    state: zod
      .enum(['pending', 'ready'])
      .describe(
        'Lifecycle state of a collab surface.\n\n`Pending` means the row exists but the sync-service session may not: the\nrow is inserted before the durable object is initialized and flipped to\n`Ready` once the initial snapshot is stored. A persisted `Pending` row is\nan ensure that died or failed mid-init; the next ensure for the same id\nretries initialization (the initializer tolerates an already-initialized\nsession), so `Pending` is self-healing rather than terminal.'
      ),
  })
  .describe('A collab surface, as returned by the API.');

/**
 * The id is caller-supplied, so an embedding surface can hold a stable id
and blindly ensure it on mount: the first ensure creates and initializes
the session, every later one (including concurrent ones) returns the same
surface. A soft-deleted id is `410 Gone` and never comes back.
 * @summary Idempotently ensure a collab surface exists (load-or-create).
 */
export const ensureCollabSurfaceParams = zod.object({
  id: zod.uuid().describe('The surface id.'),
});

export const ensureCollabSurfaceBody = zod
  .object({
    initialMarkdown: zod
      .string()
      .optional()
      .describe(
        'Markdown to seed the surface with when this ensure creates it. Empty\n(or omitted) seeds the canonical blank document. Ignored when the\nsurface already exists and is ready.'
      ),
    parentEntityId: zod.string().describe('Id of the parent entity (a uuid).'),
    parentEntityType: zod
      .enum([
        'user',
        'chat',
        'channel',
        'channel_message',
        'document',
        'project',
        'email_thread',
        'calendar_event',
        'team',
        'call',
        'foreign_entity',
        'static_file',
        'crm_company',
        'crm_contact',
        'crm_pipeline',
        'reminder',
        'skill',
        'agent_session',
        'scheduled_action',
        'initiative',
        'database',
        'database_row',
        'form',
      ])
      .describe('The type of an entity in Macro')
      .describe('Type of the parent entity access derives from.'),
  })
  .describe('Request body for ensuring a collab surface.');

export const ensureCollabSurfaceResponse = zod
  .object({
    id: zod
      .uuid()
      .describe('The surface id — also the sync-service session key.'),
    parentEntityId: zod.string().describe('Id of the parent entity.'),
    parentEntityType: zod
      .enum([
        'user',
        'chat',
        'channel',
        'channel_message',
        'document',
        'project',
        'email_thread',
        'calendar_event',
        'team',
        'call',
        'foreign_entity',
        'static_file',
        'crm_company',
        'crm_contact',
        'crm_pipeline',
        'reminder',
        'skill',
        'agent_session',
        'scheduled_action',
        'initiative',
        'database',
        'database_row',
        'form',
      ])
      .describe('The type of an entity in Macro')
      .describe('Type of the parent entity.'),
    state: zod
      .enum(['pending', 'ready'])
      .describe(
        'Lifecycle state of a collab surface.\n\n`Pending` means the row exists but the sync-service session may not: the\nrow is inserted before the durable object is initialized and flipped to\n`Ready` once the initial snapshot is stored. A persisted `Pending` row is\nan ensure that died or failed mid-init; the next ensure for the same id\nretries initialization (the initializer tolerates an already-initialized\nsession), so `Pending` is self-healing rather than terminal.'
      ),
  })
  .describe('A collab surface, as returned by the API.');

/**
 * @summary Soft-delete a collab surface.
 */
export const deleteCollabSurfaceParams = zod.object({
  id: zod.uuid().describe('The surface id.'),
});

/**
 * @summary Mint a sync-service connection token for a surface.
 */
export const createCollabSurfaceTokenParams = zod.object({
  id: zod.uuid().describe('The surface id.'),
});

export const createCollabSurfaceTokenResponse = zod
  .object({
    token: zod
      .string()
      .describe(
        'The signed JWT to pass to the sync-service websocket connect.'
      ),
  })
  .describe(
    'Response carrying a freshly minted sync-service connection token.'
  );

/**
 * @summary Handle channel list requests for `GET /comms/channels`.
 */
export const getChannelsQueryLimitMin = 0;

export const getChannelsQueryParams = zod.object({
  limit: zod
    .number()
    .min(getChannelsQueryLimitMin)
    .optional()
    .describe('Page size (1-100, default 100)'),
  cursor: zod.string().optional().describe('Opaque cursor for the next page'),
});

export const getChannelsResponseItemsItemOrgIdMin = 0;

export const getChannelsResponse = zod
  .object({
    items: zod
      .array(
        zod
          .object({
            agent_dm: zod
              .union([
                zod.null(),
                zod
                  .object({
                    avatar_url: zod
                      .string()
                      .nullish()
                      .describe('Persona avatar, when supplied.'),
                    bot_id: zod
                      .string()
                      .describe('Persona id without the bot principal prefix.'),
                    name: zod.string().describe('Persona display name.'),
                  })
                  .describe(
                    'Historical persona identity displayed beside a private conversation.'
                  ),
              ])
              .optional(),
            auto_join_team: zod
              .boolean()
              .describe('Whether team members automatically join the channel.'),
            channel_type: zod
              .enum(['public', 'private', 'direct_message', 'team'])
              .describe('Channel type in API responses.'),
            created_at: zod.iso
              .datetime({})
              .describe('Channel creation timestamp.'),
            frecency_score: zod
              .number()
              .nullish()
              .describe('Aggregate frecency score.'),
            id: zod.uuid().describe('Channel id.'),
            interacted_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Last interaction timestamp for requesting user.'),
            is_participant: zod
              .boolean()
              .describe(
                'Whether the requesting user is an active participant of the channel.'
              ),
            latest_message: zod
              .union([
                zod.null(),
                zod
                  .object({
                    content: zod.string().describe('Message content.'),
                    created_at: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    deleted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('Deletion timestamp, if deleted.'),
                    mentions: zod
                      .array(zod.string())
                      .describe(
                        'message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`'
                      ),
                    message_id: zod.uuid().describe('Message id.'),
                    sender_id: zod.string().describe('Sender user id.'),
                    thread_id: zod
                      .uuid()
                      .nullish()
                      .describe('Thread id, if the message is a reply.'),
                    updated_at: zod.iso
                      .datetime({})
                      .describe('Update timestamp.'),
                  })
                  .describe('Channel message in API responses.'),
              ])
              .optional(),
            latest_non_thread_message: zod
              .union([
                zod.null(),
                zod
                  .object({
                    content: zod.string().describe('Message content.'),
                    created_at: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    deleted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('Deletion timestamp, if deleted.'),
                    mentions: zod
                      .array(zod.string())
                      .describe(
                        'message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`'
                      ),
                    message_id: zod.uuid().describe('Message id.'),
                    sender_id: zod.string().describe('Sender user id.'),
                    thread_id: zod
                      .uuid()
                      .nullish()
                      .describe('Thread id, if the message is a reply.'),
                    updated_at: zod.iso
                      .datetime({})
                      .describe('Update timestamp.'),
                  })
                  .describe('Channel message in API responses.'),
              ])
              .optional(),
            name: zod.string().nullish().describe('Channel name.'),
            org_id: zod
              .number()
              .min(getChannelsResponseItemsItemOrgIdMin)
              .nullish()
              .describe('Organization id.'),
            owner_id: zod.string().describe('Channel owner user id.'),
            participants: zod
              .array(
                zod
                  .object({
                    channel_id: zod.uuid().describe('id of the channel'),
                    joined_at: zod.iso
                      .datetime({})
                      .describe(
                        'timestamp of when the user joined the channel'
                      ),
                    left_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('timestamp of when the user left the channel'),
                    role: zod
                      .enum(['owner', 'admin', 'member'])
                      .describe('Participant role in API responses.'),
                    user_id: zod.string().describe('id of the user'),
                  })
                  .describe('Channel participant in API responses.')
              )
              .describe('Active participants.'),
            team_id: zod.uuid().nullish().describe('Team id.'),
            updated_at: zod.iso
              .datetime({})
              .describe('Channel last-updated timestamp.'),
            viewed_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Last viewed timestamp for requesting user.'),
          })
          .describe('Channel list response item.')
      )
      .describe('Channels in this page.'),
    next_cursor: zod
      .string()
      .nullish()
      .describe('Opaque cursor for the next page, if one exists.'),
  })
  .describe('A cursor-paginated channel list response.');

/**
 * @summary Manually create a CRM company for the caller's team. Any team member
may create one. The domain must not already be tracked by the team
(409), and the team's CRM killswitch must be on (403). Returns the
created company in the same shape as `GET /crm/companies/{id}` (its
contact list is empty until emails populate it).
 */
export const createCrmCompanyBody = zod
  .object({
    domain: zod
      .string()
      .describe(
        'The company\'s email domain, e.g. \"acme.com\". Must be a bare\ndomain (no scheme, path, or email) and not a generic email\nprovider domain.'
      ),
    name: zod
      .string()
      .describe(
        'Display name for the company. Team-scoped: overrides the\ndomain-directory name on every read path.'
      ),
  })
  .describe('Request body for `POST \/crm\/companies`.');

export const createCrmCompanyResponse = zod
  .object({
    contacts: zod
      .array(
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the contact belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the contact record was created.'),
            email: zod.string().describe("The contact's email address."),
            firstInteraction: zod.iso
              .datetime({})
              .describe('Earliest known interaction with this contact.'),
            hidden: zod
              .boolean()
              .describe(
                'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
              ),
            id: zod.uuid().describe('The id of the contact record.'),
            lastInteraction: zod.iso
              .datetime({})
              .describe('Most recent known interaction with this contact.'),
            name: zod
              .string()
              .nullish()
              .describe('Display name observed for the contact, if any.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the contact record was last updated.'),
          })
          .describe(
            'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
          )
      )
      .describe(
        'Contacts attached to this company. Hidden contacts are filtered\nout for non-admin viewers.'
      ),
    createdAt: zod.iso
      .datetime({})
      .describe('Earliest known interaction with this company.'),
    description: zod
      .string()
      .nullish()
      .describe(
        "Display description from the primary domain's directory entry."
      ),
    domains: zod
      .array(
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the domain belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the domain record was created.'),
            domain: zod.string().describe('The domain (e.g. \"acme.com\").'),
            id: zod.uuid().describe('The id of the domain record.'),
          })
          .describe('A CRM domain associated with a company.')
      )
      .describe(
        'All domains associated with this company, ordered by creation\ntime ascending (primary first).'
      ),
    emailSync: zod
      .boolean()
      .describe('Whether email sync is enabled for this company.'),
    hidden: zod
      .boolean()
      .describe(
        'Whether the company is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint 404s for them); admin\/owner callers see\nhidden companies so they can render the right toggle state.'
      ),
    id: zod.uuid().describe('The id of the company.'),
    name: zod
      .string()
      .nullish()
      .describe(
        "Display name from the primary domain's directory entry, or\n`None` when unresolved."
      ),
    teamId: zod
      .uuid()
      .describe('The id of the team that owns this company record.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('Most recent known interaction with this company.'),
  })
  .describe(
    "A CRM company as returned by `GET \/crm\/companies\/{company_id}`.\nMirrors the soup-listed `crmCompany` shape (`name` \/ `description`\nresolved from the primary domain's `crm_domain_directory` entry) and\nembeds the company's contacts so the panel can render in a single\nrequest."
  );

/**
 * @summary Fetch a single CRM company by id, hydrated with its domains, the
primary domain's directory display metadata (name + description),
and the company's contacts. Access is enforced by
[`CrmCompanyAccessLevelExtractor`]: the user must be on the team
that owns the company, and hidden companies are invisible to plain
members. Admin/owner callers see hidden companies and contacts so
they can render the right unhide UI.
 */
export const getCompanyParams = zod.object({
  company_id: zod.uuid().describe('The CRM company to fetch'),
});

export const getCompanyResponse = zod
  .object({
    contacts: zod
      .array(
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the contact belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the contact record was created.'),
            email: zod.string().describe("The contact's email address."),
            firstInteraction: zod.iso
              .datetime({})
              .describe('Earliest known interaction with this contact.'),
            hidden: zod
              .boolean()
              .describe(
                'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
              ),
            id: zod.uuid().describe('The id of the contact record.'),
            lastInteraction: zod.iso
              .datetime({})
              .describe('Most recent known interaction with this contact.'),
            name: zod
              .string()
              .nullish()
              .describe('Display name observed for the contact, if any.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the contact record was last updated.'),
          })
          .describe(
            'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
          )
      )
      .describe(
        'Contacts attached to this company. Hidden contacts are filtered\nout for non-admin viewers.'
      ),
    createdAt: zod.iso
      .datetime({})
      .describe('Earliest known interaction with this company.'),
    description: zod
      .string()
      .nullish()
      .describe(
        "Display description from the primary domain's directory entry."
      ),
    domains: zod
      .array(
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the domain belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the domain record was created.'),
            domain: zod.string().describe('The domain (e.g. \"acme.com\").'),
            id: zod.uuid().describe('The id of the domain record.'),
          })
          .describe('A CRM domain associated with a company.')
      )
      .describe(
        'All domains associated with this company, ordered by creation\ntime ascending (primary first).'
      ),
    emailSync: zod
      .boolean()
      .describe('Whether email sync is enabled for this company.'),
    hidden: zod
      .boolean()
      .describe(
        'Whether the company is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint 404s for them); admin\/owner callers see\nhidden companies so they can render the right toggle state.'
      ),
    id: zod.uuid().describe('The id of the company.'),
    name: zod
      .string()
      .nullish()
      .describe(
        "Display name from the primary domain's directory entry, or\n`None` when unresolved."
      ),
    teamId: zod
      .uuid()
      .describe('The id of the team that owns this company record.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('Most recent known interaction with this company.'),
  })
  .describe(
    "A CRM company as returned by `GET \/crm\/companies\/{company_id}`.\nMirrors the soup-listed `crmCompany` shape (`name` \/ `description`\nresolved from the primary domain's `crm_domain_directory` entry) and\nembeds the company's contacts so the panel can render in a single\nrequest."
  );

/**
 * @summary List the contacts of a CRM company. Access is enforced by
[`CrmCompanyAccessLevelExtractor`]: the user must be on the team that
owns the company. Hidden companies and hidden contacts are invisible
to plain members; admin/owner callers see hidden rows so they can
render the right unhide UI.
 */
export const listCompanyContactsParams = zod.object({
  company_id: zod.uuid().describe('The CRM company whose contacts to list'),
});

export const listCompanyContactsResponseItem = zod
  .object({
    companyId: zod
      .uuid()
      .describe('The id of the company the contact belongs to.'),
    createdAt: zod.iso
      .datetime({})
      .describe('When the contact record was created.'),
    email: zod.string().describe("The contact's email address."),
    firstInteraction: zod.iso
      .datetime({})
      .describe('Earliest known interaction with this contact.'),
    hidden: zod
      .boolean()
      .describe(
        'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
      ),
    id: zod.uuid().describe('The id of the contact record.'),
    lastInteraction: zod.iso
      .datetime({})
      .describe('Most recent known interaction with this contact.'),
    name: zod
      .string()
      .nullish()
      .describe('Display name observed for the contact, if any.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the contact record was last updated.'),
  })
  .describe(
    'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
  );
export const listCompanyContactsResponse = zod.array(
  listCompanyContactsResponseItem
);

/**
 * @summary Manually create a contact under a CRM company. Access is enforced by
[`CrmCompanyAccessLevelExtractor`]: the caller must be on the team
that owns the company (hidden companies are reachable for
admin/owner only, and the new contact then inherits `hidden`). The
email's domain must be one of the company's domains (400), the
company must not already track the email (409), and the team's CRM
killswitch must be on (403).
 */
export const createCrmContactParams = zod.object({
  company_id: zod.uuid().describe('The CRM company to add the contact to'),
});

export const createCrmContactBody = zod
  .object({
    email: zod
      .string()
      .describe(
        'The contact\'s email address, e.g. \"jane@acme.com\". Its domain\nmust be one of the company\'s domains (400 otherwise).'
      ),
    name: zod.string().describe('Display name for the contact.'),
  })
  .describe(
    'Request body for `POST \/crm\/companies\/{company_id}\/contacts`.'
  );

export const createCrmContactResponse = zod
  .object({
    companyId: zod
      .uuid()
      .describe('The id of the company the contact belongs to.'),
    createdAt: zod.iso
      .datetime({})
      .describe('When the contact record was created.'),
    email: zod.string().describe("The contact's email address."),
    firstInteraction: zod.iso
      .datetime({})
      .describe('Earliest known interaction with this contact.'),
    hidden: zod
      .boolean()
      .describe(
        'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
      ),
    id: zod.uuid().describe('The id of the contact record.'),
    lastInteraction: zod.iso
      .datetime({})
      .describe('Most recent known interaction with this contact.'),
    name: zod
      .string()
      .nullish()
      .describe('Display name observed for the contact, if any.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the contact record was last updated.'),
  })
  .describe(
    'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
  );

/**
 * @summary Toggle `email_sync` on a CRM company. Purely a visibility flag —
it gates whether team members can see each other's emails with
this company. Existing CRM data is unaffected.
 */
export const setEmailSyncParams = zod.object({
  company_id: zod.uuid().describe('The CRM company to update'),
});

export const setEmailSyncBody = zod
  .object({
    email_sync: zod
      .boolean()
      .describe(
        "New value for `crm_companies.email_sync`. Purely a read-side\nvisibility\/permission gate — `soup` queries and email-permission\nchecks require `email_sync = true` before exposing the\ncompany's emails team-wide. Populate continues to write CRM\nhistory regardless, so toggling never destroys data and\nre-enabling never requires a backfill."
      ),
  })
  .describe(
    'Request body for `PUT \/crm\/companies\/{company_id}\/email-sync`.'
  );

/**
 * @summary Toggle `hidden` on a CRM company. Hiding also disables
`email_sync` and soft-hides every contact under the company.
Un-hide restores contact visibility only; `email_sync` is left
untouched (the team must re-enable it explicitly). Contact rows
and contact sources survive the cycle.
 */
export const setCompanyHiddenParams = zod.object({
  company_id: zod.uuid().describe('The CRM company to update'),
});

export const setCompanyHiddenBody = zod
  .object({
    hidden: zod
      .boolean()
      .describe(
        'New value for `crm_companies.hidden`. Setting to `true` hides\nthe company from CRM listings, disables `email_sync`, and\nsoft-hides every contact under it (`crm_contacts.hidden = true`).\nContact rows and `crm_contact_sources` are preserved across the\ncycle, so un-hide is a true reverse. Setting to `false`\nun-hides the company and soft-restores its contacts;\n`email_sync` is left untouched and the team must re-enable it\nexplicitly.'
      ),
  })
  .describe('Request body for `PUT \/companies\/{company_id}\/hidden`.');

/**
 * @summary Rename a CRM company. Access is enforced by
[`CrmCompanyAccessLevelExtractor`]: the caller must be on the team
that owns the company (hidden companies are reachable for
admin/owner only). The name is a team-scoped override
(`custom_name`) and never touches the global domain directory.
 */
export const setCrmCompanyNameParams = zod.object({
  company_id: zod.uuid().describe('The CRM company to rename'),
});

export const setCrmCompanyNameBody = zod
  .object({
    name: zod
      .string()
      .describe(
        'New display name for the company. Stored on the team-scoped\n`crm_companies.custom_name` override, which read paths COALESCE\nover the global directory name — the shared directory is never\nmodified. Must be non-blank (400 otherwise).'
      ),
  })
  .describe('Request body for `PUT \/companies\/{company_id}\/name`.');

/**
 * @summary Search the caller's team's CRM contacts by email or name. Any team member
may search visible contacts; admin/owner callers also match hidden
contacts and contacts under hidden companies.
 */
export const searchContactsQueryLimitMin = 0;

export const searchContactsQueryParams = zod.object({
  query: zod
    .string()
    .optional()
    .describe(
      "Text the contact's email or name must contain (case-insensitive).\nEmpty lists the most recently interacted contacts."
    ),
  limit: zod
    .number()
    .min(searchContactsQueryLimitMin)
    .nullish()
    .describe('Maximum contacts to return (1-500, default 20).'),
});

export const searchContactsResponse = zod
  .object({
    contacts: zod
      .array(
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the contact belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the contact record was created.'),
            email: zod.string().describe("The contact's email address."),
            firstInteraction: zod.iso
              .datetime({})
              .describe('Earliest known interaction with this contact.'),
            hidden: zod
              .boolean()
              .describe(
                'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
              ),
            id: zod.uuid().describe('The id of the contact record.'),
            lastInteraction: zod.iso
              .datetime({})
              .describe('Most recent known interaction with this contact.'),
            name: zod
              .string()
              .nullish()
              .describe('Display name observed for the contact, if any.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the contact record was last updated.'),
          })
          .describe(
            'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
          )
      )
      .describe('Matching contacts, most recently interacted first.'),
  })
  .describe('Response from searching CRM contacts.');

/**
 * @summary Look up a CRM contact by email in the caller's team. Returns a null
`contact` when no visible contact exists. Any team member may resolve a
visible contact; admin/owner callers may also resolve hidden contacts and
contacts under hidden companies.
 */
export const getContactByEmailQueryParams = zod.object({
  email: zod
    .string()
    .describe("The contact email to resolve within the caller's team."),
});

export const getContactByEmailResponse = zod
  .object({
    contact: zod
      .union([
        zod.null(),
        zod
          .object({
            companyId: zod
              .uuid()
              .describe('The id of the company the contact belongs to.'),
            createdAt: zod.iso
              .datetime({})
              .describe('When the contact record was created.'),
            email: zod.string().describe("The contact's email address."),
            firstInteraction: zod.iso
              .datetime({})
              .describe('Earliest known interaction with this contact.'),
            hidden: zod
              .boolean()
              .describe(
                'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
              ),
            id: zod.uuid().describe('The id of the contact record.'),
            lastInteraction: zod.iso
              .datetime({})
              .describe('Most recent known interaction with this contact.'),
            name: zod
              .string()
              .nullish()
              .describe('Display name observed for the contact, if any.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the contact record was last updated.'),
          })
          .describe(
            'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
          ),
      ])
      .optional(),
  })
  .describe('Response from looking up a CRM contact by email.');

/**
 * @summary Fetch a single CRM contact by id. Access is enforced by
[`CrmContactAccessLevelExtractor`]: the user must be on the team that
owns the contact's parent company, and hidden contacts are invisible
to plain members. Admin/owner callers see hidden contacts so they can
render the right unhide UI.
 */
export const getContactParams = zod.object({
  contact_id: zod.uuid().describe('The CRM contact to fetch'),
});

export const getContactResponse = zod
  .object({
    companyId: zod
      .uuid()
      .describe('The id of the company the contact belongs to.'),
    createdAt: zod.iso
      .datetime({})
      .describe('When the contact record was created.'),
    email: zod.string().describe("The contact's email address."),
    firstInteraction: zod.iso
      .datetime({})
      .describe('Earliest known interaction with this contact.'),
    hidden: zod
      .boolean()
      .describe(
        'Whether the contact is hidden from CRM listings for the\nrequesting team. Non-admin viewers never see `hidden = true`\nrows (the endpoint filters them out); admin\/owner callers see\nhidden contacts so they can render the right toggle state.'
      ),
    id: zod.uuid().describe('The id of the contact record.'),
    lastInteraction: zod.iso
      .datetime({})
      .describe('Most recent known interaction with this contact.'),
    name: zod
      .string()
      .nullish()
      .describe('Display name observed for the contact, if any.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the contact record was last updated.'),
  })
  .describe(
    'A CRM contact as returned by `GET \/crm\/companies\/{company_id}\/contacts`.'
  );

/**
 * @summary Toggle `hidden` on a CRM contact. Hiding is a display-only opt-out
scoped to the team that owns the contact's company.
 */
export const setContactHiddenParams = zod.object({
  contact_id: zod.uuid().describe('The CRM contact to update'),
});

export const setContactHiddenBody = zod
  .object({
    hidden: zod
      .boolean()
      .describe(
        'New value for `crm_contacts.hidden`. `true` hides the contact\nfrom CRM listings for the team; `false` un-hides it. Display-only\n— does not affect populate\/depopulate.'
      ),
  })
  .describe('Request body for `PUT \/contacts\/{contact_id}\/hidden`.');

/**
 * @summary Rename a CRM contact. Access is enforced by
[`CrmContactAccessLevelExtractor`]: the caller must be on the team
that owns the contact's company (hidden contacts are reachable for
admin/owner only). The name overwrites the team-scoped
`crm_contacts.name` column.
 */
export const setCrmContactNameParams = zod.object({
  contact_id: zod.uuid().describe('The CRM contact to rename'),
});

export const setCrmContactNameBody = zod
  .object({
    name: zod
      .string()
      .describe(
        'New display name for the contact. Stored on `crm_contacts.name`,\nwhich is already team-scoped — unlike company renames no global\ndirectory is involved. Must be non-blank (400 otherwise).'
      ),
  })
  .describe('Request body for `PUT \/contacts\/{contact_id}\/name`.');

/**
 * @summary List the caller's live pipelines in their CRM.
 */
export const listCrmPipelinesResponseItem = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('Creation time.'),
    databaseId: zod.uuid().describe('Dedicated backing database.'),
    id: zod
      .uuid()
      .describe('Entity identity, used with `CrmPipeline` access receipts.'),
    name: zod.string().describe('Pipeline display name.'),
    primaryColumnId: zod.uuid().describe('Protected primary column.'),
    recordType: zod
      .enum(['company', 'contact'])
      .describe(
        'Which CRM entity each row references. Fixed when a pipeline is created.'
      ),
    sharing: zod
      .enum(['private', 'team'])
      .describe(
        'Initial sharing, or the desired team grant. Individual ownership is preserved.'
      ),
    tableId: zod.uuid().describe('Table of entries.'),
    teamId: zod
      .uuid()
      .describe(
        'Team owning the pipeline; references retain their own access controls.'
      ),
    trashedAt: zod.iso
      .datetime({})
      .nullable()
      .describe('Trashed pipelines are omitted from navigation.'),
    userId: zod
      .string()
      .describe('Owning user; sharing never changes ownership.'),
  })
  .describe(
    'Pipeline metadata; table schema and data are read through the database service.'
  )
  .and(
    zod.object({
      grant: zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    })
  )
  .describe("Metadata together with the caller's effective grant.");
export const listCrmPipelinesResponse = zod.array(listCrmPipelinesResponseItem);

/**
 * @summary Create a private or team-shared pipeline.
 */
export const createCrmPipelineBody = zod
  .object({
    name: zod.string().describe('Pipeline name.'),
    recordType: zod
      .enum(['company', 'contact'])
      .describe(
        'Which CRM entity each row references. Fixed when a pipeline is created.'
      ),
    sharing: zod
      .enum(['private', 'team'])
      .optional()
      .describe(
        'Initial sharing, or the desired team grant. Individual ownership is preserved.'
      ),
  })
  .describe(
    'Request to create an empty pipeline with the standard CRM columns.'
  );

export const createCrmPipelineResponse = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('Creation time.'),
    databaseId: zod.uuid().describe('Dedicated backing database.'),
    id: zod
      .uuid()
      .describe('Entity identity, used with `CrmPipeline` access receipts.'),
    name: zod.string().describe('Pipeline display name.'),
    primaryColumnId: zod.uuid().describe('Protected primary column.'),
    recordType: zod
      .enum(['company', 'contact'])
      .describe(
        'Which CRM entity each row references. Fixed when a pipeline is created.'
      ),
    sharing: zod
      .enum(['private', 'team'])
      .describe(
        'Initial sharing, or the desired team grant. Individual ownership is preserved.'
      ),
    tableId: zod.uuid().describe('Table of entries.'),
    teamId: zod
      .uuid()
      .describe(
        'Team owning the pipeline; references retain their own access controls.'
      ),
    trashedAt: zod.iso
      .datetime({})
      .nullable()
      .describe('Trashed pipelines are omitted from navigation.'),
    userId: zod
      .string()
      .describe('Owning user; sharing never changes ownership.'),
  })
  .describe(
    'Pipeline metadata; table schema and data are read through the database service.'
  )
  .and(
    zod.object({
      grant: zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    })
  )
  .describe("Metadata together with the caller's effective grant.");

/**
 * @summary Read one pipeline by its entity identity.
 */
export const getCrmPipelineParams = zod.object({
  id: zod.uuid(),
});

export const getCrmPipelineResponse = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('Creation time.'),
    databaseId: zod.uuid().describe('Dedicated backing database.'),
    id: zod
      .uuid()
      .describe('Entity identity, used with `CrmPipeline` access receipts.'),
    name: zod.string().describe('Pipeline display name.'),
    primaryColumnId: zod.uuid().describe('Protected primary column.'),
    recordType: zod
      .enum(['company', 'contact'])
      .describe(
        'Which CRM entity each row references. Fixed when a pipeline is created.'
      ),
    sharing: zod
      .enum(['private', 'team'])
      .describe(
        'Initial sharing, or the desired team grant. Individual ownership is preserved.'
      ),
    tableId: zod.uuid().describe('Table of entries.'),
    teamId: zod
      .uuid()
      .describe(
        'Team owning the pipeline; references retain their own access controls.'
      ),
    trashedAt: zod.iso
      .datetime({})
      .nullable()
      .describe('Trashed pipelines are omitted from navigation.'),
    userId: zod
      .string()
      .describe('Owning user; sharing never changes ownership.'),
  })
  .describe(
    'Pipeline metadata; table schema and data are read through the database service.'
  )
  .and(
    zod.object({
      grant: zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    })
  )
  .describe("Metadata together with the caller's effective grant.");

/**
 * @summary Rename a pipeline.
 */
export const renameCrmPipelineParams = zod.object({
  id: zod.uuid(),
});

export const renameCrmPipelineBody = zod
  .object({
    name: zod.string().describe('New display name.'),
  })
  .describe('Rename input.');

/**
 * @summary Edit pipeline schema or records with the common typed operations.
 */
export const applyCrmPipelineOpsParams = zod.object({
  id: zod.uuid(),
});

export const applyCrmPipelineOpsBodyOpsItemChangeViewLayoutColumnsItemWidthMin = 0;

export const applyCrmPipelineOpsBodyOpsItemChangeLayoutColumnsItemWidthMin = 0;

export const applyCrmPipelineOpsBody = zod
  .object({
    baseVersions: zod
      .record(
        zod.string(),
        zod
          .number()
          .describe(
            "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
          )
      )
      .optional()
      .describe(
        'The version each named table must still be at; the batch is refused\nas a conflict if one moved. Without one, ops are last-write-wins.'
      ),
    ops: zod
      .array(
        zod
          .union([
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['create']),
                        name: zod
                          .string()
                          .describe(
                            'Its name, unique within the database ignoring case.'
                          ),
                      })
                      .describe(
                        "Add the table, after the database's other tables. It starts with no\ncolumns and no rows."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['rename']),
                        name: zod
                          .string()
                          .describe(
                            'Its new name, unique within the database ignoring case.'
                          ),
                        previousName: zod
                          .string()
                          .optional()
                          .describe(
                            'The name the caller saw. Given, the rename is refused if the\ntable goes by another one now, so a concurrent rename is not\noverwritten.'
                          ),
                      })
                      .describe(
                        'Rename the table. Its id, columns and rows stay.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe(
                        "Remove the table with its columns, rows and views. A database keeps\nat least one table, and a table another table's relation points at\nstays until that relation goes."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['reorder_columns']),
                        order: zod
                          .array(zod.uuid())
                          .describe('Its columns, in their new order.'),
                      })
                      .describe(
                        "Set the order of the table's columns: `order` names every one of\nthem once."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['reorder_views']),
                        order: zod
                          .array(zod.uuid())
                          .describe('Its views, in their new order.'),
                      })
                      .describe(
                        "Set the order of the table's views: `order` names every one of them\nonce."
                      ),
                  ])
                  .describe('A change to a table itself.'),
                kind: zod.enum(['table']),
                table: zod
                  .uuid()
                  .describe(
                    'The table; for a creation, its new id, minted by the client, which\nlater ops of the request may name.'
                  ),
              })
              .describe(
                'A change to a table itself: its creation, name, removal, or the\norder of its columns or views.'
              ),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        after: zod
                          .uuid()
                          .optional()
                          .describe(
                            "The column it goes right after; left out, it goes after the\ntable's last column."
                          ),
                        definition: zod
                          .union([
                            zod
                              .object({
                                inferType: zod
                                  .boolean()
                                  .optional()
                                  .describe(
                                    "Let the column's first value settle its type: only for a plain\ntext column."
                                  ),
                                name: zod
                                  .string()
                                  .describe(
                                    "The column's name, unique within the table ignoring case."
                                  ),
                                options: zod
                                  .array(
                                    zod
                                      .object({
                                        id: zod
                                          .uuid()
                                          .describe(
                                            'Its id, minted by the client; later ops of the request may name it.'
                                          ),
                                        label: zod
                                          .string()
                                          .describe(
                                            "Its label, unique within the column ignoring case. A numeric\nselect's labels are numbers."
                                          ),
                                      })
                                      .describe(
                                        'A select or tag option to create.'
                                      )
                                  )
                                  .optional()
                                  .describe(
                                    'For a select or tag column, the options it starts with, in\norder, each under an id the client mints. A select column with\nnone accepts nothing until options are added.'
                                  ),
                                source: zod.enum(['new']),
                                type: zod
                                  .union([
                                    zod
                                      .object({
                                        type: zod.enum(['text']),
                                      })
                                      .describe('Free text.'),
                                    zod
                                      .object({
                                        type: zod.enum(['number']),
                                      })
                                      .describe('A number.'),
                                    zod
                                      .object({
                                        type: zod.enum(['boolean']),
                                      })
                                      .describe('A checkbox.'),
                                    zod
                                      .object({
                                        type: zod.enum(['date']),
                                      })
                                      .describe('A date-time.'),
                                    zod
                                      .object({
                                        type: zod.enum(['link']),
                                      })
                                      .describe('A URL.'),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several options.'
                                          ),
                                        type: zod.enum(['select']),
                                      })
                                      .describe('Text options.'),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several options.'
                                          ),
                                        type: zod.enum(['select_number']),
                                      })
                                      .describe('Numeric options.'),
                                    zod
                                      .object({
                                        type: zod.enum(['tag']),
                                      })
                                      .describe(
                                        'Colored labels; always several per cell.'
                                      ),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several references.'
                                          ),
                                        target: zod
                                          .enum([
                                            'USER',
                                            'DOCUMENT',
                                            'TASK',
                                            'COMPANY',
                                            'CONTACT',
                                            'CALL_RECORD',
                                            'CHANNEL',
                                            'CHAT',
                                            'PROJECT',
                                            'THREAD',
                                            'CALENDAR_EVENT',
                                            'INITIATIVE',
                                          ])
                                          .describe(
                                            'A kind of Macro entity a reference column can point at.'
                                          ),
                                        type: zod.enum(['entity']),
                                      })
                                      .describe(
                                        'References to Macro entities.'
                                      ),
                                    zod
                                      .object({
                                        database: zod
                                          .uuid()
                                          .describe(
                                            'The database of the related table.'
                                          ),
                                        table: zod
                                          .uuid()
                                          .describe('The related table.'),
                                        type: zod.enum(['relation']),
                                      })
                                      .describe('Rows of another table.'),
                                  ])
                                  .describe('A type a column can have.'),
                              })
                              .describe('A new property the database owns.'),
                            zod
                              .object({
                                formula: zod
                                  .union([
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column placement.'),
                                        kind: zod.enum(['column']),
                                      })
                                      .describe(
                                        "The row's cell in another column of the table: a number, date or\nderived column."
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['number']),
                                        value: zod
                                          .number()
                                          .describe('The value; finite.'),
                                      })
                                      .describe(
                                        'A constant. Added to or subtracted from a date, it counts days.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['binary']),
                                        left: zod.unknown(),
                                        operator: zod
                                          .enum([
                                            'add',
                                            'subtract',
                                            'multiply',
                                            'divide',
                                          ])
                                          .describe('An arithmetic operator.'),
                                        right: zod.unknown(),
                                      })
                                      .describe('Two expressions combined.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['negate']),
                                        operand: zod.unknown(),
                                      })
                                      .describe("An expression's negation."),
                                  ])
                                  .describe(
                                    "An expression over one row's cells."
                                  ),
                                name: zod
                                  .string()
                                  .describe(
                                    "The column's name, unique within the table ignoring case."
                                  ),
                                source: zod.enum(['derived']),
                              })
                              .describe(
                                "A derived column: its cells are what `formula` computes from the\nrow's other cells, and nothing writes them."
                              ),
                            zod
                              .object({
                                property: zod
                                  .uuid()
                                  .describe("The property's definition."),
                                source: zod.enum(['existing']),
                              })
                              .describe(
                                "An existing property, a person's, a team's or a system one, bound\ninto the table under its own name."
                              ),
                          ])
                          .describe('What a new column holds.'),
                        kind: zod.enum(['create']),
                      })
                      .describe(
                        'Add the column to the table: a new property the database owns, or an\nexisting one bound into the table.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['rename']),
                        name: zod
                          .string()
                          .describe(
                            'Its new name, unique within the table ignoring case.'
                          ),
                        previousName: zod
                          .string()
                          .optional()
                          .describe(
                            'The name the caller saw. Given, the rename is refused if the\ncolumn goes by another one now.'
                          ),
                      })
                      .describe(
                        'Rename the column. Its id, type and cells stay; SQL names it by its\nnew name.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['change_type']),
                        to: zod
                          .union([
                            zod
                              .object({
                                type: zod.enum(['text']),
                              })
                              .describe('Free text.'),
                            zod
                              .object({
                                type: zod.enum(['number']),
                              })
                              .describe('A number.'),
                            zod
                              .object({
                                type: zod.enum(['boolean']),
                              })
                              .describe('A checkbox.'),
                            zod
                              .object({
                                type: zod.enum(['date']),
                              })
                              .describe('A date-time.'),
                            zod
                              .object({
                                type: zod.enum(['link']),
                              })
                              .describe('A URL.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select']),
                              })
                              .describe('Text options.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select_number']),
                              })
                              .describe('Numeric options.'),
                            zod
                              .object({
                                type: zod.enum(['tag']),
                              })
                              .describe(
                                'Colored labels; always several per cell.'
                              ),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several references.'
                                  ),
                                target: zod
                                  .enum([
                                    'USER',
                                    'DOCUMENT',
                                    'TASK',
                                    'COMPANY',
                                    'CONTACT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'PROJECT',
                                    'THREAD',
                                    'CALENDAR_EVENT',
                                    'INITIATIVE',
                                  ])
                                  .describe(
                                    'A kind of Macro entity a reference column can point at.'
                                  ),
                                type: zod.enum(['entity']),
                              })
                              .describe('References to Macro entities.'),
                            zod
                              .object({
                                database: zod
                                  .uuid()
                                  .describe(
                                    'The database of the related table.'
                                  ),
                                table: zod
                                  .uuid()
                                  .describe('The related table.'),
                                type: zod.enum(['relation']),
                              })
                              .describe('Rows of another table.'),
                          ])
                          .describe('A type a column can have.'),
                      })
                      .describe(
                        'Convert the column to another type, converting its cells; its id\nsurvives the change. A value that does not fit refuses the change,\ncounting and quoting the misfits: a type change never empties a\ncell. To keep the original, create a column of the new type and\nwrite it the values that convert.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe(
                        'Remove the column and its cells. The views naming it forget it; a\nboard grouped by it must go or regroup first. A property shared\nbeyond the database stays, unbound here.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['add_options']),
                        options: zod
                          .array(
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Its id, minted by the client; later ops of the request may name it.'
                                  ),
                                label: zod
                                  .string()
                                  .describe(
                                    "Its label, unique within the column ignoring case. A numeric\nselect's labels are numbers."
                                  ),
                              })
                              .describe('A select or tag option to create.')
                          )
                          .describe(
                            'The options, each under an id the client mints.'
                          ),
                      })
                      .describe(
                        'Add options to a select or tag column, after its others. An option\nwhose label the column already has, ignoring case, is left out, so\nre-sending a list adds only what is new. Like\n[`ColumnChange::UpdateOption`], an option of a property shared beyond\nthe database goes everywhere it is used.'
                      ),
                    zod
                      .object({
                        color: zod
                          .string()
                          .nullish()
                          .describe(
                            'Its new colour, a hex string like `#RRGGBB`, or `null` to clear\nit; left out, it keeps its own. A tag option always has one.'
                          ),
                        kind: zod.enum(['update_option']),
                        label: zod
                          .string()
                          .optional()
                          .describe(
                            'Its new label; left out, it keeps its own. Labels are unique\nwithin a column, ignoring case.'
                          ),
                        option: zod.uuid().describe('The option.'),
                      })
                      .describe(
                        'Relabel or recolour one option of a select or tag column. Every cell\nholding it keeps it. A column bound to a property shared outside the\ndatabase changes wherever that property is used, so it takes the\nright to edit that property.'
                      ),
                    zod
                      .object({
                        formula: zod
                          .union([
                            zod
                              .object({
                                column: zod
                                  .uuid()
                                  .describe('The column placement.'),
                                kind: zod.enum(['column']),
                              })
                              .describe(
                                "The row's cell in another column of the table: a number, date or\nderived column."
                              ),
                            zod
                              .object({
                                kind: zod.enum(['number']),
                                value: zod
                                  .number()
                                  .describe('The value; finite.'),
                              })
                              .describe(
                                'A constant. Added to or subtracted from a date, it counts days.'
                              ),
                            zod
                              .object({
                                kind: zod.enum(['binary']),
                                left: zod.unknown(),
                                operator: zod
                                  .enum([
                                    'add',
                                    'subtract',
                                    'multiply',
                                    'divide',
                                  ])
                                  .describe('An arithmetic operator.'),
                                right: zod.unknown(),
                              })
                              .describe('Two expressions combined.'),
                            zod
                              .object({
                                kind: zod.enum(['negate']),
                                operand: zod.unknown(),
                              })
                              .describe("An expression's negation."),
                          ])
                          .describe("An expression over one row's cells."),
                        kind: zod.enum(['set_formula']),
                      })
                      .describe(
                        'Give a derived column a new formula. Its cells follow at once: it\nstores none.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete_option']),
                        option: zod.uuid().describe('The option.'),
                      })
                      .describe(
                        'Remove one option of a select or tag column, and take it out of every\ncell holding it: a single-valued cell is emptied, a multi-valued one\nkeeps its other options. Like [`ColumnChange::UpdateOption`], an\noption of a shared property goes everywhere it is used.'
                      ),
                  ])
                  .describe('A change to one column.'),
                column: zod
                  .uuid()
                  .describe(
                    'The column; for a creation, its new id, minted by the client,\nwhich later ops of the request may name.'
                  ),
                kind: zod.enum(['column']),
                table: zod.uuid().describe('The table.'),
              })
              .describe(
                'A change to one column of a table: its creation, name, type, removal\nor options.'
              ),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['insert']),
                        rows: zod
                          .array(
                            zod.array(
                              zod
                                .object({
                                  column: zod
                                    .uuid()
                                    .describe('The column placement.'),
                                  value: zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['text']),
                                          value: zod
                                            .string()
                                            .describe('Free text.'),
                                        })
                                        .describe('Free text.'),
                                      zod
                                        .object({
                                          type: zod.enum(['number']),
                                          value: zod
                                            .number()
                                            .describe('A finite number.'),
                                        })
                                        .describe('A finite number.'),
                                      zod
                                        .object({
                                          type: zod.enum(['boolean']),
                                          value: zod
                                            .boolean()
                                            .describe('A checkbox.'),
                                        })
                                        .describe('A checkbox.'),
                                      zod
                                        .object({
                                          type: zod.enum(['date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe('A date-time.'),
                                        })
                                        .describe('A date-time.'),
                                      zod
                                        .object({
                                          type: zod.enum(['link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Complete http or https URLs; at most one for a single-valued column.'
                                            ),
                                        })
                                        .describe(
                                          'Complete http or https URLs; at most one for a single-valued column.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['options']),
                                          value: zod
                                            .array(
                                              zod
                                                .union([
                                                  zod
                                                    .object({
                                                      id: zod
                                                        .uuid()
                                                        .describe(
                                                          'An option the column has.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'An option the column has.'
                                                    ),
                                                  zod
                                                    .object({
                                                      label: zod
                                                        .string()
                                                        .describe(
                                                          "An option's label, matched without regard to case. An unknown label\nis refused."
                                                        ),
                                                    })
                                                    .describe(
                                                      "An option's label, matched without regard to case. An unknown label\nis refused."
                                                    ),
                                                ])
                                                .describe(
                                                  'A select option, by its id or by its label.'
                                                )
                                            )
                                            .describe(
                                              'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                            ),
                                        })
                                        .describe(
                                          'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['entities']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entityId: zod
                                                    .string()
                                                    .describe(
                                                      "The entity's id."
                                                    ),
                                                  entityType: zod
                                                    .enum([
                                                      'USER',
                                                      'DOCUMENT',
                                                      'TASK',
                                                      'COMPANY',
                                                      'CONTACT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'PROJECT',
                                                      'THREAD',
                                                      'CALENDAR_EVENT',
                                                      'INITIATIVE',
                                                    ])
                                                    .describe(
                                                      'A kind of Macro entity a reference column can point at.'
                                                    ),
                                                })
                                                .describe(
                                                  'A reference to one Macro entity.'
                                                )
                                            )
                                            .describe(
                                              'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                            ),
                                        })
                                        .describe(
                                          'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['rows']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Rows of the table a relation column points at.'
                                            ),
                                        })
                                        .describe(
                                          'Rows of the table a relation column points at.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['clear']),
                                        })
                                        .describe(
                                          'No value: the cell is emptied.'
                                        ),
                                    ])
                                    .describe(
                                      "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                    ),
                                })
                                .describe(
                                  'One cell of a row: which column, and its new value.'
                                )
                            )
                          )
                          .describe(
                            'One entry per new row: the cells it starts with. Columns left out\nstart empty.'
                          ),
                      })
                      .describe(
                        'Append rows to the table, in order, each with the cells it starts\nwith.'
                      ),
                    zod
                      .object({
                        changes: zod
                          .union([
                            zod
                              .object({
                                cells: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column placement.'),
                                        value: zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['text']),
                                                value: zod
                                                  .string()
                                                  .describe('Free text.'),
                                              })
                                              .describe('Free text.'),
                                            zod
                                              .object({
                                                type: zod.enum(['number']),
                                                value: zod
                                                  .number()
                                                  .describe('A finite number.'),
                                              })
                                              .describe('A finite number.'),
                                            zod
                                              .object({
                                                type: zod.enum(['boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe('A checkbox.'),
                                              })
                                              .describe('A checkbox.'),
                                            zod
                                              .object({
                                                type: zod.enum(['date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe('A date-time.'),
                                              })
                                              .describe('A date-time.'),
                                            zod
                                              .object({
                                                type: zod.enum(['link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Complete http or https URLs; at most one for a single-valued column.'
                                                  ),
                                              })
                                              .describe(
                                                'Complete http or https URLs; at most one for a single-valued column.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['options']),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .union([
                                                        zod
                                                          .object({
                                                            id: zod
                                                              .uuid()
                                                              .describe(
                                                                'An option the column has.'
                                                              ),
                                                          })
                                                          .describe(
                                                            'An option the column has.'
                                                          ),
                                                        zod
                                                          .object({
                                                            label: zod
                                                              .string()
                                                              .describe(
                                                                "An option's label, matched without regard to case. An unknown label\nis refused."
                                                              ),
                                                          })
                                                          .describe(
                                                            "An option's label, matched without regard to case. An unknown label\nis refused."
                                                          ),
                                                      ])
                                                      .describe(
                                                        'A select option, by its id or by its label.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                  ),
                                              })
                                              .describe(
                                                'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['entities']),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entityId: zod
                                                          .string()
                                                          .describe(
                                                            "The entity's id."
                                                          ),
                                                        entityType: zod
                                                          .enum([
                                                            'USER',
                                                            'DOCUMENT',
                                                            'TASK',
                                                            'COMPANY',
                                                            'CONTACT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'PROJECT',
                                                            'THREAD',
                                                            'CALENDAR_EVENT',
                                                            'INITIATIVE',
                                                          ])
                                                          .describe(
                                                            'A kind of Macro entity a reference column can point at.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'A reference to one Macro entity.'
                                                      )
                                                  )
                                                  .describe(
                                                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                  ),
                                              })
                                              .describe(
                                                'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['rows']),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Rows of the table a relation column points at.'
                                                  ),
                                              })
                                              .describe(
                                                'Rows of the table a relation column points at.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['clear']),
                                              })
                                              .describe(
                                                'No value: the cell is emptied.'
                                              ),
                                          ])
                                          .describe(
                                            "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                          ),
                                      })
                                      .describe(
                                        'One cell of a row: which column, and its new value.'
                                      )
                                  )
                                  .describe('The cells each of them gets.'),
                                kind: zod.enum(['uniform']),
                                rows: zod
                                  .array(zod.uuid())
                                  .describe('The rows.'),
                              })
                              .describe('The same cells on every row.'),
                            zod
                              .object({
                                kind: zod.enum(['per_row']),
                                rows: zod
                                  .array(
                                    zod
                                      .object({
                                        cells: zod
                                          .array(
                                            zod
                                              .object({
                                                column: zod
                                                  .uuid()
                                                  .describe(
                                                    'The column placement.'
                                                  ),
                                                value: zod
                                                  .union([
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'text',
                                                        ]),
                                                        value: zod
                                                          .string()
                                                          .describe(
                                                            'Free text.'
                                                          ),
                                                      })
                                                      .describe('Free text.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'number',
                                                        ]),
                                                        value: zod
                                                          .number()
                                                          .describe(
                                                            'A finite number.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'A finite number.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'boolean',
                                                        ]),
                                                        value: zod
                                                          .boolean()
                                                          .describe(
                                                            'A checkbox.'
                                                          ),
                                                      })
                                                      .describe('A checkbox.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'date',
                                                        ]),
                                                        value: zod.iso
                                                          .datetime({})
                                                          .describe(
                                                            'A date-time.'
                                                          ),
                                                      })
                                                      .describe('A date-time.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'link',
                                                        ]),
                                                        value: zod
                                                          .array(zod.string())
                                                          .describe(
                                                            'Complete http or https URLs; at most one for a single-valued column.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Complete http or https URLs; at most one for a single-valued column.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'options',
                                                        ]),
                                                        value: zod
                                                          .array(
                                                            zod
                                                              .union([
                                                                zod
                                                                  .object({
                                                                    id: zod
                                                                      .uuid()
                                                                      .describe(
                                                                        'An option the column has.'
                                                                      ),
                                                                  })
                                                                  .describe(
                                                                    'An option the column has.'
                                                                  ),
                                                                zod
                                                                  .object({
                                                                    label: zod
                                                                      .string()
                                                                      .describe(
                                                                        "An option's label, matched without regard to case. An unknown label\nis refused."
                                                                      ),
                                                                  })
                                                                  .describe(
                                                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                                                  ),
                                                              ])
                                                              .describe(
                                                                'A select option, by its id or by its label.'
                                                              )
                                                          )
                                                          .describe(
                                                            'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'entities',
                                                        ]),
                                                        value: zod
                                                          .array(
                                                            zod
                                                              .object({
                                                                entityId: zod
                                                                  .string()
                                                                  .describe(
                                                                    "The entity's id."
                                                                  ),
                                                                entityType: zod
                                                                  .enum([
                                                                    'USER',
                                                                    'DOCUMENT',
                                                                    'TASK',
                                                                    'COMPANY',
                                                                    'CONTACT',
                                                                    'CALL_RECORD',
                                                                    'CHANNEL',
                                                                    'CHAT',
                                                                    'PROJECT',
                                                                    'THREAD',
                                                                    'CALENDAR_EVENT',
                                                                    'INITIATIVE',
                                                                  ])
                                                                  .describe(
                                                                    'A kind of Macro entity a reference column can point at.'
                                                                  ),
                                                              })
                                                              .describe(
                                                                'A reference to one Macro entity.'
                                                              )
                                                          )
                                                          .describe(
                                                            'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'rows',
                                                        ]),
                                                        value: zod
                                                          .array(zod.uuid())
                                                          .describe(
                                                            'Rows of the table a relation column points at.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Rows of the table a relation column points at.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'clear',
                                                        ]),
                                                      })
                                                      .describe(
                                                        'No value: the cell is emptied.'
                                                      ),
                                                  ])
                                                  .describe(
                                                    "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                                  ),
                                              })
                                              .describe(
                                                'One cell of a row: which column, and its new value.'
                                              )
                                          )
                                          .describe('Its new cells.'),
                                        row: zod.uuid().describe('The row.'),
                                      })
                                      .describe(
                                        "One row's cells in a [`RowChanges::PerRow`] update."
                                      )
                                  )
                                  .describe(
                                    'The rows and their cells, in order.'
                                  ),
                              })
                              .describe('Each row its own cells.'),
                          ])
                          .describe(
                            'Which rows an update writes, and with what.'
                          ),
                        kind: zod.enum(['update']),
                      })
                      .describe(
                        'Write cells of existing rows. Last write wins: there is no version\ncheck.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                        rows: zod
                          .array(zod.uuid())
                          .describe('The rows, each named once.'),
                      })
                      .describe('Remove rows and their cells.'),
                  ])
                  .describe("A write to a table's rows."),
                kind: zod.enum(['rows']),
                table: zod.uuid().describe('The table the rows belong to.'),
              })
              .describe("A write to a table's rows."),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['create']),
                        view: zod
                          .object({
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyCrmPipelineOpsBodyOpsItemChangeViewLayoutColumnsItemWidthMin
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        'How lanes show, in display order.'
                                      ),
                                    title: zod
                                      .uuid()
                                      .optional()
                                      .describe(
                                        "The column a card is titled by. Left out, a board keeps the\ntitle it has, and a new board takes the table's first column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes, one per option of a single-select column or per\nperson of a single-person column, plus one for empty cells.'
                                  ),
                              ])
                              .describe(
                                'A layout as an op asks for it: a board may leave its card title out.'
                              ),
                            name: zod.string().describe('Its name.'),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .optional()
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                          })
                          .describe(
                            "A view's contents as an op creates it; the server gives it its id,\nposition and times."
                          ),
                      })
                      .describe(
                        'Add the view to the table, after its other views.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['update']),
                        layout: zod
                          .union([
                            zod
                              .object({
                                columns: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column.'),
                                        width: zod
                                          .number()
                                          .min(
                                            applyCrmPipelineOpsBodyOpsItemChangeLayoutColumnsItemWidthMin
                                          )
                                          .nullable()
                                          .describe(
                                            'Its width in pixels; the default when unset.'
                                          ),
                                      })
                                      .describe(
                                        'How one column shows in a table layout.'
                                      )
                                  )
                                  .describe(
                                    "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                  ),
                                kind: zod.enum(['table']),
                              })
                              .describe('A grid with a row per row.'),
                            zod
                              .object({
                                cardFields: zod
                                  .array(zod.uuid())
                                  .describe(
                                    'The columns a card shows under its title, in order.'
                                  ),
                                groupBy: zod
                                  .uuid()
                                  .describe(
                                    'The single-select or single-person column whose values are the\nlanes.'
                                  ),
                                hideEmptyLanes: zod
                                  .boolean()
                                  .describe(
                                    'Whether a lane with no cards is hidden.'
                                  ),
                                kind: zod.enum(['board']),
                                lanes: zod
                                  .array(
                                    zod
                                      .object({
                                        hidden: zod
                                          .boolean()
                                          .optional()
                                          .describe('Whether it is hidden.'),
                                        key: zod
                                          .union([
                                            zod
                                              .object({
                                                id: zod
                                                  .uuid()
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                kind: zod.enum(['option']),
                                              })
                                              .describe(
                                                "The cards holding this option of the board's select column."
                                              ),
                                            zod
                                              .object({
                                                id: zod
                                                  .string()
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                kind: zod.enum(['user']),
                                              })
                                              .describe(
                                                "The cards naming this person in the board's person column."
                                              ),
                                            zod
                                              .object({
                                                kind: zod.enum(['none']),
                                              })
                                              .describe(
                                                'The cards whose grouping cell is empty.'
                                              ),
                                          ])
                                          .describe(
                                            "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                          ),
                                      })
                                      .describe(
                                        'How one lane shows in a board layout.'
                                      )
                                  )
                                  .describe(
                                    'How lanes show, in display order.'
                                  ),
                                title: zod
                                  .uuid()
                                  .optional()
                                  .describe(
                                    "The column a card is titled by. Left out, a board keeps the\ntitle it has, and a new board takes the table's first column."
                                  ),
                              })
                              .describe(
                                'Cards in lanes, one per option of a single-select column or per\nperson of a single-person column, plus one for empty cells.'
                              ),
                          ])
                          .optional()
                          .describe(
                            'A layout as an op asks for it: a board may leave its card title out.'
                          ),
                        name: zod.string().optional().describe('Its new name.'),
                        query: zod
                          .object({
                            filter: zod.union([
                              zod.null(),
                              zod
                                .object({
                                  conditions: zod
                                    .array(
                                      zod
                                        .union([
                                          zod
                                            .object({
                                              column: zod
                                                .uuid()
                                                .describe('The column tested.'),
                                              test: zod
                                                .union([
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'presence',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isEmpty',
                                                          'isNotEmpty',
                                                        ])
                                                        .describe(
                                                          'Whether a cell is empty.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'Whether the cell is empty; fits a column of any type.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum(['text']),
                                                      operator: zod
                                                        .enum([
                                                          'is',
                                                          'isNot',
                                                          'contains',
                                                          'doesNotContain',
                                                          'startsWith',
                                                          'endsWith',
                                                        ])
                                                        .describe(
                                                          'How a text cell compares to a text.'
                                                        ),
                                                      value: zod
                                                        .string()
                                                        .describe(
                                                          'The text compared against, ignoring case for the containment\ntests.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A text or link column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'number',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'is',
                                                          'isNot',
                                                          'greaterThan',
                                                          'greaterThanOrEqual',
                                                          'lessThan',
                                                          'lessThanOrEqual',
                                                        ])
                                                        .describe(
                                                          'How a number cell compares to a number.'
                                                        ),
                                                      value: zod
                                                        .number()
                                                        .describe(
                                                          'The number compared against; finite.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A number column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum(['date']),
                                                      operator: zod
                                                        .enum([
                                                          'before',
                                                          'after',
                                                          'onOrBefore',
                                                          'onOrAfter',
                                                        ])
                                                        .describe(
                                                          'How a date cell compares to a date-time.'
                                                        ),
                                                      value: zod.iso
                                                        .datetime({})
                                                        .describe(
                                                          'The date-time compared against.'
                                                        ),
                                                    })
                                                    .describe('A date column.'),
                                                  zod
                                                    .object({
                                                      checked: zod
                                                        .boolean()
                                                        .describe(
                                                          'Whether the box is checked.'
                                                        ),
                                                      kind: zod.enum([
                                                        'checkbox',
                                                      ]),
                                                    })
                                                    .describe(
                                                      'A checkbox column. An unchecked box and an empty cell are the same.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'options',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isAnyOf',
                                                          'isNoneOf',
                                                          'hasAny',
                                                          'hasAll',
                                                          'hasNone',
                                                        ])
                                                        .describe(
                                                          "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                        ),
                                                      options: zod
                                                        .array(zod.uuid())
                                                        .describe(
                                                          'Options of the column; at least one.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A select or tag column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      entities: zod
                                                        .array(zod.string())
                                                        .describe(
                                                          "Entity ids, or for a relation the related rows' ids; at least\none."
                                                        ),
                                                      kind: zod.enum([
                                                        'entities',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isAnyOf',
                                                          'isNoneOf',
                                                          'hasAny',
                                                          'hasAll',
                                                          'hasNone',
                                                        ])
                                                        .describe(
                                                          "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                        ),
                                                    })
                                                    .describe(
                                                      'A reference or relation column.'
                                                    ),
                                                ])
                                                .describe(
                                                  "What a column's cell must be, by the kind of value the column holds."
                                                ),
                                            })
                                            .describe(
                                              "A test of one column's cells."
                                            )
                                            .and(
                                              zod.object({
                                                kind: zod.enum(['condition']),
                                              })
                                            )
                                            .describe('A test of one column.'),
                                          zod
                                            .unknown()
                                            .and(
                                              zod.object({
                                                kind: zod.enum(['group']),
                                              })
                                            )
                                            .describe('A nested group.'),
                                        ])
                                        .describe(
                                          'One entry of a group: a condition, or a group of its own.'
                                        )
                                    )
                                    .describe(
                                      'The conditions and nested groups. A group without any keeps every\nrow.'
                                    ),
                                  conjunction: zod
                                    .enum(['and', 'or'])
                                    .describe(
                                      "How a group's conditions combine."
                                    ),
                                })
                                .describe(
                                  'Conditions joined by one conjunction.'
                                ),
                            ]),
                            sort: zod
                              .array(
                                zod
                                  .object({
                                    column: zod
                                      .uuid()
                                      .describe('The column sorted on.'),
                                    direction: zod
                                      .enum(['ascending', 'descending'])
                                      .describe(
                                        'A sort direction. Empty cells sort last either way.'
                                      ),
                                  })
                                  .describe('One sort key.')
                              )
                              .optional()
                              .describe(
                                "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                              ),
                          })
                          .optional()
                          .describe(
                            'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                          ),
                      })
                      .describe(
                        "Change the view's name, query or layout; what is left out stays."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe('Remove the view, with where its cards were.'),
                    zod
                      .object({
                        after: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'The card that ends up just after it, if any. Given with `before`,\nit must be the card right after `before`; with neither, the card\ngoes to the end of the lane.'
                          ),
                        before: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'The card that ends up just before it (it lands right after this\none), if any.'
                          ),
                        kind: zod.enum(['move_card']),
                        lane: zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe(
                                    "The cards holding this option of the board's select column."
                                  ),
                                kind: zod.enum(['option']),
                              })
                              .describe(
                                "The cards holding this option of the board's select column."
                              ),
                            zod
                              .object({
                                id: zod
                                  .string()
                                  .describe(
                                    "The cards naming this person in the board's person column."
                                  ),
                                kind: zod.enum(['user']),
                              })
                              .describe(
                                "The cards naming this person in the board's person column."
                              ),
                            zod
                              .object({
                                kind: zod.enum(['none']),
                              })
                              .describe(
                                'The cards whose grouping cell is empty.'
                              ),
                          ])
                          .describe(
                            "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                          ),
                        row: zod.uuid().describe("The card's row."),
                      })
                      .describe(
                        "Move one of the board's cards: into a lane, which sets the row's\ngrouping cell to the lane's option or person (or empties it for the\nlane of empty cells), and to a place there, between two of its\ncards. Only an unsorted board's cards move by hand."
                      ),
                  ])
                  .describe('A change to one view.'),
                kind: zod.enum(['view']),
                table: zod.uuid().describe("The view's table."),
                view: zod
                  .uuid()
                  .describe(
                    'The view; for a creation, its new id, minted by the client.'
                  ),
              })
              .describe('A change to one view of a table.'),
            zod
              .object({
                kind: zod.enum(['reorder_tables']),
                order: zod
                  .array(zod.uuid())
                  .describe('Every table, in its new order.'),
              })
              .describe(
                "Set the order of the database's tables: `order` names every one of\nthem once."
              ),
          ])
          .describe(
            "One write to a database: its tables, columns, options, rows or views,\ngrouped by the resource it changes. A request's ops apply in order and\ntogether, or not at all, and every op names a table of the database the\nrequest is for (or, creating one, adds it there)."
          )
      )
      .describe('The ops, in the order they apply.'),
  })
  .describe(
    'A batch of ops for one database and the versions its tables must be at.'
  );

export const applyCrmPipelineOpsResponseResultsItemChangeAffectedMin = 0;

export const applyCrmPipelineOpsResponseResultsItemChangeAffectedMinOne = 0;

export const applyCrmPipelineOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMin = 0;

export const applyCrmPipelineOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMinOne = 0;

export const applyCrmPipelineOpsResponse = zod
  .object({
    changes: zod
      .array(
        zod
          .object({
            change: zod.number().describe("The journal's id of the change."),
            table: zod.uuid().describe('The table.'),
            version: zod
              .number()
              .describe(
                "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
              ),
          })
          .describe(
            "One change a committed batch journaled: a table, the version the batch\nproduced, and the journal's id for it."
          )
      )
      .describe("The journal's changes."),
    results: zod
      .array(
        zod
          .union([
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['renamed']),
                      })
                      .describe('It was renamed.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['columns_reordered']),
                      })
                      .describe('Its columns were reordered.'),
                    zod
                      .object({
                        kind: zod.enum(['views_reordered']),
                        positions: zod
                          .array(
                            zod
                              .object({
                                position: zod.string().describe('Its key.'),
                                view: zod.uuid().describe('The view.'),
                              })
                              .describe(
                                "A view's place among its table's views."
                              )
                          )
                          .describe("Every view's key, in their new order."),
                      })
                      .describe('Its views were reordered.'),
                  ])
                  .describe('What happened to a table.'),
                kind: zod.enum(['table']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .optional()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a table op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['renamed']),
                      })
                      .describe('It was renamed.'),
                    zod
                      .object({
                        kind: zod.enum(['type_changed']),
                      })
                      .describe('Its type changed, and its cells with it.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        added: zod
                          .array(zod.uuid())
                          .describe(
                            'The options created, in order: those sent, less any whose label\nthe column already had.'
                          ),
                        kind: zod.enum(['options_added']),
                      })
                      .describe('It gained options.'),
                    zod
                      .object({
                        kind: zod.enum(['option_updated']),
                      })
                      .describe(
                        'One of its options was relabelled or recoloured.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['option_deleted']),
                      })
                      .describe('One of its options was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['formula_set']),
                      })
                      .describe('Its formula changed.'),
                  ])
                  .describe('What happened to a column.'),
                column: zod.uuid().describe('The column.'),
                kind: zod.enum(['column']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a column op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['inserted']),
                        rows: zod
                          .array(zod.uuid())
                          .describe(
                            'The new rows, in the order they were sent.'
                          ),
                      })
                      .describe('Rows were added.'),
                    zod
                      .object({
                        affected: zod
                          .number()
                          .min(
                            applyCrmPipelineOpsResponseResultsItemChangeAffectedMin
                          )
                          .describe('How many rows the op updated.'),
                        kind: zod.enum(['updated']),
                      })
                      .describe("Rows' cells were written."),
                    zod
                      .object({
                        affected: zod
                          .number()
                          .min(
                            applyCrmPipelineOpsResponseResultsItemChangeAffectedMinOne
                          )
                          .describe('How many rows the op deleted.'),
                        kind: zod.enum(['deleted']),
                      })
                      .describe('Rows were removed.'),
                  ])
                  .describe("What happened to a table's rows."),
                kind: zod.enum(['rows']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a rows op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                        view: zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When it was created.'),
                            databaseId: zod
                              .uuid()
                              .describe('The database the table belongs to.'),
                            id: zod.uuid().describe('The view.'),
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyCrmPipelineOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMin
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                                      ),
                                    title: zod
                                      .uuid()
                                      .describe(
                                        "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                                  ),
                              ])
                              .describe('How a view draws its rows.'),
                            name: zod
                              .string()
                              .describe(
                                "Its name, unique among the table's views ignoring case."
                              ),
                            position: zod
                              .string()
                              .describe(
                                "Where it sorts among the table's views: a fractional key."
                              ),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                            tableId: zod.uuid().describe('The table it shows.'),
                            updatedAt: zod.iso
                              .datetime({})
                              .describe('When it last changed.'),
                          })
                          .describe('A view of one table, as stored.'),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['updated']),
                        view: zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When it was created.'),
                            databaseId: zod
                              .uuid()
                              .describe('The database the table belongs to.'),
                            id: zod.uuid().describe('The view.'),
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyCrmPipelineOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMinOne
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                                      ),
                                    title: zod
                                      .uuid()
                                      .describe(
                                        "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                                  ),
                              ])
                              .describe('How a view draws its rows.'),
                            name: zod
                              .string()
                              .describe(
                                "Its name, unique among the table's views ignoring case."
                              ),
                            position: zod
                              .string()
                              .describe(
                                "Where it sorts among the table's views: a fractional key."
                              ),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                            tableId: zod.uuid().describe('The table it shows.'),
                            updatedAt: zod.iso
                              .datetime({})
                              .describe('When it last changed.'),
                          })
                          .describe('A view of one table, as stored.'),
                      })
                      .describe('It was changed.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['card_moved']),
                        positions: zod
                          .array(
                            zod
                              .object({
                                lane: zod
                                  .union([
                                    zod
                                      .object({
                                        id: zod
                                          .uuid()
                                          .describe(
                                            "The cards holding this option of the board's select column."
                                          ),
                                        kind: zod.enum(['option']),
                                      })
                                      .describe(
                                        "The cards holding this option of the board's select column."
                                      ),
                                    zod
                                      .object({
                                        id: zod
                                          .string()
                                          .describe(
                                            "The cards naming this person in the board's person column."
                                          ),
                                        kind: zod.enum(['user']),
                                      })
                                      .describe(
                                        "The cards naming this person in the board's person column."
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['none']),
                                      })
                                      .describe(
                                        'The cards whose grouping cell is empty.'
                                      ),
                                  ])
                                  .describe(
                                    "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                  ),
                                position: zod
                                  .string()
                                  .describe("The card's key in that lane."),
                                row: zod.uuid().describe("The card's row."),
                              })
                              .describe(
                                'Where one card sits on a board: its lane, and its fractional key there.\nA card whose row has since moved to another lane has no place until it is\nmoved again.'
                              )
                          )
                          .describe(
                            "The positions written, the moved card's last."
                          ),
                      })
                      .describe('One of its cards moved.'),
                  ])
                  .describe('What happened to a view.'),
                kind: zod.enum(['view']),
                table: zod.uuid().describe("The view's table."),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
                view: zod.uuid().describe('The view.'),
              })
              .describe('What a view op did.'),
            zod
              .object({
                kind: zod.enum(['reorder_tables']),
                tables: zod
                  .array(
                    zod
                      .object({
                        table: zod.uuid().describe('The table.'),
                        version: zod
                          .number()
                          .describe(
                            "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                          ),
                      })
                      .describe('A table and its version.')
                  )
                  .describe(
                    'Every table, in its new order, with its version once the request\ncommitted.'
                  ),
              })
              .describe("The database's tables in their new order."),
          ])
          .describe(
            "What one op did, in the order the ops were sent, grouped as the ops are:\na result's `kind` is its op's, naming the same resource, and its\n`change` says what happened to it."
          )
      )
      .describe('One result per op, in order.'),
  })
  .describe(
    "What a committed batch answers: a result per op, and the journal's change\nfor each table version it produced."
  );

/**
 * @summary Read the owner, team grant and channel recipients of a pipeline the caller can edit.
 */
export const getCrmPipelinePermissionsParams = zod.object({
  id: zod.uuid(),
});

export const getCrmPipelinePermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Share a pipeline the caller can edit with channels, or change or remove
their access. Team access changes through `/sharing`; there is no share link.
 */
export const updateCrmPipelinePermissionsParams = zod.object({
  id: zod.uuid(),
});

export const updateCrmPipelinePermissionsBody = zod.object({
  channelSharePermissions: zod
    .array(
      zod.object({
        accessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
        channelId: zod.string().describe('The channel id'),
        operation: zod.enum(['add', 'remove', 'replace']),
      })
    )
    .nullish()
    .describe('Any channel share permissions to be created\/updated\/removed'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

export const updateCrmPipelinePermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Read pipeline records through pipeline access.
 */
export const getCrmPipelineRowsParams = zod.object({
  id: zod.uuid(),
});

export const getCrmPipelineRowsQueryParams = zod.object({
  after: zod.uuid().nullish().describe('Continue after this row identity.'),
});

export const getCrmPipelineRowsResponse = zod
  .object({
    next: zod
      .union([zod.null(), zod.uuid().describe('Identifier of a row.')])
      .optional(),
    rows: zod
      .array(
        zod
          .object({
            cells: zod
              .record(
                zod.string(),
                zod
                  .union([
                    zod
                      .object({
                        type: zod.enum(['Boolean']),
                        value: zod
                          .boolean()
                          .describe(
                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                          ),
                      })
                      .describe(
                        'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Number']),
                        value: zod
                          .number()
                          .describe(
                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                          ),
                      })
                      .describe(
                        'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['String']),
                        value: zod
                          .string()
                          .describe(
                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                          ),
                      })
                      .describe(
                        'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Date']),
                        value: zod.iso
                          .datetime({})
                          .describe(
                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                          ),
                      })
                      .describe(
                        'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['SelectOption']),
                        value: zod
                          .array(zod.uuid())
                          .describe(
                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                      ),
                    zod
                      .object({
                        type: zod.enum(['EntityReference']),
                        value: zod
                          .array(
                            zod
                              .object({
                                entity_id: zod.string(),
                                entity_type: zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                                specific_message_id: zod
                                  .uuid()
                                  .nullish()
                                  .describe(
                                    'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                  ),
                              })
                              .describe(
                                'Entity reference for entity-type property values.'
                              )
                          )
                          .describe(
                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Link']),
                        value: zod
                          .array(zod.string())
                          .describe(
                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                      ),
                  ])
                  .describe(
                    'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                  )
              )
              .describe('Populated cells.'),
            rowId: zod.uuid().describe('Identifier of a row.'),
          })
          .describe('One storage row, with cells keyed by column placement.')
      )
      .describe("Rows in the requested view's order."),
    version: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
  })
  .describe('A bounded page of rows at a table version.');

/**
 * @summary Query rows through pipeline access using the shared view protocol.
 */
export const queryCrmPipelineRowsParams = zod.object({
  id: zod.uuid(),
});

export const queryCrmPipelineRowsBody = zod
  .object({
    after: zod
      .union([zod.null(), zod.uuid().describe('Identifier of a row.')])
      .optional(),
    query: zod
      .object({
        filter: zod.union([
          zod.null(),
          zod
            .object({
              conditions: zod
                .array(
                  zod
                    .union([
                      zod
                        .object({
                          column: zod.uuid().describe('The column tested.'),
                          test: zod
                            .union([
                              zod
                                .object({
                                  kind: zod.enum(['presence']),
                                  operator: zod
                                    .enum(['isEmpty', 'isNotEmpty'])
                                    .describe('Whether a cell is empty.'),
                                })
                                .describe(
                                  'Whether the cell is empty; fits a column of any type.'
                                ),
                              zod
                                .object({
                                  kind: zod.enum(['text']),
                                  operator: zod
                                    .enum([
                                      'is',
                                      'isNot',
                                      'contains',
                                      'doesNotContain',
                                      'startsWith',
                                      'endsWith',
                                    ])
                                    .describe(
                                      'How a text cell compares to a text.'
                                    ),
                                  value: zod
                                    .string()
                                    .describe(
                                      'The text compared against, ignoring case for the containment\ntests.'
                                    ),
                                })
                                .describe('A text or link column.'),
                              zod
                                .object({
                                  kind: zod.enum(['number']),
                                  operator: zod
                                    .enum([
                                      'is',
                                      'isNot',
                                      'greaterThan',
                                      'greaterThanOrEqual',
                                      'lessThan',
                                      'lessThanOrEqual',
                                    ])
                                    .describe(
                                      'How a number cell compares to a number.'
                                    ),
                                  value: zod
                                    .number()
                                    .describe(
                                      'The number compared against; finite.'
                                    ),
                                })
                                .describe('A number column.'),
                              zod
                                .object({
                                  kind: zod.enum(['date']),
                                  operator: zod
                                    .enum([
                                      'before',
                                      'after',
                                      'onOrBefore',
                                      'onOrAfter',
                                    ])
                                    .describe(
                                      'How a date cell compares to a date-time.'
                                    ),
                                  value: zod.iso
                                    .datetime({})
                                    .describe(
                                      'The date-time compared against.'
                                    ),
                                })
                                .describe('A date column.'),
                              zod
                                .object({
                                  checked: zod
                                    .boolean()
                                    .describe('Whether the box is checked.'),
                                  kind: zod.enum(['checkbox']),
                                })
                                .describe(
                                  'A checkbox column. An unchecked box and an empty cell are the same.'
                                ),
                              zod
                                .object({
                                  kind: zod.enum(['options']),
                                  operator: zod
                                    .enum([
                                      'isAnyOf',
                                      'isNoneOf',
                                      'hasAny',
                                      'hasAll',
                                      'hasNone',
                                    ])
                                    .describe(
                                      "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                    ),
                                  options: zod
                                    .array(zod.uuid())
                                    .describe(
                                      'Options of the column; at least one.'
                                    ),
                                })
                                .describe('A select or tag column.'),
                              zod
                                .object({
                                  entities: zod
                                    .array(zod.string())
                                    .describe(
                                      "Entity ids, or for a relation the related rows' ids; at least\none."
                                    ),
                                  kind: zod.enum(['entities']),
                                  operator: zod
                                    .enum([
                                      'isAnyOf',
                                      'isNoneOf',
                                      'hasAny',
                                      'hasAll',
                                      'hasNone',
                                    ])
                                    .describe(
                                      "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                    ),
                                })
                                .describe('A reference or relation column.'),
                            ])
                            .describe(
                              "What a column's cell must be, by the kind of value the column holds."
                            ),
                        })
                        .describe("A test of one column's cells.")
                        .and(
                          zod.object({
                            kind: zod.enum(['condition']),
                          })
                        )
                        .describe('A test of one column.'),
                      zod
                        .unknown()
                        .and(
                          zod.object({
                            kind: zod.enum(['group']),
                          })
                        )
                        .describe('A nested group.'),
                    ])
                    .describe(
                      'One entry of a group: a condition, or a group of its own.'
                    )
                )
                .describe(
                  'The conditions and nested groups. A group without any keeps every\nrow.'
                ),
              conjunction: zod
                .enum(['and', 'or'])
                .describe("How a group's conditions combine."),
            })
            .describe('Conditions joined by one conjunction.'),
        ]),
        sort: zod
          .array(
            zod
              .object({
                column: zod.uuid().describe('The column sorted on.'),
                direction: zod
                  .enum(['ascending', 'descending'])
                  .describe(
                    'A sort direction. Empty cells sort last either way.'
                  ),
              })
              .describe('One sort key.')
          )
          .optional()
          .describe(
            "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
          ),
      })
      .optional()
      .describe(
        'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
      ),
    rowIds: zod
      .array(zod.uuid().describe('Identifier of a row.'))
      .nullish()
      .describe('Read these retained rows regardless of the active filter.'),
  })
  .describe('A query over one authorized storage table.');

export const queryCrmPipelineRowsResponse = zod
  .object({
    next: zod
      .union([zod.null(), zod.uuid().describe('Identifier of a row.')])
      .optional(),
    rows: zod
      .array(
        zod
          .object({
            cells: zod
              .record(
                zod.string(),
                zod
                  .union([
                    zod
                      .object({
                        type: zod.enum(['Boolean']),
                        value: zod
                          .boolean()
                          .describe(
                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                          ),
                      })
                      .describe(
                        'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Number']),
                        value: zod
                          .number()
                          .describe(
                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                          ),
                      })
                      .describe(
                        'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['String']),
                        value: zod
                          .string()
                          .describe(
                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                          ),
                      })
                      .describe(
                        'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Date']),
                        value: zod.iso
                          .datetime({})
                          .describe(
                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                          ),
                      })
                      .describe(
                        'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                      ),
                    zod
                      .object({
                        type: zod.enum(['SelectOption']),
                        value: zod
                          .array(zod.uuid())
                          .describe(
                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                      ),
                    zod
                      .object({
                        type: zod.enum(['EntityReference']),
                        value: zod
                          .array(
                            zod
                              .object({
                                entity_id: zod.string(),
                                entity_type: zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                                specific_message_id: zod
                                  .uuid()
                                  .nullish()
                                  .describe(
                                    'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                  ),
                              })
                              .describe(
                                'Entity reference for entity-type property values.'
                              )
                          )
                          .describe(
                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                      ),
                    zod
                      .object({
                        type: zod.enum(['Link']),
                        value: zod
                          .array(zod.string())
                          .describe(
                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                          ),
                      })
                      .describe(
                        'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                      ),
                  ])
                  .describe(
                    'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                  )
              )
              .describe('Populated cells.'),
            rowId: zod.uuid().describe('Identifier of a row.'),
          })
          .describe('One storage row, with cells keyed by column placement.')
      )
      .describe("Rows in the requested view's order."),
    version: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
  })
  .describe('A bounded page of rows at a table version.');

/**
 * @summary Update sharing.
 */
export const shareCrmPipelineParams = zod.object({
  id: zod.uuid(),
});

export const shareCrmPipelineBody = zod
  .object({
    sharing: zod
      .enum(['private', 'team'])
      .describe(
        'Initial sharing, or the desired team grant. Individual ownership is preserved.'
      ),
  })
  .describe('Sharing input.');

/**
 * @summary Read the host-owned table schema.
 */
export const getCrmPipelineTableParams = zod.object({
  id: zod.uuid(),
});

export const getCrmPipelineTableResponseViewsItemLayoutColumnsItemWidthMin = 0;

export const getCrmPipelineTableResponse = zod
  .object({
    columns: zod
      .array(
        zod
          .object({
            column: zod
              .object({
                config: zod.union([
                  zod.null(),
                  zod
                    .union([
                      zod
                        .object({
                          database_id: zod.uuid().describe('Target database.'),
                          kind: zod.enum(['link']),
                          table_id: zod.uuid().describe('Target table.'),
                        })
                        .describe(
                          'A relation column: its cells reference rows of another table.'
                        ),
                      zod
                        .object({
                          formula: zod
                            .union([
                              zod
                                .object({
                                  column: zod
                                    .uuid()
                                    .describe('The column placement.'),
                                  kind: zod.enum(['column']),
                                })
                                .describe(
                                  "The row's cell in another column of the table: a number, date or\nderived column."
                                ),
                              zod
                                .object({
                                  kind: zod.enum(['number']),
                                  value: zod
                                    .number()
                                    .describe('The value; finite.'),
                                })
                                .describe(
                                  'A constant. Added to or subtracted from a date, it counts days.'
                                ),
                              zod
                                .object({
                                  kind: zod.enum(['binary']),
                                  left: zod.unknown(),
                                  operator: zod
                                    .enum([
                                      'add',
                                      'subtract',
                                      'multiply',
                                      'divide',
                                    ])
                                    .describe('An arithmetic operator.'),
                                  right: zod.unknown(),
                                })
                                .describe('Two expressions combined.'),
                              zod
                                .object({
                                  kind: zod.enum(['negate']),
                                  operand: zod.unknown(),
                                })
                                .describe("An expression's negation."),
                            ])
                            .describe("An expression over one row's cells."),
                          kind: zod.enum(['derived']),
                        })
                        .describe(
                          "A derived column: its cells are computed from the row's others, and\nits definition, a number or date one, never holds a value."
                        ),
                    ])
                    .describe(
                      'Column-kind specific configuration stored on the placement.'
                    ),
                ]),
                display_name: zod
                  .string()
                  .nullable()
                  .describe(
                    "The placement's own label, which also names it in SQL; `None` shows\nthe definition's name."
                  ),
                id: zod.uuid().describe('Identifier of the placement.'),
                infer_type: zod
                  .boolean()
                  .describe(
                    "Whether the first nonempty value may settle this new text column's type."
                  ),
                nullable: zod
                  .boolean()
                  .optional()
                  .describe(
                    'Whether a row may omit this cell; empty collections also count as absent.'
                  ),
                position: zod
                  .string()
                  .describe('Fractional index for column ordering.'),
                property_definition_id: zod
                  .uuid()
                  .describe('The bound property definition.'),
                protections: zod
                  .array(
                    zod
                      .enum(['delete', 'change_type'])
                      .describe(
                        'A schema operation reserved by a feature using a column.'
                      )
                  )
                  .optional()
                  .describe(
                    'Schema operations reserved by a feature; ordinary edits cannot clear them.'
                  ),
                table_id: zod.uuid().describe('Table the column appears on.'),
              })
              .describe(
                'A column: the placement of a property definition on a table.\n\nThe definition carries name, [`DataType`], multi-select flag, and options;\nthis carries only where it appears and column-kind configuration.'
              ),
            definition: zod
              .object({
                definition: zod
                  .object({
                    created_at: zod.iso.datetime({}),
                    data_type: zod
                      .enum([
                        'BOOLEAN',
                        'DATE',
                        'NUMBER',
                        'STRING',
                        'SELECT_NUMBER',
                        'SELECT_STRING',
                        'TAG',
                        'ENTITY',
                        'LINK',
                      ])
                      .describe(
                        'Data type for property values, determining storage and validation.'
                      ),
                    display_name: zod.string(),
                    id: zod.uuid(),
                    is_metadata: zod
                      .boolean()
                      .describe(
                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                      ),
                    is_multi_select: zod.boolean(),
                    is_system: zod
                      .boolean()
                      .describe(
                        'Flag to indicate if this is a system property (stored in DB).'
                      ),
                    owner: zod
                      .union([
                        zod
                          .object({
                            scope: zod.enum(['user']),
                            user_id: zod.string(),
                          })
                          .describe('User-scoped property.'),
                        zod
                          .object({
                            scope: zod.enum(['team']),
                            team_id: zod.uuid(),
                          })
                          .describe('Team-scoped property.'),
                        zod
                          .object({
                            database_id: zod.uuid(),
                            scope: zod.enum(['database']),
                          })
                          .describe(
                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                          ),
                        zod
                          .object({
                            scope: zod.enum(['system']),
                          })
                          .describe(
                            'System-owned property (no user, team, or database owner).'
                          ),
                      ])
                      .describe(
                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                      ),
                    specific_entity_type: zod.union([
                      zod.null(),
                      zod
                        .enum([
                          'CALENDAR_EVENT',
                          'CALL_RECORD',
                          'CHANNEL',
                          'CHAT',
                          'COMPANY',
                          'DATABASE_ROW',
                          'CONTACT',
                          'DOCUMENT',
                          'INITIATIVE',
                          'PROJECT',
                          'TASK',
                          'THREAD',
                          'USER',
                        ])
                        .describe(
                          'Type of entity that can be referenced by entity properties.'
                        ),
                    ]),
                    updated_at: zod.iso.datetime({}),
                  })
                  .describe(
                    'Property definition model (service representation).'
                  ),
                property_options: zod.array(
                  zod
                    .object({
                      color: zod.string().nullable(),
                      created_at: zod.iso.datetime({}),
                      display_order: zod.number(),
                      id: zod.uuid(),
                      property_definition_id: zod.uuid(),
                      updated_at: zod.iso.datetime({}),
                      value: zod
                        .union([
                          zod
                            .object({
                              type: zod.enum(['string']),
                              value: zod
                                .string()
                                .describe(
                                  'String value for SelectString properties'
                                ),
                            })
                            .describe(
                              'String value for SelectString properties'
                            ),
                          zod
                            .object({
                              type: zod.enum(['number']),
                              value: zod
                                .number()
                                .describe(
                                  'Number value for SelectNumber properties'
                                ),
                            })
                            .describe(
                              'Number value for SelectNumber properties'
                            ),
                        ])
                        .describe(
                          'The value of a property option - either a string or a number.'
                        ),
                    })
                    .describe(
                      'A selectable option for select-type properties (service representation).'
                    )
                ),
              })
              .describe(
                'Property definition with its associated options (service representation).'
              ),
            shared_outside_database: zod
              .boolean()
              .describe(
                "Whether the definition belongs to something beyond this database (a\nperson's, a team's or a system property), so changing its options\nchanges them everywhere that property is used."
              ),
            sql_name: zod
              .string()
              .describe(
                'The name SQL refers to the column by: its display name, quoted.'
              ),
            writable: zod
              .boolean()
              .describe('Whether SQL may write this column.'),
          })
          .describe('One column placement with the definition behind it.')
      )
      .describe('Columns in display order.'),
    sql_name: zod
      .string()
      .describe(
        'The name SQL refers to the table by: its display name quoted and\nqualified by the database\'s (`FROM \"Plans\".\"Table 1\"`).'
      ),
    table: zod
      .object({
        database_id: zod.uuid().describe('Owning database.'),
        id: zod.uuid().describe('Identifier.'),
        name: zod
          .string()
          .describe("Display name; also the basis of the table's SQL name."),
        position: zod.string().describe('Fractional index for tab ordering.'),
        version: zod
          .number()
          .describe(
            "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
          ),
      })
      .describe('One table (tab) of a database.'),
    views: zod
      .array(
        zod
          .object({
            createdAt: zod.iso.datetime({}).describe('When it was created.'),
            databaseId: zod
              .uuid()
              .describe('The database the table belongs to.'),
            id: zod.uuid().describe('The view.'),
            layout: zod
              .union([
                zod
                  .object({
                    columns: zod
                      .array(
                        zod
                          .object({
                            column: zod.uuid().describe('The column.'),
                            width: zod
                              .number()
                              .min(
                                getCrmPipelineTableResponseViewsItemLayoutColumnsItemWidthMin
                              )
                              .nullable()
                              .describe(
                                'Its width in pixels; the default when unset.'
                              ),
                          })
                          .describe('How one column shows in a table layout.')
                      )
                      .describe(
                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                      ),
                    kind: zod.enum(['table']),
                  })
                  .describe('A grid with a row per row.'),
                zod
                  .object({
                    cardFields: zod
                      .array(zod.uuid())
                      .describe(
                        'The columns a card shows under its title, in order.'
                      ),
                    groupBy: zod
                      .uuid()
                      .describe(
                        'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                      ),
                    hideEmptyLanes: zod
                      .boolean()
                      .describe('Whether a lane with no cards is hidden.'),
                    kind: zod.enum(['board']),
                    lanes: zod
                      .array(
                        zod
                          .object({
                            hidden: zod
                              .boolean()
                              .optional()
                              .describe('Whether it is hidden.'),
                            key: zod
                              .union([
                                zod
                                  .object({
                                    id: zod
                                      .uuid()
                                      .describe(
                                        "The cards holding this option of the board's select column."
                                      ),
                                    kind: zod.enum(['option']),
                                  })
                                  .describe(
                                    "The cards holding this option of the board's select column."
                                  ),
                                zod
                                  .object({
                                    id: zod
                                      .string()
                                      .describe(
                                        "The cards naming this person in the board's person column."
                                      ),
                                    kind: zod.enum(['user']),
                                  })
                                  .describe(
                                    "The cards naming this person in the board's person column."
                                  ),
                                zod
                                  .object({
                                    kind: zod.enum(['none']),
                                  })
                                  .describe(
                                    'The cards whose grouping cell is empty.'
                                  ),
                              ])
                              .describe(
                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                              ),
                          })
                          .describe('How one lane shows in a board layout.')
                      )
                      .describe(
                        "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                      ),
                    title: zod
                      .uuid()
                      .describe(
                        "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                      ),
                  })
                  .describe(
                    'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                  ),
              ])
              .describe('How a view draws its rows.'),
            name: zod
              .string()
              .describe(
                "Its name, unique among the table's views ignoring case."
              ),
            position: zod
              .string()
              .describe(
                "Where it sorts among the table's views: a fractional key."
              ),
            query: zod
              .object({
                filter: zod.union([
                  zod.null(),
                  zod
                    .object({
                      conditions: zod
                        .array(
                          zod
                            .union([
                              zod
                                .object({
                                  column: zod
                                    .uuid()
                                    .describe('The column tested.'),
                                  test: zod
                                    .union([
                                      zod
                                        .object({
                                          kind: zod.enum(['presence']),
                                          operator: zod
                                            .enum(['isEmpty', 'isNotEmpty'])
                                            .describe(
                                              'Whether a cell is empty.'
                                            ),
                                        })
                                        .describe(
                                          'Whether the cell is empty; fits a column of any type.'
                                        ),
                                      zod
                                        .object({
                                          kind: zod.enum(['text']),
                                          operator: zod
                                            .enum([
                                              'is',
                                              'isNot',
                                              'contains',
                                              'doesNotContain',
                                              'startsWith',
                                              'endsWith',
                                            ])
                                            .describe(
                                              'How a text cell compares to a text.'
                                            ),
                                          value: zod
                                            .string()
                                            .describe(
                                              'The text compared against, ignoring case for the containment\ntests.'
                                            ),
                                        })
                                        .describe('A text or link column.'),
                                      zod
                                        .object({
                                          kind: zod.enum(['number']),
                                          operator: zod
                                            .enum([
                                              'is',
                                              'isNot',
                                              'greaterThan',
                                              'greaterThanOrEqual',
                                              'lessThan',
                                              'lessThanOrEqual',
                                            ])
                                            .describe(
                                              'How a number cell compares to a number.'
                                            ),
                                          value: zod
                                            .number()
                                            .describe(
                                              'The number compared against; finite.'
                                            ),
                                        })
                                        .describe('A number column.'),
                                      zod
                                        .object({
                                          kind: zod.enum(['date']),
                                          operator: zod
                                            .enum([
                                              'before',
                                              'after',
                                              'onOrBefore',
                                              'onOrAfter',
                                            ])
                                            .describe(
                                              'How a date cell compares to a date-time.'
                                            ),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'The date-time compared against.'
                                            ),
                                        })
                                        .describe('A date column.'),
                                      zod
                                        .object({
                                          checked: zod
                                            .boolean()
                                            .describe(
                                              'Whether the box is checked.'
                                            ),
                                          kind: zod.enum(['checkbox']),
                                        })
                                        .describe(
                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                        ),
                                      zod
                                        .object({
                                          kind: zod.enum(['options']),
                                          operator: zod
                                            .enum([
                                              'isAnyOf',
                                              'isNoneOf',
                                              'hasAny',
                                              'hasAll',
                                              'hasNone',
                                            ])
                                            .describe(
                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                            ),
                                          options: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Options of the column; at least one.'
                                            ),
                                        })
                                        .describe('A select or tag column.'),
                                      zod
                                        .object({
                                          entities: zod
                                            .array(zod.string())
                                            .describe(
                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                            ),
                                          kind: zod.enum(['entities']),
                                          operator: zod
                                            .enum([
                                              'isAnyOf',
                                              'isNoneOf',
                                              'hasAny',
                                              'hasAll',
                                              'hasNone',
                                            ])
                                            .describe(
                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                            ),
                                        })
                                        .describe(
                                          'A reference or relation column.'
                                        ),
                                    ])
                                    .describe(
                                      "What a column's cell must be, by the kind of value the column holds."
                                    ),
                                })
                                .describe("A test of one column's cells.")
                                .and(
                                  zod.object({
                                    kind: zod.enum(['condition']),
                                  })
                                )
                                .describe('A test of one column.'),
                              zod
                                .unknown()
                                .and(
                                  zod.object({
                                    kind: zod.enum(['group']),
                                  })
                                )
                                .describe('A nested group.'),
                            ])
                            .describe(
                              'One entry of a group: a condition, or a group of its own.'
                            )
                        )
                        .describe(
                          'The conditions and nested groups. A group without any keeps every\nrow.'
                        ),
                      conjunction: zod
                        .enum(['and', 'or'])
                        .describe("How a group's conditions combine."),
                    })
                    .describe('Conditions joined by one conjunction.'),
                ]),
                sort: zod
                  .array(
                    zod
                      .object({
                        column: zod.uuid().describe('The column sorted on.'),
                        direction: zod
                          .enum(['ascending', 'descending'])
                          .describe(
                            'A sort direction. Empty cells sort last either way.'
                          ),
                      })
                      .describe('One sort key.')
                  )
                  .optional()
                  .describe(
                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                  ),
              })
              .describe(
                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
              ),
            tableId: zod.uuid().describe('The table it shows.'),
            updatedAt: zod.iso.datetime({}).describe('When it last changed.'),
          })
          .describe('A view of one table, as stored.')
      )
      .describe("The table's views, in their order."),
  })
  .describe('One table with its columns and SQL name.');

/**
 * @summary Trash or restore a pipeline.
 */
export const trashCrmPipelineParams = zod.object({
  id: zod.uuid(),
});

export const trashCrmPipelineBody = zod
  .object({
    trashed: zod.boolean().describe('True to trash; false to restore.'),
  })
  .describe('Trash\/restore input.');

/**
 * @summary Read the caller's team CRM configuration. Any team member may read;
teams without a settings row get the defaults.
 */
export const getCrmTeamSettingsResponse = zod
  .object({
    closed_stage_ids: zod
      .array(zod.uuid())
      .nullish()
      .describe(
        'Stage option ids counting as closed deals; absent = the client\nfalls back to its label heuristic.'
      ),
    default_team_view_id: zod
      .string()
      .nullish()
      .describe(
        'Team view applied by default when a member opens the CRM view.'
      ),
    delete_records_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    edit_stages_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    legacy_stage_ids: zod
      .record(zod.string(), zod.uuid())
      .describe(
        'System stage option id to team stage option id for seeded stages.'
      ),
    move_closed_deals_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    team_views: zod
      .unknown()
      .describe(
        'Team saved views — an opaque JSON array owned by the frontend.'
      ),
  })
  .describe(
    "The team's CRM configuration (everything on `team_crm_settings`\nexcept the `crm_enabled` killswitch, which is managed via\n`PATCH \/team\/crm` on the auth service)."
  );

/**
 * @summary Partially update the caller's team CRM configuration. Any team
member may update the views fields (`team_views`,
`default_team_view_id`); the governance fields (permission
thresholds, `closed_stage_ids`) require an admin/owner team role
(403 otherwise). Omitted fields keep their current values;
`team_views` is replaced whole. Returns the resulting settings.
 */
export const putCrmTeamSettingsBody = zod
  .object({
    closed_stage_ids: zod
      .array(zod.uuid())
      .nullish()
      .describe(
        'New closed-stage set. Omit to keep the current value; pass\n`null` to clear it (falling back to the client label heuristic).'
      ),
    default_team_view_id: zod
      .string()
      .nullish()
      .describe(
        'New default team view id. Omit to keep the current value; pass\n`null` to clear it.'
      ),
    delete_records_role: zod
      .union([
        zod.null(),
        zod
          .enum(['admin', 'owner'])
          .describe(
            'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
          ),
      ])
      .optional(),
    edit_stages_role: zod
      .union([
        zod.null(),
        zod
          .enum(['admin', 'owner'])
          .describe(
            'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
          ),
      ])
      .optional(),
    move_closed_deals_role: zod
      .union([
        zod.null(),
        zod
          .enum(['admin', 'owner'])
          .describe(
            'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
          ),
      ])
      .optional(),
    team_views: zod
      .unknown()
      .optional()
      .describe('Replacement team-views array (whole-blob, last write wins).'),
  })
  .describe(
    'Request body for `PUT \/crm\/settings`. Every field is optional:\nomitted fields keep their current values.'
  );

export const putCrmTeamSettingsResponse = zod
  .object({
    closed_stage_ids: zod
      .array(zod.uuid())
      .nullish()
      .describe(
        'Stage option ids counting as closed deals; absent = the client\nfalls back to its label heuristic.'
      ),
    default_team_view_id: zod
      .string()
      .nullish()
      .describe(
        'Team view applied by default when a member opens the CRM view.'
      ),
    delete_records_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    edit_stages_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    legacy_stage_ids: zod
      .record(zod.string(), zod.uuid())
      .describe(
        'System stage option id to team stage option id for seeded stages.'
      ),
    move_closed_deals_role: zod
      .enum(['admin', 'owner'])
      .describe(
        'Minimum team role required for a CRM governance capability. Members\ncan edit visible CRM records (e.g. company properties), but the\ngovernance capabilities these settings gate stay restricted to\nadmin (default) vs owner. Maps to the `team_role` Postgres enum;\n`member` is deliberately not representable.'
      ),
    team_views: zod
      .unknown()
      .describe(
        'Team saved views — an opaque JSON array owned by the frontend.'
      ),
  })
  .describe(
    "The team's CRM configuration (everything on `team_crm_settings`\nexcept the `crm_enabled` killswitch, which is managed via\n`PATCH \/team\/crm` on the auth service)."
  );

/**
 * @summary Replace the team's deal stages; requires `edit_stages_role` (403 otherwise).
 */
export const putCrmTeamStagesBody = zod
  .object({
    stages: zod
      .array(
        zod
          .object({
            id: zod
              .uuid()
              .nullish()
              .describe('Existing stage to keep; omit to add one.'),
            label: zod
              .string()
              .describe(
                'Label after the update; non-blank and unique within the set.'
              ),
          })
          .describe('One stage in a `PUT \/crm\/stages` body.')
      )
      .describe('Stages first to last.'),
  })
  .describe(
    'Request body for `PUT \/crm\/stages`: the whole stage set in order.'
  );

export const putCrmTeamStagesResponse = zod
  .object({
    definition_id: zod.uuid().describe('Team-scoped stage definition id.'),
    stages: zod
      .array(
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'Property option id companies carry as their stage value.'
              ),
            label: zod.string().describe('Label.'),
          })
          .describe("One stage of the team's custom pipeline.")
      )
      .describe('Stages in pipeline order.'),
  })
  .describe("The team's custom stage set.");

/**
 * @summary List the caller's databases.
 */
export const listDatabasesResponseItem = zod
  .object({
    database: zod
      .object({
        created_at: zod.iso.datetime({}).describe('Creation time.'),
        id: zod.uuid().describe('Identifier.'),
        name: zod.string().describe('Display name.'),
        owner_id: zod.string().describe('Owning user.'),
        trashed_at: zod.iso
          .datetime({})
          .nullable()
          .describe('Set when trashed.'),
      })
      .describe(
        'A database: a named collection of tables, owned and shared as one entity.'
      ),
    grant: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    tables: zod
      .array(
        zod
          .object({
            database_id: zod.uuid().describe('Owning database.'),
            id: zod.uuid().describe('Identifier.'),
            name: zod
              .string()
              .describe(
                "Display name; also the basis of the table's SQL name."
              ),
            position: zod
              .string()
              .describe('Fractional index for tab ordering.'),
            version: zod
              .number()
              .describe(
                "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
              ),
          })
          .describe('One table (tab) of a database.')
      )
      .describe(
        "Tables in tab order, so discovery can find a table independently of\nthe containing database's display name."
      ),
  })
  .describe('A database as listed for a viewer.');
export const listDatabasesResponse = zod.array(listDatabasesResponseItem);

/**
 * @summary Create a database owned by the caller; its first table, "Table 1", holds a
"Name" text column.
 */
export const createDatabaseBody = zod
  .object({
    name: zod.string().describe('Display name.'),
  })
  .describe('Request body for creating a database.');

/**
 * @summary Save an immutable query. Editing a question saves a new one.
 */
export const saveDatabaseQueryBody = zod
  .object({
    databaseId: zod
      .uuid()
      .optional()
      .describe(
        'The database whose tables win name resolution. The caller must be\nable to see it.'
      ),
    definition: zod
      .object({
        query: zod
          .string()
          .describe('A read-only SELECT in the databases dialect.'),
        version: zod.literal(1),
      })
      .describe('A versioned query definition.'),
  })
  .describe('Request body for saving a query.');

/**
 * @summary A saved query's definition, for its creator or a viewer of its database.
 */
export const getDatabaseQueryParams = zod.object({
  query_id: zod.uuid().describe('Saved query id'),
});

export const getDatabaseQueryResponse = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('When it was saved.'),
    createdBy: zod
      .string()
      .nullable()
      .describe('Who saved it; `null` once that user is deleted.'),
    databaseId: zod
      .uuid()
      .nullable()
      .describe(
        'The database whose tables win name resolution; `null` once that\ndatabase is deleted, or when none was given.'
      ),
    definition: zod
      .object({
        query: zod
          .string()
          .describe('A read-only SELECT in the databases dialect.'),
        version: zod.literal(1),
      })
      .describe('A versioned query definition.'),
    id: zod.uuid().describe('Identifier.'),
  })
  .describe('A stored, immutable query. Editing a question saves a new one.');

/**
 * @summary Create a small example once for the authenticated user, if they have no databases.
 */
export const ensureStarterHandlerResponse = zod
  .object({
    created: zod
      .boolean()
      .describe('Whether this request created the example.'),
    databaseId: zod
      .string()
      .nullable()
      .describe('Accessible starter database, if still present.'),
    tableId: zod
      .string()
      .nullable()
      .describe('Initial table, returned only on first creation.'),
    viewId: zod
      .string()
      .nullable()
      .describe('Initial board view, returned only on first creation.'),
  })
  .describe(
    'Starter result. A missing database means the user already started or removed it.'
  );

/**
 * @summary Insert rows through a webhook. The body is a JSON object, one row, or
an array of up to 100 objects, one row each. Keys name columns by name
(ignoring case) or id; `null` leaves a cell empty. Values may be loose:
`"45"` for a number, `"true"` for a checkbox, a label or labels for a
select, `2026-10-09` for a date. Nothing is written unless every row fits.
 */
export const deliverDatabaseWebhookParams = zod.object({
  token: zod.string().describe("The webhook's secret token"),
});

export const deliverDatabaseWebhookBody = zod.unknown();

/**
 * @summary Schema detail of one database.
 */
export const getDatabaseParams = zod.object({
  id: zod.uuid().describe('Database id'),
});

export const getDatabaseResponseTablesItemViewsItemLayoutColumnsItemWidthMin = 0;

export const getDatabaseResponse = zod
  .object({
    database: zod
      .object({
        created_at: zod.iso.datetime({}).describe('Creation time.'),
        id: zod.uuid().describe('Identifier.'),
        name: zod.string().describe('Display name.'),
        owner_id: zod.string().describe('Owning user.'),
        trashed_at: zod.iso
          .datetime({})
          .nullable()
          .describe('Set when trashed.'),
      })
      .describe(
        'A database: a named collection of tables, owned and shared as one entity.'
      ),
    grant: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    tables: zod
      .array(
        zod
          .object({
            columns: zod
              .array(
                zod
                  .object({
                    column: zod
                      .object({
                        config: zod.union([
                          zod.null(),
                          zod
                            .union([
                              zod
                                .object({
                                  database_id: zod
                                    .uuid()
                                    .describe('Target database.'),
                                  kind: zod.enum(['link']),
                                  table_id: zod
                                    .uuid()
                                    .describe('Target table.'),
                                })
                                .describe(
                                  'A relation column: its cells reference rows of another table.'
                                ),
                              zod
                                .object({
                                  formula: zod
                                    .union([
                                      zod
                                        .object({
                                          column: zod
                                            .uuid()
                                            .describe('The column placement.'),
                                          kind: zod.enum(['column']),
                                        })
                                        .describe(
                                          "The row's cell in another column of the table: a number, date or\nderived column."
                                        ),
                                      zod
                                        .object({
                                          kind: zod.enum(['number']),
                                          value: zod
                                            .number()
                                            .describe('The value; finite.'),
                                        })
                                        .describe(
                                          'A constant. Added to or subtracted from a date, it counts days.'
                                        ),
                                      zod
                                        .object({
                                          kind: zod.enum(['binary']),
                                          left: zod.unknown(),
                                          operator: zod
                                            .enum([
                                              'add',
                                              'subtract',
                                              'multiply',
                                              'divide',
                                            ])
                                            .describe(
                                              'An arithmetic operator.'
                                            ),
                                          right: zod.unknown(),
                                        })
                                        .describe('Two expressions combined.'),
                                      zod
                                        .object({
                                          kind: zod.enum(['negate']),
                                          operand: zod.unknown(),
                                        })
                                        .describe("An expression's negation."),
                                    ])
                                    .describe(
                                      "An expression over one row's cells."
                                    ),
                                  kind: zod.enum(['derived']),
                                })
                                .describe(
                                  "A derived column: its cells are computed from the row's others, and\nits definition, a number or date one, never holds a value."
                                ),
                            ])
                            .describe(
                              'Column-kind specific configuration stored on the placement.'
                            ),
                        ]),
                        display_name: zod
                          .string()
                          .nullable()
                          .describe(
                            "The placement's own label, which also names it in SQL; `None` shows\nthe definition's name."
                          ),
                        id: zod.uuid().describe('Identifier of the placement.'),
                        infer_type: zod
                          .boolean()
                          .describe(
                            "Whether the first nonempty value may settle this new text column's type."
                          ),
                        nullable: zod
                          .boolean()
                          .optional()
                          .describe(
                            'Whether a row may omit this cell; empty collections also count as absent.'
                          ),
                        position: zod
                          .string()
                          .describe('Fractional index for column ordering.'),
                        property_definition_id: zod
                          .uuid()
                          .describe('The bound property definition.'),
                        protections: zod
                          .array(
                            zod
                              .enum(['delete', 'change_type'])
                              .describe(
                                'A schema operation reserved by a feature using a column.'
                              )
                          )
                          .optional()
                          .describe(
                            'Schema operations reserved by a feature; ordinary edits cannot clear them.'
                          ),
                        table_id: zod
                          .uuid()
                          .describe('Table the column appears on.'),
                      })
                      .describe(
                        'A column: the placement of a property definition on a table.\n\nThe definition carries name, [`DataType`], multi-select flag, and options;\nthis carries only where it appears and column-kind configuration.'
                      ),
                    definition: zod
                      .object({
                        definition: zod
                          .object({
                            created_at: zod.iso.datetime({}),
                            data_type: zod
                              .enum([
                                'BOOLEAN',
                                'DATE',
                                'NUMBER',
                                'STRING',
                                'SELECT_NUMBER',
                                'SELECT_STRING',
                                'TAG',
                                'ENTITY',
                                'LINK',
                              ])
                              .describe(
                                'Data type for property values, determining storage and validation.'
                              ),
                            display_name: zod.string(),
                            id: zod.uuid(),
                            is_metadata: zod
                              .boolean()
                              .describe(
                                'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                              ),
                            is_multi_select: zod.boolean(),
                            is_system: zod
                              .boolean()
                              .describe(
                                'Flag to indicate if this is a system property (stored in DB).'
                              ),
                            owner: zod
                              .union([
                                zod
                                  .object({
                                    scope: zod.enum(['user']),
                                    user_id: zod.string(),
                                  })
                                  .describe('User-scoped property.'),
                                zod
                                  .object({
                                    scope: zod.enum(['team']),
                                    team_id: zod.uuid(),
                                  })
                                  .describe('Team-scoped property.'),
                                zod
                                  .object({
                                    database_id: zod.uuid(),
                                    scope: zod.enum(['database']),
                                  })
                                  .describe(
                                    'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                  ),
                                zod
                                  .object({
                                    scope: zod.enum(['system']),
                                  })
                                  .describe(
                                    'System-owned property (no user, team, or database owner).'
                                  ),
                              ])
                              .describe(
                                'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                              ),
                            specific_entity_type: zod.union([
                              zod.null(),
                              zod
                                .enum([
                                  'CALENDAR_EVENT',
                                  'CALL_RECORD',
                                  'CHANNEL',
                                  'CHAT',
                                  'COMPANY',
                                  'DATABASE_ROW',
                                  'CONTACT',
                                  'DOCUMENT',
                                  'INITIATIVE',
                                  'PROJECT',
                                  'TASK',
                                  'THREAD',
                                  'USER',
                                ])
                                .describe(
                                  'Type of entity that can be referenced by entity properties.'
                                ),
                            ]),
                            updated_at: zod.iso.datetime({}),
                          })
                          .describe(
                            'Property definition model (service representation).'
                          ),
                        property_options: zod.array(
                          zod
                            .object({
                              color: zod.string().nullable(),
                              created_at: zod.iso.datetime({}),
                              display_order: zod.number(),
                              id: zod.uuid(),
                              property_definition_id: zod.uuid(),
                              updated_at: zod.iso.datetime({}),
                              value: zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['string']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value for SelectString properties'
                                        ),
                                    })
                                    .describe(
                                      'String value for SelectString properties'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Number value for SelectNumber properties'
                                        ),
                                    })
                                    .describe(
                                      'Number value for SelectNumber properties'
                                    ),
                                ])
                                .describe(
                                  'The value of a property option - either a string or a number.'
                                ),
                            })
                            .describe(
                              'A selectable option for select-type properties (service representation).'
                            )
                        ),
                      })
                      .describe(
                        'Property definition with its associated options (service representation).'
                      ),
                    shared_outside_database: zod
                      .boolean()
                      .describe(
                        "Whether the definition belongs to something beyond this database (a\nperson's, a team's or a system property), so changing its options\nchanges them everywhere that property is used."
                      ),
                    sql_name: zod
                      .string()
                      .describe(
                        'The name SQL refers to the column by: its display name, quoted.'
                      ),
                    writable: zod
                      .boolean()
                      .describe('Whether SQL may write this column.'),
                  })
                  .describe(
                    'One column placement with the definition behind it.'
                  )
              )
              .describe('Columns in display order.'),
            sql_name: zod
              .string()
              .describe(
                'The name SQL refers to the table by: its display name quoted and\nqualified by the database\'s (`FROM \"Plans\".\"Table 1\"`).'
              ),
            table: zod
              .object({
                database_id: zod.uuid().describe('Owning database.'),
                id: zod.uuid().describe('Identifier.'),
                name: zod
                  .string()
                  .describe(
                    "Display name; also the basis of the table's SQL name."
                  ),
                position: zod
                  .string()
                  .describe('Fractional index for tab ordering.'),
                version: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('One table (tab) of a database.'),
            views: zod
              .array(
                zod
                  .object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('When it was created.'),
                    databaseId: zod
                      .uuid()
                      .describe('The database the table belongs to.'),
                    id: zod.uuid().describe('The view.'),
                    layout: zod
                      .union([
                        zod
                          .object({
                            columns: zod
                              .array(
                                zod
                                  .object({
                                    column: zod.uuid().describe('The column.'),
                                    width: zod
                                      .number()
                                      .min(
                                        getDatabaseResponseTablesItemViewsItemLayoutColumnsItemWidthMin
                                      )
                                      .nullable()
                                      .describe(
                                        'Its width in pixels; the default when unset.'
                                      ),
                                  })
                                  .describe(
                                    'How one column shows in a table layout.'
                                  )
                              )
                              .describe(
                                "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                              ),
                            kind: zod.enum(['table']),
                          })
                          .describe('A grid with a row per row.'),
                        zod
                          .object({
                            cardFields: zod
                              .array(zod.uuid())
                              .describe(
                                'The columns a card shows under its title, in order.'
                              ),
                            groupBy: zod
                              .uuid()
                              .describe(
                                'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                              ),
                            hideEmptyLanes: zod
                              .boolean()
                              .describe(
                                'Whether a lane with no cards is hidden.'
                              ),
                            kind: zod.enum(['board']),
                            lanes: zod
                              .array(
                                zod
                                  .object({
                                    hidden: zod
                                      .boolean()
                                      .optional()
                                      .describe('Whether it is hidden.'),
                                    key: zod
                                      .union([
                                        zod
                                          .object({
                                            id: zod
                                              .uuid()
                                              .describe(
                                                "The cards holding this option of the board's select column."
                                              ),
                                            kind: zod.enum(['option']),
                                          })
                                          .describe(
                                            "The cards holding this option of the board's select column."
                                          ),
                                        zod
                                          .object({
                                            id: zod
                                              .string()
                                              .describe(
                                                "The cards naming this person in the board's person column."
                                              ),
                                            kind: zod.enum(['user']),
                                          })
                                          .describe(
                                            "The cards naming this person in the board's person column."
                                          ),
                                        zod
                                          .object({
                                            kind: zod.enum(['none']),
                                          })
                                          .describe(
                                            'The cards whose grouping cell is empty.'
                                          ),
                                      ])
                                      .describe(
                                        "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                      ),
                                  })
                                  .describe(
                                    'How one lane shows in a board layout.'
                                  )
                              )
                              .describe(
                                "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                              ),
                            title: zod
                              .uuid()
                              .describe(
                                "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                              ),
                          })
                          .describe(
                            'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                          ),
                      ])
                      .describe('How a view draws its rows.'),
                    name: zod
                      .string()
                      .describe(
                        "Its name, unique among the table's views ignoring case."
                      ),
                    position: zod
                      .string()
                      .describe(
                        "Where it sorts among the table's views: a fractional key."
                      ),
                    query: zod
                      .object({
                        filter: zod.union([
                          zod.null(),
                          zod
                            .object({
                              conditions: zod
                                .array(
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          column: zod
                                            .uuid()
                                            .describe('The column tested.'),
                                          test: zod
                                            .union([
                                              zod
                                                .object({
                                                  kind: zod.enum(['presence']),
                                                  operator: zod
                                                    .enum([
                                                      'isEmpty',
                                                      'isNotEmpty',
                                                    ])
                                                    .describe(
                                                      'Whether a cell is empty.'
                                                    ),
                                                })
                                                .describe(
                                                  'Whether the cell is empty; fits a column of any type.'
                                                ),
                                              zod
                                                .object({
                                                  kind: zod.enum(['text']),
                                                  operator: zod
                                                    .enum([
                                                      'is',
                                                      'isNot',
                                                      'contains',
                                                      'doesNotContain',
                                                      'startsWith',
                                                      'endsWith',
                                                    ])
                                                    .describe(
                                                      'How a text cell compares to a text.'
                                                    ),
                                                  value: zod
                                                    .string()
                                                    .describe(
                                                      'The text compared against, ignoring case for the containment\ntests.'
                                                    ),
                                                })
                                                .describe(
                                                  'A text or link column.'
                                                ),
                                              zod
                                                .object({
                                                  kind: zod.enum(['number']),
                                                  operator: zod
                                                    .enum([
                                                      'is',
                                                      'isNot',
                                                      'greaterThan',
                                                      'greaterThanOrEqual',
                                                      'lessThan',
                                                      'lessThanOrEqual',
                                                    ])
                                                    .describe(
                                                      'How a number cell compares to a number.'
                                                    ),
                                                  value: zod
                                                    .number()
                                                    .describe(
                                                      'The number compared against; finite.'
                                                    ),
                                                })
                                                .describe('A number column.'),
                                              zod
                                                .object({
                                                  kind: zod.enum(['date']),
                                                  operator: zod
                                                    .enum([
                                                      'before',
                                                      'after',
                                                      'onOrBefore',
                                                      'onOrAfter',
                                                    ])
                                                    .describe(
                                                      'How a date cell compares to a date-time.'
                                                    ),
                                                  value: zod.iso
                                                    .datetime({})
                                                    .describe(
                                                      'The date-time compared against.'
                                                    ),
                                                })
                                                .describe('A date column.'),
                                              zod
                                                .object({
                                                  checked: zod
                                                    .boolean()
                                                    .describe(
                                                      'Whether the box is checked.'
                                                    ),
                                                  kind: zod.enum(['checkbox']),
                                                })
                                                .describe(
                                                  'A checkbox column. An unchecked box and an empty cell are the same.'
                                                ),
                                              zod
                                                .object({
                                                  kind: zod.enum(['options']),
                                                  operator: zod
                                                    .enum([
                                                      'isAnyOf',
                                                      'isNoneOf',
                                                      'hasAny',
                                                      'hasAll',
                                                      'hasNone',
                                                    ])
                                                    .describe(
                                                      "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                    ),
                                                  options: zod
                                                    .array(zod.uuid())
                                                    .describe(
                                                      'Options of the column; at least one.'
                                                    ),
                                                })
                                                .describe(
                                                  'A select or tag column.'
                                                ),
                                              zod
                                                .object({
                                                  entities: zod
                                                    .array(zod.string())
                                                    .describe(
                                                      "Entity ids, or for a relation the related rows' ids; at least\none."
                                                    ),
                                                  kind: zod.enum(['entities']),
                                                  operator: zod
                                                    .enum([
                                                      'isAnyOf',
                                                      'isNoneOf',
                                                      'hasAny',
                                                      'hasAll',
                                                      'hasNone',
                                                    ])
                                                    .describe(
                                                      "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                    ),
                                                })
                                                .describe(
                                                  'A reference or relation column.'
                                                ),
                                            ])
                                            .describe(
                                              "What a column's cell must be, by the kind of value the column holds."
                                            ),
                                        })
                                        .describe(
                                          "A test of one column's cells."
                                        )
                                        .and(
                                          zod.object({
                                            kind: zod.enum(['condition']),
                                          })
                                        )
                                        .describe('A test of one column.'),
                                      zod
                                        .unknown()
                                        .and(
                                          zod.object({
                                            kind: zod.enum(['group']),
                                          })
                                        )
                                        .describe('A nested group.'),
                                    ])
                                    .describe(
                                      'One entry of a group: a condition, or a group of its own.'
                                    )
                                )
                                .describe(
                                  'The conditions and nested groups. A group without any keeps every\nrow.'
                                ),
                              conjunction: zod
                                .enum(['and', 'or'])
                                .describe("How a group's conditions combine."),
                            })
                            .describe('Conditions joined by one conjunction.'),
                        ]),
                        sort: zod
                          .array(
                            zod
                              .object({
                                column: zod
                                  .uuid()
                                  .describe('The column sorted on.'),
                                direction: zod
                                  .enum(['ascending', 'descending'])
                                  .describe(
                                    'A sort direction. Empty cells sort last either way.'
                                  ),
                              })
                              .describe('One sort key.')
                          )
                          .optional()
                          .describe(
                            "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                          ),
                      })
                      .describe(
                        'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                      ),
                    tableId: zod.uuid().describe('The table it shows.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('When it last changed.'),
                  })
                  .describe('A view of one table, as stored.')
              )
              .describe("The table's views, in their order."),
          })
          .describe('One table with its columns and SQL name.')
      )
      .describe('Tables in tab order.'),
  })
  .describe(
    'Everything a client needs to render and edit one database: tables,\ncolumn placements with their definitions, and the SQL names the query\nsurface exposes them under.'
  );

/**
 * @summary Tell a database's other viewers where the caller is.
 */
export const shareDatabaseAwarenessParams = zod.object({
  id: zod.uuid().describe('Database id'),
});

export const shareDatabaseAwarenessBody = zod
  .object({
    columnId: zod
      .uuid()
      .optional()
      .describe('The column placement of the focused cell, if any.'),
    editing: zod
      .boolean()
      .optional()
      .describe('Whether the cell is open for editing.'),
    endColumnId: zod
      .uuid()
      .optional()
      .describe('The opposite column corner of a selected rectangle, if any.'),
    endRowId: zod
      .uuid()
      .optional()
      .describe('The opposite row corner of a selected rectangle, if any.'),
    left: zod
      .boolean()
      .optional()
      .describe(
        'Whether the viewer left the database; other viewers drop their state.'
      ),
    peerId: zod
      .uuid()
      .optional()
      .describe(
        "This mounted client's random peer id, distinct from its authenticated user.\nOlder clients omit it and remain visible as one peer per user."
      ),
    rowId: zod
      .uuid()
      .optional()
      .describe('The row of the focused cell, if any.'),
    tableId: zod.uuid().describe('The table the viewer is looking at.'),
  })
  .describe(
    'Where one viewer is inside a database right now: ephemeral, relayed to\nthe other viewers and never stored. A missing row or column means the\nviewer is on the table but on no cell. Optional end row and column IDs\nmark the opposite corner of a rectangular cell selection.'
  );

/**
 * @summary Undo one of your own committed changes: its inverse applies as a new,
journaled batch, under the table's lock, guarded against what others
changed since. A cell someone changed since is left alone and listed; a
row or column you added that someone else wrote since, or a name, option,
order, view or card place changed since, refuses the undo. Others'
changes always stay. Redo by undoing the undo's change.
 */
export const undoDatabaseChangeParams = zod.object({
  id: zod.uuid().describe('Database id'),
  change: zod.number().describe("The change's journal id"),
});

export const undoDatabaseChangeResponse = zod
  .object({
    outcome: zod
      .union([
        zod
          .object({
            changes: zod
              .array(
                zod
                  .object({
                    change: zod
                      .number()
                      .describe("The journal's id of the change."),
                    table: zod.uuid().describe('The table.'),
                    version: zod
                      .number()
                      .describe(
                        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                      ),
                  })
                  .describe(
                    "One change a committed batch journaled: a table, the version the batch\nproduced, and the journal's id for it."
                  )
              )
              .describe(
                "The journal's changes the undo made, one per table version; undo\none of them to redo."
              ),
            kind: zod.enum(['reverted']),
          })
          .describe('Everything the change did is undone.'),
        zod
          .object({
            changes: zod
              .array(
                zod
                  .object({
                    change: zod
                      .number()
                      .describe("The journal's id of the change."),
                    table: zod.uuid().describe('The table.'),
                    version: zod
                      .number()
                      .describe(
                        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                      ),
                  })
                  .describe(
                    "One change a committed batch journaled: a table, the version the batch\nproduced, and the journal's id for it."
                  )
              )
              .describe("The journal's changes the undo made."),
            kind: zod.enum(['partial']),
            skipped: zod
              .array(
                zod
                  .object({
                    by: zod
                      .string()
                      .nullable()
                      .describe(
                        'Who changed it since, from the journal; `null` when unknown.'
                      ),
                    column: zod.uuid().describe('The column.'),
                    row: zod.uuid().describe('The row.'),
                  })
                  .describe(
                    'A cell an undo left alone, because someone changed it after the change\nbeing undone.'
                  )
              )
              .describe('The cells left alone, each with who changed it.'),
          })
          .describe(
            'Some cells were changed by someone since, and were left alone; the\nrest is undone. With no changes, nothing was left to undo.'
          ),
        zod
          .object({
            by: zod
              .string()
              .nullable()
              .describe('Whose change stands in the way, when one does.'),
            kind: zod.enum(['refused']),
            reason: zod
              .enum([
                'row_in_use',
                'option_in_use',
                'not_yours',
                'not_undoable',
                'row_edited_since',
                'column_written_since',
                'changed_since',
                'already_back',
              ])
              .describe('Why an undo was refused. Nothing was written.'),
          })
          .describe('Nothing was undone.'),
      ])
      .describe('What undoing a change did.'),
  })
  .describe('What undoing a change did.');

/**
 * @summary Import a new table and every row atomically; retries carry the same request ID.
 */
export const importDatabaseTableParams = zod.object({
  id: zod.uuid(),
});

export const importDatabaseTableBody = zod
  .object({
    columns: zod
      .array(zod.string())
      .describe('Header names, in order. All imported values remain text.'),
    name: zod.string().describe("New table's display name."),
    requestId: zod
      .uuid()
      .describe('Stable key for this import, retained through retries.'),
    rows: zod
      .array(zod.array(zod.string()))
      .describe('Rectangular text rows. Empty fields are preserved.'),
  })
  .describe(
    'An import is identified once, before sending, so retries cannot duplicate rows.'
  );

export const importDatabaseTableResponse = zod
  .object({
    database_id: zod.uuid().describe('Owning database.'),
    id: zod.uuid().describe('Identifier.'),
    name: zod
      .string()
      .describe("Display name; also the basis of the table's SQL name."),
    position: zod.string().describe('Fractional index for tab ordering.'),
    version: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
  })
  .describe('One table (tab) of a database.');

/**
 * @summary Apply a batch of typed ops, the one write surface of a database: add,
rename, remove and order tables and columns, change a column's type, add
and change options, insert, update and delete rows, and write views and a
board's card places. The ops apply in order in one transaction, so a
later op may name a table, column, option or view an earlier one created
under the id its client minted. Ops are last-write-wins unless the batch names
base versions. A refused op, named by its index (and row and column where
relevant), leaves the whole batch unwritten.
 */
export const applyDatabaseOpsParams = zod.object({
  id: zod.uuid().describe('Database id'),
});

export const applyDatabaseOpsBodyOpsItemChangeViewLayoutColumnsItemWidthMin = 0;

export const applyDatabaseOpsBodyOpsItemChangeLayoutColumnsItemWidthMin = 0;

export const applyDatabaseOpsBody = zod
  .object({
    baseVersions: zod
      .record(
        zod.string(),
        zod
          .number()
          .describe(
            "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
          )
      )
      .optional()
      .describe(
        "The version each named table must still be at, as the caller read\nit. A table that moved refuses the batch as a conflict, so a schema\nedit made against what the caller saw does not overwrite another's.\nLeft out, ops are last-write-wins."
      ),
    ops: zod
      .array(
        zod
          .union([
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['create']),
                        name: zod
                          .string()
                          .describe(
                            'Its name, unique within the database ignoring case.'
                          ),
                      })
                      .describe(
                        "Add the table, after the database's other tables. It starts with no\ncolumns and no rows."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['rename']),
                        name: zod
                          .string()
                          .describe(
                            'Its new name, unique within the database ignoring case.'
                          ),
                        previousName: zod
                          .string()
                          .optional()
                          .describe(
                            'The name the caller saw. Given, the rename is refused if the\ntable goes by another one now, so a concurrent rename is not\noverwritten.'
                          ),
                      })
                      .describe(
                        'Rename the table. Its id, columns and rows stay.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe(
                        "Remove the table with its columns, rows and views. A database keeps\nat least one table, and a table another table's relation points at\nstays until that relation goes."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['reorder_columns']),
                        order: zod
                          .array(zod.uuid())
                          .describe('Its columns, in their new order.'),
                      })
                      .describe(
                        "Set the order of the table's columns: `order` names every one of\nthem once."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['reorder_views']),
                        order: zod
                          .array(zod.uuid())
                          .describe('Its views, in their new order.'),
                      })
                      .describe(
                        "Set the order of the table's views: `order` names every one of them\nonce."
                      ),
                  ])
                  .describe('A change to a table itself.'),
                kind: zod.enum(['table']),
                table: zod
                  .uuid()
                  .describe(
                    'The table; for a creation, its new id, minted by the client, which\nlater ops of the request may name.'
                  ),
              })
              .describe(
                'A change to a table itself: its creation, name, removal, or the\norder of its columns or views.'
              ),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        after: zod
                          .uuid()
                          .optional()
                          .describe(
                            "The column it goes right after; left out, it goes after the\ntable's last column."
                          ),
                        definition: zod
                          .union([
                            zod
                              .object({
                                inferType: zod
                                  .boolean()
                                  .optional()
                                  .describe(
                                    "Let the column's first value settle its type: only for a plain\ntext column."
                                  ),
                                name: zod
                                  .string()
                                  .describe(
                                    "The column's name, unique within the table ignoring case."
                                  ),
                                options: zod
                                  .array(
                                    zod
                                      .object({
                                        id: zod
                                          .uuid()
                                          .describe(
                                            'Its id, minted by the client; later ops of the request may name it.'
                                          ),
                                        label: zod
                                          .string()
                                          .describe(
                                            "Its label, unique within the column ignoring case. A numeric\nselect's labels are numbers."
                                          ),
                                      })
                                      .describe(
                                        'A select or tag option to create.'
                                      )
                                  )
                                  .optional()
                                  .describe(
                                    'For a select or tag column, the options it starts with, in\norder, each under an id the client mints. A select column with\nnone accepts nothing until options are added.'
                                  ),
                                source: zod.enum(['new']),
                                type: zod
                                  .union([
                                    zod
                                      .object({
                                        type: zod.enum(['text']),
                                      })
                                      .describe('Free text.'),
                                    zod
                                      .object({
                                        type: zod.enum(['number']),
                                      })
                                      .describe('A number.'),
                                    zod
                                      .object({
                                        type: zod.enum(['boolean']),
                                      })
                                      .describe('A checkbox.'),
                                    zod
                                      .object({
                                        type: zod.enum(['date']),
                                      })
                                      .describe('A date-time.'),
                                    zod
                                      .object({
                                        type: zod.enum(['link']),
                                      })
                                      .describe('A URL.'),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several options.'
                                          ),
                                        type: zod.enum(['select']),
                                      })
                                      .describe('Text options.'),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several options.'
                                          ),
                                        type: zod.enum(['select_number']),
                                      })
                                      .describe('Numeric options.'),
                                    zod
                                      .object({
                                        type: zod.enum(['tag']),
                                      })
                                      .describe(
                                        'Colored labels; always several per cell.'
                                      ),
                                    zod
                                      .object({
                                        multi: zod
                                          .boolean()
                                          .describe(
                                            'Whether a cell holds several references.'
                                          ),
                                        target: zod
                                          .enum([
                                            'USER',
                                            'DOCUMENT',
                                            'TASK',
                                            'COMPANY',
                                            'CONTACT',
                                            'CALL_RECORD',
                                            'CHANNEL',
                                            'CHAT',
                                            'PROJECT',
                                            'THREAD',
                                            'CALENDAR_EVENT',
                                            'INITIATIVE',
                                          ])
                                          .describe(
                                            'A kind of Macro entity a reference column can point at.'
                                          ),
                                        type: zod.enum(['entity']),
                                      })
                                      .describe(
                                        'References to Macro entities.'
                                      ),
                                    zod
                                      .object({
                                        database: zod
                                          .uuid()
                                          .describe(
                                            'The database of the related table.'
                                          ),
                                        table: zod
                                          .uuid()
                                          .describe('The related table.'),
                                        type: zod.enum(['relation']),
                                      })
                                      .describe('Rows of another table.'),
                                  ])
                                  .describe('A type a column can have.'),
                              })
                              .describe('A new property the database owns.'),
                            zod
                              .object({
                                formula: zod
                                  .union([
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column placement.'),
                                        kind: zod.enum(['column']),
                                      })
                                      .describe(
                                        "The row's cell in another column of the table: a number, date or\nderived column."
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['number']),
                                        value: zod
                                          .number()
                                          .describe('The value; finite.'),
                                      })
                                      .describe(
                                        'A constant. Added to or subtracted from a date, it counts days.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['binary']),
                                        left: zod.unknown(),
                                        operator: zod
                                          .enum([
                                            'add',
                                            'subtract',
                                            'multiply',
                                            'divide',
                                          ])
                                          .describe('An arithmetic operator.'),
                                        right: zod.unknown(),
                                      })
                                      .describe('Two expressions combined.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['negate']),
                                        operand: zod.unknown(),
                                      })
                                      .describe("An expression's negation."),
                                  ])
                                  .describe(
                                    "An expression over one row's cells."
                                  ),
                                name: zod
                                  .string()
                                  .describe(
                                    "The column's name, unique within the table ignoring case."
                                  ),
                                source: zod.enum(['derived']),
                              })
                              .describe(
                                "A derived column: its cells are what `formula` computes from the\nrow's other cells, and nothing writes them."
                              ),
                            zod
                              .object({
                                property: zod
                                  .uuid()
                                  .describe("The property's definition."),
                                source: zod.enum(['existing']),
                              })
                              .describe(
                                "An existing property, a person's, a team's or a system one, bound\ninto the table under its own name."
                              ),
                          ])
                          .describe('What a new column holds.'),
                        kind: zod.enum(['create']),
                      })
                      .describe(
                        'Add the column to the table: a new property the database owns, or an\nexisting one bound into the table.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['rename']),
                        name: zod
                          .string()
                          .describe(
                            'Its new name, unique within the table ignoring case.'
                          ),
                        previousName: zod
                          .string()
                          .optional()
                          .describe(
                            'The name the caller saw. Given, the rename is refused if the\ncolumn goes by another one now.'
                          ),
                      })
                      .describe(
                        'Rename the column. Its id, type and cells stay; SQL names it by its\nnew name.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['change_type']),
                        to: zod
                          .union([
                            zod
                              .object({
                                type: zod.enum(['text']),
                              })
                              .describe('Free text.'),
                            zod
                              .object({
                                type: zod.enum(['number']),
                              })
                              .describe('A number.'),
                            zod
                              .object({
                                type: zod.enum(['boolean']),
                              })
                              .describe('A checkbox.'),
                            zod
                              .object({
                                type: zod.enum(['date']),
                              })
                              .describe('A date-time.'),
                            zod
                              .object({
                                type: zod.enum(['link']),
                              })
                              .describe('A URL.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select']),
                              })
                              .describe('Text options.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select_number']),
                              })
                              .describe('Numeric options.'),
                            zod
                              .object({
                                type: zod.enum(['tag']),
                              })
                              .describe(
                                'Colored labels; always several per cell.'
                              ),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several references.'
                                  ),
                                target: zod
                                  .enum([
                                    'USER',
                                    'DOCUMENT',
                                    'TASK',
                                    'COMPANY',
                                    'CONTACT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'PROJECT',
                                    'THREAD',
                                    'CALENDAR_EVENT',
                                    'INITIATIVE',
                                  ])
                                  .describe(
                                    'A kind of Macro entity a reference column can point at.'
                                  ),
                                type: zod.enum(['entity']),
                              })
                              .describe('References to Macro entities.'),
                            zod
                              .object({
                                database: zod
                                  .uuid()
                                  .describe(
                                    'The database of the related table.'
                                  ),
                                table: zod
                                  .uuid()
                                  .describe('The related table.'),
                                type: zod.enum(['relation']),
                              })
                              .describe('Rows of another table.'),
                          ])
                          .describe('A type a column can have.'),
                      })
                      .describe(
                        'Convert the column to another type, converting its cells; its id\nsurvives the change. A value that does not fit refuses the change,\ncounting and quoting the misfits: a type change never empties a\ncell. To keep the original, create a column of the new type and\nwrite it the values that convert.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe(
                        'Remove the column and its cells. The views naming it forget it; a\nboard grouped by it must go or regroup first. A property shared\nbeyond the database stays, unbound here.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['add_options']),
                        options: zod
                          .array(
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Its id, minted by the client; later ops of the request may name it.'
                                  ),
                                label: zod
                                  .string()
                                  .describe(
                                    "Its label, unique within the column ignoring case. A numeric\nselect's labels are numbers."
                                  ),
                              })
                              .describe('A select or tag option to create.')
                          )
                          .describe(
                            'The options, each under an id the client mints.'
                          ),
                      })
                      .describe(
                        'Add options to a select or tag column, after its others. An option\nwhose label the column already has, ignoring case, is left out, so\nre-sending a list adds only what is new. Like\n[`ColumnChange::UpdateOption`], an option of a property shared beyond\nthe database goes everywhere it is used.'
                      ),
                    zod
                      .object({
                        color: zod
                          .string()
                          .nullish()
                          .describe(
                            'Its new colour, a hex string like `#RRGGBB`, or `null` to clear\nit; left out, it keeps its own. A tag option always has one.'
                          ),
                        kind: zod.enum(['update_option']),
                        label: zod
                          .string()
                          .optional()
                          .describe(
                            'Its new label; left out, it keeps its own. Labels are unique\nwithin a column, ignoring case.'
                          ),
                        option: zod.uuid().describe('The option.'),
                      })
                      .describe(
                        'Relabel or recolour one option of a select or tag column. Every cell\nholding it keeps it. A column bound to a property shared outside the\ndatabase changes wherever that property is used, so it takes the\nright to edit that property.'
                      ),
                    zod
                      .object({
                        formula: zod
                          .union([
                            zod
                              .object({
                                column: zod
                                  .uuid()
                                  .describe('The column placement.'),
                                kind: zod.enum(['column']),
                              })
                              .describe(
                                "The row's cell in another column of the table: a number, date or\nderived column."
                              ),
                            zod
                              .object({
                                kind: zod.enum(['number']),
                                value: zod
                                  .number()
                                  .describe('The value; finite.'),
                              })
                              .describe(
                                'A constant. Added to or subtracted from a date, it counts days.'
                              ),
                            zod
                              .object({
                                kind: zod.enum(['binary']),
                                left: zod.unknown(),
                                operator: zod
                                  .enum([
                                    'add',
                                    'subtract',
                                    'multiply',
                                    'divide',
                                  ])
                                  .describe('An arithmetic operator.'),
                                right: zod.unknown(),
                              })
                              .describe('Two expressions combined.'),
                            zod
                              .object({
                                kind: zod.enum(['negate']),
                                operand: zod.unknown(),
                              })
                              .describe("An expression's negation."),
                          ])
                          .describe("An expression over one row's cells."),
                        kind: zod.enum(['set_formula']),
                      })
                      .describe(
                        'Give a derived column a new formula. Its cells follow at once: it\nstores none.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete_option']),
                        option: zod.uuid().describe('The option.'),
                      })
                      .describe(
                        'Remove one option of a select or tag column, and take it out of every\ncell holding it: a single-valued cell is emptied, a multi-valued one\nkeeps its other options. Like [`ColumnChange::UpdateOption`], an\noption of a shared property goes everywhere it is used.'
                      ),
                  ])
                  .describe('A change to one column.'),
                column: zod
                  .uuid()
                  .describe(
                    'The column; for a creation, its new id, minted by the client,\nwhich later ops of the request may name.'
                  ),
                kind: zod.enum(['column']),
                table: zod.uuid().describe('The table.'),
              })
              .describe(
                'A change to one column of a table: its creation, name, type, removal\nor options.'
              ),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['insert']),
                        rows: zod
                          .array(
                            zod.array(
                              zod
                                .object({
                                  column: zod
                                    .uuid()
                                    .describe('The column placement.'),
                                  value: zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['text']),
                                          value: zod
                                            .string()
                                            .describe('Free text.'),
                                        })
                                        .describe('Free text.'),
                                      zod
                                        .object({
                                          type: zod.enum(['number']),
                                          value: zod
                                            .number()
                                            .describe('A finite number.'),
                                        })
                                        .describe('A finite number.'),
                                      zod
                                        .object({
                                          type: zod.enum(['boolean']),
                                          value: zod
                                            .boolean()
                                            .describe('A checkbox.'),
                                        })
                                        .describe('A checkbox.'),
                                      zod
                                        .object({
                                          type: zod.enum(['date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe('A date-time.'),
                                        })
                                        .describe('A date-time.'),
                                      zod
                                        .object({
                                          type: zod.enum(['link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Complete http or https URLs; at most one for a single-valued column.'
                                            ),
                                        })
                                        .describe(
                                          'Complete http or https URLs; at most one for a single-valued column.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['options']),
                                          value: zod
                                            .array(
                                              zod
                                                .union([
                                                  zod
                                                    .object({
                                                      id: zod
                                                        .uuid()
                                                        .describe(
                                                          'An option the column has.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'An option the column has.'
                                                    ),
                                                  zod
                                                    .object({
                                                      label: zod
                                                        .string()
                                                        .describe(
                                                          "An option's label, matched without regard to case. An unknown label\nis refused."
                                                        ),
                                                    })
                                                    .describe(
                                                      "An option's label, matched without regard to case. An unknown label\nis refused."
                                                    ),
                                                ])
                                                .describe(
                                                  'A select option, by its id or by its label.'
                                                )
                                            )
                                            .describe(
                                              'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                            ),
                                        })
                                        .describe(
                                          'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['entities']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entityId: zod
                                                    .string()
                                                    .describe(
                                                      "The entity's id."
                                                    ),
                                                  entityType: zod
                                                    .enum([
                                                      'USER',
                                                      'DOCUMENT',
                                                      'TASK',
                                                      'COMPANY',
                                                      'CONTACT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'PROJECT',
                                                      'THREAD',
                                                      'CALENDAR_EVENT',
                                                      'INITIATIVE',
                                                    ])
                                                    .describe(
                                                      'A kind of Macro entity a reference column can point at.'
                                                    ),
                                                })
                                                .describe(
                                                  'A reference to one Macro entity.'
                                                )
                                            )
                                            .describe(
                                              'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                            ),
                                        })
                                        .describe(
                                          'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['rows']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Rows of the table a relation column points at.'
                                            ),
                                        })
                                        .describe(
                                          'Rows of the table a relation column points at.'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['clear']),
                                        })
                                        .describe(
                                          'No value: the cell is emptied.'
                                        ),
                                    ])
                                    .describe(
                                      "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                    ),
                                })
                                .describe(
                                  'One cell of a row: which column, and its new value.'
                                )
                            )
                          )
                          .describe(
                            'One entry per new row: the cells it starts with. Columns left out\nstart empty.'
                          ),
                      })
                      .describe(
                        'Append rows to the table, in order, each with the cells it starts\nwith.'
                      ),
                    zod
                      .object({
                        changes: zod
                          .union([
                            zod
                              .object({
                                cells: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column placement.'),
                                        value: zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['text']),
                                                value: zod
                                                  .string()
                                                  .describe('Free text.'),
                                              })
                                              .describe('Free text.'),
                                            zod
                                              .object({
                                                type: zod.enum(['number']),
                                                value: zod
                                                  .number()
                                                  .describe('A finite number.'),
                                              })
                                              .describe('A finite number.'),
                                            zod
                                              .object({
                                                type: zod.enum(['boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe('A checkbox.'),
                                              })
                                              .describe('A checkbox.'),
                                            zod
                                              .object({
                                                type: zod.enum(['date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe('A date-time.'),
                                              })
                                              .describe('A date-time.'),
                                            zod
                                              .object({
                                                type: zod.enum(['link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Complete http or https URLs; at most one for a single-valued column.'
                                                  ),
                                              })
                                              .describe(
                                                'Complete http or https URLs; at most one for a single-valued column.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['options']),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .union([
                                                        zod
                                                          .object({
                                                            id: zod
                                                              .uuid()
                                                              .describe(
                                                                'An option the column has.'
                                                              ),
                                                          })
                                                          .describe(
                                                            'An option the column has.'
                                                          ),
                                                        zod
                                                          .object({
                                                            label: zod
                                                              .string()
                                                              .describe(
                                                                "An option's label, matched without regard to case. An unknown label\nis refused."
                                                              ),
                                                          })
                                                          .describe(
                                                            "An option's label, matched without regard to case. An unknown label\nis refused."
                                                          ),
                                                      ])
                                                      .describe(
                                                        'A select option, by its id or by its label.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                  ),
                                              })
                                              .describe(
                                                'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['entities']),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entityId: zod
                                                          .string()
                                                          .describe(
                                                            "The entity's id."
                                                          ),
                                                        entityType: zod
                                                          .enum([
                                                            'USER',
                                                            'DOCUMENT',
                                                            'TASK',
                                                            'COMPANY',
                                                            'CONTACT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'PROJECT',
                                                            'THREAD',
                                                            'CALENDAR_EVENT',
                                                            'INITIATIVE',
                                                          ])
                                                          .describe(
                                                            'A kind of Macro entity a reference column can point at.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'A reference to one Macro entity.'
                                                      )
                                                  )
                                                  .describe(
                                                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                  ),
                                              })
                                              .describe(
                                                'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['rows']),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Rows of the table a relation column points at.'
                                                  ),
                                              })
                                              .describe(
                                                'Rows of the table a relation column points at.'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['clear']),
                                              })
                                              .describe(
                                                'No value: the cell is emptied.'
                                              ),
                                          ])
                                          .describe(
                                            "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                          ),
                                      })
                                      .describe(
                                        'One cell of a row: which column, and its new value.'
                                      )
                                  )
                                  .describe('The cells each of them gets.'),
                                kind: zod.enum(['uniform']),
                                rows: zod
                                  .array(zod.uuid())
                                  .describe('The rows.'),
                              })
                              .describe('The same cells on every row.'),
                            zod
                              .object({
                                kind: zod.enum(['per_row']),
                                rows: zod
                                  .array(
                                    zod
                                      .object({
                                        cells: zod
                                          .array(
                                            zod
                                              .object({
                                                column: zod
                                                  .uuid()
                                                  .describe(
                                                    'The column placement.'
                                                  ),
                                                value: zod
                                                  .union([
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'text',
                                                        ]),
                                                        value: zod
                                                          .string()
                                                          .describe(
                                                            'Free text.'
                                                          ),
                                                      })
                                                      .describe('Free text.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'number',
                                                        ]),
                                                        value: zod
                                                          .number()
                                                          .describe(
                                                            'A finite number.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'A finite number.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'boolean',
                                                        ]),
                                                        value: zod
                                                          .boolean()
                                                          .describe(
                                                            'A checkbox.'
                                                          ),
                                                      })
                                                      .describe('A checkbox.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'date',
                                                        ]),
                                                        value: zod.iso
                                                          .datetime({})
                                                          .describe(
                                                            'A date-time.'
                                                          ),
                                                      })
                                                      .describe('A date-time.'),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'link',
                                                        ]),
                                                        value: zod
                                                          .array(zod.string())
                                                          .describe(
                                                            'Complete http or https URLs; at most one for a single-valued column.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Complete http or https URLs; at most one for a single-valued column.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'options',
                                                        ]),
                                                        value: zod
                                                          .array(
                                                            zod
                                                              .union([
                                                                zod
                                                                  .object({
                                                                    id: zod
                                                                      .uuid()
                                                                      .describe(
                                                                        'An option the column has.'
                                                                      ),
                                                                  })
                                                                  .describe(
                                                                    'An option the column has.'
                                                                  ),
                                                                zod
                                                                  .object({
                                                                    label: zod
                                                                      .string()
                                                                      .describe(
                                                                        "An option's label, matched without regard to case. An unknown label\nis refused."
                                                                      ),
                                                                  })
                                                                  .describe(
                                                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                                                  ),
                                                              ])
                                                              .describe(
                                                                'A select option, by its id or by its label.'
                                                              )
                                                          )
                                                          .describe(
                                                            'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'entities',
                                                        ]),
                                                        value: zod
                                                          .array(
                                                            zod
                                                              .object({
                                                                entityId: zod
                                                                  .string()
                                                                  .describe(
                                                                    "The entity's id."
                                                                  ),
                                                                entityType: zod
                                                                  .enum([
                                                                    'USER',
                                                                    'DOCUMENT',
                                                                    'TASK',
                                                                    'COMPANY',
                                                                    'CONTACT',
                                                                    'CALL_RECORD',
                                                                    'CHANNEL',
                                                                    'CHAT',
                                                                    'PROJECT',
                                                                    'THREAD',
                                                                    'CALENDAR_EVENT',
                                                                    'INITIATIVE',
                                                                  ])
                                                                  .describe(
                                                                    'A kind of Macro entity a reference column can point at.'
                                                                  ),
                                                              })
                                                              .describe(
                                                                'A reference to one Macro entity.'
                                                              )
                                                          )
                                                          .describe(
                                                            'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'rows',
                                                        ]),
                                                        value: zod
                                                          .array(zod.uuid())
                                                          .describe(
                                                            'Rows of the table a relation column points at.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Rows of the table a relation column points at.'
                                                      ),
                                                    zod
                                                      .object({
                                                        type: zod.enum([
                                                          'clear',
                                                        ]),
                                                      })
                                                      .describe(
                                                        'No value: the cell is emptied.'
                                                      ),
                                                  ])
                                                  .describe(
                                                    "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                                                  ),
                                              })
                                              .describe(
                                                'One cell of a row: which column, and its new value.'
                                              )
                                          )
                                          .describe('Its new cells.'),
                                        row: zod.uuid().describe('The row.'),
                                      })
                                      .describe(
                                        "One row's cells in a [`RowChanges::PerRow`] update."
                                      )
                                  )
                                  .describe(
                                    'The rows and their cells, in order.'
                                  ),
                              })
                              .describe('Each row its own cells.'),
                          ])
                          .describe(
                            'Which rows an update writes, and with what.'
                          ),
                        kind: zod.enum(['update']),
                      })
                      .describe(
                        'Write cells of existing rows. Last write wins: there is no version\ncheck.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                        rows: zod
                          .array(zod.uuid())
                          .describe('The rows, each named once.'),
                      })
                      .describe('Remove rows and their cells.'),
                  ])
                  .describe("A write to a table's rows."),
                kind: zod.enum(['rows']),
                table: zod.uuid().describe('The table the rows belong to.'),
              })
              .describe("A write to a table's rows."),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['create']),
                        view: zod
                          .object({
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyDatabaseOpsBodyOpsItemChangeViewLayoutColumnsItemWidthMin
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        'How lanes show, in display order.'
                                      ),
                                    title: zod
                                      .uuid()
                                      .optional()
                                      .describe(
                                        "The column a card is titled by. Left out, a board keeps the\ntitle it has, and a new board takes the table's first column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes, one per option of a single-select column or per\nperson of a single-person column, plus one for empty cells.'
                                  ),
                              ])
                              .describe(
                                'A layout as an op asks for it: a board may leave its card title out.'
                              ),
                            name: zod.string().describe('Its name.'),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .optional()
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                          })
                          .describe(
                            "A view's contents as an op creates it; the server gives it its id,\nposition and times."
                          ),
                      })
                      .describe(
                        'Add the view to the table, after its other views.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['update']),
                        layout: zod
                          .union([
                            zod
                              .object({
                                columns: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column.'),
                                        width: zod
                                          .number()
                                          .min(
                                            applyDatabaseOpsBodyOpsItemChangeLayoutColumnsItemWidthMin
                                          )
                                          .nullable()
                                          .describe(
                                            'Its width in pixels; the default when unset.'
                                          ),
                                      })
                                      .describe(
                                        'How one column shows in a table layout.'
                                      )
                                  )
                                  .describe(
                                    "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                  ),
                                kind: zod.enum(['table']),
                              })
                              .describe('A grid with a row per row.'),
                            zod
                              .object({
                                cardFields: zod
                                  .array(zod.uuid())
                                  .describe(
                                    'The columns a card shows under its title, in order.'
                                  ),
                                groupBy: zod
                                  .uuid()
                                  .describe(
                                    'The single-select or single-person column whose values are the\nlanes.'
                                  ),
                                hideEmptyLanes: zod
                                  .boolean()
                                  .describe(
                                    'Whether a lane with no cards is hidden.'
                                  ),
                                kind: zod.enum(['board']),
                                lanes: zod
                                  .array(
                                    zod
                                      .object({
                                        hidden: zod
                                          .boolean()
                                          .optional()
                                          .describe('Whether it is hidden.'),
                                        key: zod
                                          .union([
                                            zod
                                              .object({
                                                id: zod
                                                  .uuid()
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                kind: zod.enum(['option']),
                                              })
                                              .describe(
                                                "The cards holding this option of the board's select column."
                                              ),
                                            zod
                                              .object({
                                                id: zod
                                                  .string()
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                kind: zod.enum(['user']),
                                              })
                                              .describe(
                                                "The cards naming this person in the board's person column."
                                              ),
                                            zod
                                              .object({
                                                kind: zod.enum(['none']),
                                              })
                                              .describe(
                                                'The cards whose grouping cell is empty.'
                                              ),
                                          ])
                                          .describe(
                                            "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                          ),
                                      })
                                      .describe(
                                        'How one lane shows in a board layout.'
                                      )
                                  )
                                  .describe(
                                    'How lanes show, in display order.'
                                  ),
                                title: zod
                                  .uuid()
                                  .optional()
                                  .describe(
                                    "The column a card is titled by. Left out, a board keeps the\ntitle it has, and a new board takes the table's first column."
                                  ),
                              })
                              .describe(
                                'Cards in lanes, one per option of a single-select column or per\nperson of a single-person column, plus one for empty cells.'
                              ),
                          ])
                          .optional()
                          .describe(
                            'A layout as an op asks for it: a board may leave its card title out.'
                          ),
                        name: zod.string().optional().describe('Its new name.'),
                        query: zod
                          .object({
                            filter: zod.union([
                              zod.null(),
                              zod
                                .object({
                                  conditions: zod
                                    .array(
                                      zod
                                        .union([
                                          zod
                                            .object({
                                              column: zod
                                                .uuid()
                                                .describe('The column tested.'),
                                              test: zod
                                                .union([
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'presence',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isEmpty',
                                                          'isNotEmpty',
                                                        ])
                                                        .describe(
                                                          'Whether a cell is empty.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'Whether the cell is empty; fits a column of any type.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum(['text']),
                                                      operator: zod
                                                        .enum([
                                                          'is',
                                                          'isNot',
                                                          'contains',
                                                          'doesNotContain',
                                                          'startsWith',
                                                          'endsWith',
                                                        ])
                                                        .describe(
                                                          'How a text cell compares to a text.'
                                                        ),
                                                      value: zod
                                                        .string()
                                                        .describe(
                                                          'The text compared against, ignoring case for the containment\ntests.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A text or link column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'number',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'is',
                                                          'isNot',
                                                          'greaterThan',
                                                          'greaterThanOrEqual',
                                                          'lessThan',
                                                          'lessThanOrEqual',
                                                        ])
                                                        .describe(
                                                          'How a number cell compares to a number.'
                                                        ),
                                                      value: zod
                                                        .number()
                                                        .describe(
                                                          'The number compared against; finite.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A number column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum(['date']),
                                                      operator: zod
                                                        .enum([
                                                          'before',
                                                          'after',
                                                          'onOrBefore',
                                                          'onOrAfter',
                                                        ])
                                                        .describe(
                                                          'How a date cell compares to a date-time.'
                                                        ),
                                                      value: zod.iso
                                                        .datetime({})
                                                        .describe(
                                                          'The date-time compared against.'
                                                        ),
                                                    })
                                                    .describe('A date column.'),
                                                  zod
                                                    .object({
                                                      checked: zod
                                                        .boolean()
                                                        .describe(
                                                          'Whether the box is checked.'
                                                        ),
                                                      kind: zod.enum([
                                                        'checkbox',
                                                      ]),
                                                    })
                                                    .describe(
                                                      'A checkbox column. An unchecked box and an empty cell are the same.'
                                                    ),
                                                  zod
                                                    .object({
                                                      kind: zod.enum([
                                                        'options',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isAnyOf',
                                                          'isNoneOf',
                                                          'hasAny',
                                                          'hasAll',
                                                          'hasNone',
                                                        ])
                                                        .describe(
                                                          "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                        ),
                                                      options: zod
                                                        .array(zod.uuid())
                                                        .describe(
                                                          'Options of the column; at least one.'
                                                        ),
                                                    })
                                                    .describe(
                                                      'A select or tag column.'
                                                    ),
                                                  zod
                                                    .object({
                                                      entities: zod
                                                        .array(zod.string())
                                                        .describe(
                                                          "Entity ids, or for a relation the related rows' ids; at least\none."
                                                        ),
                                                      kind: zod.enum([
                                                        'entities',
                                                      ]),
                                                      operator: zod
                                                        .enum([
                                                          'isAnyOf',
                                                          'isNoneOf',
                                                          'hasAny',
                                                          'hasAll',
                                                          'hasNone',
                                                        ])
                                                        .describe(
                                                          "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                        ),
                                                    })
                                                    .describe(
                                                      'A reference or relation column.'
                                                    ),
                                                ])
                                                .describe(
                                                  "What a column's cell must be, by the kind of value the column holds."
                                                ),
                                            })
                                            .describe(
                                              "A test of one column's cells."
                                            )
                                            .and(
                                              zod.object({
                                                kind: zod.enum(['condition']),
                                              })
                                            )
                                            .describe('A test of one column.'),
                                          zod
                                            .unknown()
                                            .and(
                                              zod.object({
                                                kind: zod.enum(['group']),
                                              })
                                            )
                                            .describe('A nested group.'),
                                        ])
                                        .describe(
                                          'One entry of a group: a condition, or a group of its own.'
                                        )
                                    )
                                    .describe(
                                      'The conditions and nested groups. A group without any keeps every\nrow.'
                                    ),
                                  conjunction: zod
                                    .enum(['and', 'or'])
                                    .describe(
                                      "How a group's conditions combine."
                                    ),
                                })
                                .describe(
                                  'Conditions joined by one conjunction.'
                                ),
                            ]),
                            sort: zod
                              .array(
                                zod
                                  .object({
                                    column: zod
                                      .uuid()
                                      .describe('The column sorted on.'),
                                    direction: zod
                                      .enum(['ascending', 'descending'])
                                      .describe(
                                        'A sort direction. Empty cells sort last either way.'
                                      ),
                                  })
                                  .describe('One sort key.')
                              )
                              .optional()
                              .describe(
                                "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                              ),
                          })
                          .optional()
                          .describe(
                            'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                          ),
                      })
                      .describe(
                        "Change the view's name, query or layout; what is left out stays."
                      ),
                    zod
                      .object({
                        kind: zod.enum(['delete']),
                      })
                      .describe('Remove the view, with where its cards were.'),
                    zod
                      .object({
                        after: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'The card that ends up just after it, if any. Given with `before`,\nit must be the card right after `before`; with neither, the card\ngoes to the end of the lane.'
                          ),
                        before: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'The card that ends up just before it (it lands right after this\none), if any.'
                          ),
                        kind: zod.enum(['move_card']),
                        lane: zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe(
                                    "The cards holding this option of the board's select column."
                                  ),
                                kind: zod.enum(['option']),
                              })
                              .describe(
                                "The cards holding this option of the board's select column."
                              ),
                            zod
                              .object({
                                id: zod
                                  .string()
                                  .describe(
                                    "The cards naming this person in the board's person column."
                                  ),
                                kind: zod.enum(['user']),
                              })
                              .describe(
                                "The cards naming this person in the board's person column."
                              ),
                            zod
                              .object({
                                kind: zod.enum(['none']),
                              })
                              .describe(
                                'The cards whose grouping cell is empty.'
                              ),
                          ])
                          .describe(
                            "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                          ),
                        row: zod.uuid().describe("The card's row."),
                      })
                      .describe(
                        "Move one of the board's cards: into a lane, which sets the row's\ngrouping cell to the lane's option or person (or empties it for the\nlane of empty cells), and to a place there, between two of its\ncards. Only an unsorted board's cards move by hand."
                      ),
                  ])
                  .describe('A change to one view.'),
                kind: zod.enum(['view']),
                table: zod.uuid().describe("The view's table."),
                view: zod
                  .uuid()
                  .describe(
                    'The view; for a creation, its new id, minted by the client.'
                  ),
              })
              .describe('A change to one view of a table.'),
            zod
              .object({
                kind: zod.enum(['reorder_tables']),
                order: zod
                  .array(zod.uuid())
                  .describe('Every table, in its new order.'),
              })
              .describe(
                "Set the order of the database's tables: `order` names every one of\nthem once."
              ),
          ])
          .describe(
            "One write to a database: its tables, columns, options, rows or views,\ngrouped by the resource it changes. A request's ops apply in order and\ntogether, or not at all, and every op names a table of the database the\nrequest is for (or, creating one, adds it there)."
          )
      )
      .describe(
        'The ops, in the order they apply, each grouped by the resource it\nchanges (`table`, `column`, `rows`, `view`, `reorder_tables`) with a\n`change` saying how. Every one names a table of this database, or one\nan earlier op of the batch creates: tables, columns, options and\nviews carry ids the client mints (UUIDv7), so a later op can name\nthem. An id that already names something refuses the batch.'
      ),
  })
  .describe(
    'A batch of ops for one database, applied in order, in one transaction,\ntogether or not at all.'
  );

export const applyDatabaseOpsResponseResultsItemChangeAffectedMin = 0;

export const applyDatabaseOpsResponseResultsItemChangeAffectedMinOne = 0;

export const applyDatabaseOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMin = 0;

export const applyDatabaseOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMinOne = 0;

export const applyDatabaseOpsResponse = zod
  .object({
    changes: zod
      .array(
        zod
          .object({
            change: zod.number().describe("The journal's id of the change."),
            table: zod.uuid().describe('The table.'),
            version: zod
              .number()
              .describe(
                "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
              ),
          })
          .describe(
            "One change a committed batch journaled: a table, the version the batch\nproduced, and the journal's id for it."
          )
      )
      .describe(
        "The journal's change for each table version the batch produced: the\nids `POST \/databases\/{id}\/changes\/{change}\/undo` takes."
      ),
    results: zod
      .array(
        zod
          .union([
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['renamed']),
                      })
                      .describe('It was renamed.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['columns_reordered']),
                      })
                      .describe('Its columns were reordered.'),
                    zod
                      .object({
                        kind: zod.enum(['views_reordered']),
                        positions: zod
                          .array(
                            zod
                              .object({
                                position: zod.string().describe('Its key.'),
                                view: zod.uuid().describe('The view.'),
                              })
                              .describe(
                                "A view's place among its table's views."
                              )
                          )
                          .describe("Every view's key, in their new order."),
                      })
                      .describe('Its views were reordered.'),
                  ])
                  .describe('What happened to a table.'),
                kind: zod.enum(['table']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .optional()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a table op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['renamed']),
                      })
                      .describe('It was renamed.'),
                    zod
                      .object({
                        kind: zod.enum(['type_changed']),
                      })
                      .describe('Its type changed, and its cells with it.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        added: zod
                          .array(zod.uuid())
                          .describe(
                            'The options created, in order: those sent, less any whose label\nthe column already had.'
                          ),
                        kind: zod.enum(['options_added']),
                      })
                      .describe('It gained options.'),
                    zod
                      .object({
                        kind: zod.enum(['option_updated']),
                      })
                      .describe(
                        'One of its options was relabelled or recoloured.'
                      ),
                    zod
                      .object({
                        kind: zod.enum(['option_deleted']),
                      })
                      .describe('One of its options was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['formula_set']),
                      })
                      .describe('Its formula changed.'),
                  ])
                  .describe('What happened to a column.'),
                column: zod.uuid().describe('The column.'),
                kind: zod.enum(['column']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a column op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['inserted']),
                        rows: zod
                          .array(zod.uuid())
                          .describe(
                            'The new rows, in the order they were sent.'
                          ),
                      })
                      .describe('Rows were added.'),
                    zod
                      .object({
                        affected: zod
                          .number()
                          .min(
                            applyDatabaseOpsResponseResultsItemChangeAffectedMin
                          )
                          .describe('How many rows the op updated.'),
                        kind: zod.enum(['updated']),
                      })
                      .describe("Rows' cells were written."),
                    zod
                      .object({
                        affected: zod
                          .number()
                          .min(
                            applyDatabaseOpsResponseResultsItemChangeAffectedMinOne
                          )
                          .describe('How many rows the op deleted.'),
                        kind: zod.enum(['deleted']),
                      })
                      .describe('Rows were removed.'),
                  ])
                  .describe("What happened to a table's rows."),
                kind: zod.enum(['rows']),
                table: zod.uuid().describe('The table.'),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
              })
              .describe('What a rows op did.'),
            zod
              .object({
                change: zod
                  .union([
                    zod
                      .object({
                        kind: zod.enum(['created']),
                        view: zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When it was created.'),
                            databaseId: zod
                              .uuid()
                              .describe('The database the table belongs to.'),
                            id: zod.uuid().describe('The view.'),
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyDatabaseOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMin
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                                      ),
                                    title: zod
                                      .uuid()
                                      .describe(
                                        "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                                  ),
                              ])
                              .describe('How a view draws its rows.'),
                            name: zod
                              .string()
                              .describe(
                                "Its name, unique among the table's views ignoring case."
                              ),
                            position: zod
                              .string()
                              .describe(
                                "Where it sorts among the table's views: a fractional key."
                              ),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                            tableId: zod.uuid().describe('The table it shows.'),
                            updatedAt: zod.iso
                              .datetime({})
                              .describe('When it last changed.'),
                          })
                          .describe('A view of one table, as stored.'),
                      })
                      .describe('It was added.'),
                    zod
                      .object({
                        kind: zod.enum(['updated']),
                        view: zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When it was created.'),
                            databaseId: zod
                              .uuid()
                              .describe('The database the table belongs to.'),
                            id: zod.uuid().describe('The view.'),
                            layout: zod
                              .union([
                                zod
                                  .object({
                                    columns: zod
                                      .array(
                                        zod
                                          .object({
                                            column: zod
                                              .uuid()
                                              .describe('The column.'),
                                            width: zod
                                              .number()
                                              .min(
                                                applyDatabaseOpsResponseResultsItemChangeViewLayoutColumnsItemWidthMinOne
                                              )
                                              .nullable()
                                              .describe(
                                                'Its width in pixels; the default when unset.'
                                              ),
                                          })
                                          .describe(
                                            'How one column shows in a table layout.'
                                          )
                                      )
                                      .describe(
                                        "How columns show, in display order. A column left out shows\nafter the listed ones, in the table's order."
                                      ),
                                    kind: zod.enum(['table']),
                                  })
                                  .describe('A grid with a row per row.'),
                                zod
                                  .object({
                                    cardFields: zod
                                      .array(zod.uuid())
                                      .describe(
                                        'The columns a card shows under its title, in order.'
                                      ),
                                    groupBy: zod
                                      .uuid()
                                      .describe(
                                        'The single-select or single-person column whose values are the\nlanes; moving a card to another lane sets this column.'
                                      ),
                                    hideEmptyLanes: zod
                                      .boolean()
                                      .describe(
                                        'Whether a lane with no cards is hidden.'
                                      ),
                                    kind: zod.enum(['board']),
                                    lanes: zod
                                      .array(
                                        zod
                                          .object({
                                            hidden: zod
                                              .boolean()
                                              .optional()
                                              .describe(
                                                'Whether it is hidden.'
                                              ),
                                            key: zod
                                              .union([
                                                zod
                                                  .object({
                                                    id: zod
                                                      .uuid()
                                                      .describe(
                                                        "The cards holding this option of the board's select column."
                                                      ),
                                                    kind: zod.enum(['option']),
                                                  })
                                                  .describe(
                                                    "The cards holding this option of the board's select column."
                                                  ),
                                                zod
                                                  .object({
                                                    id: zod
                                                      .string()
                                                      .describe(
                                                        "The cards naming this person in the board's person column."
                                                      ),
                                                    kind: zod.enum(['user']),
                                                  })
                                                  .describe(
                                                    "The cards naming this person in the board's person column."
                                                  ),
                                                zod
                                                  .object({
                                                    kind: zod.enum(['none']),
                                                  })
                                                  .describe(
                                                    'The cards whose grouping cell is empty.'
                                                  ),
                                              ])
                                              .describe(
                                                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                              ),
                                          })
                                          .describe(
                                            'How one lane shows in a board layout.'
                                          )
                                      )
                                      .describe(
                                        "How lanes show, in display order. A lane left out shows after the\nlisted ones: the lane of empty cells first, then options in the\ncolumn's order, or people by id."
                                      ),
                                    title: zod
                                      .uuid()
                                      .describe(
                                        "The column a card is titled by, of any type. Removing it titles\nthe cards by the table's first remaining column."
                                      ),
                                  })
                                  .describe(
                                    'Cards in lanes: one lane per option of a single-select column, or\none per person a single-person column names, plus one for cards\nwith an empty cell. A multi-valued column cannot group a board: a\ncard is in exactly one lane, so a card in several would need a place\nin each.'
                                  ),
                              ])
                              .describe('How a view draws its rows.'),
                            name: zod
                              .string()
                              .describe(
                                "Its name, unique among the table's views ignoring case."
                              ),
                            position: zod
                              .string()
                              .describe(
                                "Where it sorts among the table's views: a fractional key."
                              ),
                            query: zod
                              .object({
                                filter: zod.union([
                                  zod.null(),
                                  zod
                                    .object({
                                      conditions: zod
                                        .array(
                                          zod
                                            .union([
                                              zod
                                                .object({
                                                  column: zod
                                                    .uuid()
                                                    .describe(
                                                      'The column tested.'
                                                    ),
                                                  test: zod
                                                    .union([
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'presence',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isEmpty',
                                                              'isNotEmpty',
                                                            ])
                                                            .describe(
                                                              'Whether a cell is empty.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'Whether the cell is empty; fits a column of any type.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'text',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'contains',
                                                              'doesNotContain',
                                                              'startsWith',
                                                              'endsWith',
                                                            ])
                                                            .describe(
                                                              'How a text cell compares to a text.'
                                                            ),
                                                          value: zod
                                                            .string()
                                                            .describe(
                                                              'The text compared against, ignoring case for the containment\ntests.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A text or link column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'number',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'is',
                                                              'isNot',
                                                              'greaterThan',
                                                              'greaterThanOrEqual',
                                                              'lessThan',
                                                              'lessThanOrEqual',
                                                            ])
                                                            .describe(
                                                              'How a number cell compares to a number.'
                                                            ),
                                                          value: zod
                                                            .number()
                                                            .describe(
                                                              'The number compared against; finite.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A number column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'date',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'before',
                                                              'after',
                                                              'onOrBefore',
                                                              'onOrAfter',
                                                            ])
                                                            .describe(
                                                              'How a date cell compares to a date-time.'
                                                            ),
                                                          value: zod.iso
                                                            .datetime({})
                                                            .describe(
                                                              'The date-time compared against.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A date column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          checked: zod
                                                            .boolean()
                                                            .describe(
                                                              'Whether the box is checked.'
                                                            ),
                                                          kind: zod.enum([
                                                            'checkbox',
                                                          ]),
                                                        })
                                                        .describe(
                                                          'A checkbox column. An unchecked box and an empty cell are the same.'
                                                        ),
                                                      zod
                                                        .object({
                                                          kind: zod.enum([
                                                            'options',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                          options: zod
                                                            .array(zod.uuid())
                                                            .describe(
                                                              'Options of the column; at least one.'
                                                            ),
                                                        })
                                                        .describe(
                                                          'A select or tag column.'
                                                        ),
                                                      zod
                                                        .object({
                                                          entities: zod
                                                            .array(zod.string())
                                                            .describe(
                                                              "Entity ids, or for a relation the related rows' ids; at least\none."
                                                            ),
                                                          kind: zod.enum([
                                                            'entities',
                                                          ]),
                                                          operator: zod
                                                            .enum([
                                                              'isAnyOf',
                                                              'isNoneOf',
                                                              'hasAny',
                                                              'hasAll',
                                                              'hasNone',
                                                            ])
                                                            .describe(
                                                              "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                                            ),
                                                        })
                                                        .describe(
                                                          'A reference or relation column.'
                                                        ),
                                                    ])
                                                    .describe(
                                                      "What a column's cell must be, by the kind of value the column holds."
                                                    ),
                                                })
                                                .describe(
                                                  "A test of one column's cells."
                                                )
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum([
                                                      'condition',
                                                    ]),
                                                  })
                                                )
                                                .describe(
                                                  'A test of one column.'
                                                ),
                                              zod
                                                .unknown()
                                                .and(
                                                  zod.object({
                                                    kind: zod.enum(['group']),
                                                  })
                                                )
                                                .describe('A nested group.'),
                                            ])
                                            .describe(
                                              'One entry of a group: a condition, or a group of its own.'
                                            )
                                        )
                                        .describe(
                                          'The conditions and nested groups. A group without any keeps every\nrow.'
                                        ),
                                      conjunction: zod
                                        .enum(['and', 'or'])
                                        .describe(
                                          "How a group's conditions combine."
                                        ),
                                    })
                                    .describe(
                                      'Conditions joined by one conjunction.'
                                    ),
                                ]),
                                sort: zod
                                  .array(
                                    zod
                                      .object({
                                        column: zod
                                          .uuid()
                                          .describe('The column sorted on.'),
                                        direction: zod
                                          .enum(['ascending', 'descending'])
                                          .describe(
                                            'A sort direction. Empty cells sort last either way.'
                                          ),
                                      })
                                      .describe('One sort key.')
                                  )
                                  .optional()
                                  .describe(
                                    "The sort keys, first key first. Rows the keys leave tied keep the\ntable's own order; with no keys, the table's order is the view's."
                                  ),
                              })
                              .describe(
                                'Which rows of the table a view shows, and in what order: a filter and a\nsort, nothing that joins, groups or reshapes rows.'
                              ),
                            tableId: zod.uuid().describe('The table it shows.'),
                            updatedAt: zod.iso
                              .datetime({})
                              .describe('When it last changed.'),
                          })
                          .describe('A view of one table, as stored.'),
                      })
                      .describe('It was changed.'),
                    zod
                      .object({
                        kind: zod.enum(['deleted']),
                      })
                      .describe('It was removed.'),
                    zod
                      .object({
                        kind: zod.enum(['card_moved']),
                        positions: zod
                          .array(
                            zod
                              .object({
                                lane: zod
                                  .union([
                                    zod
                                      .object({
                                        id: zod
                                          .uuid()
                                          .describe(
                                            "The cards holding this option of the board's select column."
                                          ),
                                        kind: zod.enum(['option']),
                                      })
                                      .describe(
                                        "The cards holding this option of the board's select column."
                                      ),
                                    zod
                                      .object({
                                        id: zod
                                          .string()
                                          .describe(
                                            "The cards naming this person in the board's person column."
                                          ),
                                        kind: zod.enum(['user']),
                                      })
                                      .describe(
                                        "The cards naming this person in the board's person column."
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['none']),
                                      })
                                      .describe(
                                        'The cards whose grouping cell is empty.'
                                      ),
                                  ])
                                  .describe(
                                    "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
                                  ),
                                position: zod
                                  .string()
                                  .describe("The card's key in that lane."),
                                row: zod.uuid().describe("The card's row."),
                              })
                              .describe(
                                'Where one card sits on a board: its lane, and its fractional key there.\nA card whose row has since moved to another lane has no place until it is\nmoved again.'
                              )
                          )
                          .describe(
                            "The positions written, the moved card's last."
                          ),
                      })
                      .describe('One of its cards moved.'),
                  ])
                  .describe('What happened to a view.'),
                kind: zod.enum(['view']),
                table: zod.uuid().describe("The view's table."),
                tableVersion: zod
                  .number()
                  .describe(
                    "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                  ),
                view: zod.uuid().describe('The view.'),
              })
              .describe('What a view op did.'),
            zod
              .object({
                kind: zod.enum(['reorder_tables']),
                tables: zod
                  .array(
                    zod
                      .object({
                        table: zod.uuid().describe('The table.'),
                        version: zod
                          .number()
                          .describe(
                            "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
                          ),
                      })
                      .describe('A table and its version.')
                  )
                  .describe(
                    'Every table, in its new order, with its version once the request\ncommitted.'
                  ),
              })
              .describe("The database's tables in their new order."),
          ])
          .describe(
            "What one op did, in the order the ops were sent, grouped as the ops are:\na result's `kind` is its op's, naming the same resource, and its\n`change` says what happened to it."
          )
      )
      .describe(
        'One result per op, in the order the ops were sent. Each is grouped as\nits op is: the same outer `kind`, naming the same ids, with a\n`change` saying what happened.'
      ),
  })
  .describe('What each op of a batch did.');

/**
 * @summary Read recipients for a database owned by the caller. Link and team
sharing are always `null`: databases do not support them yet.
 */
export const getDatabasePermissionsParams = zod.object({
  id: zod.uuid(),
});

export const getDatabasePermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Update channel recipients after proving database ownership. Turning on
link or team sharing is refused; ownership cannot be changed here.
 */
export const updateDatabasePermissionsParams = zod.object({
  id: zod.uuid(),
});

export const updateDatabasePermissionsBody = zod.object({
  channelSharePermissions: zod
    .array(
      zod.object({
        accessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
        channelId: zod.string().describe('The channel id'),
        operation: zod.enum(['add', 'remove', 'replace']),
      })
    )
    .nullish()
    .describe('Any channel share permissions to be created\/updated\/removed'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

export const updateDatabasePermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary What changed in a table since a version, from the change journal: the
rows that changed, each once as it stands now (`insert`, `update` or
`delete`), and the columns. A reader holding the table at `since` reads
just those rows; it reads the table whole when a column changed, the
journal is not `complete`, or the rows are `truncated`.
 */
export const getDatabaseTableChangesParams = zod.object({
  id: zod.uuid().describe('Database id'),
  table_id: zod.uuid().describe('Table id'),
});

export const getDatabaseTableChangesQueryParams = zod.object({
  since: zod.number().describe('The table version the reader last read.'),
});

export const getDatabaseTableChangesResponse = zod
  .object({
    columns: zod
      .array(
        zod
          .object({
            column: zod.uuid().describe('The column.'),
            kind: zod
              .enum([
                'create',
                'rename',
                'change_type',
                'set_formula',
                'delete',
                'add_options',
                'update_option',
                'delete_option',
                'reorder',
                'infer_type',
                'related',
              ])
              .describe('How a change touched a column.'),
          })
          .describe("A column a table's changes since some version touched.")
      )
      .describe(
        "The columns that changed; any of them means the table's shape moved."
      ),
    complete: zod
      .boolean()
      .describe(
        'Whether every version since is journaled; without it, read the table\nwhole.'
      ),
    rows: zod
      .array(
        zod
          .object({
            kind: zod
              .enum(['insert', 'update', 'delete'])
              .describe('How a change touched a row.'),
            row: zod.uuid().describe('The row.'),
          })
          .describe(
            "A row a table's changes since some version touched, and how it stands."
          )
      )
      .describe(
        'The rows that changed, each once, as they stand now: a row added\nand written is `insert`, one removed is `delete`, and one added and\nremoved since is left out.'
      ),
    truncated: zod
      .boolean()
      .describe(
        'Whether more rows changed than are listed; then read the table whole.'
      ),
    version: zod.number().describe('The version the changes reach.'),
  })
  .describe(
    'What changed in a table since a version, for a reader holding it at\nthat version.'
  );

/**
 * @summary What changing one column to each type of the type menu would do to its
values: safe, checked (with how many values would not convert and a few
of them), or never (with why). Changes nothing.
 */
export const listDatabaseColumnCastsParams = zod.object({
  id: zod.uuid(),
  table_id: zod.uuid(),
  column_id: zod.uuid(),
});

export const listDatabaseColumnCastsResponseFailuresMin = 0;

export const listDatabaseColumnCastsResponseItem = zod
  .object({
    cast: zod
      .enum(['safe', 'checked', 'never'])
      .describe("Whether a column's values convert to a type."),
    data_type: zod
      .enum([
        'BOOLEAN',
        'DATE',
        'NUMBER',
        'STRING',
        'SELECT_NUMBER',
        'SELECT_STRING',
        'TAG',
        'ENTITY',
        'LINK',
      ])
      .describe(
        'Data type for property values, determining storage and validation.'
      ),
    examples: zod
      .array(zod.string())
      .describe('Up to three of the values that would not convert.'),
    failures: zod
      .number()
      .min(listDatabaseColumnCastsResponseFailuresMin)
      .describe('For a `checked` cast, how many cells would not convert.'),
    is_multi_select: zod
      .boolean()
      .describe('Whether the target holds several values.'),
    reason: zod
      .string()
      .nullable()
      .describe('Why nothing converts, for a `never` cast.'),
    relation: zod
      .boolean()
      .describe("Whether the target is a relation to another table's rows."),
    specific_entity_type: zod.union([
      zod.null(),
      zod
        .enum([
          'CALENDAR_EVENT',
          'CALL_RECORD',
          'CHANNEL',
          'CHAT',
          'COMPANY',
          'DATABASE_ROW',
          'CONTACT',
          'DOCUMENT',
          'INITIATIVE',
          'PROJECT',
          'TASK',
          'THREAD',
          'USER',
        ])
        .describe(
          'Type of entity that can be referenced by entity properties.'
        ),
    ]),
    summary: zod
      .string()
      .nullable()
      .describe(
        "For a `checked` cast with failures, what is wrong with them, as in\n`3 values aren't numbers`."
      ),
  })
  .describe(
    'What changing a column to one type would do to its values: the dry run\nof a type change, for one target.'
  );
export const listDatabaseColumnCastsResponse = zod.array(
  listDatabaseColumnCastsResponseItem
);

/**
 * @summary What one column's values become under another type: the values that
convert, with the options they need, for a new column of that type beside
it. The column itself is left as it is: a client writes the conversion as
one ops batch, a `create_column` then an `update_rows`, with the answered
table version as its base version. Changes nothing.
 */
export const convertDatabaseColumnParams = zod.object({
  id: zod.uuid(),
  table_id: zod.uuid(),
  column_id: zod.uuid(),
});

export const convertDatabaseColumnBody = zod
  .object({
    to: zod
      .union([
        zod
          .object({
            type: zod.enum(['text']),
          })
          .describe('Free text.'),
        zod
          .object({
            type: zod.enum(['number']),
          })
          .describe('A number.'),
        zod
          .object({
            type: zod.enum(['boolean']),
          })
          .describe('A checkbox.'),
        zod
          .object({
            type: zod.enum(['date']),
          })
          .describe('A date-time.'),
        zod
          .object({
            type: zod.enum(['link']),
          })
          .describe('A URL.'),
        zod
          .object({
            multi: zod
              .boolean()
              .describe('Whether a cell holds several options.'),
            type: zod.enum(['select']),
          })
          .describe('Text options.'),
        zod
          .object({
            multi: zod
              .boolean()
              .describe('Whether a cell holds several options.'),
            type: zod.enum(['select_number']),
          })
          .describe('Numeric options.'),
        zod
          .object({
            type: zod.enum(['tag']),
          })
          .describe('Colored labels; always several per cell.'),
        zod
          .object({
            multi: zod
              .boolean()
              .describe('Whether a cell holds several references.'),
            target: zod
              .enum([
                'USER',
                'DOCUMENT',
                'TASK',
                'COMPANY',
                'CONTACT',
                'CALL_RECORD',
                'CHANNEL',
                'CHAT',
                'PROJECT',
                'THREAD',
                'CALENDAR_EVENT',
                'INITIATIVE',
              ])
              .describe(
                'A kind of Macro entity a reference column can point at.'
              ),
            type: zod.enum(['entity']),
          })
          .describe('References to Macro entities.'),
        zod
          .object({
            database: zod.uuid().describe('The database of the related table.'),
            table: zod.uuid().describe('The related table.'),
            type: zod.enum(['relation']),
          })
          .describe('Rows of another table.'),
      ])
      .describe('A type a column can have.'),
  })
  .describe("The type a column's values are converted to.");

export const convertDatabaseColumnResponseMisfitsMin = 0;

export const convertDatabaseColumnResponse = zod
  .object({
    cells: zod
      .array(
        zod
          .object({
            row: zod.uuid().describe('The row.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['text']),
                    value: zod.string().describe('Free text.'),
                  })
                  .describe('Free text.'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number().describe('A finite number.'),
                  })
                  .describe('A finite number.'),
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean().describe('A checkbox.'),
                  })
                  .describe('A checkbox.'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}).describe('A date-time.'),
                  })
                  .describe('A date-time.'),
                zod
                  .object({
                    type: zod.enum(['link']),
                    value: zod
                      .array(zod.string())
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'Complete http or https URLs; at most one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['options']),
                    value: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe('An option the column has.'),
                              })
                              .describe('An option the column has.'),
                            zod
                              .object({
                                label: zod
                                  .string()
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              })
                              .describe(
                                "An option's label, matched without regard to case. An unknown label\nis refused."
                              ),
                          ])
                          .describe(
                            'A select option, by its id or by its label.'
                          )
                      )
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                  })
                  .describe(
                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                  ),
                zod
                  .object({
                    type: zod.enum(['entities']),
                    value: zod
                      .array(
                        zod
                          .object({
                            entityId: zod.string().describe("The entity's id."),
                            entityType: zod
                              .enum([
                                'USER',
                                'DOCUMENT',
                                'TASK',
                                'COMPANY',
                                'CONTACT',
                                'CALL_RECORD',
                                'CHANNEL',
                                'CHAT',
                                'PROJECT',
                                'THREAD',
                                'CALENDAR_EVENT',
                                'INITIATIVE',
                              ])
                              .describe(
                                'A kind of Macro entity a reference column can point at.'
                              ),
                          })
                          .describe('A reference to one Macro entity.')
                      )
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['rows']),
                    value: zod
                      .array(zod.uuid())
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                  })
                  .describe('Rows of the table a relation column points at.'),
                zod
                  .object({
                    type: zod.enum(['clear']),
                  })
                  .describe('No value: the cell is emptied.'),
              ])
              .describe(
                "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
              ),
          })
          .describe("One row's converted value.")
      )
      .describe(
        'Each row whose value converts, with that value, options named by\nlabel.'
      ),
    misfits: zod
      .number()
      .min(convertDatabaseColumnResponseMisfitsMin)
      .describe('How many values do not convert, and are left out.'),
    options: zod
      .array(zod.string())
      .describe(
        'The option labels a new select or tag column needs, in order.'
      ),
    tableVersion: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
  })
  .describe(
    "What a column's values become under another type, for a new column of\nthat type beside it: the values that convert, the options they need,\nand how many do not convert. Nothing is changed by reading it."
  );

/**
 * @summary Settle a new empty text column's type.
 */
export const inferDatabaseColumnTypeParams = zod.object({
  id: zod.uuid().describe('Database id'),
  table_id: zod.uuid().describe('Table id'),
  column_id: zod.uuid().describe('Column id'),
});

export const inferDatabaseColumnTypeBody = zod
  .object({
    baseVersion: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
    dataType: zod
      .enum([
        'BOOLEAN',
        'DATE',
        'NUMBER',
        'STRING',
        'SELECT_NUMBER',
        'SELECT_STRING',
        'TAG',
        'ENTITY',
        'LINK',
      ])
      .describe(
        'Data type for property values, determining storage and validation.'
      ),
    specificEntityType: zod
      .union([
        zod.null(),
        zod
          .enum([
            'CALENDAR_EVENT',
            'CALL_RECORD',
            'CHANNEL',
            'CHAT',
            'COMPANY',
            'DATABASE_ROW',
            'CONTACT',
            'DOCUMENT',
            'INITIATIVE',
            'PROJECT',
            'TASK',
            'THREAD',
            'USER',
          ])
          .describe(
            'Type of entity that can be referenced by entity properties.'
          ),
      ])
      .optional(),
  })
  .describe("Request to settle an empty column's first-value type.");

export const inferDatabaseColumnTypeResponse = zod
  .object({
    column: zod
      .object({
        column: zod
          .object({
            config: zod.union([
              zod.null(),
              zod
                .union([
                  zod
                    .object({
                      database_id: zod.uuid().describe('Target database.'),
                      kind: zod.enum(['link']),
                      table_id: zod.uuid().describe('Target table.'),
                    })
                    .describe(
                      'A relation column: its cells reference rows of another table.'
                    ),
                  zod
                    .object({
                      formula: zod
                        .union([
                          zod
                            .object({
                              column: zod
                                .uuid()
                                .describe('The column placement.'),
                              kind: zod.enum(['column']),
                            })
                            .describe(
                              "The row's cell in another column of the table: a number, date or\nderived column."
                            ),
                          zod
                            .object({
                              kind: zod.enum(['number']),
                              value: zod
                                .number()
                                .describe('The value; finite.'),
                            })
                            .describe(
                              'A constant. Added to or subtracted from a date, it counts days.'
                            ),
                          zod
                            .object({
                              kind: zod.enum(['binary']),
                              left: zod.unknown(),
                              operator: zod
                                .enum(['add', 'subtract', 'multiply', 'divide'])
                                .describe('An arithmetic operator.'),
                              right: zod.unknown(),
                            })
                            .describe('Two expressions combined.'),
                          zod
                            .object({
                              kind: zod.enum(['negate']),
                              operand: zod.unknown(),
                            })
                            .describe("An expression's negation."),
                        ])
                        .describe("An expression over one row's cells."),
                      kind: zod.enum(['derived']),
                    })
                    .describe(
                      "A derived column: its cells are computed from the row's others, and\nits definition, a number or date one, never holds a value."
                    ),
                ])
                .describe(
                  'Column-kind specific configuration stored on the placement.'
                ),
            ]),
            display_name: zod
              .string()
              .nullable()
              .describe(
                "The placement's own label, which also names it in SQL; `None` shows\nthe definition's name."
              ),
            id: zod.uuid().describe('Identifier of the placement.'),
            infer_type: zod
              .boolean()
              .describe(
                "Whether the first nonempty value may settle this new text column's type."
              ),
            nullable: zod
              .boolean()
              .optional()
              .describe(
                'Whether a row may omit this cell; empty collections also count as absent.'
              ),
            position: zod
              .string()
              .describe('Fractional index for column ordering.'),
            property_definition_id: zod
              .uuid()
              .describe('The bound property definition.'),
            protections: zod
              .array(
                zod
                  .enum(['delete', 'change_type'])
                  .describe(
                    'A schema operation reserved by a feature using a column.'
                  )
              )
              .optional()
              .describe(
                'Schema operations reserved by a feature; ordinary edits cannot clear them.'
              ),
            table_id: zod.uuid().describe('Table the column appears on.'),
          })
          .describe(
            'A column: the placement of a property definition on a table.\n\nThe definition carries name, [`DataType`], multi-select flag, and options;\nthis carries only where it appears and column-kind configuration.'
          ),
        definition: zod
          .object({
            definition: zod
              .object({
                created_at: zod.iso.datetime({}),
                data_type: zod
                  .enum([
                    'BOOLEAN',
                    'DATE',
                    'NUMBER',
                    'STRING',
                    'SELECT_NUMBER',
                    'SELECT_STRING',
                    'TAG',
                    'ENTITY',
                    'LINK',
                  ])
                  .describe(
                    'Data type for property values, determining storage and validation.'
                  ),
                display_name: zod.string(),
                id: zod.uuid(),
                is_metadata: zod
                  .boolean()
                  .describe(
                    'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                  ),
                is_multi_select: zod.boolean(),
                is_system: zod
                  .boolean()
                  .describe(
                    'Flag to indicate if this is a system property (stored in DB).'
                  ),
                owner: zod
                  .union([
                    zod
                      .object({
                        scope: zod.enum(['user']),
                        user_id: zod.string(),
                      })
                      .describe('User-scoped property.'),
                    zod
                      .object({
                        scope: zod.enum(['team']),
                        team_id: zod.uuid(),
                      })
                      .describe('Team-scoped property.'),
                    zod
                      .object({
                        database_id: zod.uuid(),
                        scope: zod.enum(['database']),
                      })
                      .describe(
                        'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                      ),
                    zod
                      .object({
                        scope: zod.enum(['system']),
                      })
                      .describe(
                        'System-owned property (no user, team, or database owner).'
                      ),
                  ])
                  .describe(
                    'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                  ),
                specific_entity_type: zod.union([
                  zod.null(),
                  zod
                    .enum([
                      'CALENDAR_EVENT',
                      'CALL_RECORD',
                      'CHANNEL',
                      'CHAT',
                      'COMPANY',
                      'DATABASE_ROW',
                      'CONTACT',
                      'DOCUMENT',
                      'INITIATIVE',
                      'PROJECT',
                      'TASK',
                      'THREAD',
                      'USER',
                    ])
                    .describe(
                      'Type of entity that can be referenced by entity properties.'
                    ),
                ]),
                updated_at: zod.iso.datetime({}),
              })
              .describe('Property definition model (service representation).'),
            property_options: zod.array(
              zod
                .object({
                  color: zod.string().nullable(),
                  created_at: zod.iso.datetime({}),
                  display_order: zod.number(),
                  id: zod.uuid(),
                  property_definition_id: zod.uuid(),
                  updated_at: zod.iso.datetime({}),
                  value: zod
                    .union([
                      zod
                        .object({
                          type: zod.enum(['string']),
                          value: zod
                            .string()
                            .describe(
                              'String value for SelectString properties'
                            ),
                        })
                        .describe('String value for SelectString properties'),
                      zod
                        .object({
                          type: zod.enum(['number']),
                          value: zod
                            .number()
                            .describe(
                              'Number value for SelectNumber properties'
                            ),
                        })
                        .describe('Number value for SelectNumber properties'),
                    ])
                    .describe(
                      'The value of a property option - either a string or a number.'
                    ),
                })
                .describe(
                  'A selectable option for select-type properties (service representation).'
                )
            ),
          })
          .describe(
            'Property definition with its associated options (service representation).'
          ),
        shared_outside_database: zod
          .boolean()
          .describe(
            "Whether the definition belongs to something beyond this database (a\nperson's, a team's or a system property), so changing its options\nchanges them everywhere that property is used."
          ),
        sql_name: zod
          .string()
          .describe(
            'The name SQL refers to the column by: its display name, quoted.'
          ),
        writable: zod.boolean().describe('Whether SQL may write this column.'),
      })
      .describe('One column placement with the definition behind it.'),
    table_version: zod
      .number()
      .describe(
        "Monotonic per-table version, bumped once by every committed change to a\ntable's schema or rows. Schema edits name the version they were made\nagainst, and change events carry the new one."
      ),
  })
  .describe(
    'Settled schema and the version against which its first value can be written.'
  );

/**
 * @summary A row's history, from the change journal: every committed change that
touched it, newest first, with who made it, when, and the values of the
columns it touched before and after. It reads after the row is removed,
so a removed row's last values stay readable.
 */
export const getDatabaseRowHistoryParams = zod.object({
  id: zod.uuid().describe('Database id'),
  table_id: zod.uuid().describe('Table id'),
  row_id: zod.uuid().describe('Row id'),
});

export const getDatabaseRowHistoryResponse = zod
  .object({
    changes: zod
      .array(
        zod
          .object({
            actingBot: zod
              .string()
              .nullable()
              .describe('The agent acting for them, if one was.'),
            actor: zod
              .string()
              .nullable()
              .describe(
                'Who made it; `null` for an internal caller, or a removed user.'
              ),
            after: zod
              .record(
                zod.string(),
                zod
                  .union([
                    zod
                      .object({
                        type: zod.enum(['text']),
                        value: zod.string().describe('Free text.'),
                      })
                      .describe('Free text.'),
                    zod
                      .object({
                        type: zod.enum(['number']),
                        value: zod.number().describe('A finite number.'),
                      })
                      .describe('A finite number.'),
                    zod
                      .object({
                        type: zod.enum(['boolean']),
                        value: zod.boolean().describe('A checkbox.'),
                      })
                      .describe('A checkbox.'),
                    zod
                      .object({
                        type: zod.enum(['date']),
                        value: zod.iso.datetime({}).describe('A date-time.'),
                      })
                      .describe('A date-time.'),
                    zod
                      .object({
                        type: zod.enum(['link']),
                        value: zod
                          .array(zod.string())
                          .describe(
                            'Complete http or https URLs; at most one for a single-valued column.'
                          ),
                      })
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['options']),
                        value: zod
                          .array(
                            zod
                              .union([
                                zod
                                  .object({
                                    id: zod
                                      .uuid()
                                      .describe('An option the column has.'),
                                  })
                                  .describe('An option the column has.'),
                                zod
                                  .object({
                                    label: zod
                                      .string()
                                      .describe(
                                        "An option's label, matched without regard to case. An unknown label\nis refused."
                                      ),
                                  })
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              ])
                              .describe(
                                'A select option, by its id or by its label.'
                              )
                          )
                          .describe(
                            'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                          ),
                      })
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['entities']),
                        value: zod
                          .array(
                            zod
                              .object({
                                entityId: zod
                                  .string()
                                  .describe("The entity's id."),
                                entityType: zod
                                  .enum([
                                    'USER',
                                    'DOCUMENT',
                                    'TASK',
                                    'COMPANY',
                                    'CONTACT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'PROJECT',
                                    'THREAD',
                                    'CALENDAR_EVENT',
                                    'INITIATIVE',
                                  ])
                                  .describe(
                                    'A kind of Macro entity a reference column can point at.'
                                  ),
                              })
                              .describe('A reference to one Macro entity.')
                          )
                          .describe(
                            'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                          ),
                      })
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['rows']),
                        value: zod
                          .array(zod.uuid())
                          .describe(
                            'Rows of the table a relation column points at.'
                          ),
                      })
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['clear']),
                      })
                      .describe('No value: the cell is emptied.'),
                  ])
                  .describe(
                    "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                  )
              )
              .describe(
                "Those columns' values it wrote, by column id; a cell it emptied is\nleft out."
              ),
            at: zod.iso.datetime({}).describe('When it committed.'),
            before: zod
              .record(
                zod.string(),
                zod
                  .union([
                    zod
                      .object({
                        type: zod.enum(['text']),
                        value: zod.string().describe('Free text.'),
                      })
                      .describe('Free text.'),
                    zod
                      .object({
                        type: zod.enum(['number']),
                        value: zod.number().describe('A finite number.'),
                      })
                      .describe('A finite number.'),
                    zod
                      .object({
                        type: zod.enum(['boolean']),
                        value: zod.boolean().describe('A checkbox.'),
                      })
                      .describe('A checkbox.'),
                    zod
                      .object({
                        type: zod.enum(['date']),
                        value: zod.iso.datetime({}).describe('A date-time.'),
                      })
                      .describe('A date-time.'),
                    zod
                      .object({
                        type: zod.enum(['link']),
                        value: zod
                          .array(zod.string())
                          .describe(
                            'Complete http or https URLs; at most one for a single-valued column.'
                          ),
                      })
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['options']),
                        value: zod
                          .array(
                            zod
                              .union([
                                zod
                                  .object({
                                    id: zod
                                      .uuid()
                                      .describe('An option the column has.'),
                                  })
                                  .describe('An option the column has.'),
                                zod
                                  .object({
                                    label: zod
                                      .string()
                                      .describe(
                                        "An option's label, matched without regard to case. An unknown label\nis refused."
                                      ),
                                  })
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              ])
                              .describe(
                                'A select option, by its id or by its label.'
                              )
                          )
                          .describe(
                            'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                          ),
                      })
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['entities']),
                        value: zod
                          .array(
                            zod
                              .object({
                                entityId: zod
                                  .string()
                                  .describe("The entity's id."),
                                entityType: zod
                                  .enum([
                                    'USER',
                                    'DOCUMENT',
                                    'TASK',
                                    'COMPANY',
                                    'CONTACT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'PROJECT',
                                    'THREAD',
                                    'CALENDAR_EVENT',
                                    'INITIATIVE',
                                  ])
                                  .describe(
                                    'A kind of Macro entity a reference column can point at.'
                                  ),
                              })
                              .describe('A reference to one Macro entity.')
                          )
                          .describe(
                            'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                          ),
                      })
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['rows']),
                        value: zod
                          .array(zod.uuid())
                          .describe(
                            'Rows of the table a relation column points at.'
                          ),
                      })
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                    zod
                      .object({
                        type: zod.enum(['clear']),
                      })
                      .describe('No value: the cell is emptied.'),
                  ])
                  .describe(
                    "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
                  )
              )
              .describe(
                "Those columns' values before it, by column id; an empty cell is\nleft out."
              ),
            change: zod.number().describe("The change's id in the journal."),
            columns: zod
              .array(zod.uuid())
              .describe(
                'The columns it wrote; for a removal, those the row had values in.'
              ),
            kind: zod
              .enum(['insert', 'update', 'delete'])
              .describe('How a change touched a row.'),
            version: zod.number().describe('The table version it produced.'),
          })
          .describe('One change of a row, as its history shows it.')
      )
      .describe(
        "Every committed change that touched the row, newest first: who made\nit, when, how, and the touched columns' values before and after."
      ),
  })
  .describe("A row's history.");

/**
 * @summary Where a board's cards sit, for drawing it: the views come with the
database's detail, their cards' places from here.
 */
export const getDatabaseViewPositionsParams = zod.object({
  id: zod.uuid().describe('Database id'),
  view_id: zod.uuid().describe('View id'),
});

export const getDatabaseViewPositionsResponse = zod
  .object({
    positions: zod
      .array(
        zod
          .object({
            lane: zod
              .union([
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe(
                        "The cards holding this option of the board's select column."
                      ),
                    kind: zod.enum(['option']),
                  })
                  .describe(
                    "The cards holding this option of the board's select column."
                  ),
                zod
                  .object({
                    id: zod
                      .string()
                      .describe(
                        "The cards naming this person in the board's person column."
                      ),
                    kind: zod.enum(['user']),
                  })
                  .describe(
                    "The cards naming this person in the board's person column."
                  ),
                zod
                  .object({
                    kind: zod.enum(['none']),
                  })
                  .describe('The cards whose grouping cell is empty.'),
              ])
              .describe(
                "A lane of a board, named by what its cards' grouping cells hold: one\noption of a select, one person, or nothing."
              ),
            position: zod.string().describe("The card's key in that lane."),
            row: zod.uuid().describe("The card's row."),
          })
          .describe(
            'Where one card sits on a board: its lane, and its fractional key there.\nA card whose row has since moved to another lane has no place until it is\nmoved again.'
          )
      )
      .describe(
        "The places of the cards that have one: each card's lane and its key\nthere. Cards without a place show after the placed ones of their\nlane, oldest first."
      ),
  })
  .describe("Where a board's cards sit.");

/**
 * @summary The database's webhooks.
 */
export const listDatabaseWebhooksParams = zod.object({
  id: zod.uuid().describe('Database id'),
});

export const listDatabaseWebhooksResponse = zod
  .object({
    webhooks: zod
      .array(
        zod
          .object({
            createdAt: zod.iso.datetime({}).describe('Creation time.'),
            createdBy: zod
              .string()
              .describe('Who created it; its calls write with their access.'),
            databaseId: zod.uuid().describe('The database.'),
            id: zod.uuid().describe('Identifier.'),
            tableId: zod
              .uuid()
              .describe('The table its calls insert rows into.'),
            tokenPrefix: zod
              .string()
              .describe(
                "The token's first characters, to tell webhooks apart."
              ),
          })
          .describe(
            'A webhook of a database. Its token is never shown again after creation.'
          )
      )
      .describe('Oldest first.'),
  })
  .describe("A database's webhooks.");

/**
 * @summary Create a webhook: a secret URL whose calls insert rows into one of the
database's tables, writing with the creator's access.
 */
export const createDatabaseWebhookParams = zod.object({
  id: zod.uuid().describe('Database id'),
});

export const createDatabaseWebhookBody = zod
  .object({
    tableId: zod.uuid().describe('A table of the database.'),
  })
  .describe('Which table a new webhook inserts rows into.');

/**
 * @summary Delete a webhook; its URL stops working.
 */
export const deleteDatabaseWebhookParams = zod.object({
  id: zod.uuid().describe('Database id'),
  webhook_id: zod.uuid().describe('Webhook id'),
});

/**
 * Available to every signed-in user on every plan; does not consume chat
credits. Audio and transcripts are never persisted.
 * @summary Transcribe a transient recording with OpenAI Whisper.
 */
export const transcribeDictationQueryParams = zod.object({
  language: zod.string().optional().describe('ISO 639-1 language hint'),
});

export const transcribeDictationResponse = zod
  .object({
    text: zod.string().describe('Recognized text.'),
  })
  .describe('Transcription result.');

/**
 * @summary Gets the users documents to populate their recent document list
 */
export const getUserDocumentsHandlerQueryParams = zod.object({
  limit: zod
    .number()
    .describe(
      'The maximum number of documents to retreive. Default 10, max 100.'
    ),
  offset: zod.number().describe('The offset to start from. Default 0.'),
  file_type: zod.string().describe('The file type to filter by. Default all.'),
});

export const getUserDocumentsHandlerResponse = zod.object({
  data: zod
    .union([
      zod.null(),
      zod.object({
        documents: zod.array(
          zod.object({
            branchedFromId: zod
              .string()
              .nullish()
              .describe('The id of the document this document branched from'),
            branchedFromVersionId: zod
              .number()
              .nullish()
              .describe(
                'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
              ),
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the document was created'),
            deletedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the document was deleted'),
            documentBom: zod
              .array(
                zod.object({
                  id: zod.string().describe('The uuid of the bom part'),
                  path: zod
                    .string()
                    .describe('The file path of the bom part content'),
                  sha: zod
                    .string()
                    .describe(
                      'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
                    ),
                })
              )
              .nullish()
              .describe(
                'If the document is a DOCX document and unzipped, the document_bom will be present'
              ),
            documentFamilyId: zod
              .number()
              .nullish()
              .describe(
                'The id of the document family this document belongs to'
              ),
            documentId: zod.string().describe('The document id'),
            documentName: zod.string().describe('The name of the document'),
            documentVersionId: zod
              .number()
              .describe(
                'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
              ),
            fileType: zod
              .string()
              .nullish()
              .describe('The file type of the document (file extension)'),
            modificationData: zod
              .unknown()
              .optional()
              .describe(
                'The modification data for the document instance.\nThis is only used for PDF documents.'
              ),
            owner: zod.string().describe('The owner of the document'),
            projectId: zod
              .string()
              .nullish()
              .describe('The id of the project that this document belongs to'),
            projectName: zod
              .string()
              .nullish()
              .describe(
                'The name of the project that this document belongs to'
              ),
            sha: zod
              .string()
              .nullish()
              .describe(
                'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
              ),
            subType: zod
              .union([
                zod.null(),
                zod
                  .enum(['task', 'snippet', 'skill', 'initiative_description'])
                  .describe(
                    'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                  ),
              ])
              .optional(),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'The time the document instance \/ document BOM was updated'
              ),
          })
        ),
        next_offset: zod
          .number()
          .nullish()
          .describe('The next offset to be used if there is one'),
        total: zod
          .number()
          .describe('The total number of documents the user has'),
      }),
    ])
    .optional(),
  error: zod.boolean().describe('Indicates if an error occurred'),
  message: zod.string().nullish().describe('Message to explain failure'),
});

/**
 * Creates a new document, generates an S3 presigned upload URL, and returns
the document metadata with the URL for the client to upload to.
 * @summary Handler for `POST /documents`.
 */
export const createDocumentBody = zod.object({
  branchedFromId: zod
    .string()
    .nullish()
    .describe('The document id if the document is being branched.'),
  branchedFromVersionId: zod
    .number()
    .nullish()
    .describe('The version id if the document is being branched.'),
  createdAt: zod.iso
    .datetime({})
    .nullish()
    .describe(
      "Optional time to set the document's created_at to. Set when backfilling email attachments."
    ),
  documentFamilyId: zod
    .number()
    .nullish()
    .describe('The document family id if the document is being branched.'),
  documentName: zod
    .string()
    .describe('The name of the document without extension.'),
  emailAttachmentId: zod
    .uuid()
    .nullish()
    .describe(
      'Internal only field that links the document created to the specified email attachment by\ncreating a row in the document_email table.'
    ),
  fileType: zod
    .string()
    .nullish()
    .describe('Optional file type of the document.'),
  id: zod.uuid().nullish().describe('The id of the document in the database'),
  isTask: zod
    .boolean()
    .optional()
    .describe(
      'Whether the document is a task.\nThis is only applicable for md documents and the request will fail if the file type is not\nmd.'
    ),
  jobId: zod
    .string()
    .nullish()
    .describe(
      'Optional job id to be used to track an upload job for the newly created document.\nWill need to have a corresponding job initiated for the file beforehand.'
    ),
  mimeType: zod
    .string()
    .nullish()
    .describe(
      'The content type of the document (currently only used for logging matches against file type).'
    ),
  projectId: zod.uuid().nullish(),
  sha: zod.string().describe('The sha of the document.'),
  skipHistory: zod
    .boolean()
    .optional()
    .describe(
      'Whether to add a viewed_at record for this document upon creation.'
    ),
  teamId: zod
    .uuid()
    .nullish()
    .describe(
      'Team to assign the task number within when creating a task document. If omitted,\nthe service may infer it only when the creator belongs to exactly one team.'
    ),
});

export const createDocumentResponse = zod.object({
  data: zod
    .object({
      documentMetadata: zod
        .object({
          branchedFromId: zod
            .string()
            .nullish()
            .describe('The id of the document this document branched from'),
          branchedFromVersionId: zod
            .number()
            .nullish()
            .describe(
              'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
            ),
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was created'),
          documentBom: zod
            .array(
              zod.object({
                id: zod.string().describe('The uuid of the bom part'),
                path: zod
                  .string()
                  .describe('The file path of the bom part content'),
                sha: zod
                  .string()
                  .describe(
                    'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
                  ),
              })
            )
            .nullish()
            .describe(
              'If the document is a DOCX document, the document_bom will be present'
            ),
          documentFamilyId: zod
            .number()
            .nullish()
            .describe('The id of the document family this document belongs to'),
          documentId: zod.string().describe('The document id'),
          documentName: zod.string().describe('The name of the document'),
          documentVersionId: zod
            .number()
            .describe(
              'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
            ),
          fileType: zod
            .string()
            .nullish()
            .describe('The file type of the document'),
          modificationData: zod
            .unknown()
            .optional()
            .describe(
              'The modification data for the document instance.\nThis is only used for PDF documents.'
            ),
          owner: zod.string().describe('The owner of the document'),
          sha: zod
            .string()
            .nullish()
            .describe(
              'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
            ),
          subType: zod
            .union([
              zod.null(),
              zod
                .enum(['task', 'snippet', 'skill', 'initiative_description'])
                .describe(
                  'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                ),
            ])
            .optional(),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe(
              'The time the document instance \/ document BOM was updated'
            ),
        })
        .and(
          zod.object({
            content: zod
              .object({
                location: zod
                  .union([
                    zod.null(),
                    zod
                      .enum([
                        'object_storage',
                        'sync_service',
                        'docx_bom_parts',
                        'converted_pdf',
                        'unknown',
                      ])
                      .describe(
                        'Where document content is, or is expected to be, read from.'
                      ),
                  ])
                  .optional(),
                state: zod
                  .enum(['unknown', 'pending', 'ready'])
                  .describe(
                    'API-visible content lifecycle state derived from current document metadata.'
                  ),
              })
              .describe('API-visible content lifecycle and location metadata.'),
            teamId: zod
              .uuid()
              .nullish()
              .describe(
                'The team this task number is scoped to, for task documents.'
              ),
            teamTaskId: zod
              .number()
              .nullish()
              .describe(
                'The task number assigned within the team, for task documents.'
              ),
          })
        )
        .describe(
          'Create\/copy response metadata plus content lifecycle metadata.'
        ),
      presignedUrl: zod
        .string()
        .nullish()
        .describe(
          'Presigned upload URL, when the caller still needs to upload bytes.'
        ),
    })
    .describe('Document response with content lifecycle metadata.')
    .and(
      zod.object({
        contentType: zod
          .string()
          .describe('Content type of the document converted from file type.'),
        fileType: zod
          .string()
          .nullish()
          .describe('The file type of the document.'),
      })
    )
    .describe('Create document response data with content lifecycle metadata.')
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Creates and initializes a markdown document in one backend-owned lifecycle.
 */
export const createMarkdownHandlerBody = zod
  .object({
    documentName: zod.string().describe('The document name.'),
    markdown: zod
      .string()
      .nullish()
      .describe('Markdown source text. Defaults to an empty document.'),
    projectId: zod
      .uuid()
      .nullish()
      .describe('Optional project ID to associate the document with.'),
    skipHistory: zod
      .boolean()
      .optional()
      .describe(
        'Whether to add a viewed_at record for this document upon creation.'
      ),
  })
  .describe(
    'Request body for creating a markdown document whose content is initialized\nby the backend.'
  );

export const createMarkdownHandlerResponse = zod
  .object({
    documentId: zod
      .string()
      .describe('The document ID of the created markdown document'),
    documentMetadata: zod.object({
      branchedFromId: zod
        .string()
        .nullish()
        .describe('The id of the document this document branched from'),
      branchedFromVersionId: zod
        .number()
        .nullish()
        .describe(
          'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
        ),
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was created'),
      documentBom: zod
        .array(
          zod.object({
            id: zod.string().describe('The uuid of the bom part'),
            path: zod
              .string()
              .describe('The file path of the bom part content'),
            sha: zod
              .string()
              .describe(
                'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
              ),
          })
        )
        .nullish()
        .describe(
          'If the document is a DOCX document, the document_bom will be present'
        ),
      documentFamilyId: zod
        .number()
        .nullish()
        .describe('The id of the document family this document belongs to'),
      documentId: zod.string().describe('The document id'),
      documentName: zod.string().describe('The name of the document'),
      documentVersionId: zod
        .number()
        .describe(
          'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
        ),
      fileType: zod
        .string()
        .nullish()
        .describe('The file type of the document'),
      modificationData: zod
        .unknown()
        .optional()
        .describe(
          'The modification data for the document instance.\nThis is only used for PDF documents.'
        ),
      owner: zod.string().describe('The owner of the document'),
      sha: zod
        .string()
        .nullish()
        .describe(
          'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
        ),
      subType: zod
        .union([
          zod.null(),
          zod
            .enum(['task', 'snippet', 'skill', 'initiative_description'])
            .describe(
              'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
            ),
        ])
        .optional(),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document instance \/ document BOM was updated'),
    }),
    token: zod
      .string()
      .describe('A pre-generated permission token that you can use for SS'),
  })
  .describe('Response for creating a markdown document.');

/**
 * @summary Creates a skill document with initialized markdown content in one
backend-owned lifecycle. Skills are markdown documents containing
instructions that AI reads and follows when the skill is referenced in an
AI input.
 */
export const createSkillHandlerBody = zod
  .object({
    markdown: zod
      .string()
      .nullish()
      .describe('Markdown source text. Defaults to an empty skill document.'),
    projectId: zod
      .uuid()
      .nullish()
      .describe('Optional project ID to associate the skill with.'),
    skillName: zod.string().describe('The name of the skill.'),
  })
  .describe(
    'Request body for creating a skill — a markdown document containing\ninstructions that AI reads and follows when the skill is referenced in an\nAI input.'
  );

export const createSkillHandlerResponse = zod
  .object({
    documentId: zod.string().describe('The document ID of the created skill.'),
  })
  .describe('Response for creating a skill.');

/**
 * @summary Creates a snippet document with initialized markdown content in one
backend-owned lifecycle. Snippets are created personal; team sharing is
toggled separately via `PUT /documents/{document_id}/team_share`.
 */
export const createSnippetHandlerBody = zod
  .object({
    markdown: zod
      .string()
      .nullish()
      .describe('Markdown source text. Defaults to an empty snippet document.'),
    projectId: zod
      .uuid()
      .nullish()
      .describe('Optional project ID to associate the snippet with.'),
    snippetName: zod.string().describe('The name of the snippet.'),
  })
  .describe(
    'Request body for creating a snippet — a reusable markdown document that can\nbe inserted into any markdown area.'
  );

export const createSnippetHandlerResponse = zod
  .object({
    documentId: zod
      .string()
      .describe('The document ID of the created snippet.'),
  })
  .describe('Response for creating a snippet.');

/**
 * @summary Creates a task document with properties and initialized markdown content in
one backend-owned lifecycle.
 */
export const createTaskHandlerBody = zod
  .object({
    markdown: zod
      .string()
      .nullish()
      .describe('Markdown source text. Defaults to an empty task document.'),
    projectId: zod
      .uuid()
      .nullish()
      .describe('Optional project ID to associate the task with.'),
    propertyValues: zod
      .array(
        zod
          .object({
            propertyId: zod.string().describe('The property definition ID.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean(),
                  })
                  .describe('Boolean true\/false value'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}),
                  })
                  .describe('Date and time value'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number(),
                  })
                  .describe('Numeric value'),
                zod
                  .object({
                    type: zod.enum(['string']),
                    value: zod.string(),
                  })
                  .describe('String\/text value'),
                zod
                  .object({
                    option_id: zod.uuid(),
                    type: zod.enum(['select_option']),
                  })
                  .describe('Select option by ID (for select-type properties)'),
                zod
                  .object({
                    option_ids: zod.array(zod.uuid()),
                    type: zod.enum(['multi_select_option']),
                  })
                  .describe(
                    'Multiple select options by ID (for multi-select properties)'
                  ),
                zod
                  .object({
                    reference: zod
                      .object({
                        entity_id: zod.string(),
                        entity_type: zod
                          .enum([
                            'CALENDAR_EVENT',
                            'CALL_RECORD',
                            'CHANNEL',
                            'CHAT',
                            'COMPANY',
                            'DATABASE_ROW',
                            'CONTACT',
                            'DOCUMENT',
                            'INITIATIVE',
                            'PROJECT',
                            'TASK',
                            'THREAD',
                            'USER',
                          ])
                          .describe(
                            'Type of entity that can be referenced by entity properties.'
                          ),
                        specific_message_id: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                          ),
                      })
                      .describe(
                        'Entity reference for entity-type property values.'
                      ),
                    type: zod.enum(['entity_reference']),
                  })
                  .describe('Entity reference'),
                zod
                  .object({
                    references: zod.array(
                      zod
                        .object({
                          entity_id: zod.string(),
                          entity_type: zod
                            .enum([
                              'CALENDAR_EVENT',
                              'CALL_RECORD',
                              'CHANNEL',
                              'CHAT',
                              'COMPANY',
                              'DATABASE_ROW',
                              'CONTACT',
                              'DOCUMENT',
                              'INITIATIVE',
                              'PROJECT',
                              'TASK',
                              'THREAD',
                              'USER',
                            ])
                            .describe(
                              'Type of entity that can be referenced by entity properties.'
                            ),
                          specific_message_id: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                            ),
                        })
                        .describe(
                          'Entity reference for entity-type property values.'
                        )
                    ),
                    type: zod.enum(['multi_entity_reference']),
                  })
                  .describe(
                    'Multiple entity references (for multi-select entity properties)'
                  ),
                zod
                  .object({
                    type: zod.enum(['link']),
                    url: zod.string(),
                  })
                  .describe('Link value'),
                zod
                  .object({
                    type: zod.enum(['multi_link']),
                    urls: zod.array(zod.string()),
                  })
                  .describe(
                    'Multiple link values (for multi-select link properties)'
                  ),
              ])
              .describe(
                'Type-safe enum for setting entity property values - provides compile-time validation.'
              ),
          })
          .describe('Property input for setting a property value on a task.')
      )
      .nullish()
      .describe('Optional property values to set on the task.'),
    shareWithTeam: zod
      .boolean()
      .optional()
      .describe(
        'Whether to share the task with your team or not\nDefaults to true'
      ),
    taskName: zod.string().describe('The name of the task.'),
    teamId: zod
      .uuid()
      .nullish()
      .describe(
        'Team to assign the task number within. If omitted, it is inferred only\nwhen the creator belongs to exactly one team.'
      ),
  })
  .describe('Request body for creating a task.');

export const createTaskHandlerResponse = zod
  .object({
    documentId: zod.string().describe('The document ID of the created task.'),
    documentMetadata: zod.object({
      branchedFromId: zod
        .string()
        .nullish()
        .describe('The id of the document this document branched from'),
      branchedFromVersionId: zod
        .number()
        .nullish()
        .describe(
          'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
        ),
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was created'),
      documentBom: zod
        .array(
          zod.object({
            id: zod.string().describe('The uuid of the bom part'),
            path: zod
              .string()
              .describe('The file path of the bom part content'),
            sha: zod
              .string()
              .describe(
                'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
              ),
          })
        )
        .nullish()
        .describe(
          'If the document is a DOCX document, the document_bom will be present'
        ),
      documentFamilyId: zod
        .number()
        .nullish()
        .describe('The id of the document family this document belongs to'),
      documentId: zod.string().describe('The document id'),
      documentName: zod.string().describe('The name of the document'),
      documentVersionId: zod
        .number()
        .describe(
          'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
        ),
      fileType: zod
        .string()
        .nullish()
        .describe('The file type of the document'),
      modificationData: zod
        .unknown()
        .optional()
        .describe(
          'The modification data for the document instance.\nThis is only used for PDF documents.'
        ),
      owner: zod.string().describe('The owner of the document'),
      sha: zod
        .string()
        .nullish()
        .describe(
          'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
        ),
      subType: zod
        .union([
          zod.null(),
          zod
            .enum(['task', 'snippet', 'skill', 'initiative_description'])
            .describe(
              'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
            ),
        ])
        .optional(),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document instance \/ document BOM was updated'),
    }),
    initialSnapshot: zod
      .string()
      .describe(
        'Base64-encoded canonical Loro snapshot used to initialize the task.'
      ),
    teamId: zod
      .uuid()
      .nullish()
      .describe('The team this task number is scoped to.'),
    teamTaskId: zod
      .number()
      .nullish()
      .describe('The task number assigned within the team.'),
    token: zod
      .string()
      .describe('A pre-generated permission token that you can use for SS'),
  })
  .describe('Response for creating a task.');

/**
 * Returns the task documents each of up to 100 GitHub pull requests references, for the pull
requests the caller can see.
 * @summary Handler for `POST /documents/github_prs/tasks`.
 */
export const getGithubPullRequestTasksBody = zod
  .object({
    githubKeys: zod
      .array(zod.string())
      .describe('Pull request `owner\/repo\/pull\/number` keys, at most 100.'),
  })
  .describe(
    'Request body for looking up the tasks linked to GitHub pull requests.'
  );

export const getGithubPullRequestTasksResponse = zod
  .object({
    pullRequests: zod
      .array(
        zod
          .object({
            githubKey: zod
              .string()
              .describe(
                "The pull request's `owner\/repo\/pull\/number` key, as requested."
              ),
            taskIds: zod
              .array(zod.string())
              .describe(
                'Ids of the task documents the pull request references, oldest link first. Empty when\nthe caller cannot see the pull request. The caller may still lack access to a task.'
              ),
          })
          .describe('The tasks linked to one GitHub pull request.')
      )
      .describe('One entry per requested pull request, in request order.'),
  })
  .describe('The tasks linked to each requested GitHub pull request.');

export const initializeUserDocumentsResponse = zod.object({
  success: zod.boolean().describe('Indicates if the request was successful'),
});

/**
 * @summary Gets a list of all the user's documents so they are able to be searched on
 */
export const getDocumentListHandlerResponse = zod.object({
  data: zod
    .array(
      zod.object({
        branchedFromId: zod
          .string()
          .nullish()
          .describe('The id of the document this document branched from'),
        branchedFromVersionId: zod
          .number()
          .nullish()
          .describe(
            'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
          ),
        createdAt: zod.iso
          .datetime({})
          .nullish()
          .describe('The time the document was created'),
        documentFamilyId: zod
          .number()
          .nullish()
          .describe('The id of the document family this document belongs to'),
        documentId: zod.string().describe('The id of the document'),
        documentName: zod.string().describe('The name of the document'),
        documentVersionId: zod
          .number()
          .describe('The id of the document version'),
        fileType: zod
          .string()
          .nullish()
          .describe('The file type of the document'),
        updatedAt: zod.iso
          .datetime({})
          .nullish()
          .describe(
            'The time the document instance \/ document BOM was updated'
          ),
      })
    )
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Validates the provided document permissions token
 */
export const validateDocumentPermissionsTokenBody = zod.object({
  token: zod.string().describe('The encoded document permissions token'),
});

export const validateDocumentPermissionsTokenResponseExpMin = 0;

export const validateDocumentPermissionsTokenResponse = zod.object({
  access_level: zod
    .enum(['view', 'comment', 'edit', 'owner'])
    .describe('Ordered from least to most access top -> bottom'),
  document_id: zod.string().describe('The document id'),
  exp: zod
    .number()
    .min(validateDocumentPermissionsTokenResponseExpMin)
    .describe('The expiration time of the token'),
  iss: zod.string().describe('The issuer of the token'),
  user_id: zod.union([zod.null(), zod.string()]).optional(),
});

/**
 * @summary Generates a document permissions token for a provided document id
 */
export const getDocumentPermissionsTokenParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentPermissionsTokenResponse = zod.object({
  token: zod.string().describe('The encoded document permissions token'),
});

/**
 * @deprecated
 * @summary Takes the docx document bom parts and generates presigned urls to upload
any new content
 */
export const presaveDocumentHandlerParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const presaveDocumentHandlerBody = zod.object({
  newBom: zod
    .array(
      zod.object({
        path: zod.string().describe('The file path of the bom part content'),
        sha: zod
          .string()
          .describe(
            'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
          ),
      })
    )
    .describe(
      'The updated BOM for the document\nContaining the file path and the sha'
    ),
});

export const presaveDocumentHandlerResponse = zod.object({
  data: zod.object({
    presignedUrls: zod
      .array(
        zod.object({
          presignedUrl: zod
            .string()
            .describe('The presigned url used to upload the sha'),
          sha: zod.string().describe('The sha of the item'),
        })
      )
      .describe(
        'Contains any presigned urls you need to upload specific bom parts with.'
      ),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

export const getBatchPreviewHandlerBody = zod.object({
  document_ids: zod.array(zod.string()),
});

export const getBatchPreviewHandlerResponse = zod.object({
  previews: zod.array(
    zod.union([
      zod
        .object({
          document_id: zod.string().describe('The document id'),
          document_name: zod.string().describe('The name of the document'),
          file_type: zod
            .string()
            .nullish()
            .describe('The file type of the document (e.g. pdf, docx)'),
          owner: zod.string().describe('The id of the owner of the document'),
          sub_type: zod
            .union([
              zod.null(),
              zod
                .union([
                  zod
                    .object({
                      is_completed: zod
                        .boolean()
                        .describe(
                          'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                        ),
                      type: zod.enum(['task']),
                    })
                    .describe('A task document with completion status'),
                  zod
                    .object({
                      type: zod.enum(['snippet']),
                    })
                    .describe('A snippet document — reusable markdown'),
                  zod
                    .object({
                      type: zod.enum(['skill']),
                    })
                    .describe(
                      'A skill document — markdown instructions for AI'
                    ),
                  zod
                    .object({
                      type: zod.enum(['initiative_description']),
                    })
                    .describe('The description document of an initiative'),
                ])
                .describe(
                  'The sub type of a document preview with associated properties.\nTask-related properties are encoded within the variant to ensure valid states.'
                ),
            ])
            .optional(),
          updated_at: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was last updated'),
        })
        .and(
          zod.object({
            type: zod.enum(['access']),
          })
        ),
      zod
        .object({
          document_id: zod.string(),
        })
        .and(
          zod.object({
            type: zod.enum(['no_access']),
          })
        ),
      zod
        .object({
          document_id: zod.string(),
        })
        .and(
          zod.object({
            type: zod.enum(['does_not_exist']),
          })
        ),
    ])
  ),
});

/**
 * Returns document metadata, user access level, and view location.
 * @summary Handler for `GET /documents/slug/{slug}`.
 */
export const getDocumentByTeamSlugParams = zod.object({
  slug: zod.string().describe('Team-task reference, such as ENG-42'),
});

export const getDocumentByTeamSlugResponse = zod.object({
  data: zod
    .object({
      items: zod
        .array(
          zod.union([
            zod.object({
              branchedFromId: zod
                .string()
                .nullish()
                .describe('The id of the document this document branched from'),
              branchedFromVersionId: zod
                .number()
                .nullish()
                .describe(
                  'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
                ),
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the document was created'),
              deletedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the document was deleted'),
              documentFamilyId: zod
                .number()
                .nullish()
                .describe(
                  'The id of the document family this document belongs to'
                ),
              documentVersionId: zod
                .number()
                .describe(
                  'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
                ),
              fileType: zod
                .string()
                .nullish()
                .describe('The file type of the document (e.g. pdf, docx)'),
              id: zod.string().describe('The document id'),
              name: zod.string().describe('The name of the document'),
              owner: zod.string().describe('The owner of the document'),
              projectId: zod
                .string()
                .nullish()
                .describe(
                  'The id of the project that this document belongs to'
                ),
              sha: zod
                .string()
                .nullish()
                .describe(
                  'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                ),
              subType: zod
                .union([
                  zod.null(),
                  zod
                    .union([
                      zod
                        .object({
                          is_completed: zod
                            .boolean()
                            .describe(
                              'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                            ),
                          type: zod.enum(['task']),
                        })
                        .describe(
                          'A task document with its associated properties'
                        ),
                      zod
                        .object({
                          type: zod.enum(['snippet']),
                        })
                        .describe('A snippet document — reusable markdown'),
                      zod
                        .object({
                          type: zod.enum(['skill']),
                        })
                        .describe(
                          'A skill document — markdown instructions for AI'
                        ),
                      zod
                        .object({
                          type: zod.enum(['initiative_description']),
                        })
                        .describe('The description document of an initiative'),
                    ])
                    .describe(
                      'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                    ),
                ])
                .optional(),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe(
                  'The time the document instance \/ document BOM was updated'
                ),
              type: zod.enum(['document']),
            }),
            zod.object({
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was created'),
              deletedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was deleted'),
              id: zod.string().describe('The chat uuid'),
              isPersistent: zod.boolean(),
              model: zod
                .string()
                .nullish()
                .describe('The model used to generate the chat'),
              name: zod.string().describe('The name of the chat'),
              projectId: zod
                .string()
                .nullish()
                .describe('The project id of the chat'),
              tokenCount: zod.number().nullish(),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was last updated'),
              userId: zod.string().describe('Who the chat belongs to'),
              type: zod.enum(['chat']),
            }),
            zod.object({
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the project was created'),
              deletedAt: zod.iso.datetime({}).nullish(),
              id: zod.string().describe('The id of the project'),
              name: zod.string().describe('The name of the project'),
              parentId: zod
                .string()
                .nullish()
                .describe('The parent project id'),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the project was updated'),
              userId: zod
                .string()
                .describe('The user id of who created the project'),
              type: zod.enum(['project']),
            }),
          ])
        )
        .describe('The items returned from the call'),
    })
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Resolves the current user's starter documents.
 */
export const handlerResponse = zod
  .object({
    how_to_guide_id: zod
      .string()
      .describe('Id of the user\'s \"Macro how to guide\".'),
  })
  .describe('The deterministic starter document ids for the current user.');

/**
 * @summary Lists the built-in system skills. System skills are static, code-defined
AI instructions (see the `system_skills` crate); they surface in the
skills menu and AI skill tools like user skills, but have no document
behind them, so clients must not offer to open them.
 */
export const getSystemSkillsHandlerResponse = zod
  .object({
    skills: zod
      .array(
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'The well-known id the skill is referenced by in mentions and AI tools.'
              ),
            name: zod.string().describe('The name of the skill.'),
          })
          .describe(
            'A built-in system skill: static, code-defined AI instructions surfaced\nthrough the same tools as user-authored skill documents, but with no\ndocument behind them.'
          )
      )
      .describe('Every system skill, in display order.'),
  })
  .describe('Response listing the built-in system skills.');

/**
 * Returns document metadata, user access level, and view location.
 * @summary Handler for `GET /documents/{document_id}`.
 */
export const getDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentResponse = zod.object({
  data: zod
    .object({
      documentMetadata: zod
        .object({
          branchedFromId: zod
            .string()
            .nullish()
            .describe('The id of the document this document branched from'),
          branchedFromVersionId: zod
            .number()
            .nullish()
            .describe(
              'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
            ),
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was created'),
          deletedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was deleted'),
          documentBom: zod
            .array(
              zod.object({
                id: zod.string().describe('The uuid of the bom part'),
                path: zod
                  .string()
                  .describe('The file path of the bom part content'),
                sha: zod
                  .string()
                  .describe(
                    'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
                  ),
              })
            )
            .nullish()
            .describe(
              'If the document is a DOCX document and unzipped, the document_bom will be present'
            ),
          documentFamilyId: zod
            .number()
            .nullish()
            .describe('The id of the document family this document belongs to'),
          documentId: zod.string().describe('The document id'),
          documentName: zod.string().describe('The name of the document'),
          documentVersionId: zod
            .number()
            .describe(
              'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
            ),
          fileType: zod
            .string()
            .nullish()
            .describe('The file type of the document (file extension)'),
          modificationData: zod
            .unknown()
            .optional()
            .describe(
              'The modification data for the document instance.\nThis is only used for PDF documents.'
            ),
          owner: zod.string().describe('The owner of the document'),
          projectId: zod
            .string()
            .nullish()
            .describe('The id of the project that this document belongs to'),
          projectName: zod
            .string()
            .nullish()
            .describe('The name of the project that this document belongs to'),
          sha: zod
            .string()
            .nullish()
            .describe(
              'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
            ),
          subType: zod
            .union([
              zod.null(),
              zod
                .enum(['task', 'snippet', 'skill', 'initiative_description'])
                .describe(
                  'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                ),
            ])
            .optional(),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe(
              'The time the document instance \/ document BOM was updated'
            ),
        })
        .and(
          zod.object({
            content: zod
              .object({
                location: zod
                  .union([
                    zod.null(),
                    zod
                      .enum([
                        'object_storage',
                        'sync_service',
                        'docx_bom_parts',
                        'converted_pdf',
                        'unknown',
                      ])
                      .describe(
                        'Where document content is, or is expected to be, read from.'
                      ),
                  ])
                  .optional(),
                state: zod
                  .enum(['unknown', 'pending', 'ready'])
                  .describe(
                    'API-visible content lifecycle state derived from current document metadata.'
                  ),
              })
              .describe('API-visible content lifecycle and location metadata.'),
            teamId: zod
              .uuid()
              .nullish()
              .describe(
                'The team this task number is scoped to, for task documents.'
              ),
            teamTaskId: zod
              .number()
              .nullish()
              .describe(
                'The task number assigned within the team, for task documents.'
              ),
          })
        )
        .describe('Full document metadata plus content lifecycle metadata.'),
      userAccessLevel: zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
      viewLocation: zod
        .string()
        .nullish()
        .describe("The user's view location if there is one."),
    })
    .describe('Get document response data with content lifecycle metadata.')
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Creates a new version of a document
 */
export const saveDocumentHandlerParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const saveDocumentHandlerBody = zod.object({
  modificationData: zod
    .unknown()
    .optional()
    .describe(
      'The modification data for the document instance.\nUsed to store highlights and other overlays onto a static file'
    ),
  newBom: zod
    .array(
      zod.object({
        path: zod.string().describe('The file path of the bom part content'),
        sha: zod
          .string()
          .describe(
            'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
          ),
      })
    )
    .nullish()
    .describe(
      '\*\*DOCX ONLY\*\*\nThe updated BOM for the document.\nContaining the file path and the sha.'
    ),
  sha: zod
    .string()
    .nullish()
    .describe(
      'The sha of the new document.\nThis is used to generate a presigned url to upload the new document content to s3.'
    ),
});

export const saveDocumentHandlerResponse = zod.object({
  data: zod.object({
    documentMetadata: zod.object({
      branchedFromId: zod
        .string()
        .nullish()
        .describe('The id of the document this document branched from'),
      branchedFromVersionId: zod
        .number()
        .nullish()
        .describe(
          'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
        ),
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was created'),
      documentBom: zod
        .array(
          zod.object({
            id: zod.string().describe('The uuid of the bom part'),
            path: zod
              .string()
              .describe('The file path of the bom part content'),
            sha: zod
              .string()
              .describe(
                'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
              ),
          })
        )
        .nullish()
        .describe(
          'If the document is a DOCX document, the document_bom will be present'
        ),
      documentFamilyId: zod
        .number()
        .nullish()
        .describe('The id of the document family this document belongs to'),
      documentId: zod.string().describe('The document id'),
      documentName: zod.string().describe('The name of the document'),
      documentVersionId: zod
        .number()
        .describe(
          'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
        ),
      fileType: zod
        .string()
        .nullish()
        .describe('The file type of the document'),
      modificationData: zod
        .unknown()
        .optional()
        .describe(
          'The modification data for the document instance.\nThis is only used for PDF documents.'
        ),
      owner: zod.string().describe('The owner of the document'),
      sha: zod
        .string()
        .nullish()
        .describe(
          'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
        ),
      subType: zod
        .union([
          zod.null(),
          zod
            .enum(['task', 'snippet', 'skill', 'initiative_description'])
            .describe(
              'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
            ),
        ])
        .optional(),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document instance \/ document BOM was updated'),
    }),
    presignedUrl: zod
      .string()
      .nullish()
      .describe(
        'If the document is an editable file, we provide a presigned url to save the updated file to.'
      ),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * Soft-deletes a document (only owners can delete).
 * @summary Handler for `DELETE /documents/{document_id}`.
 */
export const deleteDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const deleteDocumentResponse = zod.object({
  success: zod.boolean().describe('Indicates if the request was successful'),
});

/**
 * Edits document metadata such as name or project, and modifies
the document's share permissions. Requires edit access to the document,
and edit access to the target project if moving the document.
 * @summary Handler for `PATCH /documents/{document_id}`.
 */
export const editDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const editDocumentBody = zod
  .object({
    documentName: zod.string().nullish().describe('The name of the document.'),
    fileType: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                set: zod
                  .enum([
                    'docx',
                    'pdf',
                    'md',
                    'spreadsheet',
                    'canvas',
                    'coffee',
                    'cson',
                    'iced',
                    'c',
                    'i',
                    'cpp',
                    'cppm',
                    'cc',
                    'ccm',
                    'cxx',
                    'cxxm',
                    'cplusplus',
                    'cplusplusm',
                    'hpp',
                    'hh',
                    'hxx',
                    'hplusplus',
                    'h',
                    'ii',
                    'ino',
                    'inl',
                    'ipp',
                    'ixx',
                    'tpp',
                    'txx',
                    'hppin',
                    'hin',
                    'cu',
                    'cuh',
                    'cs',
                    'csx',
                    'cake',
                    'css',
                    'dart',
                    'diff',
                    'patch',
                    'rej',
                    'dockerfile',
                    'containerfile',
                    'go',
                    'handlebars',
                    'hbs',
                    'hjs',
                    'hlsl',
                    'hlsli',
                    'fx',
                    'fxh',
                    'vsh',
                    'psh',
                    'cginc',
                    'compute',
                    'html',
                    'htm',
                    'shtml',
                    'xhtml',
                    'xht',
                    'mdoc',
                    'jsp',
                    'asp',
                    'aspx',
                    'jshtm',
                    'volt',
                    'ejs',
                    'rhtml',
                    'ini',
                    'conf',
                    'properties',
                    'cfg',
                    'directory',
                    'gitattributes',
                    'gitconfig',
                    'gitmodules',
                    'editorconfig',
                    'repo',
                    'java',
                    'jav',
                    'jsx',
                    'js',
                    'es6',
                    'mjs',
                    'cjs',
                    'pac',
                    'json',
                    'bowerrc',
                    'jscsrc',
                    'webmanifest',
                    'jsmap',
                    'cssmap',
                    'tsmap',
                    'har',
                    'jslintrc',
                    'jsonld',
                    'geojson',
                    'ipynb',
                    'vuerc',
                    'jsonc',
                    'eslintrc',
                    'eslintrcjson',
                    'jsfmtrc',
                    'jshintrc',
                    'swcrc',
                    'hintrc',
                    'babelrc',
                    'jsonl',
                    'ndjson',
                    'codesnippets',
                    'jl',
                    'jmd',
                    'sty',
                    'cls',
                    'bbx',
                    'cbx',
                    'tex',
                    'ltx',
                    'ctx',
                    'bib',
                    'less',
                    'log',
                    'lua',
                    'mak',
                    'mk',
                    'mkd',
                    'mdwn',
                    'mdown',
                    'markdown',
                    'markdn',
                    'mdtxt',
                    'mdtext',
                    'workbook',
                    'm',
                    'mm',
                    'pl',
                    'pm',
                    'pod',
                    't',
                    'psgi',
                    'raku',
                    'rakumod',
                    'rakutest',
                    'rakudoc',
                    'nqp',
                    'p6',
                    'pl6',
                    'pm6',
                    'php',
                    'php4',
                    'php5',
                    'phtml',
                    'ctp',
                    'ps1',
                    'psm1',
                    'psd1',
                    'pssc',
                    'psrc',
                    'py',
                    'rpy',
                    'pyw',
                    'cpy',
                    'gyp',
                    'gypi',
                    'pyi',
                    'ipy',
                    'pyt',
                    'r',
                    'rhistory',
                    'rprofile',
                    'rt',
                    'cshtml',
                    'razor',
                    'rb',
                    'rbx',
                    'rjs',
                    'gemspec',
                    'rake',
                    'ru',
                    'erb',
                    'podspec',
                    'rbi',
                    'rs',
                    'scss',
                    'sass',
                    'shader',
                    'sh',
                    'bash',
                    'bashrc',
                    'bashaliases',
                    'bashprofile',
                    'bashlogin',
                    'ebuild',
                    'eclass',
                    'profile',
                    'bashlogout',
                    'xprofile',
                    'xsession',
                    'xsessionrc',
                    'zsh',
                    'zshrc',
                    'zprofile',
                    'zlogin',
                    'zlogout',
                    'zshenv',
                    'zshtheme',
                    'fish',
                    'ksh',
                    'csh',
                    'cshrc',
                    'tcshrc',
                    'yashrc',
                    'yashprofile',
                    'sql',
                    'dsql',
                    'swift',
                    'ts',
                    'cts',
                    'mts',
                    'tsx',
                    'tsbuildinfo',
                    'xml',
                    'xsd',
                    'ascx',
                    'atom',
                    'axml',
                    'axaml',
                    'bpmn',
                    'cpt',
                    'csl',
                    'csproj',
                    'csprojuser',
                    'dita',
                    'ditamap',
                    'dtd',
                    'ent',
                    'mod',
                    'dtml',
                    'fsproj',
                    'fxml',
                    'iml',
                    'isml',
                    'jmx',
                    'launch',
                    'menu',
                    'mxml',
                    'nuspec',
                    'opml',
                    'owl',
                    'proj',
                    'props',
                    'pt',
                    'publishsettings',
                    'pubxml',
                    'pubxmluser',
                    'rbxlx',
                    'rbxmx',
                    'rdf',
                    'rng',
                    'rss',
                    'shproj',
                    'storyboard',
                    'targets',
                    'tld',
                    'tmx',
                    'vbproj',
                    'vbprojuser',
                    'vcxproj',
                    'vcxprojfilters',
                    'wsdl',
                    'wxi',
                    'wxl',
                    'wxs',
                    'xaml',
                    'xbl',
                    'xib',
                    'xlf',
                    'xliff',
                    'xpdl',
                    'xul',
                    'xoml',
                    'xsl',
                    'xslt',
                    'yaml',
                    'yml',
                    'eyaml',
                    'eyml',
                    'cff',
                    'yamltmlanguage',
                    'yamltmpreferences',
                    'yamltmtheme',
                    'winget',
                    'txt',
                    'csv',
                    'tsv',
                    'jpeg',
                    'jpg',
                    'png',
                    'gif',
                    'svg',
                    'webp',
                    'avif',
                    'bmp',
                    'ico',
                    'tiff',
                    'tif',
                    'heic',
                    'heif',
                    'tar',
                    'targz',
                    'tgz',
                    'gz',
                    'bz2',
                    'tarbz2',
                    'tbz2',
                    'z',
                    'tarz',
                    'lz',
                    'tarlz',
                    'xz',
                    'tarxz',
                    'txz',
                    'lzma',
                    'tarlzma',
                    'rar',
                    'sevenz',
                    'zst',
                    'tarzst',
                    'tzst',
                    'zip',
                    'exe',
                    'msi',
                    'dll',
                    'bat',
                    'cmd',
                    'com',
                    'appimage',
                    'app',
                    'bin',
                    'deb',
                    'rpm',
                    'apk',
                    'dmg',
                    'pkg',
                    'crx',
                    'xpi',
                    'mp3',
                    'wav',
                    'ogg',
                    'flac',
                    'aac',
                    'm4a',
                    'wma',
                    'mid',
                    'midi',
                    'mp4',
                    'mkv',
                    'webm',
                    'avi',
                    'mov',
                    'wmv',
                    'mpg',
                    'mpeg',
                    'm4v',
                    'flv',
                    'f4v',
                    'threegp',
                    'ttf',
                    'otf',
                    'woff',
                    'woff2',
                    'eot',
                    'rtf',
                    'odt',
                    'ods',
                    'odp',
                    'odg',
                    'odf',
                    'epub',
                    'mobi',
                    'azw',
                    'azw3',
                    'djvu',
                    'xls',
                    'xlsm',
                    'ppt',
                    'doc',
                    'pptx',
                    'xlsx',
                    'db',
                    'sqlite',
                    'sqlite3',
                    'mdb',
                    'accdb',
                    'dbf',
                    'plist',
                    'toml',
                    'env',
                    'dot',
                    'gv',
                    'torrent',
                    'ics',
                    'vcf',
                    'ai',
                    'eps',
                    'ps',
                    'dxf',
                    'dwg',
                    'fig',
                    'psd',
                    'psb',
                    'stl',
                    'obj',
                    'fbx',
                    'blend',
                    'dae',
                    'threeds',
                    'gltf',
                    'glb',
                    'vhd',
                    'vhdx',
                    'vmdk',
                    'ova',
                    'ovf',
                    'iso',
                    'img',
                    'swf',
                  ])
                  .describe(
                    'Generates a FileType enum and associated ContentType enum with their implementations.\n\nThis macro takes a list of tuples in the format:\n(Variant, \"extension\", \"mime_type\", CONTENT_TYPE_VARIANT)\n\nFor each tuple it generates:\n- A variant in the FileType enum\n- A variant in the ContentType enum\n- Implementations for:\n  - FileType::to_str() - Converts FileType to extension string\n  - FileType::from_str() - Converts extension string to FileType\n  - From<FileType> for ContentType - Maps FileType to ContentType\n  - ContentType::mime_type() - Gets MIME type for ContentType\n'
                  ),
              })
              .describe('Set the file type to a specific value.'),
            zod.enum(['clear']).describe('Clear the file type (set to null).'),
          ])
          .describe(
            'Represents a file type update: either set to a specific type or clear to null.'
          ),
      ])
      .optional(),
    projectId: zod
      .string()
      .nullish()
      .describe('The new project id of the document.'),
    sharePermission: zod
      .union([
        zod.null(),
        zod.object({
          channelSharePermissions: zod
            .array(
              zod.object({
                accessLevel: zod
                  .union([
                    zod.null(),
                    zod
                      .enum(['view', 'comment', 'edit', 'owner'])
                      .describe(
                        'Ordered from least to most access top -> bottom'
                      ),
                  ])
                  .optional(),
                channelId: zod.string().describe('The channel id'),
                operation: zod.enum(['add', 'remove', 'replace']),
              })
            )
            .nullish()
            .describe(
              'Any channel share permissions to be created\/updated\/removed'
            ),
          linkShare: zod
            .union([
              zod.null(),
              zod
                .enum(['PUBLIC', 'TEAM'])
                .describe(
                  'Defines who can access an item through its share link.'
                ),
            ])
            .optional(),
          linkShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
          teamShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
        }),
      ])
      .optional(),
  })
  .describe('Arguments for the edit_document service call.');

export const editDocumentResponse = zod
  .object({
    data: zod.object({
      success: zod
        .boolean()
        .describe('Indicates if the request was successful'),
    }),
    error: zod.boolean().describe('Whether an error occurred.'),
  })
  .describe('Edit document response.');

/**
 * Returns the short UUID and git branch name for a task document.
Returns 400 if the document is not a task.
 * @summary Handler for `GET /documents/{document_id}/branch_name`.
 */
export const getDocumentBranchNameParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentBranchNameResponse = zod
  .object({
    branchName: zod
      .string()
      .describe('The git branch name for the task document.'),
    shortId: zod.string().describe('The short id of the document.'),
  })
  .describe('Branch name response.');

/**
 * Copies an existing document, creating a new document with the same content.
Does not require re-uploading the document file.
 * @summary Handler for `POST /documents/{document_id}/copy`.
 */
export const copyDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const copyDocumentQueryParams = zod.object({
  version_id: zod
    .number()
    .optional()
    .describe(
      'The version id of the document to copy. Defaults to copying the latest version of the document.'
    ),
});

export const copyDocumentBody = zod
  .object({
    documentName: zod
      .string()
      .describe('The name of the new document (without extension).'),
    versionId: zod
      .union([
        zod.null(),
        zod.object({
          counter: zod.number(),
          peer: zod.string(),
        }),
      ])
      .optional(),
  })
  .describe('Request body for copying a document.');

export const copyDocumentResponse = zod
  .object({
    data: zod
      .object({
        documentMetadata: zod
          .object({
            branchedFromId: zod
              .string()
              .nullish()
              .describe('The id of the document this document branched from'),
            branchedFromVersionId: zod
              .number()
              .nullish()
              .describe(
                'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
              ),
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the document was created'),
            documentBom: zod
              .array(
                zod.object({
                  id: zod.string().describe('The uuid of the bom part'),
                  path: zod
                    .string()
                    .describe('The file path of the bom part content'),
                  sha: zod
                    .string()
                    .describe(
                      'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
                    ),
                })
              )
              .nullish()
              .describe(
                'If the document is a DOCX document, the document_bom will be present'
              ),
            documentFamilyId: zod
              .number()
              .nullish()
              .describe(
                'The id of the document family this document belongs to'
              ),
            documentId: zod.string().describe('The document id'),
            documentName: zod.string().describe('The name of the document'),
            documentVersionId: zod
              .number()
              .describe(
                'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
              ),
            fileType: zod
              .string()
              .nullish()
              .describe('The file type of the document'),
            modificationData: zod
              .unknown()
              .optional()
              .describe(
                'The modification data for the document instance.\nThis is only used for PDF documents.'
              ),
            owner: zod.string().describe('The owner of the document'),
            sha: zod
              .string()
              .nullish()
              .describe(
                'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
              ),
            subType: zod
              .union([
                zod.null(),
                zod
                  .enum(['task', 'snippet', 'skill', 'initiative_description'])
                  .describe(
                    'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                  ),
              ])
              .optional(),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'The time the document instance \/ document BOM was updated'
              ),
          })
          .and(
            zod.object({
              content: zod
                .object({
                  location: zod
                    .union([
                      zod.null(),
                      zod
                        .enum([
                          'object_storage',
                          'sync_service',
                          'docx_bom_parts',
                          'converted_pdf',
                          'unknown',
                        ])
                        .describe(
                          'Where document content is, or is expected to be, read from.'
                        ),
                    ])
                    .optional(),
                  state: zod
                    .enum(['unknown', 'pending', 'ready'])
                    .describe(
                      'API-visible content lifecycle state derived from current document metadata.'
                    ),
                })
                .describe(
                  'API-visible content lifecycle and location metadata.'
                ),
              teamId: zod
                .uuid()
                .nullish()
                .describe(
                  'The team this task number is scoped to, for task documents.'
                ),
              teamTaskId: zod
                .number()
                .nullish()
                .describe(
                  'The task number assigned within the team, for task documents.'
                ),
            })
          )
          .describe(
            'Create\/copy response metadata plus content lifecycle metadata.'
          ),
        presignedUrl: zod
          .string()
          .nullish()
          .describe(
            'Presigned upload URL, when the caller still needs to upload bytes.'
          ),
      })
      .describe('Document response with content lifecycle metadata.'),
    error: zod.boolean().describe('Indicates if an error occurred.'),
  })
  .describe('Response wrapper for the copy document endpoint.');

/**
 * @summary Generates a presigned url to download the raw content of the document
For files with modification layers, they will not be applied in the downloaded file.
 */
export const exportDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const exportDocumentResponse = zod.object({
  presigned_url: zod
    .string()
    .describe('The presigned url to download the raw content of the document'),
});

/**
 * Returns GitHub pull requests associated with a task document.
Returns 400 if the document is not a task.
 * @summary Handler for `GET /documents/{document_id}/github_prs`.
 */
export const getDocumentGithubPullRequestsParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentGithubPullRequestsResponsePullRequestsItemAdditionsMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemChecksItemIdMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemAuthorIdMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemIdMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemInReplyToIdMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemLineMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemOriginalLineMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemPullRequestReviewIdMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemDeletionsMin = 0;

export const getDocumentGithubPullRequestsResponsePullRequestsItemNumberMin = 0;

export const getDocumentGithubPullRequestsResponse = zod
  .object({
    pullRequests: zod
      .array(
        zod
          .object({
            additions: zod
              .number()
              .min(
                getDocumentGithubPullRequestsResponsePullRequestsItemAdditionsMin
              )
              .nullish()
              .describe(
                'The number of added lines in the pull request, when enrichment data is available.'
              ),
            checks: zod
              .array(
                zod
                  .object({
                    completedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'When the check run completed, when available.'
                      ),
                    conclusion: zod
                      .string()
                      .nullish()
                      .describe(
                        'The raw GitHub check run conclusion, when the run has completed.'
                      ),
                    id: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemChecksItemIdMin
                      )
                      .describe(
                        'The unique GitHub identifier for the check run.'
                      ),
                    name: zod.string().describe('The check run name.'),
                    startedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('When the check run started, when available.'),
                    status: zod
                      .string()
                      .describe('The raw GitHub check run status.'),
                    url: zod
                      .string()
                      .nullish()
                      .describe(
                        'The public GitHub URL for the check run, when available.'
                      ),
                  })
                  .describe(
                    'A check run associated with a GitHub pull request.'
                  )
              )
              .nullish()
              .describe(
                'Check runs collected from the pull request head commit, when enrichment data is available.'
              ),
            comments: zod
              .array(
                zod
                  .object({
                    authorAssociation: zod
                      .string()
                      .nullish()
                      .describe(
                        "GitHub's relationship label for the author, when available."
                      ),
                    authorId: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemAuthorIdMin
                      )
                      .nullish()
                      .describe(
                        'The stable numeric GitHub user id for the comment author, when available.'
                      ),
                    authorLogin: zod
                      .string()
                      .nullish()
                      .describe(
                        'The GitHub login for the comment author, when available.'
                      ),
                    body: zod
                      .string()
                      .describe('The comment or review body text.'),
                    createdAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'When the comment was created or the review was submitted.'
                      ),
                    id: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemIdMin
                      )
                      .describe(
                        'The unique GitHub identifier for the comment or review.'
                      ),
                    inReplyToId: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemInReplyToIdMin
                      )
                      .nullish()
                      .describe(
                        'The id of the comment this one replies to, when it is part of a review\nthread. Only ever present on `review_comment` sources.'
                      ),
                    line: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemLineMin
                      )
                      .nullish()
                      .describe(
                        "The line in the current diff the comment is anchored to. Cleared by\nGitHub when later commits outdate the comment's diff."
                      ),
                    originalLine: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemOriginalLineMin
                      )
                      .nullish()
                      .describe(
                        'The line the comment was originally anchored to, kept even when the\ndiff has since changed.'
                      ),
                    path: zod
                      .string()
                      .nullish()
                      .describe(
                        'The repository-relative file path the review comment is anchored to.\nOnly ever present on `review_comment` sources.'
                      ),
                    pullRequestReviewId: zod
                      .number()
                      .min(
                        getDocumentGithubPullRequestsResponsePullRequestsItemCommentsItemPullRequestReviewIdMin
                      )
                      .nullish()
                      .describe(
                        'The id of the pull request review this comment was submitted with.\nOnly ever present on `review_comment` sources.'
                      ),
                    source: zod
                      .string()
                      .describe(
                        'The GitHub source for the comment, such as `issue_comment` or `review_comment`.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('When the comment or review was last updated.'),
                    url: zod
                      .string()
                      .nullish()
                      .describe(
                        'The public GitHub URL for the comment or review, when available.'
                      ),
                  })
                  .describe('A comment associated with a GitHub pull request.')
              )
              .nullish()
              .describe(
                'Comments collected from the pull request, when enrichment data is available.'
              ),
            deletions: zod
              .number()
              .min(
                getDocumentGithubPullRequestsResponsePullRequestsItemDeletionsMin
              )
              .nullish()
              .describe(
                'The number of deleted lines in the pull request, when enrichment data is available.'
              ),
            displayName: zod
              .string()
              .describe('A compact label suitable for display in the UI.'),
            foreignEntityId: zod
              .uuid()
              .nullish()
              .describe(
                'The internal `foreign_entity.id` UUID for the GitHub pull request row.'
              ),
            githubKey: zod
              .string()
              .describe(
                'The stored GitHub association key, in `owner\/repo\/pull\/number` format.'
              ),
            name: zod
              .string()
              .nullish()
              .describe(
                'The GitHub pull request title, when enrichment data is available.'
              ),
            number: zod
              .number()
              .min(
                getDocumentGithubPullRequestsResponsePullRequestsItemNumberMin
              )
              .describe('The GitHub pull request number.'),
            owner: zod
              .string()
              .describe('The GitHub repository owner or organization.'),
            repo: zod.string().describe('The GitHub repository name.'),
            status: zod
              .string()
              .nullish()
              .describe(
                'The GitHub pull request status, when enrichment data is available.'
              ),
            url: zod
              .string()
              .describe('The public GitHub URL for the pull request.'),
          })
          .describe(
            'Display-ready data for a GitHub pull request associated with a task.'
          )
      )
      .describe('Parsed pull requests, in repository query order.'),
  })
  .describe(
    'Response containing all GitHub pull requests associated with a task.'
  );

/**
 * @summary Gets the presigned url(s) for the document. aka location
 */
export const getLocationHandlerParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getLocationHandlerQueryParams = zod.object({
  document_version_id: zod
    .number()
    .describe('A specific document version id to get the location for.'),
  get_converted_docx_url: zod
    .boolean()
    .describe('If true, this will return the converted docx url.'),
});

export const getLocationHandlerResponse = zod.union([
  zod
    .object({
      presignedUrl: zod
        .string()
        .describe('The presigned url of the document if it is not a docx'),
    })
    .describe('The presigned url of the document if it is not a docx'),
  zod
    .object({
      presignedUrls: zod
        .array(
          zod.object({
            presignedUrl: zod
              .string()
              .describe('The presigned url used to upload the sha'),
            sha: zod.string().describe('The sha of the item'),
          })
        )
        .describe('The presigned urls of the docx bom parts if it is a docx'),
    })
    .describe('The presigned urls of the docx bom parts if it is a docx'),
]);

/**
 * Returns a presigned URL or sync service content for accessing the document.
 * @summary Handler for `GET /documents/{document_id}/location_v3`.
 */
export const getDocumentLocationV3Params = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentLocationV3QueryParams = zod.object({
  document_version_id: zod
    .number()
    .optional()
    .describe('A specific document version id to get the location for.'),
  get_converted_docx_url: zod
    .boolean()
    .optional()
    .describe('If true, this will return the converted docx url.'),
});

export const getDocumentLocationV3Response = zod
  .union([
    zod
      .object({
        content: zod
          .object({
            location: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'object_storage',
                    'sync_service',
                    'docx_bom_parts',
                    'converted_pdf',
                    'unknown',
                  ])
                  .describe(
                    'Where document content is, or is expected to be, read from.'
                  ),
              ])
              .optional(),
            state: zod
              .enum(['unknown', 'pending', 'ready'])
              .describe(
                'API-visible content lifecycle state derived from current document metadata.'
              ),
          })
          .describe('API-visible content lifecycle and location metadata.'),
        metadata: zod
          .object({
            branchedFromId: zod.string().nullish(),
            branchedFromVersionId: zod.number().nullish(),
            deletedAt: zod.iso.datetime({}).nullish(),
            documentFamilyId: zod.number().nullish(),
            documentId: zod.string(),
            documentName: zod.string(),
            fileType: zod.string().nullish(),
            owner: zod.string(),
            projectId: zod.string().nullish(),
            subType: zod
              .union([
                zod.null(),
                zod
                  .enum(['task', 'snippet', 'skill', 'initiative_description'])
                  .describe(
                    'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                  ),
              ])
              .optional(),
          })
          .describe(
            'Returns basic information of a document used for some db queries'
          ),
        presignedUrl: zod.string().describe('Presigned URL.'),
        type: zod.enum(['presignedUrl']),
      })
      .describe('A single document-storage URL.'),
    zod
      .object({
        content: zod
          .object({
            location: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'object_storage',
                    'sync_service',
                    'docx_bom_parts',
                    'converted_pdf',
                    'unknown',
                  ])
                  .describe(
                    'Where document content is, or is expected to be, read from.'
                  ),
              ])
              .optional(),
            state: zod
              .enum(['unknown', 'pending', 'ready'])
              .describe(
                'API-visible content lifecycle state derived from current document metadata.'
              ),
          })
          .describe('API-visible content lifecycle and location metadata.'),
        metadata: zod
          .object({
            branchedFromId: zod.string().nullish(),
            branchedFromVersionId: zod.number().nullish(),
            deletedAt: zod.iso.datetime({}).nullish(),
            documentFamilyId: zod.number().nullish(),
            documentId: zod.string(),
            documentName: zod.string(),
            fileType: zod.string().nullish(),
            owner: zod.string(),
            projectId: zod.string().nullish(),
            subType: zod
              .union([
                zod.null(),
                zod
                  .enum(['task', 'snippet', 'skill', 'initiative_description'])
                  .describe(
                    'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                  ),
              ])
              .optional(),
          })
          .describe(
            'Returns basic information of a document used for some db queries'
          ),
        presignedUrls: zod
          .array(
            zod.object({
              presignedUrl: zod
                .string()
                .describe('The presigned url used to upload the sha'),
              sha: zod.string().describe('The sha of the item'),
            })
          )
          .describe('Presigned URLs.'),
        type: zod.enum(['presignedUrls']),
      })
      .describe(
        'Multiple document-storage URLs, currently for DOCX BOM parts.'
      ),
    zod
      .object({
        content: zod
          .object({
            location: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'object_storage',
                    'sync_service',
                    'docx_bom_parts',
                    'converted_pdf',
                    'unknown',
                  ])
                  .describe(
                    'Where document content is, or is expected to be, read from.'
                  ),
              ])
              .optional(),
            state: zod
              .enum(['unknown', 'pending', 'ready'])
              .describe(
                'API-visible content lifecycle state derived from current document metadata.'
              ),
          })
          .describe('API-visible content lifecycle and location metadata.'),
        metadata: zod
          .object({
            branchedFromId: zod.string().nullish(),
            branchedFromVersionId: zod.number().nullish(),
            deletedAt: zod.iso.datetime({}).nullish(),
            documentFamilyId: zod.number().nullish(),
            documentId: zod.string(),
            documentName: zod.string(),
            fileType: zod.string().nullish(),
            owner: zod.string(),
            projectId: zod.string().nullish(),
            subType: zod
              .union([
                zod.null(),
                zod
                  .enum(['task', 'snippet', 'skill', 'initiative_description'])
                  .describe(
                    'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
                  ),
              ])
              .optional(),
          })
          .describe(
            'Returns basic information of a document used for some db queries'
          ),
        type: zod.enum(['syncServiceContent']),
      })
      .describe('Sync-service backed content.'),
  ])
  .describe('Location response with content lifecycle metadata.');

/**
 * @summary Permanently deletes a document.
 */
export const permanentlyDeleteDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const permanentlyDeleteDocumentResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Fetches the document pdf processing result for a given document id
 */
export const getDocumentProcessingResultParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentProcessingResultResponse = zod.object({
  data: zod
    .union([
      zod.null(),
      zod.object({
        result: zod.string().describe('The stringified result'),
      }),
    ])
    .optional(),
  error: zod.boolean().describe('Indicates if an error occurred'),
  message: zod.string().nullish().describe('Message to explain failure'),
});

/**
 * @summary Fetches the document processing result for a given document id and job id
 */
export const jobProcessingResultHandlerParams = zod.object({
  document_id: zod.string().describe('Document ID'),
  job_id: zod.string().describe('Job ID'),
});

export const jobProcessingResultHandlerResponse = zod.object({
  data: zod
    .union([
      zod.null(),
      zod.object({
        result: zod.string().describe('The stringified result'),
      }),
    ])
    .optional(),
  error: zod.boolean().describe('Indicates if an error occurred'),
  message: zod.string().nullish().describe('Message to explain failure'),
});

/**
 * @summary Deletes a specific document
 */
export const revertDeleteDocumentParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const revertDeleteDocumentResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * Returns the short UUID for a document.
 * @summary Handler for `GET /documents/{document_id}/short_id`.
 */
export const getDocumentShortIdParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

/**
 * @summary For any file that isn't PDF or DOCX, use this endpoint for saving.
This endpoint will allow you to save the document by providing the file content as part of a multipart request. Use key 'file' along with the file bytes.
simple_save is different than the save_document endpoint because it does not return a presigned URL, Instead, it simply saves the file to S3 directly (and returns the document metadata).
 */
export const simpleSaveParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const simpleSaveResponse = zod.object({
  data: zod.object({
    documentMetadata: zod.object({
      branchedFromId: zod
        .string()
        .nullish()
        .describe('The id of the document this document branched from'),
      branchedFromVersionId: zod
        .number()
        .nullish()
        .describe(
          'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
        ),
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was created'),
      documentBom: zod
        .array(
          zod.object({
            id: zod.string().describe('The uuid of the bom part'),
            path: zod
              .string()
              .describe('The file path of the bom part content'),
            sha: zod
              .string()
              .describe(
                'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
              ),
          })
        )
        .nullish()
        .describe(
          'If the document is a DOCX document, the document_bom will be present'
        ),
      documentFamilyId: zod
        .number()
        .nullish()
        .describe('The id of the document family this document belongs to'),
      documentId: zod.string().describe('The document id'),
      documentName: zod.string().describe('The name of the document'),
      documentVersionId: zod
        .number()
        .describe(
          'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
        ),
      fileType: zod
        .string()
        .nullish()
        .describe('The file type of the document'),
      modificationData: zod
        .unknown()
        .optional()
        .describe(
          'The modification data for the document instance.\nThis is only used for PDF documents.'
        ),
      owner: zod.string().describe('The owner of the document'),
      sha: zod
        .string()
        .nullish()
        .describe(
          'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
        ),
      subType: zod
        .union([
          zod.null(),
          zod
            .enum(['task', 'snippet', 'skill', 'initiative_description'])
            .describe(
              'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
            ),
        ])
        .optional(),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document instance \/ document BOM was updated'),
    }),
    presignedUrl: zod
      .string()
      .nullish()
      .describe(
        'If the document is an editable file, we provide a presigned url to save the updated file to.'
      ),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Gets the team-share state of a document. The team is resolved from the
document owner's team membership; `teamId` is omitted when the owner does
not belong to a team.
 */
export const getDocumentTeamShareParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentTeamShareResponse = zod
  .object({
    sharedWithTeam: zod
      .boolean()
      .describe(
        'Whether explicit team sharing is enabled; inherited team access does not count.'
      ),
    teamId: zod
      .uuid()
      .nullish()
      .describe(
        "The owner's team the document is (or would be) shared with. `None` when\nthe owner does not belong to a team."
      ),
  })
  .describe(
    "The team-share state of a document. The team is resolved from the document\nowner's team membership."
  );

/**
 * @summary Sets explicit team sharing. Requires a verified acting identity matching
the persisted document owner, not merely effective Edit or Owner access.
Initial enable defaults to Edit; repeated enable preserves the chosen level.
Clear removes only the managed direct grant, not inherited team access.
 */
export const setDocumentTeamShareParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const setDocumentTeamShareBody = zod
  .object({
    shareWithTeam: zod
      .boolean()
      .describe("Whether the document should be shared with the owner's team."),
  })
  .describe("Request body for setting a document's team-share state.");

export const setDocumentTeamShareResponse = zod
  .object({
    sharedWithTeam: zod
      .boolean()
      .describe(
        'Whether explicit team sharing is enabled; inherited team access does not count.'
      ),
    teamId: zod
      .uuid()
      .nullish()
      .describe(
        "The owner's team the document is (or would be) shared with. `None` when\nthe owner does not belong to a team."
      ),
  })
  .describe(
    "The team-share state of a document. The team is resolved from the document\nowner's team membership."
  );

/**
 * @summary Read current Markdown or extracted file text with the document's view receipt.
 */
export const getDocumentTextContentParams = zod.object({
  document_id: zod.string().describe('Document identity'),
});

export const getDocumentTextContentResponse = zod
  .object({
    text: zod
      .string()
      .nullable()
      .describe(
        'Current Markdown, or extracted PDF\/Word text. Null means use file export.'
      ),
  })
  .describe(
    'Text representation when the document supports one. Other file formats\nretain their existing download\/export behavior.'
  );

/**
 * @summary Gets the list of users who have viewed a given document
 */
export const getDocumentViewsHandlerParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentViewsHandlerResponse = zod.object({
  count: zod.number().describe('Total number of views for the document'),
  users: zod
    .array(zod.string())
    .describe('List of user emails who have viewed the document'),
});

/**
 * @summary Gets a particular document by its id
 */
export const getDocumentVersionParams = zod.object({
  document_id: zod.string().describe('Document ID'),
  document_version_id: zod.number().describe('Document Version ID'),
});

export const getDocumentVersionResponse = zod.object({
  data: zod.object({
    documentMetadata: zod.object({
      branchedFromId: zod
        .string()
        .nullish()
        .describe('The id of the document this document branched from'),
      branchedFromVersionId: zod
        .number()
        .nullish()
        .describe(
          'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
        ),
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was created'),
      deletedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document was deleted'),
      documentBom: zod
        .array(
          zod.object({
            id: zod.string().describe('The uuid of the bom part'),
            path: zod
              .string()
              .describe('The file path of the bom part content'),
            sha: zod
              .string()
              .describe(
                'The sha of the bom part content\nThere is an index on sha for more performant queries based on it.'
              ),
          })
        )
        .nullish()
        .describe(
          'If the document is a DOCX document and unzipped, the document_bom will be present'
        ),
      documentFamilyId: zod
        .number()
        .nullish()
        .describe('The id of the document family this document belongs to'),
      documentId: zod.string().describe('The document id'),
      documentName: zod.string().describe('The name of the document'),
      documentVersionId: zod
        .number()
        .describe(
          'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
        ),
      fileType: zod
        .string()
        .nullish()
        .describe('The file type of the document (file extension)'),
      modificationData: zod
        .unknown()
        .optional()
        .describe(
          'The modification data for the document instance.\nThis is only used for PDF documents.'
        ),
      owner: zod.string().describe('The owner of the document'),
      projectId: zod
        .string()
        .nullish()
        .describe('The id of the project that this document belongs to'),
      projectName: zod
        .string()
        .nullish()
        .describe('The name of the project that this document belongs to'),
      sha: zod
        .string()
        .nullish()
        .describe(
          'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
        ),
      subType: zod
        .union([
          zod.null(),
          zod
            .enum(['task', 'snippet', 'skill', 'initiative_description'])
            .describe(
              'The document sub type enum represents all values of document sub types.\nThese values should match the `document_sub_type_value` table in macrodb.\n\nWire, database, and `Display` spellings are all `snake_case` so a\nmulti-word variant serializes identically in every system.'
            ),
        ])
        .optional(),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the document instance \/ document BOM was updated'),
    }),
    userAccessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    viewLocation: zod
      .string()
      .nullish()
      .describe('The users view location if there is one'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary State access request state through the domain service.
 */
export const accessRequestsStateParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
});

export const accessRequestsStateQueryOffsetMin = 0;

export const accessRequestsStateQueryParams = zod.object({
  offset: zod
    .number()
    .min(accessRequestsStateQueryOffsetMin)
    .optional()
    .describe(
      'Offset into pending requests; page size is bounded by the service.'
    ),
});

export const accessRequestsStateResponseNextOffsetMin = 0;

export const accessRequestsStateResponse = zod
  .object({
    accessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    canReview: zod
      .boolean()
      .describe(
        'Whether this viewer can resolve requests and manage direct grants.'
      ),
    mine: zod
      .union([
        zod.null(),
        zod
          .object({
            createdAt: zod.iso
              .datetime({})
              .describe('Start of this request attempt.'),
            grantedLevel: zod
              .union([
                zod.null(),
                zod
                  .enum(['view', 'comment', 'edit', 'owner'])
                  .describe('Ordered from least to most access top -> bottom'),
              ])
              .optional(),
            id: zod
              .uuid()
              .describe('Stable request identity across revisions.'),
            requestedLevel: zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
            requesterId: zod.string().describe('Person requesting access.'),
            resolvedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the request stopped being pending.'),
            retryAfter: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'Earliest next submission or level change; cancellation preserves this deadline.\nSubmissions wait one minute, denials wait 24 hours, and approval clears it.'
              ),
            revision: zod.number().describe('Optimistic concurrency token.'),
            status: zod
              .enum(['pending', 'granted', 'denied', 'cancelled'])
              .describe(
                'Durable outcome; reading a notification never changes it.'
              ),
            updatedAt: zod.iso.datetime({}).describe('Most recent change.'),
          })
          .describe('Latest request for one person and one entity.'),
      ])
      .optional(),
    nextOffset: zod
      .number()
      .min(accessRequestsStateResponseNextOffsetMin)
      .nullish()
      .describe('Next page offset, absent at the end.'),
    owner: zod
      .string()
      .nullish()
      .describe(
        'Recorded owner principal, visible to reviewers and callers with access.'
      ),
    pendingCount: zod
      .number()
      .describe('Total outstanding requests, for the Share badge.'),
    requests: zod
      .array(
        zod
          .object({
            createdAt: zod.iso
              .datetime({})
              .describe('Start of this request attempt.'),
            grantedLevel: zod
              .union([
                zod.null(),
                zod
                  .enum(['view', 'comment', 'edit', 'owner'])
                  .describe('Ordered from least to most access top -> bottom'),
              ])
              .optional(),
            id: zod
              .uuid()
              .describe('Stable request identity across revisions.'),
            requestedLevel: zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
            requesterId: zod.string().describe('Person requesting access.'),
            resolvedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the request stopped being pending.'),
            retryAfter: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'Earliest next submission or level change; cancellation preserves this deadline.\nSubmissions wait one minute, denials wait 24 hours, and approval clears it.'
              ),
            revision: zod.number().describe('Optimistic concurrency token.'),
            status: zod
              .enum(['pending', 'granted', 'denied', 'cancelled'])
              .describe(
                'Durable outcome; reading a notification never changes it.'
              ),
            updatedAt: zod.iso.datetime({}).describe('Most recent change.'),
          })
          .describe('Latest request for one person and one entity.')
      )
      .describe('Reviewer-only pending page, oldest first.'),
    supportedLevels: zod
      .array(
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom')
      )
      .describe('Permitted access choices for this surface.'),
  })
  .describe('Share state visible to one authenticated viewer.');

/**
 * @summary Submit access request state through the domain service.
 */
export const accessRequestsSubmitParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
});

export const accessRequestsSubmitBody = zod
  .object({
    requestedLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    revision: zod
      .number()
      .nullish()
      .describe('Last observed revision; omitted only for the first request.'),
  })
  .describe(
    'Requester submission; an existing pending request requires its revision.'
  );

export const accessRequestsSubmitResponse = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('Start of this request attempt.'),
    grantedLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    id: zod.uuid().describe('Stable request identity across revisions.'),
    requestedLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    requesterId: zod.string().describe('Person requesting access.'),
    resolvedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When the request stopped being pending.'),
    retryAfter: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Earliest next submission or level change; cancellation preserves this deadline.\nSubmissions wait one minute, denials wait 24 hours, and approval clears it.'
      ),
    revision: zod.number().describe('Optimistic concurrency token.'),
    status: zod
      .enum(['pending', 'granted', 'denied', 'cancelled'])
      .describe('Durable outcome; reading a notification never changes it.'),
    updatedAt: zod.iso.datetime({}).describe('Most recent change.'),
  })
  .describe('Latest request for one person and one entity.');

/**
 * @summary Cancel access request state through the domain service.
 */
export const accessRequestsCancelParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
});

export const accessRequestsCancelQueryParams = zod.object({
  revision: zod.number().describe('Last observed request revision.'),
});

export const accessRequestsCancelResponseDefault = null;

export const accessRequestsCancelResponse = zod.unknown();

/**
 * @summary Resolve access request state through the domain service.
 */
export const accessRequestsResolveParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
  request_id: zod.uuid(),
});

export const accessRequestsResolveBody = zod
  .object({
    grantedLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    revision: zod.number().describe('Request revision being reviewed.'),
  })
  .describe("Owner's decision; omission of a granted level means deny.");

export const accessRequestsResolveResponse = zod
  .object({
    createdAt: zod.iso.datetime({}).describe('Start of this request attempt.'),
    grantedLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    id: zod.uuid().describe('Stable request identity across revisions.'),
    requestedLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    requesterId: zod.string().describe('Person requesting access.'),
    resolvedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When the request stopped being pending.'),
    retryAfter: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Earliest next submission or level change; cancellation preserves this deadline.\nSubmissions wait one minute, denials wait 24 hours, and approval clears it.'
      ),
    revision: zod.number().describe('Optimistic concurrency token.'),
    status: zod
      .enum(['pending', 'granted', 'denied', 'cancelled'])
      .describe('Durable outcome; reading a notification never changes it.'),
    updatedAt: zod.iso.datetime({}).describe('Most recent change.'),
  })
  .describe('Latest request for one person and one entity.');

/**
 * @summary Direct access request state through the domain service.
 */
export const accessRequestsDirectParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
});

export const accessRequestsDirectResponseItem = zod
  .object({
    accessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
    userId: zod.string().describe('Recipient.'),
  })
  .describe(
    'A direct user grant; inherited grants are deliberately not editable here.'
  );
export const accessRequestsDirectResponse = zod.array(
  accessRequestsDirectResponseItem
);

/**
 * @summary Set direct access request state through the domain service.
 */
export const accessRequestsSetDirectParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
  user_id: zod.string(),
});

export const accessRequestsSetDirectBody = zod
  .object({
    accessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
  })
  .describe(
    'Direct-grant change. A null level removes only that direct grant.'
  );

export const accessRequestsSetDirectResponseDefault = null;

export const accessRequestsSetDirectResponse = zod.unknown();

/**
 * @summary Remove direct access request state through the domain service.
 */
export const accessRequestsRemoveDirectParams = zod.object({
  entity_type: zod.enum([
    'document',
    'chat',
    'project',
    'initiative',
    'database',
    'call',
    'email_thread',
    'agent_session',
  ]),
  entity_id: zod.uuid(),
  user_id: zod.string(),
});

export const accessRequestsRemoveDirectResponseDefault = null;

export const accessRequestsRemoveDirectResponse = zod.unknown();

/**
 * @summary Get the current user's permission for a given entity.
 */
export const getEntityPermissionParams = zod.object({
  entity_type: zod
    .string()
    .describe(
      'Entity type (document, chat, project, thread, email_thread, channel)'
    ),
  entity_id: zod.string().describe('Entity ID'),
});

export const getEntityPermissionResponse = zod
  .union([
    zod.object({
      permission: zod
        .union([
          zod
            .object({
              access_level: zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
              type: zod.enum(['access_level']),
            })
            .describe(
              'Permission for item-based entities (document, chat, project, thread).'
            ),
          zod
            .object({
              type: zod.enum(['channel_view_only']),
            })
            .describe(
              'View-only permission for a channel without an active participant role.'
            ),
          zod
            .object({
              role: zod
                .enum(['owner', 'admin', 'member'])
                .describe('The role a user has within a channel.'),
              type: zod.enum(['channel_role']),
            })
            .describe(
              'Permission for channel-based entities with an active participant role.'
            ),
          zod
            .object({
              role: zod
                .enum(['member', 'admin', 'owner'])
                .describe(
                  'The role a user has within a team.\n\nOrdered least to most privileged so comparisons reflect access strength.'
                ),
              type: zod.enum(['team_role']),
            })
            .describe('Permission for team-based entities.'),
        ])
        .describe(
          "A user's permission for an entity, discriminated by entity kind.\n\nItems (documents, chats, projects, threads) use access levels.\nChannels use view-only permission or participant roles."
        ),
      status: zod.enum(['access']),
    }),
    zod.object({
      status: zod.enum(['no_access']),
    }),
  ])
  .describe('API response envelope for entity permissions.');

/**
 * @summary List the caller's favorites.
 */
export const listFavoritesQueryParams = zod.object({
  entityType: zod
    .array(
      zod
        .enum([
          'user',
          'chat',
          'channel',
          'channel_message',
          'document',
          'project',
          'email_thread',
          'calendar_event',
          'team',
          'call',
          'foreign_entity',
          'static_file',
          'crm_company',
          'crm_contact',
          'crm_pipeline',
          'reminder',
          'skill',
          'agent_session',
          'scheduled_action',
          'initiative',
          'database',
          'database_row',
          'form',
        ])
        .describe('The type of an entity in Macro')
    )
    .optional()
    .describe('Restrict to favorites whose entity is one of these types.'),
  entityId: zod
    .array(zod.string())
    .optional()
    .describe('Restrict to favorites whose entity is one of these ids.'),
});

export const listFavoritesResponse = zod
  .object({
    favorites: zod
      .array(
        zod
          .object({
            channelId: zod
              .string()
              .nullish()
              .describe(
                'Owning channel id of the favorited channel message, when applicable.'
              ),
            channelType: zod
              .string()
              .nullish()
              .describe(
                'Channel type (e.g. `public`, `direct_message`) of the favorited channel, when applicable.'
              ),
            createdAt: zod.iso
              .datetime({})
              .describe('When the favorite was created.'),
            documentSubType: zod
              .string()
              .nullish()
              .describe(
                'Document sub type (e.g. `task`) of the favorited document, when applicable.'
              ),
            entityId: zod.string().describe('The id of the favorited entity.'),
            entityType: zod
              .enum([
                'user',
                'chat',
                'channel',
                'channel_message',
                'document',
                'project',
                'email_thread',
                'calendar_event',
                'team',
                'call',
                'foreign_entity',
                'static_file',
                'crm_company',
                'crm_contact',
                'crm_pipeline',
                'reminder',
                'skill',
                'agent_session',
                'scheduled_action',
                'initiative',
                'database',
                'database_row',
                'form',
              ])
              .describe('The type of an entity in Macro')
              .describe('The type of the favorited entity.'),
            fileType: zod
              .string()
              .nullish()
              .describe(
                'File type of the favorited document, when applicable.'
              ),
            sortOrder: zod
              .number()
              .describe('Manual ordering value; lower sorts first.'),
          })
          .describe(
            "A single favorited entity, including display metadata hydrated from the\nfavorited entity where available. Display names are deliberately not\nincluded: clients resolve them from the entity previews pipeline, which\nis viewer-relative for DM channels and updated by renames.\n\nA favorite is identified by `(entity_type, entity_id)` within the user's\ncollection; there is no surrogate id."
          )
      )
      .describe("The requesting user's favorites, in manual order."),
  })
  .describe("The user's favorites, in manual order.");

/**
 * @summary Favorite an entity in the caller's collection.
 */
export const addFavoriteBody = zod
  .object({
    entityId: zod.string().describe('The id of the entity to favorite.'),
    entityType: zod
      .enum([
        'user',
        'chat',
        'channel',
        'channel_message',
        'document',
        'project',
        'email_thread',
        'calendar_event',
        'team',
        'call',
        'foreign_entity',
        'static_file',
        'crm_company',
        'crm_contact',
        'crm_pipeline',
        'reminder',
        'skill',
        'agent_session',
        'scheduled_action',
        'initiative',
        'database',
        'database_row',
        'form',
      ])
      .describe('The type of an entity in Macro')
      .describe('The type of the entity to favorite.'),
  })
  .describe('Request body for favoriting an entity.');

export const addFavoriteResponse = zod
  .object({
    channelId: zod
      .string()
      .nullish()
      .describe(
        'Owning channel id of the favorited channel message, when applicable.'
      ),
    channelType: zod
      .string()
      .nullish()
      .describe(
        'Channel type (e.g. `public`, `direct_message`) of the favorited channel, when applicable.'
      ),
    createdAt: zod.iso.datetime({}).describe('When the favorite was created.'),
    documentSubType: zod
      .string()
      .nullish()
      .describe(
        'Document sub type (e.g. `task`) of the favorited document, when applicable.'
      ),
    entityId: zod.string().describe('The id of the favorited entity.'),
    entityType: zod
      .enum([
        'user',
        'chat',
        'channel',
        'channel_message',
        'document',
        'project',
        'email_thread',
        'calendar_event',
        'team',
        'call',
        'foreign_entity',
        'static_file',
        'crm_company',
        'crm_contact',
        'crm_pipeline',
        'reminder',
        'skill',
        'agent_session',
        'scheduled_action',
        'initiative',
        'database',
        'database_row',
        'form',
      ])
      .describe('The type of an entity in Macro')
      .describe('The type of the favorited entity.'),
    fileType: zod
      .string()
      .nullish()
      .describe('File type of the favorited document, when applicable.'),
    sortOrder: zod
      .number()
      .describe('Manual ordering value; lower sorts first.'),
  })
  .describe(
    "A single favorited entity, including display metadata hydrated from the\nfavorited entity where available. Display names are deliberately not\nincluded: clients resolve them from the entity previews pipeline, which\nis viewer-relative for DM channels and updated by renames.\n\nA favorite is identified by `(entity_type, entity_id)` within the user's\ncollection; there is no surrogate id."
  );

/**
 * @summary Persist a manual order for the caller's favorites.
 */
export const reorderFavoritesBody = zod
  .object({
    favorites: zod
      .array(
        zod
          .object({
            entityId: zod.string().describe('The id of the favorited entity.'),
            entityType: zod
              .enum([
                'user',
                'chat',
                'channel',
                'channel_message',
                'document',
                'project',
                'email_thread',
                'calendar_event',
                'team',
                'call',
                'foreign_entity',
                'static_file',
                'crm_company',
                'crm_contact',
                'crm_pipeline',
                'reminder',
                'skill',
                'agent_session',
                'scheduled_action',
                'initiative',
                'database',
                'database_row',
                'form',
              ])
              .describe('The type of an entity in Macro')
              .describe('The type of the favorited entity.'),
          })
          .describe('A reference to a favorited entity.')
      )
      .describe("The user's favorited entities in the desired order."),
  })
  .describe('Request body for reordering favorites.');

export const reorderFavoritesResponseDefault = null;

export const reorderFavoritesResponse = zod.unknown();

/**
 * @summary Remove a favorite by entity.
 */
export const removeFavoriteByEntityParams = zod.object({
  entity_type: zod
    .enum([
      'user',
      'chat',
      'channel',
      'channel_message',
      'document',
      'project',
      'email_thread',
      'calendar_event',
      'team',
      'call',
      'foreign_entity',
      'static_file',
      'crm_company',
      'crm_contact',
      'crm_pipeline',
      'reminder',
      'skill',
      'agent_session',
      'scheduled_action',
      'initiative',
      'database',
      'database_row',
      'form',
    ])
    .describe('The type of the favorited entity.'),
  entity_id: zod.string().describe('The id of the favorited entity.'),
});

export const removeFavoriteByEntityResponseDefault = null;

export const removeFavoriteByEntityResponse = zod.unknown();

/**
 * `foreign_entity_id` is a wildcard path segment: sources store slashes inside
the identifier, for example `owner/repo/pull/12`.

Authorization matches the by-id route. Internal service callers see every
record; an authenticated user sees a record only when they have view access
to it, and records they cannot view are reported as `404` so the route never
reveals that a mapping exists. Bot tokens are not accepted here — they use
the by-id route, which mints a bot-scoped receipt.
 * @summary Get a visible foreign entity by the identifier its source system assigned.
 */
export const getForeignEntityBySourceParams = zod.object({
  source: zod
    .string()
    .describe('Foreign entity source, e.g. github_pull_request'),
  foreign_entity_id: zod
    .string()
    .describe('Identifier assigned by the source system; may contain slashes'),
});

export const getForeignEntityBySourceResponse = zod
  .object({
    createdAt: zod.iso
      .datetime({})
      .describe('Timestamp when the record was created.'),
    foreignEntityId: zod
      .string()
      .describe('Identifier assigned by the external system.'),
    foreignEntitySource: zod
      .string()
      .describe('Source system that owns the external identifier.'),
    id: zod
      .uuid()
      .describe('Internal primary key for this foreign entity record.'),
    metadata: zod
      .unknown()
      .describe('Arbitrary metadata stored with the mapping.'),
    storedForAuthEntity: zod
      .string()
      .describe(
        'Internal auth entity namespace this foreign entity is stored for.'
      ),
    storedForId: zod
      .string()
      .describe(
        'Internal entity identifier this foreign entity is stored for.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Timestamp when the record was last updated.'),
  })
  .describe('A persisted mapping to an entity owned by an external system.');

/**
 * @summary Get a visible foreign entity by its internal ID.
 */
export const getForeignEntityParams = zod.object({
  id: zod.uuid().describe('Foreign entity ID'),
});

export const getForeignEntityResponse = zod
  .object({
    createdAt: zod.iso
      .datetime({})
      .describe('Timestamp when the record was created.'),
    foreignEntityId: zod
      .string()
      .describe('Identifier assigned by the external system.'),
    foreignEntitySource: zod
      .string()
      .describe('Source system that owns the external identifier.'),
    id: zod
      .uuid()
      .describe('Internal primary key for this foreign entity record.'),
    metadata: zod
      .unknown()
      .describe('Arbitrary metadata stored with the mapping.'),
    storedForAuthEntity: zod
      .string()
      .describe(
        'Internal auth entity namespace this foreign entity is stored for.'
      ),
    storedForId: zod
      .string()
      .describe(
        'Internal entity identifier this foreign entity is stored for.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Timestamp when the record was last updated.'),
  })
  .describe('A persisted mapping to an entity owned by an external system.');

/**
 * @summary The live forms over a database the caller can see, for the grid's chip
and the delete-table confirmation.
 */
export const listFormsQueryParams = zod.object({
  databaseId: zod.uuid().describe('The database.'),
});

export const listFormsResponseItem = zod
  .object({
    audience: zod
      .enum(['members', 'public'])
      .describe('Who may respond to a form.'),
    closesAt: zod.iso
      .datetime({})
      .nullable()
      .describe('When it stops taking responses, if it does.'),
    confirmationMessage: zod
      .string()
      .describe(
        'What a respondent reads once their response is saved; empty for the\ndefault.'
      ),
    createdAt: zod.iso.datetime({}).describe('When it was created.'),
    databaseId: zod.uuid().describe('The database holding its responses.'),
    description: zod.string().describe('What respondents read under the name.'),
    id: zod.uuid().describe('The form.'),
    name: zod
      .string()
      .describe(
        'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
      ),
    ownerId: zod.string().describe('Its owner.'),
    respondentColumnId: zod
      .uuid()
      .nullable()
      .describe(
        'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
      ),
    status: zod
      .enum(['open', 'closed'])
      .describe(
        'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
      ),
    submittedColumnId: zod
      .uuid()
      .nullable()
      .describe('The date column each submission stamps; `null` once deleted.'),
    tableId: zod.uuid().describe('The table whose rows are its responses.'),
    tallyVisible: zod
      .boolean()
      .describe('Whether respondents may read option tallies.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When its facts or layout last changed.'),
  })
  .describe(
    'A form: a view of one database table whose rows are its responses.'
  );
export const listFormsResponse = zod.array(listFormsResponseItem);

/**
 * @summary Create a form owned by the caller: over a new database named like it, or
over an existing table of a database the caller owns. Database Edit is
not enough to attach a form: the form's editors gain Edit on the
database through it.
 */
export const createFormBody = zod
  .object({
    name: zod.string().describe('Its name; a new database takes it too.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['new']),
          })
          .describe(
            'A new database named like the form, whose one table, \"Responses\",\nstarts with only the form\'s own columns.'
          ),
        zod
          .object({
            databaseId: zod.uuid().describe("The table's database."),
            kind: zod.enum(['table']),
            tableId: zod.uuid().describe('The table.'),
          })
          .describe(
            'An existing table in a database the caller owns. The form starts with a\nquestion per column.'
          ),
      ])
      .describe("Where a new form's responses go."),
  })
  .describe('A request to create a form.');

/**
 * @summary Every live form the caller holds a grant on, newest first, with their
level: what Drive and Quick Access list. Public forms reached only by
their link are not listed.
 */
export const listAccessibleFormsResponseItem = zod
  .object({
    access: zod
      .enum(['view', 'edit', 'owner'])
      .describe(
        "The caller's level on a form: view responds, edit changes questions and\nreads responses, owner also sets the audience, closes and trashes it."
      ),
    form: zod
      .object({
        audience: zod
          .enum(['members', 'public'])
          .describe('Who may respond to a form.'),
        closesAt: zod.iso
          .datetime({})
          .nullable()
          .describe('When it stops taking responses, if it does.'),
        confirmationMessage: zod
          .string()
          .describe(
            'What a respondent reads once their response is saved; empty for the\ndefault.'
          ),
        createdAt: zod.iso.datetime({}).describe('When it was created.'),
        databaseId: zod.uuid().describe('The database holding its responses.'),
        description: zod
          .string()
          .describe('What respondents read under the name.'),
        id: zod.uuid().describe('The form.'),
        name: zod
          .string()
          .describe(
            'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
          ),
        ownerId: zod.string().describe('Its owner.'),
        respondentColumnId: zod
          .uuid()
          .nullable()
          .describe(
            'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
          ),
        status: zod
          .enum(['open', 'closed'])
          .describe(
            'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
          ),
        submittedColumnId: zod
          .uuid()
          .nullable()
          .describe(
            'The date column each submission stamps; `null` once deleted.'
          ),
        tableId: zod.uuid().describe('The table whose rows are its responses.'),
        tallyVisible: zod
          .boolean()
          .describe('Whether respondents may read option tallies.'),
        updatedAt: zod.iso
          .datetime({})
          .describe('When its facts or layout last changed.'),
      })
      .describe(
        'A form: a view of one database table whose rows are its responses.'
      ),
  })
  .describe(
    "A form the caller reaches through a grant, as the forms catalog lists\nit, with the caller's level on it."
  );
export const listAccessibleFormsResponse = zod.array(
  listAccessibleFormsResponseItem
);

/**
 * @summary A form with its layout, each question joined with its column's title,
type and options. Never rows. Readable by anyone when the form is public.
 */
export const getFormParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const getFormResponse = zod
  .object({
    access: zod
      .enum(['view', 'edit', 'owner'])
      .describe(
        "The caller's level on a form: view responds, edit changes questions and\nreads responses, owner also sets the audience, closes and trashes it."
      ),
    form: zod
      .object({
        audience: zod
          .enum(['members', 'public'])
          .describe('Who may respond to a form.'),
        closesAt: zod.iso
          .datetime({})
          .nullable()
          .describe('When it stops taking responses, if it does.'),
        confirmationMessage: zod
          .string()
          .describe(
            'What a respondent reads once their response is saved; empty for the\ndefault.'
          ),
        createdAt: zod.iso.datetime({}).describe('When it was created.'),
        databaseId: zod.uuid().describe('The database holding its responses.'),
        description: zod
          .string()
          .describe('What respondents read under the name.'),
        id: zod.uuid().describe('The form.'),
        name: zod
          .string()
          .describe(
            'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
          ),
        ownerId: zod.string().describe('Its owner.'),
        respondentColumnId: zod
          .uuid()
          .nullable()
          .describe(
            'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
          ),
        status: zod
          .enum(['open', 'closed'])
          .describe(
            'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
          ),
        submittedColumnId: zod
          .uuid()
          .nullable()
          .describe(
            'The date column each submission stamps; `null` once deleted.'
          ),
        tableId: zod.uuid().describe('The table whose rows are its responses.'),
        tallyVisible: zod
          .boolean()
          .describe('Whether respondents may read option tallies.'),
        updatedAt: zod.iso
          .datetime({})
          .describe('When its facts or layout last changed.'),
      })
      .describe(
        'A form: a view of one database table whose rows are its responses.'
      ),
    sections: zod
      .array(
        zod
          .union([
            zod
              .object({
                description: zod.string().describe('Its description.'),
                id: zod.uuid().describe('The section.'),
                kind: zod.enum(['questions']),
                questions: zod
                  .array(
                    zod
                      .object({
                        column: zod.uuid().describe('The column it writes.'),
                        helpText: zod
                          .string()
                          .describe('What respondents read under the title.'),
                        id: zod.uuid().describe('The question.'),
                        kind: zod
                          .union([
                            zod
                              .object({
                                type: zod.enum(['text']),
                              })
                              .describe('Free text.'),
                            zod
                              .object({
                                type: zod.enum(['number']),
                              })
                              .describe('A number.'),
                            zod
                              .object({
                                type: zod.enum(['boolean']),
                              })
                              .describe('A checkbox.'),
                            zod
                              .object({
                                type: zod.enum(['date']),
                              })
                              .describe('A date-time.'),
                            zod
                              .object({
                                type: zod.enum(['link']),
                              })
                              .describe('A URL.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select']),
                              })
                              .describe('Text options.'),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several options.'
                                  ),
                                type: zod.enum(['select_number']),
                              })
                              .describe('Numeric options.'),
                            zod
                              .object({
                                type: zod.enum(['tag']),
                              })
                              .describe(
                                'Colored labels; always several per cell.'
                              ),
                            zod
                              .object({
                                multi: zod
                                  .boolean()
                                  .describe(
                                    'Whether a cell holds several references.'
                                  ),
                                target: zod
                                  .enum([
                                    'USER',
                                    'DOCUMENT',
                                    'TASK',
                                    'COMPANY',
                                    'CONTACT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'PROJECT',
                                    'THREAD',
                                    'CALENDAR_EVENT',
                                    'INITIATIVE',
                                  ])
                                  .describe(
                                    'A kind of Macro entity a reference column can point at.'
                                  ),
                                type: zod.enum(['entity']),
                              })
                              .describe('References to Macro entities.'),
                            zod
                              .object({
                                database: zod
                                  .uuid()
                                  .describe(
                                    'The database of the related table.'
                                  ),
                                table: zod
                                  .uuid()
                                  .describe('The related table.'),
                                type: zod.enum(['relation']),
                              })
                              .describe('Rows of another table.'),
                          ])
                          .describe('A type a column can have.'),
                        options: zod
                          .array(
                            zod
                              .object({
                                color: zod
                                  .string()
                                  .nullable()
                                  .describe(
                                    'Its colour, a hex string, if it has one.'
                                  ),
                                id: zod.uuid().describe('The option.'),
                                label: zod.string().describe('Its label.'),
                              })
                              .describe("One option of a question's column.")
                          )
                          .describe(
                            "The column's options, in order, for a select or tag column."
                          ),
                        required: zod
                          .boolean()
                          .describe('Whether a response must answer it.'),
                        title: zod.string().describe("The column's name."),
                        widget: zod.union([
                          zod.null(),
                          zod
                            .enum([
                              'short',
                              'paragraph',
                              'datetime',
                              'date',
                              'url',
                              'file',
                              'choice',
                              'dropdown',
                              'checkboxes',
                            ])
                            .describe(
                              'How a question is asked. Each column kind takes a few, the first its\ndefault; kinds asked one way only (numbers, checkboxes, entity and row\npickers) take none.'
                            ),
                        ]),
                      })
                      .describe("A question with its column's facts.")
                  )
                  .describe('Its questions, in order.'),
                title: zod.string().describe('Its title.'),
              })
              .describe('Questions asked together.'),
            zod
              .object({
                description: zod.string().describe('Its description.'),
                id: zod.uuid().describe('The section.'),
                kind: zod.enum(['gate']),
                message: zod
                  .string()
                  .describe('What a stopped respondent reads.'),
                rules: zod
                  .object({
                    conditions: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                column: zod
                                  .uuid()
                                  .describe('The column tested.'),
                                test: zod
                                  .union([
                                    zod
                                      .object({
                                        kind: zod.enum(['presence']),
                                        operator: zod
                                          .enum(['isEmpty', 'isNotEmpty'])
                                          .describe('Whether a cell is empty.'),
                                      })
                                      .describe(
                                        'Whether the cell is empty; fits a column of any type.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['text']),
                                        operator: zod
                                          .enum([
                                            'is',
                                            'isNot',
                                            'contains',
                                            'doesNotContain',
                                            'startsWith',
                                            'endsWith',
                                          ])
                                          .describe(
                                            'How a text cell compares to a text.'
                                          ),
                                        value: zod
                                          .string()
                                          .describe(
                                            'The text compared against, ignoring case for the containment\ntests.'
                                          ),
                                      })
                                      .describe('A text or link column.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['number']),
                                        operator: zod
                                          .enum([
                                            'is',
                                            'isNot',
                                            'greaterThan',
                                            'greaterThanOrEqual',
                                            'lessThan',
                                            'lessThanOrEqual',
                                          ])
                                          .describe(
                                            'How a number cell compares to a number.'
                                          ),
                                        value: zod
                                          .number()
                                          .describe(
                                            'The number compared against; finite.'
                                          ),
                                      })
                                      .describe('A number column.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['date']),
                                        operator: zod
                                          .enum([
                                            'before',
                                            'after',
                                            'onOrBefore',
                                            'onOrAfter',
                                          ])
                                          .describe(
                                            'How a date cell compares to a date-time.'
                                          ),
                                        value: zod.iso
                                          .datetime({})
                                          .describe(
                                            'The date-time compared against.'
                                          ),
                                      })
                                      .describe('A date column.'),
                                    zod
                                      .object({
                                        checked: zod
                                          .boolean()
                                          .describe(
                                            'Whether the box is checked.'
                                          ),
                                        kind: zod.enum(['checkbox']),
                                      })
                                      .describe(
                                        'A checkbox column. An unchecked box and an empty cell are the same.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['options']),
                                        operator: zod
                                          .enum([
                                            'isAnyOf',
                                            'isNoneOf',
                                            'hasAny',
                                            'hasAll',
                                            'hasNone',
                                          ])
                                          .describe(
                                            "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                          ),
                                        options: zod
                                          .array(zod.uuid())
                                          .describe(
                                            'Options of the column; at least one.'
                                          ),
                                      })
                                      .describe('A select or tag column.'),
                                    zod
                                      .object({
                                        entities: zod
                                          .array(zod.string())
                                          .describe(
                                            "Entity ids, or for a relation the related rows' ids; at least\none."
                                          ),
                                        kind: zod.enum(['entities']),
                                        operator: zod
                                          .enum([
                                            'isAnyOf',
                                            'isNoneOf',
                                            'hasAny',
                                            'hasAll',
                                            'hasNone',
                                          ])
                                          .describe(
                                            "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                          ),
                                      })
                                      .describe(
                                        'A reference or relation column.'
                                      ),
                                  ])
                                  .describe(
                                    "What a column's cell must be, by the kind of value the column holds."
                                  ),
                              })
                              .describe("A test of one column's cells.")
                              .and(
                                zod.object({
                                  kind: zod.enum(['condition']),
                                })
                              )
                              .describe('A test of one column.'),
                            zod
                              .unknown()
                              .and(
                                zod.object({
                                  kind: zod.enum(['group']),
                                })
                              )
                              .describe('A nested group.'),
                          ])
                          .describe(
                            'One entry of a group: a condition, or a group of its own.'
                          )
                      )
                      .describe(
                        'The conditions and nested groups. A group without any keeps every\nrow.'
                      ),
                    conjunction: zod
                      .enum(['and', 'or'])
                      .describe("How a group's conditions combine."),
                  })
                  .describe('Conditions joined by one conjunction.'),
                title: zod.string().describe('Its title.'),
              })
              .describe(
                "A check of earlier answers. The rules are sent to every caller so a\nrespondent's page can stop them before submitting; respondents are\nnever shown them."
              ),
            zod
              .object({
                description: zod.string().describe('Its description.'),
                id: zod.uuid().describe('The section.'),
                kind: zod.enum(['booking']),
                target: zod
                  .object({
                    eventTypeId: zod.uuid().describe('The event type to book.'),
                    profileId: zod
                      .uuid()
                      .describe('The scheduling profile that owns the event.'),
                  })
                  .optional()
                  .describe(
                    'An existing native Macro scheduling event offered after an accepted response.'
                  ),
                title: zod.string().describe('Its title.'),
              })
              .describe(
                'A booking step. Its destination is withheld from respondents until\ntheir response has passed every screener and been accepted.'
              ),
          ])
          .describe('One section of a form as it reads.')
      )
      .describe('Its sections, in order.'),
    tableGone: zod
      .boolean()
      .describe(
        'Whether its database is in the trash, so it has no table to show or\nwrite: its sections keep no questions and it takes no responses.'
      ),
  })
  .describe('A form with its layout, as the caller may see it.');

/**
 * @summary Change a form's facts. The description and confirmation message take
edit; who responds, open or closed, the closing time and tallies take
its owner.
 */
export const updateFormParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const updateFormBody = zod
  .object({
    audience: zod
      .enum(['members', 'public'])
      .optional()
      .describe('Who may respond to a form.'),
    closesAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When it stops taking responses, or `null` for never.'),
    confirmationMessage: zod
      .string()
      .optional()
      .describe('Its new confirmation message.'),
    description: zod.string().optional().describe('Its new description.'),
    status: zod
      .enum(['open', 'closed'])
      .optional()
      .describe(
        'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
      ),
    tallyVisible: zod
      .boolean()
      .optional()
      .describe('Whether respondents may read option tallies.'),
  })
  .describe(
    "A change to a form's facts; what is left out stays. The description and\nconfirmation message take edit, the rest owner. A form is renamed\nthrough the entity mutation router."
  );

export const updateFormResponse = zod
  .object({
    audience: zod
      .enum(['members', 'public'])
      .describe('Who may respond to a form.'),
    closesAt: zod.iso
      .datetime({})
      .nullable()
      .describe('When it stops taking responses, if it does.'),
    confirmationMessage: zod
      .string()
      .describe(
        'What a respondent reads once their response is saved; empty for the\ndefault.'
      ),
    createdAt: zod.iso.datetime({}).describe('When it was created.'),
    databaseId: zod.uuid().describe('The database holding its responses.'),
    description: zod.string().describe('What respondents read under the name.'),
    id: zod.uuid().describe('The form.'),
    name: zod
      .string()
      .describe(
        'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
      ),
    ownerId: zod.string().describe('Its owner.'),
    respondentColumnId: zod
      .uuid()
      .nullable()
      .describe(
        'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
      ),
    status: zod
      .enum(['open', 'closed'])
      .describe(
        'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
      ),
    submittedColumnId: zod
      .uuid()
      .nullable()
      .describe('The date column each submission stamps; `null` once deleted.'),
    tableId: zod.uuid().describe('The table whose rows are its responses.'),
    tallyVisible: zod
      .boolean()
      .describe('Whether respondents may read option tallies.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When its facts or layout last changed.'),
  })
  .describe(
    'A form: a view of one database table whose rows are its responses.'
  );

/**
 * @summary Open the editor-only collaborative layout, or publish its latest valid
revision. Content stays in the existing collaboration surface.
 */
export const collaborateFormParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const collaborateFormResponsePublicationErrorProblemMaxMin = 0;

export const collaborateFormResponse = zod
  .object({
    detail: zod
      .object({
        access: zod
          .enum(['view', 'edit', 'owner'])
          .describe(
            "The caller's level on a form: view responds, edit changes questions and\nreads responses, owner also sets the audience, closes and trashes it."
          ),
        form: zod
          .object({
            audience: zod
              .enum(['members', 'public'])
              .describe('Who may respond to a form.'),
            closesAt: zod.iso
              .datetime({})
              .nullable()
              .describe('When it stops taking responses, if it does.'),
            confirmationMessage: zod
              .string()
              .describe(
                'What a respondent reads once their response is saved; empty for the\ndefault.'
              ),
            createdAt: zod.iso.datetime({}).describe('When it was created.'),
            databaseId: zod
              .uuid()
              .describe('The database holding its responses.'),
            description: zod
              .string()
              .describe('What respondents read under the name.'),
            id: zod.uuid().describe('The form.'),
            name: zod
              .string()
              .describe(
                'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
              ),
            ownerId: zod.string().describe('Its owner.'),
            respondentColumnId: zod
              .uuid()
              .nullable()
              .describe(
                'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
              ),
            status: zod
              .enum(['open', 'closed'])
              .describe(
                'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
              ),
            submittedColumnId: zod
              .uuid()
              .nullable()
              .describe(
                'The date column each submission stamps; `null` once deleted.'
              ),
            tableId: zod
              .uuid()
              .describe('The table whose rows are its responses.'),
            tallyVisible: zod
              .boolean()
              .describe('Whether respondents may read option tallies.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When its facts or layout last changed.'),
          })
          .describe(
            'A form: a view of one database table whose rows are its responses.'
          ),
        sections: zod
          .array(
            zod
              .union([
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['questions']),
                    questions: zod
                      .array(
                        zod
                          .object({
                            column: zod
                              .uuid()
                              .describe('The column it writes.'),
                            helpText: zod
                              .string()
                              .describe(
                                'What respondents read under the title.'
                              ),
                            id: zod.uuid().describe('The question.'),
                            kind: zod
                              .union([
                                zod
                                  .object({
                                    type: zod.enum(['text']),
                                  })
                                  .describe('Free text.'),
                                zod
                                  .object({
                                    type: zod.enum(['number']),
                                  })
                                  .describe('A number.'),
                                zod
                                  .object({
                                    type: zod.enum(['boolean']),
                                  })
                                  .describe('A checkbox.'),
                                zod
                                  .object({
                                    type: zod.enum(['date']),
                                  })
                                  .describe('A date-time.'),
                                zod
                                  .object({
                                    type: zod.enum(['link']),
                                  })
                                  .describe('A URL.'),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several options.'
                                      ),
                                    type: zod.enum(['select']),
                                  })
                                  .describe('Text options.'),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several options.'
                                      ),
                                    type: zod.enum(['select_number']),
                                  })
                                  .describe('Numeric options.'),
                                zod
                                  .object({
                                    type: zod.enum(['tag']),
                                  })
                                  .describe(
                                    'Colored labels; always several per cell.'
                                  ),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several references.'
                                      ),
                                    target: zod
                                      .enum([
                                        'USER',
                                        'DOCUMENT',
                                        'TASK',
                                        'COMPANY',
                                        'CONTACT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'PROJECT',
                                        'THREAD',
                                        'CALENDAR_EVENT',
                                        'INITIATIVE',
                                      ])
                                      .describe(
                                        'A kind of Macro entity a reference column can point at.'
                                      ),
                                    type: zod.enum(['entity']),
                                  })
                                  .describe('References to Macro entities.'),
                                zod
                                  .object({
                                    database: zod
                                      .uuid()
                                      .describe(
                                        'The database of the related table.'
                                      ),
                                    table: zod
                                      .uuid()
                                      .describe('The related table.'),
                                    type: zod.enum(['relation']),
                                  })
                                  .describe('Rows of another table.'),
                              ])
                              .describe('A type a column can have.'),
                            options: zod
                              .array(
                                zod
                                  .object({
                                    color: zod
                                      .string()
                                      .nullable()
                                      .describe(
                                        'Its colour, a hex string, if it has one.'
                                      ),
                                    id: zod.uuid().describe('The option.'),
                                    label: zod.string().describe('Its label.'),
                                  })
                                  .describe(
                                    "One option of a question's column."
                                  )
                              )
                              .describe(
                                "The column's options, in order, for a select or tag column."
                              ),
                            required: zod
                              .boolean()
                              .describe('Whether a response must answer it.'),
                            title: zod.string().describe("The column's name."),
                            widget: zod.union([
                              zod.null(),
                              zod
                                .enum([
                                  'short',
                                  'paragraph',
                                  'datetime',
                                  'date',
                                  'url',
                                  'file',
                                  'choice',
                                  'dropdown',
                                  'checkboxes',
                                ])
                                .describe(
                                  'How a question is asked. Each column kind takes a few, the first its\ndefault; kinds asked one way only (numbers, checkboxes, entity and row\npickers) take none.'
                                ),
                            ]),
                          })
                          .describe("A question with its column's facts.")
                      )
                      .describe('Its questions, in order.'),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe('Questions asked together.'),
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['gate']),
                    message: zod
                      .string()
                      .describe('What a stopped respondent reads.'),
                    rules: zod
                      .object({
                        conditions: zod
                          .array(
                            zod
                              .union([
                                zod
                                  .object({
                                    column: zod
                                      .uuid()
                                      .describe('The column tested.'),
                                    test: zod
                                      .union([
                                        zod
                                          .object({
                                            kind: zod.enum(['presence']),
                                            operator: zod
                                              .enum(['isEmpty', 'isNotEmpty'])
                                              .describe(
                                                'Whether a cell is empty.'
                                              ),
                                          })
                                          .describe(
                                            'Whether the cell is empty; fits a column of any type.'
                                          ),
                                        zod
                                          .object({
                                            kind: zod.enum(['text']),
                                            operator: zod
                                              .enum([
                                                'is',
                                                'isNot',
                                                'contains',
                                                'doesNotContain',
                                                'startsWith',
                                                'endsWith',
                                              ])
                                              .describe(
                                                'How a text cell compares to a text.'
                                              ),
                                            value: zod
                                              .string()
                                              .describe(
                                                'The text compared against, ignoring case for the containment\ntests.'
                                              ),
                                          })
                                          .describe('A text or link column.'),
                                        zod
                                          .object({
                                            kind: zod.enum(['number']),
                                            operator: zod
                                              .enum([
                                                'is',
                                                'isNot',
                                                'greaterThan',
                                                'greaterThanOrEqual',
                                                'lessThan',
                                                'lessThanOrEqual',
                                              ])
                                              .describe(
                                                'How a number cell compares to a number.'
                                              ),
                                            value: zod
                                              .number()
                                              .describe(
                                                'The number compared against; finite.'
                                              ),
                                          })
                                          .describe('A number column.'),
                                        zod
                                          .object({
                                            kind: zod.enum(['date']),
                                            operator: zod
                                              .enum([
                                                'before',
                                                'after',
                                                'onOrBefore',
                                                'onOrAfter',
                                              ])
                                              .describe(
                                                'How a date cell compares to a date-time.'
                                              ),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'The date-time compared against.'
                                              ),
                                          })
                                          .describe('A date column.'),
                                        zod
                                          .object({
                                            checked: zod
                                              .boolean()
                                              .describe(
                                                'Whether the box is checked.'
                                              ),
                                            kind: zod.enum(['checkbox']),
                                          })
                                          .describe(
                                            'A checkbox column. An unchecked box and an empty cell are the same.'
                                          ),
                                        zod
                                          .object({
                                            kind: zod.enum(['options']),
                                            operator: zod
                                              .enum([
                                                'isAnyOf',
                                                'isNoneOf',
                                                'hasAny',
                                                'hasAll',
                                                'hasNone',
                                              ])
                                              .describe(
                                                "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                              ),
                                            options: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Options of the column; at least one.'
                                              ),
                                          })
                                          .describe('A select or tag column.'),
                                        zod
                                          .object({
                                            entities: zod
                                              .array(zod.string())
                                              .describe(
                                                "Entity ids, or for a relation the related rows' ids; at least\none."
                                              ),
                                            kind: zod.enum(['entities']),
                                            operator: zod
                                              .enum([
                                                'isAnyOf',
                                                'isNoneOf',
                                                'hasAny',
                                                'hasAll',
                                                'hasNone',
                                              ])
                                              .describe(
                                                "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                              ),
                                          })
                                          .describe(
                                            'A reference or relation column.'
                                          ),
                                      ])
                                      .describe(
                                        "What a column's cell must be, by the kind of value the column holds."
                                      ),
                                  })
                                  .describe("A test of one column's cells.")
                                  .and(
                                    zod.object({
                                      kind: zod.enum(['condition']),
                                    })
                                  )
                                  .describe('A test of one column.'),
                                zod
                                  .unknown()
                                  .and(
                                    zod.object({
                                      kind: zod.enum(['group']),
                                    })
                                  )
                                  .describe('A nested group.'),
                              ])
                              .describe(
                                'One entry of a group: a condition, or a group of its own.'
                              )
                          )
                          .describe(
                            'The conditions and nested groups. A group without any keeps every\nrow.'
                          ),
                        conjunction: zod
                          .enum(['and', 'or'])
                          .describe("How a group's conditions combine."),
                      })
                      .describe('Conditions joined by one conjunction.'),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe(
                    "A check of earlier answers. The rules are sent to every caller so a\nrespondent's page can stop them before submitting; respondents are\nnever shown them."
                  ),
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['booking']),
                    target: zod
                      .object({
                        eventTypeId: zod
                          .uuid()
                          .describe('The event type to book.'),
                        profileId: zod
                          .uuid()
                          .describe(
                            'The scheduling profile that owns the event.'
                          ),
                      })
                      .optional()
                      .describe(
                        'An existing native Macro scheduling event offered after an accepted response.'
                      ),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe(
                    'A booking step. Its destination is withheld from respondents until\ntheir response has passed every screener and been accepted.'
                  ),
              ])
              .describe('One section of a form as it reads.')
          )
          .describe('Its sections, in order.'),
        tableGone: zod
          .boolean()
          .describe(
            'Whether its database is in the trash, so it has no table to show or\nwrite: its sections keep no questions and it takes no responses.'
          ),
      })
      .describe('A form with its layout, as the caller may see it.'),
    publicationError: zod
      .union([
        zod
          .object({
            kind: zod.enum(['invalidDraft']),
          })
          .describe(
            'The draft contains unsupported or malformed layout records. An explicit\nlayout replacement can repair it without replacing its document history.'
          ),
        zod
          .object({
            kind: zod.enum(['layout']),
            problem: zod
              .union([
                zod
                  .object({
                    kind: zod.enum(['bookingMustBeLast']),
                  })
                  .describe(
                    'A booking step must be unique and follow all questions and screeners.'
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['unknownColumn']),
                  })
                  .describe(
                    "A question names a column the form's table does not have."
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['managedColumn']),
                  })
                  .describe(
                    'A question names a column the form writes itself.'
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['repeatedColumn']),
                  })
                  .describe('Two questions name one column.'),
                zod
                  .object({
                    id: zod.uuid().describe('The id.'),
                    kind: zod.enum(['repeatedId']),
                  })
                  .describe('Two sections or questions share an id.'),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['gateNamesLaterColumn']),
                  })
                  .describe('A gate tests a column no earlier section asks.'),
                zod
                  .object({
                    kind: zod.enum(['gateRule']),
                    reason: zod.string().describe('Why.'),
                  })
                  .describe("A gate's rule does not fit its column."),
                zod
                  .object({
                    kind: zod.enum(['textTooLong']),
                    max: zod
                      .number()
                      .min(collaborateFormResponsePublicationErrorProblemMaxMin)
                      .describe('The longest allowed.'),
                  })
                  .describe('A text is longer than allowed.'),
              ])
              .describe("Why a layout does not fit the form's table."),
          })
          .describe('The layout violates a form invariant.'),
        zod
          .object({
            kind: zod.enum(['widgetMismatch']),
            question: zod.uuid().describe('The question to repair.'),
          })
          .describe("A question's widget no longer fits its column."),
        zod
          .object({
            kind: zod.enum(['fileUploadNeedsSignIn']),
          })
          .describe('File questions require a signed-in audience.'),
        zod
          .object({
            kind: zod.enum(['pending']),
          })
          .describe(
            'The draft was saved, but publication could not finish. Reading the\ncollaboration endpoint retries publication; do not repeat the write.'
          ),
      ])
      .optional()
      .describe(
        "An editor's saved draft is not yet the version respondents can use."
      ),
  })
  .describe(
    'A ready collaborative form and the result of publishing its latest draft.'
  );

/**
 * @summary Save a validated replacement in the shared layout and report publication.
A 200 response with `publicationError` means the draft is saved, while
respondents still see `detail`. Retrying collaboration retries publication.
 */
export const putFormLayoutParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const putFormLayoutBody = zod
  .object({
    sections: zod
      .array(
        zod
          .union([
            zod
              .object({
                description: zod
                  .string()
                  .describe('What respondents read under the title.'),
                id: zod
                  .uuid()
                  .describe('The section, under an id the client mints.'),
                kind: zod.enum(['questions']),
                questions: zod
                  .array(
                    zod
                      .object({
                        column: zod
                          .uuid()
                          .describe(
                            "The column it writes; its title, type and options are the column's."
                          ),
                        helpText: zod
                          .string()
                          .describe('What respondents read under the title.'),
                        id: zod
                          .uuid()
                          .describe(
                            'The question, under an id the client mints; answers name it.'
                          ),
                        required: zod
                          .boolean()
                          .describe('Whether a response must answer it.'),
                        widget: zod.union([
                          zod.null(),
                          zod
                            .enum([
                              'short',
                              'paragraph',
                              'datetime',
                              'date',
                              'url',
                              'file',
                              'choice',
                              'dropdown',
                              'checkboxes',
                            ])
                            .describe(
                              'How a question is asked. Each column kind takes a few, the first its\ndefault; kinds asked one way only (numbers, checkboxes, entity and row\npickers) take none.'
                            ),
                        ]),
                      })
                      .describe('How one column of the table is asked.')
                  )
                  .describe('Its questions, in order.'),
                title: zod.string().describe('Its title; may be empty.'),
              })
              .describe('Questions asked together.'),
            zod
              .object({
                description: zod
                  .string()
                  .describe('Its description, for editors.'),
                id: zod
                  .uuid()
                  .describe('The section, under an id the client mints.'),
                kind: zod.enum(['gate']),
                message: zod
                  .string()
                  .describe('What a stopped respondent reads.'),
                rules: zod
                  .object({
                    conditions: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                column: zod
                                  .uuid()
                                  .describe('The column tested.'),
                                test: zod
                                  .union([
                                    zod
                                      .object({
                                        kind: zod.enum(['presence']),
                                        operator: zod
                                          .enum(['isEmpty', 'isNotEmpty'])
                                          .describe('Whether a cell is empty.'),
                                      })
                                      .describe(
                                        'Whether the cell is empty; fits a column of any type.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['text']),
                                        operator: zod
                                          .enum([
                                            'is',
                                            'isNot',
                                            'contains',
                                            'doesNotContain',
                                            'startsWith',
                                            'endsWith',
                                          ])
                                          .describe(
                                            'How a text cell compares to a text.'
                                          ),
                                        value: zod
                                          .string()
                                          .describe(
                                            'The text compared against, ignoring case for the containment\ntests.'
                                          ),
                                      })
                                      .describe('A text or link column.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['number']),
                                        operator: zod
                                          .enum([
                                            'is',
                                            'isNot',
                                            'greaterThan',
                                            'greaterThanOrEqual',
                                            'lessThan',
                                            'lessThanOrEqual',
                                          ])
                                          .describe(
                                            'How a number cell compares to a number.'
                                          ),
                                        value: zod
                                          .number()
                                          .describe(
                                            'The number compared against; finite.'
                                          ),
                                      })
                                      .describe('A number column.'),
                                    zod
                                      .object({
                                        kind: zod.enum(['date']),
                                        operator: zod
                                          .enum([
                                            'before',
                                            'after',
                                            'onOrBefore',
                                            'onOrAfter',
                                          ])
                                          .describe(
                                            'How a date cell compares to a date-time.'
                                          ),
                                        value: zod.iso
                                          .datetime({})
                                          .describe(
                                            'The date-time compared against.'
                                          ),
                                      })
                                      .describe('A date column.'),
                                    zod
                                      .object({
                                        checked: zod
                                          .boolean()
                                          .describe(
                                            'Whether the box is checked.'
                                          ),
                                        kind: zod.enum(['checkbox']),
                                      })
                                      .describe(
                                        'A checkbox column. An unchecked box and an empty cell are the same.'
                                      ),
                                    zod
                                      .object({
                                        kind: zod.enum(['options']),
                                        operator: zod
                                          .enum([
                                            'isAnyOf',
                                            'isNoneOf',
                                            'hasAny',
                                            'hasAll',
                                            'hasNone',
                                          ])
                                          .describe(
                                            "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                          ),
                                        options: zod
                                          .array(zod.uuid())
                                          .describe(
                                            'Options of the column; at least one.'
                                          ),
                                      })
                                      .describe('A select or tag column.'),
                                    zod
                                      .object({
                                        entities: zod
                                          .array(zod.string())
                                          .describe(
                                            "Entity ids, or for a relation the related rows' ids; at least\none."
                                          ),
                                        kind: zod.enum(['entities']),
                                        operator: zod
                                          .enum([
                                            'isAnyOf',
                                            'isNoneOf',
                                            'hasAny',
                                            'hasAll',
                                            'hasNone',
                                          ])
                                          .describe(
                                            "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                          ),
                                      })
                                      .describe(
                                        'A reference or relation column.'
                                      ),
                                  ])
                                  .describe(
                                    "What a column's cell must be, by the kind of value the column holds."
                                  ),
                              })
                              .describe("A test of one column's cells.")
                              .and(
                                zod.object({
                                  kind: zod.enum(['condition']),
                                })
                              )
                              .describe('A test of one column.'),
                            zod
                              .unknown()
                              .and(
                                zod.object({
                                  kind: zod.enum(['group']),
                                })
                              )
                              .describe('A nested group.'),
                          ])
                          .describe(
                            'One entry of a group: a condition, or a group of its own.'
                          )
                      )
                      .describe(
                        'The conditions and nested groups. A group without any keeps every\nrow.'
                      ),
                    conjunction: zod
                      .enum(['and', 'or'])
                      .describe("How a group's conditions combine."),
                  })
                  .describe('Conditions joined by one conjunction.'),
                title: zod.string().describe('Its title, for editors.'),
              })
              .describe(
                'A check of earlier answers: a respondent whose answers fail the rules\nis stopped here with the message, and nothing is written.'
              ),
            zod
              .object({
                description: zod
                  .string()
                  .describe('What respondents read before choosing a time.'),
                id: zod
                  .uuid()
                  .describe('The section, under an id the client mints.'),
                kind: zod.enum(['booking']),
                target: zod
                  .object({
                    eventTypeId: zod.uuid().describe('The event type to book.'),
                    profileId: zod
                      .uuid()
                      .describe('The scheduling profile that owns the event.'),
                  })
                  .describe(
                    'An existing native Macro scheduling event offered after an accepted response.'
                  ),
                title: zod.string().describe('Its title.'),
              })
              .describe(
                'The final step, revealed only after the server accepts the response.'
              ),
          ])
          .describe(
            'One section of a layout: questions on one screen, or a gate the answers\nso far must pass.'
          )
      )
      .describe('The sections, first first.'),
  })
  .describe('Every section of a form, in order.');

export const putFormLayoutResponsePublicationErrorProblemMaxMin = 0;

export const putFormLayoutResponse = zod
  .object({
    detail: zod
      .object({
        access: zod
          .enum(['view', 'edit', 'owner'])
          .describe(
            "The caller's level on a form: view responds, edit changes questions and\nreads responses, owner also sets the audience, closes and trashes it."
          ),
        form: zod
          .object({
            audience: zod
              .enum(['members', 'public'])
              .describe('Who may respond to a form.'),
            closesAt: zod.iso
              .datetime({})
              .nullable()
              .describe('When it stops taking responses, if it does.'),
            confirmationMessage: zod
              .string()
              .describe(
                'What a respondent reads once their response is saved; empty for the\ndefault.'
              ),
            createdAt: zod.iso.datetime({}).describe('When it was created.'),
            databaseId: zod
              .uuid()
              .describe('The database holding its responses.'),
            description: zod
              .string()
              .describe('What respondents read under the name.'),
            id: zod.uuid().describe('The form.'),
            name: zod
              .string()
              .describe(
                'Its display name. A standalone form follows the database it created;\na form attached to an existing table has its own name.'
              ),
            ownerId: zod.string().describe('Its owner.'),
            respondentColumnId: zod
              .uuid()
              .nullable()
              .describe(
                'The person column each signed-in submission names its respondent in;\n`null` once deleted.'
              ),
            status: zod
              .enum(['open', 'closed'])
              .describe(
                'Whether a form takes responses, as its owner set it. A form also stops\ntaking them once its closing time passes.'
              ),
            submittedColumnId: zod
              .uuid()
              .nullable()
              .describe(
                'The date column each submission stamps; `null` once deleted.'
              ),
            tableId: zod
              .uuid()
              .describe('The table whose rows are its responses.'),
            tallyVisible: zod
              .boolean()
              .describe('Whether respondents may read option tallies.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When its facts or layout last changed.'),
          })
          .describe(
            'A form: a view of one database table whose rows are its responses.'
          ),
        sections: zod
          .array(
            zod
              .union([
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['questions']),
                    questions: zod
                      .array(
                        zod
                          .object({
                            column: zod
                              .uuid()
                              .describe('The column it writes.'),
                            helpText: zod
                              .string()
                              .describe(
                                'What respondents read under the title.'
                              ),
                            id: zod.uuid().describe('The question.'),
                            kind: zod
                              .union([
                                zod
                                  .object({
                                    type: zod.enum(['text']),
                                  })
                                  .describe('Free text.'),
                                zod
                                  .object({
                                    type: zod.enum(['number']),
                                  })
                                  .describe('A number.'),
                                zod
                                  .object({
                                    type: zod.enum(['boolean']),
                                  })
                                  .describe('A checkbox.'),
                                zod
                                  .object({
                                    type: zod.enum(['date']),
                                  })
                                  .describe('A date-time.'),
                                zod
                                  .object({
                                    type: zod.enum(['link']),
                                  })
                                  .describe('A URL.'),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several options.'
                                      ),
                                    type: zod.enum(['select']),
                                  })
                                  .describe('Text options.'),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several options.'
                                      ),
                                    type: zod.enum(['select_number']),
                                  })
                                  .describe('Numeric options.'),
                                zod
                                  .object({
                                    type: zod.enum(['tag']),
                                  })
                                  .describe(
                                    'Colored labels; always several per cell.'
                                  ),
                                zod
                                  .object({
                                    multi: zod
                                      .boolean()
                                      .describe(
                                        'Whether a cell holds several references.'
                                      ),
                                    target: zod
                                      .enum([
                                        'USER',
                                        'DOCUMENT',
                                        'TASK',
                                        'COMPANY',
                                        'CONTACT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'PROJECT',
                                        'THREAD',
                                        'CALENDAR_EVENT',
                                        'INITIATIVE',
                                      ])
                                      .describe(
                                        'A kind of Macro entity a reference column can point at.'
                                      ),
                                    type: zod.enum(['entity']),
                                  })
                                  .describe('References to Macro entities.'),
                                zod
                                  .object({
                                    database: zod
                                      .uuid()
                                      .describe(
                                        'The database of the related table.'
                                      ),
                                    table: zod
                                      .uuid()
                                      .describe('The related table.'),
                                    type: zod.enum(['relation']),
                                  })
                                  .describe('Rows of another table.'),
                              ])
                              .describe('A type a column can have.'),
                            options: zod
                              .array(
                                zod
                                  .object({
                                    color: zod
                                      .string()
                                      .nullable()
                                      .describe(
                                        'Its colour, a hex string, if it has one.'
                                      ),
                                    id: zod.uuid().describe('The option.'),
                                    label: zod.string().describe('Its label.'),
                                  })
                                  .describe(
                                    "One option of a question's column."
                                  )
                              )
                              .describe(
                                "The column's options, in order, for a select or tag column."
                              ),
                            required: zod
                              .boolean()
                              .describe('Whether a response must answer it.'),
                            title: zod.string().describe("The column's name."),
                            widget: zod.union([
                              zod.null(),
                              zod
                                .enum([
                                  'short',
                                  'paragraph',
                                  'datetime',
                                  'date',
                                  'url',
                                  'file',
                                  'choice',
                                  'dropdown',
                                  'checkboxes',
                                ])
                                .describe(
                                  'How a question is asked. Each column kind takes a few, the first its\ndefault; kinds asked one way only (numbers, checkboxes, entity and row\npickers) take none.'
                                ),
                            ]),
                          })
                          .describe("A question with its column's facts.")
                      )
                      .describe('Its questions, in order.'),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe('Questions asked together.'),
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['gate']),
                    message: zod
                      .string()
                      .describe('What a stopped respondent reads.'),
                    rules: zod
                      .object({
                        conditions: zod
                          .array(
                            zod
                              .union([
                                zod
                                  .object({
                                    column: zod
                                      .uuid()
                                      .describe('The column tested.'),
                                    test: zod
                                      .union([
                                        zod
                                          .object({
                                            kind: zod.enum(['presence']),
                                            operator: zod
                                              .enum(['isEmpty', 'isNotEmpty'])
                                              .describe(
                                                'Whether a cell is empty.'
                                              ),
                                          })
                                          .describe(
                                            'Whether the cell is empty; fits a column of any type.'
                                          ),
                                        zod
                                          .object({
                                            kind: zod.enum(['text']),
                                            operator: zod
                                              .enum([
                                                'is',
                                                'isNot',
                                                'contains',
                                                'doesNotContain',
                                                'startsWith',
                                                'endsWith',
                                              ])
                                              .describe(
                                                'How a text cell compares to a text.'
                                              ),
                                            value: zod
                                              .string()
                                              .describe(
                                                'The text compared against, ignoring case for the containment\ntests.'
                                              ),
                                          })
                                          .describe('A text or link column.'),
                                        zod
                                          .object({
                                            kind: zod.enum(['number']),
                                            operator: zod
                                              .enum([
                                                'is',
                                                'isNot',
                                                'greaterThan',
                                                'greaterThanOrEqual',
                                                'lessThan',
                                                'lessThanOrEqual',
                                              ])
                                              .describe(
                                                'How a number cell compares to a number.'
                                              ),
                                            value: zod
                                              .number()
                                              .describe(
                                                'The number compared against; finite.'
                                              ),
                                          })
                                          .describe('A number column.'),
                                        zod
                                          .object({
                                            kind: zod.enum(['date']),
                                            operator: zod
                                              .enum([
                                                'before',
                                                'after',
                                                'onOrBefore',
                                                'onOrAfter',
                                              ])
                                              .describe(
                                                'How a date cell compares to a date-time.'
                                              ),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'The date-time compared against.'
                                              ),
                                          })
                                          .describe('A date column.'),
                                        zod
                                          .object({
                                            checked: zod
                                              .boolean()
                                              .describe(
                                                'Whether the box is checked.'
                                              ),
                                            kind: zod.enum(['checkbox']),
                                          })
                                          .describe(
                                            'A checkbox column. An unchecked box and an empty cell are the same.'
                                          ),
                                        zod
                                          .object({
                                            kind: zod.enum(['options']),
                                            operator: zod
                                              .enum([
                                                'isAnyOf',
                                                'isNoneOf',
                                                'hasAny',
                                                'hasAll',
                                                'hasNone',
                                              ])
                                              .describe(
                                                "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                              ),
                                            options: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Options of the column; at least one.'
                                              ),
                                          })
                                          .describe('A select or tag column.'),
                                        zod
                                          .object({
                                            entities: zod
                                              .array(zod.string())
                                              .describe(
                                                "Entity ids, or for a relation the related rows' ids; at least\none."
                                              ),
                                            kind: zod.enum(['entities']),
                                            operator: zod
                                              .enum([
                                                'isAnyOf',
                                                'isNoneOf',
                                                'hasAny',
                                                'hasAll',
                                                'hasNone',
                                              ])
                                              .describe(
                                                "How a cell's options or references relate to a set of them. The first\ntwo fit a column holding one value, the last three one holding several."
                                              ),
                                          })
                                          .describe(
                                            'A reference or relation column.'
                                          ),
                                      ])
                                      .describe(
                                        "What a column's cell must be, by the kind of value the column holds."
                                      ),
                                  })
                                  .describe("A test of one column's cells.")
                                  .and(
                                    zod.object({
                                      kind: zod.enum(['condition']),
                                    })
                                  )
                                  .describe('A test of one column.'),
                                zod
                                  .unknown()
                                  .and(
                                    zod.object({
                                      kind: zod.enum(['group']),
                                    })
                                  )
                                  .describe('A nested group.'),
                              ])
                              .describe(
                                'One entry of a group: a condition, or a group of its own.'
                              )
                          )
                          .describe(
                            'The conditions and nested groups. A group without any keeps every\nrow.'
                          ),
                        conjunction: zod
                          .enum(['and', 'or'])
                          .describe("How a group's conditions combine."),
                      })
                      .describe('Conditions joined by one conjunction.'),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe(
                    "A check of earlier answers. The rules are sent to every caller so a\nrespondent's page can stop them before submitting; respondents are\nnever shown them."
                  ),
                zod
                  .object({
                    description: zod.string().describe('Its description.'),
                    id: zod.uuid().describe('The section.'),
                    kind: zod.enum(['booking']),
                    target: zod
                      .object({
                        eventTypeId: zod
                          .uuid()
                          .describe('The event type to book.'),
                        profileId: zod
                          .uuid()
                          .describe(
                            'The scheduling profile that owns the event.'
                          ),
                      })
                      .optional()
                      .describe(
                        'An existing native Macro scheduling event offered after an accepted response.'
                      ),
                    title: zod.string().describe('Its title.'),
                  })
                  .describe(
                    'A booking step. Its destination is withheld from respondents until\ntheir response has passed every screener and been accepted.'
                  ),
              ])
              .describe('One section of a form as it reads.')
          )
          .describe('Its sections, in order.'),
        tableGone: zod
          .boolean()
          .describe(
            'Whether its database is in the trash, so it has no table to show or\nwrite: its sections keep no questions and it takes no responses.'
          ),
      })
      .describe('A form with its layout, as the caller may see it.'),
    publicationError: zod
      .union([
        zod
          .object({
            kind: zod.enum(['invalidDraft']),
          })
          .describe(
            'The draft contains unsupported or malformed layout records. An explicit\nlayout replacement can repair it without replacing its document history.'
          ),
        zod
          .object({
            kind: zod.enum(['layout']),
            problem: zod
              .union([
                zod
                  .object({
                    kind: zod.enum(['bookingMustBeLast']),
                  })
                  .describe(
                    'A booking step must be unique and follow all questions and screeners.'
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['unknownColumn']),
                  })
                  .describe(
                    "A question names a column the form's table does not have."
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['managedColumn']),
                  })
                  .describe(
                    'A question names a column the form writes itself.'
                  ),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['repeatedColumn']),
                  })
                  .describe('Two questions name one column.'),
                zod
                  .object({
                    id: zod.uuid().describe('The id.'),
                    kind: zod.enum(['repeatedId']),
                  })
                  .describe('Two sections or questions share an id.'),
                zod
                  .object({
                    column: zod.uuid().describe('The column.'),
                    kind: zod.enum(['gateNamesLaterColumn']),
                  })
                  .describe('A gate tests a column no earlier section asks.'),
                zod
                  .object({
                    kind: zod.enum(['gateRule']),
                    reason: zod.string().describe('Why.'),
                  })
                  .describe("A gate's rule does not fit its column."),
                zod
                  .object({
                    kind: zod.enum(['textTooLong']),
                    max: zod
                      .number()
                      .min(putFormLayoutResponsePublicationErrorProblemMaxMin)
                      .describe('The longest allowed.'),
                  })
                  .describe('A text is longer than allowed.'),
              ])
              .describe("Why a layout does not fit the form's table."),
          })
          .describe('The layout violates a form invariant.'),
        zod
          .object({
            kind: zod.enum(['widgetMismatch']),
            question: zod.uuid().describe('The question to repair.'),
          })
          .describe("A question's widget no longer fits its column."),
        zod
          .object({
            kind: zod.enum(['fileUploadNeedsSignIn']),
          })
          .describe('File questions require a signed-in audience.'),
        zod
          .object({
            kind: zod.enum(['pending']),
          })
          .describe(
            'The draft was saved, but publication could not finish. Reading the\ncollaboration endpoint retries publication; do not repeat the write.'
          ),
      ])
      .optional()
      .describe(
        "An editor's saved draft is not yet the version respondents can use."
      ),
  })
  .describe(
    'A ready collaborative form and the result of publishing its latest draft.'
  );

/**
 * @summary A form's recipients, for its owner. Link and team sharing are always
`null`: a form's public audience is its own setting.
 */
export const getFormPermissionsParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const getFormPermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Change a form's channel recipients, for its owner. Turning on link or
team sharing is refused.
 */
export const updateFormPermissionsParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const updateFormPermissionsBody = zod.object({
  channelSharePermissions: zod
    .array(
      zod.object({
        accessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
        channelId: zod.string().describe('The channel id'),
        operation: zod.enum(['add', 'remove', 'replace']),
      })
    )
    .nullish()
    .describe('Any channel share permissions to be created\/updated\/removed'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

export const updateFormPermissionsResponse = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Respond to a form. A gate that stops the response answers `stopped` and
writes nothing to the table. A public form takes anonymous responses.
 */
export const submitFormResponseParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const submitFormResponseBody = zod
  .object({
    answers: zod
      .array(
        zod
          .object({
            question: zod.uuid().describe('The question.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['text']),
                    value: zod.string().describe('Free text.'),
                  })
                  .describe('Free text.'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number().describe('A finite number.'),
                  })
                  .describe('A finite number.'),
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean().describe('A checkbox.'),
                  })
                  .describe('A checkbox.'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}).describe('A date-time.'),
                  })
                  .describe('A date-time.'),
                zod
                  .object({
                    type: zod.enum(['link']),
                    value: zod
                      .array(zod.string())
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'Complete http or https URLs; at most one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['options']),
                    value: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe('An option the column has.'),
                              })
                              .describe('An option the column has.'),
                            zod
                              .object({
                                label: zod
                                  .string()
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              })
                              .describe(
                                "An option's label, matched without regard to case. An unknown label\nis refused."
                              ),
                          ])
                          .describe(
                            'A select option, by its id or by its label.'
                          )
                      )
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                  })
                  .describe(
                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                  ),
                zod
                  .object({
                    type: zod.enum(['entities']),
                    value: zod
                      .array(
                        zod
                          .object({
                            entityId: zod.string().describe("The entity's id."),
                            entityType: zod
                              .enum([
                                'USER',
                                'DOCUMENT',
                                'TASK',
                                'COMPANY',
                                'CONTACT',
                                'CALL_RECORD',
                                'CHANNEL',
                                'CHAT',
                                'PROJECT',
                                'THREAD',
                                'CALENDAR_EVENT',
                                'INITIATIVE',
                              ])
                              .describe(
                                'A kind of Macro entity a reference column can point at.'
                              ),
                          })
                          .describe('A reference to one Macro entity.')
                      )
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['rows']),
                    value: zod
                      .array(zod.uuid())
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                  })
                  .describe('Rows of the table a relation column points at.'),
                zod
                  .object({
                    type: zod.enum(['clear']),
                  })
                  .describe('No value: the cell is emptied.'),
              ])
              .describe(
                "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
              ),
          })
          .describe(
            "One question's answer: a value for its column, typed as the column's\ncells are."
          )
      )
      .describe(
        'One answer per answered question; a question left out is unanswered.'
      ),
  })
  .describe('A whole set of answers, sent at once.');

export const submitFormResponseResponse = zod
  .union([
    zod
      .object({
        booking: zod
          .object({
            description: zod
              .string()
              .describe('What respondents read before choosing a time.'),
            section: zod.uuid().describe('The booking section.'),
            target: zod
              .object({
                eventTypeId: zod.uuid().describe('The event type to book.'),
                profileId: zod
                  .uuid()
                  .describe('The scheduling profile that owns the event.'),
              })
              .describe(
                'An existing native Macro scheduling event offered after an accepted response.'
              ),
            title: zod.string().describe('Its title.'),
          })
          .optional()
          .describe(
            'A booking step a passing response has unlocked, with its destination.'
          ),
        outcome: zod.enum(['submitted']),
        response: zod.uuid().describe('The ledger entry.'),
        row: zod.uuid().describe('The row holding the answers.'),
      })
      .describe("The answers are saved as a row of the form's table."),
    zod
      .object({
        message: zod.string().describe("The gate's message."),
        outcome: zod.enum(['stopped']),
        section: zod.uuid().describe('The gate.'),
      })
      .describe(
        'A gate stopped the response; nothing was written to the table.'
      ),
  ])
  .describe('What a submission came to.');

/**
 * @summary The signed-in caller's own response, with the row's current cells.
 */
export const getMyFormResponseParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const getMyFormResponseResponse = zod
  .object({
    answers: zod
      .array(
        zod
          .object({
            question: zod.uuid().describe('The question.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['text']),
                    value: zod.string().describe('Free text.'),
                  })
                  .describe('Free text.'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number().describe('A finite number.'),
                  })
                  .describe('A finite number.'),
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean().describe('A checkbox.'),
                  })
                  .describe('A checkbox.'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}).describe('A date-time.'),
                  })
                  .describe('A date-time.'),
                zod
                  .object({
                    type: zod.enum(['link']),
                    value: zod
                      .array(zod.string())
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'Complete http or https URLs; at most one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['options']),
                    value: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe('An option the column has.'),
                              })
                              .describe('An option the column has.'),
                            zod
                              .object({
                                label: zod
                                  .string()
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              })
                              .describe(
                                "An option's label, matched without regard to case. An unknown label\nis refused."
                              ),
                          ])
                          .describe(
                            'A select option, by its id or by its label.'
                          )
                      )
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                  })
                  .describe(
                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                  ),
                zod
                  .object({
                    type: zod.enum(['entities']),
                    value: zod
                      .array(
                        zod
                          .object({
                            entityId: zod.string().describe("The entity's id."),
                            entityType: zod
                              .enum([
                                'USER',
                                'DOCUMENT',
                                'TASK',
                                'COMPANY',
                                'CONTACT',
                                'CALL_RECORD',
                                'CHANNEL',
                                'CHAT',
                                'PROJECT',
                                'THREAD',
                                'CALENDAR_EVENT',
                                'INITIATIVE',
                              ])
                              .describe(
                                'A kind of Macro entity a reference column can point at.'
                              ),
                          })
                          .describe('A reference to one Macro entity.')
                      )
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['rows']),
                    value: zod
                      .array(zod.uuid())
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                  })
                  .describe('Rows of the table a relation column points at.'),
                zod
                  .object({
                    type: zod.enum(['clear']),
                  })
                  .describe('No value: the cell is emptied.'),
              ])
              .describe(
                "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
              ),
          })
          .describe(
            "One question's answer: a value for its column, typed as the column's\ncells are."
          )
      )
      .describe(
        "The row's cells for the form's current questions, the empty ones\nleft out; none when the row is gone."
      ),
    booking: zod
      .object({
        description: zod
          .string()
          .describe('What respondents read before choosing a time.'),
        section: zod.uuid().describe('The booking section.'),
        target: zod
          .object({
            eventTypeId: zod.uuid().describe('The event type to book.'),
            profileId: zod
              .uuid()
              .describe('The scheduling profile that owns the event.'),
          })
          .describe(
            'An existing native Macro scheduling event offered after an accepted response.'
          ),
        title: zod.string().describe('Its title.'),
      })
      .optional()
      .describe(
        'A booking step a passing response has unlocked, with its destination.'
      ),
    response: zod
      .object({
        formId: zod.uuid().describe('The form.'),
        id: zod.uuid().describe('The entry.'),
        row: zod
          .uuid()
          .nullable()
          .describe(
            'The row holding the answers; `null` when stopped, or once the row was\ndeleted from the table.'
          ),
        status: zod
          .enum(['submitted', 'stopped'])
          .describe(
            'Whether a ledger entry is a saved response or a stop at a gate.'
          ),
        stoppedAtSection: zod
          .uuid()
          .nullable()
          .describe('The gate that stopped it.'),
        submittedAt: zod.iso
          .datetime({})
          .describe('When it was first submitted.'),
        updatedAt: zod.iso.datetime({}).describe('When it last changed.'),
      })
      .describe(
        "One entry of a form's submission ledger: who answered, when, and where\nthe answers went."
      ),
  })
  .describe(
    "A signed-in respondent's own response, with its answers as the row\nholds them now."
  );

/**
 * @summary Replace the signed-in caller's answers while the form is open. A gate
that stops the edit leaves the saved response as it was.
 */
export const editMyFormResponseParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const editMyFormResponseBody = zod
  .object({
    answers: zod
      .array(
        zod
          .object({
            question: zod.uuid().describe('The question.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['text']),
                    value: zod.string().describe('Free text.'),
                  })
                  .describe('Free text.'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number().describe('A finite number.'),
                  })
                  .describe('A finite number.'),
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean().describe('A checkbox.'),
                  })
                  .describe('A checkbox.'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}).describe('A date-time.'),
                  })
                  .describe('A date-time.'),
                zod
                  .object({
                    type: zod.enum(['link']),
                    value: zod
                      .array(zod.string())
                      .describe(
                        'Complete http or https URLs; at most one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'Complete http or https URLs; at most one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['options']),
                    value: zod
                      .array(
                        zod
                          .union([
                            zod
                              .object({
                                id: zod
                                  .uuid()
                                  .describe('An option the column has.'),
                              })
                              .describe('An option the column has.'),
                            zod
                              .object({
                                label: zod
                                  .string()
                                  .describe(
                                    "An option's label, matched without regard to case. An unknown label\nis refused."
                                  ),
                              })
                              .describe(
                                "An option's label, matched without regard to case. An unknown label\nis refused."
                              ),
                          ])
                          .describe(
                            'A select option, by its id or by its label.'
                          )
                      )
                      .describe(
                        'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                      ),
                  })
                  .describe(
                    'Options of a select or tag column; at most one for a single-valued\ncolumn.'
                  ),
                zod
                  .object({
                    type: zod.enum(['entities']),
                    value: zod
                      .array(
                        zod
                          .object({
                            entityId: zod.string().describe("The entity's id."),
                            entityType: zod
                              .enum([
                                'USER',
                                'DOCUMENT',
                                'TASK',
                                'COMPANY',
                                'CONTACT',
                                'CALL_RECORD',
                                'CHANNEL',
                                'CHAT',
                                'PROJECT',
                                'THREAD',
                                'CALENDAR_EVENT',
                                'INITIATIVE',
                              ])
                              .describe(
                                'A kind of Macro entity a reference column can point at.'
                              ),
                          })
                          .describe('A reference to one Macro entity.')
                      )
                      .describe(
                        'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                      ),
                  })
                  .describe(
                    'References to Macro entities of the kind the column points at; at\nmost one for a single-valued column.'
                  ),
                zod
                  .object({
                    type: zod.enum(['rows']),
                    value: zod
                      .array(zod.uuid())
                      .describe(
                        'Rows of the table a relation column points at.'
                      ),
                  })
                  .describe('Rows of the table a relation column points at.'),
                zod
                  .object({
                    type: zod.enum(['clear']),
                  })
                  .describe('No value: the cell is emptied.'),
              ])
              .describe(
                "A cell's value. It must fit the column's type: text for a text column,\noptions of the column for a select, and so on."
              ),
          })
          .describe(
            "One question's answer: a value for its column, typed as the column's\ncells are."
          )
      )
      .describe(
        'One answer per answered question; a question left out is unanswered.'
      ),
  })
  .describe('A whole set of answers, sent at once.');

export const editMyFormResponseResponse = zod
  .union([
    zod
      .object({
        booking: zod
          .object({
            description: zod
              .string()
              .describe('What respondents read before choosing a time.'),
            section: zod.uuid().describe('The booking section.'),
            target: zod
              .object({
                eventTypeId: zod.uuid().describe('The event type to book.'),
                profileId: zod
                  .uuid()
                  .describe('The scheduling profile that owns the event.'),
              })
              .describe(
                'An existing native Macro scheduling event offered after an accepted response.'
              ),
            title: zod.string().describe('Its title.'),
          })
          .optional()
          .describe(
            'A booking step a passing response has unlocked, with its destination.'
          ),
        outcome: zod.enum(['submitted']),
        response: zod.uuid().describe('The ledger entry.'),
        row: zod.uuid().describe('The row holding the answers.'),
      })
      .describe("The answers are saved as a row of the form's table."),
    zod
      .object({
        message: zod.string().describe("The gate's message."),
        outcome: zod.enum(['stopped']),
        section: zod.uuid().describe('The gate.'),
      })
      .describe(
        'A gate stopped the response; nothing was written to the table.'
      ),
  ])
  .describe('What a submission came to.');

/**
 * @summary Response counts for the form's editors.
 */
export const getFormResponseSummaryParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const getFormResponseSummaryResponseRowsMin = 0;

export const getFormResponseSummaryResponseStoppedMin = 0;

export const getFormResponseSummaryResponseStoppedBySectionItemCountMin = 0;

export const getFormResponseSummaryResponseSubmittedMin = 0;

export const getFormResponseSummaryResponse = zod
  .object({
    rows: zod
      .number()
      .min(getFormResponseSummaryResponseRowsMin)
      .describe("Rows of the form's table, whoever wrote them."),
    stopped: zod
      .number()
      .min(getFormResponseSummaryResponseStoppedMin)
      .describe('Respondents stopped at a gate.'),
    stoppedBySection: zod
      .array(
        zod
          .object({
            count: zod
              .number()
              .min(getFormResponseSummaryResponseStoppedBySectionItemCountMin)
              .describe('How many it stopped.'),
            section: zod.uuid().describe('The gate.'),
          })
          .describe('How many responses one gate stopped.')
      )
      .describe('The stops, by gate.'),
    submitted: zod
      .number()
      .min(getFormResponseSummaryResponseSubmittedMin)
      .describe('Responses saved.'),
  })
  .describe("A form's response counts, for its editors.");

/**
 * @summary Option counts of the form's choice questions: for respondents when the
owner shows them, for editors always.
 */
export const getFormTallyParams = zod.object({
  id: zod.uuid().describe('Form id'),
});

export const getFormTallyResponseQuestionsItemBucketsItemCountMin = 0;

export const getFormTallyResponseQuestionsItemResponsesMin = 0;

export const getFormTallyResponse = zod
  .object({
    questions: zod
      .array(
        zod
          .object({
            buckets: zod
              .array(
                zod
                  .object({
                    count: zod
                      .number()
                      .min(getFormTallyResponseQuestionsItemBucketsItemCountMin)
                      .describe('How many rows hold it.'),
                    value: zod
                      .union([
                        zod
                          .object({
                            kind: zod.enum(['option']),
                            option: zod.uuid().describe('The option.'),
                          })
                          .describe('One option of the column.'),
                        zod
                          .object({
                            checked: zod.boolean().describe('Checked, or not.'),
                            kind: zod.enum(['checkbox']),
                          })
                          .describe('A checkbox state.'),
                      ])
                      .describe('A value a tally counts.'),
                  })
                  .describe('How many rows hold one value.')
              )
              .describe(
                "A count per option in the column's order, zeros included; for a\ncheckbox, checked then unchecked."
              ),
            question: zod.uuid().describe('The question.'),
            responses: zod
              .number()
              .min(getFormTallyResponseQuestionsItemResponsesMin)
              .describe('Rows with a value in its column.'),
          })
          .describe("One question's counts.")
      )
      .describe(
        'One tally per select, numeric select, tag or checkbox question, in\nlayout order.'
      ),
  })
  .describe("How the table's rows answer each choice question.");

/**
 * @summary List the repositories and authors among the GitHub pull requests visible to the caller.
 */
export const getGithubPullRequestFacetsResponse = zod
  .object({
    assignees: zod
      .array(
        zod
          .object({
            count: zod
              .number()
              .describe(
                'Number of visible pull requests the user opened, or is assigned to.'
              ),
            githubUserId: zod
              .string()
              .describe("The user's numeric GitHub user id."),
            login: zod
              .string()
              .nullish()
              .describe(
                "The user's most recently synced GitHub login, when known."
              ),
          })
          .describe(
            'A GitHub user among the visible pull requests, as an author or an assignee.'
          )
      )
      .describe('Assignees, most pull requests first.'),
    authors: zod
      .array(
        zod
          .object({
            count: zod
              .number()
              .describe(
                'Number of visible pull requests the user opened, or is assigned to.'
              ),
            githubUserId: zod
              .string()
              .describe("The user's numeric GitHub user id."),
            login: zod
              .string()
              .nullish()
              .describe(
                "The user's most recently synced GitHub login, when known."
              ),
          })
          .describe(
            'A GitHub user among the visible pull requests, as an author or an assignee.'
          )
      )
      .describe('Authors, most pull requests first.'),
    labels: zod
      .array(
        zod
          .object({
            color: zod
              .string()
              .nullish()
              .describe(
                "The label's most recently synced color, as six hex digits without `#`."
              ),
            count: zod
              .number()
              .describe('Number of visible pull requests with the label.'),
            name: zod.string().describe("The label's name."),
          })
          .describe(
            'A label among the visible GitHub pull requests. Labels with the same name in different\nrepositories count together.'
          )
      )
      .describe('Labels, most pull requests first.'),
    repositories: zod
      .array(
        zod
          .object({
            count: zod
              .number()
              .describe('Number of visible pull requests in the repository.'),
            repository: zod
              .string()
              .describe(
                "The repository's most recently synced name, as `owner\/repo`."
              ),
            repositoryId: zod
              .string()
              .describe(
                'The numeric GitHub repository id, which survives renames and transfers.'
              ),
          })
          .describe('A repository among the visible GitHub pull requests.')
      )
      .describe('Repositories, most pull requests first.'),
  })
  .describe(
    'Repositories, authors, assignees, and labels among the GitHub pull requests a caller can see,\neach with the number of pull requests it covers.'
  );

/**
 * @summary Get the pull request behind a foreign entity record the caller can view.
 */
export const getGithubPullRequestParams = zod.object({
  id: zod
    .uuid()
    .describe("The caller's foreign entity record for the pull request"),
});

export const getGithubPullRequestResponseAdditionsMin = 0;

export const getGithubPullRequestResponseChecksItemIdMin = 0;

export const getGithubPullRequestResponseCommentsItemAuthorIdMin = 0;

export const getGithubPullRequestResponseCommentsItemIdMin = 0;

export const getGithubPullRequestResponseCommentsItemInReplyToIdMin = 0;

export const getGithubPullRequestResponseCommentsItemLineMin = 0;

export const getGithubPullRequestResponseCommentsItemOriginalLineMin = 0;

export const getGithubPullRequestResponseCommentsItemPullRequestReviewIdMin = 0;

export const getGithubPullRequestResponseDeletionsMin = 0;

export const getGithubPullRequestResponse = zod
  .object({
    additions: zod
      .number()
      .min(getGithubPullRequestResponseAdditionsMin)
      .nullish()
      .describe("Lines added across the pull request's changes."),
    assignees: zod
      .array(
        zod
          .object({
            githubUserId: zod
              .string()
              .describe('The stable numeric GitHub user id, as a string.'),
            login: zod
              .string()
              .nullish()
              .describe("The user's GitHub login, when known."),
          })
          .describe(
            'A GitHub user named on a pull request, such as an assignee.'
          )
      )
      .describe('The users assigned to the pull request.'),
    authorGithubUserId: zod
      .string()
      .nullish()
      .describe('Stable numeric GitHub user id of the author.'),
    authorLogin: zod
      .string()
      .nullish()
      .describe(
        "The author's GitHub login when the pull request was last synced."
      ),
    base: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .nullish()
              .describe('The branch name, when known.'),
            sha: zod.string().nullish().describe('The commit, when known.'),
          })
          .describe('One end of the compared range.'),
      ])
      .optional(),
    checks: zod
      .array(
        zod
          .object({
            completedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the check run completed, when available.'),
            conclusion: zod
              .string()
              .nullish()
              .describe(
                'The raw GitHub check run conclusion, when the run has completed.'
              ),
            id: zod
              .number()
              .min(getGithubPullRequestResponseChecksItemIdMin)
              .describe('The unique GitHub identifier for the check run.'),
            name: zod.string().describe('The check run name.'),
            startedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the check run started, when available.'),
            status: zod.string().describe('The raw GitHub check run status.'),
            url: zod
              .string()
              .nullish()
              .describe(
                'The public GitHub URL for the check run, when available.'
              ),
          })
          .describe('A check run associated with a GitHub pull request.')
      )
      .describe("The latest check runs on the pull request's head commit."),
    comments: zod
      .array(
        zod
          .object({
            authorAssociation: zod
              .string()
              .nullish()
              .describe(
                "GitHub's relationship label for the author, when available."
              ),
            authorId: zod
              .number()
              .min(getGithubPullRequestResponseCommentsItemAuthorIdMin)
              .nullish()
              .describe(
                'The stable numeric GitHub user id for the comment author, when available.'
              ),
            authorLogin: zod
              .string()
              .nullish()
              .describe(
                'The GitHub login for the comment author, when available.'
              ),
            body: zod.string().describe('The comment or review body text.'),
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the comment was created or the review was submitted.'
              ),
            id: zod
              .number()
              .min(getGithubPullRequestResponseCommentsItemIdMin)
              .describe(
                'The unique GitHub identifier for the comment or review.'
              ),
            inReplyToId: zod
              .number()
              .min(getGithubPullRequestResponseCommentsItemInReplyToIdMin)
              .nullish()
              .describe(
                'The id of the comment this one replies to, when it is part of a review\nthread. Only ever present on `review_comment` sources.'
              ),
            line: zod
              .number()
              .min(getGithubPullRequestResponseCommentsItemLineMin)
              .nullish()
              .describe(
                "The line in the current diff the comment is anchored to. Cleared by\nGitHub when later commits outdate the comment's diff."
              ),
            originalLine: zod
              .number()
              .min(getGithubPullRequestResponseCommentsItemOriginalLineMin)
              .nullish()
              .describe(
                'The line the comment was originally anchored to, kept even when the\ndiff has since changed.'
              ),
            path: zod
              .string()
              .nullish()
              .describe(
                'The repository-relative file path the review comment is anchored to.\nOnly ever present on `review_comment` sources.'
              ),
            pullRequestReviewId: zod
              .number()
              .min(
                getGithubPullRequestResponseCommentsItemPullRequestReviewIdMin
              )
              .nullish()
              .describe(
                'The id of the pull request review this comment was submitted with.\nOnly ever present on `review_comment` sources.'
              ),
            source: zod
              .string()
              .describe(
                'The GitHub source for the comment, such as `issue_comment` or `review_comment`.'
              ),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the comment or review was last updated.'),
            url: zod
              .string()
              .nullish()
              .describe(
                'The public GitHub URL for the comment or review, when available.'
              ),
          })
          .describe('A comment associated with a GitHub pull request.')
      )
      .describe(
        "Comments from the pull request's conversation, reviews, and review threads.\nComment `authorId` intentionally retains the shared numeric GitHub comment contract;\nconvert it to a decimal string before comparing it with `authorGithubUserId`."
      ),
    deletions: zod
      .number()
      .min(getGithubPullRequestResponseDeletionsMin)
      .nullish()
      .describe("Lines deleted across the pull request's changes."),
    description: zod
      .string()
      .nullish()
      .describe('The pull request body, as GitHub markdown.'),
    draft: zod.boolean().describe('Whether the pull request is a draft.'),
    githubKey: zod
      .string()
      .describe("The pull request's `owner\/repo\/pull\/number` key."),
    githubUpdatedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('When GitHub last updated the pull request.'),
    head: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .nullish()
              .describe('The branch name, when known.'),
            sha: zod.string().nullish().describe('The commit, when known.'),
          })
          .describe('One end of the compared range.'),
      ])
      .optional(),
    id: zod.uuid().describe("The caller's record for the pull request."),
    labels: zod
      .array(
        zod
          .object({
            color: zod
              .string()
              .nullish()
              .describe(
                'The label color as six hex digits without a leading `#`, when known.'
              ),
            name: zod
              .string()
              .describe(
                'The label name, unique within its repository regardless of case.'
              ),
          })
          .describe('A label on a GitHub pull request.')
      )
      .describe("The pull request's labels."),
    number: zod
      .number()
      .describe('The pull request number within its repository.'),
    owner: zod
      .string()
      .describe('The repository owner the pull request was last synced under.'),
    repo: zod
      .string()
      .describe('The repository name the pull request was last synced under.'),
    requestedReviewerGithubUserIds: zod
      .array(zod.string())
      .describe('Stable numeric GitHub user ids of the users asked to review.'),
    reviewDecision: zod
      .union([
        zod.null(),
        zod
          .enum(['approved', 'changes_requested', 'review_required'])
          .describe(
            "Where a pull request's review stands, from its reviewers' latest reviews."
          ),
      ])
      .optional(),
    reviews: zod
      .array(
        zod
          .object({
            reviewerGithubUserId: zod
              .string()
              .describe(
                'The stable numeric GitHub user id of the reviewer, as a string.'
              ),
            reviewerLogin: zod
              .string()
              .nullish()
              .describe("The reviewer's GitHub login, when known."),
            state: zod
              .enum(['approved', 'changes_requested', 'commented', 'dismissed'])
              .describe(
                "What a reviewer's latest review on a pull request said."
              ),
            submittedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('When the review was submitted, when known.'),
          })
          .describe("A reviewer's latest submitted review on a pull request.")
      )
      .describe("Each reviewer's latest submitted review."),
    status: zod
      .union([
        zod.null(),
        zod
          .enum(['open', 'closed', 'merged'])
          .describe(
            'The normalized lifecycle status for a GitHub pull request.'
          ),
      ])
      .optional()
      .describe('The normalized pull request status.'),
    title: zod.string().nullish().describe('The pull request title.'),
    url: zod.string().describe("The pull request's page on GitHub."),
  })
  .describe(
    "A GitHub pull request as Macro stores it, read through one of the caller's records."
  );

/**
 * @summary Get the changes of the pull request behind a foreign entity record the caller can view.
 */
export const getGithubPullRequestChangesParams = zod.object({
  id: zod
    .uuid()
    .describe("The caller's foreign entity record for the pull request"),
});

export const getGithubPullRequestChangesResponseChangesetAdditionsMin = 0;

export const getGithubPullRequestChangesResponseChangesetDeletionsMin = 0;

export const getGithubPullRequestChangesResponseChangesetFilesItemAdditionsMin = 0;

export const getGithubPullRequestChangesResponseChangesetFilesItemDeletionsMin = 0;

export const getGithubPullRequestChangesResponseChangesetPatchBytesMin = 0;

export const getGithubPullRequestChangesResponse = zod
  .object({
    changeset: zod
      .union([
        zod.null(),
        zod
          .object({
            additions: zod
              .number()
              .min(getGithubPullRequestChangesResponseChangesetAdditionsMin)
              .describe('Lines added across all files.'),
            base: zod
              .object({
                name: zod
                  .string()
                  .nullish()
                  .describe('The branch name, when known.'),
                sha: zod.string().nullish().describe('The commit, when known.'),
              })
              .describe('One end of the compared range.'),
            capturedAt: zod.iso
              .datetime({})
              .describe('When the diff was taken.'),
            deletions: zod
              .number()
              .min(getGithubPullRequestChangesResponseChangesetDeletionsMin)
              .describe('Lines removed across all files.'),
            files: zod
              .array(
                zod
                  .object({
                    additions: zod
                      .number()
                      .min(
                        getGithubPullRequestChangesResponseChangesetFilesItemAdditionsMin
                      )
                      .describe('Lines added.'),
                    binary: zod
                      .boolean()
                      .describe('The diff carries no text for this file.'),
                    deletions: zod
                      .number()
                      .min(
                        getGithubPullRequestChangesResponseChangesetFilesItemDeletionsMin
                      )
                      .describe('Lines removed.'),
                    kind: zod
                      .enum(['added', 'modified', 'deleted', 'renamed'])
                      .describe('What happened to a file, on the wire.'),
                    patchOmitted: zod
                      .boolean()
                      .describe(
                        "The file's hunks were left out of the patch to fit the size budget."
                      ),
                    path: zod
                      .string()
                      .describe(
                        "The file's path after the change, or before it for a deletion."
                      ),
                    previousPath: zod
                      .string()
                      .nullish()
                      .describe('Where a renamed file came from.'),
                  })
                  .describe(
                    'One changed file.\n\nClients deserialize this, so both derives are used.'
                  )
              )
              .describe('Every changed file, in patch order.'),
            head: zod
              .object({
                name: zod
                  .string()
                  .nullish()
                  .describe('The branch name, when known.'),
                sha: zod.string().nullish().describe('The commit, when known.'),
              })
              .describe('One end of the compared range.'),
            id: zod
              .uuid()
              .describe(
                "The changeset's id; a different id means different changes."
              ),
            patchBytes: zod
              .number()
              .min(getGithubPullRequestChangesResponseChangesetPatchBytesMin)
              .describe(
                'Size of the patch the matching patch route serves; zero when nothing\nchanged.'
              ),
            repository: zod
              .string()
              .nullish()
              .describe('`https:\/\/github.com\/owner\/name`, when known.'),
            source: zod
              .enum(['github_pull_request'])
              .describe("The source of a changeset's diff, on the wire."),
            truncated: zod
              .boolean()
              .describe("Some files' hunks were left out of the patch."),
          })
          .describe(
            'One changeset: the files a patch touches and what happened to each.\n\nClients deserialize this, so both derives are used.'
          ),
      ])
      .optional(),
    error: zod
      .string()
      .nullish()
      .describe('Why there are no changes, in a sentence the user can act on.'),
  })
  .describe(
    'Response body for `GET \/github_pull_requests\/{id}\/changes`.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * @summary Get the patch of changes of the pull request behind a foreign entity record the caller can
view.
 */
export const getGithubPullRequestChangesPatchParams = zod.object({
  id: zod
    .uuid()
    .describe("The caller's foreign entity record for the pull request"),
});

export const getGithubPullRequestChangesPatchQueryParams = zod.object({
  changeset: zod.uuid().describe('The changeset whose patch to read'),
});

export const getGithubPullRequestChangesPatchResponse = zod
  .object({
    patch: zod
      .string()
      .describe('The git-style unified diff of the changeset.'),
  })
  .describe(
    'Response body for `GET \/github_pull_requests\/{id}\/changes\/patch`.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * Unauthenticated by design: the daemon has no credential yet - obtaining one
is the point. The pairing releases nothing until a signed-in user approves
it, and creation is throttled in the domain service.
 * @summary Handler for `POST /harness-pairings`.
 */
export const createHarnessPairingBody = zod
  .object({
    allow_permission_bypass: zod
      .boolean()
      .nullish()
      .describe(
        'Daemon operator consent ceiling. Omitted by older clients; web approval decides.'
      ),
    host: zod
      .string()
      .nullish()
      .describe(
        'Display-only description of the machine, e.g. `eric@macbook \/ darwin`.'
      ),
    name: zod
      .string()
      .describe(
        "Requested harness display name (typically the machine's hostname)."
      ),
    scope: zod
      .union([
        zod.null(),
        zod
          .enum(['private', 'team'])
          .describe(
            "The ownership scope a daemon's config asks for.\n\nAdvisory, not binding: the approving user confirms it in the dialog, which\narrives preselected to this. Approval is what actually sets ownership."
          ),
      ])
      .optional(),
  })
  .describe(
    'Request to open a pairing: the daemon asks for a code the user approves.\n\nThe daemon serializes this, so both derives are used.'
  );

/**
 * @summary Handler for `GET /harness-pairings/{code}`.
 */
export const getHarnessPairingParams = zod.object({
  code: zod.string().describe('Pairing code'),
});

export const getHarnessPairingResponse = zod
  .object({
    code: zod.string().describe('The pairing code, normalized to `XXXX-XXXX`.'),
    created_at: zod.iso.datetime({}).describe('When the pairing was created.'),
    expires_at: zod.iso.datetime({}).describe('When the pairing expires.'),
    host: zod
      .string()
      .nullish()
      .describe('Display-only description of the machine.'),
    requested_allow_permission_bypass: zod
      .boolean()
      .nullish()
      .describe(
        'Daemon operator consent ceiling; false forbids bypass at approval.'
      ),
    requested_name: zod
      .string()
      .describe('Harness display name the daemon asked for.'),
    requested_scope: zod
      .union([
        zod.null(),
        zod
          .enum(['private', 'team'])
          .describe(
            "The ownership scope a daemon's config asks for.\n\nAdvisory, not binding: the approving user confirms it in the dialog, which\narrives preselected to this. Approval is what actually sets ownership."
          ),
      ])
      .optional(),
  })
  .describe('A pending pairing, as shown to the approving user.');

/**
 * @summary Handler for `POST /harness-pairings/{code}/approve`.
 */
export const approveHarnessPairingParams = zod.object({
  code: zod.string().describe('Pairing code'),
});

export const approveHarnessPairingBody = zod
  .object({
    allow_permission_bypass: zod
      .boolean()
      .optional()
      .describe(
        'Whether agents may bypass ACP permission requests on this harness.'
      ),
    name: zod
      .string()
      .nullish()
      .describe(
        "Display name override. Defaults to the daemon's requested name."
      ),
    team_id: zod
      .uuid()
      .nullish()
      .describe('Owning team. Omit for a private, user-owned harness.'),
  })
  .describe('Request to approve a pairing and register the harness.');

export const approveHarnessPairingResponse = zod
  .object({
    allow_permission_bypass: zod
      .boolean()
      .optional()
      .describe(
        'Whether agents may bypass ACP permission requests on this harness.'
      ),
    connected: zod
      .boolean()
      .describe('Whether the daemon currently holds a runtime connection.'),
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by: zod.string().describe('User that registered this harness.'),
    id: zod.string(),
    kind: zod.string().describe('Runtime kind. Currently always `macrod`.'),
    last_connected_at: zod.iso
      .datetime({})
      .nullish()
      .describe('When the daemon last attached a runtime connection.'),
    name: zod.string().describe('Display name.'),
    owner: zod
      .union([
        zod
          .object({
            type: zod.enum(['user']),
            user_id: zod.string().describe('Owner user id.'),
          })
          .describe('User-owned (private) harness.'),
        zod
          .object({
            team_id: zod.uuid().describe('Owner team id.'),
            type: zod.enum(['team']),
          })
          .describe('Team-owned harness, usable by every team member.'),
      ])
      .describe(
        'Harness owner.\n\nExactly one of a user or a team, mirroring the bots owner pattern. There\nare no system harnesses.'
      ),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
  })
  .describe(
    'A registered user-run harness.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * Unauthenticated like pairing creation; the device secret minted alongside
the pairing is the credential, and the pairing id (not the short user
code) addresses it.
 * @summary Handler for `POST /harness-pairings/{pairing_id}/claim`.
 */
export const claimHarnessPairingParams = zod.object({
  pairing_id: zod.uuid().describe('Pairing ID'),
});

export const claimHarnessPairingBody = zod
  .object({
    device_secret: zod
      .string()
      .describe('The claim credential returned when the pairing was created.'),
  })
  .describe(
    "Request to claim an approved pairing's credential.\n\nThe daemon serializes this, so both derives are used."
  );

export const claimHarnessPairingResponse = zod
  .object({
    harness: zod
      .object({
        allow_permission_bypass: zod
          .boolean()
          .optional()
          .describe(
            'Whether agents may bypass ACP permission requests on this harness.'
          ),
        connected: zod
          .boolean()
          .describe('Whether the daemon currently holds a runtime connection.'),
        created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
        created_by: zod.string().describe('User that registered this harness.'),
        id: zod.string(),
        kind: zod.string().describe('Runtime kind. Currently always `macrod`.'),
        last_connected_at: zod.iso
          .datetime({})
          .nullish()
          .describe('When the daemon last attached a runtime connection.'),
        name: zod.string().describe('Display name.'),
        owner: zod
          .union([
            zod
              .object({
                type: zod.enum(['user']),
                user_id: zod.string().describe('Owner user id.'),
              })
              .describe('User-owned (private) harness.'),
            zod
              .object({
                team_id: zod.uuid().describe('Owner team id.'),
                type: zod.enum(['team']),
              })
              .describe('Team-owned harness, usable by every team member.'),
          ])
          .describe(
            'Harness owner.\n\nExactly one of a user or a team, mirroring the bots owner pattern. There\nare no system harnesses.'
          ),
        updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
      })
      .describe(
        'A registered user-run harness.\n\nClients deserialize this, so both derives are used.'
      ),
    token: zod
      .string()
      .describe(
        'The raw harness bearer token. Shown only here; only its hash is stored.'
      ),
  })
  .describe('The credential released to the daemon when a pairing is claimed.');

/**
 * @summary Handler for `GET /harnesses`.
 */
export const listHarnessesResponseItem = zod
  .object({
    allow_permission_bypass: zod
      .boolean()
      .optional()
      .describe(
        'Whether agents may bypass ACP permission requests on this harness.'
      ),
    connected: zod
      .boolean()
      .describe('Whether the daemon currently holds a runtime connection.'),
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by: zod.string().describe('User that registered this harness.'),
    id: zod.string(),
    kind: zod.string().describe('Runtime kind. Currently always `macrod`.'),
    last_connected_at: zod.iso
      .datetime({})
      .nullish()
      .describe('When the daemon last attached a runtime connection.'),
    name: zod.string().describe('Display name.'),
    owner: zod
      .union([
        zod
          .object({
            type: zod.enum(['user']),
            user_id: zod.string().describe('Owner user id.'),
          })
          .describe('User-owned (private) harness.'),
        zod
          .object({
            team_id: zod.uuid().describe('Owner team id.'),
            type: zod.enum(['team']),
          })
          .describe('Team-owned harness, usable by every team member.'),
      ])
      .describe(
        'Harness owner.\n\nExactly one of a user or a team, mirroring the bots owner pattern. There\nare no system harnesses.'
      ),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
  })
  .describe(
    'A registered user-run harness.\n\nClients deserialize this, so both derives are used.'
  );
export const listHarnessesResponse = zod.array(listHarnessesResponseItem);

/**
 * @summary Handler for `GET /harnesses/me`.
 */
export const getSelfHarnessResponse = zod
  .object({
    allow_permission_bypass: zod
      .boolean()
      .optional()
      .describe(
        'Whether agents may bypass ACP permission requests on this harness.'
      ),
    connected: zod
      .boolean()
      .describe('Whether the daemon currently holds a runtime connection.'),
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by: zod.string().describe('User that registered this harness.'),
    id: zod.string(),
    kind: zod.string().describe('Runtime kind. Currently always `macrod`.'),
    last_connected_at: zod.iso
      .datetime({})
      .nullish()
      .describe('When the daemon last attached a runtime connection.'),
    name: zod.string().describe('Display name.'),
    owner: zod
      .union([
        zod
          .object({
            type: zod.enum(['user']),
            user_id: zod.string().describe('Owner user id.'),
          })
          .describe('User-owned (private) harness.'),
        zod
          .object({
            team_id: zod.uuid().describe('Owner team id.'),
            type: zod.enum(['team']),
          })
          .describe('Team-owned harness, usable by every team member.'),
      ])
      .describe(
        'Harness owner.\n\nExactly one of a user or a team, mirroring the bots owner pattern. There\nare no system harnesses.'
      ),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
  })
  .describe(
    'A registered user-run harness.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * @summary Handler for `GET /harnesses/me/agents`.
 */
export const listHarnessAgentsResponseItem = zod
  .object({
    bot_id: zod.string(),
    handle: zod.string().describe('Stable `@` handle.'),
    name: zod.string().describe('Display name.'),
  })
  .describe('An agent bound to a harness, as listed for the daemon.');
export const listHarnessAgentsResponse = zod.array(
  listHarnessAgentsResponseItem
);

/**
 * @summary Handler for `GET /harnesses/me/sessions`.
 */
export const listHarnessSessionsResponseItem = zod
  .object({
    bot_handle: zod.string().describe("The agent's `@` handle."),
    bot_id: zod.string(),
    bot_name: zod.string().describe("The agent's display name."),
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    model: zod.string().describe('Model the session was opened with.'),
    modified_at: zod.iso.datetime({}).describe('Last-activity timestamp.'),
    name: zod.string().describe("The session's display name."),
    owner_id: zod.string().describe('The user the session belongs to.'),
    session_id: zod.uuid().describe('The session id.'),
    status: zod
      .string()
      .describe('Session lifecycle status, e.g. `no_messages`, `active`.'),
  })
  .describe(
    "An agent session running on a harness, as listed for the daemon's UI."
  );
export const listHarnessSessionsResponse = zod.array(
  listHarnessSessionsResponseItem
);

/**
 * @summary Handler for `DELETE /harnesses/{harness_id}`.
 */
export const deleteHarnessParams = zod.object({
  harness_id: zod.string().describe('Harness ID'),
});

/**
 * @summary Gets the users history
 */
export const getHistoryHandlerResponse = zod.object({
  data: zod
    .array(
      zod.union([
        zod.object({
          branchedFromId: zod
            .string()
            .nullish()
            .describe('The id of the document this document branched from'),
          branchedFromVersionId: zod
            .number()
            .nullish()
            .describe(
              'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
            ),
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was created'),
          deletedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the document was deleted'),
          documentFamilyId: zod
            .number()
            .nullish()
            .describe('The id of the document family this document belongs to'),
          documentVersionId: zod
            .number()
            .describe(
              'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
            ),
          fileType: zod
            .string()
            .nullish()
            .describe('The file type of the document (e.g. pdf, docx)'),
          id: zod.string().describe('The document id'),
          name: zod.string().describe('The name of the document'),
          owner: zod.string().describe('The owner of the document'),
          projectId: zod
            .string()
            .nullish()
            .describe('The id of the project that this document belongs to'),
          sha: zod
            .string()
            .nullish()
            .describe(
              'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
            ),
          subType: zod
            .union([
              zod.null(),
              zod
                .union([
                  zod
                    .object({
                      is_completed: zod
                        .boolean()
                        .describe(
                          'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                        ),
                      type: zod.enum(['task']),
                    })
                    .describe('A task document with its associated properties'),
                  zod
                    .object({
                      type: zod.enum(['snippet']),
                    })
                    .describe('A snippet document — reusable markdown'),
                  zod
                    .object({
                      type: zod.enum(['skill']),
                    })
                    .describe(
                      'A skill document — markdown instructions for AI'
                    ),
                  zod
                    .object({
                      type: zod.enum(['initiative_description']),
                    })
                    .describe('The description document of an initiative'),
                ])
                .describe(
                  'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                ),
            ])
            .optional(),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe(
              'The time the document instance \/ document BOM was updated'
            ),
          type: zod.enum(['document']),
        }),
        zod.object({
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the chat was created'),
          deletedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the chat was deleted'),
          id: zod.string().describe('The chat uuid'),
          isPersistent: zod.boolean(),
          model: zod
            .string()
            .nullish()
            .describe('The model used to generate the chat'),
          name: zod.string().describe('The name of the chat'),
          projectId: zod
            .string()
            .nullish()
            .describe('The project id of the chat'),
          tokenCount: zod.number().nullish(),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the chat was last updated'),
          userId: zod.string().describe('Who the chat belongs to'),
          type: zod.enum(['chat']),
        }),
        zod.object({
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the project was created'),
          deletedAt: zod.iso.datetime({}).nullish(),
          id: zod.string().describe('The id of the project'),
          name: zod.string().describe('The name of the project'),
          parentId: zod.string().nullish().describe('The parent project id'),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the project was updated'),
          userId: zod
            .string()
            .describe('The user id of who created the project'),
          type: zod.enum(['project']),
        }),
      ])
    )
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Upserts an item into the user's history and performs other necessary tracking actions
 */
export const upsertHistoryHandlerParams = zod.object({
  item_type: zod.string().describe('Type of the item'),
  item_id: zod.string().describe('ID of the item'),
});

export const upsertHistoryHandlerResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Deletes an item from the user's history
 */
export const deleteHistoryHandlerParams = zod.object({
  item_type: zod.string().describe('Type of the item'),
  item_id: zod.string().describe('ID of the item'),
});

export const deleteHistoryHandlerResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary List initiatives the caller can view.
 */
export const listInitiativesResponse = zod
  .object({
    initiatives: zod
      .array(
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'Opaque identifier for an initiative. Minted as UUIDv7 in application code.'
              ),
            name: zod.string().describe('Display name.'),
            updatedAt: zod.iso
              .datetime({})
              .describe('When the initiative was last updated.'),
          })
          .describe('List-row view of an initiative.')
      )
      .describe('Initiatives the caller can view.'),
  })
  .describe('Accessible-initiative list.');

/**
 * @summary Create an initiative owned by the caller.
 */
export const createInitiativeBody = zod
  .object({
    description: zod
      .string()
      .nullish()
      .describe(
        'Initial markdown for the description surface. Not stored on the initiative; later\nedits happen in the collaborative description editor.'
      ),
    memberIds: zod
      .array(zod.string())
      .nullish()
      .describe(
        'Optional member user ids. Invalid ids fail at the service boundary.'
      ),
    name: zod.string().describe('Display name.'),
    propertyValues: zod
      .array(
        zod
          .object({
            propertyDefinitionId: zod
              .uuid()
              .describe('Property definition to set.'),
            value: zod
              .union([
                zod
                  .object({
                    type: zod.enum(['boolean']),
                    value: zod.boolean(),
                  })
                  .describe('Boolean true\/false value'),
                zod
                  .object({
                    type: zod.enum(['date']),
                    value: zod.iso.datetime({}),
                  })
                  .describe('Date and time value'),
                zod
                  .object({
                    type: zod.enum(['number']),
                    value: zod.number(),
                  })
                  .describe('Numeric value'),
                zod
                  .object({
                    type: zod.enum(['string']),
                    value: zod.string(),
                  })
                  .describe('String\/text value'),
                zod
                  .object({
                    option_id: zod.uuid(),
                    type: zod.enum(['select_option']),
                  })
                  .describe('Select option by ID (for select-type properties)'),
                zod
                  .object({
                    option_ids: zod.array(zod.uuid()),
                    type: zod.enum(['multi_select_option']),
                  })
                  .describe(
                    'Multiple select options by ID (for multi-select properties)'
                  ),
                zod
                  .object({
                    reference: zod
                      .object({
                        entity_id: zod.string(),
                        entity_type: zod
                          .enum([
                            'CALENDAR_EVENT',
                            'CALL_RECORD',
                            'CHANNEL',
                            'CHAT',
                            'COMPANY',
                            'DATABASE_ROW',
                            'CONTACT',
                            'DOCUMENT',
                            'INITIATIVE',
                            'PROJECT',
                            'TASK',
                            'THREAD',
                            'USER',
                          ])
                          .describe(
                            'Type of entity that can be referenced by entity properties.'
                          ),
                        specific_message_id: zod
                          .uuid()
                          .nullish()
                          .describe(
                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                          ),
                      })
                      .describe(
                        'Entity reference for entity-type property values.'
                      ),
                    type: zod.enum(['entity_reference']),
                  })
                  .describe('Entity reference'),
                zod
                  .object({
                    references: zod.array(
                      zod
                        .object({
                          entity_id: zod.string(),
                          entity_type: zod
                            .enum([
                              'CALENDAR_EVENT',
                              'CALL_RECORD',
                              'CHANNEL',
                              'CHAT',
                              'COMPANY',
                              'DATABASE_ROW',
                              'CONTACT',
                              'DOCUMENT',
                              'INITIATIVE',
                              'PROJECT',
                              'TASK',
                              'THREAD',
                              'USER',
                            ])
                            .describe(
                              'Type of entity that can be referenced by entity properties.'
                            ),
                          specific_message_id: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                            ),
                        })
                        .describe(
                          'Entity reference for entity-type property values.'
                        )
                    ),
                    type: zod.enum(['multi_entity_reference']),
                  })
                  .describe(
                    'Multiple entity references (for multi-select entity properties)'
                  ),
                zod
                  .object({
                    type: zod.enum(['link']),
                    url: zod.string(),
                  })
                  .describe('Link value'),
                zod
                  .object({
                    type: zod.enum(['multi_link']),
                    urls: zod.array(zod.string()),
                  })
                  .describe(
                    'Multiple link values (for multi-select link properties)'
                  ),
              ])
              .describe(
                'Type-safe enum for setting entity property values - provides compile-time validation.'
              ),
          })
          .describe(
            'A property value set on a new initiative as part of its create.'
          )
      )
      .optional()
      .describe(
        'Property values set as the owner within the create. A value the properties\nservice rejects fails the whole create; no initiative is left behind.'
      ),
    shareWithTeam: zod
      .boolean()
      .nullish()
      .describe(
        "Share with the owner's team at create time. Defaults to true; users without\na team create an unshared initiative. Explicit false skips the team grant."
      ),
  })
  .describe('Create-initiative HTTP body.');

export const createInitiativeResponse = zod
  .object({
    createdAt: zod.iso
      .datetime({})
      .describe('When the initiative was created.'),
    id: zod
      .uuid()
      .describe(
        'Opaque identifier for an initiative. Minted as UUIDv7 in application code.'
      ),
    memberIds: zod
      .array(zod.string())
      .describe('Member user ids. The owner is never stored here.'),
    name: zod.string().describe('Display name.'),
    ownerId: zod.string(),
    sharePermission: zod.object({
      channelSharePermissions: zod
        .array(
          zod
            .object({
              access_level: zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
              channel_id: zod.string().describe('The channel id'),
            })
            .describe('The channel share permission')
        )
        .nullish()
        .describe('The channel share permissions for the item'),
      id: zod.string().describe('The share permission id'),
      linkShare: zod
        .union([
          zod.null(),
          zod
            .enum(['PUBLIC', 'TEAM'])
            .describe('Defines who can access an item through its share link.'),
        ])
        .optional(),
      linkShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
      owner: zod.string().describe('The owner of the item'),
      teamShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
    }),
    taskIds: zod
      .array(zod.string())
      .describe('Task ids currently assigned to the initiative.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the initiative was last updated.'),
    userAccessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
  })
  .describe(
    'Full initiative returned to a caller, including members, tasks, and share state.'
  );

/**
 * @summary Fetch one initiative the caller can view.
 */
export const getInitiativeParams = zod.object({
  initiative_id: zod.string().describe('Initiative identifier.'),
});

export const getInitiativeResponse = zod
  .object({
    createdAt: zod.iso
      .datetime({})
      .describe('When the initiative was created.'),
    id: zod
      .uuid()
      .describe(
        'Opaque identifier for an initiative. Minted as UUIDv7 in application code.'
      ),
    memberIds: zod
      .array(zod.string())
      .describe('Member user ids. The owner is never stored here.'),
    name: zod.string().describe('Display name.'),
    ownerId: zod.string(),
    sharePermission: zod.object({
      channelSharePermissions: zod
        .array(
          zod
            .object({
              access_level: zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
              channel_id: zod.string().describe('The channel id'),
            })
            .describe('The channel share permission')
        )
        .nullish()
        .describe('The channel share permissions for the item'),
      id: zod.string().describe('The share permission id'),
      linkShare: zod
        .union([
          zod.null(),
          zod
            .enum(['PUBLIC', 'TEAM'])
            .describe('Defines who can access an item through its share link.'),
        ])
        .optional(),
      linkShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
      owner: zod.string().describe('The owner of the item'),
      teamShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
    }),
    taskIds: zod
      .array(zod.string())
      .describe('Task ids currently assigned to the initiative.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the initiative was last updated.'),
    userAccessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
  })
  .describe(
    'Full initiative returned to a caller, including members, tasks, and share state.'
  );

/**
 * @summary Delete the initiative the caller owns.
 */
export const deleteInitiativeParams = zod.object({
  initiative_id: zod.string().describe('Initiative identifier.'),
});

export const deleteInitiativeResponse = zod.object({
  success: zod.boolean().describe('Indicates if the request was successful'),
});

/**
 * @summary Update fields the caller can edit.
 */
export const updateInitiativeParams = zod.object({
  initiative_id: zod.string().describe('Initiative identifier.'),
});

export const updateInitiativeBody = zod
  .object({
    memberIds: zod
      .array(zod.string())
      .nullish()
      .describe(
        'Full replacement collaborator list when present. Only the owner may send this field.'
      ),
    name: zod.string().nullish().describe('Replacement name.'),
    sharePermission: zod
      .union([
        zod.null(),
        zod.object({
          channelSharePermissions: zod
            .array(
              zod.object({
                accessLevel: zod
                  .union([
                    zod.null(),
                    zod
                      .enum(['view', 'comment', 'edit', 'owner'])
                      .describe(
                        'Ordered from least to most access top -> bottom'
                      ),
                  ])
                  .optional(),
                channelId: zod.string().describe('The channel id'),
                operation: zod.enum(['add', 'remove', 'replace']),
              })
            )
            .nullish()
            .describe(
              'Any channel share permissions to be created\/updated\/removed'
            ),
          linkShare: zod
            .union([
              zod.null(),
              zod
                .enum(['PUBLIC', 'TEAM'])
                .describe(
                  'Defines who can access an item through its share link.'
                ),
            ])
            .optional(),
          linkShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
          teamShareAccessLevel: zod
            .union([
              zod.null(),
              zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
            ])
            .optional(),
        }),
      ])
      .optional(),
  })
  .describe(
    'Update-initiative HTTP body. Absent fields are left unchanged. `member_ids`\npresent is a full replace. The description is edited in its collab surface, not here.'
  );

export const updateInitiativeResponse = zod
  .object({
    createdAt: zod.iso
      .datetime({})
      .describe('When the initiative was created.'),
    id: zod
      .uuid()
      .describe(
        'Opaque identifier for an initiative. Minted as UUIDv7 in application code.'
      ),
    memberIds: zod
      .array(zod.string())
      .describe('Member user ids. The owner is never stored here.'),
    name: zod.string().describe('Display name.'),
    ownerId: zod.string(),
    sharePermission: zod.object({
      channelSharePermissions: zod
        .array(
          zod
            .object({
              access_level: zod
                .enum(['view', 'comment', 'edit', 'owner'])
                .describe('Ordered from least to most access top -> bottom'),
              channel_id: zod.string().describe('The channel id'),
            })
            .describe('The channel share permission')
        )
        .nullish()
        .describe('The channel share permissions for the item'),
      id: zod.string().describe('The share permission id'),
      linkShare: zod
        .union([
          zod.null(),
          zod
            .enum(['PUBLIC', 'TEAM'])
            .describe('Defines who can access an item through its share link.'),
        ])
        .optional(),
      linkShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
      owner: zod.string().describe('The owner of the item'),
      teamShareAccessLevel: zod
        .union([
          zod.null(),
          zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
        ])
        .optional(),
    }),
    taskIds: zod
      .array(zod.string())
      .describe('Task ids currently assigned to the initiative.'),
    updatedAt: zod.iso
      .datetime({})
      .describe('When the initiative was last updated.'),
    userAccessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
  })
  .describe(
    'Full initiative returned to a caller, including members, tasks, and share state.'
  );

/**
 * @summary Gets the instructions document for the current user
 */
export const getInstructionsHandlerResponse = zod.object({
  documentId: zod.string(),
});

/**
 * @summary Creates an instructions document for the current user
 */
export const createInstructionsHandlerResponse = zod.object({
  documentId: zod.string(),
});

/**
 * @summary Gets the items the user has access to
 */
export const getItemsSoupQueryLimitMin = 0;

export const getItemsSoupQueryParams = zod.object({
  expand: zod
    .boolean()
    .optional()
    .describe('Whether to expand projects. Defaults to true.'),
  limit: zod
    .number()
    .min(getItemsSoupQueryLimitMin)
    .optional()
    .describe('Limit the number of items returned. Defaults to 20. Max 500.'),
  sort_method: zod
    .enum([
      'viewed_at',
      'created_at',
      'updated_at',
      'viewed_updated',
      'frecency',
      'touched_by_me',
      'notified_at',
    ])
    .optional()
    .describe(
      'Sort method. Options are viewed_at, created_at, updated_at,\nviewed_updated, frecency, touched_by_me, notified_at. Defaults to\nviewed_at.'
    ),
  sort_direction: zod
    .enum(['asc', 'desc'])
    .optional()
    .describe(
      'Sort direction. Options are asc, desc. Defaults to desc.\n\nRe-send this with every page: it is not carried in the cursor.\n\nApplies to the timestamp sort methods only. `asc` combined with\n`frecency` is rejected rather than ignored: frecency pages are ordered\nby relevance score and the cursor comparison assumes descending, so\nthere is no ascending frecency order to give.'
    ),
  cursor: zod.string().optional().describe('Base64 encoded cursor value.'),
});

export const getItemsSoupResponseItemsItemDataChannelOrgIdMin = 0;

export const getItemsSoupResponse = zod
  .object({
    items: zod.array(
      zod
        .union([
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    branchedFromId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the document this document branched from'
                      ),
                    branchedFromVersionId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on the file type'
                      ),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the document was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was deleted'),
                    documentFamilyId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the document family this document belongs to'
                      ),
                    documentVersionId: zod
                      .number()
                      .describe(
                        'The version of the document\nThis could be the document_instance_id or document_bom_id depending on the file type'
                      ),
                    fileType: zod
                      .string()
                      .nullish()
                      .describe(
                        'The file type of the document (e.g. pdf, docx)'
                      ),
                    id: zod.uuid().describe('The document id'),
                    name: zod.string().describe('The name of the document'),
                    ownerId: zod.string().describe('The owner of the document'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the project that this document belongs to'
                      ),
                    sha: zod
                      .string()
                      .nullish()
                      .describe(
                        'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                      ),
                    subType: zod
                      .union([
                        zod.null(),
                        zod
                          .union([
                            zod
                              .object({
                                is_completed: zod
                                  .boolean()
                                  .describe(
                                    'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                  ),
                                type: zod.enum(['task']),
                              })
                              .describe(
                                'A task document with its associated properties'
                              ),
                            zod
                              .object({
                                type: zod.enum(['snippet']),
                              })
                              .describe(
                                'A snippet document — reusable markdown'
                              ),
                            zod
                              .object({
                                type: zod.enum(['skill']),
                              })
                              .describe(
                                'A skill document — markdown instructions for AI'
                              ),
                            zod
                              .object({
                                type: zod.enum(['initiative_description']),
                              })
                              .describe(
                                'The description document of an initiative'
                              ),
                          ])
                          .describe(
                            'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                          ),
                      ])
                      .optional(),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe(
                        'The time the document instance \/ document BOM was updated'
                      ),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A document as displayed in Soup.'),
              tag: zod.enum(['document']),
            })
            .describe('Document item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was deleted'),
                    id: zod.uuid().describe('The chat uuid'),
                    isPersistent: zod
                      .boolean()
                      .describe('Whether the chat is persistent or not'),
                    model: zod
                      .string()
                      .nullish()
                      .describe(
                        'The last model selected for a sent message (`provider\/model` id).'
                      ),
                    name: zod.string().describe('The name of the chat'),
                    ownerId: zod.string().describe('Who the chat belongs to'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe('The project id of the chat'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was last updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was last viewed'),
                  })
                )
                .describe('A chat as displayed in Soup.'),
              tag: zod.enum(['chat']),
            })
            .describe('Chat item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the project was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the project was deleted'),
                    id: zod.uuid().describe('The id of the project'),
                    name: zod.string().describe('The name of the project'),
                    ownerId: zod.string().describe('The owner of the project'),
                    parentId: zod
                      .uuid()
                      .nullish()
                      .describe('The parent project id'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the project was updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A project as displayed in Soup.'),
              tag: zod.enum(['project']),
            })
            .describe('Project item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    id: zod.uuid().describe('Initiative identifier.'),
                    name: zod.string().describe('Initiative display name.'),
                    ownerId: zod.string().describe('Initiative owner.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Last time the requesting user viewed the initiative.'
                      ),
                  })
                )
                .describe(
                  'An initiative (called a project in the frontend) in the Soup feed.'
                ),
              tag: zod.enum(['initiative']),
            })
            .describe('Initiative entity.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was created.'),
                  id: zod.uuid().describe('Thread id.'),
                  inboxVisible: zod
                    .boolean()
                    .describe('Whether the thread should appear in the inbox.'),
                  isDraft: zod
                    .boolean()
                    .describe('Whether the thread contains a draft.'),
                  isImportant: zod
                    .boolean()
                    .describe('Whether the thread is marked important.'),
                  isRead: zod
                    .boolean()
                    .describe('Whether the thread has been read.'),
                  isSignal: zod
                    .boolean()
                    .describe(
                      "The denormalized `email_threads.is_signal` importance classification —\nthe same flag the soup Importance filter evaluates, distinct from\n`is_important` (Gmail's IMPORTANT label)."
                    ),
                  name: zod
                    .string()
                    .nullish()
                    .describe('Thread display name or subject.'),
                  ownerId: zod.string().describe('Owner user id.'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe('Project id associated with the thread.'),
                  providerId: zod
                    .string()
                    .nullish()
                    .describe('Provider-specific thread id.'),
                  senderEmail: zod
                    .string()
                    .nullish()
                    .describe('Sender email address for the preview.'),
                  senderName: zod
                    .string()
                    .nullish()
                    .describe('Sender display name for the preview.'),
                  senderPhotoUrl: zod
                    .string()
                    .nullish()
                    .describe('Sender photo URL for the preview.'),
                  snippet: zod.string().nullish().describe('Thread snippet.'),
                  sortTs: zod.iso
                    .datetime({})
                    .describe('Timestamp used for email thread sorting.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was last updated.'),
                  viewedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'Timestamp when the requesting user last viewed the thread.'
                    ),
                })
                .describe('Email thread preview data as displayed in Soup.')
                .and(
                  zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    )
                )
                .and(
                  zod.object({
                    attachments: zod
                      .array(
                        zod
                          .object({
                            contentId: zod
                              .string()
                              .nullish()
                              .describe('Content id for inline attachments.'),
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the attachment was created.'
                              ),
                            filename: zod
                              .string()
                              .nullish()
                              .describe('Attachment filename.'),
                            id: zod.uuid().describe('Attachment id.'),
                            messageId: zod
                              .uuid()
                              .describe(
                                'Message id that owns this attachment.'
                              ),
                            mimeType: zod
                              .string()
                              .nullish()
                              .describe('Attachment MIME type.'),
                            providerAttachmentId: zod
                              .string()
                              .nullish()
                              .describe('Provider-specific attachment id.'),
                            sizeBytes: zod
                              .number()
                              .nullish()
                              .describe('Attachment size in bytes.'),
                          })
                          .describe('An email attachment as displayed in Soup.')
                      )
                      .describe('Attachments on the thread.'),
                    labels: zod
                      .array(
                        zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the label was created.'
                              ),
                            id: zod.uuid().describe('Label id.'),
                            labelListVisibility: zod
                              .enum([
                                'labelShow',
                                'labelShowIfUnread',
                                'labelHide',
                              ])
                              .describe('Gmail-style label list visibility.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the label.'
                              ),
                            messageListVisibility: zod
                              .enum(['show', 'hide'])
                              .describe(
                                'Gmail-style message list visibility for a label.'
                              ),
                            name: zod.string().describe('Label display name.'),
                            providerLabelId: zod
                              .string()
                              .describe('Provider-specific label id.'),
                            type: zod
                              .enum(['system', 'user'])
                              .describe(
                                'Whether an email label is provider-created or user-created.'
                              ),
                          })
                          .describe('An email label as displayed in Soup.')
                      )
                      .describe('Labels attached to the thread.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            emailAddress: zod
                              .string()
                              .nullish()
                              .describe('Contact email address.'),
                            id: zod.uuid().describe('Contact id.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the contact.'
                              ),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Contact display name.'),
                            sfsPhotoUrl: zod
                              .string()
                              .nullish()
                              .describe(
                                'Contact photo URL from SFS, if available.'
                              ),
                          })
                          .describe(
                            'A contact participating in an email thread as displayed in Soup.'
                          )
                      )
                      .describe('Contacts participating in the thread.'),
                  })
                )
                .describe(
                  'Email thread preview enriched with related metadata for Soup.'
                ),
              tag: zod.enum(['emailThread']),
            })
            .describe('Email thread item.'),
          zod
            .object({
              data: zod
                .object({
                  channel: zod
                    .object({
                      channel_type: zod
                        .enum(['public', 'private', 'direct_message', 'team'])
                        .describe('Type of channel.'),
                      created_at: zod.iso
                        .datetime({})
                        .describe('Creation timestamp.'),
                      id: zod.uuid().describe('Channel id.'),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Channel display name.'),
                      org_id: zod
                        .number()
                        .min(getItemsSoupResponseItemsItemDataChannelOrgIdMin)
                        .nullish()
                        .describe('Organization id.'),
                      owner_id: zod.string().describe('Channel owner.'),
                      team_id: zod.uuid().nullish().describe('Team id.'),
                      updated_at: zod.iso
                        .datetime({})
                        .describe('Update timestamp.'),
                    })
                    .describe('Channel metadata in soup payloads.'),
                  is_participant: zod
                    .boolean()
                    .optional()
                    .describe(
                      "Whether the requesting user is an active participant of the channel.\nFalse for team channels of the user's teams they have not joined."
                    ),
                  participants: zod
                    .array(
                      zod
                        .object({
                          channel_id: zod
                            .uuid()
                            .describe(
                              'Channel id for the participant membership.'
                            ),
                          joined_at: zod.iso
                            .datetime({})
                            .describe(
                              'Timestamp when the participant joined the channel.'
                            ),
                          left_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the participant left the channel, if any.'
                            ),
                          role: zod
                            .enum(['owner', 'admin', 'member'])
                            .describe('The role a user has within a channel.'),
                          user_id: zod
                            .string()
                            .describe('Participant user id.'),
                        })
                        .describe("A user's membership in a channel.")
                    )
                    .describe('Participants in the channel.'),
                })
                .describe('Channel metadata together with its participants.')
                .and(
                  zod
                    .object({
                      latest_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                      latest_non_thread_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                    })
                    .describe('Latest-message bundle for soup payloads.')
                )
                .and(
                  zod.object({
                    interacted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last interacted with this channel.'
                      ),
                    viewed_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last viewed this channel.'
                      ),
                  })
                )
                .describe('A channel as displayed in Soup.'),
              tag: zod.enum(['channel']),
            })
            .describe('Channel item.'),
          zod
            .object({
              data: zod
                .object({
                  attachments: zod
                    .array(
                      zod
                        .object({
                          created_at: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          entity_id: zod.string().describe('Entity id.'),
                          entity_type: zod.string().describe('Type of entity.'),
                          height: zod
                            .number()
                            .nullish()
                            .describe('Height for images.'),
                          id: zod.uuid().describe('Attachment id.'),
                          width: zod
                            .number()
                            .nullish()
                            .describe('Width for images.'),
                        })
                        .describe('An attachment on a message.')
                    )
                    .describe('Attachments on this message.'),
                  channel_id: zod.uuid().describe('Channel id.'),
                  content: zod.string().describe('Message content.'),
                  created_at: zod.iso
                    .datetime({})
                    .describe('Creation timestamp.'),
                  deleted_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Deletion timestamp.'),
                  edited_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Edit timestamp.'),
                  id: zod.uuid().describe('Message id.'),
                  reactions: zod
                    .array(
                      zod
                        .object({
                          emoji: zod.string().describe('Emoji string.'),
                          users: zod
                            .array(zod.string())
                            .describe('User ids who added this reaction.'),
                        })
                        .describe('A reaction with emoji and user list.')
                    )
                    .describe('Reactions on this message.'),
                  sender: zod
                    .object({
                      avatar_url: zod
                        .string()
                        .nullish()
                        .describe('Avatar URL for bot senders.'),
                      id: zod
                        .string()
                        .describe(
                          'Sender id without the storage namespace prefix.'
                        ),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Display name for bot senders.'),
                      type: zod
                        .enum(['user', 'bot'])
                        .describe(
                          'Public sender type for soup channel messages.'
                        ),
                    })
                    .describe(
                      'Public sender identity for soup channel messages.'
                    ),
                  sender_id: zod.string().describe('Sender id.'),
                  thread: zod
                    .object({
                      latest_reply_at: zod.iso
                        .datetime({})
                        .nullish()
                        .describe('Timestamp of the latest reply.'),
                      preview: zod
                        .array(
                          zod
                            .object({
                              attachments: zod
                                .array(
                                  zod
                                    .object({
                                      created_at: zod.iso
                                        .datetime({})
                                        .describe('Creation timestamp.'),
                                      entity_id: zod
                                        .string()
                                        .describe('Entity id.'),
                                      entity_type: zod
                                        .string()
                                        .describe('Type of entity.'),
                                      height: zod
                                        .number()
                                        .nullish()
                                        .describe('Height for images.'),
                                      id: zod.uuid().describe('Attachment id.'),
                                      width: zod
                                        .number()
                                        .nullish()
                                        .describe('Width for images.'),
                                    })
                                    .describe('An attachment on a message.')
                                )
                                .describe('Attachments on this reply.'),
                              content: zod.string().describe('Reply content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              edited_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Edit timestamp.'),
                              id: zod.uuid().describe('Reply id.'),
                              reactions: zod
                                .array(
                                  zod
                                    .object({
                                      emoji: zod
                                        .string()
                                        .describe('Emoji string.'),
                                      users: zod
                                        .array(zod.string())
                                        .describe(
                                          'User ids who added this reaction.'
                                        ),
                                    })
                                    .describe(
                                      'A reaction with emoji and user list.'
                                    )
                                )
                                .describe('Reactions on this reply.'),
                              sender: zod
                                .object({
                                  avatar_url: zod
                                    .string()
                                    .nullish()
                                    .describe('Avatar URL for bot senders.'),
                                  id: zod
                                    .string()
                                    .describe(
                                      'Sender id without the storage namespace prefix.'
                                    ),
                                  name: zod
                                    .string()
                                    .nullish()
                                    .describe('Display name for bot senders.'),
                                  type: zod
                                    .enum(['user', 'bot'])
                                    .describe(
                                      'Public sender type for soup channel messages.'
                                    ),
                                })
                                .describe(
                                  'Public sender identity for soup channel messages.'
                                ),
                              sender_id: zod.string().describe('Sender id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe('A thread reply shown in preview.')
                        )
                        .describe('Preview replies.'),
                      reply_count: zod.number().describe('Total reply count.'),
                    })
                    .describe(
                      'Thread metadata and preview replies for soup channel messages.'
                    ),
                  updated_at: zod.iso
                    .datetime({})
                    .describe('Update timestamp.'),
                })
                .describe(
                  'A top-level channel message thread for soup payloads.\n\nThis mirrors the public channel-message API shape so soup consumers can\nrender a thread root the same way they render a channel timeline message.'
                ),
              tag: zod.enum(['channelThread']),
            })
            .describe('Channel thread item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    attended: zod
                      .boolean()
                      .describe(
                        'Whether the requesting user attended this call. Kept for compatibility\nand derived from `status == ATTENDED`.'
                      ),
                    callId: zod.uuid().describe('The call identifier.'),
                    channelId: zod
                      .uuid()
                      .nullish()
                      .describe('The channel this call belongs to.'),
                    channelName: zod
                      .string()
                      .nullish()
                      .describe('Resolved display name for the channel.'),
                    createdBy: zod
                      .string()
                      .describe('User who created the call.'),
                    customName: zod
                      .string()
                      .nullish()
                      .describe(
                        'User-supplied or AI-generated display name for the call.'
                      ),
                    durationMs: zod
                      .number()
                      .nullish()
                      .describe(
                        'Call duration in milliseconds (None if still active).'
                      ),
                    endedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('When the call ended (None if still active).'),
                    guests: zod
                      .array(
                        zod
                          .object({
                            displayName: zod
                              .string()
                              .describe('Guest-provided display name.'),
                            id: zod
                              .uuid()
                              .describe(
                                "Opaque guest identity; matches the guest's transcript speaker id."
                              ),
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the guest joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the guest left (None if still in an active call).'
                              ),
                          })
                          .describe(
                            'A non-account guest of a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Non-account guests in the call.'),
                    isActive: zod
                      .boolean()
                      .describe('Whether the call is currently active.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the user joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the user left (None if still in an active call).'
                              ),
                            userId: zod.string().describe('The Macro user id.'),
                          })
                          .describe(
                            'A Macro-account participant in a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Macro-account participants in the call.'),
                    startedAt: zod.iso
                      .datetime({})
                      .describe('When the call started.'),
                    status: zod
                      .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
                      .describe(
                        'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
                      ),
                    summary: zod
                      .string()
                      .nullish()
                      .describe(
                        'AI-generated summary of the call. Only set on archived\n`call_records` once summarization has run; active calls always\nreturn `None`.'
                      ),
                  })
                )
                .describe(
                  'A call record as displayed in Soup. Excludes room_name, egress_id,\nand transcript — fields that are irrelevant for the soup feed.'
                ),
              tag: zod.enum(['call']),
            })
            .describe('Call record item.'),
          zod
            .object({
              data: zod
                .object({
                  conferenceProvider: zod
                    .string()
                    .nullish()
                    .describe(
                      'Which conferencing system backs `conference_url`.'
                    ),
                  conferenceUrl: zod
                    .string()
                    .nullish()
                    .describe('Direct conference join URL.'),
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Entity creation timestamp.'),
                  description: zod
                    .string()
                    .nullish()
                    .describe('Optional description.'),
                  extra: zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    ),
                  icalUid: zod
                    .string()
                    .describe('RFC 5545 UID used for source reconciliation.'),
                  id: zod.uuid().describe('Entity identifier.'),
                  isReadOnly: zod
                    .boolean()
                    .describe(
                      'Whether the selected canonical source is read-only.'
                    ),
                  lastReminderFiredAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      "When this event's most recent reminder notification was delivered."
                    ),
                  location: zod
                    .string()
                    .nullish()
                    .describe('Optional location label.'),
                  organizerEmail: zod
                    .string()
                    .nullish()
                    .describe('Organizer email.'),
                  organizerName: zod
                    .string()
                    .nullish()
                    .describe('Organizer display name.'),
                  ownerId: zod.string().describe('Owning Macro user.'),
                  status: zod.string().describe('Canonical status.'),
                  time: zod
                    .union([
                      zod
                        .object({
                          endsAt: zod.iso
                            .datetime({})
                            .describe('Exclusive end.'),
                          kind: zod.enum(['timed']),
                          startsAt: zod.iso
                            .datetime({})
                            .describe('Inclusive start.'),
                          timeZone: zod
                            .string()
                            .nullish()
                            .describe('Original IANA time zone.'),
                        })
                        .describe('Absolute timed event.'),
                      zod
                        .object({
                          endDate: zod.iso
                            .date()
                            .describe('Exclusive end date.'),
                          kind: zod.enum(['allDay']),
                          startDate: zod.iso
                            .date()
                            .describe('Inclusive start date.'),
                        })
                        .describe('All-day event with an exclusive end date.'),
                    ])
                    .describe(
                      'Timed or all-day calendar event span.\n\nFields are camelCased per variant rather than via `rename_all_fields`,\nwhich utoipa ignores — the generated OpenAPI schema would otherwise\nclaim snake_case fields the wire never carries.'
                    ),
                  title: zod.string().describe('Display title.'),
                  transparency: zod
                    .string()
                    .describe('Availability transparency.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Entity update timestamp.'),
                  visibility: zod.string().describe('Canonical visibility.'),
                })
                .describe('A canonical calendar event entity in Soup.'),
              tag: zod.enum(['calendarEvent']),
            })
            .describe('Calendar event item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('When the company was created.'),
                    description: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display description from the primary domain's directory entry."
                      ),
                    domains: zod
                      .array(
                        zod
                          .object({
                            companyId: zod
                              .uuid()
                              .describe(
                                'The id of the company the domain belongs to.'
                              ),
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When the domain record was created.'),
                            domain: zod
                              .string()
                              .describe(
                                'The domain (lowercased, e.g. \"acme.com\").'
                              ),
                            id: zod
                              .uuid()
                              .describe('The id of the domain record.'),
                          })
                          .describe(
                            "A CRM domain as displayed in Soup. Mirrors the crm crate's\n[`CrmDomain`] with a stable wire shape that the FE can rely on."
                          )
                      )
                      .describe(
                        'Domains associated with this company, ordered by creation time\nascending (primary first).'
                      ),
                    emailSync: zod
                      .boolean()
                      .describe(
                        'Whether email sync is enabled for this company.'
                      ),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the company is hidden from CRM listings. Soup filters\nthese out by default.'
                      ),
                    id: zod.uuid().describe('The id of the company.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display name from the primary domain's directory entry, or\n`None` when unresolved."
                      ),
                    teamId: zod
                      .uuid()
                      .describe(
                        'The id of the team that owns this company record.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('When the company was last updated.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'When the requesting user last viewed this company, or `None` if\nnever viewed. Mirrors the `viewed_at` other soup entities carry.'
                      ),
                  })
                )
                .describe(
                  'A CRM company as displayed in Soup. Carries the core company\nfields plus display metadata resolved from `crm_domain_directory`\nagainst the primary (earliest-created) domain.'
                ),
              tag: zod.enum(['crmCompany']),
            })
            .describe('CRM company item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    companyId: zod.uuid().describe('Parent company ID.'),
                    companyName: zod
                      .string()
                      .describe("Parent company's display name."),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Record creation time.'),
                    email: zod.string().describe('Full email address.'),
                    firstInteraction: zod.iso
                      .datetime({})
                      .describe('Earliest interaction for this team record.'),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the contact or parent company is hidden.'
                      ),
                    id: zod.uuid().describe('Original team-owned contact ID.'),
                    lastInteraction: zod.iso
                      .datetime({})
                      .describe('Latest interaction for this team record.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe('Team-local display name, if known.'),
                    teamId: zod.uuid().describe('Team owning this record.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Record update time.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe("The viewer's latest visit to this record."),
                  })
                )
                .describe(
                  "One original CRM contact record, selected from the viewer's accessible teams."
                ),
              tag: zod.enum(['crmContact']),
            })
            .describe('Team-owned CRM contact.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was created.'),
                  foreignEntityId: zod
                    .string()
                    .describe('Identifier assigned by the external system.'),
                  foreignEntitySource: zod
                    .string()
                    .describe(
                      'Source system that owns the external identifier.'
                    ),
                  id: zod
                    .uuid()
                    .describe(
                      'Internal primary key for this foreign entity record.'
                    ),
                  metadata: zod
                    .object({})
                    .describe('Arbitrary metadata stored with the mapping.'),
                  storedForAuthEntity: zod
                    .string()
                    .describe(
                      'Internal auth entity namespace this foreign entity is stored for.'
                    ),
                  storedForId: zod
                    .string()
                    .describe(
                      'Internal entity identifier this foreign entity is stored for.'
                    ),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was last updated.'),
                })
                .describe('A foreign entity record as displayed in Soup.'),
              tag: zod.enum(['foreignEntity']),
            })
            .describe('Foreign entity item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    botId: zod.uuid().describe('The bot running this session'),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the session was created'),
                    harness: zod
                      .string()
                      .describe(
                        'The runtime snapshotted when the session was created.'
                      ),
                    id: zod.uuid().describe('The agent session uuid'),
                    isArchived: zod
                      .boolean()
                      .describe(
                        'Whether the session is archived and read-only.'
                      ),
                    name: zod
                      .string()
                      .describe('The user-facing name of the session'),
                    ownerId: zod
                      .string()
                      .describe('Who the session belongs to'),
                    pullRequestId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        "The linked pull request's Macro entity, when visible to the viewer."
                      ),
                    pullRequestState: zod
                      .union([
                        zod.null(),
                        zod
                          .enum(['open', 'draft', 'closed', 'merged'])
                          .describe(
                            "Last synchronized state of a session's linked GitHub pull request."
                          ),
                      ])
                      .optional(),
                    pullRequestUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The persisted pull request associated with the session.'
                      ),
                    repoBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'The starting branch selected for this session, not its current branch.'
                      ),
                    repoUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The repository the session works with, when one was selected.'
                      ),
                    status: zod
                      .string()
                      .describe(
                        "The session's last known status.\n\n`no_messages` until the first system event arrives, `disconnected` if\nthe connection dropped without a clean close, otherwise the wire name\nof the most recent system event (for example `session\/end`)."
                      ),
                    threadId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The channel thread the session was opened from, when any'
                      ),
                    turnState: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last persisted fold turn state. Absent until an older session next runs.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the session was last modified'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'The time the session was last viewed by the requesting user'
                      ),
                    workingBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last captured working branch, when the runtime has reported one.'
                      ),
                  })
                )
                .describe(
                  'An agent session as displayed in Soup.\n\nIncludes the persisted runtime and repository metadata needed to render\ncoding and non-coding sessions without fetching each session separately.'
                ),
              tag: zod.enum(['agentSession']),
            })
            .describe('Agent session item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    createdBy: zod
                      .string()
                      .nullish()
                      .describe('Who created the row, when they still exist.'),
                    databaseId: zod
                      .uuid()
                      .describe('The database the table belongs to.'),
                    id: zod.uuid().describe('Row identifier.'),
                    ownerId: zod
                      .string()
                      .describe(
                        "The database's owner, which owns every row in it."
                      ),
                    position: zod
                      .string()
                      .describe(
                        'Fractional index ordering the row within its table.'
                      ),
                    tableId: zod
                      .uuid()
                      .describe('The table the row belongs to.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                  })
                )
                .describe(
                  "A row of a Macro database table in the Soup feed. Its cells are its\nentity properties; access is its database's."
                ),
              tag: zod.enum(['databaseRow']),
            })
            .describe('Database row item.'),
        ])
        .describe('A single item in the Soup feed.')
        .and(
          zod.object({
            frecency_score: zod.number(),
            is_favorited: zod
              .boolean()
              .describe(
                'Whether the requesting user has favorited this entity.'
              ),
            notified_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the caller was last notified about this entity, present only\nwhen the page was ordered by `notified_at`. Clients keep the notified\nfeed ordered and date-bucketed on this value.'
              ),
            touched_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                "The caller's latest own mutation of this entity, present only when the\npage was ordered by `touched_by_me`. Clients keep the touched feed\nordered on this value, so it can be bumped optimistically."
              ),
          })
        )
        .describe(
          'API representation of a soup item with its per-viewer enrichments.'
        )
    ),
    next_cursor: zod.string().nullish(),
  })
  .describe('Response page for non-grouped soup endpoints.');

/**
 * @summary Gets the items the user has access to
 */
export const postItemsSoupQueryParams = zod.object({
  cursor: zod.string().optional().describe('Base64 encoded cursor value.'),
});

export const postItemsSoupBodyLimitMin = 0;

export const postItemsSoupBody = zod
  .object({
    agent_session_filters: zod
      .object({
        ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Agent session ids to filter by. Empty to include all accessible sessions.'
          ),
        include: zod
          .boolean()
          .optional()
          .describe(
            'Opt this query into agent sessions at all. Agent sessions are off by\ndefault — see [`crate::ast::agent_session::AgentSessionLiteral::Include`].\nAsking for specific `ids` or `owners` also opts in.'
          ),
        owners: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by session owner principal — a user ('macro|user1@user.com'), a bot\n('bot|<uuid>'), or a team (a bare hyphenated uuid). Empty to include every\nowner."
          ),
      })
      .optional()
      .describe('Filters for agent sessions.'),
    calendar_event_filters: zod
      .object({
        attendees: zod
          .array(zod.string())
          .optional()
          .describe('Attendee email addresses.'),
        calendar_event_ids: zod
          .array(zod.string())
          .optional()
          .describe('Canonical event ids.'),
        ends_after: zod.iso
          .datetime({})
          .nullish()
          .describe('Include master events ending after this instant.'),
        organizers: zod
          .array(zod.string())
          .optional()
          .describe('Organizer email addresses.'),
        starts_before: zod.iso
          .datetime({})
          .nullish()
          .describe('Include master events starting before this instant.'),
        statuses: zod
          .array(zod.string())
          .optional()
          .describe(
            'Event statuses such as `confirmed`, `tentative`, or `cancelled`.'
          ),
      })
      .optional()
      .describe('Filters for canonical calendar-event entities.'),
    call_filters: zod
      .object({
        attended: zod
          .boolean()
          .nullish()
          .describe(
            'Legacy filter by whether the requesting user attended the call.\nPrefer [`CallFilters::status`] for new callers.\n`None` = no filter, `Some(true)` = only calls the user joined,\n`Some(false)` = only calls the user did not join.'
          ),
        call_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Call record IDs to filter by. Empty to include all calls.'
          ),
        channel_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Channel IDs to filter calls by. Empty to include all calls.'
          ),
        speaker_ids: zod
          .array(zod.string())
          .optional()
          .describe('Speaker macro user ids. Empty to include all.'),
        status: zod
          .union([
            zod.null(),
            zod
              .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
              .describe(
                'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
              ),
          ])
          .optional(),
      })
      .optional()
      .describe('Filters for call records.'),
    channel_filters: zod
      .object({
        channel_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Channel IDs to search within. Examples: ['general']. Empty to search all accessible channels."
          ),
        channel_types: zod
          .array(zod.string())
          .optional()
          .describe(
            "Channel types to filter by. Examples: ['public'], ['direct_message', 'private']. Empty to search all channel types."
          ),
        importance: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by channel importance. None to ignore, true to pass through (no clause), false to short-circuit and return nothing.'
          ),
        is_participant: zod
          .boolean()
          .nullish()
          .describe(
            "Filter by whether the requesting user is an active participant of the channel.\nPresence of this filter also widens the candidate set to team channels of the\nuser's teams that they have not joined, so `false` matches those channels.\nNone to ignore (participant channels only, today's default)."
          ),
        mentions: zod
          .array(zod.string())
          .optional()
          .describe(
            "Channel user mentions to search for. Examples: ['@username']. Empty if not filtering by mentions."
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
        org_id: zod
          .number()
          .nullish()
          .describe(
            'Channel organization ID to search within. Empty to ignore organization filtering.'
          ),
        sender_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Sender IDs to search within. Examples: ['user1']. Empty to search all accessible senders."
          ),
        team_id: zod
          .string()
          .nullish()
          .describe(
            'Channel team ID to search within. Empty to ignore team filtering.'
          ),
        thread_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Channel thread IDs to search within. Examples: ['thread123']. Empty to search all threads."
          ),
      })
      .optional()
      .describe(
        'The channel message filters used to filter down what channel messages you search over.'
      ),
    channel_thread_filters: zod
      .object({
        channel_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Channel IDs containing the thread. Empty to include threads from all accessible channels.'
          ),
        participant_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Thread participant user IDs. A participant is the root message sender, anyone\nwho replied in the thread, or anyone @-mentioned in the thread (group mentions\nlike @here count through their per-user expansion at send time). Empty to\ninclude threads regardless of participants.'
          ),
        root_sender_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Sender IDs for the root thread message. Empty to include all root senders.'
          ),
        thread_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Channel thread parent message IDs to include. Empty to include all accessible threads.'
          ),
      })
      .optional()
      .describe(
        'The channel thread filters used to filter down channel-thread entities.'
      ),
    chat_filters: zod
      .object({
        chat_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Chat ids to search over. Examples: ['chat1'], ['chat1', 'chat2']. When provided, chat search will only match results on these chats. Empty to search all accessible chats."
          ),
        importance: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by chat importance. None to ignore, true to pass through (no clause), false to short-circuit and return nothing.'
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
        owners: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by chat owner principal — a user ('macro|user1@user.com'), a bot\n('bot|<uuid>'), or a team (a bare hyphenated uuid). Examples:\n['macro|user1@user.com'], ['macro|user1@user.com', 'bot|0199...']. Empty to\nsearch all owners."
          ),
        project_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "A list of project ids to search within. Examples: ['project1']. Empty to ignore project filtering."
          ),
        role: zod
          .array(zod.string())
          .optional()
          .describe(
            "Chat message roles to search. Examples: ['user'], ['assistant']. Empty to search all roles."
          ),
      })
      .optional()
      .describe(
        'The chat filters used to filter down what chats you search over.'
      ),
    crm_company_filters: zod
      .object({
        company_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "CRM company ids to filter by. Examples: ['11111111-...']. Empty to\ninclude all of the team's visible CRM companies."
          ),
        hidden: zod
          .boolean()
          .nullish()
          .describe(
            "Optional `crm_companies.hidden` filter. `None` = visible only\n(default for back-compat with non-admin callers). `Some(false)` =\nvisible only (explicit). `Some(true)` = hidden only — requires\nadmin\/owner team role; enforced by the CRM service from the\ncaller's team receipt."
          ),
      })
      .optional()
      .describe(
        'The crm company filters used to narrow which CRM companies appear in soup.'
      ),
    document_filters: zod
      .object({
        document_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Document ids to search over. Examples: ['doc1'], ['doc1', 'doc2']. Empty to search all accessible documents."
          ),
        file_types: zod
          .array(zod.string())
          .optional()
          .describe(
            "Document file types to search. Examples: ['pdf'], ['md', 'txt']. Empty to search all file types."
          ),
        importance: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by document importance. None to ignore, true to pass through (no clause), false to short-circuit and return nothing.'
          ),
        is_email_attachment: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by email attachment status. true = only email attachments, false = only non-email attachments, None = both.'
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
        owners: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by document owner principal — a user ('macro|user1@user.com'), a bot\n('bot|<uuid>'), or a team (a bare hyphenated uuid). Examples:\n['macro|user1@user.com'], ['macro|user1@user.com', 'bot|0199...']. Empty to\nsearch all owners."
          ),
        project_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "A list of project ids to search within. Examples: ['project1'].\nfiltering. Empty to ignore project filtering."
          ),
        sub_types: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by document sub type. Examples: ['task']. Empty to search all sub types."
          ),
        task_filters: zod
          .object({
            include_cbm_atm_nc: zod
              .boolean()
              .nullish()
              .describe(
                'Include tasks that are created by me, assigned to me, and not completed,\neven when they do not match other document filters.'
              ),
          })
          .optional()
          .describe('Task-only filters nested under document filters.'),
      })
      .optional()
      .describe(
        'The document filters used to filter down what documents you search over.'
      ),
    email_filters: zod
      .object({
        bcc: zod
          .array(zod.string())
          .optional()
          .describe(
            "Email BCC addresses to filter by. Examples: ['user@example.com']. Empty if not filtering by BCC."
          ),
        calendar_only: zod
          .boolean()
          .nullish()
          .describe(
            'When `Some(true)`, only include threads that have at least one message\nwith an iCalendar attachment (`.ics` filename or `application\/ics` mime\ntype). `Some(false)` and `None` apply no constraint.'
          ),
        cc: zod
          .array(zod.string())
          .optional()
          .describe(
            "Email CC addresses to filter by. Examples: ['user@example.com']. Empty if not filtering by CC."
          ),
        crm_addresses: zod
          .array(zod.string())
          .optional()
          .describe(
            "CRM-scoped address filter. When non-empty, expands visibility to every\nteammate's mailbox and restricts to threads involving any of these\nfully-qualified addresses. Each address is authorized against\n`crm_contacts` + `crm_companies` (contact must not be hidden, company\nmust not be hidden, `email_sync` must be true).\nMutually exclusive with `crm_domains`."
          ),
        crm_domains: zod
          .array(zod.string())
          .optional()
          .describe(
            "CRM-scoped domain filter. When non-empty, expands visibility to every\nteammate's mailbox and restricts to threads involving any of these\ndomains (in any of sender\/cc\/bcc\/recipient). Each domain is authorized\nagainst `crm_domains` + `crm_companies` (must exist for the caller's\nteam, company must not be hidden, `email_sync` must be true).\nMutually exclusive with `crm_addresses`."
          ),
        email_thread_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Email thread IDs to filter by. Examples: ['thread-uuid-1']. Empty to search all threads."
          ),
        exclude_labels: zod
          .array(zod.string())
          .optional()
          .describe(
            'Exclude emails that have any of these labels. Supports both Gmail system labels (e.g. \"CATEGORY_PROMOTIONS\") and user-created labels. Empty to not exclude any labels.\nNote: SPAM and TRASH emails are not indexed in OpenSearch, so they are already excluded by default.'
          ),
        importance: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by email importance. None to not filter. True to show only important emails\n(drafts, personal, sent, or uncategorized). False to show only unimportant emails\n(those categorized as promotions, social, updates, or forums).'
          ),
        include_labels: zod
          .array(zod.string())
          .optional()
          .describe(
            'Only include emails that have at least one of these labels. Supports both Gmail system labels (e.g. \"INBOX\", \"CATEGORY_PROMOTIONS\") and user-created labels (e.g. \"github\"). Empty to not filter by included labels.\nNote: SPAM and TRASH emails are not indexed in OpenSearch, so they will never appear in results regardless of this filter.'
          ),
        is_read: zod
          .boolean()
          .nullish()
          .describe(
            "Filter by the email thread's read flag, independently of notification state."
          ),
        link_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Restrict to specific inboxes by email_links.id. Empty means \"any inbox the\ncaller can access\" (soup expands to the full set at the router edge).'
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
        project_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'A list of project ids to search within. Empty to ignore project filtering.'
          ),
        recipients: zod
          .array(zod.string())
          .optional()
          .describe(
            "Email Recipient addresses to filter by. Examples: ['user@example.com']. Empty if not filtering by Recipient."
          ),
        senders: zod
          .array(zod.string())
          .optional()
          .describe(
            "Email sender addresses to filter by. Examples: ['user@example.com']. Empty to search all senders."
          ),
        shared: zod
          .enum(['exclude', 'include', 'only'])
          .optional()
          .describe(
            'Controls whether shared email threads are included in results.'
          ),
      })
      .optional()
      .describe(
        'The email filters used to filter down what emails you search over.'
      ),
    favorites_only: zod
      .boolean()
      .nullish()
      .describe(
        "Restrict results to the authenticated viewer's favorites when true."
      ),
    foreign_entity_filters: zod
      .object({
        foreign_entity_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'External entity identifiers to filter by. Empty to include all external IDs.'
          ),
        foreign_entity_sources: zod
          .array(zod.string())
          .optional()
          .describe(
            'External source names to filter by. Empty to include all sources.'
          ),
        ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Internal foreign entity record IDs to filter by. Empty to include all records.'
          ),
        includes_me: zod
          .boolean()
          .optional()
          .describe(
            'When true, only return foreign entities whose metadata lists the requesting user as a\nparticipant (GitHub `involves:me` semantics for `github_pull_request` records). False or\nabsent applies no filter. Serialized in filter ASTs as the `\"me\"` literal.'
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
      })
      .optional()
      .describe('Filters for foreign entity records.'),
    initiative_filters: zod
      .object({
        due_after: zod.iso
          .datetime({})
          .nullish()
          .describe('Inclusive lower due-date bound.'),
        due_before: zod.iso
          .datetime({})
          .nullish()
          .describe('Inclusive upper due-date bound.'),
        include: zod
          .boolean()
          .optional()
          .describe(
            'Opt this query into initiatives at all. Initiatives are off by\ndefault — see [`crate::ast::initiative::InitiativeLiteral::Include`].\nAsking for specific `initiative_ids` or `owners` also opts in.'
          ),
        initiative_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            'Initiative ids to filter by. Empty to include all accessible initiatives.'
          ),
        name: zod
          .string()
          .nullish()
          .describe('Case-insensitive name substring.'),
        owners: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by initiative owner principal — a user ('macro|user1@user.com'), a bot\n('bot|<uuid>'), or a team (a bare hyphenated uuid). Empty to include every\nowner."
          ),
      })
      .optional()
      .describe('Filters for initiatives.'),
    project_filters: zod
      .object({
        importance: zod
          .boolean()
          .nullish()
          .describe(
            'Filter by project importance. None to ignore, true to pass through (no clause), false to short-circuit and return nothing.'
          ),
        include_root: zod
          .boolean()
          .optional()
          .describe(
            'When true, `project_ids` also matches the projects themselves in addition to their children.'
          ),
        notification_filters: zod
          .object({
            states: zod
              .array(
                zod
                  .enum(['unseen', 'seen', 'done'])
                  .describe(
                    "The mutually exclusive lifecycle states of a user's notification."
                  )
              )
              .optional()
              .describe(
                'Include entities with a non-deleted notification in any of these exact states.\nEmpty means no notification restriction. Active means `[unseen, seen]`.'
              ),
          })
          .optional()
          .describe('Notification-level filters that apply to an entity type.'),
        owners: zod
          .array(zod.string())
          .optional()
          .describe(
            "Filter by project owner principal — a user ('macro|user1@user.com'), a bot\n('bot|<uuid>'), or a team (a bare hyphenated uuid). Examples:\n['macro|user1@user.com'], ['macro|user1@user.com', 'bot|0199...']. Empty to\nsearch all owners."
          ),
        project_ids: zod
          .array(zod.string())
          .optional()
          .describe(
            "Project IDs to search within. Examples: ['project1']. Empty to search all accessible projects.\nBy default matches children of these projects; set `include_root` to also match the projects themselves."
          ),
      })
      .optional()
      .describe(
        'The project filters used to filter down what projects you search over.'
      ),
    property_filters: zod
      .array(
        zod
          .object({
            entity_ids: zod
              .array(zod.string())
              .optional()
              .describe(
                "Entity reference IDs to match. Multiple values are OR'd together."
              ),
            entity_type: zod
              .string()
              .nullish()
              .describe(
                'The entity type for the property lookup (e.g., \"TASK\", \"DOCUMENT\", \"PROJECT\").\nWhen None, matches across all entity types.'
              ),
            option_ids: zod
              .array(zod.string())
              .optional()
              .describe(
                "Select option UUIDs to match. Multiple values are OR'd together."
              ),
            property_definition_id: zod
              .string()
              .describe('The UUID of the property definition to filter on.'),
          })
          .describe(
            "A single property-based filter condition.\n\nEach filter targets a specific property definition on entities of a given type,\nmatching against select option UUIDs or entity reference IDs.\nMultiple values within a single filter are OR'd together.\nMultiple filters are AND'd together."
          )
      )
      .optional()
      .describe('property-based filters applied across entity types'),
    tag_filter_mode: zod
      .enum(['any', 'all'])
      .optional()
      .describe('How multiple `tag_option_ids` combine when filtering.'),
    tag_option_ids: zod
      .array(zod.string())
      .optional()
      .describe(
        "tag option ids matched against `properties.values`, OR'd together across\nall tag definitions. Option ids are globally unique, so the match ignores\nthe owning definition id (personal and team tags combine into one OR)."
      ),
  })
  .describe('a bundle of all of the filters for each entity type')
  .and(
    zod
      .object({
        expand: zod
          .boolean()
          .nullish()
          .describe('Whether to expand projects. Defaults to true.'),
        limit: zod
          .number()
          .min(postItemsSoupBodyLimitMin)
          .nullish()
          .describe(
            'Limit the number of items returned. Defaults to 20. Max 500.'
          ),
        sort_direction: zod
          .union([
            zod.null(),
            zod
              .enum(['asc', 'desc'])
              .describe(
                'Sort direction accepted by non-grouped soup API endpoints.'
              ),
          ])
          .optional(),
        sort_method: zod
          .union([
            zod.null(),
            zod
              .enum([
                'viewed_at',
                'created_at',
                'updated_at',
                'viewed_updated',
                'frecency',
                'touched_by_me',
                'notified_at',
              ])
              .describe(
                'Sort options accepted by non-grouped soup API endpoints.'
              ),
          ])
          .optional(),
      })
      .describe('Query parameters shared by soup endpoints.')
  )
  .and(
    zod.object({
      emailView: zod
        .string()
        .optional()
        .describe('the view of specific emails to display'),
    })
  )
  .describe('Request body for the typed soup endpoint.');

export const postItemsSoupResponseItemsItemDataChannelOrgIdMin = 0;

export const postItemsSoupResponse = zod
  .object({
    items: zod.array(
      zod
        .union([
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    branchedFromId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the document this document branched from'
                      ),
                    branchedFromVersionId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on the file type'
                      ),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the document was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was deleted'),
                    documentFamilyId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the document family this document belongs to'
                      ),
                    documentVersionId: zod
                      .number()
                      .describe(
                        'The version of the document\nThis could be the document_instance_id or document_bom_id depending on the file type'
                      ),
                    fileType: zod
                      .string()
                      .nullish()
                      .describe(
                        'The file type of the document (e.g. pdf, docx)'
                      ),
                    id: zod.uuid().describe('The document id'),
                    name: zod.string().describe('The name of the document'),
                    ownerId: zod.string().describe('The owner of the document'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the project that this document belongs to'
                      ),
                    sha: zod
                      .string()
                      .nullish()
                      .describe(
                        'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                      ),
                    subType: zod
                      .union([
                        zod.null(),
                        zod
                          .union([
                            zod
                              .object({
                                is_completed: zod
                                  .boolean()
                                  .describe(
                                    'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                  ),
                                type: zod.enum(['task']),
                              })
                              .describe(
                                'A task document with its associated properties'
                              ),
                            zod
                              .object({
                                type: zod.enum(['snippet']),
                              })
                              .describe(
                                'A snippet document — reusable markdown'
                              ),
                            zod
                              .object({
                                type: zod.enum(['skill']),
                              })
                              .describe(
                                'A skill document — markdown instructions for AI'
                              ),
                            zod
                              .object({
                                type: zod.enum(['initiative_description']),
                              })
                              .describe(
                                'The description document of an initiative'
                              ),
                          ])
                          .describe(
                            'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                          ),
                      ])
                      .optional(),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe(
                        'The time the document instance \/ document BOM was updated'
                      ),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A document as displayed in Soup.'),
              tag: zod.enum(['document']),
            })
            .describe('Document item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was deleted'),
                    id: zod.uuid().describe('The chat uuid'),
                    isPersistent: zod
                      .boolean()
                      .describe('Whether the chat is persistent or not'),
                    model: zod
                      .string()
                      .nullish()
                      .describe(
                        'The last model selected for a sent message (`provider\/model` id).'
                      ),
                    name: zod.string().describe('The name of the chat'),
                    ownerId: zod.string().describe('Who the chat belongs to'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe('The project id of the chat'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was last updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was last viewed'),
                  })
                )
                .describe('A chat as displayed in Soup.'),
              tag: zod.enum(['chat']),
            })
            .describe('Chat item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the project was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the project was deleted'),
                    id: zod.uuid().describe('The id of the project'),
                    name: zod.string().describe('The name of the project'),
                    ownerId: zod.string().describe('The owner of the project'),
                    parentId: zod
                      .uuid()
                      .nullish()
                      .describe('The parent project id'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the project was updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A project as displayed in Soup.'),
              tag: zod.enum(['project']),
            })
            .describe('Project item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    id: zod.uuid().describe('Initiative identifier.'),
                    name: zod.string().describe('Initiative display name.'),
                    ownerId: zod.string().describe('Initiative owner.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Last time the requesting user viewed the initiative.'
                      ),
                  })
                )
                .describe(
                  'An initiative (called a project in the frontend) in the Soup feed.'
                ),
              tag: zod.enum(['initiative']),
            })
            .describe('Initiative entity.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was created.'),
                  id: zod.uuid().describe('Thread id.'),
                  inboxVisible: zod
                    .boolean()
                    .describe('Whether the thread should appear in the inbox.'),
                  isDraft: zod
                    .boolean()
                    .describe('Whether the thread contains a draft.'),
                  isImportant: zod
                    .boolean()
                    .describe('Whether the thread is marked important.'),
                  isRead: zod
                    .boolean()
                    .describe('Whether the thread has been read.'),
                  isSignal: zod
                    .boolean()
                    .describe(
                      "The denormalized `email_threads.is_signal` importance classification —\nthe same flag the soup Importance filter evaluates, distinct from\n`is_important` (Gmail's IMPORTANT label)."
                    ),
                  name: zod
                    .string()
                    .nullish()
                    .describe('Thread display name or subject.'),
                  ownerId: zod.string().describe('Owner user id.'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe('Project id associated with the thread.'),
                  providerId: zod
                    .string()
                    .nullish()
                    .describe('Provider-specific thread id.'),
                  senderEmail: zod
                    .string()
                    .nullish()
                    .describe('Sender email address for the preview.'),
                  senderName: zod
                    .string()
                    .nullish()
                    .describe('Sender display name for the preview.'),
                  senderPhotoUrl: zod
                    .string()
                    .nullish()
                    .describe('Sender photo URL for the preview.'),
                  snippet: zod.string().nullish().describe('Thread snippet.'),
                  sortTs: zod.iso
                    .datetime({})
                    .describe('Timestamp used for email thread sorting.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was last updated.'),
                  viewedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'Timestamp when the requesting user last viewed the thread.'
                    ),
                })
                .describe('Email thread preview data as displayed in Soup.')
                .and(
                  zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    )
                )
                .and(
                  zod.object({
                    attachments: zod
                      .array(
                        zod
                          .object({
                            contentId: zod
                              .string()
                              .nullish()
                              .describe('Content id for inline attachments.'),
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the attachment was created.'
                              ),
                            filename: zod
                              .string()
                              .nullish()
                              .describe('Attachment filename.'),
                            id: zod.uuid().describe('Attachment id.'),
                            messageId: zod
                              .uuid()
                              .describe(
                                'Message id that owns this attachment.'
                              ),
                            mimeType: zod
                              .string()
                              .nullish()
                              .describe('Attachment MIME type.'),
                            providerAttachmentId: zod
                              .string()
                              .nullish()
                              .describe('Provider-specific attachment id.'),
                            sizeBytes: zod
                              .number()
                              .nullish()
                              .describe('Attachment size in bytes.'),
                          })
                          .describe('An email attachment as displayed in Soup.')
                      )
                      .describe('Attachments on the thread.'),
                    labels: zod
                      .array(
                        zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the label was created.'
                              ),
                            id: zod.uuid().describe('Label id.'),
                            labelListVisibility: zod
                              .enum([
                                'labelShow',
                                'labelShowIfUnread',
                                'labelHide',
                              ])
                              .describe('Gmail-style label list visibility.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the label.'
                              ),
                            messageListVisibility: zod
                              .enum(['show', 'hide'])
                              .describe(
                                'Gmail-style message list visibility for a label.'
                              ),
                            name: zod.string().describe('Label display name.'),
                            providerLabelId: zod
                              .string()
                              .describe('Provider-specific label id.'),
                            type: zod
                              .enum(['system', 'user'])
                              .describe(
                                'Whether an email label is provider-created or user-created.'
                              ),
                          })
                          .describe('An email label as displayed in Soup.')
                      )
                      .describe('Labels attached to the thread.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            emailAddress: zod
                              .string()
                              .nullish()
                              .describe('Contact email address.'),
                            id: zod.uuid().describe('Contact id.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the contact.'
                              ),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Contact display name.'),
                            sfsPhotoUrl: zod
                              .string()
                              .nullish()
                              .describe(
                                'Contact photo URL from SFS, if available.'
                              ),
                          })
                          .describe(
                            'A contact participating in an email thread as displayed in Soup.'
                          )
                      )
                      .describe('Contacts participating in the thread.'),
                  })
                )
                .describe(
                  'Email thread preview enriched with related metadata for Soup.'
                ),
              tag: zod.enum(['emailThread']),
            })
            .describe('Email thread item.'),
          zod
            .object({
              data: zod
                .object({
                  channel: zod
                    .object({
                      channel_type: zod
                        .enum(['public', 'private', 'direct_message', 'team'])
                        .describe('Type of channel.'),
                      created_at: zod.iso
                        .datetime({})
                        .describe('Creation timestamp.'),
                      id: zod.uuid().describe('Channel id.'),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Channel display name.'),
                      org_id: zod
                        .number()
                        .min(postItemsSoupResponseItemsItemDataChannelOrgIdMin)
                        .nullish()
                        .describe('Organization id.'),
                      owner_id: zod.string().describe('Channel owner.'),
                      team_id: zod.uuid().nullish().describe('Team id.'),
                      updated_at: zod.iso
                        .datetime({})
                        .describe('Update timestamp.'),
                    })
                    .describe('Channel metadata in soup payloads.'),
                  is_participant: zod
                    .boolean()
                    .optional()
                    .describe(
                      "Whether the requesting user is an active participant of the channel.\nFalse for team channels of the user's teams they have not joined."
                    ),
                  participants: zod
                    .array(
                      zod
                        .object({
                          channel_id: zod
                            .uuid()
                            .describe(
                              'Channel id for the participant membership.'
                            ),
                          joined_at: zod.iso
                            .datetime({})
                            .describe(
                              'Timestamp when the participant joined the channel.'
                            ),
                          left_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the participant left the channel, if any.'
                            ),
                          role: zod
                            .enum(['owner', 'admin', 'member'])
                            .describe('The role a user has within a channel.'),
                          user_id: zod
                            .string()
                            .describe('Participant user id.'),
                        })
                        .describe("A user's membership in a channel.")
                    )
                    .describe('Participants in the channel.'),
                })
                .describe('Channel metadata together with its participants.')
                .and(
                  zod
                    .object({
                      latest_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                      latest_non_thread_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                    })
                    .describe('Latest-message bundle for soup payloads.')
                )
                .and(
                  zod.object({
                    interacted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last interacted with this channel.'
                      ),
                    viewed_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last viewed this channel.'
                      ),
                  })
                )
                .describe('A channel as displayed in Soup.'),
              tag: zod.enum(['channel']),
            })
            .describe('Channel item.'),
          zod
            .object({
              data: zod
                .object({
                  attachments: zod
                    .array(
                      zod
                        .object({
                          created_at: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          entity_id: zod.string().describe('Entity id.'),
                          entity_type: zod.string().describe('Type of entity.'),
                          height: zod
                            .number()
                            .nullish()
                            .describe('Height for images.'),
                          id: zod.uuid().describe('Attachment id.'),
                          width: zod
                            .number()
                            .nullish()
                            .describe('Width for images.'),
                        })
                        .describe('An attachment on a message.')
                    )
                    .describe('Attachments on this message.'),
                  channel_id: zod.uuid().describe('Channel id.'),
                  content: zod.string().describe('Message content.'),
                  created_at: zod.iso
                    .datetime({})
                    .describe('Creation timestamp.'),
                  deleted_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Deletion timestamp.'),
                  edited_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Edit timestamp.'),
                  id: zod.uuid().describe('Message id.'),
                  reactions: zod
                    .array(
                      zod
                        .object({
                          emoji: zod.string().describe('Emoji string.'),
                          users: zod
                            .array(zod.string())
                            .describe('User ids who added this reaction.'),
                        })
                        .describe('A reaction with emoji and user list.')
                    )
                    .describe('Reactions on this message.'),
                  sender: zod
                    .object({
                      avatar_url: zod
                        .string()
                        .nullish()
                        .describe('Avatar URL for bot senders.'),
                      id: zod
                        .string()
                        .describe(
                          'Sender id without the storage namespace prefix.'
                        ),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Display name for bot senders.'),
                      type: zod
                        .enum(['user', 'bot'])
                        .describe(
                          'Public sender type for soup channel messages.'
                        ),
                    })
                    .describe(
                      'Public sender identity for soup channel messages.'
                    ),
                  sender_id: zod.string().describe('Sender id.'),
                  thread: zod
                    .object({
                      latest_reply_at: zod.iso
                        .datetime({})
                        .nullish()
                        .describe('Timestamp of the latest reply.'),
                      preview: zod
                        .array(
                          zod
                            .object({
                              attachments: zod
                                .array(
                                  zod
                                    .object({
                                      created_at: zod.iso
                                        .datetime({})
                                        .describe('Creation timestamp.'),
                                      entity_id: zod
                                        .string()
                                        .describe('Entity id.'),
                                      entity_type: zod
                                        .string()
                                        .describe('Type of entity.'),
                                      height: zod
                                        .number()
                                        .nullish()
                                        .describe('Height for images.'),
                                      id: zod.uuid().describe('Attachment id.'),
                                      width: zod
                                        .number()
                                        .nullish()
                                        .describe('Width for images.'),
                                    })
                                    .describe('An attachment on a message.')
                                )
                                .describe('Attachments on this reply.'),
                              content: zod.string().describe('Reply content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              edited_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Edit timestamp.'),
                              id: zod.uuid().describe('Reply id.'),
                              reactions: zod
                                .array(
                                  zod
                                    .object({
                                      emoji: zod
                                        .string()
                                        .describe('Emoji string.'),
                                      users: zod
                                        .array(zod.string())
                                        .describe(
                                          'User ids who added this reaction.'
                                        ),
                                    })
                                    .describe(
                                      'A reaction with emoji and user list.'
                                    )
                                )
                                .describe('Reactions on this reply.'),
                              sender: zod
                                .object({
                                  avatar_url: zod
                                    .string()
                                    .nullish()
                                    .describe('Avatar URL for bot senders.'),
                                  id: zod
                                    .string()
                                    .describe(
                                      'Sender id without the storage namespace prefix.'
                                    ),
                                  name: zod
                                    .string()
                                    .nullish()
                                    .describe('Display name for bot senders.'),
                                  type: zod
                                    .enum(['user', 'bot'])
                                    .describe(
                                      'Public sender type for soup channel messages.'
                                    ),
                                })
                                .describe(
                                  'Public sender identity for soup channel messages.'
                                ),
                              sender_id: zod.string().describe('Sender id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe('A thread reply shown in preview.')
                        )
                        .describe('Preview replies.'),
                      reply_count: zod.number().describe('Total reply count.'),
                    })
                    .describe(
                      'Thread metadata and preview replies for soup channel messages.'
                    ),
                  updated_at: zod.iso
                    .datetime({})
                    .describe('Update timestamp.'),
                })
                .describe(
                  'A top-level channel message thread for soup payloads.\n\nThis mirrors the public channel-message API shape so soup consumers can\nrender a thread root the same way they render a channel timeline message.'
                ),
              tag: zod.enum(['channelThread']),
            })
            .describe('Channel thread item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    attended: zod
                      .boolean()
                      .describe(
                        'Whether the requesting user attended this call. Kept for compatibility\nand derived from `status == ATTENDED`.'
                      ),
                    callId: zod.uuid().describe('The call identifier.'),
                    channelId: zod
                      .uuid()
                      .nullish()
                      .describe('The channel this call belongs to.'),
                    channelName: zod
                      .string()
                      .nullish()
                      .describe('Resolved display name for the channel.'),
                    createdBy: zod
                      .string()
                      .describe('User who created the call.'),
                    customName: zod
                      .string()
                      .nullish()
                      .describe(
                        'User-supplied or AI-generated display name for the call.'
                      ),
                    durationMs: zod
                      .number()
                      .nullish()
                      .describe(
                        'Call duration in milliseconds (None if still active).'
                      ),
                    endedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('When the call ended (None if still active).'),
                    guests: zod
                      .array(
                        zod
                          .object({
                            displayName: zod
                              .string()
                              .describe('Guest-provided display name.'),
                            id: zod
                              .uuid()
                              .describe(
                                "Opaque guest identity; matches the guest's transcript speaker id."
                              ),
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the guest joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the guest left (None if still in an active call).'
                              ),
                          })
                          .describe(
                            'A non-account guest of a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Non-account guests in the call.'),
                    isActive: zod
                      .boolean()
                      .describe('Whether the call is currently active.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the user joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the user left (None if still in an active call).'
                              ),
                            userId: zod.string().describe('The Macro user id.'),
                          })
                          .describe(
                            'A Macro-account participant in a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Macro-account participants in the call.'),
                    startedAt: zod.iso
                      .datetime({})
                      .describe('When the call started.'),
                    status: zod
                      .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
                      .describe(
                        'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
                      ),
                    summary: zod
                      .string()
                      .nullish()
                      .describe(
                        'AI-generated summary of the call. Only set on archived\n`call_records` once summarization has run; active calls always\nreturn `None`.'
                      ),
                  })
                )
                .describe(
                  'A call record as displayed in Soup. Excludes room_name, egress_id,\nand transcript — fields that are irrelevant for the soup feed.'
                ),
              tag: zod.enum(['call']),
            })
            .describe('Call record item.'),
          zod
            .object({
              data: zod
                .object({
                  conferenceProvider: zod
                    .string()
                    .nullish()
                    .describe(
                      'Which conferencing system backs `conference_url`.'
                    ),
                  conferenceUrl: zod
                    .string()
                    .nullish()
                    .describe('Direct conference join URL.'),
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Entity creation timestamp.'),
                  description: zod
                    .string()
                    .nullish()
                    .describe('Optional description.'),
                  extra: zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    ),
                  icalUid: zod
                    .string()
                    .describe('RFC 5545 UID used for source reconciliation.'),
                  id: zod.uuid().describe('Entity identifier.'),
                  isReadOnly: zod
                    .boolean()
                    .describe(
                      'Whether the selected canonical source is read-only.'
                    ),
                  lastReminderFiredAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      "When this event's most recent reminder notification was delivered."
                    ),
                  location: zod
                    .string()
                    .nullish()
                    .describe('Optional location label.'),
                  organizerEmail: zod
                    .string()
                    .nullish()
                    .describe('Organizer email.'),
                  organizerName: zod
                    .string()
                    .nullish()
                    .describe('Organizer display name.'),
                  ownerId: zod.string().describe('Owning Macro user.'),
                  status: zod.string().describe('Canonical status.'),
                  time: zod
                    .union([
                      zod
                        .object({
                          endsAt: zod.iso
                            .datetime({})
                            .describe('Exclusive end.'),
                          kind: zod.enum(['timed']),
                          startsAt: zod.iso
                            .datetime({})
                            .describe('Inclusive start.'),
                          timeZone: zod
                            .string()
                            .nullish()
                            .describe('Original IANA time zone.'),
                        })
                        .describe('Absolute timed event.'),
                      zod
                        .object({
                          endDate: zod.iso
                            .date()
                            .describe('Exclusive end date.'),
                          kind: zod.enum(['allDay']),
                          startDate: zod.iso
                            .date()
                            .describe('Inclusive start date.'),
                        })
                        .describe('All-day event with an exclusive end date.'),
                    ])
                    .describe(
                      'Timed or all-day calendar event span.\n\nFields are camelCased per variant rather than via `rename_all_fields`,\nwhich utoipa ignores — the generated OpenAPI schema would otherwise\nclaim snake_case fields the wire never carries.'
                    ),
                  title: zod.string().describe('Display title.'),
                  transparency: zod
                    .string()
                    .describe('Availability transparency.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Entity update timestamp.'),
                  visibility: zod.string().describe('Canonical visibility.'),
                })
                .describe('A canonical calendar event entity in Soup.'),
              tag: zod.enum(['calendarEvent']),
            })
            .describe('Calendar event item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('When the company was created.'),
                    description: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display description from the primary domain's directory entry."
                      ),
                    domains: zod
                      .array(
                        zod
                          .object({
                            companyId: zod
                              .uuid()
                              .describe(
                                'The id of the company the domain belongs to.'
                              ),
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When the domain record was created.'),
                            domain: zod
                              .string()
                              .describe(
                                'The domain (lowercased, e.g. \"acme.com\").'
                              ),
                            id: zod
                              .uuid()
                              .describe('The id of the domain record.'),
                          })
                          .describe(
                            "A CRM domain as displayed in Soup. Mirrors the crm crate's\n[`CrmDomain`] with a stable wire shape that the FE can rely on."
                          )
                      )
                      .describe(
                        'Domains associated with this company, ordered by creation time\nascending (primary first).'
                      ),
                    emailSync: zod
                      .boolean()
                      .describe(
                        'Whether email sync is enabled for this company.'
                      ),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the company is hidden from CRM listings. Soup filters\nthese out by default.'
                      ),
                    id: zod.uuid().describe('The id of the company.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display name from the primary domain's directory entry, or\n`None` when unresolved."
                      ),
                    teamId: zod
                      .uuid()
                      .describe(
                        'The id of the team that owns this company record.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('When the company was last updated.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'When the requesting user last viewed this company, or `None` if\nnever viewed. Mirrors the `viewed_at` other soup entities carry.'
                      ),
                  })
                )
                .describe(
                  'A CRM company as displayed in Soup. Carries the core company\nfields plus display metadata resolved from `crm_domain_directory`\nagainst the primary (earliest-created) domain.'
                ),
              tag: zod.enum(['crmCompany']),
            })
            .describe('CRM company item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    companyId: zod.uuid().describe('Parent company ID.'),
                    companyName: zod
                      .string()
                      .describe("Parent company's display name."),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Record creation time.'),
                    email: zod.string().describe('Full email address.'),
                    firstInteraction: zod.iso
                      .datetime({})
                      .describe('Earliest interaction for this team record.'),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the contact or parent company is hidden.'
                      ),
                    id: zod.uuid().describe('Original team-owned contact ID.'),
                    lastInteraction: zod.iso
                      .datetime({})
                      .describe('Latest interaction for this team record.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe('Team-local display name, if known.'),
                    teamId: zod.uuid().describe('Team owning this record.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Record update time.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe("The viewer's latest visit to this record."),
                  })
                )
                .describe(
                  "One original CRM contact record, selected from the viewer's accessible teams."
                ),
              tag: zod.enum(['crmContact']),
            })
            .describe('Team-owned CRM contact.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was created.'),
                  foreignEntityId: zod
                    .string()
                    .describe('Identifier assigned by the external system.'),
                  foreignEntitySource: zod
                    .string()
                    .describe(
                      'Source system that owns the external identifier.'
                    ),
                  id: zod
                    .uuid()
                    .describe(
                      'Internal primary key for this foreign entity record.'
                    ),
                  metadata: zod
                    .object({})
                    .describe('Arbitrary metadata stored with the mapping.'),
                  storedForAuthEntity: zod
                    .string()
                    .describe(
                      'Internal auth entity namespace this foreign entity is stored for.'
                    ),
                  storedForId: zod
                    .string()
                    .describe(
                      'Internal entity identifier this foreign entity is stored for.'
                    ),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was last updated.'),
                })
                .describe('A foreign entity record as displayed in Soup.'),
              tag: zod.enum(['foreignEntity']),
            })
            .describe('Foreign entity item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    botId: zod.uuid().describe('The bot running this session'),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the session was created'),
                    harness: zod
                      .string()
                      .describe(
                        'The runtime snapshotted when the session was created.'
                      ),
                    id: zod.uuid().describe('The agent session uuid'),
                    isArchived: zod
                      .boolean()
                      .describe(
                        'Whether the session is archived and read-only.'
                      ),
                    name: zod
                      .string()
                      .describe('The user-facing name of the session'),
                    ownerId: zod
                      .string()
                      .describe('Who the session belongs to'),
                    pullRequestId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        "The linked pull request's Macro entity, when visible to the viewer."
                      ),
                    pullRequestState: zod
                      .union([
                        zod.null(),
                        zod
                          .enum(['open', 'draft', 'closed', 'merged'])
                          .describe(
                            "Last synchronized state of a session's linked GitHub pull request."
                          ),
                      ])
                      .optional(),
                    pullRequestUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The persisted pull request associated with the session.'
                      ),
                    repoBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'The starting branch selected for this session, not its current branch.'
                      ),
                    repoUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The repository the session works with, when one was selected.'
                      ),
                    status: zod
                      .string()
                      .describe(
                        "The session's last known status.\n\n`no_messages` until the first system event arrives, `disconnected` if\nthe connection dropped without a clean close, otherwise the wire name\nof the most recent system event (for example `session\/end`)."
                      ),
                    threadId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The channel thread the session was opened from, when any'
                      ),
                    turnState: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last persisted fold turn state. Absent until an older session next runs.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the session was last modified'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'The time the session was last viewed by the requesting user'
                      ),
                    workingBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last captured working branch, when the runtime has reported one.'
                      ),
                  })
                )
                .describe(
                  'An agent session as displayed in Soup.\n\nIncludes the persisted runtime and repository metadata needed to render\ncoding and non-coding sessions without fetching each session separately.'
                ),
              tag: zod.enum(['agentSession']),
            })
            .describe('Agent session item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    createdBy: zod
                      .string()
                      .nullish()
                      .describe('Who created the row, when they still exist.'),
                    databaseId: zod
                      .uuid()
                      .describe('The database the table belongs to.'),
                    id: zod.uuid().describe('Row identifier.'),
                    ownerId: zod
                      .string()
                      .describe(
                        "The database's owner, which owns every row in it."
                      ),
                    position: zod
                      .string()
                      .describe(
                        'Fractional index ordering the row within its table.'
                      ),
                    tableId: zod
                      .uuid()
                      .describe('The table the row belongs to.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                  })
                )
                .describe(
                  "A row of a Macro database table in the Soup feed. Its cells are its\nentity properties; access is its database's."
                ),
              tag: zod.enum(['databaseRow']),
            })
            .describe('Database row item.'),
        ])
        .describe('A single item in the Soup feed.')
        .and(
          zod.object({
            frecency_score: zod.number(),
            is_favorited: zod
              .boolean()
              .describe(
                'Whether the requesting user has favorited this entity.'
              ),
            notified_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the caller was last notified about this entity, present only\nwhen the page was ordered by `notified_at`. Clients keep the notified\nfeed ordered and date-bucketed on this value.'
              ),
            touched_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                "The caller's latest own mutation of this entity, present only when the\npage was ordered by `touched_by_me`. Clients keep the touched feed\nordered on this value, so it can be bumped optimistically."
              ),
          })
        )
        .describe(
          'API representation of a soup item with its per-viewer enrichments.'
        )
    ),
    next_cursor: zod.string().nullish(),
  })
  .describe('Response page for non-grouped soup endpoints.');

/**
 * @summary Gets the items the user has access to using AST filters
 */
export const postItemsSoupAstQueryParams = zod.object({
  cursor: zod.string().optional().describe('Base64 encoded cursor value.'),
});

export const postItemsSoupAstBodyLimitMin = 0;

export const postItemsSoupAstBody = zod
  .object({
    asf: zod
      .unknown()
      .optional()
      .describe(
        'Filters applied to agent sessions (wire key `asf`). An empty or\nomitted filter returns \*\*no\*\* agent sessions: they are opt-in, so the\ncaller must send `inc`, an id, or an owner to get any.'
      ),
    calf: zod
      .unknown()
      .optional()
      .describe('filters applied to canonical calendar events'),
    callf: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to the call entity'),
    ccf: zod
      .unknown()
      .optional()
      .describe(
        "Filters applied to the crm_company entity (wire key `ccf`).\nEmpty\/omitted = team's full visible list."
      ),
    cf: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to the chat entity'),
    chanf: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to the channel entity'),
    crma: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                company: zod
                  .uuid()
                  .describe(
                    'Items associated with the company or with any of its contacts.'
                  ),
              })
              .describe(
                'Items associated with the company or with any of its contacts.'
              ),
            zod
              .object({
                contact: zod
                  .uuid()
                  .describe('Items associated with the contact.'),
              })
              .describe('Items associated with the contact.'),
          ])
          .describe('The CRM record an association filter names.'),
      ])
      .optional(),
    crmf: zod
      .unknown()
      .optional()
      .describe(
        'Opt-in filters for viewer-accessible CRM contacts (wire key `crmf`).'
      ),
    cthf: zod
      .unknown()
      .optional()
      .describe(
        'the filters that should be applied to the channel-thread entity'
      ),
    df: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to the document entity'),
    eca: zod
      .array(zod.string())
      .optional()
      .describe(
        'CRM-scoped address filter (wire key: `eca`). Symmetric counterpart\nto `ecd` for fully-qualified email addresses.'
      ),
    ecd: zod
      .array(zod.string())
      .optional()
      .describe(
        'CRM-scoped domain filter (wire key: `ecd`). Parallel to the\nfreeform `ef` AST. Expanded by the router into an any-direction\nOR sub-tree AND-merged into `ef`, plus a `CrmScope` tag stamped\non the resulting [`item_filters::ast::EmailFilterAst::crm_scope`].\nDrives the per-team CRM authorization pre-check and candidate-set\nwidening downstream. Mutually exclusive with `eca`.'
      ),
    ef: zod
      .unknown()
      .optional()
      .describe(
        'the filters that should be applied to the email entity (raw AST\ntree only; CRM scope is carried by the `ecd` \/ `eca` sibling\nfields). On this endpoint the email filter stays a bare tree,\nunlike the materialized [`EntityFilterAst`] used for cursors.'
      ),
    favorites_only: zod
      .boolean()
      .nullish()
      .describe(
        "Restrict to the authenticated viewer's favorites before pagination when true."
      ),
    fef: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to foreign entity records'),
    ghprf: zod
      .unknown()
      .optional()
      .describe(
        'the filters that should be applied to GitHub pull request records, on top of `fef`'
      ),
    pf: zod
      .unknown()
      .optional()
      .describe('the filters that should be applied to the project entity'),
    propf: zod
      .unknown()
      .optional()
      .describe(
        'the filters that should be applied based on entity properties'
      ),
  })
  .describe('Wire-format entity filter AST accepted by soup AST endpoints.')
  .and(
    zod
      .object({
        expand: zod
          .boolean()
          .nullish()
          .describe('Whether to expand projects. Defaults to true.'),
        limit: zod
          .number()
          .min(postItemsSoupAstBodyLimitMin)
          .nullish()
          .describe(
            'Limit the number of items returned. Defaults to 20. Max 500.'
          ),
        sort_direction: zod
          .union([
            zod.null(),
            zod
              .enum(['asc', 'desc'])
              .describe(
                'Sort direction accepted by non-grouped soup API endpoints.'
              ),
          ])
          .optional(),
        sort_method: zod
          .union([
            zod.null(),
            zod
              .enum([
                'viewed_at',
                'created_at',
                'updated_at',
                'viewed_updated',
                'frecency',
                'touched_by_me',
                'notified_at',
              ])
              .describe(
                'Sort options accepted by non-grouped soup API endpoints.'
              ),
          ])
          .optional(),
      })
      .describe('Query parameters shared by soup endpoints.')
  )
  .and(
    zod.object({
      emailView: zod
        .string()
        .optional()
        .describe('the view of specific emails to display'),
    })
  )
  .describe('Request body for the AST soup endpoint.');

export const postItemsSoupAstResponseItemsItemDataChannelOrgIdMin = 0;

export const postItemsSoupAstResponse = zod
  .object({
    items: zod.array(
      zod
        .union([
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    branchedFromId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the document this document branched from'
                      ),
                    branchedFromVersionId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on the file type'
                      ),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the document was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was deleted'),
                    documentFamilyId: zod
                      .number()
                      .nullish()
                      .describe(
                        'The id of the document family this document belongs to'
                      ),
                    documentVersionId: zod
                      .number()
                      .describe(
                        'The version of the document\nThis could be the document_instance_id or document_bom_id depending on the file type'
                      ),
                    fileType: zod
                      .string()
                      .nullish()
                      .describe(
                        'The file type of the document (e.g. pdf, docx)'
                      ),
                    id: zod.uuid().describe('The document id'),
                    name: zod.string().describe('The name of the document'),
                    ownerId: zod.string().describe('The owner of the document'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The id of the project that this document belongs to'
                      ),
                    sha: zod
                      .string()
                      .nullish()
                      .describe(
                        'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                      ),
                    subType: zod
                      .union([
                        zod.null(),
                        zod
                          .union([
                            zod
                              .object({
                                is_completed: zod
                                  .boolean()
                                  .describe(
                                    'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                  ),
                                type: zod.enum(['task']),
                              })
                              .describe(
                                'A task document with its associated properties'
                              ),
                            zod
                              .object({
                                type: zod.enum(['snippet']),
                              })
                              .describe(
                                'A snippet document — reusable markdown'
                              ),
                            zod
                              .object({
                                type: zod.enum(['skill']),
                              })
                              .describe(
                                'A skill document — markdown instructions for AI'
                              ),
                            zod
                              .object({
                                type: zod.enum(['initiative_description']),
                              })
                              .describe(
                                'The description document of an initiative'
                              ),
                          ])
                          .describe(
                            'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                          ),
                      ])
                      .optional(),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe(
                        'The time the document instance \/ document BOM was updated'
                      ),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A document as displayed in Soup.'),
              tag: zod.enum(['document']),
            })
            .describe('Document item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was deleted'),
                    id: zod.uuid().describe('The chat uuid'),
                    isPersistent: zod
                      .boolean()
                      .describe('Whether the chat is persistent or not'),
                    model: zod
                      .string()
                      .nullish()
                      .describe(
                        'The last model selected for a sent message (`provider\/model` id).'
                      ),
                    name: zod.string().describe('The name of the chat'),
                    ownerId: zod.string().describe('Who the chat belongs to'),
                    projectId: zod
                      .uuid()
                      .nullish()
                      .describe('The project id of the chat'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the chat was last updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the chat was last viewed'),
                  })
                )
                .describe('A chat as displayed in Soup.'),
              tag: zod.enum(['chat']),
            })
            .describe('Chat item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the project was created'),
                    deletedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the project was deleted'),
                    id: zod.uuid().describe('The id of the project'),
                    name: zod.string().describe('The name of the project'),
                    ownerId: zod.string().describe('The owner of the project'),
                    parentId: zod
                      .uuid()
                      .nullish()
                      .describe('The parent project id'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the project was updated'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('The time the document was last viewed'),
                  })
                )
                .describe('A project as displayed in Soup.'),
              tag: zod.enum(['project']),
            })
            .describe('Project item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    id: zod.uuid().describe('Initiative identifier.'),
                    name: zod.string().describe('Initiative display name.'),
                    ownerId: zod.string().describe('Initiative owner.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Last time the requesting user viewed the initiative.'
                      ),
                  })
                )
                .describe(
                  'An initiative (called a project in the frontend) in the Soup feed.'
                ),
              tag: zod.enum(['initiative']),
            })
            .describe('Initiative entity.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was created.'),
                  id: zod.uuid().describe('Thread id.'),
                  inboxVisible: zod
                    .boolean()
                    .describe('Whether the thread should appear in the inbox.'),
                  isDraft: zod
                    .boolean()
                    .describe('Whether the thread contains a draft.'),
                  isImportant: zod
                    .boolean()
                    .describe('Whether the thread is marked important.'),
                  isRead: zod
                    .boolean()
                    .describe('Whether the thread has been read.'),
                  isSignal: zod
                    .boolean()
                    .describe(
                      "The denormalized `email_threads.is_signal` importance classification —\nthe same flag the soup Importance filter evaluates, distinct from\n`is_important` (Gmail's IMPORTANT label)."
                    ),
                  name: zod
                    .string()
                    .nullish()
                    .describe('Thread display name or subject.'),
                  ownerId: zod.string().describe('Owner user id.'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe('Project id associated with the thread.'),
                  providerId: zod
                    .string()
                    .nullish()
                    .describe('Provider-specific thread id.'),
                  senderEmail: zod
                    .string()
                    .nullish()
                    .describe('Sender email address for the preview.'),
                  senderName: zod
                    .string()
                    .nullish()
                    .describe('Sender display name for the preview.'),
                  senderPhotoUrl: zod
                    .string()
                    .nullish()
                    .describe('Sender photo URL for the preview.'),
                  snippet: zod.string().nullish().describe('Thread snippet.'),
                  sortTs: zod.iso
                    .datetime({})
                    .describe('Timestamp used for email thread sorting.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the thread was last updated.'),
                  viewedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'Timestamp when the requesting user last viewed the thread.'
                    ),
                })
                .describe('Email thread preview data as displayed in Soup.')
                .and(
                  zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    )
                )
                .and(
                  zod.object({
                    attachments: zod
                      .array(
                        zod
                          .object({
                            contentId: zod
                              .string()
                              .nullish()
                              .describe('Content id for inline attachments.'),
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the attachment was created.'
                              ),
                            filename: zod
                              .string()
                              .nullish()
                              .describe('Attachment filename.'),
                            id: zod.uuid().describe('Attachment id.'),
                            messageId: zod
                              .uuid()
                              .describe(
                                'Message id that owns this attachment.'
                              ),
                            mimeType: zod
                              .string()
                              .nullish()
                              .describe('Attachment MIME type.'),
                            providerAttachmentId: zod
                              .string()
                              .nullish()
                              .describe('Provider-specific attachment id.'),
                            sizeBytes: zod
                              .number()
                              .nullish()
                              .describe('Attachment size in bytes.'),
                          })
                          .describe('An email attachment as displayed in Soup.')
                      )
                      .describe('Attachments on the thread.'),
                    labels: zod
                      .array(
                        zod
                          .object({
                            createdAt: zod.iso
                              .datetime({})
                              .describe(
                                'Timestamp when the label was created.'
                              ),
                            id: zod.uuid().describe('Label id.'),
                            labelListVisibility: zod
                              .enum([
                                'labelShow',
                                'labelShowIfUnread',
                                'labelHide',
                              ])
                              .describe('Gmail-style label list visibility.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the label.'
                              ),
                            messageListVisibility: zod
                              .enum(['show', 'hide'])
                              .describe(
                                'Gmail-style message list visibility for a label.'
                              ),
                            name: zod.string().describe('Label display name.'),
                            providerLabelId: zod
                              .string()
                              .describe('Provider-specific label id.'),
                            type: zod
                              .enum(['system', 'user'])
                              .describe(
                                'Whether an email label is provider-created or user-created.'
                              ),
                          })
                          .describe('An email label as displayed in Soup.')
                      )
                      .describe('Labels attached to the thread.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            emailAddress: zod
                              .string()
                              .nullish()
                              .describe('Contact email address.'),
                            id: zod.uuid().describe('Contact id.'),
                            linkId: zod
                              .uuid()
                              .describe(
                                'Connected account link id for the contact.'
                              ),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Contact display name.'),
                            sfsPhotoUrl: zod
                              .string()
                              .nullish()
                              .describe(
                                'Contact photo URL from SFS, if available.'
                              ),
                          })
                          .describe(
                            'A contact participating in an email thread as displayed in Soup.'
                          )
                      )
                      .describe('Contacts participating in the thread.'),
                  })
                )
                .describe(
                  'Email thread preview enriched with related metadata for Soup.'
                ),
              tag: zod.enum(['emailThread']),
            })
            .describe('Email thread item.'),
          zod
            .object({
              data: zod
                .object({
                  channel: zod
                    .object({
                      channel_type: zod
                        .enum(['public', 'private', 'direct_message', 'team'])
                        .describe('Type of channel.'),
                      created_at: zod.iso
                        .datetime({})
                        .describe('Creation timestamp.'),
                      id: zod.uuid().describe('Channel id.'),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Channel display name.'),
                      org_id: zod
                        .number()
                        .min(
                          postItemsSoupAstResponseItemsItemDataChannelOrgIdMin
                        )
                        .nullish()
                        .describe('Organization id.'),
                      owner_id: zod.string().describe('Channel owner.'),
                      team_id: zod.uuid().nullish().describe('Team id.'),
                      updated_at: zod.iso
                        .datetime({})
                        .describe('Update timestamp.'),
                    })
                    .describe('Channel metadata in soup payloads.'),
                  is_participant: zod
                    .boolean()
                    .optional()
                    .describe(
                      "Whether the requesting user is an active participant of the channel.\nFalse for team channels of the user's teams they have not joined."
                    ),
                  participants: zod
                    .array(
                      zod
                        .object({
                          channel_id: zod
                            .uuid()
                            .describe(
                              'Channel id for the participant membership.'
                            ),
                          joined_at: zod.iso
                            .datetime({})
                            .describe(
                              'Timestamp when the participant joined the channel.'
                            ),
                          left_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the participant left the channel, if any.'
                            ),
                          role: zod
                            .enum(['owner', 'admin', 'member'])
                            .describe('The role a user has within a channel.'),
                          user_id: zod
                            .string()
                            .describe('Participant user id.'),
                        })
                        .describe("A user's membership in a channel.")
                    )
                    .describe('Participants in the channel.'),
                })
                .describe('Channel metadata together with its participants.')
                .and(
                  zod
                    .object({
                      latest_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                      latest_non_thread_message: zod
                        .union([
                          zod.null(),
                          zod
                            .object({
                              content: zod
                                .string()
                                .describe('Message content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              deleted_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Deletion timestamp.'),
                              mentions: zod
                                .array(zod.string())
                                .describe(
                                  'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                ),
                              message_id: zod.uuid().describe('Message id.'),
                              sender_id: zod.string().describe('Sender id.'),
                              thread_id: zod
                                .uuid()
                                .nullish()
                                .describe('Thread parent id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe(
                              'Lightweight channel message for soup payloads.'
                            ),
                        ])
                        .optional(),
                    })
                    .describe('Latest-message bundle for soup payloads.')
                )
                .and(
                  zod.object({
                    interacted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last interacted with this channel.'
                      ),
                    viewed_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Timestamp when the requesting user last viewed this channel.'
                      ),
                  })
                )
                .describe('A channel as displayed in Soup.'),
              tag: zod.enum(['channel']),
            })
            .describe('Channel item.'),
          zod
            .object({
              data: zod
                .object({
                  attachments: zod
                    .array(
                      zod
                        .object({
                          created_at: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          entity_id: zod.string().describe('Entity id.'),
                          entity_type: zod.string().describe('Type of entity.'),
                          height: zod
                            .number()
                            .nullish()
                            .describe('Height for images.'),
                          id: zod.uuid().describe('Attachment id.'),
                          width: zod
                            .number()
                            .nullish()
                            .describe('Width for images.'),
                        })
                        .describe('An attachment on a message.')
                    )
                    .describe('Attachments on this message.'),
                  channel_id: zod.uuid().describe('Channel id.'),
                  content: zod.string().describe('Message content.'),
                  created_at: zod.iso
                    .datetime({})
                    .describe('Creation timestamp.'),
                  deleted_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Deletion timestamp.'),
                  edited_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Edit timestamp.'),
                  id: zod.uuid().describe('Message id.'),
                  reactions: zod
                    .array(
                      zod
                        .object({
                          emoji: zod.string().describe('Emoji string.'),
                          users: zod
                            .array(zod.string())
                            .describe('User ids who added this reaction.'),
                        })
                        .describe('A reaction with emoji and user list.')
                    )
                    .describe('Reactions on this message.'),
                  sender: zod
                    .object({
                      avatar_url: zod
                        .string()
                        .nullish()
                        .describe('Avatar URL for bot senders.'),
                      id: zod
                        .string()
                        .describe(
                          'Sender id without the storage namespace prefix.'
                        ),
                      name: zod
                        .string()
                        .nullish()
                        .describe('Display name for bot senders.'),
                      type: zod
                        .enum(['user', 'bot'])
                        .describe(
                          'Public sender type for soup channel messages.'
                        ),
                    })
                    .describe(
                      'Public sender identity for soup channel messages.'
                    ),
                  sender_id: zod.string().describe('Sender id.'),
                  thread: zod
                    .object({
                      latest_reply_at: zod.iso
                        .datetime({})
                        .nullish()
                        .describe('Timestamp of the latest reply.'),
                      preview: zod
                        .array(
                          zod
                            .object({
                              attachments: zod
                                .array(
                                  zod
                                    .object({
                                      created_at: zod.iso
                                        .datetime({})
                                        .describe('Creation timestamp.'),
                                      entity_id: zod
                                        .string()
                                        .describe('Entity id.'),
                                      entity_type: zod
                                        .string()
                                        .describe('Type of entity.'),
                                      height: zod
                                        .number()
                                        .nullish()
                                        .describe('Height for images.'),
                                      id: zod.uuid().describe('Attachment id.'),
                                      width: zod
                                        .number()
                                        .nullish()
                                        .describe('Width for images.'),
                                    })
                                    .describe('An attachment on a message.')
                                )
                                .describe('Attachments on this reply.'),
                              content: zod.string().describe('Reply content.'),
                              created_at: zod.iso
                                .datetime({})
                                .describe('Creation timestamp.'),
                              edited_at: zod.iso
                                .datetime({})
                                .nullish()
                                .describe('Edit timestamp.'),
                              id: zod.uuid().describe('Reply id.'),
                              reactions: zod
                                .array(
                                  zod
                                    .object({
                                      emoji: zod
                                        .string()
                                        .describe('Emoji string.'),
                                      users: zod
                                        .array(zod.string())
                                        .describe(
                                          'User ids who added this reaction.'
                                        ),
                                    })
                                    .describe(
                                      'A reaction with emoji and user list.'
                                    )
                                )
                                .describe('Reactions on this reply.'),
                              sender: zod
                                .object({
                                  avatar_url: zod
                                    .string()
                                    .nullish()
                                    .describe('Avatar URL for bot senders.'),
                                  id: zod
                                    .string()
                                    .describe(
                                      'Sender id without the storage namespace prefix.'
                                    ),
                                  name: zod
                                    .string()
                                    .nullish()
                                    .describe('Display name for bot senders.'),
                                  type: zod
                                    .enum(['user', 'bot'])
                                    .describe(
                                      'Public sender type for soup channel messages.'
                                    ),
                                })
                                .describe(
                                  'Public sender identity for soup channel messages.'
                                ),
                              sender_id: zod.string().describe('Sender id.'),
                              updated_at: zod.iso
                                .datetime({})
                                .describe('Update timestamp.'),
                            })
                            .describe('A thread reply shown in preview.')
                        )
                        .describe('Preview replies.'),
                      reply_count: zod.number().describe('Total reply count.'),
                    })
                    .describe(
                      'Thread metadata and preview replies for soup channel messages.'
                    ),
                  updated_at: zod.iso
                    .datetime({})
                    .describe('Update timestamp.'),
                })
                .describe(
                  'A top-level channel message thread for soup payloads.\n\nThis mirrors the public channel-message API shape so soup consumers can\nrender a thread root the same way they render a channel timeline message.'
                ),
              tag: zod.enum(['channelThread']),
            })
            .describe('Channel thread item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    attended: zod
                      .boolean()
                      .describe(
                        'Whether the requesting user attended this call. Kept for compatibility\nand derived from `status == ATTENDED`.'
                      ),
                    callId: zod.uuid().describe('The call identifier.'),
                    channelId: zod
                      .uuid()
                      .nullish()
                      .describe('The channel this call belongs to.'),
                    channelName: zod
                      .string()
                      .nullish()
                      .describe('Resolved display name for the channel.'),
                    createdBy: zod
                      .string()
                      .describe('User who created the call.'),
                    customName: zod
                      .string()
                      .nullish()
                      .describe(
                        'User-supplied or AI-generated display name for the call.'
                      ),
                    durationMs: zod
                      .number()
                      .nullish()
                      .describe(
                        'Call duration in milliseconds (None if still active).'
                      ),
                    endedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('When the call ended (None if still active).'),
                    guests: zod
                      .array(
                        zod
                          .object({
                            displayName: zod
                              .string()
                              .describe('Guest-provided display name.'),
                            id: zod
                              .uuid()
                              .describe(
                                "Opaque guest identity; matches the guest's transcript speaker id."
                              ),
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the guest joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the guest left (None if still in an active call).'
                              ),
                          })
                          .describe(
                            'A non-account guest of a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Non-account guests in the call.'),
                    isActive: zod
                      .boolean()
                      .describe('Whether the call is currently active.'),
                    participants: zod
                      .array(
                        zod
                          .object({
                            joinedAt: zod.iso
                              .datetime({})
                              .describe('When the user joined the call.'),
                            leftAt: zod.iso
                              .datetime({})
                              .nullish()
                              .describe(
                                'When the user left (None if still in an active call).'
                              ),
                            userId: zod.string().describe('The Macro user id.'),
                          })
                          .describe(
                            'A Macro-account participant in a call record, as displayed in Soup.'
                          )
                      )
                      .describe('Macro-account participants in the call.'),
                    startedAt: zod.iso
                      .datetime({})
                      .describe('When the call started.'),
                    status: zod
                      .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
                      .describe(
                        'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
                      ),
                    summary: zod
                      .string()
                      .nullish()
                      .describe(
                        'AI-generated summary of the call. Only set on archived\n`call_records` once summarization has run; active calls always\nreturn `None`.'
                      ),
                  })
                )
                .describe(
                  'A call record as displayed in Soup. Excludes room_name, egress_id,\nand transcript — fields that are irrelevant for the soup feed.'
                ),
              tag: zod.enum(['call']),
            })
            .describe('Call record item.'),
          zod
            .object({
              data: zod
                .object({
                  conferenceProvider: zod
                    .string()
                    .nullish()
                    .describe(
                      'Which conferencing system backs `conference_url`.'
                    ),
                  conferenceUrl: zod
                    .string()
                    .nullish()
                    .describe('Direct conference join URL.'),
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Entity creation timestamp.'),
                  description: zod
                    .string()
                    .nullish()
                    .describe('Optional description.'),
                  extra: zod
                    .object({
                      properties: zod
                        .array(
                          zod
                            .object({
                              definition: zod
                                .object({
                                  created_at: zod.iso.datetime({}),
                                  data_type: zod
                                    .enum([
                                      'BOOLEAN',
                                      'DATE',
                                      'NUMBER',
                                      'STRING',
                                      'SELECT_NUMBER',
                                      'SELECT_STRING',
                                      'TAG',
                                      'ENTITY',
                                      'LINK',
                                    ])
                                    .describe(
                                      'Data type for property values, determining storage and validation.'
                                    ),
                                  display_name: zod.string(),
                                  id: zod.uuid(),
                                  is_metadata: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                    ),
                                  is_multi_select: zod.boolean(),
                                  is_system: zod
                                    .boolean()
                                    .describe(
                                      'Flag to indicate if this is a system property (stored in DB).'
                                    ),
                                  owner: zod
                                    .union([
                                      zod
                                        .object({
                                          scope: zod.enum(['user']),
                                          user_id: zod.string(),
                                        })
                                        .describe('User-scoped property.'),
                                      zod
                                        .object({
                                          scope: zod.enum(['team']),
                                          team_id: zod.uuid(),
                                        })
                                        .describe('Team-scoped property.'),
                                      zod
                                        .object({
                                          database_id: zod.uuid(),
                                          scope: zod.enum(['database']),
                                        })
                                        .describe(
                                          'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                        ),
                                      zod
                                        .object({
                                          scope: zod.enum(['system']),
                                        })
                                        .describe(
                                          'System-owned property (no user, team, or database owner).'
                                        ),
                                    ])
                                    .describe(
                                      'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                    ),
                                  specific_entity_type: zod.union([
                                    zod.null(),
                                    zod
                                      .enum([
                                        'CALENDAR_EVENT',
                                        'CALL_RECORD',
                                        'CHANNEL',
                                        'CHAT',
                                        'COMPANY',
                                        'DATABASE_ROW',
                                        'CONTACT',
                                        'DOCUMENT',
                                        'INITIATIVE',
                                        'PROJECT',
                                        'TASK',
                                        'THREAD',
                                        'USER',
                                      ])
                                      .describe(
                                        'Type of entity that can be referenced by entity properties.'
                                      ),
                                  ]),
                                  updated_at: zod.iso.datetime({}),
                                })
                                .describe(
                                  'Property definition model (service representation).'
                                ),
                              id: zod
                                .uuid()
                                .describe(
                                  'Globally unique id of the assignment attaching this property to an entity.'
                                ),
                              value: zod
                                .union([
                                  zod.null(),
                                  zod
                                    .union([
                                      zod
                                        .object({
                                          type: zod.enum(['Boolean']),
                                          value: zod
                                            .boolean()
                                            .describe(
                                              'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                            ),
                                        })
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Number']),
                                          value: zod
                                            .number()
                                            .describe(
                                              'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                            ),
                                        })
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['String']),
                                          value: zod
                                            .string()
                                            .describe(
                                              'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                            ),
                                        })
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Date']),
                                          value: zod.iso
                                            .datetime({})
                                            .describe(
                                              'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                            ),
                                        })
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['SelectOption']),
                                          value: zod
                                            .array(zod.uuid())
                                            .describe(
                                              'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['EntityReference']),
                                          value: zod
                                            .array(
                                              zod
                                                .object({
                                                  entity_id: zod.string(),
                                                  entity_type: zod
                                                    .enum([
                                                      'CALENDAR_EVENT',
                                                      'CALL_RECORD',
                                                      'CHANNEL',
                                                      'CHAT',
                                                      'COMPANY',
                                                      'DATABASE_ROW',
                                                      'CONTACT',
                                                      'DOCUMENT',
                                                      'INITIATIVE',
                                                      'PROJECT',
                                                      'TASK',
                                                      'THREAD',
                                                      'USER',
                                                    ])
                                                    .describe(
                                                      'Type of entity that can be referenced by entity properties.'
                                                    ),
                                                  specific_message_id: zod
                                                    .uuid()
                                                    .nullish()
                                                    .describe(
                                                      'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                    ),
                                                })
                                                .describe(
                                                  'Entity reference for entity-type property values.'
                                                )
                                            )
                                            .describe(
                                              'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                      zod
                                        .object({
                                          type: zod.enum(['Link']),
                                          value: zod
                                            .array(zod.string())
                                            .describe(
                                              'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                            ),
                                        })
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    ])
                                    .describe(
                                      'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                    ),
                                ])
                                .optional(),
                            })
                            .describe(
                              'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                            )
                        )
                        .describe('Properties attached to the entity.'),
                    })
                    .describe(
                      'Property fields that can be flattened into property-bearing Soup items.'
                    ),
                  icalUid: zod
                    .string()
                    .describe('RFC 5545 UID used for source reconciliation.'),
                  id: zod.uuid().describe('Entity identifier.'),
                  isReadOnly: zod
                    .boolean()
                    .describe(
                      'Whether the selected canonical source is read-only.'
                    ),
                  lastReminderFiredAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      "When this event's most recent reminder notification was delivered."
                    ),
                  location: zod
                    .string()
                    .nullish()
                    .describe('Optional location label.'),
                  organizerEmail: zod
                    .string()
                    .nullish()
                    .describe('Organizer email.'),
                  organizerName: zod
                    .string()
                    .nullish()
                    .describe('Organizer display name.'),
                  ownerId: zod.string().describe('Owning Macro user.'),
                  status: zod.string().describe('Canonical status.'),
                  time: zod
                    .union([
                      zod
                        .object({
                          endsAt: zod.iso
                            .datetime({})
                            .describe('Exclusive end.'),
                          kind: zod.enum(['timed']),
                          startsAt: zod.iso
                            .datetime({})
                            .describe('Inclusive start.'),
                          timeZone: zod
                            .string()
                            .nullish()
                            .describe('Original IANA time zone.'),
                        })
                        .describe('Absolute timed event.'),
                      zod
                        .object({
                          endDate: zod.iso
                            .date()
                            .describe('Exclusive end date.'),
                          kind: zod.enum(['allDay']),
                          startDate: zod.iso
                            .date()
                            .describe('Inclusive start date.'),
                        })
                        .describe('All-day event with an exclusive end date.'),
                    ])
                    .describe(
                      'Timed or all-day calendar event span.\n\nFields are camelCased per variant rather than via `rename_all_fields`,\nwhich utoipa ignores — the generated OpenAPI schema would otherwise\nclaim snake_case fields the wire never carries.'
                    ),
                  title: zod.string().describe('Display title.'),
                  transparency: zod
                    .string()
                    .describe('Availability transparency.'),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Entity update timestamp.'),
                  visibility: zod.string().describe('Canonical visibility.'),
                })
                .describe('A canonical calendar event entity in Soup.'),
              tag: zod.enum(['calendarEvent']),
            })
            .describe('Calendar event item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('When the company was created.'),
                    description: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display description from the primary domain's directory entry."
                      ),
                    domains: zod
                      .array(
                        zod
                          .object({
                            companyId: zod
                              .uuid()
                              .describe(
                                'The id of the company the domain belongs to.'
                              ),
                            createdAt: zod.iso
                              .datetime({})
                              .describe('When the domain record was created.'),
                            domain: zod
                              .string()
                              .describe(
                                'The domain (lowercased, e.g. \"acme.com\").'
                              ),
                            id: zod
                              .uuid()
                              .describe('The id of the domain record.'),
                          })
                          .describe(
                            "A CRM domain as displayed in Soup. Mirrors the crm crate's\n[`CrmDomain`] with a stable wire shape that the FE can rely on."
                          )
                      )
                      .describe(
                        'Domains associated with this company, ordered by creation time\nascending (primary first).'
                      ),
                    emailSync: zod
                      .boolean()
                      .describe(
                        'Whether email sync is enabled for this company.'
                      ),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the company is hidden from CRM listings. Soup filters\nthese out by default.'
                      ),
                    id: zod.uuid().describe('The id of the company.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe(
                        "Display name from the primary domain's directory entry, or\n`None` when unresolved."
                      ),
                    teamId: zod
                      .uuid()
                      .describe(
                        'The id of the team that owns this company record.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('When the company was last updated.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'When the requesting user last viewed this company, or `None` if\nnever viewed. Mirrors the `viewed_at` other soup entities carry.'
                      ),
                  })
                )
                .describe(
                  'A CRM company as displayed in Soup. Carries the core company\nfields plus display metadata resolved from `crm_domain_directory`\nagainst the primary (earliest-created) domain.'
                ),
              tag: zod.enum(['crmCompany']),
            })
            .describe('CRM company item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    companyId: zod.uuid().describe('Parent company ID.'),
                    companyName: zod
                      .string()
                      .describe("Parent company's display name."),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Record creation time.'),
                    email: zod.string().describe('Full email address.'),
                    firstInteraction: zod.iso
                      .datetime({})
                      .describe('Earliest interaction for this team record.'),
                    hidden: zod
                      .boolean()
                      .describe(
                        'Whether the contact or parent company is hidden.'
                      ),
                    id: zod.uuid().describe('Original team-owned contact ID.'),
                    lastInteraction: zod.iso
                      .datetime({})
                      .describe('Latest interaction for this team record.'),
                    name: zod
                      .string()
                      .nullish()
                      .describe('Team-local display name, if known.'),
                    teamId: zod.uuid().describe('Team owning this record.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Record update time.'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe("The viewer's latest visit to this record."),
                  })
                )
                .describe(
                  "One original CRM contact record, selected from the viewer's accessible teams."
                ),
              tag: zod.enum(['crmContact']),
            })
            .describe('Team-owned CRM contact.'),
          zod
            .object({
              data: zod
                .object({
                  createdAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was created.'),
                  foreignEntityId: zod
                    .string()
                    .describe('Identifier assigned by the external system.'),
                  foreignEntitySource: zod
                    .string()
                    .describe(
                      'Source system that owns the external identifier.'
                    ),
                  id: zod
                    .uuid()
                    .describe(
                      'Internal primary key for this foreign entity record.'
                    ),
                  metadata: zod
                    .object({})
                    .describe('Arbitrary metadata stored with the mapping.'),
                  storedForAuthEntity: zod
                    .string()
                    .describe(
                      'Internal auth entity namespace this foreign entity is stored for.'
                    ),
                  storedForId: zod
                    .string()
                    .describe(
                      'Internal entity identifier this foreign entity is stored for.'
                    ),
                  updatedAt: zod.iso
                    .datetime({})
                    .describe('Timestamp when the record was last updated.'),
                })
                .describe('A foreign entity record as displayed in Soup.'),
              tag: zod.enum(['foreignEntity']),
            })
            .describe('Foreign entity item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    botId: zod.uuid().describe('The bot running this session'),
                    createdAt: zod.iso
                      .datetime({})
                      .describe('The time the session was created'),
                    harness: zod
                      .string()
                      .describe(
                        'The runtime snapshotted when the session was created.'
                      ),
                    id: zod.uuid().describe('The agent session uuid'),
                    isArchived: zod
                      .boolean()
                      .describe(
                        'Whether the session is archived and read-only.'
                      ),
                    name: zod
                      .string()
                      .describe('The user-facing name of the session'),
                    ownerId: zod
                      .string()
                      .describe('Who the session belongs to'),
                    pullRequestId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        "The linked pull request's Macro entity, when visible to the viewer."
                      ),
                    pullRequestState: zod
                      .union([
                        zod.null(),
                        zod
                          .enum(['open', 'draft', 'closed', 'merged'])
                          .describe(
                            "Last synchronized state of a session's linked GitHub pull request."
                          ),
                      ])
                      .optional(),
                    pullRequestUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The persisted pull request associated with the session.'
                      ),
                    repoBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'The starting branch selected for this session, not its current branch.'
                      ),
                    repoUrl: zod
                      .string()
                      .nullish()
                      .describe(
                        'The repository the session works with, when one was selected.'
                      ),
                    status: zod
                      .string()
                      .describe(
                        "The session's last known status.\n\n`no_messages` until the first system event arrives, `disconnected` if\nthe connection dropped without a clean close, otherwise the wire name\nof the most recent system event (for example `session\/end`)."
                      ),
                    threadId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'The channel thread the session was opened from, when any'
                      ),
                    turnState: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last persisted fold turn state. Absent until an older session next runs.'
                      ),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('The time the session was last modified'),
                    viewedAt: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'The time the session was last viewed by the requesting user'
                      ),
                    workingBranch: zod
                      .string()
                      .nullish()
                      .describe(
                        'Last captured working branch, when the runtime has reported one.'
                      ),
                  })
                )
                .describe(
                  'An agent session as displayed in Soup.\n\nIncludes the persisted runtime and repository metadata needed to render\ncoding and non-coding sessions without fetching each session separately.'
                ),
              tag: zod.enum(['agentSession']),
            })
            .describe('Agent session item.'),
          zod
            .object({
              data: zod
                .object({
                  properties: zod
                    .array(
                      zod
                        .object({
                          definition: zod
                            .object({
                              created_at: zod.iso.datetime({}),
                              data_type: zod
                                .enum([
                                  'BOOLEAN',
                                  'DATE',
                                  'NUMBER',
                                  'STRING',
                                  'SELECT_NUMBER',
                                  'SELECT_STRING',
                                  'TAG',
                                  'ENTITY',
                                  'LINK',
                                ])
                                .describe(
                                  'Data type for property values, determining storage and validation.'
                                ),
                              display_name: zod.string(),
                              id: zod.uuid(),
                              is_metadata: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                ),
                              is_multi_select: zod.boolean(),
                              is_system: zod
                                .boolean()
                                .describe(
                                  'Flag to indicate if this is a system property (stored in DB).'
                                ),
                              owner: zod
                                .union([
                                  zod
                                    .object({
                                      scope: zod.enum(['user']),
                                      user_id: zod.string(),
                                    })
                                    .describe('User-scoped property.'),
                                  zod
                                    .object({
                                      scope: zod.enum(['team']),
                                      team_id: zod.uuid(),
                                    })
                                    .describe('Team-scoped property.'),
                                  zod
                                    .object({
                                      database_id: zod.uuid(),
                                      scope: zod.enum(['database']),
                                    })
                                    .describe(
                                      'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                    ),
                                  zod
                                    .object({
                                      scope: zod.enum(['system']),
                                    })
                                    .describe(
                                      'System-owned property (no user, team, or database owner).'
                                    ),
                                ])
                                .describe(
                                  'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                ),
                              specific_entity_type: zod.union([
                                zod.null(),
                                zod
                                  .enum([
                                    'CALENDAR_EVENT',
                                    'CALL_RECORD',
                                    'CHANNEL',
                                    'CHAT',
                                    'COMPANY',
                                    'DATABASE_ROW',
                                    'CONTACT',
                                    'DOCUMENT',
                                    'INITIATIVE',
                                    'PROJECT',
                                    'TASK',
                                    'THREAD',
                                    'USER',
                                  ])
                                  .describe(
                                    'Type of entity that can be referenced by entity properties.'
                                  ),
                              ]),
                              updated_at: zod.iso.datetime({}),
                            })
                            .describe(
                              'Property definition model (service representation).'
                            ),
                          id: zod
                            .uuid()
                            .describe(
                              'Globally unique id of the assignment attaching this property to an entity.'
                            ),
                          value: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      type: zod.enum(['Boolean']),
                                      value: zod
                                        .boolean()
                                        .describe(
                                          'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                        ),
                                    })
                                    .describe(
                                      'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Number']),
                                      value: zod
                                        .number()
                                        .describe(
                                          'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                        ),
                                    })
                                    .describe(
                                      'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['String']),
                                      value: zod
                                        .string()
                                        .describe(
                                          'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                        ),
                                    })
                                    .describe(
                                      'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Date']),
                                      value: zod.iso
                                        .datetime({})
                                        .describe(
                                          'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                        ),
                                    })
                                    .describe(
                                      'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['SelectOption']),
                                      value: zod
                                        .array(zod.uuid())
                                        .describe(
                                          'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['EntityReference']),
                                      value: zod
                                        .array(
                                          zod
                                            .object({
                                              entity_id: zod.string(),
                                              entity_type: zod
                                                .enum([
                                                  'CALENDAR_EVENT',
                                                  'CALL_RECORD',
                                                  'CHANNEL',
                                                  'CHAT',
                                                  'COMPANY',
                                                  'DATABASE_ROW',
                                                  'CONTACT',
                                                  'DOCUMENT',
                                                  'INITIATIVE',
                                                  'PROJECT',
                                                  'TASK',
                                                  'THREAD',
                                                  'USER',
                                                ])
                                                .describe(
                                                  'Type of entity that can be referenced by entity properties.'
                                                ),
                                              specific_message_id: zod
                                                .uuid()
                                                .nullish()
                                                .describe(
                                                  'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                ),
                                            })
                                            .describe(
                                              'Entity reference for entity-type property values.'
                                            )
                                        )
                                        .describe(
                                          'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['Link']),
                                      value: zod
                                        .array(zod.string())
                                        .describe(
                                          'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                        ),
                                    })
                                    .describe(
                                      'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                    ),
                                ])
                                .describe(
                                  'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                ),
                            ])
                            .optional(),
                        })
                        .describe(
                          'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                        )
                    )
                    .describe('Properties attached to the entity.'),
                })
                .describe(
                  'Property fields that can be flattened into property-bearing Soup items.'
                )
                .and(
                  zod.object({
                    createdAt: zod.iso
                      .datetime({})
                      .describe('Creation timestamp.'),
                    createdBy: zod
                      .string()
                      .nullish()
                      .describe('Who created the row, when they still exist.'),
                    databaseId: zod
                      .uuid()
                      .describe('The database the table belongs to.'),
                    id: zod.uuid().describe('Row identifier.'),
                    ownerId: zod
                      .string()
                      .describe(
                        "The database's owner, which owns every row in it."
                      ),
                    position: zod
                      .string()
                      .describe(
                        'Fractional index ordering the row within its table.'
                      ),
                    tableId: zod
                      .uuid()
                      .describe('The table the row belongs to.'),
                    updatedAt: zod.iso
                      .datetime({})
                      .describe('Last modification timestamp.'),
                  })
                )
                .describe(
                  "A row of a Macro database table in the Soup feed. Its cells are its\nentity properties; access is its database's."
                ),
              tag: zod.enum(['databaseRow']),
            })
            .describe('Database row item.'),
        ])
        .describe('A single item in the Soup feed.')
        .and(
          zod.object({
            frecency_score: zod.number(),
            is_favorited: zod
              .boolean()
              .describe(
                'Whether the requesting user has favorited this entity.'
              ),
            notified_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'When the caller was last notified about this entity, present only\nwhen the page was ordered by `notified_at`. Clients keep the notified\nfeed ordered and date-bucketed on this value.'
              ),
            touched_at: zod.iso
              .datetime({})
              .nullish()
              .describe(
                "The caller's latest own mutation of this entity, present only when the\npage was ordered by `touched_by_me`. Clients keep the touched feed\nordered on this value, so it can be bumped optimistically."
              ),
          })
        )
        .describe(
          'API representation of a soup item with its per-viewer enrichments.'
        )
    ),
    next_cursor: zod.string().nullish(),
  })
  .describe('Response page for non-grouped soup endpoints.');

/**
 * @summary Gets the items grouped by the specified field using AST filters.
 */
export const postItemsSoupAstGroupedQueryParams = zod.object({
  cursor: zod.string().optional().describe('Base64 encoded cursor value.'),
});

export const postItemsSoupAstGroupedBodyPerGroupLimitMin = 0;

export const postItemsSoupAstGroupedBodyLimitMin = 0;

export const postItemsSoupAstGroupedBody = zod
  .union([
    zod
      .object({
        asf: zod
          .unknown()
          .optional()
          .describe(
            'Filters applied to agent sessions (wire key `asf`). An empty or\nomitted filter returns \*\*no\*\* agent sessions: they are opt-in, so the\ncaller must send `inc`, an id, or an owner to get any.'
          ),
        calf: zod
          .unknown()
          .optional()
          .describe('filters applied to canonical calendar events'),
        callf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the call entity'),
        ccf: zod
          .unknown()
          .optional()
          .describe(
            "Filters applied to the crm_company entity (wire key `ccf`).\nEmpty\/omitted = team's full visible list."
          ),
        cf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the chat entity'),
        chanf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the channel entity'),
        crma: zod
          .union([
            zod.null(),
            zod
              .union([
                zod
                  .object({
                    company: zod
                      .uuid()
                      .describe(
                        'Items associated with the company or with any of its contacts.'
                      ),
                  })
                  .describe(
                    'Items associated with the company or with any of its contacts.'
                  ),
                zod
                  .object({
                    contact: zod
                      .uuid()
                      .describe('Items associated with the contact.'),
                  })
                  .describe('Items associated with the contact.'),
              ])
              .describe('The CRM record an association filter names.'),
          ])
          .optional(),
        crmf: zod
          .unknown()
          .optional()
          .describe(
            'Opt-in filters for viewer-accessible CRM contacts (wire key `crmf`).'
          ),
        cthf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the channel-thread entity'
          ),
        df: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the document entity'
          ),
        eca: zod
          .array(zod.string())
          .optional()
          .describe(
            'CRM-scoped address filter (wire key: `eca`). Symmetric counterpart\nto `ecd` for fully-qualified email addresses.'
          ),
        ecd: zod
          .array(zod.string())
          .optional()
          .describe(
            'CRM-scoped domain filter (wire key: `ecd`). Parallel to the\nfreeform `ef` AST. Expanded by the router into an any-direction\nOR sub-tree AND-merged into `ef`, plus a `CrmScope` tag stamped\non the resulting [`item_filters::ast::EmailFilterAst::crm_scope`].\nDrives the per-team CRM authorization pre-check and candidate-set\nwidening downstream. Mutually exclusive with `eca`.'
          ),
        ef: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the email entity (raw AST\ntree only; CRM scope is carried by the `ecd` \/ `eca` sibling\nfields). On this endpoint the email filter stays a bare tree,\nunlike the materialized [`EntityFilterAst`] used for cursors.'
          ),
        favorites_only: zod
          .boolean()
          .nullish()
          .describe(
            "Restrict to the authenticated viewer's favorites before pagination when true."
          ),
        fef: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to foreign entity records'
          ),
        ghprf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to GitHub pull request records, on top of `fef`'
          ),
        pf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the project entity'),
        propf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied based on entity properties'
          ),
      })
      .describe('Wire-format entity filter AST accepted by soup AST endpoints.')
      .and(
        zod
          .object({
            group_by: zod
              .union([
                zod
                  .enum(['date'])
                  .describe(
                    'Smart date buckets: Today, Yesterday, This Week, Last Week, This Month, Last Month, Older'
                  ),
                zod
                  .enum(['entity_type'])
                  .describe(
                    'Group by entity type (document, email, channel, etc.)'
                  ),
                zod.enum(['project']).describe('Group by project'),
                zod
                  .object({
                    property: zod
                      .object({
                        entity_type: zod
                          .union([
                            zod.null(),
                            zod
                              .enum([
                                'CHANNEL',
                                'CHAT',
                                'COMPANY',
                                'DOCUMENT',
                                'PROJECT',
                                'TASK',
                                'THREAD',
                                'USER',
                              ])
                              .describe(
                                'Entity type for property lookups (API representation).'
                              ),
                          ])
                          .optional(),
                        property_definition_id: zod
                          .uuid()
                          .describe('The property definition UUID to group by'),
                      })
                      .describe(
                        'Group by a property value (e.g., status, priority, or custom properties)'
                      ),
                  })
                  .describe(
                    'Group by a property value (e.g., status, priority, or custom properties)'
                  ),
              ])
              .describe('API representation of group-by field.'),
            per_group_limit: zod
              .number()
              .min(postItemsSoupAstGroupedBodyPerGroupLimitMin)
              .nullish()
              .describe(
                'Limit the number of items returned per group. Defaults to 20. Max 500.'
              ),
            sort_method: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'viewed_at',
                    'created_at',
                    'updated_at',
                    'viewed_updated',
                  ])
                  .describe(
                    'Sort method for grouped queries (frecency not supported).'
                  ),
              ])
              .optional(),
          })
          .describe('Parameters for the initial grouped soup query.')
      )
      .describe('Request body for the initial grouped soup query.')
      .and(
        zod.object({
          mode: zod.enum(['initial']),
        })
      )
      .describe('Initial grouped soup request.'),
    zod
      .object({
        asf: zod
          .unknown()
          .optional()
          .describe(
            'Filters applied to agent sessions (wire key `asf`). An empty or\nomitted filter returns \*\*no\*\* agent sessions: they are opt-in, so the\ncaller must send `inc`, an id, or an owner to get any.'
          ),
        calf: zod
          .unknown()
          .optional()
          .describe('filters applied to canonical calendar events'),
        callf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the call entity'),
        ccf: zod
          .unknown()
          .optional()
          .describe(
            "Filters applied to the crm_company entity (wire key `ccf`).\nEmpty\/omitted = team's full visible list."
          ),
        cf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the chat entity'),
        chanf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the channel entity'),
        crma: zod
          .union([
            zod.null(),
            zod
              .union([
                zod
                  .object({
                    company: zod
                      .uuid()
                      .describe(
                        'Items associated with the company or with any of its contacts.'
                      ),
                  })
                  .describe(
                    'Items associated with the company or with any of its contacts.'
                  ),
                zod
                  .object({
                    contact: zod
                      .uuid()
                      .describe('Items associated with the contact.'),
                  })
                  .describe('Items associated with the contact.'),
              ])
              .describe('The CRM record an association filter names.'),
          ])
          .optional(),
        crmf: zod
          .unknown()
          .optional()
          .describe(
            'Opt-in filters for viewer-accessible CRM contacts (wire key `crmf`).'
          ),
        cthf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the channel-thread entity'
          ),
        df: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the document entity'
          ),
        eca: zod
          .array(zod.string())
          .optional()
          .describe(
            'CRM-scoped address filter (wire key: `eca`). Symmetric counterpart\nto `ecd` for fully-qualified email addresses.'
          ),
        ecd: zod
          .array(zod.string())
          .optional()
          .describe(
            'CRM-scoped domain filter (wire key: `ecd`). Parallel to the\nfreeform `ef` AST. Expanded by the router into an any-direction\nOR sub-tree AND-merged into `ef`, plus a `CrmScope` tag stamped\non the resulting [`item_filters::ast::EmailFilterAst::crm_scope`].\nDrives the per-team CRM authorization pre-check and candidate-set\nwidening downstream. Mutually exclusive with `eca`.'
          ),
        ef: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to the email entity (raw AST\ntree only; CRM scope is carried by the `ecd` \/ `eca` sibling\nfields). On this endpoint the email filter stays a bare tree,\nunlike the materialized [`EntityFilterAst`] used for cursors.'
          ),
        favorites_only: zod
          .boolean()
          .nullish()
          .describe(
            "Restrict to the authenticated viewer's favorites before pagination when true."
          ),
        fef: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to foreign entity records'
          ),
        ghprf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied to GitHub pull request records, on top of `fef`'
          ),
        pf: zod
          .unknown()
          .optional()
          .describe('the filters that should be applied to the project entity'),
        propf: zod
          .unknown()
          .optional()
          .describe(
            'the filters that should be applied based on entity properties'
          ),
      })
      .describe('Wire-format entity filter AST accepted by soup AST endpoints.')
      .and(
        zod
          .object({
            group_by: zod
              .union([
                zod
                  .enum(['date'])
                  .describe(
                    'Smart date buckets: Today, Yesterday, This Week, Last Week, This Month, Last Month, Older'
                  ),
                zod
                  .enum(['entity_type'])
                  .describe(
                    'Group by entity type (document, email, channel, etc.)'
                  ),
                zod.enum(['project']).describe('Group by project'),
                zod
                  .object({
                    property: zod
                      .object({
                        entity_type: zod
                          .union([
                            zod.null(),
                            zod
                              .enum([
                                'CHANNEL',
                                'CHAT',
                                'COMPANY',
                                'DOCUMENT',
                                'PROJECT',
                                'TASK',
                                'THREAD',
                                'USER',
                              ])
                              .describe(
                                'Entity type for property lookups (API representation).'
                              ),
                          ])
                          .optional(),
                        property_definition_id: zod
                          .uuid()
                          .describe('The property definition UUID to group by'),
                      })
                      .describe(
                        'Group by a property value (e.g., status, priority, or custom properties)'
                      ),
                  })
                  .describe(
                    'Group by a property value (e.g., status, priority, or custom properties)'
                  ),
              ])
              .describe('API representation of group-by field.'),
            group_key: zod.string().describe('Group key to fetch.'),
            limit: zod
              .number()
              .min(postItemsSoupAstGroupedBodyLimitMin)
              .nullish()
              .describe(
                'Limit the number of items returned. Defaults to 20. Max 500.'
              ),
            sort_method: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'viewed_at',
                    'created_at',
                    'updated_at',
                    'viewed_updated',
                  ])
                  .describe(
                    'Sort method for grouped queries (frecency not supported).'
                  ),
              ])
              .optional(),
          })
          .describe('Parameters for fetching one page within a specific group.')
      )
      .describe('Request body for fetching one page within a specific group.')
      .and(
        zod.object({
          mode: zod.enum(['group_page']),
        })
      )
      .describe('Request for a page within one group.'),
  ])
  .describe('Request body for grouped soup queries with AST filters.');

export const postItemsSoupAstGroupedResponseGroupsItemTotalCountMin = 0;

export const postItemsSoupAstGroupedResponseItemsDataChannelOrgIdMin = 0;

export const postItemsSoupAstGroupedResponseGroupTotalCountMin = 0;

export const postItemsSoupAstGroupedResponseItemsDataChannelOrgIdMinOne = 0;

export const postItemsSoupAstGroupedResponse = zod
  .union([
    zod
      .object({
        groups: zod
          .array(
            zod
              .object({
                display_order: zod
                  .number()
                  .nullish()
                  .describe('Display order for sorting groups (lower = first)'),
                item_ids: zod
                  .array(zod.uuid())
                  .describe(
                    'Ordered ids of items in this group for the current page.\nEach id keys into `GroupedSoupPage.items`.'
                  ),
                key: zod
                  .string()
                  .describe('Group key - format depends on group_by field'),
                label: zod
                  .string()
                  .describe('Human-readable label for the group'),
                next_cursor: zod
                  .string()
                  .nullish()
                  .describe(
                    'Cursor to load more items specifically from this group'
                  ),
                total_count: zod
                  .number()
                  .min(postItemsSoupAstGroupedResponseGroupsItemTotalCountMin)
                  .describe(
                    'Total count of items in this group across all pages'
                  ),
              })
              .describe(
                'API representation of group metadata.\n\nItems belonging to this group are referenced by `item_ids`, each of which\nlooks up an entry in `GroupedSoupPage.items` (a normalized pool).'
              )
          )
          .describe('Ordered group metadata for this grouped response.'),
        items: zod
          .record(
            zod.string(),
            zod
              .union([
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          branchedFromId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The id of the document this document branched from'
                            ),
                          branchedFromVersionId: zod
                            .number()
                            .nullish()
                            .describe(
                              'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on the file type'
                            ),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the document was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was deleted'),
                          documentFamilyId: zod
                            .number()
                            .nullish()
                            .describe(
                              'The id of the document family this document belongs to'
                            ),
                          documentVersionId: zod
                            .number()
                            .describe(
                              'The version of the document\nThis could be the document_instance_id or document_bom_id depending on the file type'
                            ),
                          fileType: zod
                            .string()
                            .nullish()
                            .describe(
                              'The file type of the document (e.g. pdf, docx)'
                            ),
                          id: zod.uuid().describe('The document id'),
                          name: zod
                            .string()
                            .describe('The name of the document'),
                          ownerId: zod
                            .string()
                            .describe('The owner of the document'),
                          projectId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The id of the project that this document belongs to'
                            ),
                          sha: zod
                            .string()
                            .nullish()
                            .describe(
                              'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                            ),
                          subType: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      is_completed: zod
                                        .boolean()
                                        .describe(
                                          'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                        ),
                                      type: zod.enum(['task']),
                                    })
                                    .describe(
                                      'A task document with its associated properties'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['snippet']),
                                    })
                                    .describe(
                                      'A snippet document — reusable markdown'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['skill']),
                                    })
                                    .describe(
                                      'A skill document — markdown instructions for AI'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum([
                                        'initiative_description',
                                      ]),
                                    })
                                    .describe(
                                      'The description document of an initiative'
                                    ),
                                ])
                                .describe(
                                  'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                                ),
                            ])
                            .optional(),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe(
                              'The time the document instance \/ document BOM was updated'
                            ),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was last viewed'),
                        })
                      )
                      .describe('A document as displayed in Soup.'),
                    tag: zod.enum(['document']),
                  })
                  .describe('Document item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the chat was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the chat was deleted'),
                          id: zod.uuid().describe('The chat uuid'),
                          isPersistent: zod
                            .boolean()
                            .describe('Whether the chat is persistent or not'),
                          model: zod
                            .string()
                            .nullish()
                            .describe(
                              'The last model selected for a sent message (`provider\/model` id).'
                            ),
                          name: zod.string().describe('The name of the chat'),
                          ownerId: zod
                            .string()
                            .describe('Who the chat belongs to'),
                          projectId: zod
                            .uuid()
                            .nullish()
                            .describe('The project id of the chat'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the chat was last updated'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the chat was last viewed'),
                        })
                      )
                      .describe('A chat as displayed in Soup.'),
                    tag: zod.enum(['chat']),
                  })
                  .describe('Chat item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the project was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the project was deleted'),
                          id: zod.uuid().describe('The id of the project'),
                          name: zod
                            .string()
                            .describe('The name of the project'),
                          ownerId: zod
                            .string()
                            .describe('The owner of the project'),
                          parentId: zod
                            .uuid()
                            .nullish()
                            .describe('The parent project id'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the project was updated'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was last viewed'),
                        })
                      )
                      .describe('A project as displayed in Soup.'),
                    tag: zod.enum(['project']),
                  })
                  .describe('Project item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          id: zod.uuid().describe('Initiative identifier.'),
                          name: zod
                            .string()
                            .describe('Initiative display name.'),
                          ownerId: zod.string().describe('Initiative owner.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Last modification timestamp.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Last time the requesting user viewed the initiative.'
                            ),
                        })
                      )
                      .describe(
                        'An initiative (called a project in the frontend) in the Soup feed.'
                      ),
                    tag: zod.enum(['initiative']),
                  })
                  .describe('Initiative entity.'),
                zod
                  .object({
                    data: zod
                      .object({
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Timestamp when the thread was created.'),
                        id: zod.uuid().describe('Thread id.'),
                        inboxVisible: zod
                          .boolean()
                          .describe(
                            'Whether the thread should appear in the inbox.'
                          ),
                        isDraft: zod
                          .boolean()
                          .describe('Whether the thread contains a draft.'),
                        isImportant: zod
                          .boolean()
                          .describe('Whether the thread is marked important.'),
                        isRead: zod
                          .boolean()
                          .describe('Whether the thread has been read.'),
                        isSignal: zod
                          .boolean()
                          .describe(
                            "The denormalized `email_threads.is_signal` importance classification —\nthe same flag the soup Importance filter evaluates, distinct from\n`is_important` (Gmail's IMPORTANT label)."
                          ),
                        name: zod
                          .string()
                          .nullish()
                          .describe('Thread display name or subject.'),
                        ownerId: zod.string().describe('Owner user id.'),
                        projectId: zod
                          .string()
                          .nullish()
                          .describe('Project id associated with the thread.'),
                        providerId: zod
                          .string()
                          .nullish()
                          .describe('Provider-specific thread id.'),
                        senderEmail: zod
                          .string()
                          .nullish()
                          .describe('Sender email address for the preview.'),
                        senderName: zod
                          .string()
                          .nullish()
                          .describe('Sender display name for the preview.'),
                        senderPhotoUrl: zod
                          .string()
                          .nullish()
                          .describe('Sender photo URL for the preview.'),
                        snippet: zod
                          .string()
                          .nullish()
                          .describe('Thread snippet.'),
                        sortTs: zod.iso
                          .datetime({})
                          .describe('Timestamp used for email thread sorting.'),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe(
                            'Timestamp when the thread was last updated.'
                          ),
                        viewedAt: zod.iso
                          .datetime({})
                          .nullish()
                          .describe(
                            'Timestamp when the requesting user last viewed the thread.'
                          ),
                      })
                      .describe(
                        'Email thread preview data as displayed in Soup.'
                      )
                      .and(
                        zod
                          .object({
                            properties: zod
                              .array(
                                zod
                                  .object({
                                    definition: zod
                                      .object({
                                        created_at: zod.iso.datetime({}),
                                        data_type: zod
                                          .enum([
                                            'BOOLEAN',
                                            'DATE',
                                            'NUMBER',
                                            'STRING',
                                            'SELECT_NUMBER',
                                            'SELECT_STRING',
                                            'TAG',
                                            'ENTITY',
                                            'LINK',
                                          ])
                                          .describe(
                                            'Data type for property values, determining storage and validation.'
                                          ),
                                        display_name: zod.string(),
                                        id: zod.uuid(),
                                        is_metadata: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                          ),
                                        is_multi_select: zod.boolean(),
                                        is_system: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system property (stored in DB).'
                                          ),
                                        owner: zod
                                          .union([
                                            zod
                                              .object({
                                                scope: zod.enum(['user']),
                                                user_id: zod.string(),
                                              })
                                              .describe(
                                                'User-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['team']),
                                                team_id: zod.uuid(),
                                              })
                                              .describe(
                                                'Team-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                database_id: zod.uuid(),
                                                scope: zod.enum(['database']),
                                              })
                                              .describe(
                                                'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['system']),
                                              })
                                              .describe(
                                                'System-owned property (no user, team, or database owner).'
                                              ),
                                          ])
                                          .describe(
                                            'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                          ),
                                        specific_entity_type: zod.union([
                                          zod.null(),
                                          zod
                                            .enum([
                                              'CALENDAR_EVENT',
                                              'CALL_RECORD',
                                              'CHANNEL',
                                              'CHAT',
                                              'COMPANY',
                                              'DATABASE_ROW',
                                              'CONTACT',
                                              'DOCUMENT',
                                              'INITIATIVE',
                                              'PROJECT',
                                              'TASK',
                                              'THREAD',
                                              'USER',
                                            ])
                                            .describe(
                                              'Type of entity that can be referenced by entity properties.'
                                            ),
                                        ]),
                                        updated_at: zod.iso.datetime({}),
                                      })
                                      .describe(
                                        'Property definition model (service representation).'
                                      ),
                                    id: zod
                                      .uuid()
                                      .describe(
                                        'Globally unique id of the assignment attaching this property to an entity.'
                                      ),
                                    value: zod
                                      .union([
                                        zod.null(),
                                        zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['Boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe(
                                                    'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                                  ),
                                              })
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Number']),
                                                value: zod
                                                  .number()
                                                  .describe(
                                                    'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                                  ),
                                              })
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['String']),
                                                value: zod
                                                  .string()
                                                  .describe(
                                                    'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                                  ),
                                              })
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe(
                                                    'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                                  ),
                                              })
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'SelectOption',
                                                ]),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'EntityReference',
                                                ]),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entity_id: zod.string(),
                                                        entity_type: zod
                                                          .enum([
                                                            'CALENDAR_EVENT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'COMPANY',
                                                            'DATABASE_ROW',
                                                            'CONTACT',
                                                            'DOCUMENT',
                                                            'INITIATIVE',
                                                            'PROJECT',
                                                            'TASK',
                                                            'THREAD',
                                                            'USER',
                                                          ])
                                                          .describe(
                                                            'Type of entity that can be referenced by entity properties.'
                                                          ),
                                                        specific_message_id: zod
                                                          .uuid()
                                                          .nullish()
                                                          .describe(
                                                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Entity reference for entity-type property values.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          ])
                                          .describe(
                                            'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                          ),
                                      ])
                                      .optional(),
                                  })
                                  .describe(
                                    'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                                  )
                              )
                              .describe('Properties attached to the entity.'),
                          })
                          .describe(
                            'Property fields that can be flattened into property-bearing Soup items.'
                          )
                      )
                      .and(
                        zod.object({
                          attachments: zod
                            .array(
                              zod
                                .object({
                                  contentId: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Content id for inline attachments.'
                                    ),
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'Timestamp when the attachment was created.'
                                    ),
                                  filename: zod
                                    .string()
                                    .nullish()
                                    .describe('Attachment filename.'),
                                  id: zod.uuid().describe('Attachment id.'),
                                  messageId: zod
                                    .uuid()
                                    .describe(
                                      'Message id that owns this attachment.'
                                    ),
                                  mimeType: zod
                                    .string()
                                    .nullish()
                                    .describe('Attachment MIME type.'),
                                  providerAttachmentId: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Provider-specific attachment id.'
                                    ),
                                  sizeBytes: zod
                                    .number()
                                    .nullish()
                                    .describe('Attachment size in bytes.'),
                                })
                                .describe(
                                  'An email attachment as displayed in Soup.'
                                )
                            )
                            .describe('Attachments on the thread.'),
                          labels: zod
                            .array(
                              zod
                                .object({
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'Timestamp when the label was created.'
                                    ),
                                  id: zod.uuid().describe('Label id.'),
                                  labelListVisibility: zod
                                    .enum([
                                      'labelShow',
                                      'labelShowIfUnread',
                                      'labelHide',
                                    ])
                                    .describe(
                                      'Gmail-style label list visibility.'
                                    ),
                                  linkId: zod
                                    .uuid()
                                    .describe(
                                      'Connected account link id for the label.'
                                    ),
                                  messageListVisibility: zod
                                    .enum(['show', 'hide'])
                                    .describe(
                                      'Gmail-style message list visibility for a label.'
                                    ),
                                  name: zod
                                    .string()
                                    .describe('Label display name.'),
                                  providerLabelId: zod
                                    .string()
                                    .describe('Provider-specific label id.'),
                                  type: zod
                                    .enum(['system', 'user'])
                                    .describe(
                                      'Whether an email label is provider-created or user-created.'
                                    ),
                                })
                                .describe(
                                  'An email label as displayed in Soup.'
                                )
                            )
                            .describe('Labels attached to the thread.'),
                          participants: zod
                            .array(
                              zod
                                .object({
                                  emailAddress: zod
                                    .string()
                                    .nullish()
                                    .describe('Contact email address.'),
                                  id: zod.uuid().describe('Contact id.'),
                                  linkId: zod
                                    .uuid()
                                    .describe(
                                      'Connected account link id for the contact.'
                                    ),
                                  name: zod
                                    .string()
                                    .nullish()
                                    .describe('Contact display name.'),
                                  sfsPhotoUrl: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Contact photo URL from SFS, if available.'
                                    ),
                                })
                                .describe(
                                  'A contact participating in an email thread as displayed in Soup.'
                                )
                            )
                            .describe('Contacts participating in the thread.'),
                        })
                      )
                      .describe(
                        'Email thread preview enriched with related metadata for Soup.'
                      ),
                    tag: zod.enum(['emailThread']),
                  })
                  .describe('Email thread item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        channel: zod
                          .object({
                            channel_type: zod
                              .enum([
                                'public',
                                'private',
                                'direct_message',
                                'team',
                              ])
                              .describe('Type of channel.'),
                            created_at: zod.iso
                              .datetime({})
                              .describe('Creation timestamp.'),
                            id: zod.uuid().describe('Channel id.'),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Channel display name.'),
                            org_id: zod
                              .number()
                              .min(
                                postItemsSoupAstGroupedResponseItemsDataChannelOrgIdMin
                              )
                              .nullish()
                              .describe('Organization id.'),
                            owner_id: zod.string().describe('Channel owner.'),
                            team_id: zod.uuid().nullish().describe('Team id.'),
                            updated_at: zod.iso
                              .datetime({})
                              .describe('Update timestamp.'),
                          })
                          .describe('Channel metadata in soup payloads.'),
                        is_participant: zod
                          .boolean()
                          .optional()
                          .describe(
                            "Whether the requesting user is an active participant of the channel.\nFalse for team channels of the user's teams they have not joined."
                          ),
                        participants: zod
                          .array(
                            zod
                              .object({
                                channel_id: zod
                                  .uuid()
                                  .describe(
                                    'Channel id for the participant membership.'
                                  ),
                                joined_at: zod.iso
                                  .datetime({})
                                  .describe(
                                    'Timestamp when the participant joined the channel.'
                                  ),
                                left_at: zod.iso
                                  .datetime({})
                                  .nullish()
                                  .describe(
                                    'Timestamp when the participant left the channel, if any.'
                                  ),
                                role: zod
                                  .enum(['owner', 'admin', 'member'])
                                  .describe(
                                    'The role a user has within a channel.'
                                  ),
                                user_id: zod
                                  .string()
                                  .describe('Participant user id.'),
                              })
                              .describe("A user's membership in a channel.")
                          )
                          .describe('Participants in the channel.'),
                      })
                      .describe(
                        'Channel metadata together with its participants.'
                      )
                      .and(
                        zod
                          .object({
                            latest_message: zod
                              .union([
                                zod.null(),
                                zod
                                  .object({
                                    content: zod
                                      .string()
                                      .describe('Message content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    deleted_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Deletion timestamp.'),
                                    mentions: zod
                                      .array(zod.string())
                                      .describe(
                                        'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                      ),
                                    message_id: zod
                                      .uuid()
                                      .describe('Message id.'),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    thread_id: zod
                                      .uuid()
                                      .nullish()
                                      .describe('Thread parent id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe(
                                    'Lightweight channel message for soup payloads.'
                                  ),
                              ])
                              .optional(),
                            latest_non_thread_message: zod
                              .union([
                                zod.null(),
                                zod
                                  .object({
                                    content: zod
                                      .string()
                                      .describe('Message content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    deleted_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Deletion timestamp.'),
                                    mentions: zod
                                      .array(zod.string())
                                      .describe(
                                        'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                      ),
                                    message_id: zod
                                      .uuid()
                                      .describe('Message id.'),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    thread_id: zod
                                      .uuid()
                                      .nullish()
                                      .describe('Thread parent id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe(
                                    'Lightweight channel message for soup payloads.'
                                  ),
                              ])
                              .optional(),
                          })
                          .describe('Latest-message bundle for soup payloads.')
                      )
                      .and(
                        zod.object({
                          interacted_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the requesting user last interacted with this channel.'
                            ),
                          viewed_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the requesting user last viewed this channel.'
                            ),
                        })
                      )
                      .describe('A channel as displayed in Soup.'),
                    tag: zod.enum(['channel']),
                  })
                  .describe('Channel item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        attachments: zod
                          .array(
                            zod
                              .object({
                                created_at: zod.iso
                                  .datetime({})
                                  .describe('Creation timestamp.'),
                                entity_id: zod.string().describe('Entity id.'),
                                entity_type: zod
                                  .string()
                                  .describe('Type of entity.'),
                                height: zod
                                  .number()
                                  .nullish()
                                  .describe('Height for images.'),
                                id: zod.uuid().describe('Attachment id.'),
                                width: zod
                                  .number()
                                  .nullish()
                                  .describe('Width for images.'),
                              })
                              .describe('An attachment on a message.')
                          )
                          .describe('Attachments on this message.'),
                        channel_id: zod.uuid().describe('Channel id.'),
                        content: zod.string().describe('Message content.'),
                        created_at: zod.iso
                          .datetime({})
                          .describe('Creation timestamp.'),
                        deleted_at: zod.iso
                          .datetime({})
                          .nullish()
                          .describe('Deletion timestamp.'),
                        edited_at: zod.iso
                          .datetime({})
                          .nullish()
                          .describe('Edit timestamp.'),
                        id: zod.uuid().describe('Message id.'),
                        reactions: zod
                          .array(
                            zod
                              .object({
                                emoji: zod.string().describe('Emoji string.'),
                                users: zod
                                  .array(zod.string())
                                  .describe(
                                    'User ids who added this reaction.'
                                  ),
                              })
                              .describe('A reaction with emoji and user list.')
                          )
                          .describe('Reactions on this message.'),
                        sender: zod
                          .object({
                            avatar_url: zod
                              .string()
                              .nullish()
                              .describe('Avatar URL for bot senders.'),
                            id: zod
                              .string()
                              .describe(
                                'Sender id without the storage namespace prefix.'
                              ),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Display name for bot senders.'),
                            type: zod
                              .enum(['user', 'bot'])
                              .describe(
                                'Public sender type for soup channel messages.'
                              ),
                          })
                          .describe(
                            'Public sender identity for soup channel messages.'
                          ),
                        sender_id: zod.string().describe('Sender id.'),
                        thread: zod
                          .object({
                            latest_reply_at: zod.iso
                              .datetime({})
                              .nullish()
                              .describe('Timestamp of the latest reply.'),
                            preview: zod
                              .array(
                                zod
                                  .object({
                                    attachments: zod
                                      .array(
                                        zod
                                          .object({
                                            created_at: zod.iso
                                              .datetime({})
                                              .describe('Creation timestamp.'),
                                            entity_id: zod
                                              .string()
                                              .describe('Entity id.'),
                                            entity_type: zod
                                              .string()
                                              .describe('Type of entity.'),
                                            height: zod
                                              .number()
                                              .nullish()
                                              .describe('Height for images.'),
                                            id: zod
                                              .uuid()
                                              .describe('Attachment id.'),
                                            width: zod
                                              .number()
                                              .nullish()
                                              .describe('Width for images.'),
                                          })
                                          .describe(
                                            'An attachment on a message.'
                                          )
                                      )
                                      .describe('Attachments on this reply.'),
                                    content: zod
                                      .string()
                                      .describe('Reply content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    edited_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Edit timestamp.'),
                                    id: zod.uuid().describe('Reply id.'),
                                    reactions: zod
                                      .array(
                                        zod
                                          .object({
                                            emoji: zod
                                              .string()
                                              .describe('Emoji string.'),
                                            users: zod
                                              .array(zod.string())
                                              .describe(
                                                'User ids who added this reaction.'
                                              ),
                                          })
                                          .describe(
                                            'A reaction with emoji and user list.'
                                          )
                                      )
                                      .describe('Reactions on this reply.'),
                                    sender: zod
                                      .object({
                                        avatar_url: zod
                                          .string()
                                          .nullish()
                                          .describe(
                                            'Avatar URL for bot senders.'
                                          ),
                                        id: zod
                                          .string()
                                          .describe(
                                            'Sender id without the storage namespace prefix.'
                                          ),
                                        name: zod
                                          .string()
                                          .nullish()
                                          .describe(
                                            'Display name for bot senders.'
                                          ),
                                        type: zod
                                          .enum(['user', 'bot'])
                                          .describe(
                                            'Public sender type for soup channel messages.'
                                          ),
                                      })
                                      .describe(
                                        'Public sender identity for soup channel messages.'
                                      ),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe('A thread reply shown in preview.')
                              )
                              .describe('Preview replies.'),
                            reply_count: zod
                              .number()
                              .describe('Total reply count.'),
                          })
                          .describe(
                            'Thread metadata and preview replies for soup channel messages.'
                          ),
                        updated_at: zod.iso
                          .datetime({})
                          .describe('Update timestamp.'),
                      })
                      .describe(
                        'A top-level channel message thread for soup payloads.\n\nThis mirrors the public channel-message API shape so soup consumers can\nrender a thread root the same way they render a channel timeline message.'
                      ),
                    tag: zod.enum(['channelThread']),
                  })
                  .describe('Channel thread item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          attended: zod
                            .boolean()
                            .describe(
                              'Whether the requesting user attended this call. Kept for compatibility\nand derived from `status == ATTENDED`.'
                            ),
                          callId: zod.uuid().describe('The call identifier.'),
                          channelId: zod
                            .uuid()
                            .nullish()
                            .describe('The channel this call belongs to.'),
                          channelName: zod
                            .string()
                            .nullish()
                            .describe('Resolved display name for the channel.'),
                          createdBy: zod
                            .string()
                            .describe('User who created the call.'),
                          customName: zod
                            .string()
                            .nullish()
                            .describe(
                              'User-supplied or AI-generated display name for the call.'
                            ),
                          durationMs: zod
                            .number()
                            .nullish()
                            .describe(
                              'Call duration in milliseconds (None if still active).'
                            ),
                          endedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'When the call ended (None if still active).'
                            ),
                          guests: zod
                            .array(
                              zod
                                .object({
                                  displayName: zod
                                    .string()
                                    .describe('Guest-provided display name.'),
                                  id: zod
                                    .uuid()
                                    .describe(
                                      "Opaque guest identity; matches the guest's transcript speaker id."
                                    ),
                                  joinedAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'When the guest joined the call.'
                                    ),
                                  leftAt: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe(
                                      'When the guest left (None if still in an active call).'
                                    ),
                                })
                                .describe(
                                  'A non-account guest of a call record, as displayed in Soup.'
                                )
                            )
                            .describe('Non-account guests in the call.'),
                          isActive: zod
                            .boolean()
                            .describe('Whether the call is currently active.'),
                          participants: zod
                            .array(
                              zod
                                .object({
                                  joinedAt: zod.iso
                                    .datetime({})
                                    .describe('When the user joined the call.'),
                                  leftAt: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe(
                                      'When the user left (None if still in an active call).'
                                    ),
                                  userId: zod
                                    .string()
                                    .describe('The Macro user id.'),
                                })
                                .describe(
                                  'A Macro-account participant in a call record, as displayed in Soup.'
                                )
                            )
                            .describe(
                              'Macro-account participants in the call.'
                            ),
                          startedAt: zod.iso
                            .datetime({})
                            .describe('When the call started.'),
                          status: zod
                            .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
                            .describe(
                              'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
                            ),
                          summary: zod
                            .string()
                            .nullish()
                            .describe(
                              'AI-generated summary of the call. Only set on archived\n`call_records` once summarization has run; active calls always\nreturn `None`.'
                            ),
                        })
                      )
                      .describe(
                        'A call record as displayed in Soup. Excludes room_name, egress_id,\nand transcript — fields that are irrelevant for the soup feed.'
                      ),
                    tag: zod.enum(['call']),
                  })
                  .describe('Call record item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        conferenceProvider: zod
                          .string()
                          .nullish()
                          .describe(
                            'Which conferencing system backs `conference_url`.'
                          ),
                        conferenceUrl: zod
                          .string()
                          .nullish()
                          .describe('Direct conference join URL.'),
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Entity creation timestamp.'),
                        description: zod
                          .string()
                          .nullish()
                          .describe('Optional description.'),
                        extra: zod
                          .object({
                            properties: zod
                              .array(
                                zod
                                  .object({
                                    definition: zod
                                      .object({
                                        created_at: zod.iso.datetime({}),
                                        data_type: zod
                                          .enum([
                                            'BOOLEAN',
                                            'DATE',
                                            'NUMBER',
                                            'STRING',
                                            'SELECT_NUMBER',
                                            'SELECT_STRING',
                                            'TAG',
                                            'ENTITY',
                                            'LINK',
                                          ])
                                          .describe(
                                            'Data type for property values, determining storage and validation.'
                                          ),
                                        display_name: zod.string(),
                                        id: zod.uuid(),
                                        is_metadata: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                          ),
                                        is_multi_select: zod.boolean(),
                                        is_system: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system property (stored in DB).'
                                          ),
                                        owner: zod
                                          .union([
                                            zod
                                              .object({
                                                scope: zod.enum(['user']),
                                                user_id: zod.string(),
                                              })
                                              .describe(
                                                'User-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['team']),
                                                team_id: zod.uuid(),
                                              })
                                              .describe(
                                                'Team-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                database_id: zod.uuid(),
                                                scope: zod.enum(['database']),
                                              })
                                              .describe(
                                                'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['system']),
                                              })
                                              .describe(
                                                'System-owned property (no user, team, or database owner).'
                                              ),
                                          ])
                                          .describe(
                                            'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                          ),
                                        specific_entity_type: zod.union([
                                          zod.null(),
                                          zod
                                            .enum([
                                              'CALENDAR_EVENT',
                                              'CALL_RECORD',
                                              'CHANNEL',
                                              'CHAT',
                                              'COMPANY',
                                              'DATABASE_ROW',
                                              'CONTACT',
                                              'DOCUMENT',
                                              'INITIATIVE',
                                              'PROJECT',
                                              'TASK',
                                              'THREAD',
                                              'USER',
                                            ])
                                            .describe(
                                              'Type of entity that can be referenced by entity properties.'
                                            ),
                                        ]),
                                        updated_at: zod.iso.datetime({}),
                                      })
                                      .describe(
                                        'Property definition model (service representation).'
                                      ),
                                    id: zod
                                      .uuid()
                                      .describe(
                                        'Globally unique id of the assignment attaching this property to an entity.'
                                      ),
                                    value: zod
                                      .union([
                                        zod.null(),
                                        zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['Boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe(
                                                    'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                                  ),
                                              })
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Number']),
                                                value: zod
                                                  .number()
                                                  .describe(
                                                    'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                                  ),
                                              })
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['String']),
                                                value: zod
                                                  .string()
                                                  .describe(
                                                    'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                                  ),
                                              })
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe(
                                                    'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                                  ),
                                              })
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'SelectOption',
                                                ]),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'EntityReference',
                                                ]),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entity_id: zod.string(),
                                                        entity_type: zod
                                                          .enum([
                                                            'CALENDAR_EVENT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'COMPANY',
                                                            'DATABASE_ROW',
                                                            'CONTACT',
                                                            'DOCUMENT',
                                                            'INITIATIVE',
                                                            'PROJECT',
                                                            'TASK',
                                                            'THREAD',
                                                            'USER',
                                                          ])
                                                          .describe(
                                                            'Type of entity that can be referenced by entity properties.'
                                                          ),
                                                        specific_message_id: zod
                                                          .uuid()
                                                          .nullish()
                                                          .describe(
                                                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Entity reference for entity-type property values.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          ])
                                          .describe(
                                            'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                          ),
                                      ])
                                      .optional(),
                                  })
                                  .describe(
                                    'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                                  )
                              )
                              .describe('Properties attached to the entity.'),
                          })
                          .describe(
                            'Property fields that can be flattened into property-bearing Soup items.'
                          ),
                        icalUid: zod
                          .string()
                          .describe(
                            'RFC 5545 UID used for source reconciliation.'
                          ),
                        id: zod.uuid().describe('Entity identifier.'),
                        isReadOnly: zod
                          .boolean()
                          .describe(
                            'Whether the selected canonical source is read-only.'
                          ),
                        lastReminderFiredAt: zod.iso
                          .datetime({})
                          .nullish()
                          .describe(
                            "When this event's most recent reminder notification was delivered."
                          ),
                        location: zod
                          .string()
                          .nullish()
                          .describe('Optional location label.'),
                        organizerEmail: zod
                          .string()
                          .nullish()
                          .describe('Organizer email.'),
                        organizerName: zod
                          .string()
                          .nullish()
                          .describe('Organizer display name.'),
                        ownerId: zod.string().describe('Owning Macro user.'),
                        status: zod.string().describe('Canonical status.'),
                        time: zod
                          .union([
                            zod
                              .object({
                                endsAt: zod.iso
                                  .datetime({})
                                  .describe('Exclusive end.'),
                                kind: zod.enum(['timed']),
                                startsAt: zod.iso
                                  .datetime({})
                                  .describe('Inclusive start.'),
                                timeZone: zod
                                  .string()
                                  .nullish()
                                  .describe('Original IANA time zone.'),
                              })
                              .describe('Absolute timed event.'),
                            zod
                              .object({
                                endDate: zod.iso
                                  .date()
                                  .describe('Exclusive end date.'),
                                kind: zod.enum(['allDay']),
                                startDate: zod.iso
                                  .date()
                                  .describe('Inclusive start date.'),
                              })
                              .describe(
                                'All-day event with an exclusive end date.'
                              ),
                          ])
                          .describe(
                            'Timed or all-day calendar event span.\n\nFields are camelCased per variant rather than via `rename_all_fields`,\nwhich utoipa ignores — the generated OpenAPI schema would otherwise\nclaim snake_case fields the wire never carries.'
                          ),
                        title: zod.string().describe('Display title.'),
                        transparency: zod
                          .string()
                          .describe('Availability transparency.'),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe('Entity update timestamp.'),
                        visibility: zod
                          .string()
                          .describe('Canonical visibility.'),
                      })
                      .describe('A canonical calendar event entity in Soup.'),
                    tag: zod.enum(['calendarEvent']),
                  })
                  .describe('Calendar event item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('When the company was created.'),
                          description: zod
                            .string()
                            .nullish()
                            .describe(
                              "Display description from the primary domain's directory entry."
                            ),
                          domains: zod
                            .array(
                              zod
                                .object({
                                  companyId: zod
                                    .uuid()
                                    .describe(
                                      'The id of the company the domain belongs to.'
                                    ),
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'When the domain record was created.'
                                    ),
                                  domain: zod
                                    .string()
                                    .describe(
                                      'The domain (lowercased, e.g. \"acme.com\").'
                                    ),
                                  id: zod
                                    .uuid()
                                    .describe('The id of the domain record.'),
                                })
                                .describe(
                                  "A CRM domain as displayed in Soup. Mirrors the crm crate's\n[`CrmDomain`] with a stable wire shape that the FE can rely on."
                                )
                            )
                            .describe(
                              'Domains associated with this company, ordered by creation time\nascending (primary first).'
                            ),
                          emailSync: zod
                            .boolean()
                            .describe(
                              'Whether email sync is enabled for this company.'
                            ),
                          hidden: zod
                            .boolean()
                            .describe(
                              'Whether the company is hidden from CRM listings. Soup filters\nthese out by default.'
                            ),
                          id: zod.uuid().describe('The id of the company.'),
                          name: zod
                            .string()
                            .nullish()
                            .describe(
                              "Display name from the primary domain's directory entry, or\n`None` when unresolved."
                            ),
                          teamId: zod
                            .uuid()
                            .describe(
                              'The id of the team that owns this company record.'
                            ),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('When the company was last updated.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'When the requesting user last viewed this company, or `None` if\nnever viewed. Mirrors the `viewed_at` other soup entities carry.'
                            ),
                        })
                      )
                      .describe(
                        'A CRM company as displayed in Soup. Carries the core company\nfields plus display metadata resolved from `crm_domain_directory`\nagainst the primary (earliest-created) domain.'
                      ),
                    tag: zod.enum(['crmCompany']),
                  })
                  .describe('CRM company item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          companyId: zod.uuid().describe('Parent company ID.'),
                          companyName: zod
                            .string()
                            .describe("Parent company's display name."),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Record creation time.'),
                          email: zod.string().describe('Full email address.'),
                          firstInteraction: zod.iso
                            .datetime({})
                            .describe(
                              'Earliest interaction for this team record.'
                            ),
                          hidden: zod
                            .boolean()
                            .describe(
                              'Whether the contact or parent company is hidden.'
                            ),
                          id: zod
                            .uuid()
                            .describe('Original team-owned contact ID.'),
                          lastInteraction: zod.iso
                            .datetime({})
                            .describe(
                              'Latest interaction for this team record.'
                            ),
                          name: zod
                            .string()
                            .nullish()
                            .describe('Team-local display name, if known.'),
                          teamId: zod
                            .uuid()
                            .describe('Team owning this record.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Record update time.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              "The viewer's latest visit to this record."
                            ),
                        })
                      )
                      .describe(
                        "One original CRM contact record, selected from the viewer's accessible teams."
                      ),
                    tag: zod.enum(['crmContact']),
                  })
                  .describe('Team-owned CRM contact.'),
                zod
                  .object({
                    data: zod
                      .object({
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Timestamp when the record was created.'),
                        foreignEntityId: zod
                          .string()
                          .describe(
                            'Identifier assigned by the external system.'
                          ),
                        foreignEntitySource: zod
                          .string()
                          .describe(
                            'Source system that owns the external identifier.'
                          ),
                        id: zod
                          .uuid()
                          .describe(
                            'Internal primary key for this foreign entity record.'
                          ),
                        metadata: zod
                          .object({})
                          .describe(
                            'Arbitrary metadata stored with the mapping.'
                          ),
                        storedForAuthEntity: zod
                          .string()
                          .describe(
                            'Internal auth entity namespace this foreign entity is stored for.'
                          ),
                        storedForId: zod
                          .string()
                          .describe(
                            'Internal entity identifier this foreign entity is stored for.'
                          ),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe(
                            'Timestamp when the record was last updated.'
                          ),
                      })
                      .describe(
                        'A foreign entity record as displayed in Soup.'
                      ),
                    tag: zod.enum(['foreignEntity']),
                  })
                  .describe('Foreign entity item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          botId: zod
                            .uuid()
                            .describe('The bot running this session'),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the session was created'),
                          harness: zod
                            .string()
                            .describe(
                              'The runtime snapshotted when the session was created.'
                            ),
                          id: zod.uuid().describe('The agent session uuid'),
                          isArchived: zod
                            .boolean()
                            .describe(
                              'Whether the session is archived and read-only.'
                            ),
                          name: zod
                            .string()
                            .describe('The user-facing name of the session'),
                          ownerId: zod
                            .string()
                            .describe('Who the session belongs to'),
                          pullRequestId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              "The linked pull request's Macro entity, when visible to the viewer."
                            ),
                          pullRequestState: zod
                            .union([
                              zod.null(),
                              zod
                                .enum(['open', 'draft', 'closed', 'merged'])
                                .describe(
                                  "Last synchronized state of a session's linked GitHub pull request."
                                ),
                            ])
                            .optional(),
                          pullRequestUrl: zod
                            .string()
                            .nullish()
                            .describe(
                              'The persisted pull request associated with the session.'
                            ),
                          repoBranch: zod
                            .string()
                            .nullish()
                            .describe(
                              'The starting branch selected for this session, not its current branch.'
                            ),
                          repoUrl: zod
                            .string()
                            .nullish()
                            .describe(
                              'The repository the session works with, when one was selected.'
                            ),
                          status: zod
                            .string()
                            .describe(
                              "The session's last known status.\n\n`no_messages` until the first system event arrives, `disconnected` if\nthe connection dropped without a clean close, otherwise the wire name\nof the most recent system event (for example `session\/end`)."
                            ),
                          threadId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The channel thread the session was opened from, when any'
                            ),
                          turnState: zod
                            .string()
                            .nullish()
                            .describe(
                              'Last persisted fold turn state. Absent until an older session next runs.'
                            ),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the session was last modified'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'The time the session was last viewed by the requesting user'
                            ),
                          workingBranch: zod
                            .string()
                            .nullish()
                            .describe(
                              'Last captured working branch, when the runtime has reported one.'
                            ),
                        })
                      )
                      .describe(
                        'An agent session as displayed in Soup.\n\nIncludes the persisted runtime and repository metadata needed to render\ncoding and non-coding sessions without fetching each session separately.'
                      ),
                    tag: zod.enum(['agentSession']),
                  })
                  .describe('Agent session item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          createdBy: zod
                            .string()
                            .nullish()
                            .describe(
                              'Who created the row, when they still exist.'
                            ),
                          databaseId: zod
                            .uuid()
                            .describe('The database the table belongs to.'),
                          id: zod.uuid().describe('Row identifier.'),
                          ownerId: zod
                            .string()
                            .describe(
                              "The database's owner, which owns every row in it."
                            ),
                          position: zod
                            .string()
                            .describe(
                              'Fractional index ordering the row within its table.'
                            ),
                          tableId: zod
                            .uuid()
                            .describe('The table the row belongs to.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Last modification timestamp.'),
                        })
                      )
                      .describe(
                        "A row of a Macro database table in the Soup feed. Its cells are its\nentity properties; access is its database's."
                      ),
                    tag: zod.enum(['databaseRow']),
                  })
                  .describe('Database row item.'),
              ])
              .describe('A single item in the Soup feed.')
              .and(
                zod.object({
                  frecency_score: zod.number(),
                  is_favorited: zod
                    .boolean()
                    .describe(
                      'Whether the requesting user has favorited this entity.'
                    ),
                  notified_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'When the caller was last notified about this entity, present only\nwhen the page was ordered by `notified_at`. Clients keep the notified\nfeed ordered and date-bucketed on this value.'
                    ),
                  touched_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      "The caller's latest own mutation of this entity, present only when the\npage was ordered by `touched_by_me`. Clients keep the touched feed\nordered on this value, so it can be bumped optimistically."
                    ),
                })
              )
              .describe(
                'API representation of a soup item with its per-viewer enrichments.'
              )
          )
          .describe(
            'Items in this page, keyed by id. Ordering lives in `groups[].item_ids`.'
          ),
      })
      .describe(
        'Response for the initial grouped soup query.\n\nItems are returned as a pool keyed by id; each `ApiGroupMeta.item_ids`\ndescribes the ordered membership of one group.'
      )
      .and(
        zod.object({
          mode: zod.enum(['initial']),
        })
      )
      .describe('Initial grouped response containing multiple groups.'),
    zod
      .object({
        group: zod
          .object({
            display_order: zod
              .number()
              .nullish()
              .describe('Display order for sorting groups (lower = first)'),
            item_ids: zod
              .array(zod.uuid())
              .describe(
                'Ordered ids of items in this group for the current page.\nEach id keys into `GroupedSoupPage.items`.'
              ),
            key: zod
              .string()
              .describe('Group key - format depends on group_by field'),
            label: zod.string().describe('Human-readable label for the group'),
            next_cursor: zod
              .string()
              .nullish()
              .describe(
                'Cursor to load more items specifically from this group'
              ),
            total_count: zod
              .number()
              .min(postItemsSoupAstGroupedResponseGroupTotalCountMin)
              .describe('Total count of items in this group across all pages'),
          })
          .describe(
            'API representation of group metadata.\n\nItems belonging to this group are referenced by `item_ids`, each of which\nlooks up an entry in `GroupedSoupPage.items` (a normalized pool).'
          ),
        items: zod
          .record(
            zod.string(),
            zod
              .union([
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          branchedFromId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The id of the document this document branched from'
                            ),
                          branchedFromVersionId: zod
                            .number()
                            .nullish()
                            .describe(
                              'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on the file type'
                            ),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the document was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was deleted'),
                          documentFamilyId: zod
                            .number()
                            .nullish()
                            .describe(
                              'The id of the document family this document belongs to'
                            ),
                          documentVersionId: zod
                            .number()
                            .describe(
                              'The version of the document\nThis could be the document_instance_id or document_bom_id depending on the file type'
                            ),
                          fileType: zod
                            .string()
                            .nullish()
                            .describe(
                              'The file type of the document (e.g. pdf, docx)'
                            ),
                          id: zod.uuid().describe('The document id'),
                          name: zod
                            .string()
                            .describe('The name of the document'),
                          ownerId: zod
                            .string()
                            .describe('The owner of the document'),
                          projectId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The id of the project that this document belongs to'
                            ),
                          sha: zod
                            .string()
                            .nullish()
                            .describe(
                              'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                            ),
                          subType: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      is_completed: zod
                                        .boolean()
                                        .describe(
                                          'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                        ),
                                      type: zod.enum(['task']),
                                    })
                                    .describe(
                                      'A task document with its associated properties'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['snippet']),
                                    })
                                    .describe(
                                      'A snippet document — reusable markdown'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum(['skill']),
                                    })
                                    .describe(
                                      'A skill document — markdown instructions for AI'
                                    ),
                                  zod
                                    .object({
                                      type: zod.enum([
                                        'initiative_description',
                                      ]),
                                    })
                                    .describe(
                                      'The description document of an initiative'
                                    ),
                                ])
                                .describe(
                                  'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                                ),
                            ])
                            .optional(),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe(
                              'The time the document instance \/ document BOM was updated'
                            ),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was last viewed'),
                        })
                      )
                      .describe('A document as displayed in Soup.'),
                    tag: zod.enum(['document']),
                  })
                  .describe('Document item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the chat was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the chat was deleted'),
                          id: zod.uuid().describe('The chat uuid'),
                          isPersistent: zod
                            .boolean()
                            .describe('Whether the chat is persistent or not'),
                          model: zod
                            .string()
                            .nullish()
                            .describe(
                              'The last model selected for a sent message (`provider\/model` id).'
                            ),
                          name: zod.string().describe('The name of the chat'),
                          ownerId: zod
                            .string()
                            .describe('Who the chat belongs to'),
                          projectId: zod
                            .uuid()
                            .nullish()
                            .describe('The project id of the chat'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the chat was last updated'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the chat was last viewed'),
                        })
                      )
                      .describe('A chat as displayed in Soup.'),
                    tag: zod.enum(['chat']),
                  })
                  .describe('Chat item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the project was created'),
                          deletedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the project was deleted'),
                          id: zod.uuid().describe('The id of the project'),
                          name: zod
                            .string()
                            .describe('The name of the project'),
                          ownerId: zod
                            .string()
                            .describe('The owner of the project'),
                          parentId: zod
                            .uuid()
                            .nullish()
                            .describe('The parent project id'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the project was updated'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('The time the document was last viewed'),
                        })
                      )
                      .describe('A project as displayed in Soup.'),
                    tag: zod.enum(['project']),
                  })
                  .describe('Project item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          id: zod.uuid().describe('Initiative identifier.'),
                          name: zod
                            .string()
                            .describe('Initiative display name.'),
                          ownerId: zod.string().describe('Initiative owner.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Last modification timestamp.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Last time the requesting user viewed the initiative.'
                            ),
                        })
                      )
                      .describe(
                        'An initiative (called a project in the frontend) in the Soup feed.'
                      ),
                    tag: zod.enum(['initiative']),
                  })
                  .describe('Initiative entity.'),
                zod
                  .object({
                    data: zod
                      .object({
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Timestamp when the thread was created.'),
                        id: zod.uuid().describe('Thread id.'),
                        inboxVisible: zod
                          .boolean()
                          .describe(
                            'Whether the thread should appear in the inbox.'
                          ),
                        isDraft: zod
                          .boolean()
                          .describe('Whether the thread contains a draft.'),
                        isImportant: zod
                          .boolean()
                          .describe('Whether the thread is marked important.'),
                        isRead: zod
                          .boolean()
                          .describe('Whether the thread has been read.'),
                        isSignal: zod
                          .boolean()
                          .describe(
                            "The denormalized `email_threads.is_signal` importance classification —\nthe same flag the soup Importance filter evaluates, distinct from\n`is_important` (Gmail's IMPORTANT label)."
                          ),
                        name: zod
                          .string()
                          .nullish()
                          .describe('Thread display name or subject.'),
                        ownerId: zod.string().describe('Owner user id.'),
                        projectId: zod
                          .string()
                          .nullish()
                          .describe('Project id associated with the thread.'),
                        providerId: zod
                          .string()
                          .nullish()
                          .describe('Provider-specific thread id.'),
                        senderEmail: zod
                          .string()
                          .nullish()
                          .describe('Sender email address for the preview.'),
                        senderName: zod
                          .string()
                          .nullish()
                          .describe('Sender display name for the preview.'),
                        senderPhotoUrl: zod
                          .string()
                          .nullish()
                          .describe('Sender photo URL for the preview.'),
                        snippet: zod
                          .string()
                          .nullish()
                          .describe('Thread snippet.'),
                        sortTs: zod.iso
                          .datetime({})
                          .describe('Timestamp used for email thread sorting.'),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe(
                            'Timestamp when the thread was last updated.'
                          ),
                        viewedAt: zod.iso
                          .datetime({})
                          .nullish()
                          .describe(
                            'Timestamp when the requesting user last viewed the thread.'
                          ),
                      })
                      .describe(
                        'Email thread preview data as displayed in Soup.'
                      )
                      .and(
                        zod
                          .object({
                            properties: zod
                              .array(
                                zod
                                  .object({
                                    definition: zod
                                      .object({
                                        created_at: zod.iso.datetime({}),
                                        data_type: zod
                                          .enum([
                                            'BOOLEAN',
                                            'DATE',
                                            'NUMBER',
                                            'STRING',
                                            'SELECT_NUMBER',
                                            'SELECT_STRING',
                                            'TAG',
                                            'ENTITY',
                                            'LINK',
                                          ])
                                          .describe(
                                            'Data type for property values, determining storage and validation.'
                                          ),
                                        display_name: zod.string(),
                                        id: zod.uuid(),
                                        is_metadata: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                          ),
                                        is_multi_select: zod.boolean(),
                                        is_system: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system property (stored in DB).'
                                          ),
                                        owner: zod
                                          .union([
                                            zod
                                              .object({
                                                scope: zod.enum(['user']),
                                                user_id: zod.string(),
                                              })
                                              .describe(
                                                'User-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['team']),
                                                team_id: zod.uuid(),
                                              })
                                              .describe(
                                                'Team-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                database_id: zod.uuid(),
                                                scope: zod.enum(['database']),
                                              })
                                              .describe(
                                                'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['system']),
                                              })
                                              .describe(
                                                'System-owned property (no user, team, or database owner).'
                                              ),
                                          ])
                                          .describe(
                                            'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                          ),
                                        specific_entity_type: zod.union([
                                          zod.null(),
                                          zod
                                            .enum([
                                              'CALENDAR_EVENT',
                                              'CALL_RECORD',
                                              'CHANNEL',
                                              'CHAT',
                                              'COMPANY',
                                              'DATABASE_ROW',
                                              'CONTACT',
                                              'DOCUMENT',
                                              'INITIATIVE',
                                              'PROJECT',
                                              'TASK',
                                              'THREAD',
                                              'USER',
                                            ])
                                            .describe(
                                              'Type of entity that can be referenced by entity properties.'
                                            ),
                                        ]),
                                        updated_at: zod.iso.datetime({}),
                                      })
                                      .describe(
                                        'Property definition model (service representation).'
                                      ),
                                    id: zod
                                      .uuid()
                                      .describe(
                                        'Globally unique id of the assignment attaching this property to an entity.'
                                      ),
                                    value: zod
                                      .union([
                                        zod.null(),
                                        zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['Boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe(
                                                    'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                                  ),
                                              })
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Number']),
                                                value: zod
                                                  .number()
                                                  .describe(
                                                    'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                                  ),
                                              })
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['String']),
                                                value: zod
                                                  .string()
                                                  .describe(
                                                    'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                                  ),
                                              })
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe(
                                                    'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                                  ),
                                              })
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'SelectOption',
                                                ]),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'EntityReference',
                                                ]),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entity_id: zod.string(),
                                                        entity_type: zod
                                                          .enum([
                                                            'CALENDAR_EVENT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'COMPANY',
                                                            'DATABASE_ROW',
                                                            'CONTACT',
                                                            'DOCUMENT',
                                                            'INITIATIVE',
                                                            'PROJECT',
                                                            'TASK',
                                                            'THREAD',
                                                            'USER',
                                                          ])
                                                          .describe(
                                                            'Type of entity that can be referenced by entity properties.'
                                                          ),
                                                        specific_message_id: zod
                                                          .uuid()
                                                          .nullish()
                                                          .describe(
                                                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Entity reference for entity-type property values.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          ])
                                          .describe(
                                            'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                          ),
                                      ])
                                      .optional(),
                                  })
                                  .describe(
                                    'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                                  )
                              )
                              .describe('Properties attached to the entity.'),
                          })
                          .describe(
                            'Property fields that can be flattened into property-bearing Soup items.'
                          )
                      )
                      .and(
                        zod.object({
                          attachments: zod
                            .array(
                              zod
                                .object({
                                  contentId: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Content id for inline attachments.'
                                    ),
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'Timestamp when the attachment was created.'
                                    ),
                                  filename: zod
                                    .string()
                                    .nullish()
                                    .describe('Attachment filename.'),
                                  id: zod.uuid().describe('Attachment id.'),
                                  messageId: zod
                                    .uuid()
                                    .describe(
                                      'Message id that owns this attachment.'
                                    ),
                                  mimeType: zod
                                    .string()
                                    .nullish()
                                    .describe('Attachment MIME type.'),
                                  providerAttachmentId: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Provider-specific attachment id.'
                                    ),
                                  sizeBytes: zod
                                    .number()
                                    .nullish()
                                    .describe('Attachment size in bytes.'),
                                })
                                .describe(
                                  'An email attachment as displayed in Soup.'
                                )
                            )
                            .describe('Attachments on the thread.'),
                          labels: zod
                            .array(
                              zod
                                .object({
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'Timestamp when the label was created.'
                                    ),
                                  id: zod.uuid().describe('Label id.'),
                                  labelListVisibility: zod
                                    .enum([
                                      'labelShow',
                                      'labelShowIfUnread',
                                      'labelHide',
                                    ])
                                    .describe(
                                      'Gmail-style label list visibility.'
                                    ),
                                  linkId: zod
                                    .uuid()
                                    .describe(
                                      'Connected account link id for the label.'
                                    ),
                                  messageListVisibility: zod
                                    .enum(['show', 'hide'])
                                    .describe(
                                      'Gmail-style message list visibility for a label.'
                                    ),
                                  name: zod
                                    .string()
                                    .describe('Label display name.'),
                                  providerLabelId: zod
                                    .string()
                                    .describe('Provider-specific label id.'),
                                  type: zod
                                    .enum(['system', 'user'])
                                    .describe(
                                      'Whether an email label is provider-created or user-created.'
                                    ),
                                })
                                .describe(
                                  'An email label as displayed in Soup.'
                                )
                            )
                            .describe('Labels attached to the thread.'),
                          participants: zod
                            .array(
                              zod
                                .object({
                                  emailAddress: zod
                                    .string()
                                    .nullish()
                                    .describe('Contact email address.'),
                                  id: zod.uuid().describe('Contact id.'),
                                  linkId: zod
                                    .uuid()
                                    .describe(
                                      'Connected account link id for the contact.'
                                    ),
                                  name: zod
                                    .string()
                                    .nullish()
                                    .describe('Contact display name.'),
                                  sfsPhotoUrl: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'Contact photo URL from SFS, if available.'
                                    ),
                                })
                                .describe(
                                  'A contact participating in an email thread as displayed in Soup.'
                                )
                            )
                            .describe('Contacts participating in the thread.'),
                        })
                      )
                      .describe(
                        'Email thread preview enriched with related metadata for Soup.'
                      ),
                    tag: zod.enum(['emailThread']),
                  })
                  .describe('Email thread item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        channel: zod
                          .object({
                            channel_type: zod
                              .enum([
                                'public',
                                'private',
                                'direct_message',
                                'team',
                              ])
                              .describe('Type of channel.'),
                            created_at: zod.iso
                              .datetime({})
                              .describe('Creation timestamp.'),
                            id: zod.uuid().describe('Channel id.'),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Channel display name.'),
                            org_id: zod
                              .number()
                              .min(
                                postItemsSoupAstGroupedResponseItemsDataChannelOrgIdMinOne
                              )
                              .nullish()
                              .describe('Organization id.'),
                            owner_id: zod.string().describe('Channel owner.'),
                            team_id: zod.uuid().nullish().describe('Team id.'),
                            updated_at: zod.iso
                              .datetime({})
                              .describe('Update timestamp.'),
                          })
                          .describe('Channel metadata in soup payloads.'),
                        is_participant: zod
                          .boolean()
                          .optional()
                          .describe(
                            "Whether the requesting user is an active participant of the channel.\nFalse for team channels of the user's teams they have not joined."
                          ),
                        participants: zod
                          .array(
                            zod
                              .object({
                                channel_id: zod
                                  .uuid()
                                  .describe(
                                    'Channel id for the participant membership.'
                                  ),
                                joined_at: zod.iso
                                  .datetime({})
                                  .describe(
                                    'Timestamp when the participant joined the channel.'
                                  ),
                                left_at: zod.iso
                                  .datetime({})
                                  .nullish()
                                  .describe(
                                    'Timestamp when the participant left the channel, if any.'
                                  ),
                                role: zod
                                  .enum(['owner', 'admin', 'member'])
                                  .describe(
                                    'The role a user has within a channel.'
                                  ),
                                user_id: zod
                                  .string()
                                  .describe('Participant user id.'),
                              })
                              .describe("A user's membership in a channel.")
                          )
                          .describe('Participants in the channel.'),
                      })
                      .describe(
                        'Channel metadata together with its participants.'
                      )
                      .and(
                        zod
                          .object({
                            latest_message: zod
                              .union([
                                zod.null(),
                                zod
                                  .object({
                                    content: zod
                                      .string()
                                      .describe('Message content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    deleted_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Deletion timestamp.'),
                                    mentions: zod
                                      .array(zod.string())
                                      .describe(
                                        'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                      ),
                                    message_id: zod
                                      .uuid()
                                      .describe('Message id.'),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    thread_id: zod
                                      .uuid()
                                      .nullish()
                                      .describe('Thread parent id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe(
                                    'Lightweight channel message for soup payloads.'
                                  ),
                              ])
                              .optional(),
                            latest_non_thread_message: zod
                              .union([
                                zod.null(),
                                zod
                                  .object({
                                    content: zod
                                      .string()
                                      .describe('Message content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    deleted_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Deletion timestamp.'),
                                    mentions: zod
                                      .array(zod.string())
                                      .describe(
                                        'Message mentions formatted as `{ENTITY_TYPE}:{ENTITY_ID}`.'
                                      ),
                                    message_id: zod
                                      .uuid()
                                      .describe('Message id.'),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    thread_id: zod
                                      .uuid()
                                      .nullish()
                                      .describe('Thread parent id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe(
                                    'Lightweight channel message for soup payloads.'
                                  ),
                              ])
                              .optional(),
                          })
                          .describe('Latest-message bundle for soup payloads.')
                      )
                      .and(
                        zod.object({
                          interacted_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the requesting user last interacted with this channel.'
                            ),
                          viewed_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Timestamp when the requesting user last viewed this channel.'
                            ),
                        })
                      )
                      .describe('A channel as displayed in Soup.'),
                    tag: zod.enum(['channel']),
                  })
                  .describe('Channel item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        attachments: zod
                          .array(
                            zod
                              .object({
                                created_at: zod.iso
                                  .datetime({})
                                  .describe('Creation timestamp.'),
                                entity_id: zod.string().describe('Entity id.'),
                                entity_type: zod
                                  .string()
                                  .describe('Type of entity.'),
                                height: zod
                                  .number()
                                  .nullish()
                                  .describe('Height for images.'),
                                id: zod.uuid().describe('Attachment id.'),
                                width: zod
                                  .number()
                                  .nullish()
                                  .describe('Width for images.'),
                              })
                              .describe('An attachment on a message.')
                          )
                          .describe('Attachments on this message.'),
                        channel_id: zod.uuid().describe('Channel id.'),
                        content: zod.string().describe('Message content.'),
                        created_at: zod.iso
                          .datetime({})
                          .describe('Creation timestamp.'),
                        deleted_at: zod.iso
                          .datetime({})
                          .nullish()
                          .describe('Deletion timestamp.'),
                        edited_at: zod.iso
                          .datetime({})
                          .nullish()
                          .describe('Edit timestamp.'),
                        id: zod.uuid().describe('Message id.'),
                        reactions: zod
                          .array(
                            zod
                              .object({
                                emoji: zod.string().describe('Emoji string.'),
                                users: zod
                                  .array(zod.string())
                                  .describe(
                                    'User ids who added this reaction.'
                                  ),
                              })
                              .describe('A reaction with emoji and user list.')
                          )
                          .describe('Reactions on this message.'),
                        sender: zod
                          .object({
                            avatar_url: zod
                              .string()
                              .nullish()
                              .describe('Avatar URL for bot senders.'),
                            id: zod
                              .string()
                              .describe(
                                'Sender id without the storage namespace prefix.'
                              ),
                            name: zod
                              .string()
                              .nullish()
                              .describe('Display name for bot senders.'),
                            type: zod
                              .enum(['user', 'bot'])
                              .describe(
                                'Public sender type for soup channel messages.'
                              ),
                          })
                          .describe(
                            'Public sender identity for soup channel messages.'
                          ),
                        sender_id: zod.string().describe('Sender id.'),
                        thread: zod
                          .object({
                            latest_reply_at: zod.iso
                              .datetime({})
                              .nullish()
                              .describe('Timestamp of the latest reply.'),
                            preview: zod
                              .array(
                                zod
                                  .object({
                                    attachments: zod
                                      .array(
                                        zod
                                          .object({
                                            created_at: zod.iso
                                              .datetime({})
                                              .describe('Creation timestamp.'),
                                            entity_id: zod
                                              .string()
                                              .describe('Entity id.'),
                                            entity_type: zod
                                              .string()
                                              .describe('Type of entity.'),
                                            height: zod
                                              .number()
                                              .nullish()
                                              .describe('Height for images.'),
                                            id: zod
                                              .uuid()
                                              .describe('Attachment id.'),
                                            width: zod
                                              .number()
                                              .nullish()
                                              .describe('Width for images.'),
                                          })
                                          .describe(
                                            'An attachment on a message.'
                                          )
                                      )
                                      .describe('Attachments on this reply.'),
                                    content: zod
                                      .string()
                                      .describe('Reply content.'),
                                    created_at: zod.iso
                                      .datetime({})
                                      .describe('Creation timestamp.'),
                                    edited_at: zod.iso
                                      .datetime({})
                                      .nullish()
                                      .describe('Edit timestamp.'),
                                    id: zod.uuid().describe('Reply id.'),
                                    reactions: zod
                                      .array(
                                        zod
                                          .object({
                                            emoji: zod
                                              .string()
                                              .describe('Emoji string.'),
                                            users: zod
                                              .array(zod.string())
                                              .describe(
                                                'User ids who added this reaction.'
                                              ),
                                          })
                                          .describe(
                                            'A reaction with emoji and user list.'
                                          )
                                      )
                                      .describe('Reactions on this reply.'),
                                    sender: zod
                                      .object({
                                        avatar_url: zod
                                          .string()
                                          .nullish()
                                          .describe(
                                            'Avatar URL for bot senders.'
                                          ),
                                        id: zod
                                          .string()
                                          .describe(
                                            'Sender id without the storage namespace prefix.'
                                          ),
                                        name: zod
                                          .string()
                                          .nullish()
                                          .describe(
                                            'Display name for bot senders.'
                                          ),
                                        type: zod
                                          .enum(['user', 'bot'])
                                          .describe(
                                            'Public sender type for soup channel messages.'
                                          ),
                                      })
                                      .describe(
                                        'Public sender identity for soup channel messages.'
                                      ),
                                    sender_id: zod
                                      .string()
                                      .describe('Sender id.'),
                                    updated_at: zod.iso
                                      .datetime({})
                                      .describe('Update timestamp.'),
                                  })
                                  .describe('A thread reply shown in preview.')
                              )
                              .describe('Preview replies.'),
                            reply_count: zod
                              .number()
                              .describe('Total reply count.'),
                          })
                          .describe(
                            'Thread metadata and preview replies for soup channel messages.'
                          ),
                        updated_at: zod.iso
                          .datetime({})
                          .describe('Update timestamp.'),
                      })
                      .describe(
                        'A top-level channel message thread for soup payloads.\n\nThis mirrors the public channel-message API shape so soup consumers can\nrender a thread root the same way they render a channel timeline message.'
                      ),
                    tag: zod.enum(['channelThread']),
                  })
                  .describe('Channel thread item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          attended: zod
                            .boolean()
                            .describe(
                              'Whether the requesting user attended this call. Kept for compatibility\nand derived from `status == ATTENDED`.'
                            ),
                          callId: zod.uuid().describe('The call identifier.'),
                          channelId: zod
                            .uuid()
                            .nullish()
                            .describe('The channel this call belongs to.'),
                          channelName: zod
                            .string()
                            .nullish()
                            .describe('Resolved display name for the channel.'),
                          createdBy: zod
                            .string()
                            .describe('User who created the call.'),
                          customName: zod
                            .string()
                            .nullish()
                            .describe(
                              'User-supplied or AI-generated display name for the call.'
                            ),
                          durationMs: zod
                            .number()
                            .nullish()
                            .describe(
                              'Call duration in milliseconds (None if still active).'
                            ),
                          endedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'When the call ended (None if still active).'
                            ),
                          guests: zod
                            .array(
                              zod
                                .object({
                                  displayName: zod
                                    .string()
                                    .describe('Guest-provided display name.'),
                                  id: zod
                                    .uuid()
                                    .describe(
                                      "Opaque guest identity; matches the guest's transcript speaker id."
                                    ),
                                  joinedAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'When the guest joined the call.'
                                    ),
                                  leftAt: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe(
                                      'When the guest left (None if still in an active call).'
                                    ),
                                })
                                .describe(
                                  'A non-account guest of a call record, as displayed in Soup.'
                                )
                            )
                            .describe('Non-account guests in the call.'),
                          isActive: zod
                            .boolean()
                            .describe('Whether the call is currently active.'),
                          participants: zod
                            .array(
                              zod
                                .object({
                                  joinedAt: zod.iso
                                    .datetime({})
                                    .describe('When the user joined the call.'),
                                  leftAt: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe(
                                      'When the user left (None if still in an active call).'
                                    ),
                                  userId: zod
                                    .string()
                                    .describe('The Macro user id.'),
                                })
                                .describe(
                                  'A Macro-account participant in a call record, as displayed in Soup.'
                                )
                            )
                            .describe(
                              'Macro-account participants in the call.'
                            ),
                          startedAt: zod.iso
                            .datetime({})
                            .describe('When the call started.'),
                          status: zod
                            .enum(['ATTENDED', 'MISSED', 'UNATTENDED'])
                            .describe(
                              'Viewer-relative attendance status for a call record.\nSerializes as `ATTENDED`, `MISSED`, or `UNATTENDED`.'
                            ),
                          summary: zod
                            .string()
                            .nullish()
                            .describe(
                              'AI-generated summary of the call. Only set on archived\n`call_records` once summarization has run; active calls always\nreturn `None`.'
                            ),
                        })
                      )
                      .describe(
                        'A call record as displayed in Soup. Excludes room_name, egress_id,\nand transcript — fields that are irrelevant for the soup feed.'
                      ),
                    tag: zod.enum(['call']),
                  })
                  .describe('Call record item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        conferenceProvider: zod
                          .string()
                          .nullish()
                          .describe(
                            'Which conferencing system backs `conference_url`.'
                          ),
                        conferenceUrl: zod
                          .string()
                          .nullish()
                          .describe('Direct conference join URL.'),
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Entity creation timestamp.'),
                        description: zod
                          .string()
                          .nullish()
                          .describe('Optional description.'),
                        extra: zod
                          .object({
                            properties: zod
                              .array(
                                zod
                                  .object({
                                    definition: zod
                                      .object({
                                        created_at: zod.iso.datetime({}),
                                        data_type: zod
                                          .enum([
                                            'BOOLEAN',
                                            'DATE',
                                            'NUMBER',
                                            'STRING',
                                            'SELECT_NUMBER',
                                            'SELECT_STRING',
                                            'TAG',
                                            'ENTITY',
                                            'LINK',
                                          ])
                                          .describe(
                                            'Data type for property values, determining storage and validation.'
                                          ),
                                        display_name: zod.string(),
                                        id: zod.uuid(),
                                        is_metadata: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                          ),
                                        is_multi_select: zod.boolean(),
                                        is_system: zod
                                          .boolean()
                                          .describe(
                                            'Flag to indicate if this is a system property (stored in DB).'
                                          ),
                                        owner: zod
                                          .union([
                                            zod
                                              .object({
                                                scope: zod.enum(['user']),
                                                user_id: zod.string(),
                                              })
                                              .describe(
                                                'User-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['team']),
                                                team_id: zod.uuid(),
                                              })
                                              .describe(
                                                'Team-scoped property.'
                                              ),
                                            zod
                                              .object({
                                                database_id: zod.uuid(),
                                                scope: zod.enum(['database']),
                                              })
                                              .describe(
                                                'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                              ),
                                            zod
                                              .object({
                                                scope: zod.enum(['system']),
                                              })
                                              .describe(
                                                'System-owned property (no user, team, or database owner).'
                                              ),
                                          ])
                                          .describe(
                                            'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                          ),
                                        specific_entity_type: zod.union([
                                          zod.null(),
                                          zod
                                            .enum([
                                              'CALENDAR_EVENT',
                                              'CALL_RECORD',
                                              'CHANNEL',
                                              'CHAT',
                                              'COMPANY',
                                              'DATABASE_ROW',
                                              'CONTACT',
                                              'DOCUMENT',
                                              'INITIATIVE',
                                              'PROJECT',
                                              'TASK',
                                              'THREAD',
                                              'USER',
                                            ])
                                            .describe(
                                              'Type of entity that can be referenced by entity properties.'
                                            ),
                                        ]),
                                        updated_at: zod.iso.datetime({}),
                                      })
                                      .describe(
                                        'Property definition model (service representation).'
                                      ),
                                    id: zod
                                      .uuid()
                                      .describe(
                                        'Globally unique id of the assignment attaching this property to an entity.'
                                      ),
                                    value: zod
                                      .union([
                                        zod.null(),
                                        zod
                                          .union([
                                            zod
                                              .object({
                                                type: zod.enum(['Boolean']),
                                                value: zod
                                                  .boolean()
                                                  .describe(
                                                    'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                                  ),
                                              })
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Number']),
                                                value: zod
                                                  .number()
                                                  .describe(
                                                    'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                                  ),
                                              })
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['String']),
                                                value: zod
                                                  .string()
                                                  .describe(
                                                    'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                                  ),
                                              })
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Date']),
                                                value: zod.iso
                                                  .datetime({})
                                                  .describe(
                                                    'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                                  ),
                                              })
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'SelectOption',
                                                ]),
                                                value: zod
                                                  .array(zod.uuid())
                                                  .describe(
                                                    'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum([
                                                  'EntityReference',
                                                ]),
                                                value: zod
                                                  .array(
                                                    zod
                                                      .object({
                                                        entity_id: zod.string(),
                                                        entity_type: zod
                                                          .enum([
                                                            'CALENDAR_EVENT',
                                                            'CALL_RECORD',
                                                            'CHANNEL',
                                                            'CHAT',
                                                            'COMPANY',
                                                            'DATABASE_ROW',
                                                            'CONTACT',
                                                            'DOCUMENT',
                                                            'INITIATIVE',
                                                            'PROJECT',
                                                            'TASK',
                                                            'THREAD',
                                                            'USER',
                                                          ])
                                                          .describe(
                                                            'Type of entity that can be referenced by entity properties.'
                                                          ),
                                                        specific_message_id: zod
                                                          .uuid()
                                                          .nullish()
                                                          .describe(
                                                            'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                          ),
                                                      })
                                                      .describe(
                                                        'Entity reference for entity-type property values.'
                                                      )
                                                  )
                                                  .describe(
                                                    'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                            zod
                                              .object({
                                                type: zod.enum(['Link']),
                                                value: zod
                                                  .array(zod.string())
                                                  .describe(
                                                    'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                                  ),
                                              })
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          ])
                                          .describe(
                                            'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                          ),
                                      ])
                                      .optional(),
                                  })
                                  .describe(
                                    'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                                  )
                              )
                              .describe('Properties attached to the entity.'),
                          })
                          .describe(
                            'Property fields that can be flattened into property-bearing Soup items.'
                          ),
                        icalUid: zod
                          .string()
                          .describe(
                            'RFC 5545 UID used for source reconciliation.'
                          ),
                        id: zod.uuid().describe('Entity identifier.'),
                        isReadOnly: zod
                          .boolean()
                          .describe(
                            'Whether the selected canonical source is read-only.'
                          ),
                        lastReminderFiredAt: zod.iso
                          .datetime({})
                          .nullish()
                          .describe(
                            "When this event's most recent reminder notification was delivered."
                          ),
                        location: zod
                          .string()
                          .nullish()
                          .describe('Optional location label.'),
                        organizerEmail: zod
                          .string()
                          .nullish()
                          .describe('Organizer email.'),
                        organizerName: zod
                          .string()
                          .nullish()
                          .describe('Organizer display name.'),
                        ownerId: zod.string().describe('Owning Macro user.'),
                        status: zod.string().describe('Canonical status.'),
                        time: zod
                          .union([
                            zod
                              .object({
                                endsAt: zod.iso
                                  .datetime({})
                                  .describe('Exclusive end.'),
                                kind: zod.enum(['timed']),
                                startsAt: zod.iso
                                  .datetime({})
                                  .describe('Inclusive start.'),
                                timeZone: zod
                                  .string()
                                  .nullish()
                                  .describe('Original IANA time zone.'),
                              })
                              .describe('Absolute timed event.'),
                            zod
                              .object({
                                endDate: zod.iso
                                  .date()
                                  .describe('Exclusive end date.'),
                                kind: zod.enum(['allDay']),
                                startDate: zod.iso
                                  .date()
                                  .describe('Inclusive start date.'),
                              })
                              .describe(
                                'All-day event with an exclusive end date.'
                              ),
                          ])
                          .describe(
                            'Timed or all-day calendar event span.\n\nFields are camelCased per variant rather than via `rename_all_fields`,\nwhich utoipa ignores — the generated OpenAPI schema would otherwise\nclaim snake_case fields the wire never carries.'
                          ),
                        title: zod.string().describe('Display title.'),
                        transparency: zod
                          .string()
                          .describe('Availability transparency.'),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe('Entity update timestamp.'),
                        visibility: zod
                          .string()
                          .describe('Canonical visibility.'),
                      })
                      .describe('A canonical calendar event entity in Soup.'),
                    tag: zod.enum(['calendarEvent']),
                  })
                  .describe('Calendar event item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('When the company was created.'),
                          description: zod
                            .string()
                            .nullish()
                            .describe(
                              "Display description from the primary domain's directory entry."
                            ),
                          domains: zod
                            .array(
                              zod
                                .object({
                                  companyId: zod
                                    .uuid()
                                    .describe(
                                      'The id of the company the domain belongs to.'
                                    ),
                                  createdAt: zod.iso
                                    .datetime({})
                                    .describe(
                                      'When the domain record was created.'
                                    ),
                                  domain: zod
                                    .string()
                                    .describe(
                                      'The domain (lowercased, e.g. \"acme.com\").'
                                    ),
                                  id: zod
                                    .uuid()
                                    .describe('The id of the domain record.'),
                                })
                                .describe(
                                  "A CRM domain as displayed in Soup. Mirrors the crm crate's\n[`CrmDomain`] with a stable wire shape that the FE can rely on."
                                )
                            )
                            .describe(
                              'Domains associated with this company, ordered by creation time\nascending (primary first).'
                            ),
                          emailSync: zod
                            .boolean()
                            .describe(
                              'Whether email sync is enabled for this company.'
                            ),
                          hidden: zod
                            .boolean()
                            .describe(
                              'Whether the company is hidden from CRM listings. Soup filters\nthese out by default.'
                            ),
                          id: zod.uuid().describe('The id of the company.'),
                          name: zod
                            .string()
                            .nullish()
                            .describe(
                              "Display name from the primary domain's directory entry, or\n`None` when unresolved."
                            ),
                          teamId: zod
                            .uuid()
                            .describe(
                              'The id of the team that owns this company record.'
                            ),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('When the company was last updated.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'When the requesting user last viewed this company, or `None` if\nnever viewed. Mirrors the `viewed_at` other soup entities carry.'
                            ),
                        })
                      )
                      .describe(
                        'A CRM company as displayed in Soup. Carries the core company\nfields plus display metadata resolved from `crm_domain_directory`\nagainst the primary (earliest-created) domain.'
                      ),
                    tag: zod.enum(['crmCompany']),
                  })
                  .describe('CRM company item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          companyId: zod.uuid().describe('Parent company ID.'),
                          companyName: zod
                            .string()
                            .describe("Parent company's display name."),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Record creation time.'),
                          email: zod.string().describe('Full email address.'),
                          firstInteraction: zod.iso
                            .datetime({})
                            .describe(
                              'Earliest interaction for this team record.'
                            ),
                          hidden: zod
                            .boolean()
                            .describe(
                              'Whether the contact or parent company is hidden.'
                            ),
                          id: zod
                            .uuid()
                            .describe('Original team-owned contact ID.'),
                          lastInteraction: zod.iso
                            .datetime({})
                            .describe(
                              'Latest interaction for this team record.'
                            ),
                          name: zod
                            .string()
                            .nullish()
                            .describe('Team-local display name, if known.'),
                          teamId: zod
                            .uuid()
                            .describe('Team owning this record.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Record update time.'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              "The viewer's latest visit to this record."
                            ),
                        })
                      )
                      .describe(
                        "One original CRM contact record, selected from the viewer's accessible teams."
                      ),
                    tag: zod.enum(['crmContact']),
                  })
                  .describe('Team-owned CRM contact.'),
                zod
                  .object({
                    data: zod
                      .object({
                        createdAt: zod.iso
                          .datetime({})
                          .describe('Timestamp when the record was created.'),
                        foreignEntityId: zod
                          .string()
                          .describe(
                            'Identifier assigned by the external system.'
                          ),
                        foreignEntitySource: zod
                          .string()
                          .describe(
                            'Source system that owns the external identifier.'
                          ),
                        id: zod
                          .uuid()
                          .describe(
                            'Internal primary key for this foreign entity record.'
                          ),
                        metadata: zod
                          .object({})
                          .describe(
                            'Arbitrary metadata stored with the mapping.'
                          ),
                        storedForAuthEntity: zod
                          .string()
                          .describe(
                            'Internal auth entity namespace this foreign entity is stored for.'
                          ),
                        storedForId: zod
                          .string()
                          .describe(
                            'Internal entity identifier this foreign entity is stored for.'
                          ),
                        updatedAt: zod.iso
                          .datetime({})
                          .describe(
                            'Timestamp when the record was last updated.'
                          ),
                      })
                      .describe(
                        'A foreign entity record as displayed in Soup.'
                      ),
                    tag: zod.enum(['foreignEntity']),
                  })
                  .describe('Foreign entity item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          botId: zod
                            .uuid()
                            .describe('The bot running this session'),
                          createdAt: zod.iso
                            .datetime({})
                            .describe('The time the session was created'),
                          harness: zod
                            .string()
                            .describe(
                              'The runtime snapshotted when the session was created.'
                            ),
                          id: zod.uuid().describe('The agent session uuid'),
                          isArchived: zod
                            .boolean()
                            .describe(
                              'Whether the session is archived and read-only.'
                            ),
                          name: zod
                            .string()
                            .describe('The user-facing name of the session'),
                          ownerId: zod
                            .string()
                            .describe('Who the session belongs to'),
                          pullRequestId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              "The linked pull request's Macro entity, when visible to the viewer."
                            ),
                          pullRequestState: zod
                            .union([
                              zod.null(),
                              zod
                                .enum(['open', 'draft', 'closed', 'merged'])
                                .describe(
                                  "Last synchronized state of a session's linked GitHub pull request."
                                ),
                            ])
                            .optional(),
                          pullRequestUrl: zod
                            .string()
                            .nullish()
                            .describe(
                              'The persisted pull request associated with the session.'
                            ),
                          repoBranch: zod
                            .string()
                            .nullish()
                            .describe(
                              'The starting branch selected for this session, not its current branch.'
                            ),
                          repoUrl: zod
                            .string()
                            .nullish()
                            .describe(
                              'The repository the session works with, when one was selected.'
                            ),
                          status: zod
                            .string()
                            .describe(
                              "The session's last known status.\n\n`no_messages` until the first system event arrives, `disconnected` if\nthe connection dropped without a clean close, otherwise the wire name\nof the most recent system event (for example `session\/end`)."
                            ),
                          threadId: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'The channel thread the session was opened from, when any'
                            ),
                          turnState: zod
                            .string()
                            .nullish()
                            .describe(
                              'Last persisted fold turn state. Absent until an older session next runs.'
                            ),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('The time the session was last modified'),
                          viewedAt: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'The time the session was last viewed by the requesting user'
                            ),
                          workingBranch: zod
                            .string()
                            .nullish()
                            .describe(
                              'Last captured working branch, when the runtime has reported one.'
                            ),
                        })
                      )
                      .describe(
                        'An agent session as displayed in Soup.\n\nIncludes the persisted runtime and repository metadata needed to render\ncoding and non-coding sessions without fetching each session separately.'
                      ),
                    tag: zod.enum(['agentSession']),
                  })
                  .describe('Agent session item.'),
                zod
                  .object({
                    data: zod
                      .object({
                        properties: zod
                          .array(
                            zod
                              .object({
                                definition: zod
                                  .object({
                                    created_at: zod.iso.datetime({}),
                                    data_type: zod
                                      .enum([
                                        'BOOLEAN',
                                        'DATE',
                                        'NUMBER',
                                        'STRING',
                                        'SELECT_NUMBER',
                                        'SELECT_STRING',
                                        'TAG',
                                        'ENTITY',
                                        'LINK',
                                      ])
                                      .describe(
                                        'Data type for property values, determining storage and validation.'
                                      ),
                                    display_name: zod.string(),
                                    id: zod.uuid(),
                                    is_metadata: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system-generated metadata property.\nNot stored in database - computed at service layer.'
                                      ),
                                    is_multi_select: zod.boolean(),
                                    is_system: zod
                                      .boolean()
                                      .describe(
                                        'Flag to indicate if this is a system property (stored in DB).'
                                      ),
                                    owner: zod
                                      .union([
                                        zod
                                          .object({
                                            scope: zod.enum(['user']),
                                            user_id: zod.string(),
                                          })
                                          .describe('User-scoped property.'),
                                        zod
                                          .object({
                                            scope: zod.enum(['team']),
                                            team_id: zod.uuid(),
                                          })
                                          .describe('Team-scoped property.'),
                                        zod
                                          .object({
                                            database_id: zod.uuid(),
                                            scope: zod.enum(['database']),
                                          })
                                          .describe(
                                            'Database-scoped property: the definition is a column of one Macro\ndatabase and is invisible to the shared user\/team property namespace.'
                                          ),
                                        zod
                                          .object({
                                            scope: zod.enum(['system']),
                                          })
                                          .describe(
                                            'System-owned property (no user, team, or database owner).'
                                          ),
                                      ])
                                      .describe(
                                        'Defines who owns a property - user-scoped, team-scoped, database-scoped, or system.'
                                      ),
                                    specific_entity_type: zod.union([
                                      zod.null(),
                                      zod
                                        .enum([
                                          'CALENDAR_EVENT',
                                          'CALL_RECORD',
                                          'CHANNEL',
                                          'CHAT',
                                          'COMPANY',
                                          'DATABASE_ROW',
                                          'CONTACT',
                                          'DOCUMENT',
                                          'INITIATIVE',
                                          'PROJECT',
                                          'TASK',
                                          'THREAD',
                                          'USER',
                                        ])
                                        .describe(
                                          'Type of entity that can be referenced by entity properties.'
                                        ),
                                    ]),
                                    updated_at: zod.iso.datetime({}),
                                  })
                                  .describe(
                                    'Property definition model (service representation).'
                                  ),
                                id: zod
                                  .uuid()
                                  .describe(
                                    'Globally unique id of the assignment attaching this property to an entity.'
                                  ),
                                value: zod
                                  .union([
                                    zod.null(),
                                    zod
                                      .union([
                                        zod
                                          .object({
                                            type: zod.enum(['Boolean']),
                                            value: zod
                                              .boolean()
                                              .describe(
                                                'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                              ),
                                          })
                                          .describe(
                                            'Boolean value\nSerializes as: {\"type\": \"Boolean\", \"value\": true}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Number']),
                                            value: zod
                                              .number()
                                              .describe(
                                                'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                              ),
                                          })
                                          .describe(
                                            'Numeric value\nSerializes as: {\"type\": \"Number\", \"value\": 42.5}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['String']),
                                            value: zod
                                              .string()
                                              .describe(
                                                'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                              ),
                                          })
                                          .describe(
                                            'String value\nSerializes as: {\"type\": \"String\", \"value\": \"text\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Date']),
                                            value: zod.iso
                                              .datetime({})
                                              .describe(
                                                'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                              ),
                                          })
                                          .describe(
                                            'Date\/timestamp value\nSerializes as: {\"type\": \"Date\", \"value\": \"2025-01-01T00:00:00Z\"}'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['SelectOption']),
                                            value: zod
                                              .array(zod.uuid())
                                              .describe(
                                                'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Select option(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"SelectOption\", \"value\": [\"uuid\"]} (length 0 or 1)\nMulti-select: {\"type\": \"SelectOption\", \"value\": [\"uuid1\", \"uuid2\", ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['EntityReference']),
                                            value: zod
                                              .array(
                                                zod
                                                  .object({
                                                    entity_id: zod.string(),
                                                    entity_type: zod
                                                      .enum([
                                                        'CALENDAR_EVENT',
                                                        'CALL_RECORD',
                                                        'CHANNEL',
                                                        'CHAT',
                                                        'COMPANY',
                                                        'DATABASE_ROW',
                                                        'CONTACT',
                                                        'DOCUMENT',
                                                        'INITIATIVE',
                                                        'PROJECT',
                                                        'TASK',
                                                        'THREAD',
                                                        'USER',
                                                      ])
                                                      .describe(
                                                        'Type of entity that can be referenced by entity properties.'
                                                      ),
                                                    specific_message_id: zod
                                                      .uuid()
                                                      .nullish()
                                                      .describe(
                                                        'For CHANNEL, CHAT, THREAD entity types - optional specific message ID.\nThis allows referencing a specific message within a thread\/channel\/chat.'
                                                      ),
                                                  })
                                                  .describe(
                                                    'Entity reference for entity-type property values.'
                                                  )
                                              )
                                              .describe(
                                                'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Entity reference(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"EntityReference\", \"value\": [{...}]} (length 0 or 1)\nMulti-select: {\"type\": \"EntityReference\", \"value\": [{...}, {...}, ...]} (length 0+)'
                                          ),
                                        zod
                                          .object({
                                            type: zod.enum(['Link']),
                                            value: zod
                                              .array(zod.string())
                                              .describe(
                                                'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                              ),
                                          })
                                          .describe(
                                            'Link value(s) - always an array (check is_multi_select to determine if single or multi)\nSingle-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\"]} (length 0 or 1)\nMulti-select: {\"type\": \"Link\", \"value\": [\"https:\/\/example.com\", \"https:\/\/other.com\"]} (length 0+)'
                                          ),
                                      ])
                                      .describe(
                                        'Property value (service representation).\n\nRepresents the actual value stored for an entity property.\nThis is serialized to\/from JSONB in the database.'
                                      ),
                                  ])
                                  .optional(),
                              })
                              .describe(
                                'A property attached to a Soup item.\n\nThis is a simplified representation that includes only the definition and value,\nomitting the entity property assignment metadata and options.'
                              )
                          )
                          .describe('Properties attached to the entity.'),
                      })
                      .describe(
                        'Property fields that can be flattened into property-bearing Soup items.'
                      )
                      .and(
                        zod.object({
                          createdAt: zod.iso
                            .datetime({})
                            .describe('Creation timestamp.'),
                          createdBy: zod
                            .string()
                            .nullish()
                            .describe(
                              'Who created the row, when they still exist.'
                            ),
                          databaseId: zod
                            .uuid()
                            .describe('The database the table belongs to.'),
                          id: zod.uuid().describe('Row identifier.'),
                          ownerId: zod
                            .string()
                            .describe(
                              "The database's owner, which owns every row in it."
                            ),
                          position: zod
                            .string()
                            .describe(
                              'Fractional index ordering the row within its table.'
                            ),
                          tableId: zod
                            .uuid()
                            .describe('The table the row belongs to.'),
                          updatedAt: zod.iso
                            .datetime({})
                            .describe('Last modification timestamp.'),
                        })
                      )
                      .describe(
                        "A row of a Macro database table in the Soup feed. Its cells are its\nentity properties; access is its database's."
                      ),
                    tag: zod.enum(['databaseRow']),
                  })
                  .describe('Database row item.'),
              ])
              .describe('A single item in the Soup feed.')
              .and(
                zod.object({
                  frecency_score: zod.number(),
                  is_favorited: zod
                    .boolean()
                    .describe(
                      'Whether the requesting user has favorited this entity.'
                    ),
                  notified_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'When the caller was last notified about this entity, present only\nwhen the page was ordered by `notified_at`. Clients keep the notified\nfeed ordered and date-bucketed on this value.'
                    ),
                  touched_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      "The caller's latest own mutation of this entity, present only when the\npage was ordered by `touched_by_me`. Clients keep the touched feed\nordered on this value, so it can be bumped optimistically."
                    ),
                })
              )
              .describe(
                'API representation of a soup item with its per-viewer enrichments.'
              )
          )
          .describe(
            'Items in this page, keyed by id. Ordering lives in `group.item_ids`.'
          ),
      })
      .describe('Response for fetching a page within one group.')
      .and(
        zod.object({
          mode: zod.enum(['group_page']),
        })
      )
      .describe('Follow-up response for one specific group.'),
  ])
  .describe('Response for grouped soup queries.');

/**
 * @summary Read a bounded timeline with lazy thread previews.
 */
export const messageTimelineParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
});

export const messageTimelineQueryParams = zod.object({
  selection: zod
    .string()
    .nullish()
    .describe(
      'Serialized MessageTimelineQuery; absent selects the latest roots.'
    ),
});

export const messageTimelineResponse = zod
  .object({
    items: zod
      .array(
        zod
          .object({
            attachments: zod
              .array(
                zod
                  .object({
                    created_at: zod.iso
                      .datetime({})
                      .describe('When the attachment was added.'),
                    entity_id: zod
                      .string()
                      .describe('Attached entity identifier.'),
                    entity_type: zod.string().describe('Attached entity type.'),
                    height: zod
                      .number()
                      .nullish()
                      .describe('Optional media height.'),
                    id: zod.uuid().describe('Attachment UUID.'),
                    width: zod
                      .number()
                      .nullish()
                      .describe('Optional media width.'),
                  })
                  .describe('An entity attached to a message.')
              )
              .describe('Attached entities.'),
            bot_profile: zod
              .union([
                zod.null(),
                zod
                  .object({
                    avatar_url: zod
                      .string()
                      .nullish()
                      .describe('Bot avatar URL.'),
                    name: zod.string().describe('Bot display name.'),
                  })
                  .describe(
                    'Public bot profile attached to bot-authored messages.'
                  ),
              ])
              .optional(),
            content: zod.string().describe('Macro Markdown body.'),
            created_at: zod.iso.datetime({}).describe('Creation time.'),
            deleted_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Message tombstone, independent of thread deletion.'),
            edited_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Last content edit, if any.'),
            id: zod.uuid().describe('Message UUID.'),
            imported_author: zod
              .union([
                zod.null(),
                zod
                  .object({
                    name: zod
                      .string()
                      .describe(
                        'Original author text; never interpreted as an authenticated principal.'
                      ),
                  })
                  .describe(
                    'Display attribution for a comment imported from an external document.'
                  ),
              ])
              .optional(),
            mentions: zod
              .array(
                zod
                  .object({
                    entity_id: zod
                      .string()
                      .describe('Mentioned entity identifier.'),
                    entity_type: zod
                      .string()
                      .describe('Mentioned entity type.'),
                  })
                  .describe('A mention tracked in a message body.')
              )
              .describe(
                'Tracked mentions, retained when a caller changes attachments only.'
              ),
            parent: zod
              .union([
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe('A channel, including direct messages.'),
                    type: zod.enum(['channel']),
                  })
                  .describe('A channel, including direct messages.'),
                zod
                  .object({
                    id: zod
                      .string()
                      .describe(
                        'A validated document identifier. Historical document ids need not be UUIDs.'
                      ),
                    type: zod.enum(['document']),
                  })
                  .describe('A document, including tasks and PDFs.'),
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe(
                        'An initiative, presented as a project in the application.'
                      ),
                    type: zod.enum(['initiative']),
                  })
                  .describe(
                    'An initiative, presented as a project in the application.'
                  ),
                zod
                  .object({
                    id: zod.uuid().describe('A CRM company.'),
                    type: zod.enum(['crm_company']),
                  })
                  .describe('A CRM company.'),
                zod
                  .object({
                    id: zod.uuid().describe('A CRM contact.'),
                    type: zod.enum(['crm_contact']),
                  })
                  .describe('A CRM contact.'),
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe('A video call and its persistent chat thread.'),
                    type: zod.enum(['call']),
                  })
                  .describe('A video call and its persistent chat thread.'),
              ])
              .describe(
                'The entity whose permissions and lifecycle govern a message.'
              ),
            reactions: zod
              .array(
                zod
                  .object({
                    emoji: zod.string().describe('Emoji being reacted with.'),
                    users: zod
                      .array(zod.string())
                      .describe('User identifiers.'),
                  })
                  .describe('Reaction emoji and the users who added it.')
              )
              .describe('Aggregated reactions.'),
            sender_id: zod
              .string()
              .describe('Authenticated actor or owner of imported content.'),
            thread_id: zod
              .uuid()
              .nullish()
              .describe('Root message UUID for replies; absent on roots.'),
            triggered_by: zod
              .string()
              .nullish()
              .describe('User who triggered a bot-authored message.'),
            updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
          })
          .describe(
            'Shared message representation for channel timelines and entity discussions.'
          )
          .and(
            zod.object({
              state: zod
                .object({
                  anchor: zod
                    .union([
                      zod.null(),
                      zod
                        .union([
                          zod
                            .object({
                              mark_id: zod
                                .uuid()
                                .describe(
                                  'Mark UUID serialized in the document.'
                                ),
                              marked_text: zod
                                .string()
                                .nullish()
                                .describe(
                                  'The marked text as it read when the discussion was created, already\ntrimmed and bounded. Absent on threads created or imported before\nsnapshots were captured: the text a mark covers cannot be recovered\nfrom the mark id alone.'
                                ),
                              type: zod.enum(['markdown']),
                            })
                            .describe(
                              'Stable Lexical mark identity, independent of transient node keys.'
                            ),
                          zod
                            .object({
                              anchor_id: zod
                                .uuid()
                                .describe('Highlight annotation UUID.'),
                              marked_text: zod
                                .string()
                                .nullish()
                                .describe(
                                  'The text the highlight covers, trimmed and bounded like a markdown\nsnapshot. The highlight owns it and it can be edited there, so it is\nread from the highlight whenever the thread is, never stored on the\nthread. Absent when the highlight carries no text.'
                                ),
                              type: zod.enum(['pdf_highlight']),
                            })
                            .describe(
                              'An independently existing PDF highlight.'
                            ),
                          zod
                            .object({
                              anchor_id: zod
                                .uuid()
                                .describe('Placeable annotation UUID.'),
                              type: zod.enum(['pdf_placeable']),
                            })
                            .describe(
                              'A comment-only placeable PDF annotation. It marks a point on a page,\nnot a span of text, so it has no marked text.'
                            ),
                          zod
                            .object({
                              range: zod
                                .string()
                                .describe(
                                  'A1 cell or range, such as B4 or B4:C9.'
                                ),
                              sheetId: zod
                                .string()
                                .describe(
                                  'Stable sheet identity within the workbook.'
                                ),
                              sheetName: zod
                                .string()
                                .describe(
                                  'Sheet name when the discussion was created.'
                                ),
                              type: zod.enum(['spreadsheet']),
                            })
                            .describe(
                              'A cell or rectangular range in a native spreadsheet.'
                            ),
                          zod
                            .object({
                              nodeId: zod
                                .string()
                                .nullish()
                                .describe(
                                  'Layer the pin follows; absent for a pin on the bare canvas.'
                                ),
                              pageId: zod
                                .string()
                                .describe('Page (canvas) the pin is on.'),
                              type: zod.enum(['fig']),
                              x: zod
                                .number()
                                .describe(
                                  "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                                ),
                              y: zod
                                .number()
                                .describe(
                                  'Vertical offset, measured like `x`.'
                                ),
                            })
                            .describe(
                              'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
                            ),
                        ])
                        .describe(
                          "A thread's location within its document. PDF geometry remains annotation-owned."
                        ),
                    ])
                    .optional(),
                  created_at: zod.iso
                    .datetime({})
                    .describe('Creation time of the discussion.'),
                  deleted_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'Explicit deletion of the entire thread, distinct from root deletion.'
                    ),
                  resolved: zod
                    .boolean()
                    .describe('Whether this discussion has been resolved.'),
                  root_id: zod
                    .uuid()
                    .describe(
                      'Root message UUID; there is no separate thread identity.'
                    ),
                  updated_at: zod.iso
                    .datetime({})
                    .describe('Last state change.'),
                  user_id: zod
                    .string()
                    .describe(
                      'User who owns this discussion, including imported discussions.'
                    ),
                })
                .describe(
                  'State belonging to a whole thread, keyed by its root message.'
                ),
              thread: zod
                .object({
                  latest_reply_at: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('Creation time of the latest live reply.'),
                  preview: zod
                    .array(
                      zod
                        .object({
                          attachments: zod
                            .array(
                              zod
                                .object({
                                  created_at: zod.iso
                                    .datetime({})
                                    .describe('When the attachment was added.'),
                                  entity_id: zod
                                    .string()
                                    .describe('Attached entity identifier.'),
                                  entity_type: zod
                                    .string()
                                    .describe('Attached entity type.'),
                                  height: zod
                                    .number()
                                    .nullish()
                                    .describe('Optional media height.'),
                                  id: zod.uuid().describe('Attachment UUID.'),
                                  width: zod
                                    .number()
                                    .nullish()
                                    .describe('Optional media width.'),
                                })
                                .describe('An entity attached to a message.')
                            )
                            .describe('Attached entities.'),
                          bot_profile: zod
                            .union([
                              zod.null(),
                              zod
                                .object({
                                  avatar_url: zod
                                    .string()
                                    .nullish()
                                    .describe('Bot avatar URL.'),
                                  name: zod
                                    .string()
                                    .describe('Bot display name.'),
                                })
                                .describe(
                                  'Public bot profile attached to bot-authored messages.'
                                ),
                            ])
                            .optional(),
                          content: zod
                            .string()
                            .describe('Macro Markdown body.'),
                          created_at: zod.iso
                            .datetime({})
                            .describe('Creation time.'),
                          deleted_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Message tombstone, independent of thread deletion.'
                            ),
                          edited_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe('Last content edit, if any.'),
                          id: zod.uuid().describe('Message UUID.'),
                          imported_author: zod
                            .union([
                              zod.null(),
                              zod
                                .object({
                                  name: zod
                                    .string()
                                    .describe(
                                      'Original author text; never interpreted as an authenticated principal.'
                                    ),
                                })
                                .describe(
                                  'Display attribution for a comment imported from an external document.'
                                ),
                            ])
                            .optional(),
                          mentions: zod
                            .array(
                              zod
                                .object({
                                  entity_id: zod
                                    .string()
                                    .describe('Mentioned entity identifier.'),
                                  entity_type: zod
                                    .string()
                                    .describe('Mentioned entity type.'),
                                })
                                .describe(
                                  'A mention tracked in a message body.'
                                )
                            )
                            .describe(
                              'Tracked mentions, retained when a caller changes attachments only.'
                            ),
                          parent: zod
                            .union([
                              zod
                                .object({
                                  id: zod
                                    .uuid()
                                    .describe(
                                      'A channel, including direct messages.'
                                    ),
                                  type: zod.enum(['channel']),
                                })
                                .describe(
                                  'A channel, including direct messages.'
                                ),
                              zod
                                .object({
                                  id: zod
                                    .string()
                                    .describe(
                                      'A validated document identifier. Historical document ids need not be UUIDs.'
                                    ),
                                  type: zod.enum(['document']),
                                })
                                .describe(
                                  'A document, including tasks and PDFs.'
                                ),
                              zod
                                .object({
                                  id: zod
                                    .uuid()
                                    .describe(
                                      'An initiative, presented as a project in the application.'
                                    ),
                                  type: zod.enum(['initiative']),
                                })
                                .describe(
                                  'An initiative, presented as a project in the application.'
                                ),
                              zod
                                .object({
                                  id: zod.uuid().describe('A CRM company.'),
                                  type: zod.enum(['crm_company']),
                                })
                                .describe('A CRM company.'),
                              zod
                                .object({
                                  id: zod.uuid().describe('A CRM contact.'),
                                  type: zod.enum(['crm_contact']),
                                })
                                .describe('A CRM contact.'),
                              zod
                                .object({
                                  id: zod
                                    .uuid()
                                    .describe(
                                      'A video call and its persistent chat thread.'
                                    ),
                                  type: zod.enum(['call']),
                                })
                                .describe(
                                  'A video call and its persistent chat thread.'
                                ),
                            ])
                            .describe(
                              'The entity whose permissions and lifecycle govern a message.'
                            ),
                          reactions: zod
                            .array(
                              zod
                                .object({
                                  emoji: zod
                                    .string()
                                    .describe('Emoji being reacted with.'),
                                  users: zod
                                    .array(zod.string())
                                    .describe('User identifiers.'),
                                })
                                .describe(
                                  'Reaction emoji and the users who added it.'
                                )
                            )
                            .describe('Aggregated reactions.'),
                          sender_id: zod
                            .string()
                            .describe(
                              'Authenticated actor or owner of imported content.'
                            ),
                          thread_id: zod
                            .uuid()
                            .nullish()
                            .describe(
                              'Root message UUID for replies; absent on roots.'
                            ),
                          triggered_by: zod
                            .string()
                            .nullish()
                            .describe(
                              'User who triggered a bot-authored message.'
                            ),
                          updated_at: zod.iso
                            .datetime({})
                            .describe('Last persisted update.'),
                        })
                        .describe(
                          'Shared message representation for channel timelines and entity discussions.'
                        )
                    )
                    .describe(
                      'Bounded preview using the canonical message shape.'
                    ),
                  reply_count: zod.number().describe('Total live reply count.'),
                })
                .describe('Thread counts and its oldest three live replies.'),
            })
          )
          .describe(
            'Root message with its small thread preview, independent of its parent type.'
          )
      )
      .describe('Root messages with bounded previews.'),
    next_cursor: zod
      .union([
        zod.null(),
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe(
                'Last message creation time or activity occurrence time.'
              ),
            id: zod
              .uuid()
              .describe(
                'Last entry UUID, used to break timestamp ties across both sources.'
              ),
          })
          .describe('Cursor for a chronological parent timeline.'),
      ])
      .optional(),
    previous_cursor: zod
      .union([
        zod.null(),
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe(
                'Last message creation time or activity occurrence time.'
              ),
            id: zod
              .uuid()
              .describe(
                'Last entry UUID, used to break timestamp ties across both sources.'
              ),
          })
          .describe('Cursor for a chronological parent timeline.'),
      ])
      .optional(),
  })
  .describe('Bidirectional, bounded timeline page, ordered newest root first.');

/**
 * @summary Create a root message or reply.
 */
export const entityMessageCreateParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
});

export const entityMessageCreateBody = zod
  .object({
    anchor: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                mark_id: zod.uuid().describe('Serialized mark identifier.'),
                marked_text: zod
                  .string()
                  .nullish()
                  .describe(
                    'The document text the mark covers, captured by the editor as the\ncomment is written. Trimmed and bounded before it is stored, so an\noversized or whitespace-only claim cannot reach the thread row.'
                  ),
                type: zod.enum(['markdown']),
              })
              .describe('Attach a discussion to a stable Markdown mark.'),
            zod
              .object({
                anchor_id: zod
                  .uuid()
                  .describe('Highlight annotation identifier.'),
                type: zod.enum(['pdf_highlight']),
              })
              .describe(
                'Attach an independently existing highlight on this document.'
              ),
            zod
              .object({
                anchor_id: zod
                  .uuid()
                  .describe(
                    'Client-generated annotation identifier used by optimistic rendering.'
                  ),
                height_pct: zod
                  .number()
                  .describe('Height as a fraction of the page height.'),
                page: zod.number().describe('PDF page number.'),
                type: zod.enum(['pdf_placeable']),
                width_pct: zod
                  .number()
                  .describe('Width as a fraction of the page width.'),
                x_pct: zod
                  .number()
                  .describe(
                    'Horizontal position as a fraction of the page width.'
                  ),
                y_pct: zod
                  .number()
                  .describe(
                    'Vertical position as a fraction of the page height.'
                  ),
              })
              .describe(
                'Atomically create a placeable annotation with the root message.'
              ),
            zod
              .object({
                range: zod
                  .string()
                  .describe('A1 cell or range, such as B4 or B4:C9.'),
                sheetId: zod
                  .string()
                  .describe('Stable sheet identity within the workbook.'),
                sheetName: zod
                  .string()
                  .describe('Sheet name when the discussion was created.'),
                type: zod.enum(['spreadsheet']),
              })
              .describe('A cell or rectangular range in a native spreadsheet.'),
            zod
              .object({
                nodeId: zod
                  .string()
                  .nullish()
                  .describe(
                    'Layer the pin follows; absent or null for a pin on the bare canvas.'
                  ),
                pageId: zod.string().describe('Page (canvas) the pin is on.'),
                type: zod.enum(['fig']),
                x: zod
                  .number()
                  .describe(
                    "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                  ),
                y: zod.number().describe('Vertical offset, measured like `x`.'),
              })
              .describe(
                'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
              ),
          ])
          .describe('Location supplied when creating a document discussion.'),
      ])
      .optional(),
    attachments: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An attachment to add to a message.')
      )
      .optional()
      .describe('Initial attachments.'),
    content: zod.string().describe('Macro Markdown body.'),
    id: zod
      .uuid()
      .nullish()
      .describe(
        'Client-minted UUIDv7 for the new message, so an optimistic message\nalready carries its final id; the server mints one when absent.'
      ),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .optional()
      .describe('Mentions tracked by the editor.'),
    nonce: zod
      .string()
      .nullish()
      .describe('Client nonce for optimistic reconciliation.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root to reply to, if this is a reply.'),
  })
  .describe(
    'Create a root or reply. Thread state may only be supplied on a root.'
  );

export const entityMessageCreateResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Resolve a message within its parent.
 */
export const entityMessageGetMessageParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageGetMessageResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Tombstone one message; deleting a discussion's root deletes the discussion.
 */
export const entityMessageDeleteMessageParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageDeleteMessageQueryParams = zod.object({
  nonce: zod.string().nullish().describe('Client nonce.'),
});

export const entityMessageDeleteMessageResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Edit an owned message.
 */
export const entityMessageEditParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageEditBody = zod
  .object({
    attachments: zod
      .union([
        zod
          .object({
            type: zod.enum(['preserve']),
          })
          .describe('Keep current attachments.'),
        zod
          .object({
            type: zod.enum(['replace']),
            value: zod
              .array(
                zod
                  .object({
                    entity_id: zod
                      .string()
                      .describe('Attached entity identifier.'),
                    entity_type: zod.string().describe('Attached entity type.'),
                    height: zod
                      .number()
                      .nullish()
                      .describe('Optional media height.'),
                    width: zod
                      .number()
                      .nullish()
                      .describe('Optional media width.'),
                  })
                  .describe('An attachment to add to a message.')
              )
              .describe('Replace all attachments.'),
          })
          .describe('Replace all attachments.'),
        zod
          .object({
            type: zod.enum(['delta']),
            value: zod
              .object({
                add: zod
                  .array(
                    zod
                      .object({
                        entity_id: zod
                          .string()
                          .describe('Attached entity identifier.'),
                        entity_type: zod
                          .string()
                          .describe('Attached entity type.'),
                        height: zod
                          .number()
                          .nullish()
                          .describe('Optional media height.'),
                        width: zod
                          .number()
                          .nullish()
                          .describe('Optional media width.'),
                      })
                      .describe('An attachment to add to a message.')
                  )
                  .describe('New attachments to append.'),
                remove: zod
                  .array(zod.uuid())
                  .describe('Existing attachment UUIDs to remove.'),
              })
              .describe(
                'Remove stored attachment identities and append new references.'
              ),
          })
          .describe(
            'Remove stored attachment identities and append new references.'
          ),
      ])
      .optional()
      .describe(
        'Attachment changes interpreted by the common command boundary.'
      ),
    content: zod
      .string()
      .nullish()
      .describe('Replacement body; absent preserves current content.'),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .nullish()
      .describe(
        'Replacement authored mentions; absent preserves current mentions.'
      ),
    nonce: zod.string().nullish().describe('Client mutation nonce.'),
  })
  .describe(
    'Partial updates share the same authorship, reference, and delivery rules as edits.'
  );

export const entityMessageEditResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Change the caller's reaction.
 */
export const entityMessageReactParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageReactBody = zod
  .object({
    add: zod.boolean().describe('Add when true, remove when false.'),
    emoji: zod.string().describe('Emoji.'),
    nonce: zod.string().nullish().describe('Client nonce.'),
  })
  .describe('Reaction mutation for the authenticated user.');

export const entityMessageReactResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Resolve an old link under current parent permissions.
 */
export const entityMessageLegacyParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  legacy_id: zod.number(),
});

export const entityMessageLegacyQueryParams = zod.object({
  thread: zod
    .boolean()
    .optional()
    .describe('Resolve a thread id instead of a comment id.'),
});

export const entityMessageLegacyResponse = zod
  .object({
    attachments: zod
      .array(
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe('When the attachment was added.'),
            entity_id: zod.string().describe('Attached entity identifier.'),
            entity_type: zod.string().describe('Attached entity type.'),
            height: zod.number().nullish().describe('Optional media height.'),
            id: zod.uuid().describe('Attachment UUID.'),
            width: zod.number().nullish().describe('Optional media width.'),
          })
          .describe('An entity attached to a message.')
      )
      .describe('Attached entities.'),
    bot_profile: zod
      .union([
        zod.null(),
        zod
          .object({
            avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
            name: zod.string().describe('Bot display name.'),
          })
          .describe('Public bot profile attached to bot-authored messages.'),
      ])
      .optional(),
    content: zod.string().describe('Macro Markdown body.'),
    created_at: zod.iso.datetime({}).describe('Creation time.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Message tombstone, independent of thread deletion.'),
    edited_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Last content edit, if any.'),
    id: zod.uuid().describe('Message UUID.'),
    imported_author: zod
      .union([
        zod.null(),
        zod
          .object({
            name: zod
              .string()
              .describe(
                'Original author text; never interpreted as an authenticated principal.'
              ),
          })
          .describe(
            'Display attribution for a comment imported from an external document.'
          ),
      ])
      .optional(),
    mentions: zod
      .array(
        zod
          .object({
            entity_id: zod.string().describe('Mentioned entity identifier.'),
            entity_type: zod.string().describe('Mentioned entity type.'),
          })
          .describe('A mention tracked in a message body.')
      )
      .describe(
        'Tracked mentions, retained when a caller changes attachments only.'
      ),
    parent: zod
      .union([
        zod
          .object({
            id: zod.uuid().describe('A channel, including direct messages.'),
            type: zod.enum(['channel']),
          })
          .describe('A channel, including direct messages.'),
        zod
          .object({
            id: zod
              .string()
              .describe(
                'A validated document identifier. Historical document ids need not be UUIDs.'
              ),
            type: zod.enum(['document']),
          })
          .describe('A document, including tasks and PDFs.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            type: zod.enum(['initiative']),
          })
          .describe(
            'An initiative, presented as a project in the application.'
          ),
        zod
          .object({
            id: zod.uuid().describe('A CRM company.'),
            type: zod.enum(['crm_company']),
          })
          .describe('A CRM company.'),
        zod
          .object({
            id: zod.uuid().describe('A CRM contact.'),
            type: zod.enum(['crm_contact']),
          })
          .describe('A CRM contact.'),
        zod
          .object({
            id: zod
              .uuid()
              .describe('A video call and its persistent chat thread.'),
            type: zod.enum(['call']),
          })
          .describe('A video call and its persistent chat thread.'),
      ])
      .describe('The entity whose permissions and lifecycle govern a message.'),
    reactions: zod
      .array(
        zod
          .object({
            emoji: zod.string().describe('Emoji being reacted with.'),
            users: zod.array(zod.string()).describe('User identifiers.'),
          })
          .describe('Reaction emoji and the users who added it.')
      )
      .describe('Aggregated reactions.'),
    sender_id: zod
      .string()
      .describe('Authenticated actor or owner of imported content.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root message UUID for replies; absent on roots.'),
    triggered_by: zod
      .string()
      .nullish()
      .describe('User who triggered a bot-authored message.'),
    updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
  })
  .describe(
    'Shared message representation for channel timelines and entity discussions.'
  );

/**
 * @summary Open a specific discussion from a link or annotation.
 */
export const entityMessageGetThreadParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageGetThreadResponse = zod
  .object({
    replies: zod
      .array(
        zod
          .object({
            attachments: zod
              .array(
                zod
                  .object({
                    created_at: zod.iso
                      .datetime({})
                      .describe('When the attachment was added.'),
                    entity_id: zod
                      .string()
                      .describe('Attached entity identifier.'),
                    entity_type: zod.string().describe('Attached entity type.'),
                    height: zod
                      .number()
                      .nullish()
                      .describe('Optional media height.'),
                    id: zod.uuid().describe('Attachment UUID.'),
                    width: zod
                      .number()
                      .nullish()
                      .describe('Optional media width.'),
                  })
                  .describe('An entity attached to a message.')
              )
              .describe('Attached entities.'),
            bot_profile: zod
              .union([
                zod.null(),
                zod
                  .object({
                    avatar_url: zod
                      .string()
                      .nullish()
                      .describe('Bot avatar URL.'),
                    name: zod.string().describe('Bot display name.'),
                  })
                  .describe(
                    'Public bot profile attached to bot-authored messages.'
                  ),
              ])
              .optional(),
            content: zod.string().describe('Macro Markdown body.'),
            created_at: zod.iso.datetime({}).describe('Creation time.'),
            deleted_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Message tombstone, independent of thread deletion.'),
            edited_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Last content edit, if any.'),
            id: zod.uuid().describe('Message UUID.'),
            imported_author: zod
              .union([
                zod.null(),
                zod
                  .object({
                    name: zod
                      .string()
                      .describe(
                        'Original author text; never interpreted as an authenticated principal.'
                      ),
                  })
                  .describe(
                    'Display attribution for a comment imported from an external document.'
                  ),
              ])
              .optional(),
            mentions: zod
              .array(
                zod
                  .object({
                    entity_id: zod
                      .string()
                      .describe('Mentioned entity identifier.'),
                    entity_type: zod
                      .string()
                      .describe('Mentioned entity type.'),
                  })
                  .describe('A mention tracked in a message body.')
              )
              .describe(
                'Tracked mentions, retained when a caller changes attachments only.'
              ),
            parent: zod
              .union([
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe('A channel, including direct messages.'),
                    type: zod.enum(['channel']),
                  })
                  .describe('A channel, including direct messages.'),
                zod
                  .object({
                    id: zod
                      .string()
                      .describe(
                        'A validated document identifier. Historical document ids need not be UUIDs.'
                      ),
                    type: zod.enum(['document']),
                  })
                  .describe('A document, including tasks and PDFs.'),
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe(
                        'An initiative, presented as a project in the application.'
                      ),
                    type: zod.enum(['initiative']),
                  })
                  .describe(
                    'An initiative, presented as a project in the application.'
                  ),
                zod
                  .object({
                    id: zod.uuid().describe('A CRM company.'),
                    type: zod.enum(['crm_company']),
                  })
                  .describe('A CRM company.'),
                zod
                  .object({
                    id: zod.uuid().describe('A CRM contact.'),
                    type: zod.enum(['crm_contact']),
                  })
                  .describe('A CRM contact.'),
                zod
                  .object({
                    id: zod
                      .uuid()
                      .describe('A video call and its persistent chat thread.'),
                    type: zod.enum(['call']),
                  })
                  .describe('A video call and its persistent chat thread.'),
              ])
              .describe(
                'The entity whose permissions and lifecycle govern a message.'
              ),
            reactions: zod
              .array(
                zod
                  .object({
                    emoji: zod.string().describe('Emoji being reacted with.'),
                    users: zod
                      .array(zod.string())
                      .describe('User identifiers.'),
                  })
                  .describe('Reaction emoji and the users who added it.')
              )
              .describe('Aggregated reactions.'),
            sender_id: zod
              .string()
              .describe('Authenticated actor or owner of imported content.'),
            thread_id: zod
              .uuid()
              .nullish()
              .describe('Root message UUID for replies; absent on roots.'),
            triggered_by: zod
              .string()
              .nullish()
              .describe('User who triggered a bot-authored message.'),
            updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
          })
          .describe(
            'Shared message representation for channel timelines and entity discussions.'
          )
      )
      .describe('Replies in display order.'),
    root: zod
      .object({
        attachments: zod
          .array(
            zod
              .object({
                created_at: zod.iso
                  .datetime({})
                  .describe('When the attachment was added.'),
                entity_id: zod.string().describe('Attached entity identifier.'),
                entity_type: zod.string().describe('Attached entity type.'),
                height: zod
                  .number()
                  .nullish()
                  .describe('Optional media height.'),
                id: zod.uuid().describe('Attachment UUID.'),
                width: zod.number().nullish().describe('Optional media width.'),
              })
              .describe('An entity attached to a message.')
          )
          .describe('Attached entities.'),
        bot_profile: zod
          .union([
            zod.null(),
            zod
              .object({
                avatar_url: zod.string().nullish().describe('Bot avatar URL.'),
                name: zod.string().describe('Bot display name.'),
              })
              .describe(
                'Public bot profile attached to bot-authored messages.'
              ),
          ])
          .optional(),
        content: zod.string().describe('Macro Markdown body.'),
        created_at: zod.iso.datetime({}).describe('Creation time.'),
        deleted_at: zod.iso
          .datetime({})
          .nullish()
          .describe('Message tombstone, independent of thread deletion.'),
        edited_at: zod.iso
          .datetime({})
          .nullish()
          .describe('Last content edit, if any.'),
        id: zod.uuid().describe('Message UUID.'),
        imported_author: zod
          .union([
            zod.null(),
            zod
              .object({
                name: zod
                  .string()
                  .describe(
                    'Original author text; never interpreted as an authenticated principal.'
                  ),
              })
              .describe(
                'Display attribution for a comment imported from an external document.'
              ),
          ])
          .optional(),
        mentions: zod
          .array(
            zod
              .object({
                entity_id: zod
                  .string()
                  .describe('Mentioned entity identifier.'),
                entity_type: zod.string().describe('Mentioned entity type.'),
              })
              .describe('A mention tracked in a message body.')
          )
          .describe(
            'Tracked mentions, retained when a caller changes attachments only.'
          ),
        parent: zod
          .union([
            zod
              .object({
                id: zod
                  .uuid()
                  .describe('A channel, including direct messages.'),
                type: zod.enum(['channel']),
              })
              .describe('A channel, including direct messages.'),
            zod
              .object({
                id: zod
                  .string()
                  .describe(
                    'A validated document identifier. Historical document ids need not be UUIDs.'
                  ),
                type: zod.enum(['document']),
              })
              .describe('A document, including tasks and PDFs.'),
            zod
              .object({
                id: zod
                  .uuid()
                  .describe(
                    'An initiative, presented as a project in the application.'
                  ),
                type: zod.enum(['initiative']),
              })
              .describe(
                'An initiative, presented as a project in the application.'
              ),
            zod
              .object({
                id: zod.uuid().describe('A CRM company.'),
                type: zod.enum(['crm_company']),
              })
              .describe('A CRM company.'),
            zod
              .object({
                id: zod.uuid().describe('A CRM contact.'),
                type: zod.enum(['crm_contact']),
              })
              .describe('A CRM contact.'),
            zod
              .object({
                id: zod
                  .uuid()
                  .describe('A video call and its persistent chat thread.'),
                type: zod.enum(['call']),
              })
              .describe('A video call and its persistent chat thread.'),
          ])
          .describe(
            'The entity whose permissions and lifecycle govern a message.'
          ),
        reactions: zod
          .array(
            zod
              .object({
                emoji: zod.string().describe('Emoji being reacted with.'),
                users: zod.array(zod.string()).describe('User identifiers.'),
              })
              .describe('Reaction emoji and the users who added it.')
          )
          .describe('Aggregated reactions.'),
        sender_id: zod
          .string()
          .describe('Authenticated actor or owner of imported content.'),
        thread_id: zod
          .uuid()
          .nullish()
          .describe('Root message UUID for replies; absent on roots.'),
        triggered_by: zod
          .string()
          .nullish()
          .describe('User who triggered a bot-authored message.'),
        updated_at: zod.iso.datetime({}).describe('Last persisted update.'),
      })
      .describe(
        'Shared message representation for channel timelines and entity discussions.'
      ),
    state: zod
      .object({
        anchor: zod
          .union([
            zod.null(),
            zod
              .union([
                zod
                  .object({
                    mark_id: zod
                      .uuid()
                      .describe('Mark UUID serialized in the document.'),
                    marked_text: zod
                      .string()
                      .nullish()
                      .describe(
                        'The marked text as it read when the discussion was created, already\ntrimmed and bounded. Absent on threads created or imported before\nsnapshots were captured: the text a mark covers cannot be recovered\nfrom the mark id alone.'
                      ),
                    type: zod.enum(['markdown']),
                  })
                  .describe(
                    'Stable Lexical mark identity, independent of transient node keys.'
                  ),
                zod
                  .object({
                    anchor_id: zod
                      .uuid()
                      .describe('Highlight annotation UUID.'),
                    marked_text: zod
                      .string()
                      .nullish()
                      .describe(
                        'The text the highlight covers, trimmed and bounded like a markdown\nsnapshot. The highlight owns it and it can be edited there, so it is\nread from the highlight whenever the thread is, never stored on the\nthread. Absent when the highlight carries no text.'
                      ),
                    type: zod.enum(['pdf_highlight']),
                  })
                  .describe('An independently existing PDF highlight.'),
                zod
                  .object({
                    anchor_id: zod
                      .uuid()
                      .describe('Placeable annotation UUID.'),
                    type: zod.enum(['pdf_placeable']),
                  })
                  .describe(
                    'A comment-only placeable PDF annotation. It marks a point on a page,\nnot a span of text, so it has no marked text.'
                  ),
                zod
                  .object({
                    range: zod
                      .string()
                      .describe('A1 cell or range, such as B4 or B4:C9.'),
                    sheetId: zod
                      .string()
                      .describe('Stable sheet identity within the workbook.'),
                    sheetName: zod
                      .string()
                      .describe('Sheet name when the discussion was created.'),
                    type: zod.enum(['spreadsheet']),
                  })
                  .describe(
                    'A cell or rectangular range in a native spreadsheet.'
                  ),
                zod
                  .object({
                    nodeId: zod
                      .string()
                      .nullish()
                      .describe(
                        'Layer the pin follows; absent for a pin on the bare canvas.'
                      ),
                    pageId: zod
                      .string()
                      .describe('Page (canvas) the pin is on.'),
                    type: zod.enum(['fig']),
                    x: zod
                      .number()
                      .describe(
                        "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                      ),
                    y: zod
                      .number()
                      .describe('Vertical offset, measured like `x`.'),
                  })
                  .describe(
                    'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
                  ),
              ])
              .describe(
                "A thread's location within its document. PDF geometry remains annotation-owned."
              ),
          ])
          .optional(),
        created_at: zod.iso
          .datetime({})
          .describe('Creation time of the discussion.'),
        deleted_at: zod.iso
          .datetime({})
          .nullish()
          .describe(
            'Explicit deletion of the entire thread, distinct from root deletion.'
          ),
        resolved: zod
          .boolean()
          .describe('Whether this discussion has been resolved.'),
        root_id: zod
          .uuid()
          .describe('Root message UUID; there is no separate thread identity.'),
        updated_at: zod.iso.datetime({}).describe('Last state change.'),
        user_id: zod
          .string()
          .describe(
            'User who owns this discussion, including imported discussions.'
          ),
      })
      .describe(
        'State belonging to a whole thread, keyed by its root message.'
      ),
  })
  .describe(
    'A discussion with its root and ordered replies, including root tombstones.'
  );

/**
 * @summary Explicitly remove a discussion.
 */
export const entityMessageDeleteThreadParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessageDeleteThreadQueryParams = zod.object({
  nonce: zod.string().nullish().describe('Client nonce.'),
});

export const entityMessageDeleteThreadResponse = zod
  .object({
    anchor: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                mark_id: zod
                  .uuid()
                  .describe('Mark UUID serialized in the document.'),
                marked_text: zod
                  .string()
                  .nullish()
                  .describe(
                    'The marked text as it read when the discussion was created, already\ntrimmed and bounded. Absent on threads created or imported before\nsnapshots were captured: the text a mark covers cannot be recovered\nfrom the mark id alone.'
                  ),
                type: zod.enum(['markdown']),
              })
              .describe(
                'Stable Lexical mark identity, independent of transient node keys.'
              ),
            zod
              .object({
                anchor_id: zod.uuid().describe('Highlight annotation UUID.'),
                marked_text: zod
                  .string()
                  .nullish()
                  .describe(
                    'The text the highlight covers, trimmed and bounded like a markdown\nsnapshot. The highlight owns it and it can be edited there, so it is\nread from the highlight whenever the thread is, never stored on the\nthread. Absent when the highlight carries no text.'
                  ),
                type: zod.enum(['pdf_highlight']),
              })
              .describe('An independently existing PDF highlight.'),
            zod
              .object({
                anchor_id: zod.uuid().describe('Placeable annotation UUID.'),
                type: zod.enum(['pdf_placeable']),
              })
              .describe(
                'A comment-only placeable PDF annotation. It marks a point on a page,\nnot a span of text, so it has no marked text.'
              ),
            zod
              .object({
                range: zod
                  .string()
                  .describe('A1 cell or range, such as B4 or B4:C9.'),
                sheetId: zod
                  .string()
                  .describe('Stable sheet identity within the workbook.'),
                sheetName: zod
                  .string()
                  .describe('Sheet name when the discussion was created.'),
                type: zod.enum(['spreadsheet']),
              })
              .describe('A cell or rectangular range in a native spreadsheet.'),
            zod
              .object({
                nodeId: zod
                  .string()
                  .nullish()
                  .describe(
                    'Layer the pin follows; absent for a pin on the bare canvas.'
                  ),
                pageId: zod.string().describe('Page (canvas) the pin is on.'),
                type: zod.enum(['fig']),
                x: zod
                  .number()
                  .describe(
                    "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                  ),
                y: zod.number().describe('Vertical offset, measured like `x`.'),
              })
              .describe(
                'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
              ),
          ])
          .describe(
            "A thread's location within its document. PDF geometry remains annotation-owned."
          ),
      ])
      .optional(),
    created_at: zod.iso
      .datetime({})
      .describe('Creation time of the discussion.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Explicit deletion of the entire thread, distinct from root deletion.'
      ),
    resolved: zod
      .boolean()
      .describe('Whether this discussion has been resolved.'),
    root_id: zod
      .uuid()
      .describe('Root message UUID; there is no separate thread identity.'),
    updated_at: zod.iso.datetime({}).describe('Last state change.'),
    user_id: zod
      .string()
      .describe(
        'User who owns this discussion, including imported discussions.'
      ),
  })
  .describe('State belonging to a whole thread, keyed by its root message.');

/**
 * @summary Update a discussion's lifecycle and placement.
 */
export const entityMessagePatchThreadParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
  id: zod.uuid(),
});

export const entityMessagePatchThreadBody = zod
  .object({
    detach_anchor: zod
      .boolean()
      .optional()
      .describe(
        'Move a Markdown discussion to the document when its marked text is removed.'
      ),
    nonce: zod
      .string()
      .nullish()
      .describe('Client nonce for the shared thread update event.'),
    resolved: zod
      .boolean()
      .nullish()
      .describe(
        'Resolve or reopen the discussion; absent preserves its state.'
      ),
  })
  .describe(
    'Partial changes to discussion lifecycle or document anchor placement.'
  );

export const entityMessagePatchThreadResponse = zod
  .object({
    anchor: zod
      .union([
        zod.null(),
        zod
          .union([
            zod
              .object({
                mark_id: zod
                  .uuid()
                  .describe('Mark UUID serialized in the document.'),
                marked_text: zod
                  .string()
                  .nullish()
                  .describe(
                    'The marked text as it read when the discussion was created, already\ntrimmed and bounded. Absent on threads created or imported before\nsnapshots were captured: the text a mark covers cannot be recovered\nfrom the mark id alone.'
                  ),
                type: zod.enum(['markdown']),
              })
              .describe(
                'Stable Lexical mark identity, independent of transient node keys.'
              ),
            zod
              .object({
                anchor_id: zod.uuid().describe('Highlight annotation UUID.'),
                marked_text: zod
                  .string()
                  .nullish()
                  .describe(
                    'The text the highlight covers, trimmed and bounded like a markdown\nsnapshot. The highlight owns it and it can be edited there, so it is\nread from the highlight whenever the thread is, never stored on the\nthread. Absent when the highlight carries no text.'
                  ),
                type: zod.enum(['pdf_highlight']),
              })
              .describe('An independently existing PDF highlight.'),
            zod
              .object({
                anchor_id: zod.uuid().describe('Placeable annotation UUID.'),
                type: zod.enum(['pdf_placeable']),
              })
              .describe(
                'A comment-only placeable PDF annotation. It marks a point on a page,\nnot a span of text, so it has no marked text.'
              ),
            zod
              .object({
                range: zod
                  .string()
                  .describe('A1 cell or range, such as B4 or B4:C9.'),
                sheetId: zod
                  .string()
                  .describe('Stable sheet identity within the workbook.'),
                sheetName: zod
                  .string()
                  .describe('Sheet name when the discussion was created.'),
                type: zod.enum(['spreadsheet']),
              })
              .describe('A cell or rectangular range in a native spreadsheet.'),
            zod
              .object({
                nodeId: zod
                  .string()
                  .nullish()
                  .describe(
                    'Layer the pin follows; absent for a pin on the bare canvas.'
                  ),
                pageId: zod.string().describe('Page (canvas) the pin is on.'),
                type: zod.enum(['fig']),
                x: zod
                  .number()
                  .describe(
                    "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                  ),
                y: zod.number().describe('Vertical offset, measured like `x`.'),
              })
              .describe(
                'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
              ),
          ])
          .describe(
            "A thread's location within its document. PDF geometry remains annotation-owned."
          ),
      ])
      .optional(),
    created_at: zod.iso
      .datetime({})
      .describe('Creation time of the discussion.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Explicit deletion of the entire thread, distinct from root deletion.'
      ),
    resolved: zod
      .boolean()
      .describe('Whether this discussion has been resolved.'),
    root_id: zod
      .uuid()
      .describe('Root message UUID; there is no separate thread identity.'),
    updated_at: zod.iso.datetime({}).describe('Last state change.'),
    user_id: zod
      .string()
      .describe(
        'User who owns this discussion, including imported discussions.'
      ),
  })
  .describe('State belonging to a whole thread, keyed by its root message.');

/**
 * @summary Read a bounded window of messages and the parent's activity, ordered by the server.
 */
export const messageTimelineEntriesParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
});

export const messageTimelineEntriesQueryParams = zod.object({
  selection: zod
    .string()
    .nullish()
    .describe(
      'Serialized MessageTimelineQuery; absent selects the latest roots.'
    ),
});

export const messageTimelineEntriesResponse = zod
  .object({
    entries: zod
      .array(
        zod
          .union([
            zod
              .object({
                message: zod
                  .object({
                    attachments: zod
                      .array(
                        zod
                          .object({
                            created_at: zod.iso
                              .datetime({})
                              .describe('When the attachment was added.'),
                            entity_id: zod
                              .string()
                              .describe('Attached entity identifier.'),
                            entity_type: zod
                              .string()
                              .describe('Attached entity type.'),
                            height: zod
                              .number()
                              .nullish()
                              .describe('Optional media height.'),
                            id: zod.uuid().describe('Attachment UUID.'),
                            width: zod
                              .number()
                              .nullish()
                              .describe('Optional media width.'),
                          })
                          .describe('An entity attached to a message.')
                      )
                      .describe('Attached entities.'),
                    bot_profile: zod
                      .union([
                        zod.null(),
                        zod
                          .object({
                            avatar_url: zod
                              .string()
                              .nullish()
                              .describe('Bot avatar URL.'),
                            name: zod.string().describe('Bot display name.'),
                          })
                          .describe(
                            'Public bot profile attached to bot-authored messages.'
                          ),
                      ])
                      .optional(),
                    content: zod.string().describe('Macro Markdown body.'),
                    created_at: zod.iso.datetime({}).describe('Creation time.'),
                    deleted_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe(
                        'Message tombstone, independent of thread deletion.'
                      ),
                    edited_at: zod.iso
                      .datetime({})
                      .nullish()
                      .describe('Last content edit, if any.'),
                    id: zod.uuid().describe('Message UUID.'),
                    imported_author: zod
                      .union([
                        zod.null(),
                        zod
                          .object({
                            name: zod
                              .string()
                              .describe(
                                'Original author text; never interpreted as an authenticated principal.'
                              ),
                          })
                          .describe(
                            'Display attribution for a comment imported from an external document.'
                          ),
                      ])
                      .optional(),
                    mentions: zod
                      .array(
                        zod
                          .object({
                            entity_id: zod
                              .string()
                              .describe('Mentioned entity identifier.'),
                            entity_type: zod
                              .string()
                              .describe('Mentioned entity type.'),
                          })
                          .describe('A mention tracked in a message body.')
                      )
                      .describe(
                        'Tracked mentions, retained when a caller changes attachments only.'
                      ),
                    parent: zod
                      .union([
                        zod
                          .object({
                            id: zod
                              .uuid()
                              .describe(
                                'A channel, including direct messages.'
                              ),
                            type: zod.enum(['channel']),
                          })
                          .describe('A channel, including direct messages.'),
                        zod
                          .object({
                            id: zod
                              .string()
                              .describe(
                                'A validated document identifier. Historical document ids need not be UUIDs.'
                              ),
                            type: zod.enum(['document']),
                          })
                          .describe('A document, including tasks and PDFs.'),
                        zod
                          .object({
                            id: zod
                              .uuid()
                              .describe(
                                'An initiative, presented as a project in the application.'
                              ),
                            type: zod.enum(['initiative']),
                          })
                          .describe(
                            'An initiative, presented as a project in the application.'
                          ),
                        zod
                          .object({
                            id: zod.uuid().describe('A CRM company.'),
                            type: zod.enum(['crm_company']),
                          })
                          .describe('A CRM company.'),
                        zod
                          .object({
                            id: zod.uuid().describe('A CRM contact.'),
                            type: zod.enum(['crm_contact']),
                          })
                          .describe('A CRM contact.'),
                        zod
                          .object({
                            id: zod
                              .uuid()
                              .describe(
                                'A video call and its persistent chat thread.'
                              ),
                            type: zod.enum(['call']),
                          })
                          .describe(
                            'A video call and its persistent chat thread.'
                          ),
                      ])
                      .describe(
                        'The entity whose permissions and lifecycle govern a message.'
                      ),
                    reactions: zod
                      .array(
                        zod
                          .object({
                            emoji: zod
                              .string()
                              .describe('Emoji being reacted with.'),
                            users: zod
                              .array(zod.string())
                              .describe('User identifiers.'),
                          })
                          .describe(
                            'Reaction emoji and the users who added it.'
                          )
                      )
                      .describe('Aggregated reactions.'),
                    sender_id: zod
                      .string()
                      .describe(
                        'Authenticated actor or owner of imported content.'
                      ),
                    thread_id: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'Root message UUID for replies; absent on roots.'
                      ),
                    triggered_by: zod
                      .string()
                      .nullish()
                      .describe('User who triggered a bot-authored message.'),
                    updated_at: zod.iso
                      .datetime({})
                      .describe('Last persisted update.'),
                  })
                  .describe(
                    'Shared message representation for channel timelines and entity discussions.'
                  )
                  .and(
                    zod.object({
                      state: zod
                        .object({
                          anchor: zod
                            .union([
                              zod.null(),
                              zod
                                .union([
                                  zod
                                    .object({
                                      mark_id: zod
                                        .uuid()
                                        .describe(
                                          'Mark UUID serialized in the document.'
                                        ),
                                      marked_text: zod
                                        .string()
                                        .nullish()
                                        .describe(
                                          'The marked text as it read when the discussion was created, already\ntrimmed and bounded. Absent on threads created or imported before\nsnapshots were captured: the text a mark covers cannot be recovered\nfrom the mark id alone.'
                                        ),
                                      type: zod.enum(['markdown']),
                                    })
                                    .describe(
                                      'Stable Lexical mark identity, independent of transient node keys.'
                                    ),
                                  zod
                                    .object({
                                      anchor_id: zod
                                        .uuid()
                                        .describe('Highlight annotation UUID.'),
                                      marked_text: zod
                                        .string()
                                        .nullish()
                                        .describe(
                                          'The text the highlight covers, trimmed and bounded like a markdown\nsnapshot. The highlight owns it and it can be edited there, so it is\nread from the highlight whenever the thread is, never stored on the\nthread. Absent when the highlight carries no text.'
                                        ),
                                      type: zod.enum(['pdf_highlight']),
                                    })
                                    .describe(
                                      'An independently existing PDF highlight.'
                                    ),
                                  zod
                                    .object({
                                      anchor_id: zod
                                        .uuid()
                                        .describe('Placeable annotation UUID.'),
                                      type: zod.enum(['pdf_placeable']),
                                    })
                                    .describe(
                                      'A comment-only placeable PDF annotation. It marks a point on a page,\nnot a span of text, so it has no marked text.'
                                    ),
                                  zod
                                    .object({
                                      range: zod
                                        .string()
                                        .describe(
                                          'A1 cell or range, such as B4 or B4:C9.'
                                        ),
                                      sheetId: zod
                                        .string()
                                        .describe(
                                          'Stable sheet identity within the workbook.'
                                        ),
                                      sheetName: zod
                                        .string()
                                        .describe(
                                          'Sheet name when the discussion was created.'
                                        ),
                                      type: zod.enum(['spreadsheet']),
                                    })
                                    .describe(
                                      'A cell or rectangular range in a native spreadsheet.'
                                    ),
                                  zod
                                    .object({
                                      nodeId: zod
                                        .string()
                                        .nullish()
                                        .describe(
                                          'Layer the pin follows; absent for a pin on the bare canvas.'
                                        ),
                                      pageId: zod
                                        .string()
                                        .describe(
                                          'Page (canvas) the pin is on.'
                                        ),
                                      type: zod.enum(['fig']),
                                      x: zod
                                        .number()
                                        .describe(
                                          "Horizontal offset from the layer's origin, or the page's when\nthe pin is on no layer, in design units."
                                        ),
                                      y: zod
                                        .number()
                                        .describe(
                                          'Vertical offset, measured like `x`.'
                                        ),
                                    })
                                    .describe(
                                      'A point pinned on a design (`.fig`): on a layer, or on the page\ncanvas when no layer was under it.'
                                    ),
                                ])
                                .describe(
                                  "A thread's location within its document. PDF geometry remains annotation-owned."
                                ),
                            ])
                            .optional(),
                          created_at: zod.iso
                            .datetime({})
                            .describe('Creation time of the discussion.'),
                          deleted_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Explicit deletion of the entire thread, distinct from root deletion.'
                            ),
                          resolved: zod
                            .boolean()
                            .describe(
                              'Whether this discussion has been resolved.'
                            ),
                          root_id: zod
                            .uuid()
                            .describe(
                              'Root message UUID; there is no separate thread identity.'
                            ),
                          updated_at: zod.iso
                            .datetime({})
                            .describe('Last state change.'),
                          user_id: zod
                            .string()
                            .describe(
                              'User who owns this discussion, including imported discussions.'
                            ),
                        })
                        .describe(
                          'State belonging to a whole thread, keyed by its root message.'
                        ),
                      thread: zod
                        .object({
                          latest_reply_at: zod.iso
                            .datetime({})
                            .nullish()
                            .describe(
                              'Creation time of the latest live reply.'
                            ),
                          preview: zod
                            .array(
                              zod
                                .object({
                                  attachments: zod
                                    .array(
                                      zod
                                        .object({
                                          created_at: zod.iso
                                            .datetime({})
                                            .describe(
                                              'When the attachment was added.'
                                            ),
                                          entity_id: zod
                                            .string()
                                            .describe(
                                              'Attached entity identifier.'
                                            ),
                                          entity_type: zod
                                            .string()
                                            .describe('Attached entity type.'),
                                          height: zod
                                            .number()
                                            .nullish()
                                            .describe('Optional media height.'),
                                          id: zod
                                            .uuid()
                                            .describe('Attachment UUID.'),
                                          width: zod
                                            .number()
                                            .nullish()
                                            .describe('Optional media width.'),
                                        })
                                        .describe(
                                          'An entity attached to a message.'
                                        )
                                    )
                                    .describe('Attached entities.'),
                                  bot_profile: zod
                                    .union([
                                      zod.null(),
                                      zod
                                        .object({
                                          avatar_url: zod
                                            .string()
                                            .nullish()
                                            .describe('Bot avatar URL.'),
                                          name: zod
                                            .string()
                                            .describe('Bot display name.'),
                                        })
                                        .describe(
                                          'Public bot profile attached to bot-authored messages.'
                                        ),
                                    ])
                                    .optional(),
                                  content: zod
                                    .string()
                                    .describe('Macro Markdown body.'),
                                  created_at: zod.iso
                                    .datetime({})
                                    .describe('Creation time.'),
                                  deleted_at: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe(
                                      'Message tombstone, independent of thread deletion.'
                                    ),
                                  edited_at: zod.iso
                                    .datetime({})
                                    .nullish()
                                    .describe('Last content edit, if any.'),
                                  id: zod.uuid().describe('Message UUID.'),
                                  imported_author: zod
                                    .union([
                                      zod.null(),
                                      zod
                                        .object({
                                          name: zod
                                            .string()
                                            .describe(
                                              'Original author text; never interpreted as an authenticated principal.'
                                            ),
                                        })
                                        .describe(
                                          'Display attribution for a comment imported from an external document.'
                                        ),
                                    ])
                                    .optional(),
                                  mentions: zod
                                    .array(
                                      zod
                                        .object({
                                          entity_id: zod
                                            .string()
                                            .describe(
                                              'Mentioned entity identifier.'
                                            ),
                                          entity_type: zod
                                            .string()
                                            .describe('Mentioned entity type.'),
                                        })
                                        .describe(
                                          'A mention tracked in a message body.'
                                        )
                                    )
                                    .describe(
                                      'Tracked mentions, retained when a caller changes attachments only.'
                                    ),
                                  parent: zod
                                    .union([
                                      zod
                                        .object({
                                          id: zod
                                            .uuid()
                                            .describe(
                                              'A channel, including direct messages.'
                                            ),
                                          type: zod.enum(['channel']),
                                        })
                                        .describe(
                                          'A channel, including direct messages.'
                                        ),
                                      zod
                                        .object({
                                          id: zod
                                            .string()
                                            .describe(
                                              'A validated document identifier. Historical document ids need not be UUIDs.'
                                            ),
                                          type: zod.enum(['document']),
                                        })
                                        .describe(
                                          'A document, including tasks and PDFs.'
                                        ),
                                      zod
                                        .object({
                                          id: zod
                                            .uuid()
                                            .describe(
                                              'An initiative, presented as a project in the application.'
                                            ),
                                          type: zod.enum(['initiative']),
                                        })
                                        .describe(
                                          'An initiative, presented as a project in the application.'
                                        ),
                                      zod
                                        .object({
                                          id: zod
                                            .uuid()
                                            .describe('A CRM company.'),
                                          type: zod.enum(['crm_company']),
                                        })
                                        .describe('A CRM company.'),
                                      zod
                                        .object({
                                          id: zod
                                            .uuid()
                                            .describe('A CRM contact.'),
                                          type: zod.enum(['crm_contact']),
                                        })
                                        .describe('A CRM contact.'),
                                      zod
                                        .object({
                                          id: zod
                                            .uuid()
                                            .describe(
                                              'A video call and its persistent chat thread.'
                                            ),
                                          type: zod.enum(['call']),
                                        })
                                        .describe(
                                          'A video call and its persistent chat thread.'
                                        ),
                                    ])
                                    .describe(
                                      'The entity whose permissions and lifecycle govern a message.'
                                    ),
                                  reactions: zod
                                    .array(
                                      zod
                                        .object({
                                          emoji: zod
                                            .string()
                                            .describe(
                                              'Emoji being reacted with.'
                                            ),
                                          users: zod
                                            .array(zod.string())
                                            .describe('User identifiers.'),
                                        })
                                        .describe(
                                          'Reaction emoji and the users who added it.'
                                        )
                                    )
                                    .describe('Aggregated reactions.'),
                                  sender_id: zod
                                    .string()
                                    .describe(
                                      'Authenticated actor or owner of imported content.'
                                    ),
                                  thread_id: zod
                                    .uuid()
                                    .nullish()
                                    .describe(
                                      'Root message UUID for replies; absent on roots.'
                                    ),
                                  triggered_by: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                      'User who triggered a bot-authored message.'
                                    ),
                                  updated_at: zod.iso
                                    .datetime({})
                                    .describe('Last persisted update.'),
                                })
                                .describe(
                                  'Shared message representation for channel timelines and entity discussions.'
                                )
                            )
                            .describe(
                              'Bounded preview using the canonical message shape.'
                            ),
                          reply_count: zod
                            .number()
                            .describe('Total live reply count.'),
                        })
                        .describe(
                          'Thread counts and its oldest three live replies.'
                        ),
                    })
                  )
                  .describe(
                    'Root message with its small thread preview, independent of its parent type.'
                  ),
                type: zod.enum(['message']),
              })
              .describe('A root message with its bounded thread preview.'),
            zod
              .object({
                activity: zod
                  .object({
                    action: zod
                      .string()
                      .describe(
                        'Durable action tag. Unknown tags remain representable during rollouts.'
                      ),
                    actor_id: zod
                      .string()
                      .describe('Principal who performed the action.'),
                    id: zod
                      .uuid()
                      .describe(
                        'Stable activity identity, independent of message ids.'
                      ),
                    occurred_at: zod.iso
                      .datetime({})
                      .describe('Immutable chronological position.'),
                    payload: zod
                      .unknown()
                      .optional()
                      .describe("The action's stored payload."),
                  })
                  .describe(
                    'A displayable fact returned together with a message page.'
                  ),
                type: zod.enum(['activity']),
              })
              .describe(
                'A recorded fact about the parent, such as a rename or a finished call.'
              ),
          ])
          .describe("One chronological entry in a parent's timeline.")
      )
      .describe('Messages and activity, newest first.'),
    next_cursor: zod
      .union([
        zod.null(),
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe(
                'Last message creation time or activity occurrence time.'
              ),
            id: zod
              .uuid()
              .describe(
                'Last entry UUID, used to break timestamp ties across both sources.'
              ),
          })
          .describe('Cursor for a chronological parent timeline.'),
      ])
      .optional(),
    previous_cursor: zod
      .union([
        zod.null(),
        zod
          .object({
            created_at: zod.iso
              .datetime({})
              .describe(
                'Last message creation time or activity occurrence time.'
              ),
            id: zod
              .uuid()
              .describe(
                'Last entry UUID, used to break timestamp ties across both sources.'
              ),
          })
          .describe('Cursor for a chronological parent timeline.'),
      ])
      .optional(),
  })
  .describe(
    "A bounded, newest-first window of a parent's messages and activity, ordered\nby the server on one `(timestamp, id)` keyset."
  );

/**
 * @summary Broadcast typing within an authorized discussion.
 */
export const entityMessageTypingParams = zod.object({
  parent_type: zod.string(),
  parent_id: zod.string(),
});

export const entityMessageTypingBody = zod
  .object({
    active: zod.boolean().describe('Whether the caller is typing.'),
    nonce: zod.string().nullish().describe('Client mutation nonce.'),
    thread_id: zod
      .uuid()
      .nullish()
      .describe('Root being replied to, absent for the parent composer.'),
  })
  .describe('Transient typing update.');

/**
 * @summary Gets the users pinned items
 */
export const getPinsHandlerResponse = zod.object({
  data: zod
    .union([
      zod.null(),
      zod.object({
        recent: zod
          .array(
            zod.object({
              activity: zod.union([
                zod.object({
                  branchedFromId: zod
                    .string()
                    .nullish()
                    .describe(
                      'The id of the document this document branched from'
                    ),
                  branchedFromVersionId: zod
                    .number()
                    .nullish()
                    .describe(
                      'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
                    ),
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the document was created'),
                  deletedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the document was deleted'),
                  documentFamilyId: zod
                    .number()
                    .nullish()
                    .describe(
                      'The id of the document family this document belongs to'
                    ),
                  documentVersionId: zod
                    .number()
                    .describe(
                      'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
                    ),
                  fileType: zod
                    .string()
                    .nullish()
                    .describe('The file type of the document (e.g. pdf, docx)'),
                  id: zod.string().describe('The document id'),
                  name: zod.string().describe('The name of the document'),
                  owner: zod.string().describe('The owner of the document'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe(
                      'The id of the project that this document belongs to'
                    ),
                  sha: zod
                    .string()
                    .nullish()
                    .describe(
                      'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                    ),
                  subType: zod
                    .union([
                      zod.null(),
                      zod
                        .union([
                          zod
                            .object({
                              is_completed: zod
                                .boolean()
                                .describe(
                                  'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                ),
                              type: zod.enum(['task']),
                            })
                            .describe(
                              'A task document with its associated properties'
                            ),
                          zod
                            .object({
                              type: zod.enum(['snippet']),
                            })
                            .describe('A snippet document — reusable markdown'),
                          zod
                            .object({
                              type: zod.enum(['skill']),
                            })
                            .describe(
                              'A skill document — markdown instructions for AI'
                            ),
                          zod
                            .object({
                              type: zod.enum(['initiative_description']),
                            })
                            .describe(
                              'The description document of an initiative'
                            ),
                        ])
                        .describe(
                          'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                        ),
                    ])
                    .optional(),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'The time the document instance \/ document BOM was updated'
                    ),
                  type: zod.enum(['document']),
                }),
                zod.object({
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was created'),
                  deletedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was deleted'),
                  id: zod.string().describe('The chat uuid'),
                  isPersistent: zod.boolean(),
                  model: zod
                    .string()
                    .nullish()
                    .describe('The model used to generate the chat'),
                  name: zod.string().describe('The name of the chat'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe('The project id of the chat'),
                  tokenCount: zod.number().nullish(),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was last updated'),
                  userId: zod.string().describe('Who the chat belongs to'),
                  type: zod.enum(['chat']),
                }),
                zod.object({
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the project was created'),
                  deletedAt: zod.iso.datetime({}).nullish(),
                  id: zod.string().describe('The id of the project'),
                  name: zod.string().describe('The name of the project'),
                  parentId: zod
                    .string()
                    .nullish()
                    .describe('The parent project id'),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the project was updated'),
                  userId: zod
                    .string()
                    .describe('The user id of who created the project'),
                  type: zod.enum(['project']),
                }),
              ]),
              item: zod.union([
                zod.object({
                  branchedFromId: zod
                    .string()
                    .nullish()
                    .describe(
                      'The id of the document this document branched from'
                    ),
                  branchedFromVersionId: zod
                    .number()
                    .nullish()
                    .describe(
                      'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
                    ),
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the document was created'),
                  deletedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the document was deleted'),
                  documentFamilyId: zod
                    .number()
                    .nullish()
                    .describe(
                      'The id of the document family this document belongs to'
                    ),
                  documentVersionId: zod
                    .number()
                    .describe(
                      'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
                    ),
                  fileType: zod
                    .string()
                    .nullish()
                    .describe('The file type of the document (e.g. pdf, docx)'),
                  id: zod.string().describe('The document id'),
                  name: zod.string().describe('The name of the document'),
                  owner: zod.string().describe('The owner of the document'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe(
                      'The id of the project that this document belongs to'
                    ),
                  sha: zod
                    .string()
                    .nullish()
                    .describe(
                      'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                    ),
                  subType: zod
                    .union([
                      zod.null(),
                      zod
                        .union([
                          zod
                            .object({
                              is_completed: zod
                                .boolean()
                                .describe(
                                  'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                                ),
                              type: zod.enum(['task']),
                            })
                            .describe(
                              'A task document with its associated properties'
                            ),
                          zod
                            .object({
                              type: zod.enum(['snippet']),
                            })
                            .describe('A snippet document — reusable markdown'),
                          zod
                            .object({
                              type: zod.enum(['skill']),
                            })
                            .describe(
                              'A skill document — markdown instructions for AI'
                            ),
                          zod
                            .object({
                              type: zod.enum(['initiative_description']),
                            })
                            .describe(
                              'The description document of an initiative'
                            ),
                        ])
                        .describe(
                          'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                        ),
                    ])
                    .optional(),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe(
                      'The time the document instance \/ document BOM was updated'
                    ),
                  type: zod.enum(['document']),
                }),
                zod.object({
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was created'),
                  deletedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was deleted'),
                  id: zod.string().describe('The chat uuid'),
                  isPersistent: zod.boolean(),
                  model: zod
                    .string()
                    .nullish()
                    .describe('The model used to generate the chat'),
                  name: zod.string().describe('The name of the chat'),
                  projectId: zod
                    .string()
                    .nullish()
                    .describe('The project id of the chat'),
                  tokenCount: zod.number().nullish(),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the chat was last updated'),
                  userId: zod.string().describe('Who the chat belongs to'),
                  type: zod.enum(['chat']),
                }),
                zod.object({
                  createdAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the project was created'),
                  deletedAt: zod.iso.datetime({}).nullish(),
                  id: zod.string().describe('The id of the project'),
                  name: zod.string().describe('The name of the project'),
                  parentId: zod
                    .string()
                    .nullish()
                    .describe('The parent project id'),
                  updatedAt: zod.iso
                    .datetime({})
                    .nullish()
                    .describe('The time the project was updated'),
                  userId: zod
                    .string()
                    .describe('The user id of who created the project'),
                  type: zod.enum(['project']),
                }),
              ]),
              pinIndex: zod.number().describe('The pin index'),
            })
          )
          .describe('The pins returned from the query'),
      }),
    ])
    .optional(),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Saves the updated order of the pins for the user
 */
export const reorderPinsHandlerBodyItem = zod.object({
  pinIndex: zod.number().describe('The index of the pin'),
  pinnedItemId: zod.string().describe('The id of the pinned item'),
  pinnedItemType: zod.string().describe('The type of the pin'),
});
export const reorderPinsHandlerBody = zod.array(reorderPinsHandlerBodyItem);

export const reorderPinsHandlerResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Pins the item for the user
 */
export const addPinHandlerParams = zod.object({
  pinned_item_id: zod.string().describe('ID of the pinned item'),
});

export const addPinHandlerBody = zod.object({
  pinIndex: zod.number().describe('The index of the pin'),
  pinType: zod.string().describe('The type of the pin'),
});

export const addPinHandlerResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Deletes the pin for the user
 */
export const removePinHandlerParams = zod.object({
  pinned_item_id: zod.string().describe('ID of the pinned item'),
});

export const removePinHandlerBody = zod.object({
  pinType: zod.string().describe('The type of the pin'),
});

export const removePinHandlerResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary List projects visible to the authenticated user.
 */
export const getProjectsHandlerResponse = zod.object({
  data: zod
    .array(
      zod.object({
        createdAt: zod.iso
          .datetime({})
          .nullish()
          .describe('The time the project was created'),
        deletedAt: zod.iso.datetime({}).nullish(),
        id: zod.string().describe('The id of the project'),
        name: zod.string().describe('The name of the project'),
        parentId: zod.string().nullish().describe('The parent project id'),
        updatedAt: zod.iso
          .datetime({})
          .nullish()
          .describe('The time the project was updated'),
        userId: zod.string().describe('The user id of who created the project'),
        type: zod.enum(['project']),
      })
    )
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Create a project, optionally beneath an existing project.
 */
export const createProjectHandlerBody = zod.object({
  name: zod.string().describe('The name of the project.'),
  projectParentId: zod
    .uuid()
    .nullish()
    .describe('The project that the new project will belong to.'),
});

export const createProjectHandlerResponse = zod.object({
  data: zod.object({
    createdAt: zod.iso
      .datetime({})
      .nullish()
      .describe('The time the project was created'),
    deletedAt: zod.iso.datetime({}).nullish(),
    id: zod.string().describe('The id of the project'),
    name: zod.string().describe('The name of the project'),
    parentId: zod.string().nullish().describe('The parent project id'),
    updatedAt: zod.iso
      .datetime({})
      .nullish()
      .describe('The time the project was updated'),
    userId: zod.string().describe('The user id of who created the project'),
    type: zod.enum(['project']),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary List pending root projects owned by the authenticated user.
 */
export const getPendingProjectsHandlerResponse = zod.object({
  data: zod
    .array(
      zod
        .object({
          createdAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the project was created'),
          deletedAt: zod.iso.datetime({}).nullish(),
          id: zod.string().describe('The id of the project'),
          name: zod.string().describe('The name of the project'),
          parentId: zod.string().nullish().describe('The parent project id'),
          updatedAt: zod.iso
            .datetime({})
            .nullish()
            .describe('The time the project was updated'),
          userId: zod
            .string()
            .describe('The user id of who created the project'),
          type: zod.enum(['project']),
        })
        .and(
          zod.object({
            documentStatuses: zod.array(
              zod.object({
                documentId: zod.string(),
                status: zod
                  .enum(['pending', 'completed', 'failed', 'unknown'])
                  .describe('The upload status of the document'),
              })
            ),
          })
        )
    )
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Get previews for a batch of project IDs.
 */
export const getBatchProjectPreviewBody = zod.object({
  projectIds: zod.array(zod.string()),
});

export const getBatchProjectPreviewResponse = zod.object({
  previews: zod.array(
    zod.union([
      zod
        .object({
          id: zod.string(),
          name: zod.string(),
          owner: zod.string(),
          path: zod.array(zod.string()),
          updatedAt: zod.iso.datetime({}).nullish(),
        })
        .and(
          zod.object({
            access: zod.enum(['access']),
          })
        ),
      zod
        .object({
          id: zod.string(),
        })
        .and(
          zod.object({
            access: zod.enum(['no_access']),
          })
        ),
      zod
        .object({
          id: zod.string(),
        })
        .and(
          zod.object({
            access: zod.enum(['does_not_exist']),
          })
        ),
    ])
  ),
});

/**
 * @summary Upload a folder tree and create its upload destinations.
 */
export const uploadFolderHandlerBody = zod.object({
  content: zod
    .array(
      zod.object({
        fileType: zod
          .union([
            zod.null(),
            zod
              .enum([
                'docx',
                'pdf',
                'md',
                'spreadsheet',
                'canvas',
                'coffee',
                'cson',
                'iced',
                'c',
                'i',
                'cpp',
                'cppm',
                'cc',
                'ccm',
                'cxx',
                'cxxm',
                'cplusplus',
                'cplusplusm',
                'hpp',
                'hh',
                'hxx',
                'hplusplus',
                'h',
                'ii',
                'ino',
                'inl',
                'ipp',
                'ixx',
                'tpp',
                'txx',
                'hppin',
                'hin',
                'cu',
                'cuh',
                'cs',
                'csx',
                'cake',
                'css',
                'dart',
                'diff',
                'patch',
                'rej',
                'dockerfile',
                'containerfile',
                'go',
                'handlebars',
                'hbs',
                'hjs',
                'hlsl',
                'hlsli',
                'fx',
                'fxh',
                'vsh',
                'psh',
                'cginc',
                'compute',
                'html',
                'htm',
                'shtml',
                'xhtml',
                'xht',
                'mdoc',
                'jsp',
                'asp',
                'aspx',
                'jshtm',
                'volt',
                'ejs',
                'rhtml',
                'ini',
                'conf',
                'properties',
                'cfg',
                'directory',
                'gitattributes',
                'gitconfig',
                'gitmodules',
                'editorconfig',
                'repo',
                'java',
                'jav',
                'jsx',
                'js',
                'es6',
                'mjs',
                'cjs',
                'pac',
                'json',
                'bowerrc',
                'jscsrc',
                'webmanifest',
                'jsmap',
                'cssmap',
                'tsmap',
                'har',
                'jslintrc',
                'jsonld',
                'geojson',
                'ipynb',
                'vuerc',
                'jsonc',
                'eslintrc',
                'eslintrcjson',
                'jsfmtrc',
                'jshintrc',
                'swcrc',
                'hintrc',
                'babelrc',
                'jsonl',
                'ndjson',
                'codesnippets',
                'jl',
                'jmd',
                'sty',
                'cls',
                'bbx',
                'cbx',
                'tex',
                'ltx',
                'ctx',
                'bib',
                'less',
                'log',
                'lua',
                'mak',
                'mk',
                'mkd',
                'mdwn',
                'mdown',
                'markdown',
                'markdn',
                'mdtxt',
                'mdtext',
                'workbook',
                'm',
                'mm',
                'pl',
                'pm',
                'pod',
                't',
                'psgi',
                'raku',
                'rakumod',
                'rakutest',
                'rakudoc',
                'nqp',
                'p6',
                'pl6',
                'pm6',
                'php',
                'php4',
                'php5',
                'phtml',
                'ctp',
                'ps1',
                'psm1',
                'psd1',
                'pssc',
                'psrc',
                'py',
                'rpy',
                'pyw',
                'cpy',
                'gyp',
                'gypi',
                'pyi',
                'ipy',
                'pyt',
                'r',
                'rhistory',
                'rprofile',
                'rt',
                'cshtml',
                'razor',
                'rb',
                'rbx',
                'rjs',
                'gemspec',
                'rake',
                'ru',
                'erb',
                'podspec',
                'rbi',
                'rs',
                'scss',
                'sass',
                'shader',
                'sh',
                'bash',
                'bashrc',
                'bashaliases',
                'bashprofile',
                'bashlogin',
                'ebuild',
                'eclass',
                'profile',
                'bashlogout',
                'xprofile',
                'xsession',
                'xsessionrc',
                'zsh',
                'zshrc',
                'zprofile',
                'zlogin',
                'zlogout',
                'zshenv',
                'zshtheme',
                'fish',
                'ksh',
                'csh',
                'cshrc',
                'tcshrc',
                'yashrc',
                'yashprofile',
                'sql',
                'dsql',
                'swift',
                'ts',
                'cts',
                'mts',
                'tsx',
                'tsbuildinfo',
                'xml',
                'xsd',
                'ascx',
                'atom',
                'axml',
                'axaml',
                'bpmn',
                'cpt',
                'csl',
                'csproj',
                'csprojuser',
                'dita',
                'ditamap',
                'dtd',
                'ent',
                'mod',
                'dtml',
                'fsproj',
                'fxml',
                'iml',
                'isml',
                'jmx',
                'launch',
                'menu',
                'mxml',
                'nuspec',
                'opml',
                'owl',
                'proj',
                'props',
                'pt',
                'publishsettings',
                'pubxml',
                'pubxmluser',
                'rbxlx',
                'rbxmx',
                'rdf',
                'rng',
                'rss',
                'shproj',
                'storyboard',
                'targets',
                'tld',
                'tmx',
                'vbproj',
                'vbprojuser',
                'vcxproj',
                'vcxprojfilters',
                'wsdl',
                'wxi',
                'wxl',
                'wxs',
                'xaml',
                'xbl',
                'xib',
                'xlf',
                'xliff',
                'xpdl',
                'xul',
                'xoml',
                'xsl',
                'xslt',
                'yaml',
                'yml',
                'eyaml',
                'eyml',
                'cff',
                'yamltmlanguage',
                'yamltmpreferences',
                'yamltmtheme',
                'winget',
                'txt',
                'csv',
                'tsv',
                'jpeg',
                'jpg',
                'png',
                'gif',
                'svg',
                'webp',
                'avif',
                'bmp',
                'ico',
                'tiff',
                'tif',
                'heic',
                'heif',
                'tar',
                'targz',
                'tgz',
                'gz',
                'bz2',
                'tarbz2',
                'tbz2',
                'z',
                'tarz',
                'lz',
                'tarlz',
                'xz',
                'tarxz',
                'txz',
                'lzma',
                'tarlzma',
                'rar',
                'sevenz',
                'zst',
                'tarzst',
                'tzst',
                'zip',
                'exe',
                'msi',
                'dll',
                'bat',
                'cmd',
                'com',
                'appimage',
                'app',
                'bin',
                'deb',
                'rpm',
                'apk',
                'dmg',
                'pkg',
                'crx',
                'xpi',
                'mp3',
                'wav',
                'ogg',
                'flac',
                'aac',
                'm4a',
                'wma',
                'mid',
                'midi',
                'mp4',
                'mkv',
                'webm',
                'avi',
                'mov',
                'wmv',
                'mpg',
                'mpeg',
                'm4v',
                'flv',
                'f4v',
                'threegp',
                'ttf',
                'otf',
                'woff',
                'woff2',
                'eot',
                'rtf',
                'odt',
                'ods',
                'odp',
                'odg',
                'odf',
                'epub',
                'mobi',
                'azw',
                'azw3',
                'djvu',
                'xls',
                'xlsm',
                'ppt',
                'doc',
                'pptx',
                'xlsx',
                'db',
                'sqlite',
                'sqlite3',
                'mdb',
                'accdb',
                'dbf',
                'plist',
                'toml',
                'env',
                'dot',
                'gv',
                'torrent',
                'ics',
                'vcf',
                'ai',
                'eps',
                'ps',
                'dxf',
                'dwg',
                'fig',
                'psd',
                'psb',
                'stl',
                'obj',
                'fbx',
                'blend',
                'dae',
                'threeds',
                'gltf',
                'glb',
                'vhd',
                'vhdx',
                'vmdk',
                'ova',
                'ovf',
                'iso',
                'img',
                'swf',
              ])
              .describe(
                'Generates a FileType enum and associated ContentType enum with their implementations.\n\nThis macro takes a list of tuples in the format:\n(Variant, \"extension\", \"mime_type\", CONTENT_TYPE_VARIANT)\n\nFor each tuple it generates:\n- A variant in the FileType enum\n- A variant in the ContentType enum\n- Implementations for:\n  - FileType::to_str() - Converts FileType to extension string\n  - FileType::from_str() - Converts extension string to FileType\n  - From<FileType> for ContentType - Maps FileType to ContentType\n  - ContentType::mime_type() - Gets MIME type for ContentType\n'
              ),
          ])
          .optional(),
        fullName: zod
          .string()
          .describe('The full OS name of the file for deduplication'),
        name: zod
          .string()
          .describe('The name of the file, without the extension'),
        relativePath: zod
          .string()
          .describe(
            'The relative path of the file.\n\nThis is the `webkitRelativePath` with the name of the file stripped at the end.'
          ),
        sha: zod.string().describe('The sha of the file.'),
      })
    )
    .describe('The content of the folder'),
  folders: zod
    .array(zod.string())
    .optional()
    .describe(
      'Relative paths of folders to create even when they hold no files.\n\nUses the same shape as [FolderItem::relative_path], root folder included.'
    ),
  parentId: zod
    .string()
    .nullish()
    .describe('Optional parent project id to upload the folder into'),
  rootFolderName: zod
    .string()
    .describe(
      'The name of the folder you are uploading.\n\nThis is used to help us generate the folder map more easily.'
    ),
  uploadRequestId: zod.string().describe('The upload request id'),
});

export const uploadFolderHandlerResponse = zod.object({
  data: zod
    .object({
      destinationMap: zod.record(
        zod.string(),
        zod.union([
          zod
            .object({
              presignedUrl: zod
                .string()
                .describe('The presigned url used to upload the sha'),
              sha: zod.string().describe('The sha of the item'),
            })
            .and(
              zod.object({
                type: zod.enum(['external']),
              })
            ),
          zod
            .object({
              bucket: zod.string(),
              key: zod.string(),
            })
            .and(
              zod.object({
                type: zod.enum(['internal']),
              })
            ),
        ])
      ),
      fileSystem: zod.union([
        zod.object({
          document_id: zod.string(),
          item: zod.object({
            fileType: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'docx',
                    'pdf',
                    'md',
                    'spreadsheet',
                    'canvas',
                    'coffee',
                    'cson',
                    'iced',
                    'c',
                    'i',
                    'cpp',
                    'cppm',
                    'cc',
                    'ccm',
                    'cxx',
                    'cxxm',
                    'cplusplus',
                    'cplusplusm',
                    'hpp',
                    'hh',
                    'hxx',
                    'hplusplus',
                    'h',
                    'ii',
                    'ino',
                    'inl',
                    'ipp',
                    'ixx',
                    'tpp',
                    'txx',
                    'hppin',
                    'hin',
                    'cu',
                    'cuh',
                    'cs',
                    'csx',
                    'cake',
                    'css',
                    'dart',
                    'diff',
                    'patch',
                    'rej',
                    'dockerfile',
                    'containerfile',
                    'go',
                    'handlebars',
                    'hbs',
                    'hjs',
                    'hlsl',
                    'hlsli',
                    'fx',
                    'fxh',
                    'vsh',
                    'psh',
                    'cginc',
                    'compute',
                    'html',
                    'htm',
                    'shtml',
                    'xhtml',
                    'xht',
                    'mdoc',
                    'jsp',
                    'asp',
                    'aspx',
                    'jshtm',
                    'volt',
                    'ejs',
                    'rhtml',
                    'ini',
                    'conf',
                    'properties',
                    'cfg',
                    'directory',
                    'gitattributes',
                    'gitconfig',
                    'gitmodules',
                    'editorconfig',
                    'repo',
                    'java',
                    'jav',
                    'jsx',
                    'js',
                    'es6',
                    'mjs',
                    'cjs',
                    'pac',
                    'json',
                    'bowerrc',
                    'jscsrc',
                    'webmanifest',
                    'jsmap',
                    'cssmap',
                    'tsmap',
                    'har',
                    'jslintrc',
                    'jsonld',
                    'geojson',
                    'ipynb',
                    'vuerc',
                    'jsonc',
                    'eslintrc',
                    'eslintrcjson',
                    'jsfmtrc',
                    'jshintrc',
                    'swcrc',
                    'hintrc',
                    'babelrc',
                    'jsonl',
                    'ndjson',
                    'codesnippets',
                    'jl',
                    'jmd',
                    'sty',
                    'cls',
                    'bbx',
                    'cbx',
                    'tex',
                    'ltx',
                    'ctx',
                    'bib',
                    'less',
                    'log',
                    'lua',
                    'mak',
                    'mk',
                    'mkd',
                    'mdwn',
                    'mdown',
                    'markdown',
                    'markdn',
                    'mdtxt',
                    'mdtext',
                    'workbook',
                    'm',
                    'mm',
                    'pl',
                    'pm',
                    'pod',
                    't',
                    'psgi',
                    'raku',
                    'rakumod',
                    'rakutest',
                    'rakudoc',
                    'nqp',
                    'p6',
                    'pl6',
                    'pm6',
                    'php',
                    'php4',
                    'php5',
                    'phtml',
                    'ctp',
                    'ps1',
                    'psm1',
                    'psd1',
                    'pssc',
                    'psrc',
                    'py',
                    'rpy',
                    'pyw',
                    'cpy',
                    'gyp',
                    'gypi',
                    'pyi',
                    'ipy',
                    'pyt',
                    'r',
                    'rhistory',
                    'rprofile',
                    'rt',
                    'cshtml',
                    'razor',
                    'rb',
                    'rbx',
                    'rjs',
                    'gemspec',
                    'rake',
                    'ru',
                    'erb',
                    'podspec',
                    'rbi',
                    'rs',
                    'scss',
                    'sass',
                    'shader',
                    'sh',
                    'bash',
                    'bashrc',
                    'bashaliases',
                    'bashprofile',
                    'bashlogin',
                    'ebuild',
                    'eclass',
                    'profile',
                    'bashlogout',
                    'xprofile',
                    'xsession',
                    'xsessionrc',
                    'zsh',
                    'zshrc',
                    'zprofile',
                    'zlogin',
                    'zlogout',
                    'zshenv',
                    'zshtheme',
                    'fish',
                    'ksh',
                    'csh',
                    'cshrc',
                    'tcshrc',
                    'yashrc',
                    'yashprofile',
                    'sql',
                    'dsql',
                    'swift',
                    'ts',
                    'cts',
                    'mts',
                    'tsx',
                    'tsbuildinfo',
                    'xml',
                    'xsd',
                    'ascx',
                    'atom',
                    'axml',
                    'axaml',
                    'bpmn',
                    'cpt',
                    'csl',
                    'csproj',
                    'csprojuser',
                    'dita',
                    'ditamap',
                    'dtd',
                    'ent',
                    'mod',
                    'dtml',
                    'fsproj',
                    'fxml',
                    'iml',
                    'isml',
                    'jmx',
                    'launch',
                    'menu',
                    'mxml',
                    'nuspec',
                    'opml',
                    'owl',
                    'proj',
                    'props',
                    'pt',
                    'publishsettings',
                    'pubxml',
                    'pubxmluser',
                    'rbxlx',
                    'rbxmx',
                    'rdf',
                    'rng',
                    'rss',
                    'shproj',
                    'storyboard',
                    'targets',
                    'tld',
                    'tmx',
                    'vbproj',
                    'vbprojuser',
                    'vcxproj',
                    'vcxprojfilters',
                    'wsdl',
                    'wxi',
                    'wxl',
                    'wxs',
                    'xaml',
                    'xbl',
                    'xib',
                    'xlf',
                    'xliff',
                    'xpdl',
                    'xul',
                    'xoml',
                    'xsl',
                    'xslt',
                    'yaml',
                    'yml',
                    'eyaml',
                    'eyml',
                    'cff',
                    'yamltmlanguage',
                    'yamltmpreferences',
                    'yamltmtheme',
                    'winget',
                    'txt',
                    'csv',
                    'tsv',
                    'jpeg',
                    'jpg',
                    'png',
                    'gif',
                    'svg',
                    'webp',
                    'avif',
                    'bmp',
                    'ico',
                    'tiff',
                    'tif',
                    'heic',
                    'heif',
                    'tar',
                    'targz',
                    'tgz',
                    'gz',
                    'bz2',
                    'tarbz2',
                    'tbz2',
                    'z',
                    'tarz',
                    'lz',
                    'tarlz',
                    'xz',
                    'tarxz',
                    'txz',
                    'lzma',
                    'tarlzma',
                    'rar',
                    'sevenz',
                    'zst',
                    'tarzst',
                    'tzst',
                    'zip',
                    'exe',
                    'msi',
                    'dll',
                    'bat',
                    'cmd',
                    'com',
                    'appimage',
                    'app',
                    'bin',
                    'deb',
                    'rpm',
                    'apk',
                    'dmg',
                    'pkg',
                    'crx',
                    'xpi',
                    'mp3',
                    'wav',
                    'ogg',
                    'flac',
                    'aac',
                    'm4a',
                    'wma',
                    'mid',
                    'midi',
                    'mp4',
                    'mkv',
                    'webm',
                    'avi',
                    'mov',
                    'wmv',
                    'mpg',
                    'mpeg',
                    'm4v',
                    'flv',
                    'f4v',
                    'threegp',
                    'ttf',
                    'otf',
                    'woff',
                    'woff2',
                    'eot',
                    'rtf',
                    'odt',
                    'ods',
                    'odp',
                    'odg',
                    'odf',
                    'epub',
                    'mobi',
                    'azw',
                    'azw3',
                    'djvu',
                    'xls',
                    'xlsm',
                    'ppt',
                    'doc',
                    'pptx',
                    'xlsx',
                    'db',
                    'sqlite',
                    'sqlite3',
                    'mdb',
                    'accdb',
                    'dbf',
                    'plist',
                    'toml',
                    'env',
                    'dot',
                    'gv',
                    'torrent',
                    'ics',
                    'vcf',
                    'ai',
                    'eps',
                    'ps',
                    'dxf',
                    'dwg',
                    'fig',
                    'psd',
                    'psb',
                    'stl',
                    'obj',
                    'fbx',
                    'blend',
                    'dae',
                    'threeds',
                    'gltf',
                    'glb',
                    'vhd',
                    'vhdx',
                    'vmdk',
                    'ova',
                    'ovf',
                    'iso',
                    'img',
                    'swf',
                  ])
                  .describe(
                    'Generates a FileType enum and associated ContentType enum with their implementations.\n\nThis macro takes a list of tuples in the format:\n(Variant, \"extension\", \"mime_type\", CONTENT_TYPE_VARIANT)\n\nFor each tuple it generates:\n- A variant in the FileType enum\n- A variant in the ContentType enum\n- Implementations for:\n  - FileType::to_str() - Converts FileType to extension string\n  - FileType::from_str() - Converts extension string to FileType\n  - From<FileType> for ContentType - Maps FileType to ContentType\n  - ContentType::mime_type() - Gets MIME type for ContentType\n'
                  ),
              ])
              .optional(),
            fullName: zod
              .string()
              .describe('The full OS name of the file for deduplication'),
            name: zod
              .string()
              .describe('The name of the file, without the extension'),
            relativePath: zod
              .string()
              .describe(
                'The relative path of the file.\n\nThis is the `webkitRelativePath` with the name of the file stripped at the end.'
              ),
            sha: zod.string().describe('The sha of the file.'),
          }),
          type: zod.enum(['file']),
        }),
        zod.object({
          content: zod.record(zod.string(), zod.unknown()),
          project_id: zod.string(),
          type: zod.enum(['folder']),
        }),
      ]),
    })
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Create a request for extracting an uploaded folder archive.
 */
export const uploadExtractFolderHandlerBody = zod.object({
  name: zod.string().nullish(),
  parentId: zod.string().nullish(),
  sha: zod.string(),
});

export const uploadExtractFolderHandlerResponse = zod.object({
  data: zod
    .object({
      presignedUrl: zod.string(),
      requestId: zod.string(),
    })
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Get project metadata.
 */
export const getProjectHandlerParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const getProjectHandlerResponse = zod.object({
  data: zod.object({
    projectMetadata: zod.object({
      createdAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the project was created'),
      deletedAt: zod.iso.datetime({}).nullish(),
      id: zod.string().describe('The id of the project'),
      name: zod.string().describe('The name of the project'),
      parentId: zod.string().nullish().describe('The parent project id'),
      updatedAt: zod.iso
        .datetime({})
        .nullish()
        .describe('The time the project was updated'),
      userId: zod.string().describe('The user id of who created the project'),
      type: zod.enum(['project']),
    }),
    userAccessLevel: zod
      .enum(['view', 'comment', 'edit', 'owner'])
      .describe('Ordered from least to most access top -> bottom'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Soft-delete a project and all of its children.
 */
export const deleteProjectHandlerParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const deleteProjectHandlerResponse = zod.object({
  data: zod
    .object({
      chat_ids: zod.array(zod.string()).describe('Deleted chats.'),
      document_ids: zod.array(zod.string()).describe('Deleted documents.'),
      project_ids: zod
        .array(zod.string())
        .describe('Deleted projects, including the requested root.'),
    })
    .describe('Identifiers affected by a recursive project soft deletion.')
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Get the caller's project access level.
 */
export const getProjectUserAccessLevelParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const getProjectUserAccessLevelResponse = zod
  .enum(['view', 'comment', 'edit', 'owner'])
  .describe('Ordered from least to most access top -> bottom');

/**
 * @summary Get a project's immediate children.
 */
export const getProjectContentHandlerParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const getProjectContentHandlerResponse = zod.object({
  data: zod
    .array(
      zod.object({
        item: zod.union([
          zod.object({
            branchedFromId: zod
              .string()
              .nullish()
              .describe('The id of the document this document branched from'),
            branchedFromVersionId: zod
              .number()
              .nullish()
              .describe(
                'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
              ),
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the document was created'),
            deletedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the document was deleted'),
            documentFamilyId: zod
              .number()
              .nullish()
              .describe(
                'The id of the document family this document belongs to'
              ),
            documentVersionId: zod
              .number()
              .describe(
                'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
              ),
            fileType: zod
              .string()
              .nullish()
              .describe('The file type of the document (e.g. pdf, docx)'),
            id: zod.string().describe('The document id'),
            name: zod.string().describe('The name of the document'),
            owner: zod.string().describe('The owner of the document'),
            projectId: zod
              .string()
              .nullish()
              .describe('The id of the project that this document belongs to'),
            sha: zod
              .string()
              .nullish()
              .describe(
                'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
              ),
            subType: zod
              .union([
                zod.null(),
                zod
                  .union([
                    zod
                      .object({
                        is_completed: zod
                          .boolean()
                          .describe(
                            'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                          ),
                        type: zod.enum(['task']),
                      })
                      .describe(
                        'A task document with its associated properties'
                      ),
                    zod
                      .object({
                        type: zod.enum(['snippet']),
                      })
                      .describe('A snippet document — reusable markdown'),
                    zod
                      .object({
                        type: zod.enum(['skill']),
                      })
                      .describe(
                        'A skill document — markdown instructions for AI'
                      ),
                    zod
                      .object({
                        type: zod.enum(['initiative_description']),
                      })
                      .describe('The description document of an initiative'),
                  ])
                  .describe(
                    'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                  ),
              ])
              .optional(),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'The time the document instance \/ document BOM was updated'
              ),
            type: zod.enum(['document']),
          }),
          zod.object({
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the chat was created'),
            deletedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the chat was deleted'),
            id: zod.string().describe('The chat uuid'),
            isPersistent: zod.boolean(),
            model: zod
              .string()
              .nullish()
              .describe('The model used to generate the chat'),
            name: zod.string().describe('The name of the chat'),
            projectId: zod
              .string()
              .nullish()
              .describe('The project id of the chat'),
            tokenCount: zod.number().nullish(),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the chat was last updated'),
            userId: zod.string().describe('Who the chat belongs to'),
            type: zod.enum(['chat']),
          }),
          zod.object({
            createdAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the project was created'),
            deletedAt: zod.iso.datetime({}).nullish(),
            id: zod.string().describe('The id of the project'),
            name: zod.string().describe('The name of the project'),
            parentId: zod.string().nullish().describe('The parent project id'),
            updatedAt: zod.iso
              .datetime({})
              .nullish()
              .describe('The time the project was updated'),
            userId: zod
              .string()
              .describe('The user id of who created the project'),
            type: zod.enum(['project']),
          }),
        ]),
        userAccessLevel: zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      })
    )
    .describe(
      'DSS Items inside of a given project with their user access level attached to each item'
    ),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Permanently delete a soft-deleted project and all of its children.
 */
export const permanentlyDeleteProjectParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const permanentlyDeleteProjectResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Get a project's share permissions.
 */
export const getProjectPermissionsV2Params = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const getProjectPermissionsV2Response = zod.object({
  channelSharePermissions: zod
    .array(
      zod
        .object({
          access_level: zod
            .enum(['view', 'comment', 'edit', 'owner'])
            .describe('Ordered from least to most access top -> bottom'),
          channel_id: zod.string().describe('The channel id'),
        })
        .describe('The channel share permission')
    )
    .nullish()
    .describe('The channel share permissions for the item'),
  id: zod.string().describe('The share permission id'),
  linkShare: zod
    .union([
      zod.null(),
      zod
        .enum(['PUBLIC', 'TEAM'])
        .describe('Defines who can access an item through its share link.'),
    ])
    .optional(),
  linkShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
  owner: zod.string().describe('The owner of the item'),
  teamShareAccessLevel: zod
    .union([
      zod.null(),
      zod
        .enum(['view', 'comment', 'edit', 'owner'])
        .describe('Ordered from least to most access top -> bottom'),
    ])
    .optional(),
});

/**
 * @summary Restore a soft-deleted project and its children.
 */
export const revertDeleteProjectParams = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const revertDeleteProjectResponse = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Gets the users recently deleted items.
 */
export const recentlyDeletedResponse = zod.object({
  data: zod
    .object({
      items: zod
        .array(
          zod.union([
            zod.object({
              branchedFromId: zod
                .string()
                .nullish()
                .describe('The id of the document this document branched from'),
              branchedFromVersionId: zod
                .number()
                .nullish()
                .describe(
                  'The id of the version this document branched from\nThis could be either DocumentInstance or DocumentBom id depending on\nthe file type'
                ),
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the document was created'),
              deletedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the document was deleted'),
              documentFamilyId: zod
                .number()
                .nullish()
                .describe(
                  'The id of the document family this document belongs to'
                ),
              documentVersionId: zod
                .number()
                .describe(
                  'The version of the document\nThis could be the document_instance_id or document_bom_id depending on\nthe file type'
                ),
              fileType: zod
                .string()
                .nullish()
                .describe('The file type of the document (e.g. pdf, docx)'),
              id: zod.string().describe('The document id'),
              name: zod.string().describe('The name of the document'),
              owner: zod.string().describe('The owner of the document'),
              projectId: zod
                .string()
                .nullish()
                .describe(
                  'The id of the project that this document belongs to'
                ),
              sha: zod
                .string()
                .nullish()
                .describe(
                  'If the document is a PDF, this is the SHA of the pdf\nIf the document is a DOCX, this will not be present'
                ),
              subType: zod
                .union([
                  zod.null(),
                  zod
                    .union([
                      zod
                        .object({
                          is_completed: zod
                            .boolean()
                            .describe(
                              'Whether the task is completed.\nTrue if the Status property is set to \"Completed\".'
                            ),
                          type: zod.enum(['task']),
                        })
                        .describe(
                          'A task document with its associated properties'
                        ),
                      zod
                        .object({
                          type: zod.enum(['snippet']),
                        })
                        .describe('A snippet document — reusable markdown'),
                      zod
                        .object({
                          type: zod.enum(['skill']),
                        })
                        .describe(
                          'A skill document — markdown instructions for AI'
                        ),
                      zod
                        .object({
                          type: zod.enum(['initiative_description']),
                        })
                        .describe('The description document of an initiative'),
                    ])
                    .describe(
                      'Sub type of a document with associated properties encoded in each variant.\nThis ensures type-safety: task properties only exist when the document is a task.'
                    ),
                ])
                .optional(),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe(
                  'The time the document instance \/ document BOM was updated'
                ),
              type: zod.enum(['document']),
            }),
            zod.object({
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was created'),
              deletedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was deleted'),
              id: zod.string().describe('The chat uuid'),
              isPersistent: zod.boolean(),
              model: zod
                .string()
                .nullish()
                .describe('The model used to generate the chat'),
              name: zod.string().describe('The name of the chat'),
              projectId: zod
                .string()
                .nullish()
                .describe('The project id of the chat'),
              tokenCount: zod.number().nullish(),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the chat was last updated'),
              userId: zod.string().describe('Who the chat belongs to'),
              type: zod.enum(['chat']),
            }),
            zod.object({
              createdAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the project was created'),
              deletedAt: zod.iso.datetime({}).nullish(),
              id: zod.string().describe('The id of the project'),
              name: zod.string().describe('The name of the project'),
              parentId: zod
                .string()
                .nullish()
                .describe('The parent project id'),
              updatedAt: zod.iso
                .datetime({})
                .nullish()
                .describe('The time the project was updated'),
              userId: zod
                .string()
                .describe('The user id of who created the project'),
              type: zod.enum(['project']),
            }),
          ])
        )
        .describe('The items returned from the call'),
    })
    .describe('Data to be returned'),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary List original email threads with caller-private reminder work.
 */
export const listEmailRemindersQueryLimitMax = 100;

export const listEmailRemindersQueryParams = zod.object({
  inboxIds: zod
    .array(zod.uuid())
    .nullish()
    .describe('Selected inboxes; omission selects all accessible inboxes.'),
  noInboxes: zod
    .boolean()
    .nullish()
    .describe('Explicitly empty inbox selection.'),
  done: zod
    .boolean()
    .nullish()
    .describe('Email archive filter, independent of reminder completion.'),
  read: zod.boolean().nullish().describe('Email read status.'),
  calendar: zod.boolean().nullish().describe('Restrict to calendar mail.'),
  tags: zod
    .array(zod.string())
    .nullish()
    .describe('Repeated property-definition:select-option UUID pairs.'),
  attachments: zod
    .array(zod.string())
    .nullish()
    .describe('Repeated attachment categories: pdf, image, document.'),
  cursor: zod
    .string()
    .nullish()
    .describe('Continuation from the previous page.'),
  limit: zod
    .number()
    .min(1)
    .max(listEmailRemindersQueryLimitMax)
    .nullish()
    .describe('Maximum rows (1–100).'),
});

export const listEmailRemindersResponse = zod
  .object({
    items: zod
      .array(
        zod
          .object({
            followup: zod
              .object({
                condition: zod
                  .enum(['if_no_reply', 'regardless'])
                  .describe(
                    'When an email follow-up should return the conversation.'
                  ),
                linkId: zod
                  .uuid()
                  .describe('Canonical owned\/delegated inbox.'),
                remindAt: zod.iso.datetime({}).describe('Confirmed schedule.'),
                reminderId: zod
                  .uuid()
                  .describe('Identity of the snooze and its delivery records.'),
                revision: zod
                  .uuid()
                  .describe(
                    'Last accepted operation; edits\/removal compare this to prevent stale undo.'
                  ),
                state: zod
                  .enum([
                    'archiving',
                    'pending',
                    'returning',
                    'returned',
                    'cancelled',
                    'removed',
                  ])
                  .describe('Durable progress of an email operation.'),
                threadId: zod.uuid().describe('Conversation identity.'),
              })
              .describe(
                'Public status shown on email and in the Reminders editor.'
              ),
            threadId: zod
              .uuid()
              .describe(
                'Original email identity, never a mirror reminder identity.'
              ),
          })
          .describe('An original thread with its active snooze.')
      )
      .describe('Original threads ordered by their snooze return time.'),
    nextCursor: zod
      .string()
      .nullish()
      .describe(
        'Progress through all examined candidates, absent at exhaustion.'
      ),
  })
  .describe(
    'A page of original-email identities and private reminder metadata.'
  );

/**
 * @summary Read an email follow-up and reconcile inbound reply cancellation.
 */
export const getEmailFollowupParams = zod.object({
  thread_id: zod.uuid(),
});

export const getEmailFollowupResponse = zod
  .object({
    followup: zod
      .union([
        zod.null(),
        zod
          .object({
            condition: zod
              .enum(['if_no_reply', 'regardless'])
              .describe(
                'When an email follow-up should return the conversation.'
              ),
            linkId: zod.uuid().describe('Canonical owned\/delegated inbox.'),
            remindAt: zod.iso.datetime({}).describe('Confirmed schedule.'),
            reminderId: zod
              .uuid()
              .describe('Identity of the snooze and its delivery records.'),
            revision: zod
              .uuid()
              .describe(
                'Last accepted operation; edits\/removal compare this to prevent stale undo.'
              ),
            state: zod
              .enum([
                'archiving',
                'pending',
                'returning',
                'returned',
                'cancelled',
                'removed',
              ])
              .describe('Durable progress of an email operation.'),
            threadId: zod.uuid().describe('Conversation identity.'),
          })
          .describe(
            'Public status shown on email and in the Reminders editor.'
          ),
      ])
      .optional(),
  })
  .describe('Lookup response, including an email with no follow-up yet.');

/**
 * @summary Schedule, edit or remove one email follow-up as an idempotent operation.
 */
export const setEmailFollowupParams = zod.object({
  thread_id: zod.uuid(),
});

export const setEmailFollowupBody = zod
  .union([
    zod
      .object({
        condition: zod
          .enum(['if_no_reply', 'regardless'])
          .describe('When an email follow-up should return the conversation.'),
        expectedRevision: zod
          .uuid()
          .nullish()
          .describe('None for creation, the current revision for edits.'),
        operationId: zod
          .uuid()
          .describe('Unique request identity retained across network retries.'),
        remindAt: zod.iso
          .datetime({})
          .describe(
            'One future instant; conditional recurrence is deliberately absent.'
          ),
        type: zod.enum(['set']),
      })
      .describe(
        'Create or edit one follow-up. An edit requires the displayed revision.'
      ),
    zod
      .object({
        expectedRevision: zod
          .uuid()
          .describe(
            'Reject removal if a newer edit has replaced this operation.'
          ),
        operationId: zod.uuid().describe('Unique request identity.'),
        type: zod.enum(['remove']),
        undo: zod
          .boolean()
          .optional()
          .describe(
            'Undo restores original visibility; ordinary Remove returns to inbox.'
          ),
      })
      .describe(
        'Cancel and restore the conversation without leaving a future alert.'
      ),
  ])
  .describe(
    'Idempotent email command. Reusing an operation ID with different data fails.'
  );

export const setEmailFollowupResponse = zod
  .object({
    condition: zod
      .enum(['if_no_reply', 'regardless'])
      .describe('When an email follow-up should return the conversation.'),
    linkId: zod.uuid().describe('Canonical owned\/delegated inbox.'),
    remindAt: zod.iso.datetime({}).describe('Confirmed schedule.'),
    reminderId: zod
      .uuid()
      .describe('Identity of the snooze and its delivery records.'),
    revision: zod
      .uuid()
      .describe(
        'Last accepted operation; edits\/removal compare this to prevent stale undo.'
      ),
    state: zod
      .enum([
        'archiving',
        'pending',
        'returning',
        'returned',
        'cancelled',
        'removed',
      ])
      .describe('Durable progress of an email operation.'),
    threadId: zod.uuid().describe('Conversation identity.'),
  })
  .describe('Public status shown on email and in the Reminders editor.');

export const getViewsHandlerResponse = zod.object({
  excludedDefaultViews: zod.array(
    zod
      .object({
        defaultViewId: zod.string(),
        id: zod.uuid(),
        userId: zod.string(),
      })
      .describe(
        "Frontend can define any set of its own default views for the user.\nThis is a list of views that are excluded from the default views list on the frontend.\n\nIt is important that the frontend can quickly iterate on default views, for that reason\nwe don't keep track of default views in the database, only those that are explicitly excluded."
      )
  ),
  views: zod.array(
    zod.object({
      config: zod
        .unknown()
        .describe(
          'It is an explicit choice that the structure of the view configuration\nis up to the frontend. The structure and composition of view configuration\nis still very much in flux.'
        ),
      createdAt: zod.iso.datetime({}),
      id: zod.uuid(),
      name: zod.string(),
      updatedAt: zod.iso.datetime({}),
      userId: zod.string(),
    })
  ),
});

export const createViewHandlerBody = zod.object({
  config: zod.unknown(),
  name: zod.string(),
});

export const createViewHandlerResponse = zod.object({
  config: zod
    .unknown()
    .describe(
      'It is an explicit choice that the structure of the view configuration\nis up to the frontend. The structure and composition of view configuration\nis still very much in flux.'
    ),
  createdAt: zod.iso.datetime({}),
  id: zod.uuid(),
  name: zod.string(),
  updatedAt: zod.iso.datetime({}),
  userId: zod.string(),
});

export const excludeDefaultViewHandlerBody = zod.object({
  defaultViewId: zod.string(),
});

export const deleteViewHandlerParams = zod.object({
  saved_view_id: zod.string().describe('The id of the saved view to delete'),
});

export const patchViewHandlerParams = zod.object({
  saved_view_id: zod.string().describe('The id of the saved view to patch'),
});

export const patchViewHandlerBody = zod.object({
  config: zod.unknown().optional(),
  name: zod.string().nullish(),
});

/**
 * @summary List this administrator's team receipts, effective server limits and source binding.
 */
export const listSlackImportsQueryParams = zod.object({
  before: zod.uuid().optional(),
});

export const listSlackImportsResponseJobsItemConversationsItemCountersDuplicatesMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemCountersImportedMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemCountersProcessedMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemCountersReactionsMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemCountersSkippedMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemPartCountMin = 0;

export const listSlackImportsResponseJobsItemConversationsItemVerifiedPartsMin = 0;

export const listSlackImportsResponseJobsItemLimitsConversationsMin = 0;

export const listSlackImportsResponseJobsItemLimitsDatabaseBatchBytesMin = 0;

export const listSlackImportsResponseJobsItemLimitsDatabaseBatchMessagesMin = 0;

export const listSlackImportsResponseJobsItemLimitsJsonBytesMin = 0;

export const listSlackImportsResponseJobsItemLimitsPartBytesMin = 0;

export const listSlackImportsResponseJobsItemLimitsPartRecordsMin = 0;

export const listSlackImportsResponseJobsItemLimitsRecordBytesMin = 0;

export const listSlackImportsResponseJobsItemLimitsRegistrationBatchMin = 0;

export const listSlackImportsResponseJobsItemLimitsSelectedBytesMin = 0;

export const listSlackImportsResponseJobsItemLimitsZipEntriesMin = 0;

export const listSlackImportsResponseJobsItemRevisionMin = 0;

export const listSlackImportsResponseLimitsConversationsMin = 0;

export const listSlackImportsResponseLimitsDatabaseBatchBytesMin = 0;

export const listSlackImportsResponseLimitsDatabaseBatchMessagesMin = 0;

export const listSlackImportsResponseLimitsJsonBytesMin = 0;

export const listSlackImportsResponseLimitsPartBytesMin = 0;

export const listSlackImportsResponseLimitsPartRecordsMin = 0;

export const listSlackImportsResponseLimitsRecordBytesMin = 0;

export const listSlackImportsResponseLimitsRegistrationBatchMin = 0;

export const listSlackImportsResponseLimitsSelectedBytesMin = 0;

export const listSlackImportsResponseLimitsZipEntriesMin = 0;

export const listSlackImportsResponse = zod
  .object({
    jobs: zod
      .array(
        zod
          .object({
            conversations: zod
              .array(
                zod
                  .object({
                    archived: zod
                      .boolean()
                      .describe(
                        'Whether the selected source conversation was archived.'
                      ),
                    channelId: zod
                      .uuid()
                      .nullish()
                      .describe(
                        'Authorized target only; absent for inaccessible reused targets.'
                      ),
                    counters: zod
                      .object({
                        duplicates: zod
                          .number()
                          .min(
                            listSlackImportsResponseJobsItemConversationsItemCountersDuplicatesMin
                          )
                          .describe('Previously committed source identities.'),
                        imported: zod
                          .number()
                          .min(
                            listSlackImportsResponseJobsItemConversationsItemCountersImportedMin
                          )
                          .describe('Newly persisted messages.'),
                        processed: zod
                          .number()
                          .min(
                            listSlackImportsResponseJobsItemConversationsItemCountersProcessedMin
                          )
                          .describe(
                            'Source records examined through the committed checkpoint.'
                          ),
                        reactions: zod
                          .number()
                          .min(
                            listSlackImportsResponseJobsItemConversationsItemCountersReactionsMin
                          )
                          .describe('Newly persisted reactions.'),
                        skipped: zod
                          .number()
                          .min(
                            listSlackImportsResponseJobsItemConversationsItemCountersSkippedMin
                          )
                          .describe(
                            'Unsupported, empty or otherwise deliberately skipped records.'
                          ),
                      })
                      .describe(
                        'Committed counters, never optimistic browser or uncommitted worker counts.'
                      ),
                    error: zod
                      .union([
                        zod.null(),
                        zod
                          .enum([
                            'disabled',
                            'invalid_input',
                            'limit_exceeded',
                            'unavailable',
                            'admin_required',
                            'source_mismatch',
                            'conflict',
                            'upload_mismatch',
                            'lease_lost',
                            'retryable',
                            'internal',
                          ])
                          .describe(
                            'Public error codes; never carry raw provider errors, keys, emails or source text.'
                          ),
                      ])
                      .optional(),
                    kind: zod
                      .enum([
                        'public_channel',
                        'private_channel',
                        'direct_message',
                        'group_direct_message',
                      ])
                      .describe(
                        'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
                      ),
                    name: zod
                      .string()
                      .describe(
                        "Persisted source display name, never the reused Macro target's name."
                      ),
                    partCount: zod
                      .number()
                      .min(
                        listSlackImportsResponseJobsItemConversationsItemPartCountMin
                      )
                      .nullish()
                      .describe(
                        'Null until sealed; zero is a valid sealed empty manifest.'
                      ),
                    search: zod
                      .union([
                        zod
                          .object({
                            status: zod.enum(['not_needed']),
                          })
                          .describe('No committed history requires indexing.'),
                        zod
                          .object({
                            status: zod.enum(['pending']),
                          })
                          .describe(
                            'Committed history requires an outbox-backed scoped submission.'
                          ),
                        zod
                          .object({
                            receiptId: zod
                              .uuid()
                              .describe('Receipt from the search service.'),
                            status: zod.enum(['submitted']),
                          })
                          .describe(
                            'Search service accepted the request, but has not completed it.'
                          ),
                        zod
                          .object({
                            status: zod.enum(['completed']),
                          })
                          .describe(
                            'Search service durably published the scope; eventual indexing is separate.'
                          ),
                        zod
                          .object({
                            status: zod.enum(['failed']),
                          })
                          .describe(
                            'Publication failed; retained history must be retried independently.'
                          ),
                      ])
                      .describe(
                        'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
                      ),
                    slackChannelId: zod
                      .string()
                      .describe(
                        'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
                      ),
                    status: zod
                      .enum([
                        'awaiting_uploads',
                        'queued',
                        'importing',
                        'completed',
                        'skipped',
                        'failed',
                      ])
                      .describe('Durable per-conversation lifecycle.'),
                    verifiedParts: zod
                      .number()
                      .min(
                        listSlackImportsResponseJobsItemConversationsItemVerifiedPartsMin
                      )
                      .describe(
                        'Number of parts whose object identity has been verified.'
                      ),
                    warnings: zod
                      .array(
                        zod
                          .enum([
                            'creation_time_from_message',
                            'creation_time_from_job',
                            'unresolvable_direct_message',
                            'target_unavailable',
                            'uploads_incomplete',
                          ])
                          .describe(
                            'Non-fatal, sanitized explanations displayed in admin progress.'
                          )
                      )
                      .describe('Non-fatal metadata\/skip explanations.'),
                  })
                  .describe(
                    'Admin-visible progress for one selected conversation.'
                  )
              )
              .describe(
                'All selected conversations, bounded by the create limit.'
              ),
            createdAt: zod.iso
              .datetime({})
              .describe(
                'Persisted creation time, also the final historical-time fallback.'
              ),
            includeMessageHistory: zod
              .boolean()
              .describe(
                'Immutable history option confirmed when creating this job.'
              ),
            jobId: zod
              .uuid()
              .describe(
                'Import job identity. Generate UUIDv7 in application code.'
              ),
            limits: zod
              .object({
                conversations: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsConversationsMin)
                  .describe('Maximum selected conversations.'),
                databaseBatchBytes: zod
                  .number()
                  .min(
                    listSlackImportsResponseJobsItemLimitsDatabaseBatchBytesMin
                  )
                  .describe('Maximum historical batch payload bytes.'),
                databaseBatchMessages: zod
                  .number()
                  .min(
                    listSlackImportsResponseJobsItemLimitsDatabaseBatchMessagesMin
                  )
                  .describe('Maximum messages in a historical batch.'),
                jsonBytes: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsJsonBytesMin)
                  .describe(
                    'Maximum users, root metadata or individual day JSON bytes.'
                  ),
                partBytes: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsPartBytesMin)
                  .describe('Maximum NDJSON part bytes.'),
                partRecords: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsPartRecordsMin)
                  .describe('Maximum records per part.'),
                recordBytes: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsRecordBytesMin)
                  .describe(
                    'Maximum bytes per NDJSON record, including newline.'
                  ),
                registrationBatch: zod
                  .number()
                  .min(
                    listSlackImportsResponseJobsItemLimitsRegistrationBatchMin
                  )
                  .describe(
                    'Maximum descriptors or completion identities per call.'
                  ),
                selectedBytes: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsSelectedBytesMin)
                  .describe(
                    'Maximum selected temporary data bytes across all uploads.'
                  ),
                zipEntries: zod
                  .number()
                  .min(listSlackImportsResponseJobsItemLimitsZipEntriesMin)
                  .describe('Maximum ZIP entries scanned by the browser.'),
              })
              .describe(
                'Configurable bounds shared with browser staging and enforced again by the worker.'
              ),
            registrationClosedAt: zod.iso
              .datetime({})
              .nullish()
              .describe(
                'Registration closure time; cancellation also closes registration.'
              ),
            revision: zod
              .number()
              .min(listSlackImportsResponseJobsItemRevisionMin)
              .describe(
                'Monotonic job revision for polling\/websocket invalidation.'
              ),
            source: zod
              .union([
                zod
                  .object({
                    kind: zod.enum(['known']),
                    sourceId: zod
                      .string()
                      .describe(
                        'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
                      ),
                  })
                  .describe(
                    'Archive names its Slack workspace; reject a conflicting team binding.'
                  ),
                zod
                  .object({
                    kind: zod.enum(['confirmed_unknown']),
                  })
                  .describe(
                    "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
                  ),
              ])
              .describe(
                'Source identity supplied when creating a job. One binding per Macro team in v1.'
              ),
            status: zod
              .enum([
                'uploading',
                'processing',
                'completed',
                'completed_with_errors',
                'failed',
                'cancelling',
                'cancelled',
              ])
              .describe(
                'Job lifecycle. Cancellation remains in progress while any lease is active.'
              ),
            updatedAt: zod.iso
              .datetime({})
              .describe('Last durable lifecycle\/progress update.'),
            usersVerified: zod
              .boolean()
              .describe('Whether users metadata is verified and pinned.'),
          })
          .describe(
            'Common receipt returned by create, completion, finalize, cancel and progress.'
          )
      )
      .describe('At most 50 job receipts, newest first.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(listSlackImportsResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(listSlackImportsResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(listSlackImportsResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(listSlackImportsResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(listSlackImportsResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(listSlackImportsResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(listSlackImportsResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(listSlackImportsResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(listSlackImportsResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(listSlackImportsResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    nextCursor: zod
      .union([
        zod.null(),
        zod
          .uuid()
          .describe(
            'Import job identity. Generate UUIDv7 in application code.'
          ),
      ])
      .optional(),
    sourceBinding: zod
      .union([
        zod
          .object({
            kind: zod.enum(['unbound']),
          })
          .describe('No import has bound this team yet.'),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            'Legacy\/onboarding or explicitly confirmed unidentified source.'
          ),
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe('Known Slack workspace; reject mismatches.'),
      ])
      .describe(
        "Team's durable single-source binding, including confirmed unidentified archives."
      ),
  })
  .describe(
    'Admin list result, also supplying upload limits before a job exists.'
  );

/**
 * @summary Create or replay a job using a scoped idempotency token and full selected metadata.
Missing creation timestamps and unknown message counts are represented as null.
 */
export const createSlackImportBodyConversationsItemMessageCountMin = 0;

export const createSlackImportBodyConversationsMax = 2000;

export const createSlackImportBody = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Source archived flag (does not silently archive a reused Macro target).'
              ),
            createdAt: zod
              .union([
                zod.null(),
                zod
                  .string()
                  .describe(
                    'Slack time represented exactly as nonnegative Unix microseconds.\nJSON is a string with six fractional digits, never a floating-point number.'
                  ),
              ])
              .optional(),
            creatorId: zod
              .union([
                zod.null(),
                zod
                  .string()
                  .describe(
                    'Slack member identity (U or W prefix), including USLACKBOT.'
                  ),
              ])
              .optional(),
            folder: zod
              .string()
              .describe(
                'Single safe source folder\/key segment; never a path or authorization proof.'
              ),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            memberIds: zod
              .array(
                zod
                  .string()
                  .describe(
                    'Slack member identity (U or W prefix), including USLACKBOT.'
                  )
              )
              .describe(
                'Complete source member list, including members unknown to Macro.'
              ),
            messageCount: zod
              .number()
              .min(createSlackImportBodyConversationsItemMessageCountMin)
              .nullish()
              .describe('Advisory only; null means not yet counted, not zero.'),
            name: zod
              .string()
              .describe('Original display name; not a storage key.'),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
          })
          .describe(
            'Full selected conversation metadata, persisted before granting any uploads.'
          )
      )
      .min(1)
      .max(createSlackImportBodyConversationsMax)
      .describe(
        'Full, unique selected conversations (at most the configured bound).'
      ),
    idempotencyToken: zod
      .uuid()
      .describe(
        'Client-generated create idempotency token, scoped to team and administrator.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe(
        'Default true at the client; false still requires users and zero-part seals.'
      ),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
  })
  .describe('Create command. Team identity is deliberately absent.');

export const createSlackImportResponseConversationsItemCountersDuplicatesMin = 0;

export const createSlackImportResponseConversationsItemCountersImportedMin = 0;

export const createSlackImportResponseConversationsItemCountersProcessedMin = 0;

export const createSlackImportResponseConversationsItemCountersReactionsMin = 0;

export const createSlackImportResponseConversationsItemCountersSkippedMin = 0;

export const createSlackImportResponseConversationsItemPartCountMin = 0;

export const createSlackImportResponseConversationsItemVerifiedPartsMin = 0;

export const createSlackImportResponseLimitsConversationsMin = 0;

export const createSlackImportResponseLimitsDatabaseBatchBytesMin = 0;

export const createSlackImportResponseLimitsDatabaseBatchMessagesMin = 0;

export const createSlackImportResponseLimitsJsonBytesMin = 0;

export const createSlackImportResponseLimitsPartBytesMin = 0;

export const createSlackImportResponseLimitsPartRecordsMin = 0;

export const createSlackImportResponseLimitsRecordBytesMin = 0;

export const createSlackImportResponseLimitsRegistrationBatchMin = 0;

export const createSlackImportResponseLimitsSelectedBytesMin = 0;

export const createSlackImportResponseLimitsZipEntriesMin = 0;

export const createSlackImportResponseRevisionMin = 0;

export const createSlackImportResponse = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Whether the selected source conversation was archived.'
              ),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Authorized target only; absent for inaccessible reused targets.'
              ),
            counters: zod
              .object({
                duplicates: zod
                  .number()
                  .min(
                    createSlackImportResponseConversationsItemCountersDuplicatesMin
                  )
                  .describe('Previously committed source identities.'),
                imported: zod
                  .number()
                  .min(
                    createSlackImportResponseConversationsItemCountersImportedMin
                  )
                  .describe('Newly persisted messages.'),
                processed: zod
                  .number()
                  .min(
                    createSlackImportResponseConversationsItemCountersProcessedMin
                  )
                  .describe(
                    'Source records examined through the committed checkpoint.'
                  ),
                reactions: zod
                  .number()
                  .min(
                    createSlackImportResponseConversationsItemCountersReactionsMin
                  )
                  .describe('Newly persisted reactions.'),
                skipped: zod
                  .number()
                  .min(
                    createSlackImportResponseConversationsItemCountersSkippedMin
                  )
                  .describe(
                    'Unsupported, empty or otherwise deliberately skipped records.'
                  ),
              })
              .describe(
                'Committed counters, never optimistic browser or uncommitted worker counts.'
              ),
            error: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'disabled',
                    'invalid_input',
                    'limit_exceeded',
                    'unavailable',
                    'admin_required',
                    'source_mismatch',
                    'conflict',
                    'upload_mismatch',
                    'lease_lost',
                    'retryable',
                    'internal',
                  ])
                  .describe(
                    'Public error codes; never carry raw provider errors, keys, emails or source text.'
                  ),
              ])
              .optional(),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            name: zod
              .string()
              .describe(
                "Persisted source display name, never the reused Macro target's name."
              ),
            partCount: zod
              .number()
              .min(createSlackImportResponseConversationsItemPartCountMin)
              .nullish()
              .describe(
                'Null until sealed; zero is a valid sealed empty manifest.'
              ),
            search: zod
              .union([
                zod
                  .object({
                    status: zod.enum(['not_needed']),
                  })
                  .describe('No committed history requires indexing.'),
                zod
                  .object({
                    status: zod.enum(['pending']),
                  })
                  .describe(
                    'Committed history requires an outbox-backed scoped submission.'
                  ),
                zod
                  .object({
                    receiptId: zod
                      .uuid()
                      .describe('Receipt from the search service.'),
                    status: zod.enum(['submitted']),
                  })
                  .describe(
                    'Search service accepted the request, but has not completed it.'
                  ),
                zod
                  .object({
                    status: zod.enum(['completed']),
                  })
                  .describe(
                    'Search service durably published the scope; eventual indexing is separate.'
                  ),
                zod
                  .object({
                    status: zod.enum(['failed']),
                  })
                  .describe(
                    'Publication failed; retained history must be retried independently.'
                  ),
              ])
              .describe(
                'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
            status: zod
              .enum([
                'awaiting_uploads',
                'queued',
                'importing',
                'completed',
                'skipped',
                'failed',
              ])
              .describe('Durable per-conversation lifecycle.'),
            verifiedParts: zod
              .number()
              .min(createSlackImportResponseConversationsItemVerifiedPartsMin)
              .describe(
                'Number of parts whose object identity has been verified.'
              ),
            warnings: zod
              .array(
                zod
                  .enum([
                    'creation_time_from_message',
                    'creation_time_from_job',
                    'unresolvable_direct_message',
                    'target_unavailable',
                    'uploads_incomplete',
                  ])
                  .describe(
                    'Non-fatal, sanitized explanations displayed in admin progress.'
                  )
              )
              .describe('Non-fatal metadata\/skip explanations.'),
          })
          .describe('Admin-visible progress for one selected conversation.')
      )
      .describe('All selected conversations, bounded by the create limit.'),
    createdAt: zod.iso
      .datetime({})
      .describe(
        'Persisted creation time, also the final historical-time fallback.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe('Immutable history option confirmed when creating this job.'),
    jobId: zod
      .uuid()
      .describe('Import job identity. Generate UUIDv7 in application code.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(createSlackImportResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(createSlackImportResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(createSlackImportResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(createSlackImportResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(createSlackImportResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(createSlackImportResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(createSlackImportResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(createSlackImportResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(createSlackImportResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(createSlackImportResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    registrationClosedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Registration closure time; cancellation also closes registration.'
      ),
    revision: zod
      .number()
      .min(createSlackImportResponseRevisionMin)
      .describe('Monotonic job revision for polling\/websocket invalidation.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
    status: zod
      .enum([
        'uploading',
        'processing',
        'completed',
        'completed_with_errors',
        'failed',
        'cancelling',
        'cancelled',
      ])
      .describe(
        'Job lifecycle. Cancellation remains in progress while any lease is active.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Last durable lifecycle\/progress update.'),
    usersVerified: zod
      .boolean()
      .describe('Whether users metadata is verified and pinned.'),
  })
  .describe(
    'Common receipt returned by create, completion, finalize, cancel and progress.'
  );

/**
 * @summary Read sanitized progress; foreign jobs are indistinguishable from missing jobs.
 */
export const getSlackImportParams = zod.object({
  job_id: zod.uuid(),
});

export const getSlackImportResponseConversationsItemCountersDuplicatesMin = 0;

export const getSlackImportResponseConversationsItemCountersImportedMin = 0;

export const getSlackImportResponseConversationsItemCountersProcessedMin = 0;

export const getSlackImportResponseConversationsItemCountersReactionsMin = 0;

export const getSlackImportResponseConversationsItemCountersSkippedMin = 0;

export const getSlackImportResponseConversationsItemPartCountMin = 0;

export const getSlackImportResponseConversationsItemVerifiedPartsMin = 0;

export const getSlackImportResponseLimitsConversationsMin = 0;

export const getSlackImportResponseLimitsDatabaseBatchBytesMin = 0;

export const getSlackImportResponseLimitsDatabaseBatchMessagesMin = 0;

export const getSlackImportResponseLimitsJsonBytesMin = 0;

export const getSlackImportResponseLimitsPartBytesMin = 0;

export const getSlackImportResponseLimitsPartRecordsMin = 0;

export const getSlackImportResponseLimitsRecordBytesMin = 0;

export const getSlackImportResponseLimitsRegistrationBatchMin = 0;

export const getSlackImportResponseLimitsSelectedBytesMin = 0;

export const getSlackImportResponseLimitsZipEntriesMin = 0;

export const getSlackImportResponseRevisionMin = 0;

export const getSlackImportResponse = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Whether the selected source conversation was archived.'
              ),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Authorized target only; absent for inaccessible reused targets.'
              ),
            counters: zod
              .object({
                duplicates: zod
                  .number()
                  .min(
                    getSlackImportResponseConversationsItemCountersDuplicatesMin
                  )
                  .describe('Previously committed source identities.'),
                imported: zod
                  .number()
                  .min(
                    getSlackImportResponseConversationsItemCountersImportedMin
                  )
                  .describe('Newly persisted messages.'),
                processed: zod
                  .number()
                  .min(
                    getSlackImportResponseConversationsItemCountersProcessedMin
                  )
                  .describe(
                    'Source records examined through the committed checkpoint.'
                  ),
                reactions: zod
                  .number()
                  .min(
                    getSlackImportResponseConversationsItemCountersReactionsMin
                  )
                  .describe('Newly persisted reactions.'),
                skipped: zod
                  .number()
                  .min(
                    getSlackImportResponseConversationsItemCountersSkippedMin
                  )
                  .describe(
                    'Unsupported, empty or otherwise deliberately skipped records.'
                  ),
              })
              .describe(
                'Committed counters, never optimistic browser or uncommitted worker counts.'
              ),
            error: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'disabled',
                    'invalid_input',
                    'limit_exceeded',
                    'unavailable',
                    'admin_required',
                    'source_mismatch',
                    'conflict',
                    'upload_mismatch',
                    'lease_lost',
                    'retryable',
                    'internal',
                  ])
                  .describe(
                    'Public error codes; never carry raw provider errors, keys, emails or source text.'
                  ),
              ])
              .optional(),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            name: zod
              .string()
              .describe(
                "Persisted source display name, never the reused Macro target's name."
              ),
            partCount: zod
              .number()
              .min(getSlackImportResponseConversationsItemPartCountMin)
              .nullish()
              .describe(
                'Null until sealed; zero is a valid sealed empty manifest.'
              ),
            search: zod
              .union([
                zod
                  .object({
                    status: zod.enum(['not_needed']),
                  })
                  .describe('No committed history requires indexing.'),
                zod
                  .object({
                    status: zod.enum(['pending']),
                  })
                  .describe(
                    'Committed history requires an outbox-backed scoped submission.'
                  ),
                zod
                  .object({
                    receiptId: zod
                      .uuid()
                      .describe('Receipt from the search service.'),
                    status: zod.enum(['submitted']),
                  })
                  .describe(
                    'Search service accepted the request, but has not completed it.'
                  ),
                zod
                  .object({
                    status: zod.enum(['completed']),
                  })
                  .describe(
                    'Search service durably published the scope; eventual indexing is separate.'
                  ),
                zod
                  .object({
                    status: zod.enum(['failed']),
                  })
                  .describe(
                    'Publication failed; retained history must be retried independently.'
                  ),
              ])
              .describe(
                'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
            status: zod
              .enum([
                'awaiting_uploads',
                'queued',
                'importing',
                'completed',
                'skipped',
                'failed',
              ])
              .describe('Durable per-conversation lifecycle.'),
            verifiedParts: zod
              .number()
              .min(getSlackImportResponseConversationsItemVerifiedPartsMin)
              .describe(
                'Number of parts whose object identity has been verified.'
              ),
            warnings: zod
              .array(
                zod
                  .enum([
                    'creation_time_from_message',
                    'creation_time_from_job',
                    'unresolvable_direct_message',
                    'target_unavailable',
                    'uploads_incomplete',
                  ])
                  .describe(
                    'Non-fatal, sanitized explanations displayed in admin progress.'
                  )
              )
              .describe('Non-fatal metadata\/skip explanations.'),
          })
          .describe('Admin-visible progress for one selected conversation.')
      )
      .describe('All selected conversations, bounded by the create limit.'),
    createdAt: zod.iso
      .datetime({})
      .describe(
        'Persisted creation time, also the final historical-time fallback.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe('Immutable history option confirmed when creating this job.'),
    jobId: zod
      .uuid()
      .describe('Import job identity. Generate UUIDv7 in application code.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(getSlackImportResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(getSlackImportResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(getSlackImportResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(getSlackImportResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(getSlackImportResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(getSlackImportResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(getSlackImportResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(getSlackImportResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(getSlackImportResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(getSlackImportResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    registrationClosedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Registration closure time; cancellation also closes registration.'
      ),
    revision: zod
      .number()
      .min(getSlackImportResponseRevisionMin)
      .describe('Monotonic job revision for polling\/websocket invalidation.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
    status: zod
      .enum([
        'uploading',
        'processing',
        'completed',
        'completed_with_errors',
        'failed',
        'cancelling',
        'cancelled',
      ])
      .describe(
        'Job lifecycle. Cancellation remains in progress while any lease is active.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Last durable lifecycle\/progress update.'),
    usersVerified: zod
      .boolean()
      .describe('Whether users metadata is verified and pinned.'),
  })
  .describe(
    'Common receipt returned by create, completion, finalize, cancel and progress.'
  );

/**
 * @summary Stop unclaimed work. Active leases may settle; cancellation does not roll back history.
 */
export const cancelSlackImportParams = zod.object({
  job_id: zod.uuid(),
});

export const cancelSlackImportResponseConversationsItemCountersDuplicatesMin = 0;

export const cancelSlackImportResponseConversationsItemCountersImportedMin = 0;

export const cancelSlackImportResponseConversationsItemCountersProcessedMin = 0;

export const cancelSlackImportResponseConversationsItemCountersReactionsMin = 0;

export const cancelSlackImportResponseConversationsItemCountersSkippedMin = 0;

export const cancelSlackImportResponseConversationsItemPartCountMin = 0;

export const cancelSlackImportResponseConversationsItemVerifiedPartsMin = 0;

export const cancelSlackImportResponseLimitsConversationsMin = 0;

export const cancelSlackImportResponseLimitsDatabaseBatchBytesMin = 0;

export const cancelSlackImportResponseLimitsDatabaseBatchMessagesMin = 0;

export const cancelSlackImportResponseLimitsJsonBytesMin = 0;

export const cancelSlackImportResponseLimitsPartBytesMin = 0;

export const cancelSlackImportResponseLimitsPartRecordsMin = 0;

export const cancelSlackImportResponseLimitsRecordBytesMin = 0;

export const cancelSlackImportResponseLimitsRegistrationBatchMin = 0;

export const cancelSlackImportResponseLimitsSelectedBytesMin = 0;

export const cancelSlackImportResponseLimitsZipEntriesMin = 0;

export const cancelSlackImportResponseRevisionMin = 0;

export const cancelSlackImportResponse = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Whether the selected source conversation was archived.'
              ),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Authorized target only; absent for inaccessible reused targets.'
              ),
            counters: zod
              .object({
                duplicates: zod
                  .number()
                  .min(
                    cancelSlackImportResponseConversationsItemCountersDuplicatesMin
                  )
                  .describe('Previously committed source identities.'),
                imported: zod
                  .number()
                  .min(
                    cancelSlackImportResponseConversationsItemCountersImportedMin
                  )
                  .describe('Newly persisted messages.'),
                processed: zod
                  .number()
                  .min(
                    cancelSlackImportResponseConversationsItemCountersProcessedMin
                  )
                  .describe(
                    'Source records examined through the committed checkpoint.'
                  ),
                reactions: zod
                  .number()
                  .min(
                    cancelSlackImportResponseConversationsItemCountersReactionsMin
                  )
                  .describe('Newly persisted reactions.'),
                skipped: zod
                  .number()
                  .min(
                    cancelSlackImportResponseConversationsItemCountersSkippedMin
                  )
                  .describe(
                    'Unsupported, empty or otherwise deliberately skipped records.'
                  ),
              })
              .describe(
                'Committed counters, never optimistic browser or uncommitted worker counts.'
              ),
            error: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'disabled',
                    'invalid_input',
                    'limit_exceeded',
                    'unavailable',
                    'admin_required',
                    'source_mismatch',
                    'conflict',
                    'upload_mismatch',
                    'lease_lost',
                    'retryable',
                    'internal',
                  ])
                  .describe(
                    'Public error codes; never carry raw provider errors, keys, emails or source text.'
                  ),
              ])
              .optional(),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            name: zod
              .string()
              .describe(
                "Persisted source display name, never the reused Macro target's name."
              ),
            partCount: zod
              .number()
              .min(cancelSlackImportResponseConversationsItemPartCountMin)
              .nullish()
              .describe(
                'Null until sealed; zero is a valid sealed empty manifest.'
              ),
            search: zod
              .union([
                zod
                  .object({
                    status: zod.enum(['not_needed']),
                  })
                  .describe('No committed history requires indexing.'),
                zod
                  .object({
                    status: zod.enum(['pending']),
                  })
                  .describe(
                    'Committed history requires an outbox-backed scoped submission.'
                  ),
                zod
                  .object({
                    receiptId: zod
                      .uuid()
                      .describe('Receipt from the search service.'),
                    status: zod.enum(['submitted']),
                  })
                  .describe(
                    'Search service accepted the request, but has not completed it.'
                  ),
                zod
                  .object({
                    status: zod.enum(['completed']),
                  })
                  .describe(
                    'Search service durably published the scope; eventual indexing is separate.'
                  ),
                zod
                  .object({
                    status: zod.enum(['failed']),
                  })
                  .describe(
                    'Publication failed; retained history must be retried independently.'
                  ),
              ])
              .describe(
                'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
            status: zod
              .enum([
                'awaiting_uploads',
                'queued',
                'importing',
                'completed',
                'skipped',
                'failed',
              ])
              .describe('Durable per-conversation lifecycle.'),
            verifiedParts: zod
              .number()
              .min(cancelSlackImportResponseConversationsItemVerifiedPartsMin)
              .describe(
                'Number of parts whose object identity has been verified.'
              ),
            warnings: zod
              .array(
                zod
                  .enum([
                    'creation_time_from_message',
                    'creation_time_from_job',
                    'unresolvable_direct_message',
                    'target_unavailable',
                    'uploads_incomplete',
                  ])
                  .describe(
                    'Non-fatal, sanitized explanations displayed in admin progress.'
                  )
              )
              .describe('Non-fatal metadata\/skip explanations.'),
          })
          .describe('Admin-visible progress for one selected conversation.')
      )
      .describe('All selected conversations, bounded by the create limit.'),
    createdAt: zod.iso
      .datetime({})
      .describe(
        'Persisted creation time, also the final historical-time fallback.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe('Immutable history option confirmed when creating this job.'),
    jobId: zod
      .uuid()
      .describe('Import job identity. Generate UUIDv7 in application code.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(cancelSlackImportResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(cancelSlackImportResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(cancelSlackImportResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(cancelSlackImportResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(cancelSlackImportResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(cancelSlackImportResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(cancelSlackImportResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(cancelSlackImportResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(cancelSlackImportResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(cancelSlackImportResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    registrationClosedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Registration closure time; cancellation also closes registration.'
      ),
    revision: zod
      .number()
      .min(cancelSlackImportResponseRevisionMin)
      .describe('Monotonic job revision for polling\/websocket invalidation.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
    status: zod
      .enum([
        'uploading',
        'processing',
        'completed',
        'completed_with_errors',
        'failed',
        'cancelling',
        'cancelled',
      ])
      .describe(
        'Job lifecycle. Cancellation remains in progress while any lease is active.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Last durable lifecycle\/progress update.'),
    usersVerified: zod
      .boolean()
      .describe('Whether users metadata is verified and pinned.'),
  })
  .describe(
    'Common receipt returned by create, completion, finalize, cancel and progress.'
  );

/**
 * @summary Close registration idempotently; never-ready conversations are skipped, not queued work.
 */
export const finalizeSlackImportParams = zod.object({
  job_id: zod.uuid(),
});

export const finalizeSlackImportResponseConversationsItemCountersDuplicatesMin = 0;

export const finalizeSlackImportResponseConversationsItemCountersImportedMin = 0;

export const finalizeSlackImportResponseConversationsItemCountersProcessedMin = 0;

export const finalizeSlackImportResponseConversationsItemCountersReactionsMin = 0;

export const finalizeSlackImportResponseConversationsItemCountersSkippedMin = 0;

export const finalizeSlackImportResponseConversationsItemPartCountMin = 0;

export const finalizeSlackImportResponseConversationsItemVerifiedPartsMin = 0;

export const finalizeSlackImportResponseLimitsConversationsMin = 0;

export const finalizeSlackImportResponseLimitsDatabaseBatchBytesMin = 0;

export const finalizeSlackImportResponseLimitsDatabaseBatchMessagesMin = 0;

export const finalizeSlackImportResponseLimitsJsonBytesMin = 0;

export const finalizeSlackImportResponseLimitsPartBytesMin = 0;

export const finalizeSlackImportResponseLimitsPartRecordsMin = 0;

export const finalizeSlackImportResponseLimitsRecordBytesMin = 0;

export const finalizeSlackImportResponseLimitsRegistrationBatchMin = 0;

export const finalizeSlackImportResponseLimitsSelectedBytesMin = 0;

export const finalizeSlackImportResponseLimitsZipEntriesMin = 0;

export const finalizeSlackImportResponseRevisionMin = 0;

export const finalizeSlackImportResponse = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Whether the selected source conversation was archived.'
              ),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Authorized target only; absent for inaccessible reused targets.'
              ),
            counters: zod
              .object({
                duplicates: zod
                  .number()
                  .min(
                    finalizeSlackImportResponseConversationsItemCountersDuplicatesMin
                  )
                  .describe('Previously committed source identities.'),
                imported: zod
                  .number()
                  .min(
                    finalizeSlackImportResponseConversationsItemCountersImportedMin
                  )
                  .describe('Newly persisted messages.'),
                processed: zod
                  .number()
                  .min(
                    finalizeSlackImportResponseConversationsItemCountersProcessedMin
                  )
                  .describe(
                    'Source records examined through the committed checkpoint.'
                  ),
                reactions: zod
                  .number()
                  .min(
                    finalizeSlackImportResponseConversationsItemCountersReactionsMin
                  )
                  .describe('Newly persisted reactions.'),
                skipped: zod
                  .number()
                  .min(
                    finalizeSlackImportResponseConversationsItemCountersSkippedMin
                  )
                  .describe(
                    'Unsupported, empty or otherwise deliberately skipped records.'
                  ),
              })
              .describe(
                'Committed counters, never optimistic browser or uncommitted worker counts.'
              ),
            error: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'disabled',
                    'invalid_input',
                    'limit_exceeded',
                    'unavailable',
                    'admin_required',
                    'source_mismatch',
                    'conflict',
                    'upload_mismatch',
                    'lease_lost',
                    'retryable',
                    'internal',
                  ])
                  .describe(
                    'Public error codes; never carry raw provider errors, keys, emails or source text.'
                  ),
              ])
              .optional(),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            name: zod
              .string()
              .describe(
                "Persisted source display name, never the reused Macro target's name."
              ),
            partCount: zod
              .number()
              .min(finalizeSlackImportResponseConversationsItemPartCountMin)
              .nullish()
              .describe(
                'Null until sealed; zero is a valid sealed empty manifest.'
              ),
            search: zod
              .union([
                zod
                  .object({
                    status: zod.enum(['not_needed']),
                  })
                  .describe('No committed history requires indexing.'),
                zod
                  .object({
                    status: zod.enum(['pending']),
                  })
                  .describe(
                    'Committed history requires an outbox-backed scoped submission.'
                  ),
                zod
                  .object({
                    receiptId: zod
                      .uuid()
                      .describe('Receipt from the search service.'),
                    status: zod.enum(['submitted']),
                  })
                  .describe(
                    'Search service accepted the request, but has not completed it.'
                  ),
                zod
                  .object({
                    status: zod.enum(['completed']),
                  })
                  .describe(
                    'Search service durably published the scope; eventual indexing is separate.'
                  ),
                zod
                  .object({
                    status: zod.enum(['failed']),
                  })
                  .describe(
                    'Publication failed; retained history must be retried independently.'
                  ),
              ])
              .describe(
                'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
            status: zod
              .enum([
                'awaiting_uploads',
                'queued',
                'importing',
                'completed',
                'skipped',
                'failed',
              ])
              .describe('Durable per-conversation lifecycle.'),
            verifiedParts: zod
              .number()
              .min(finalizeSlackImportResponseConversationsItemVerifiedPartsMin)
              .describe(
                'Number of parts whose object identity has been verified.'
              ),
            warnings: zod
              .array(
                zod
                  .enum([
                    'creation_time_from_message',
                    'creation_time_from_job',
                    'unresolvable_direct_message',
                    'target_unavailable',
                    'uploads_incomplete',
                  ])
                  .describe(
                    'Non-fatal, sanitized explanations displayed in admin progress.'
                  )
              )
              .describe('Non-fatal metadata\/skip explanations.'),
          })
          .describe('Admin-visible progress for one selected conversation.')
      )
      .describe('All selected conversations, bounded by the create limit.'),
    createdAt: zod.iso
      .datetime({})
      .describe(
        'Persisted creation time, also the final historical-time fallback.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe('Immutable history option confirmed when creating this job.'),
    jobId: zod
      .uuid()
      .describe('Import job identity. Generate UUIDv7 in application code.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(finalizeSlackImportResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(finalizeSlackImportResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(finalizeSlackImportResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(finalizeSlackImportResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(finalizeSlackImportResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(finalizeSlackImportResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(finalizeSlackImportResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(finalizeSlackImportResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(finalizeSlackImportResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(finalizeSlackImportResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    registrationClosedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Registration closure time; cancellation also closes registration.'
      ),
    revision: zod
      .number()
      .min(finalizeSlackImportResponseRevisionMin)
      .describe('Monotonic job revision for polling\/websocket invalidation.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
    status: zod
      .enum([
        'uploading',
        'processing',
        'completed',
        'completed_with_errors',
        'failed',
        'cancelling',
        'cancelled',
      ])
      .describe(
        'Job lifecycle. Cancellation remains in progress while any lease is active.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Last durable lifecycle\/progress update.'),
    usersVerified: zod
      .boolean()
      .describe('Whether users metadata is verified and pinned.'),
  })
  .describe(
    'Common receipt returned by create, completion, finalize, cancel and progress.'
  );

/**
 * @summary Issue short-lived, checksum/length-bound, create-only PUT grants. Do not log or persist URLs.
Retry a PUT returning 412 by completing/verifying the existing object, not overwriting it.
 */
export const registerSlackImportUploadsParams = zod.object({
  job_id: zod.uuid(),
});

export const registerSlackImportUploadsBodyDescriptorsItemByteLengthMin = 0;

export const registerSlackImportUploadsBodyDescriptorsItemRecordCountMin = 0;

export const registerSlackImportUploadsBodyDescriptorsItemUploadPartIndexMin = 0;

export const registerSlackImportUploadsBodyDescriptorsMax = 50;

export const registerSlackImportUploadsBody = zod
  .object({
    descriptors: zod
      .array(
        zod
          .object({
            byteLength: zod
              .number()
              .min(registerSlackImportUploadsBodyDescriptorsItemByteLengthMin)
              .describe('Exact byte length including delimiters.'),
            recordCount: zod
              .number()
              .min(registerSlackImportUploadsBodyDescriptorsItemRecordCountMin)
              .nullish()
              .describe(
                'Exact NDJSON record count; null for the users JSON payload.'
              ),
            sha256: zod
              .string()
              .describe(
                'SHA-256 encoded as exactly 64 lowercase hexadecimal characters.'
              ),
            upload: zod
              .union([
                zod
                  .object({
                    kind: zod.enum(['users']),
                  })
                  .describe(
                    'One normalized users payload from users.json and\/or org_users.json.'
                  ),
                zod
                  .object({
                    kind: zod.enum(['conversation_part']),
                    partIndex: zod
                      .number()
                      .min(
                        registerSlackImportUploadsBodyDescriptorsItemUploadPartIndexMin
                      )
                      .describe(
                        'Zero-based part index; sealed manifests must be contiguous.'
                      ),
                    slackChannelId: zod
                      .string()
                      .describe(
                        'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
                      ),
                  })
                  .describe('One NDJSON part in a selected conversation.'),
              ])
              .describe(
                "Identity in a job's persisted manifest. No user-supplied object keys."
              ),
          })
          .describe(
            'Immutable expected object properties, registered before signing an upload.'
          )
      )
      .max(registerSlackImportUploadsBodyDescriptorsMax)
      .describe('At most 50 descriptors; no duplicate identities in a call.'),
  })
  .describe(
    "Batched registration, also used to renew an unchanged descriptor's grant."
  );

export const registerSlackImportUploadsResponseDescriptorByteLengthMin = 0;

export const registerSlackImportUploadsResponseDescriptorRecordCountMin = 0;

export const registerSlackImportUploadsResponseDescriptorUploadPartIndexMin = 0;

export const registerSlackImportUploadsResponseItem = zod
  .object({
    descriptor: zod
      .object({
        byteLength: zod
          .number()
          .min(registerSlackImportUploadsResponseDescriptorByteLengthMin)
          .describe('Exact byte length including delimiters.'),
        recordCount: zod
          .number()
          .min(registerSlackImportUploadsResponseDescriptorRecordCountMin)
          .nullish()
          .describe(
            'Exact NDJSON record count; null for the users JSON payload.'
          ),
        sha256: zod
          .string()
          .describe(
            'SHA-256 encoded as exactly 64 lowercase hexadecimal characters.'
          ),
        upload: zod
          .union([
            zod
              .object({
                kind: zod.enum(['users']),
              })
              .describe(
                'One normalized users payload from users.json and\/or org_users.json.'
              ),
            zod
              .object({
                kind: zod.enum(['conversation_part']),
                partIndex: zod
                  .number()
                  .min(
                    registerSlackImportUploadsResponseDescriptorUploadPartIndexMin
                  )
                  .describe(
                    'Zero-based part index; sealed manifests must be contiguous.'
                  ),
                slackChannelId: zod
                  .string()
                  .describe(
                    'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
                  ),
              })
              .describe('One NDJSON part in a selected conversation.'),
          ])
          .describe(
            "Identity in a job's persisted manifest. No user-supplied object keys."
          ),
      })
      .describe(
        'Immutable expected object properties, registered before signing an upload.'
      ),
    expiresAt: zod.iso
      .datetime({})
      .describe(
        'Grant expiry; callers renew by re-registering the identical descriptor.'
      ),
    requiredHeaders: zod
      .record(zod.string(), zod.string())
      .describe(
        'Required signed upload headers (including checksum, content type and create-only\ncondition). Browser-forbidden Content-Length is derived from the exact Blob bytes.'
      ),
    url: zod
      .string()
      .describe('Short-lived signed destination; must not be logged.'),
  })
  .describe(
    'Signed upload permission returned just before PUT; never persisted as an identity.'
  );
export const registerSlackImportUploadsResponse = zod.array(
  registerSlackImportUploadsResponseItem
);

/**
 * @summary Verify storage checksums and lengths for registered objects. A seal fixes the ENTIRE
descriptor set (contiguous indices, sizes and hashes), not just this completion batch.
Empty uploads with a zero-part seal support shape-only or empty-history imports.
Work becomes ready only after verified users metadata, a seal and every required part.
 */
export const completeSlackImportUploadsParams = zod.object({
  job_id: zod.uuid(),
});

export const completeSlackImportUploadsBodySealPartCountMin = 0;

export const completeSlackImportUploadsBodyUploadsItemPartIndexMin = 0;

export const completeSlackImportUploadsBodyUploadsMax = 50;

export const completeSlackImportUploadsBody = zod
  .object({
    seal: zod
      .union([
        zod.null(),
        zod
          .object({
            manifestSha256: zod
              .string()
              .describe(
                'SHA-256 encoded as exactly 64 lowercase hexadecimal characters.'
              ),
            partCount: zod
              .number()
              .min(completeSlackImportUploadsBodySealPartCountMin)
              .describe(
                'Exact number of registered parts, starting at index zero.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
          })
          .describe(
            'Complete immutable expected part set for one conversation, including zero parts.'
          ),
      ])
      .optional(),
    uploads: zod
      .array(
        zod
          .union([
            zod
              .object({
                kind: zod.enum(['users']),
              })
              .describe(
                'One normalized users payload from users.json and\/or org_users.json.'
              ),
            zod
              .object({
                kind: zod.enum(['conversation_part']),
                partIndex: zod
                  .number()
                  .min(completeSlackImportUploadsBodyUploadsItemPartIndexMin)
                  .describe(
                    'Zero-based part index; sealed manifests must be contiguous.'
                  ),
                slackChannelId: zod
                  .string()
                  .describe(
                    'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
                  ),
              })
              .describe('One NDJSON part in a selected conversation.'),
          ])
          .describe(
            "Identity in a job's persisted manifest. No user-supplied object keys."
          )
      )
      .max(completeSlackImportUploadsBodyUploadsMax)
      .describe(
        'Registered identities to verify against storage (at most 50).'
      ),
  })
  .describe(
    'Explicit upload completion; a seal can be submitted separately with an empty list.'
  );

export const completeSlackImportUploadsResponseConversationsItemCountersDuplicatesMin = 0;

export const completeSlackImportUploadsResponseConversationsItemCountersImportedMin = 0;

export const completeSlackImportUploadsResponseConversationsItemCountersProcessedMin = 0;

export const completeSlackImportUploadsResponseConversationsItemCountersReactionsMin = 0;

export const completeSlackImportUploadsResponseConversationsItemCountersSkippedMin = 0;

export const completeSlackImportUploadsResponseConversationsItemPartCountMin = 0;

export const completeSlackImportUploadsResponseConversationsItemVerifiedPartsMin = 0;

export const completeSlackImportUploadsResponseLimitsConversationsMin = 0;

export const completeSlackImportUploadsResponseLimitsDatabaseBatchBytesMin = 0;

export const completeSlackImportUploadsResponseLimitsDatabaseBatchMessagesMin = 0;

export const completeSlackImportUploadsResponseLimitsJsonBytesMin = 0;

export const completeSlackImportUploadsResponseLimitsPartBytesMin = 0;

export const completeSlackImportUploadsResponseLimitsPartRecordsMin = 0;

export const completeSlackImportUploadsResponseLimitsRecordBytesMin = 0;

export const completeSlackImportUploadsResponseLimitsRegistrationBatchMin = 0;

export const completeSlackImportUploadsResponseLimitsSelectedBytesMin = 0;

export const completeSlackImportUploadsResponseLimitsZipEntriesMin = 0;

export const completeSlackImportUploadsResponseRevisionMin = 0;

export const completeSlackImportUploadsResponse = zod
  .object({
    conversations: zod
      .array(
        zod
          .object({
            archived: zod
              .boolean()
              .describe(
                'Whether the selected source conversation was archived.'
              ),
            channelId: zod
              .uuid()
              .nullish()
              .describe(
                'Authorized target only; absent for inaccessible reused targets.'
              ),
            counters: zod
              .object({
                duplicates: zod
                  .number()
                  .min(
                    completeSlackImportUploadsResponseConversationsItemCountersDuplicatesMin
                  )
                  .describe('Previously committed source identities.'),
                imported: zod
                  .number()
                  .min(
                    completeSlackImportUploadsResponseConversationsItemCountersImportedMin
                  )
                  .describe('Newly persisted messages.'),
                processed: zod
                  .number()
                  .min(
                    completeSlackImportUploadsResponseConversationsItemCountersProcessedMin
                  )
                  .describe(
                    'Source records examined through the committed checkpoint.'
                  ),
                reactions: zod
                  .number()
                  .min(
                    completeSlackImportUploadsResponseConversationsItemCountersReactionsMin
                  )
                  .describe('Newly persisted reactions.'),
                skipped: zod
                  .number()
                  .min(
                    completeSlackImportUploadsResponseConversationsItemCountersSkippedMin
                  )
                  .describe(
                    'Unsupported, empty or otherwise deliberately skipped records.'
                  ),
              })
              .describe(
                'Committed counters, never optimistic browser or uncommitted worker counts.'
              ),
            error: zod
              .union([
                zod.null(),
                zod
                  .enum([
                    'disabled',
                    'invalid_input',
                    'limit_exceeded',
                    'unavailable',
                    'admin_required',
                    'source_mismatch',
                    'conflict',
                    'upload_mismatch',
                    'lease_lost',
                    'retryable',
                    'internal',
                  ])
                  .describe(
                    'Public error codes; never carry raw provider errors, keys, emails or source text.'
                  ),
              ])
              .optional(),
            kind: zod
              .enum([
                'public_channel',
                'private_channel',
                'direct_message',
                'group_direct_message',
              ])
              .describe(
                'Slack conversation kind. Public Slack channels map to Macro Team, never Public.'
              ),
            name: zod
              .string()
              .describe(
                "Persisted source display name, never the reused Macro target's name."
              ),
            partCount: zod
              .number()
              .min(
                completeSlackImportUploadsResponseConversationsItemPartCountMin
              )
              .nullish()
              .describe(
                'Null until sealed; zero is a valid sealed empty manifest.'
              ),
            search: zod
              .union([
                zod
                  .object({
                    status: zod.enum(['not_needed']),
                  })
                  .describe('No committed history requires indexing.'),
                zod
                  .object({
                    status: zod.enum(['pending']),
                  })
                  .describe(
                    'Committed history requires an outbox-backed scoped submission.'
                  ),
                zod
                  .object({
                    receiptId: zod
                      .uuid()
                      .describe('Receipt from the search service.'),
                    status: zod.enum(['submitted']),
                  })
                  .describe(
                    'Search service accepted the request, but has not completed it.'
                  ),
                zod
                  .object({
                    status: zod.enum(['completed']),
                  })
                  .describe(
                    'Search service durably published the scope; eventual indexing is separate.'
                  ),
                zod
                  .object({
                    status: zod.enum(['failed']),
                  })
                  .describe(
                    'Publication failed; retained history must be retried independently.'
                  ),
              ])
              .describe(
                'Search publication\/receipt state. Acceptance is not completed publication;\neven completed publication still awaits consumer indexing and refresh.'
              ),
            slackChannelId: zod
              .string()
              .describe(
                'Source Slack conversation ID (C, G or D prefix); unique only within a source.'
              ),
            status: zod
              .enum([
                'awaiting_uploads',
                'queued',
                'importing',
                'completed',
                'skipped',
                'failed',
              ])
              .describe('Durable per-conversation lifecycle.'),
            verifiedParts: zod
              .number()
              .min(
                completeSlackImportUploadsResponseConversationsItemVerifiedPartsMin
              )
              .describe(
                'Number of parts whose object identity has been verified.'
              ),
            warnings: zod
              .array(
                zod
                  .enum([
                    'creation_time_from_message',
                    'creation_time_from_job',
                    'unresolvable_direct_message',
                    'target_unavailable',
                    'uploads_incomplete',
                  ])
                  .describe(
                    'Non-fatal, sanitized explanations displayed in admin progress.'
                  )
              )
              .describe('Non-fatal metadata\/skip explanations.'),
          })
          .describe('Admin-visible progress for one selected conversation.')
      )
      .describe('All selected conversations, bounded by the create limit.'),
    createdAt: zod.iso
      .datetime({})
      .describe(
        'Persisted creation time, also the final historical-time fallback.'
      ),
    includeMessageHistory: zod
      .boolean()
      .describe('Immutable history option confirmed when creating this job.'),
    jobId: zod
      .uuid()
      .describe('Import job identity. Generate UUIDv7 in application code.'),
    limits: zod
      .object({
        conversations: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsConversationsMin)
          .describe('Maximum selected conversations.'),
        databaseBatchBytes: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsDatabaseBatchBytesMin)
          .describe('Maximum historical batch payload bytes.'),
        databaseBatchMessages: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsDatabaseBatchMessagesMin)
          .describe('Maximum messages in a historical batch.'),
        jsonBytes: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsJsonBytesMin)
          .describe(
            'Maximum users, root metadata or individual day JSON bytes.'
          ),
        partBytes: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsPartBytesMin)
          .describe('Maximum NDJSON part bytes.'),
        partRecords: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsPartRecordsMin)
          .describe('Maximum records per part.'),
        recordBytes: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsRecordBytesMin)
          .describe('Maximum bytes per NDJSON record, including newline.'),
        registrationBatch: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsRegistrationBatchMin)
          .describe('Maximum descriptors or completion identities per call.'),
        selectedBytes: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsSelectedBytesMin)
          .describe(
            'Maximum selected temporary data bytes across all uploads.'
          ),
        zipEntries: zod
          .number()
          .min(completeSlackImportUploadsResponseLimitsZipEntriesMin)
          .describe('Maximum ZIP entries scanned by the browser.'),
      })
      .describe(
        'Configurable bounds shared with browser staging and enforced again by the worker.'
      ),
    registrationClosedAt: zod.iso
      .datetime({})
      .nullish()
      .describe(
        'Registration closure time; cancellation also closes registration.'
      ),
    revision: zod
      .number()
      .min(completeSlackImportUploadsResponseRevisionMin)
      .describe('Monotonic job revision for polling\/websocket invalidation.'),
    source: zod
      .union([
        zod
          .object({
            kind: zod.enum(['known']),
            sourceId: zod
              .string()
              .describe(
                'Known Slack workspace identity (T prefix); an enterprise ID alone is insufficient.'
              ),
          })
          .describe(
            'Archive names its Slack workspace; reject a conflicting team binding.'
          ),
        zod
          .object({
            kind: zod.enum(['confirmed_unknown']),
          })
          .describe(
            "Explicit confirmation that an unidentified archive belongs to this team's source.\nChoosing this variant is affirmative confirmation, not an inferred default."
          ),
      ])
      .describe(
        'Source identity supplied when creating a job. One binding per Macro team in v1.'
      ),
    status: zod
      .enum([
        'uploading',
        'processing',
        'completed',
        'completed_with_errors',
        'failed',
        'cancelling',
        'cancelled',
      ])
      .describe(
        'Job lifecycle. Cancellation remains in progress while any lease is active.'
      ),
    updatedAt: zod.iso
      .datetime({})
      .describe('Last durable lifecycle\/progress update.'),
    usersVerified: zod
      .boolean()
      .describe('Whether users metadata is verified and pinned.'),
  })
  .describe(
    'Common receipt returned by create, completion, finalize, cancel and progress.'
  );

export const bulkWakeupSyncServiceDocumentsBody = zod.object({
  document_ids: zod.array(zod.string()),
});

/**
 * @summary Edits the share permissions of a thread.
 */
export const editThreadV2Params = zod.object({
  thread_id: zod.string().describe('thread ID'),
});

export const editThreadV2Body = zod.object({
  projectId: zod
    .string()
    .nullish()
    .describe('The new project that the thread will belong to.'),
  sharePermission: zod
    .union([
      zod.null(),
      zod.object({
        channelSharePermissions: zod
          .array(
            zod.object({
              accessLevel: zod
                .union([
                  zod.null(),
                  zod
                    .enum(['view', 'comment', 'edit', 'owner'])
                    .describe(
                      'Ordered from least to most access top -> bottom'
                    ),
                ])
                .optional(),
              channelId: zod.string().describe('The channel id'),
              operation: zod.enum(['add', 'remove', 'replace']),
            })
          )
          .nullish()
          .describe(
            'Any channel share permissions to be created\/updated\/removed'
          ),
        linkShare: zod
          .union([
            zod.null(),
            zod
              .enum(['PUBLIC', 'TEAM'])
              .describe(
                'Defines who can access an item through its share link.'
              ),
          ])
          .optional(),
        linkShareAccessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
        teamShareAccessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
      }),
    ])
    .optional(),
});

export const editThreadV2Response = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary List the caller's API keys.
 */
export const listUserApiKeysResponse = zod
  .object({
    keys: zod
      .array(
        zod
          .object({
            createdAt: zod.iso
              .datetime({})
              .describe('When the key was created.'),
            id: zod
              .uuid()
              .describe('Opaque identifier for a stored user API key.'),
            name: zod.string().describe('User-facing name.'),
          })
          .describe(
            'Safe metadata for a stored key. Never contains the secret or its hash.'
          )
      )
      .describe("The caller's keys. Never includes the raw secret or hash."),
  })
  .describe("The caller's API keys as id, name, and created_at.");

/**
 * @summary Mint a new API key for the caller.
 */
export const createUserApiKeyBody = zod
  .object({
    name: zod.string().describe('User-facing name for the key.'),
  })
  .describe('Request body for minting a key.');

/**
 * @summary Delete one of the caller's API keys.
 */
export const deleteUserApiKeyParams = zod.object({
  id: zod.uuid().describe('Opaque key identifier.'),
});

/**
 * @summary Gets a UserPdfDocumentLocation entry
 */
export const getUserDocumentViewLocationParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getUserDocumentViewLocationResponse = zod.object({
  location: zod.string().nullish(),
});

export const upsertUserDocumentViewLocationParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const upsertUserDocumentViewLocationBody = zod.object({
  location: zod.string(),
});

export const upsertUserDocumentViewLocationResponse = zod
  .object({})
  .describe(
    'Empty response is required due to custom fetch forcing `response.json()`'
  );

/**
 * @summary Deletes a document location for the user
 */
export const deleteUserDocumentViewLocationParams = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const deleteUserDocumentViewLocationResponse = zod
  .object({})
  .describe(
    'Empty response is required due to custom fetch forcing `response.json()`'
  );

/**
 * @summary Gets the current documents share permissions
 */
export const getDocumentPermissionsV2Params = zod.object({
  document_id: zod.string().describe('Document ID'),
});

export const getDocumentPermissionsV2Response = zod.object({
  documentPermissions: zod.object({
    channelSharePermissions: zod
      .array(
        zod
          .object({
            access_level: zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
            channel_id: zod.string().describe('The channel id'),
          })
          .describe('The channel share permission')
      )
      .nullish()
      .describe('The channel share permissions for the item'),
    id: zod.string().describe('The share permission id'),
    linkShare: zod
      .union([
        zod.null(),
        zod
          .enum(['PUBLIC', 'TEAM'])
          .describe('Defines who can access an item through its share link.'),
      ])
      .optional(),
    linkShareAccessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
    owner: zod.string().describe('The owner of the item'),
    teamShareAccessLevel: zod
      .union([
        zod.null(),
        zod
          .enum(['view', 'comment', 'edit', 'owner'])
          .describe('Ordered from least to most access top -> bottom'),
      ])
      .optional(),
  }),
});

/**
 * @summary Edit project metadata and sharing settings.
 */
export const editProjectV2Params = zod.object({
  id: zod.string().describe('ID of the project'),
});

export const editProjectV2Body = zod.object({
  name: zod.string().nullish().describe('The new name of the project.'),
  projectParentId: zod
    .string()
    .nullish()
    .describe('The new project that the new project will belong to.'),
  sharePermission: zod
    .union([
      zod.null(),
      zod.object({
        channelSharePermissions: zod
          .array(
            zod.object({
              accessLevel: zod
                .union([
                  zod.null(),
                  zod
                    .enum(['view', 'comment', 'edit', 'owner'])
                    .describe(
                      'Ordered from least to most access top -> bottom'
                    ),
                ])
                .optional(),
              channelId: zod.string().describe('The channel id'),
              operation: zod.enum(['add', 'remove', 'replace']),
            })
          )
          .nullish()
          .describe(
            'Any channel share permissions to be created\/updated\/removed'
          ),
        linkShare: zod
          .union([
            zod.null(),
            zod
              .enum(['PUBLIC', 'TEAM'])
              .describe(
                'Defines who can access an item through its share link.'
              ),
          ])
          .optional(),
        linkShareAccessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
        teamShareAccessLevel: zod
          .union([
            zod.null(),
            zod
              .enum(['view', 'comment', 'edit', 'owner'])
              .describe('Ordered from least to most access top -> bottom'),
          ])
          .optional(),
      }),
    ])
    .optional(),
});

export const editProjectV2Response = zod.object({
  data: zod.object({
    success: zod.boolean().describe('Indicates if the request was successful'),
  }),
  error: zod.boolean().describe('Indicates if an error occurred'),
});

/**
 * @summary Stream matching broker events to the caller over Server-Sent Events.
 */
export const streamEventsQueryParams = zod.object({
  scope: zod
    .enum(['user', 'team'])
    .describe(
      'Personal or team workspace whose webhook lifecycle events are delivered.'
    ),
  filters: zod
    .string()
    .optional()
    .describe(
      'URL-encoded JSON array of webhook filters, identical to the persisted\nwebhook `filters` field.'
    ),
});

/**
 * @summary List the caller's webhooks.
 */
export const listWebhooksResponse = zod
  .object({
    webhooks: zod
      .array(
        zod
          .object({
            created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
            created_by_user_id: zod
              .string()
              .describe('User that created the webhook.'),
            deleted_at: zod.iso
              .datetime({})
              .nullish()
              .describe('Soft-delete timestamp.'),
            endpoint_url: zod
              .string()
              .describe(
                'Endpoint URL. HTTPS is required outside local environments.'
              ),
            filters: zod.array(
              zod
                .object({
                  events: zod
                    .array(zod.string())
                    .describe('Event names matched by this filter.'),
                  ids: zod
                    .array(zod.string())
                    .nullish()
                    .describe(
                      'Entity ids matched by this filter. When absent, the filter matches all entity ids.'
                    ),
                })
                .describe(
                  'Event and optional entity-id constraints used to match webhook deliveries.'
                )
            ),
            headers: zod.record(zod.string(), zod.string()),
            id: zod.string(),
            is_valid: zod
              .boolean()
              .describe(
                'Whether the current endpoint configuration has passed validation.'
              ),
            name: zod.string().describe('Display name.'),
            namespace: zod
              .string()
              .describe(
                "Caller-chosen namespace, unique among the owning workspace's webhooks.\nSet at creation time only; it cannot be changed afterwards."
              ),
            status: zod
              .enum(['active', 'paused', 'disabled'])
              .describe('Webhook lifecycle status.'),
            updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
            workspace_id: zod.string().describe('Owning workspace id.'),
          })
          .describe(
            'Webhook row returned by application APIs.\n\nClients deserialize this, so both derives are used.'
          )
      )
      .describe(
        "The caller's webhooks, newest first. Signing secrets are omitted."
      ),
  })
  .describe(
    'Webhooks visible to the caller across their personal and team workspaces.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * @summary Create a webhook.
 */
export const createWebhookBody = zod
  .object({
    endpoint_url: zod
      .string()
      .describe('Endpoint URL. HTTPS is required outside local environments.'),
    filters: zod.array(
      zod
        .object({
          events: zod
            .array(zod.string())
            .describe('Event names matched by this filter.'),
          ids: zod
            .array(zod.string())
            .nullish()
            .describe(
              'Entity ids matched by this filter. When absent, the filter matches all entity ids.'
            ),
        })
        .describe(
          'Event and optional entity-id constraints used to match webhook deliveries.'
        )
    ),
    headers: zod
      .union([zod.null(), zod.record(zod.string(), zod.string())])
      .optional(),
    name: zod.string().describe('Display name.'),
    namespace: zod
      .string()
      .describe(
        "Caller-chosen namespace, unique among the owning workspace's webhooks.\nSet at creation time only; it cannot be changed afterwards."
      ),
    scope: zod
      .enum(['user', 'team'])
      .describe(
        'Scope that owns a newly-created webhook.\n\nClients serialize this, so both derives are used. `Display`\/`FromStr`\nspell the same names as serde, for query strings and config values.'
      ),
  })
  .describe(
    'Request to create a webhook.\n\nClients serialize this, so both derives are used.'
  );

/**
 * @summary Get a webhook.
 */
export const getWebhookParams = zod.object({
  webhook_id: zod.string().describe('Webhook id'),
});

export const getWebhookResponse = zod
  .object({
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by_user_id: zod.string().describe('User that created the webhook.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Soft-delete timestamp.'),
    endpoint_url: zod
      .string()
      .describe('Endpoint URL. HTTPS is required outside local environments.'),
    filters: zod.array(
      zod
        .object({
          events: zod
            .array(zod.string())
            .describe('Event names matched by this filter.'),
          ids: zod
            .array(zod.string())
            .nullish()
            .describe(
              'Entity ids matched by this filter. When absent, the filter matches all entity ids.'
            ),
        })
        .describe(
          'Event and optional entity-id constraints used to match webhook deliveries.'
        )
    ),
    headers: zod.record(zod.string(), zod.string()),
    id: zod.string(),
    is_valid: zod
      .boolean()
      .describe(
        'Whether the current endpoint configuration has passed validation.'
      ),
    name: zod.string().describe('Display name.'),
    namespace: zod
      .string()
      .describe(
        "Caller-chosen namespace, unique among the owning workspace's webhooks.\nSet at creation time only; it cannot be changed afterwards."
      ),
    status: zod
      .enum(['active', 'paused', 'disabled'])
      .describe('Webhook lifecycle status.'),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
    workspace_id: zod.string().describe('Owning workspace id.'),
  })
  .describe(
    'Webhook row returned by application APIs.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * @summary Delete a webhook.
 */
export const deleteWebhookParams = zod.object({
  webhook_id: zod.string().describe('Webhook id'),
});

/**
 * @summary Patch a webhook.
 */
export const patchWebhookParams = zod.object({
  webhook_id: zod.string().describe('Webhook id'),
});

export const patchWebhookBody = zod
  .object({
    endpoint_url: zod
      .string()
      .nullish()
      .describe('Endpoint URL. HTTPS is required outside local environments.'),
    filters: zod
      .union([
        zod.null(),
        zod.array(
          zod
            .object({
              events: zod
                .array(zod.string())
                .describe('Event names matched by this filter.'),
              ids: zod
                .array(zod.string())
                .nullish()
                .describe(
                  'Entity ids matched by this filter. When absent, the filter matches all entity ids.'
                ),
            })
            .describe(
              'Event and optional entity-id constraints used to match webhook deliveries.'
            )
        ),
      ])
      .optional(),
    headers: zod
      .union([zod.null(), zod.record(zod.string(), zod.string())])
      .optional(),
    name: zod.string().nullish().describe('Display name.'),
    status: zod
      .union([
        zod.null(),
        zod
          .enum(['active', 'paused', 'disabled'])
          .describe('Webhook lifecycle status.'),
      ])
      .optional(),
  })
  .describe(
    "Request to patch a webhook. The webhook's namespace is fixed at creation\ntime and is deliberately not patchable."
  );

export const patchWebhookResponse = zod
  .object({
    created_at: zod.iso.datetime({}).describe('Creation timestamp.'),
    created_by_user_id: zod.string().describe('User that created the webhook.'),
    deleted_at: zod.iso
      .datetime({})
      .nullish()
      .describe('Soft-delete timestamp.'),
    endpoint_url: zod
      .string()
      .describe('Endpoint URL. HTTPS is required outside local environments.'),
    filters: zod.array(
      zod
        .object({
          events: zod
            .array(zod.string())
            .describe('Event names matched by this filter.'),
          ids: zod
            .array(zod.string())
            .nullish()
            .describe(
              'Entity ids matched by this filter. When absent, the filter matches all entity ids.'
            ),
        })
        .describe(
          'Event and optional entity-id constraints used to match webhook deliveries.'
        )
    ),
    headers: zod.record(zod.string(), zod.string()),
    id: zod.string(),
    is_valid: zod
      .boolean()
      .describe(
        'Whether the current endpoint configuration has passed validation.'
      ),
    name: zod.string().describe('Display name.'),
    namespace: zod
      .string()
      .describe(
        "Caller-chosen namespace, unique among the owning workspace's webhooks.\nSet at creation time only; it cannot be changed afterwards."
      ),
    status: zod
      .enum(['active', 'paused', 'disabled'])
      .describe('Webhook lifecycle status.'),
    updated_at: zod.iso.datetime({}).describe('Update timestamp.'),
    workspace_id: zod.string().describe('Owning workspace id.'),
  })
  .describe(
    'Webhook row returned by application APIs.\n\nClients deserialize this, so both derives are used.'
  );

/**
 * @summary Validate a webhook endpoint.
 */
export const validateWebhookParams = zod.object({
  webhook_id: zod.string().describe('Webhook id'),
});

export const validateWebhookResponseResponseStatusMin = 0;

export const validateWebhookResponse = zod
  .object({
    is_valid: zod
      .boolean()
      .describe(
        'Whether the endpoint accepted the signed validation delivery.'
      ),
    message: zod
      .string()
      .nullish()
      .describe('Sanitized message explaining validation failure.'),
    response_status: zod
      .number()
      .min(validateWebhookResponseResponseStatusMin)
      .nullish()
      .describe(
        'HTTP response status returned by the webhook endpoint, when available.'
      ),
    webhook_id: zod.string(),
  })
  .describe('Sanitized result of validating a webhook endpoint.');
